import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import "./App.css";
import "./toast-custom.css";
import axios from "axios";
import Login from "./Login";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Plus, Save, Send, Trash2, Download, LogOut, User, ArrowLeft, Search, Edit, Eye, Upload, X, Mail, Settings, RefreshCw, FileText, FileSearch, Ticket, MessageSquare, Wallet, TruckIcon, CheckCircle, Wrench, CalendarDays, BarChart3, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import SignatureCanvas from 'react-signature-canvas';
import { translations, getTranslation } from './translations';
import NativeTestPage from './NativeTestPage';
import PDFViewerModal from './PDFViewerModal';
import FloatingPDFViewer from './components/FloatingPDFViewer';
import ScheduleMapModal from './components/ScheduleMapModal';
import ScheduleTimeEditor from './components/ScheduleTimeEditor';import ExtraLaborPresetsDropdown from './components/ExtraLaborPresetsDropdown';
import DuplicateLaborBillsCleanup from './components/admin/DuplicateLaborBillsCleanup';
import DupCheckSettingsCard from './components/admin/DupCheckSettingsCard';
import DemoUserPanel from './components/admin/DemoUserPanel';
import TeamResetPanel from './components/admin/TeamResetPanel';
import DashboardActivityFeed from './components/admin/DashboardActivityFeed';
import AdminHub from './components/admin/AdminHub';
import SavannahExpensesPanel from './components/admin/SavannahExpensesPanel';
import SalesUsersPanel from './components/admin/SalesUsersPanel';
import AccessMonitor from './components/admin/AccessMonitor';
import AlertsManager from './components/admin/AlertsManager';
import InsuranceManager from './components/admin/InsuranceManager';
import AlertsLoginPopup from './components/admin/AlertsLoginPopup';
import { openExternalLink, EXTERNAL_LINKS, completionChecklistUrl, leadFormUrl } from './utils/externalLinks';
import { AiFieldMappingCard } from './components/admin/AiFieldMappingCard';
import { formatNyDateTime, formatNyDate } from './utils/datetime';
import { Editor } from '@tinymce/tinymce-react';
import ImageViewerModal from './ImageViewerModal';
import OfficeViewerModal from './OfficeViewerModal';
import VideoViewerModal from './VideoViewerModal';
import MobilePDFViewer from './MobilePDFViewer';
import WaiverModal from './components/WaiverModal';
import WaiverSettingsModal from './components/WaiverSettingsModal';
import LaborBillDesigner from './components/LaborBillDesigner';
import InstructionManual from './components/manuals/InstructionManual';
import ActiveRentalCard from './components/trucks/ActiveRentalCard';
import RentalUpdateModal from './components/trucks/RentalUpdateModal';
import TruckCard from './components/trucks/TruckCard';
import RentalRequestModal from './components/trucks/RentalRequestModal';
import MaintenanceModal from './components/trucks/MaintenanceModal';
import MaintenanceHistoryModal from './components/trucks/MaintenanceHistoryModal';
import CompleteMaintenanceModal from './components/trucks/CompleteMaintenanceModal';
import AddEditWorkModal from './components/schedule/AddEditWorkModal';
import MeasuredJobsCard from './components/schedule/MeasuredJobsCard';
import SoldProjectsCard from './components/schedule/SoldProjectsCard';
import CrewsWeeklyView from './components/crews/CrewsWeeklyView';
import NyClock from './components/NyClock';
import IntegrationsStatusCard from './components/admin/IntegrationsStatusCard';
import { buildTeamWeekly } from './components/crews/CrewsWeeklyView';
import CompareTeamsModal from './components/crews/CompareTeamsModal';
import { EmailWithCopy } from './components/schedule/EmailWithCopy';
import BillTeamBadges from './components/BillTeamBadges';
import SentSupervisorBadge from './components/SentSupervisorBadge';
import OldBillComparisonModal from './components/OldBillComparisonModal';
import UnsentBillsAlert from './components/UnsentBillsAlert';
import ServiceNotesModal from './components/schedule/ServiceNotesModal';
import BlockDayModal from './components/schedule/BlockDayModal';
import JobHoverPopover from './components/schedule/JobHoverPopover';
import HelperFinancialReportModal from './components/helper/HelperFinancialReportModal';
import AIProgressToast from './components/AIProgressToast';
import NotificationBell from './components/notifications/NotificationBell';
import RentalNotificationModal from './components/notifications/RentalNotificationModal';
import ActivityCard from './components/notifications/ActivityCard';
import FomChangesModal from './components/FomChangesModal';
import PushNotificationsPanel from './components/admin/PushNotificationsPanel';
import CompanyBillsTab from './components/admin/CompanyBillsTab';
import PasswordVaultTab from './components/admin/PasswordVaultTab';
import PermissionsTab from './components/admin/PermissionsTab';
import DailyLeaderReport from './components/daily-leader/DailyLeaderReport';
import CheckInModal from './components/trucks/CheckInModal';
import CheckOutModal from './components/trucks/CheckOutModal';
import RentalRequestCard from './components/trucks/RentalRequestCard';
import RentalHistoryModal from './components/trucks/RentalHistoryModal';
import ApproveRejectModal from './components/trucks/ApproveRejectModal';
import TruckFormModal from './components/trucks/TruckFormModal';
import DaysReport from './components/helper/DaysReport';
// Push Notifications
import { initializePushNotifications } from './services/pushNotifications';
import NotificationFlyer from './components/notifications/NotificationFlyer';
import EmbeddedFormSheet from './components/EmbeddedFormSheet';
import HelperWaiverPanel from './components/docs/HelperWaiverPanel';
import CompareTeamsPanel from './components/admin/CompareTeamsPanel';
import PayrollReportsList from './components/admin/PayrollReportsList';
import ReportBackchargeModal from './components/admin/ReportBackchargeModal';
import NotificationsPurgeDialog from './components/admin/NotificationsPurgeDialog';
// Recharts imports for charts visualization
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { 
  saveUserCredentials, 
  clearSavedCredentials, 
  attemptAutoLogin, 
  confirmLogout,
  performBiometricAuth 
} from './lib/secureAuth';
// Capacitor imports for native app features
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import ShareReceiverModal from './components/share/ShareReceiverModal';
// Firebase imports removed - using internal notification system

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WEB_APP_VERSION = '2.9.2';

const compareVersions = (a, b) => {
  const pa = String(a).split('.').map((n) => parseInt(n) || 0);
  const pb = String(b).split('.').map((n) => parseInt(n) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d) return d;
  }
  return 0;
};
// Origem pública p/ montar o link do FOM no e-mail. No app iOS (Capacitor/webview) o
// window.location.origin pode ser inválido (capacitor://localhost) e quebrar o link;
// nesse caso usamos o domínio público do backend (mesmo domínio do app na web).
const publicOrigin = () => {
  const o = (typeof window !== 'undefined' && window.location && window.location.origin) || '';
  if (/^https?:\/\//i.test(o) && !/localhost|127\.0\.0\.1|capacitor/i.test(o)) return o.replace(/\/$/, '');
  return (BACKEND_URL || '').replace(/\/$/, '');
};
const LOGO_URL = "https://customer-assets.emergentagent.com/job_truck-rental/artifacts/uoxnksao_logo.png";


const BILL_CATEGORY_ICONS = {
  'Aluguel': '🏠', 'Internet': '🌐', 'Telefone': '📱', 'Seguros': '🛡️', 'Energia': '⚡',
  'Ferramentas': '🔧', 'Software': '💻', 'Veículos': '🚗', 'Impostos': '📋', 'Serviços': '🔨', 'Outros': '📌'
};

const BILL_ALERT_ICONS = {
  'Seguro Veicular': '🚗', 'Seguro Empresa': '🏢', 'Licença': '📜', 'Certificado': '🎓', 'Contrato': '📑',
  'Registro Veículo': '🪪', 'Documento PM': '👤', 'Garantia': '🛡️', 'Inspeção': '🔍', 'Outros': '⚠️'
};

const MY_ALERT_ICONS_HOME = {
  seguro: '🛡️', registro: '🔖', manutencao: '🔧', pagamento: '💰', documento: '📄', outro: '📌'
};

// Gerar ocorrências de contas recorrentes para um mês
function getBillsForMonth(bills, year, month) {
  const map = {};
  const dIM = new Date(year, month + 1, 0).getDate();
  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
  
  (bills || []).forEach(b => {
    if (!b.next_due_date) return;
    const due = new Date(b.next_due_date + 'T12:00:00');
    const freq = b.frequency;
    
    // 1. Adicionar datas pagas que estão neste mês
    if (b.paid_dates) {
      Object.keys(b.paid_dates).forEach(paidDate => {
        if (paidDate.startsWith(monthStr)) {
          const day = parseInt(paidDate.split('-')[2]);
          if (!map[day]) map[day] = [];
          if (!map[day].some(x => x.id === b.id)) map[day].push(b);
        }
      });
    }
    
    // 2. Adicionar recorrências futuras (next_due_date em diante)
    if (freq === 'daily') {
      for (let d = 1; d <= dIM; d++) {
        if (new Date(year, month, d) >= due) {
          if (!map[d]) map[d] = [];
          if (!map[d].some(x => x.id === b.id)) map[d].push(b);
        }
      }
    } else if (freq === 'weekly') {
      // Calcular qual seria o dia da semana baseado no due_day original
      const dueDay = due.getDay();
      let cur = new Date(year, month, 1);
      // Encontrar primeiro dia do mês com o mesmo dia da semana
      while (cur.getDay() !== dueDay) cur.setDate(cur.getDate() + 1);
      while (cur.getMonth() === month && cur.getFullYear() === year) {
        const d = cur.getDate();
        if (!map[d]) map[d] = [];
        if (!map[d].some(x => x.id === b.id)) map[d].push(b);
        cur.setDate(cur.getDate() + 7);
      }
    } else if (freq === 'monthly') {
      const day = Math.min(due.getDate(), dIM);
      if (!map[day]) map[day] = [];
      if (!map[day].some(x => x.id === b.id)) map[day].push(b);
    } else if (freq === 'quarterly') {
      const diff = (month - due.getMonth() + 12) % 12;
      if (diff % 3 === 0) {
        const day = Math.min(due.getDate(), dIM);
        if (!map[day]) map[day] = [];
        if (!map[day].some(x => x.id === b.id)) map[day].push(b);
      }
    } else if (freq === 'annual') {
      if (due.getMonth() === month) {
        const day = Math.min(due.getDate(), dIM);
        if (!map[day]) map[day] = [];
        if (!map[day].some(x => x.id === b.id)) map[day].push(b);
      }
    } else {
      const [y, m] = b.next_due_date.split('-').map(Number);
      if (y === year && m === month + 1) {
        const day = parseInt(b.next_due_date.split('-')[2]);
        if (!map[day]) map[day] = [];
        if (!map[day].some(x => x.id === b.id)) map[day].push(b);
      }
    }
  });
  return map;
}

const BillCalendarMini = ({ bills, onSelectDay, selectedDay }) => {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const todayStr = now.toISOString().slice(0, 10);
  const todayDay = now.getDate();

  const billsByDay = getBillsForMonth(bills, year, month);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="grid grid-cols-7 gap-1">
      {['D','S','T','Q','Q','S','S'].map((d,i) => <div key={i} className="text-center text-[10px] font-medium text-gray-400">{d}</div>)}
      {cells.map((day, i) => {
        if (!day) return <div key={`e${i}`} className="h-10" />;
        const dayBills = billsByDay[day] || [];
        const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
        const isToday = day === todayDay;
        const isSelected = day === selectedDay;
        return (
          <div 
            key={day} 
            onClick={(e) => { e.stopPropagation(); onSelectDay && onSelectDay(day); }}
            className={`h-10 border rounded flex flex-col items-center justify-center text-xs cursor-pointer transition-all ${
              isSelected ? 'bg-blue-100 border-blue-500 ring-1 ring-blue-400' :
              isToday ? 'bg-blue-50 border-blue-400 font-bold' :
              dayBills.length > 0 ? (dateStr < todayStr ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300') :
              'border-gray-100 hover:bg-gray-50'
            }`} 
            title={dayBills.map(b => `${b.name} $${b.value}`).join(', ')}
          >
            <span className={isToday || isSelected ? 'text-blue-700' : 'text-gray-700'}>{day}</span>
            {dayBills.length > 0 && (
              <span className="text-[9px] leading-none">{dayBills.map(b => {
                const ds = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                return (b.paid_dates && b.paid_dates[ds]) ? '✅' : b.item_type === 'my_alert' ? (MY_ALERT_ICONS_HOME[b.category] || '📅') : b.item_type === 'alert' ? (BILL_ALERT_ICONS[b.category] || '⚠️') : (BILL_CATEGORY_ICONS[b.category] || '📌');
              }).join('')}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};


// Helper para download universal (Web + Capacitor) - SEMPRE COMPARTILHAR
const downloadFile = async (blob, filename, mimeType = 'application/octet-stream') => {
  const isNativeApp = Capacitor.isNativePlatform();
  
  if (isNativeApp) {
    // Download no app nativo usando Capacitor
    try {
      console.log(`📱 Compartilhamento nativo: ${filename}`);
      
      // Converter blob para base64
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      
      await new Promise((resolve, reject) => {
        reader.onloadend = async () => {
          try {
            const base64Data = reader.result.split(',')[1];
            
            // Salvar arquivo no dispositivo
            const savedFile = await Filesystem.writeFile({
              path: filename,
              data: base64Data,
              directory: Directory.Documents
            });
            
            console.log(`✅ Arquivo salvo: ${savedFile.uri}`);
            
            // Compartilhar arquivo (abre menu de compartilhamento do iOS)
            await Share.share({
              title: filename,
              text: `Arquivo: ${filename}`,
              url: savedFile.uri,
              dialogTitle: 'Compartilhar PDF'
            });
            
            resolve();
          } catch (error) {
            console.error('❌ Erro ao salvar/compartilhar arquivo:', error);
            reject(error);
          }
        };
        reader.onerror = reject;
      });
      
      toast.success(`✅ ${filename} compartilhado!`);
    } catch (error) {
      console.error('❌ Erro no compartilhamento nativo:', error);
      toast.error('Erro ao compartilhar arquivo');
      throw error;
    }
  } else {
    // Tentar usar Web Share API (navegador moderno)
    try {
      console.log(`🌐 Compartilhamento web: ${filename}`);
      
      // Verificar se Web Share API está disponível
      if (navigator.share && navigator.canShare) {
        // Criar File a partir do Blob
        const file = new File([blob], filename, { type: mimeType });
        
        // Verificar se pode compartilhar arquivos
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: filename,
            text: 'Compartilhar PDF',
            files: [file]
          });
          
          toast.success(`✅ ${filename} compartilhado!`);
          return;
        }
      }
      
      // Fallback: se Web Share não disponível, tentar compartilhar URL
      if (navigator.share) {
        const url = window.URL.createObjectURL(blob);
        await navigator.share({
          title: filename,
          text: 'Compartilhar PDF',
          url: url
        });
        window.URL.revokeObjectURL(url);
        toast.success(`✅ ${filename} compartilhado!`);
        return;
      }
      
      // Fallback final: download tradicional se Web Share não disponível
      console.log('⚠️ Web Share não disponível, fazendo download...');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.info(`Download iniciado: ${filename}`);
      
    } catch (error) {
      // Se usuário cancelou o compartilhamento, não é erro
      if (error.name === 'AbortError') {
        console.log('Compartilhamento cancelado pelo usuário');
        return;
      }
      
      console.error('❌ Erro no compartilhamento web:', error);
      
      // Fallback para download em caso de erro
      try {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.info(`Download iniciado: ${filename}`);
      } catch (downloadError) {
        toast.error('Erro ao compartilhar/baixar arquivo');
        throw downloadError;
      }
    }
  }
};

// Helper para extrair mensagem de erro segura
const getErrMsg = (e, fallback = "Ocorreu um erro") => {
  try {
    const d = e?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map(x => x?.msg || (typeof x === 'string' ? x : JSON.stringify(x))).join(' | ');
    if (d && typeof d === 'object') return d.message || d.error || JSON.stringify(d);
    return e?.message || fallback;
  } catch {
    return fallback;
  }
};

// Normaliza nome do cliente para detectar possíveis duplicados no mesmo dia
const normalizeCustomerName = (name) => {
  return (name || '')
    .toString()
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // remove acentos
    .replace(/[&+,./]/g, ' ')
    .replace(/\b(crew|llc|inc|jr|sr|mr|mrs|ms|and)\b/g, ' ')
    .replace(/\d+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

// Retorna um Set com os ids de jobs que parecem duplicados (mesmo dia, nome parecido)
const getDuplicateJobIds = (jobs) => {
  const dupIds = new Set();
  const items = (jobs || [])
    .map(j => {
      const tokens = new Set(normalizeCustomerName(j.customer_name).split(' ').filter(t => t.length > 1));
      return { id: j.id, tokens };
    })
    .filter(it => it.tokens.size > 0);

  for (let i = 0; i < items.length; i++) {
    for (let k = i + 1; k < items.length; k++) {
      const a = items[i], b = items[k];
      const small = a.tokens.size <= b.tokens.size ? a.tokens : b.tokens;
      const big = a.tokens.size <= b.tokens.size ? b.tokens : a.tokens;
      let subset = true;
      for (const t of small) { if (!big.has(t)) { subset = false; break; } }
      if (subset) { dupIds.add(a.id); dupIds.add(b.id); }
    }
  }
  return dupIds;
};


// Helper para formatar dinheiro no padrão $00,000.00
const formatMoney = (value) => {
  const num = parseFloat(value) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

  // Helper para separar Labor Bills por data (período configurável)
const separateLaborBillsByDate = (bills, periodInDays = 60) => {
  // Calcular data limite baseada no período selecionado
  const now = new Date();
  const cutoffDate = new Date(now);
  cutoffDate.setDate(now.getDate() - periodInDays);
  
  const recentBills = [];
  const oldBills = [];
  
  bills.forEach(bill => {
    if (!bill.install_date) {
      // Bills sem data vão para recentes
      recentBills.push(bill);
      return;
    }
    
    try {
      let billDate;
      
      // Tentar parsing de diferentes formatos de data
      if (bill.install_date.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
        // Formato MM-DD-YYYY
        const [month, day, year] = bill.install_date.split('-');
        billDate = new Date(year, parseInt(month) - 1, parseInt(day));
      } else if (bill.install_date.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
        // Formato YYYY-MM-DD
        billDate = new Date(bill.install_date);
      } else if (bill.install_date.includes('/')) {
        // Formato MM/DD/YYYY ou M/D/YY
        const parts = bill.install_date.split('/');
        if (parts.length === 3) {
          let [month, day, year] = parts;
          if (year.length === 2) {
            const yearInt = parseInt(year);
            year = yearInt < 50 ? `20${year}` : `19${year}`;
          }
          billDate = new Date(year, parseInt(month) - 1, parseInt(day));
        } else {
          billDate = new Date(bill.install_date);
        }
      } else {
        billDate = new Date(bill.install_date);
      }
      
      // Verificar se a data é válida
      if (isNaN(billDate.getTime())) {
        recentBills.push(bill);
        return;
      }
      
      // Separar por data baseado no período configurado
      if (billDate >= cutoffDate) {
        recentBills.push(bill);
      } else {
        oldBills.push(bill);
      }
      
    } catch (e) {
      console.warn('⚠️ Data parse error for bill separation:', bill.install_date, e);
      recentBills.push(bill); // Default para recentes se houver erro
    }
  });
  
  return {
    recent: recentBills,
    old: oldBills
  };
};

// 🧭 Rótulos de navegação (rastreamento silencioso de abas/sub-abas)
const NAV_TAB_LABELS = {
  home: 'Início (Schedule)', reports: 'Relatórios', report: 'Relatório', crews: 'Equipes',
  arbitration: 'Arbitration', trucks: 'Caminhões', admin: 'Admin', documents: 'Documentos',
  create: 'Criar Labor Bill', payroll: 'Folha de Pagamento', drafts: 'Rascunhos',
  support: 'Suporte', config: 'Configurações', map: 'Mapa',
};
const NAV_SUBTAB_LABELS = {
  admin: {
    usuarios: 'Usuários', dados: 'Dados', sistema: 'Sistema', emails: 'E-mails',
    importar: 'Importar', leaders: 'Líderes de Equipe', notificacoes: 'Notificações',
    videos: 'Vídeos', mensagens: 'Mensagens', helper: 'Helpers', acessos: 'Acessos',
    alertas: 'Alertas', arquivos: 'Arquivos', cofre: 'Cofre', contas: 'Contas a Pagar',
    migracao: 'Migração', permissoes: 'Permissões', relatorios: 'Relatórios', suporte: 'Suporte',
    seguros: 'Seguros', laborbill: 'Labor Bill', comparar: 'Comparar Trabalhos',
    savannah: 'Despesas Savannah', vendas: 'Portal de Vendas',
  },
  arbitration: {
    files: 'Arquivos', contractor: 'Contractor', installation: 'Instalação',
    helpers: 'Helpers', lswp: 'LSWP', manual: 'Manual', seguro: 'Helper WC',
  },
  trucks: {
    history: 'Histórico', dashboard: 'Painel', fleet: 'Frota', available: 'Disponíveis',
    'my-rentals': 'Meus Aluguéis', requests: 'Solicitações',
  },
  payroll: { upload: 'Upload', teams: 'Equipes', reports: 'Relatórios' },
};


// Helper para obter nome do período baseado nos dias
const getPeriodName = (days, language = 'pt-BR') => {
  const names = {
    '7': { 'pt-BR': 'Última Semana', 'en': 'Last Week' },
    '15': { 'pt-BR': 'Últimos 15 Dias', 'en': 'Last 15 Days' },
    '30': { 'pt-BR': 'Último Mês', 'en': 'Last Month' },
    '60': { 'pt-BR': 'Últimos 2 Meses', 'en': 'Last 2 Months' },
    '90': { 'pt-BR': 'Últimos 3 Meses', 'en': 'Last 3 Months' },
    '180': { 'pt-BR': 'Últimos 6 Meses', 'en': 'Last 6 Months' }
  };
  return names[days]?.[language] || 'Período Personalizado';
};

// Helper para ordenar nomes de PMs alfanumericamente (PM1, PM2, PM10...)
const sortPMNames = (a, b) => {
  const regex = /^PM(\d+)$/;
  const matchA = a.match(regex);
  const matchB = b.match(regex);
  
  if (matchA && matchB) {
    // Ambos são PMs - ordenar numericamente
    return parseInt(matchA[1]) - parseInt(matchB[1]);
  } else if (matchA && !matchB) {
    // A é PM, B não é - PM vem primeiro
    return -1;
  } else if (!matchA && matchB) {
    // A não é PM, B é PM - PM vem primeiro
    return 1;
  } else {
    // Nenhum é PM - ordenação alfabética normal
    return a.localeCompare(b);
  }
};

// ============================================================================
// LABOR BILL - DEFINIÇÕES ESTÁTICAS (FORA DO COMPONENTE)
// ============================================================================

const DEFAULT_LEFT_SUBGROUPS = [
  {
    title: "Insert Frame Window Installation",
    items: [
      { description: "Unit Up to 120 UI", unit_price: 175.0 },
      { description: "Unit over 120 UI", unit_price: 215.0 },
      { description: "Casement/Glider Up to 120 UI", unit_price: 240.0 },
      { description: "Casement/Glider Over 120 UI", unit_price: 260.0 },
      { description: "Capping per window", unit_price: 25.0 }
    ]
  },
  {
    title: "Full Frame Window Installation",
    items: [
      { description: "Additional Per Unit", unit_price: 40.0 }
    ]
  },
  {
    title: "Bay/Bow Installation",
    items: [
      { description: "Install Bay/Bow", unit_price: 500.0 },
      { description: "Bay/Bow Soffit Tie In", unit_price: 300.0 },
      { description: "Bay/Bow Build Skirt", unit_price: 300.0 },
      { description: "Bay/Bow Add Roof-Shingle", unit_price: 200.0 },
      { description: "Bay/Bow Add Roof-Metal", unit_price: 300.0 }
    ]
  },
  {
    title: "Patio Doors",
    items: [
      { description: "2 Panel Glider", unit_price: 830.0 },
      { description: "Additional Panel", unit_price: 350.0 },
      { description: "Field Assemble Door Frame", unit_price: 75.0 },
      { description: "Hinged/Retractable Screen Door", unit_price: 150.0 },
      { description: "Double Hinged/Retractable Screen Door", unit_price: 300.0 },
      { description: "Single Hinge", unit_price: 425.0 },
      { description: "Double Hinge/Hinge w/Fixed Panel", unit_price: 930.0 },
      { description: "Door Over 8' - Carrying Charge", unit_price: 200.0 }
    ]
  },
  {
    title: "Entry Doors",
    items: [
      { description: "Single Entry Door", unit_price: 425.0 },
      { description: "Double Entry Door", unit_price: 930.0 },
      { description: "Sidelight/Transom", unit_price: 120.0 },
      { description: "Storm Entry", unit_price: 150.0 },
      { description: "Double Storm Entry", unit_price: 300.0 }
    ]
  },
  {
    title: "Miscellaneous Labor Charges",
    items: [
      { description: "Trip Charge 50-74mi", unit_price: 50.0 },
      { description: "Trip Charge 75-99mi", unit_price: 75.0 },
      { description: "Trip Charge 100-149mi", unit_price: 100.0 },
      { description: "Trip Charge 150+mi/Out of Town", unit_price: 250.0 },
      { description: "Job Minimum", unit_price: 300.0 },
      { description: "Daily Minimum", unit_price: 500.0 }
    ]
  },
  { title: "Extra Labor", items: [] }
];

const DEFAULT_RIGHT_SUBGROUPS = [
  {
    title: "Miscellaneous Window Charges",
    items: [
      { description: "Buck Frame Repair", unit_price: 25.0 },
      { description: "Add Complete Buck Frame", unit_price: 50.0 },
      { description: "Conversion 2/1 - 3/1", unit_price: 35.0 },
      { description: "Interior Casing - Picture", unit_price: 25.0 },
      { description: "Interior Casing/Stool", unit_price: 40.0 },
      { description: "Knee Wall Per LF", unit_price: 55.0 },
      { description: "Mull Windows Together", unit_price: 30.0 },
      { description: "R&R Blinds", unit_price: 10.0 },
      { description: "R&R Ext. Shutters - Pair", unit_price: 20.0 },
      { description: "R&R Vinyl Siding", unit_price: 50.0 },
      { description: "Remove Burglar Bars", unit_price: 35.0 },
      { description: "Remove Storm Windows", unit_price: 10.0 },
      { description: "Sub Floor Repair", unit_price: 100.0 },
      { description: "Window Install - 2nd Floor Open Int./Side", unit_price: 250.0 },
      { description: "Window Install - 3rd Floor Ext./Side", unit_price: 150.0 },
      { description: "Furniture - Move per Room", unit_price: 15.0 }
    ]
  }
];

// Mapa IA (chave interna -> descrição do novo catálogo). Deve bater com
// FIELD_TO_DESCRIPTION no backend (labor_bill_ai_service.py). Portas/legados são best-guess.
const AI_FIELD_TO_DESCRIPTION = {
  unit_up_to_120: "Unit Up to 120 UI",
  unit_over_120: "Unit over 120 UI",
  casement_up_to_120: "Casement/Glider Up to 120 UI",
  casement_over_120: "Casement/Glider Over 120 UI",
  glider_up_to_120: "Casement/Glider Up to 120 UI",
  glider_over_120: "Casement/Glider Over 120 UI",
  picture_over_120: "Casement/Glider Over 120 UI",
  capping: "Capping per window",
  rr_shutters: "R&R Ext. Shutters - Pair",
  single_panel: "Single Hinge",
  "2_panel_glider": "2 Panel Glider",
  dbl_hinge_hinge_w_fixed_pnl: "Double Hinge/Hinge w/Fixed Panel",
  "8_door": "Door Over 8' - Carrying Charge",
  per_lf_over_8: "Door Over 8' - Carrying Charge",
  sidelights: "Sidelight/Transom",
  transom: "Sidelight/Transom",
  brickmold: null,
  casing: "Interior Casing - Picture",
  replace_stops_l_trim: null,
  lswp_opening: null,
  second_floor_open_interior: "Window Install - 2nd Floor Open Int./Side",
  rr_vinyl_siding: "R&R Vinyl Siding",
  blinds_rr_storms: "R&R Blinds",
  trip_charge_50: "Trip Charge 50-74mi",
  trip_charge_75: "Trip Charge 75-99mi",
  trip_charge_100: "Trip Charge 100-149mi",
  trip_charge_250: "Trip Charge 150+mi/Out of Town",
  minimum_job: "Job Minimum",
  minimum_daily: "Daily Minimum"
};
// Chave da IA -> campo do molde (qty_field): elo ESTÁVEL com o Designer (renomear item/preço não quebra o pré-preenchimento)
const AI_FIELD_TO_QTY_FIELD = {
  unit_up_to_120: "qty_dh_up120", unit_over_120: "qty_dh_over120", casement_up_to_120: "qty_all_up120", casement_over_120: "qty_all_over120",
  glider_up_to_120: "qty_all_up120", glider_over_120: "qty_all_over120", picture_over_120: "qty_all_over120", capping: "qty_capping", rr_shutters: "qty_rr_shutters",
  // Portas: desligadas por padrão (null) — o Admin liga em Admin → Labor Bill → "Ligação IA → campo"
  single_panel: null, "2_panel_glider": null, dbl_hinge_hinge_w_fixed_pnl: null, "8_door": null, per_lf_over_8: null,
  sidelights: null, transom: null, casing: "qty_casing_picture", second_floor_open_interior: "qty_window_2nd", rr_vinyl_siding: "qty_rr_siding", blinds_rr_storms: "qty_rr_blinds",
  trip_charge_50: "qty_trip50", trip_charge_75: "qty_trip75", trip_charge_100: "qty_trip100", trip_charge_250: "qty_trip_oot", minimum_job: "qty_min_job", minimum_daily: "qty_min_daily",
};
// Item do catálogo atual (Designer) que a chave da IA representa: pela ligação do Admin (qty_field);
// `null` na ligação = IA não preenche essa chave. Fallback pelo nome original só quando não há qty_field nos itens.
const findAiItem = (items, aiKey, mapping = AI_FIELD_TO_QTY_FIELD) => {
  if (Object.prototype.hasOwnProperty.call(mapping, aiKey) && !mapping[aiKey]) return null;
  const qf = mapping[aiKey] || AI_FIELD_TO_QTY_FIELD[aiKey];
  if (qf) {
    const hit = items.find((it) => it.qty_field === qf);
    if (hit || items.some((it) => it.qty_field)) return hit || null;
  }
  return items.find((it) => it.description === AI_FIELD_TO_DESCRIPTION[aiKey]) || null;
};

const getDefaultItemsStatic = () => {
  const all = [];
  DEFAULT_LEFT_SUBGROUPS.forEach((sg) => sg.items.forEach((it) => all.push({ description: it.description, qty: '', unit_price: it.unit_price, total: 0, category: "left" })));
  DEFAULT_RIGHT_SUBGROUPS.forEach((sg) => sg.items.forEach((it) => all.push({ description: it.description, qty: '', unit_price: it.unit_price, total: 0, category: "right" })));
  return all;
};

function App() {
  // Estado para controlar seções abertas do manual (previne fechamento no scroll)
  const [openManualSections, setOpenManualSections] = useState({});
  
  const toggleManualSection = (sectionId) => {
    setOpenManualSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  // ===== LABOR BILL TEMPLATE (Designer) — catálogo dinâmico (seções/itens/preços) =====
  const [leftColumnSubgroups, setLeftColumnSubgroups] = useState(DEFAULT_LEFT_SUBGROUPS);
  const [rightColumnSubgroups, setRightColumnSubgroups] = useState(DEFAULT_RIGHT_SUBGROUPS);
  const [showLaborDesigner, setShowLaborDesigner] = useState(false);

  const getDefaultItems = () => {
    const all = [];
    leftColumnSubgroups.forEach((sg) => { if (sg.title !== 'Extra Labor') sg.items.forEach((it) => all.push({ description: it.description, qty: '', unit_price: it.unit_price, total: 0, category: 'left', qty_field: it.qty_field || null, total_field: it.total_field || null })); });
    rightColumnSubgroups.forEach((sg) => sg.items.forEach((it) => all.push({ description: it.description, qty: '', unit_price: it.unit_price, total: 0, category: 'right', qty_field: it.qty_field || null, total_field: it.total_field || null })));
    return all;
  };

  // ===== TRUCK RENTAL SYSTEM - Estados =====
  const [trucks, setTrucks] = useState([]);
  const [myRentals, setMyRentals] = useState([]);
  const [activeRental, setActiveRental] = useState(null);
  const [showRentalRequestModal, setShowRentalRequestModal] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);
  const [showRentalUpdateModal, setShowRentalUpdateModal] = useState(false);
  const [showRentalHistoryModal, setShowRentalHistoryModal] = useState(false);
  const [showTruckFormModal, setShowTruckFormModal] = useState(false);
  const [showApproveRejectModal, setShowApproveRejectModal] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [selectedRental, setSelectedRental] = useState(null);
  const [approveRejectAction, setApproveRejectAction] = useState('approve');
  const [trucksSubTab, setTrucksSubTab] = useState('history'); // PM default: history | Admin default: dashboard
  const [showTruckDocModal, setShowTruckDocModal] = useState(false);
  const [truckDocToView, setTruckDocToView] = useState(null);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showMaintenanceHistoryModal, setShowMaintenanceHistoryModal] = useState(false);
  const [showCompleteMaintenanceModal, setShowCompleteMaintenanceModal] = useState(false);
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  
  // Notification states
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [fomReview, setFomReview] = useState(null);
  const [showFomReviewModal, setShowFomReviewModal] = useState(false);
  const [showOldNotificationModal, setShowOldNotificationModal] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(null);
  // Language State
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem('preferred_language') || 'pt-BR';
    }
    return 'pt-BR';
  });

  // Translation helper function
  const t = (key, fallback = key) => {
    return getTranslation(language, key, fallback);
  };

  const changeLanguage = (lang) => {
    setLanguage(lang);
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('preferred_language', lang);
    }
    toast.success(lang === 'pt-BR' ? 'Idioma alterado para Português' : 'Language changed to English');
  };

  const [user, setUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  
  // Permission check helper
  const hasPermission = (category, key) => {
    if (!userPermissions) return true; // No permissions loaded = show everything
    if (user?.role === 'admin' && user?.username === 'ADMIN') return true; // ADMIN always has full access
    const perms = userPermissions[category];
    if (!perms) return true; // Category not defined = allow
    return perms.includes(key);
  };
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(true); // Loading state for auto-login
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false); // Prevent duplicate auto-login attempts
  
  // Animação de entrada APÓS LOGIN (não no reload)
  const [showSplashScreen, setShowSplashScreen] = useState(false); // Controla exibição do vídeo (false inicialmente)
  const [splashScreenEnabled, setSplashScreenEnabled] = useState(() => {
    // Configuração salva (para o toggle)
    const saved = localStorage.getItem('splash_screen_enabled');
    return saved === null ? true : saved === 'true';
  });
  const [splashAnimation, setSplashAnimation] = useState('entering'); // 'entering' | 'exiting'
  const [videoCanPlay, setVideoCanPlay] = useState(false); // Para detectar se vídeo pode tocar
  
  // Lista de vídeos para splash screen (carregada do banco)
  const [splashVideos, setSplashVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  
  // Carregar múltiplos vídeos e selecionar aleatório APÓS LOGIN
  const loadSplashVideos = async () => {
    try {
      console.log('🎬 INICIANDO loadSplashVideos após login...');
      
      // Verificar se splash screen está habilitado
      const localSetting = localStorage.getItem('splash_screen_enabled');
      const isEnabled = localSetting === null ? true : localSetting === 'true';
      
      if (!isEnabled) {
        console.log('🎬 Splash screen DESABILITADO - não carregando vídeos');
        return; // NÃO mostra vídeo
      }
      
      console.log('🎬 Splash screen ATIVADO - carregando vídeos...');
      
      // Vídeo padrão
      const defaultVideos = [
        {
          id: "default",
          url: "https://customer-assets.emergentagent.com/job_893c8c98-bbbd-438c-beb8-ac02ccc9688c/artifacts/yhcwkawb_152b2cbf57b840fb8635aa29a476b1c4.MP4",
          name: "Vídeo Padrão"
        }
      ];
      
      // Buscar vídeos do banco
      let dbVideos = [];
      try {
        const token = sessionStorage.getItem('access_token');
        console.log(`🔑 Token encontrado: ${token ? 'SIM' : 'NÃO'}`);
        
        if (token) {
          const { data } = await axios.get(`${API}/admin/splash-videos`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          console.log(`📹 Vídeos do banco: ${data?.length || 0}`);
          
          dbVideos = (data || []).map(video => ({
            id: video.id,
            url: `${API}/admin/splash-videos/${video.id}/file`,
            name: video.name
          }));
        }
      } catch (error) {
        console.log('📹 Erro buscando vídeos do banco:', error.message);
      }
      
      // Combinar todos os vídeos
      const allVideos = [...defaultVideos, ...dbVideos];
      console.log(`🎬 Total de vídeos disponíveis: ${allVideos.length}`);
      
      // Seleção aleatória
      const randomIndex = Math.floor(Math.random() * allVideos.length);
      const selected = allVideos[randomIndex];
      
      console.log(`🎲 Vídeo selecionado: ${randomIndex + 1}/${allVideos.length} - ${selected.name}`);
      console.log(`🔗 URL: ${selected.url}`);
      
      setSelectedVideo(selected.url);
      
      // Mostrar splash screen
      console.log('🎬 Mostrando splash screen...');
      setShowSplashScreen(true);
      setSplashAnimation('entering');
      
    } catch (error) {
      console.error('❌ Erro geral em loadSplashVideos:', error);
    }
  };
  
  // Notificações internas removidas

  const [laborBills, setLaborBills] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]); // Histórico de envios de emails
  const [currentBill, setCurrentBill] = useState(null);
  // Floating PDF viewer (source PDF da work order, visível enquanto edita Labor Bill)
  const [sourcePdfUrl, setSourcePdfUrl] = useState(null);
  const [sourcePdfTitle, setSourcePdfTitle] = useState('');
  const [showFloatingPdf, setShowFloatingPdf] = useState(false);
  const [floatingPdfMinimized, setFloatingPdfMinimized] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  // Active Tab state - different defaults for different user types
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== 'undefined') {
      const user_info = JSON.parse(sessionStorage.getItem('user_info') || '{}');
      if (user_info.role === 'personal') {
        return 'documents';
      }
    }
    return 'home';
  });
  
  // Crews Report States (ADMIN only)
  const [crewsReportData, setCrewsReportData] = useState(null);
  const [crewsReportLoading, setCrewsReportLoading] = useState(false);
  const [crewsReportPeriodType, setCrewsReportPeriodType] = useState('month'); // week, month, quarter, year, custom
  const [crewsReportSelectedWeek, setCrewsReportSelectedWeek] = useState('');
  const [crewsReportSelectedMonth, setCrewsReportSelectedMonth] = useState('');
  const [crewsReportStartDate, setCrewsReportStartDate] = useState('');
  const [crewsReportEndDate, setCrewsReportEndDate] = useState('');
  
  // Novos estados para os sub-modos de cada período
  const [crewsMonthlyMode, setCrewsMonthlyMode] = useState('last4weeks'); // last4weeks, lastCompleteMonth, chooseMonth
  const [crewsQuarterlyMode, setCrewsQuarterlyMode] = useState('last12weeks'); // last12weeks, lastCompleteQuarter, chooseQuarter
  const [crewsAnnualMode, setCrewsAnnualMode] = useState('last52weeks'); // last52weeks, lastCompleteYear, chooseYear
  const [crewsSelectedMonthOption, setCrewsSelectedMonthOption] = useState('');
  const [crewsSelectedQuarterOption, setCrewsSelectedQuarterOption] = useState('');
  const [crewsSelectedYearOption, setCrewsSelectedYearOption] = useState('');
  
  // Listas dinâmicas de opções disponíveis (baseadas nos relatórios)
  const [crewsAvailableMonths, setCrewsAvailableMonths] = useState([]); // ['2025-11', '2025-10', ...]
  const [crewsAvailableQuarters, setCrewsAvailableQuarters] = useState([]); // ['Q4/2025', 'Q3/2025', ...]
  const [crewsAvailableYears, setCrewsAvailableYears] = useState([]); // ['2025', '2024', ...]
  const [crewsWeekMeta, setCrewsWeekMeta] = useState({}); // {week_ending: {work_start, work_end}}
  const [crewsWorkMonths, setCrewsWorkMonths] = useState([]); // meses por DATA DO TRABALHO
  const [crewsPayMonths, setCrewsPayMonths] = useState([]); // meses por DATA DE PAGAMENTO
  const [crewsSumBasis, setCrewsSumBasis] = useState('work'); // 'work' (data do trabalho) | 'payment' (data de pagamento)
  // Comparar Meses (2 meses, cada um em Trabalho e Pagamento)
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [crewsCompareMonthA, setCrewsCompareMonthA] = useState('');
  const [crewsCompareMonthB, setCrewsCompareMonthB] = useState('');
  const [crewsCompareData, setCrewsCompareData] = useState(null);
  const [crewsCompareLoading, setCrewsCompareLoading] = useState(false);
  
  const [crewsReportSelectedTeams, setCrewsReportSelectedTeams] = useState([]); // Array de team names
  const [crewsReportViewMode, setCrewsReportViewMode] = useState('cards'); // cards, table, ranking, charts
  const [crewsReportSortBy, setCrewsReportSortBy] = useState('total_final'); // total_final, total_jobs, total_labor
  const [crewsReportSortOrder, setCrewsReportSortOrder] = useState('desc'); // asc, desc
  const [crewsShowDetailedBills, setCrewsShowDetailedBills] = useState(false); // Toggle para mostrar bills individuais
  const [crewsGroupBy, setCrewsGroupBy] = useState('job'); // 'job' (por trabalho) | 'week' (por semana)
  const [crewsWeeksCollapsed, setCrewsWeeksCollapsed] = useState(() => localStorage.getItem('crewsWeeksBreakdownCollapsed') === 'true');
  const [showCompareTeamsModal, setShowCompareTeamsModal] = useState(false); // Modal comparar equipes selecionadas
  const [crewsAvailableWeeks, setCrewsAvailableWeeks] = useState([]); // Lista de semanas disponíveis
  const [crewsSummaryExpanded, setCrewsSummaryExpanded] = useState(true); // Resumo geral expandido/minimizado
  const [crewsWeeksExpanded, setCrewsWeeksExpanded] = useState(() => localStorage.getItem('crewsWeeksBreakdownExpanded') !== 'false'); // Subtotais por Semana expandido/minimizado
  
  // Estados para visualização de gráficos
  const [selectedWeeksForCharts, setSelectedWeeksForCharts] = useState([]); // Semanas selecionadas para comparação
  const [weeksDataForCharts, setWeeksDataForCharts] = useState({}); // Dados de cada semana {week: data}
  const [crewsChartType, setCrewsChartType] = useState('timeline'); // timeline, comparison, heatmap, pie
  const [crewsChartMetric, setCrewsChartMetric] = useState('total_final'); // Métrica a visualizar
  const [loadingChartsData, setLoadingChartsData] = useState(false);
  
  // Customização de campos visíveis (reseta ao reload)
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [crewsCardsFields, setCrewsCardsFields] = useState({
    trabalhos: true,
    total_labor: true,
    total_final: true,
    lucro: true,
    porcentagem: true,
    despesas: true,
    backcharge: true
  });
  const [crewsTableFields, setCrewsTableFields] = useState({
    trabalhos: true,
    total_labor: true,
    total_final: true,
    lucro: true,
    porcentagem: true,
    despesas: true,
    backcharge: true
  });
  const [crewsRankingFields, setCrewsRankingFields] = useState({
    trabalhos: true,
    total_labor: true,
    total_final: true,
    lucro: true,
    porcentagem: true,
    despesas: true,
    backcharge: true
  });
  const [crewsSummaryFields, setCrewsSummaryFields] = useState({
    total_equipes: true,
    total_trabalhos: true,
    total_labor: true,
    total_final: true,
    total_lucro: true,
    total_despesas: true
  });
  
  // Submenu states
  const [arbitrationSubmenu, setArbitrationSubmenu] = useState(false);
  const [arbitrationSubTab, setArbitrationSubTab] = useState('files'); // 'files' | 'contractor' | 'installation' | 'helpers' | 'lswp'
  const [isDirty, setIsDirty] = useState(false);

  // Native iOS Test Page state
  const [showNativeTest, setShowNativeTest] = useState(false);

  // Email dialog
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);

  // User Settings dialog
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profile, setProfile] = useState({ username: "", first_name: "", last_name: "", phone: "", address: "" });
  const [pw, setPw] = useState({ current: "", next: "" });

  // New email recipient form
  const [newRecipient, setNewRecipient] = useState({ name: "", email: "" });

  // Backup UI state
  const [backupLoading, setBackupLoading] = useState(false);
  const [importMode, setImportMode] = useState("merge");
  const [backupFile, setBackupFile] = useState(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  
  // Global loading state - controla TODAS as operações
  const [globalLoading, setGlobalLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Carregando...');

  // AI Labor Bill Progress Toast
  const [showAIToast, setShowAIToast] = useState(false);
  const [aiToastProgress, setAiToastProgress] = useState(0);
  const [aiToastStep, setAiToastStep] = useState('');
  const [aiToastComplete, setAiToastComplete] = useState(false);
  const [aiToastError, setAiToastError] = useState(false);
  const [aiProcessingJobId, setAiProcessingJobId] = useState(null);
  const [aiQueueInfo, setAiQueueInfo] = useState(''); // ex: "1/3" (posição/total na fila)
  // Fila do 2º IA (Labor Bill): processa 1 por vez; novos trabalhos entram na fila.
  const aiQueueRef = useRef([]);        // jobIds aguardando
  const aiQueueBusyRef = useRef(false); // worker rodando?
  const aiQueueTotalRef = useRef(0);    // total do lote atual
  const aiQueueDoneRef = useRef(0);     // concluídos no lote atual
  
  // Labor Bill AI Draft
  const [laborBillDraft, setLaborBillDraft] = useState(null);
  const [aiFilledFields, setAiFilledFields] = useState([]);
  const [aiFieldMap, setAiFieldMap] = useState(AI_FIELD_TO_QTY_FIELD); // chave IA → qty_field (Admin pode religar)
  const [draftLoading, setDraftLoading] = useState(false);
  
  // Job Hover Popover (calendário) - MÚLTIPLOS
  const [hoveredJobs, setHoveredJobs] = useState([]); // Array de job IDs
  const [hoveredJobsData, setHoveredJobsData] = useState([]); // Array de dados
  const [popoverPositions, setPopoverPositions] = useState([]); // Array de posições
  const hoverTimerRef = useRef(null);

  // Trash state and search/sort
  const [trash, setTrash] = useState([]);
  const [trashSearch, setTrashSearch] = useState("");
  const [trashSort, setTrashSort] = useState("deleted_desc");

  // Home & Drafts search and filters
  const [homeSearch, setHomeSearch] = useState("");
  const [draftsSearch, setDraftsSearch] = useState("");
  const [draftsSort, setDraftsSort] = useState("date_desc"); // Default: newest first
  const [showOnlyPaid, setShowOnlyPaid] = useState(false); // New filter for paid clients
  const [showOnlySentToSupervisor, setShowOnlySentToSupervisor] = useState(false); // Filtro: só bills enviados ao supervisor
  
  // Mobile menu and scroll states
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isIPhone, setIsIPhone] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  // Cabeçalho fixo + paddingTop valem para TODO iPhone (navegador Safari/Chrome E app nativo Capacitor).
  // Antes isolamos só o navegador (isIPhoneWeb), mas isso deixava o app nativo com header sticky + paddingTop
  // = um vão gigante no topo. Voltamos a usar isIPhone (no navegador o comportamento é idêntico).
  const isIPhoneWeb = isIPhone;
  // No iOS/mobile (<=768px) o CSS força o header para sticky (!important), então
  // NÃO devemos somar paddingTop=headerHeight (senão cria um vão gigante no topo).
  // Só compensamos quando o header é realmente fixed (ex.: iPad/landscape > 768px).
  const [headerStickyMode, setHeaderStickyMode] = useState(
    typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 768px)').matches
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 768px)');
    const on = () => setHeaderStickyMode(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, []);
  
  // Floating hamburger button states (iOS/iPad only)
  const [showFloatingButton, setShowFloatingButton] = useState(false); // Mostra quando rola
  const [showFloatingMenu, setShowFloatingMenu] = useState(false); // Menu lateral aberto
  const [floatingDocsExpanded, setFloatingDocsExpanded] = useState(false); // Docs expandido no menu flutuante
  const [floatingButtonPosition, setFloatingButtonPosition] = useState(() => {
    // Carregar posição salva ou usar padrão (canto inferior direito)
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = localStorage.getItem('floating_button_position');
      if (saved) {
        return JSON.parse(saved);
      }
    }
    return { x: window.innerWidth - 80, y: window.innerHeight - 100 };
  });
  const [isDraggingButton, setIsDraggingButton] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0, startX: 0, startY: 0, time: 0 });

  // Detectar iPhone e prevenir scroll automático no app Capacitor
  useEffect(() => {
    const iPhone = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    setIsIPhone(iPhone);
    console.log('📱 Dispositivo iPhone/iPad:', iPhone);
    
    // Prevenir scroll automático APENAS no app Capacitor (não no Safari web)
    const isNativeApp = window.Capacitor !== undefined;
    
    if (iPhone && isNativeApp) {
      console.log('🔧 App Capacitor iOS detectado - aplicando classe CSS e prevenção de scroll');
      
      // Adicionar classe CSS para styling específico
      document.body.classList.add('capacitor-ios');
      
      // iOS Capacitor: Double requestAnimationFrame para override do WebView
      const preventScrollOnFocus = (e) => {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
          const scrollY = window.scrollY;
          const scrollX = window.scrollX;
          
          console.log('📱 Input focus - mantendo posição:', scrollY);
          
          // Double requestAnimationFrame to override iOS WebView
          requestAnimationFrame(() => {
            window.scrollTo(scrollX, scrollY);
            requestAnimationFrame(() => {
              window.scrollTo(scrollX, scrollY);
            });
          });
        }
      };
      
      document.addEventListener('focusin', preventScrollOnFocus, true);
      
      return () => {
        document.body.classList.remove('capacitor-ios');
        document.removeEventListener('focusin', preventScrollOnFocus, true);
      };
    }
  }, []);

  // Share Extension listener - receber arquivos compartilhados (somente ADMIN)
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const handler = CapApp.addListener('appUrlOpen', async (event) => {
        console.log('📥 Share Extension URL:', event.url);
        
        // Bloquear se não for ADMIN
        if (user?.role !== 'admin') {
          toast.error('Apenas o ADMIN pode usar esta funcionalidade.');
          return;
        }
        
        const url = new URL(event.url);
        const filePath = url.searchParams.get('file');
        const fileType = url.searchParams.get('type') || '';
        const fileName = url.searchParams.get('name') || 'shared-file';
        
        if (filePath) {
          try {
            const result = await Filesystem.readFile({ path: filePath });
            const blob = new Blob([Uint8Array.from(atob(result.data), c => c.charCodeAt(0))], { type: fileType });
            const file = new File([blob], fileName, { type: fileType });
            setSharedFile(file);
            setSharedFileType(fileType);
            setShareModalOpen(true);
          } catch (e) {
            console.error('Erro ao ler arquivo compartilhado:', e);
          }
        }
      });
      return () => handler.remove();
    }
  }, [user?.role]);

  // Auto-atualizar: SÓ no app nativo (Capacitor). Ao voltar do background, compara o hash
  // do bundle JS atual com o da produção; se saiu um deploy novo, recarrega sozinho.
  // No navegador NÃO roda (para não interromper quem está usando).
  useEffect(() => {
    let isNative = false;
    try { isNative = Capacitor.isNativePlatform(); } catch (e) { isNative = false; }
    if (!isNative) return undefined;

    const currentBundle = (() => {
      const s = document.querySelector('script[src*="/static/js/main"]');
      return s ? s.src.split('/').pop() : null; // main.<hash>.js
    })();

    let lastCheck = 0;
    let reloading = false;
    const checkForUpdate = async () => {
      const now = Date.now();
      if (reloading || now - lastCheck < 5000) return; // evita checagens repetidas
      lastCheck = now;
      try {
        const res = await fetch(`/index.html?ts=${now}`, { cache: 'no-store' });
        if (!res.ok) return;
        const html = await res.text();
        const m = html.match(/\/static\/js\/main\.[A-Za-z0-9]+\.js/);
        const latest = m ? m[0].split('/').pop() : null;
        if (latest && currentBundle && latest !== currentBundle) {
          reloading = true;
          console.log('🔄 Nova versão detectada, recarregando app…', currentBundle, '→', latest);
          window.location.reload();
        }
      } catch (e) { /* offline / erro de rede: ignora */ }
    };

    const onVisible = () => { if (document.visibilityState === 'visible') checkForUpdate(); };
    document.addEventListener('visibilitychange', onVisible);
    let resumeSub;
    try { resumeSub = CapApp.addListener('resume', checkForUpdate); } catch (e) {}
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      try { resumeSub && resumeSub.remove && resumeSub.remove(); } catch (e) {}
    };
  }, []);

  // Reporta a versão do app/plataforma ao backend (Admin vê em Usuários)
  useEffect(() => {
    if (!user) return;
    const reportAppInfo = async () => {
      try {
        const payload = { platform: 'web', web_version: WEB_APP_VERSION, native_version: null, native_build: null };
        try {
          if (Capacitor.isNativePlatform()) {
            payload.platform = Capacitor.getPlatform();
            const info = await CapApp.getInfo();
            payload.native_version = info.version || null;
            payload.native_build = info.build ? String(info.build) : null;
          }
        } catch (e) { /* plugin indisponível */ }
        const res = await axios.post(`${API}/users/app-info`, payload);
        const minV = res.data?.min_native_version;
        if (
          minV && payload.native_version &&
          (payload.platform === 'android' || payload.platform === 'ios') &&
          compareVersions(payload.native_version, minV) < 0 &&
          !sessionStorage.getItem('updateNoticeShown')
        ) {
          sessionStorage.setItem('updateNoticeShown', '1');
          setUpdateNotice({ current: payload.native_version, min: minV, platform: payload.platform });
        }
      } catch (e) { /* silencioso */ }
    };
    reportAppInfo();
  }, [user]);



  
  // Medir altura do header para iPhone (ResizeObserver mantém sempre exato,
  // mesmo com safe-area/relógio renderizando depois ou ao girar a tela)
  useEffect(() => {
    if (!isIPhone && !headerStickyMode) return undefined;
    const header = document.querySelector('header');
    const measureHeader = () => {
      const h = document.querySelector('header');
      if (h) setHeaderHeight(h.offsetHeight);
    };
    // Medições em vários momentos p/ pegar o layout final (fontes/safe-area)
    const timers = [0, 100, 300, 600, 1200].map((ms) => setTimeout(measureHeader, ms));
    let ro;
    if (header && 'ResizeObserver' in window) {
      ro = new ResizeObserver(measureHeader);
      ro.observe(header);
    }
    window.addEventListener('resize', measureHeader);
    window.addEventListener('orientationchange', measureHeader);
    return () => {
      timers.forEach(clearTimeout);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measureHeader);
      window.removeEventListener('orientationchange', measureHeader);
    };
  }, [isIPhone, headerStickyMode, user]);
  
  // Detectar scroll para mostrar/esconder botão flutuante (iOS/iPad only)
  useEffect(() => {
    if (!isIPhone || !isAuthenticated) return; // Só funciona em iOS/iPad após login
    
    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      
      // Mostrar botão quando rolar para baixo (header sai da tela)
      if (scrollY > headerHeight) {
        setShowFloatingButton(true);
      } else {
        setShowFloatingButton(false);
        setShowFloatingMenu(false); // Fechar menu quando voltar ao topo
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check inicial
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isIPhone, isAuthenticated, headerHeight]);
  
  // Funções para arrastar o botão flutuante
  const handleButtonTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    setIsDraggingButton(false); // Reset dragging state
    setDragStartPos({
      x: touch.clientX - floatingButtonPosition.x,
      y: touch.clientY - floatingButtonPosition.y,
      startX: touch.clientX,
      startY: touch.clientY,
      time: Date.now()
    });
  };
  
  const handleButtonTouchMove = (e) => {
    if (e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    
    // Detectar se está arrastando (movimento > 5px)
    const deltaX = Math.abs(touch.clientX - dragStartPos.startX);
    const deltaY = Math.abs(touch.clientY - dragStartPos.startY);
    
    if (deltaX > 5 || deltaY > 5) {
      setIsDraggingButton(true);
      e.preventDefault();
      
      let newX = touch.clientX - dragStartPos.x;
      let newY = touch.clientY - dragStartPos.y;
      
      // Limitar dentro da tela (com margem de 10px)
      const buttonSize = 60;
      newX = Math.max(10, Math.min(window.innerWidth - buttonSize - 10, newX));
      newY = Math.max(10, Math.min(window.innerHeight - buttonSize - 10, newY));
      
      setFloatingButtonPosition({ x: newX, y: newY });
    }
  };
  
  const handleButtonTouchEnd = () => {
    const wasDragging = isDraggingButton;
    const timeDiff = Date.now() - dragStartPos.time;
    
    if (wasDragging) {
      // Salvar posição no localStorage apenas se arrastou
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem('floating_button_position', JSON.stringify(floatingButtonPosition));
        console.log('💾 Posição do botão flutuante salva:', floatingButtonPosition);
      }
      
      // Reset dragging state após pequeno delay para evitar trigger de clique
      setTimeout(() => {
        setIsDraggingButton(false);
      }, 100);
    } else {
      setIsDraggingButton(false);
    }
  };
  
  const handleButtonClick = (e) => {
    // Só abrir menu se não estava arrastando e foi clique rápido
    if (!isDraggingButton) {
      e.preventDefault();
      e.stopPropagation();
      setShowFloatingMenu(true);
      console.log('📱 Menu flutuante aberto');
    }
  };
  
  const closeFloatingMenu = () => {
    setShowFloatingMenu(false);
    setFloatingDocsExpanded(false); // Resetar expansão do Docs ao fechar menu
    console.log('📱 Menu flutuante fechado');
  };

  // Filtro por PM para Labor Bills (ADMIN only)
  const [laborBillPMFilter, setLaborBillPMFilter] = useState('ALL');
  
  // Estados para seções expansíveis de Labor Bills por data
  const [recentBillsExpanded, setRecentBillsExpanded] = useState(true); // Últimos 2 meses - expandido por padrão
  const [oldBillsExpanded, setOldBillsExpanded] = useState(false); // Anteriores - minimizado por padrão
  
  // Filtro de período de Labor Bills (salvo por usuário)
  const [laborBillPeriod, setLaborBillPeriod] = useState(() => {
    // Carregar do localStorage por usuário
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = localStorage.getItem(`labor_bill_period_${user?.username || 'default'}`);
      return saved || '60'; // Padrão: 60 dias (2 meses) se nunca escolheu
    }
    return '60';
  });

  // View Labor Bill Modal
  const [viewBillModalOpen, setViewBillModalOpen] = useState(false);
  const [viewBillData, setViewBillData] = useState(null);

  // Comparar com Labor Bill ANTIGO (formulário editável, só p/ comparar)
  const [showOldCompareModal, setShowOldCompareModal] = useState(false);
  const [compareBill, setCompareBill] = useState(null);

  // Admin - Users
  const [adminUsers, setAdminUsers] = useState([]);
  const [minNativeVersion, setMinNativeVersion] = useState('');
  const [minNativeVersionInput, setMinNativeVersionInput] = useState('');
  const [updateNotice, setUpdateNotice] = useState(null); // {current, min, platform}
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminCreate, setAdminCreate] = useState({ username: "", password: "", role: "user", first_name: "", last_name: "", phone: "", address: "" });
  const [adminCreateOpen, setAdminCreateOpen] = useState(false);
  const [adminEdit, setAdminEdit] = useState(null); // { username,... }
  const [adminEditOpen, setAdminEditOpen] = useState(false);
  const [adminEditData, setAdminEditData] = useState({ full_name: "", first_name: "", last_name: "", phone: "", address: "", role: "user", is_active: true });

  // Admin - SubTabs and PM Data
  const [adminSubTab, setAdminSubTab] = useState('hub'); // 'hub' (painel) | 'usuarios' | 'dados' | 'sistema' | ...
  const pmOptions = ['ADMIN', ...Array.from({ length: 10 }, (_, i) => `PM${i + 1}`), 'Johnny']; // ADMIN + PM1-PM10 + Personal Users
  const emailScopeOptions = ['FOM', ...pmOptions]; // FOM (Supervisor) scope + PMs — usado só na aba de Emails
  const scopeLabel = (v) => (v === 'FOM' ? 'FOM (Supervisor)' : v);
  const [selectedPM, setSelectedPM] = useState('ADMIN');
  const [pmSearch, setPmSearch] = useState('');
  const [adminPMDrafts, setAdminPMDrafts] = useState([]);
  const [adminPMHistory, setAdminPMHistory] = useState([]);
  const [adminPMTrash, setAdminPMTrash] = useState([]);
  const [adminPMLoading, setAdminPMLoading] = useState(false);
  const [adminPMEmailLogs, setAdminPMEmailLogs] = useState([]);
  
  // Admin > Push Notifications state
  const [pushNotificationConfigs, setPushNotificationConfigs] = useState([]);
  const [pushNotificationHistory, setPushNotificationHistory] = useState([]);
  const [pushNotificationStatus, setPushNotificationStatus] = useState({});
  const [pushNotificationLoading, setPushNotificationLoading] = useState(false);
  const [manualNotificationForm, setManualNotificationForm] = useState({
    title: '',
    message: '',
    recipients: 'all'
  });
  
  // Admin > Suporte state
  const [adminTickets, setAdminTickets] = useState([]);
  const [adminTicketsLoading, setAdminTicketsLoading] = useState(false);
  const [adminTicketFilter, setAdminTicketFilter] = useState({ pm: 'PM1', status: '' });
  const [adminTicketView, setAdminTicketView] = useState(null); // ticket detalhado
  const [adminTicketReply, setAdminTicketReply] = useState('');
  const [adminTicketFiles, setAdminTicketFiles] = useState([]);

  // Admin > File Manager state
  const [fileManagerCurrentPath, setFileManagerCurrentPath] = useState('/'); // Caminho atual
  const [fileManagerItems, setFileManagerItems] = useState([]); // Arquivos e pastas no caminho atual
  const [fileManagerViewMode, setFileManagerViewMode] = useState('list'); // list, grid
  const [fileManagerSelectedItems, setFileManagerSelectedItems] = useState([]); // IDs selecionados
  const [fileManagerLoading, setFileManagerLoading] = useState(false);
  const [fileManagerSearchQuery, setFileManagerSearchQuery] = useState('');
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showShareLinkModal, setShowShareLinkModal] = useState(false);
  const [fileManagerClipboard, setFileManagerClipboard] = useState({ items: [], action: '' }); // copy/cut
  const [fileManagerUploadingFiles, setFileManagerUploadingFiles] = useState([]);
  const [fileManagerUploadProgress, setFileManagerUploadProgress] = useState({});
  const [fileManagerNewFolderName, setFileManagerNewFolderName] = useState('');
  const [fileManagerRenameItemId, setFileManagerRenameItemId] = useState(null);
  const [fileManagerRenameNewName, setFileManagerRenameNewName] = useState('');
  const [shareLinkItem, setShareLinkItem] = useState(null);
  const [generatedShareLink, setGeneratedShareLink] = useState('');

  // Helper Control state
  const [helperTeams, setHelperTeams] = useState([]);
  const [helperControls, setHelperControls] = useState([]);
  const [helperLoading, setHelperLoading] = useState(false);
  const [showHelperTeamModal, setShowHelperTeamModal] = useState(false);
  const [showHelperControlModal, setShowHelperControlModal] = useState(false);
  const [showHelperExpenseModal, setShowHelperExpenseModal] = useState(false);
  const [editingHelperTeam, setEditingHelperTeam] = useState(null);
  const [selectedHelperControl, setSelectedHelperControl] = useState(null);
  const [helperTeamForm, setHelperTeamForm] = useState({
    lider_nome: '',
    lider_diaria: 0,
    helpers: [],
    equipe: 'PM1'
  });
  const [helperControlForm, setHelperControlForm] = useState({
    team_id: '',
    ano: new Date().getFullYear(),
    semana: 1,
    data_inicio: '',
    data_fim: ''
  });
  const [helperExpenseForm, setHelperExpenseForm] = useState({
    descricao: '',
    valor: 0
  });
  const [helperExpenseFile, setHelperExpenseFile] = useState(null);
  const [helperExpenseType, setHelperExpenseType] = useState('manual'); // 'manual' ou 'ai'
  const [helperViewMode, setHelperViewMode] = useState('controls'); // 'teams', 'controls', 'monthly' - INICIA EM CONTROLES
  const [showTeamsConfigModal, setShowTeamsConfigModal] = useState(false); // Modal de configuração de equipes
  const [showMonthlyViewModal, setShowMonthlyViewModal] = useState(false); // Modal de visão mensal
  const [showTeamsModal, setShowTeamsModal] = useState(false); // Modal de configuração de equipes
  const [showMonthlyModal, setShowMonthlyModal] = useState(false); // Modal de visão mensal
  const [helperMonthFilter, setHelperMonthFilter] = useState({
    ano: new Date().getFullYear(),
    mes: new Date().getMonth() + 1
  });
  
  // Novos estados para Visão Mensal Avançada
  const [monthlyViewPeriodType, setMonthlyViewPeriodType] = useState('month'); // '4weeks', '8weeks', '12weeks', 'month', 'custom'
  const [monthlyViewCustomStart, setMonthlyViewCustomStart] = useState('');
  const [monthlyViewCustomEnd, setMonthlyViewCustomEnd] = useState('');
  const [monthlyViewTeamFilter, setMonthlyViewTeamFilter] = useState('all');
  const [monthlyViewLayout, setMonthlyViewLayout] = useState('cards'); // 'cards', 'table', 'dashboard'
  const [advancedMonthlyData, setAdvancedMonthlyData] = useState(null);
  
  // Helper Financial Report Modal
  const [showHelperFinancialReport, setShowHelperFinancialReport] = useState(false);
  const [selectedHelperTeamForReport, setSelectedHelperTeamForReport] = useState(null);
  const [loadingAdvancedReport, setLoadingAdvancedReport] = useState(false);
  
  // Estados para Waiver (Termo de Responsabilidade)
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [currentWaiverPM, setCurrentWaiverPM] = useState(null);
  const [waiverData, setWaiverData] = useState(null);
  const [waiverSettings, setWaiverSettings] = useState(null);
  const [waiverMode, setWaiverMode] = useState('admin'); // 'admin' ou 'pm'
  const [pendingWaivers, setPendingWaivers] = useState([]);
  const [currentWaiverToSign, setCurrentWaiverToSign] = useState(null);
  const [showWaiverSettingsModal, setShowWaiverSettingsModal] = useState(false);


  // Admin > Emails state
  const [adminPMRecipients, setAdminPMRecipients] = useState([]);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [adminNewRecipient, setAdminNewRecipient] = useState({ name: '', email: '' });
  const [adminBulkRecipient, setAdminBulkRecipient] = useState({ name: '', email: '', pms: [] });
  // Admin > Importar state
  const [importFile, setImportFile] = useState(null);
  const [importFiles, setImportFiles] = useState([]);
  const [pdfImportMode, setPdfImportMode] = useState('single'); // 'single' or 'multiple'
  const [importPreviewData, setImportPreviewData] = useState(null);
  const [importMultiplePreviewData, setImportMultiplePreviewData] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importCommitLoading, setImportCommitLoading] = useState(false);
  const [importPM, setImportPM] = useState('PM1');
  const [importEditData, setImportEditData] = useState(null);
  const [importMultipleEditData, setImportMultipleEditData] = useState([]);
  const [importAutoCommitResults, setImportAutoCommitResults] = useState(null);
  
  // Estado para edição individual de PDFs múltiplos
  const [editingMultiplePdfIndex, setEditingMultiplePdfIndex] = useState(null);
  const [showMultiplePdfEditModal, setShowMultiplePdfEditModal] = useState(false);
  
  // Estado para seleção de itens da lista
  const [showItemSelectionModal, setShowItemSelectionModal] = useState(false);
  const [itemSelectionMode, setItemSelectionMode] = useState('main'); // 'main' or 'extra'
  const [availableItems, setAvailableItems] = useState([]);
  const [itemSearchTerm, setItemSearchTerm] = useState(''); // Estado para busca

  // Weekly Report Summary State
  const [weeklyReportSummary, setWeeklyReportSummary] = useState({
    weeklyData: [], // Array of weeks, each with totalJobs, totalLabor, totalFinal, lucro (admin), totalExpenses, weekEnding
    reportsCount: 0,
    availableMonths: [] // Meses disponíveis
  });
  const [weeklyReportLoading, setWeeklyReportLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(''); // Será definido dinamicamente
  
  // Monthly/Yearly Summary
  const [monthlyYearlySummary, setMonthlyYearlySummary] = useState({
    month: { total_jobs: 0, total_labor: 0, total_final: 0, lucro: 0, total_expenses: 0 },
    year: { total_jobs: 0, total_labor: 0, total_final: 0, lucro: 0, total_expenses: 0 }
  });
  const [selectedMonthForSummary, setSelectedMonthForSummary] = useState(''); // Mês selecionado para resumo mensal/anual
  
  // Estados para expandir/colapsar cards dos usuários PM
  const [weeklyReportExpanded, setWeeklyReportExpanded] = useState(false);
  const [monthlyYearlyExpanded, setMonthlyYearlyExpanded] = useState(false);
  const [paymentEstimateExpanded, setPaymentEstimateExpanded] = useState(false); // Inicia minimizado
  
  // Estados para drag and drop de cards com grid dinâmica
  const [draggedCardId, setDraggedCardId] = useState(null);
  const [dragOverCardId, setDragOverCardId] = useState(null);
  const [cardOrder, setCardOrder] = useState([
    'info-warehouse',
    'weekly-report', 
    'monthly-yearly',
    'labor-bills',
    'email-history',
    'tickets'
  ]);
  
  // Histórico de estados para desfazer
  const [cardHistoryStack, setCardHistoryStack] = useState([]);
  const [cardSizes, setCardSizes] = useState({});

  // Funções para drag and drop com grid dinâmica
  const saveCardState = () => {
    const currentState = {
      order: [...cardOrder],
      sizes: { ...cardSizes },
      timestamp: Date.now()
    };
    
    setCardHistoryStack(prev => [...prev.slice(-9), currentState]);
  };

  const undoCardChanges = () => {
    if (cardHistoryStack.length === 0) {
      toast.info('Nenhuma alteração para desfazer');
      return;
    }
    
    const previousState = cardHistoryStack[cardHistoryStack.length - 1];
    setCardOrder(previousState.order);
    setCardSizes(previousState.sizes);
    setCardHistoryStack(prev => prev.slice(0, -1));
    
    toast.success('Alteração desfeita!');
  };

  const handleCardDragStart = (e, cardId) => {
    console.log(`🚀 Starting drag: ${cardId}`);
    console.log(`📋 Current order: ${cardOrder.join(' → ')}`);
    
    saveCardState(); // Salvar estado antes de começar
    setDraggedCardId(cardId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', cardId);
    
    // Adicionar classe visual durante drag
    setTimeout(() => {
      e.target.style.opacity = '0.7';
      e.target.style.transform = 'rotate(2deg) scale(1.02)';
      e.target.style.zIndex = '1000';
      e.target.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
    }, 0);
  };

  const handleCardDragEnd = (e) => {
    // Reset visual
    e.target.style.opacity = '1';
    e.target.style.transform = 'none';
    e.target.style.zIndex = 'auto';
    e.target.style.boxShadow = '';
    
    setDraggedCardId(null);
    setDragOverCardId(null);
  };

  const handleCardDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleCardDragEnter = (e, cardId) => {
    e.preventDefault();
    if (draggedCardId && draggedCardId !== cardId && cardId !== dragOverCardId) {
      console.log(`🔄 Dragging ${draggedCardId} over ${cardId} - reorganizing...`);
      setDragOverCardId(cardId);
      
      // Reorganizar cards IMEDIATAMENTE
      const currentOrder = [...cardOrder];
      const draggedIndex = currentOrder.indexOf(draggedCardId);
      const targetIndex = currentOrder.indexOf(cardId);
      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        // Remover card da posição atual
        const [draggedCard] = currentOrder.splice(draggedIndex, 1);
        // Inserir na nova posição
        currentOrder.splice(targetIndex, 0, draggedCard);
        
        console.log(`📐 New order: ${currentOrder.join(' → ')}`);
        setCardOrder(currentOrder);
      }
    }
  };

  const handleCardDrop = (e, targetCardId) => {
    e.preventDefault();
    
    console.log(`✅ Drop completed: ${draggedCardId} → ${targetCardId}`);
    console.log(`📋 Final order: ${cardOrder.join(' → ')}`);
    
    // Reorganização já foi feita no dragEnter - apenas finalizar
    setDraggedCardId(null);
    setDragOverCardId(null);
    toast.success(`Card reposicionado! Nova ordem salva.`);
  };

  // Helper para obter CSS order baseado na posição atual
  const getCardOrderClass = (cardId) => {
    const index = cardOrder.indexOf(cardId);
    return index >= 0 ? `card-order-${index}` : '';
  };

  // Renderizar cards dinamicamente baseado na ordem
  const getDynamicCardElements = () => {
    const elements = {};
    
    // Info Warehouse (ADMIN)
    elements['info-warehouse'] = (
      <div 
        key="info-warehouse"
        className={`bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-200 rounded-lg p-4 resizable-card ${dragOverCardId === 'info-warehouse' ? 'drag-over' : ''}`}
        draggable="true"
        onDragStart={(e) => handleCardDragStart(e, 'info-warehouse')}
        onDragEnd={handleCardDragEnd}
        onDragOver={(e) => handleCardDragOver(e)}
        onDragEnter={(e) => handleCardDragEnter(e, 'info-warehouse')}
        onDrop={(e) => handleCardDrop(e, 'info-warehouse')}
      >
        <div className="resize-icon" title="Arraste para redimensionar"></div>
        
        {/* Conteúdo do Info Warehouse - Admin */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">📅</span>
            <h4 className="font-bold text-blue-800">
              {user?.role === 'admin' ? 
                (language === 'pt-BR' ? 'Trabalhos Diários - Administração' : 'Daily Work - Administration') :
                (language === 'pt-BR' ? 'Info Warehouse' : 'Info Warehouse')
              }
            </h4>
          </div>
          {user?.role === 'admin' && (
            <div className="flex gap-2">
              <Button
                onClick={() => setShowExcelDataPasteModal(true)}
                className="bg-green-600 hover:bg-green-700"
                size="sm"
              >
                📋 Colar Dados Excel
              </Button>
            </div>
          )}
        </div>
        {/* TODO: Adicionar resto do conteúdo aqui */}
      </div>
    );
    
    return elements;
  };

  // Renderizar cards na ordem dinâmica
  const renderCardsInOrder = () => {
    const cardElements = getDynamicCardElements();
    
    // Renderizar cards na ordem definida por cardOrder
    return cardOrder.map(cardId => cardElements[cardId]).filter(Boolean);
  };

  // Renderizar cards na ordem correta
  const renderCardsByOrder = (cardElements) => {
    return cardOrder.map(cardId => cardElements[cardId]).filter(Boolean);
  };

  // Empty Trash Modal
  const [emptyTrashOpen, setEmptyTrashOpen] = useState(false);
  const [emptyTrashLoading, setEmptyTrashLoading] = useState(false);
  const [emptyTrashConfirmText, setEmptyTrashConfirmText] = useState('');

  // Save to Database State
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveReportLoading, setSaveReportLoading] = useState(false);

  // Conflict Modal State
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictData, setConflictData] = useState(null);

  // Add Item Modal for Import
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [addItemType, setAddItemType] = useState('regular'); // 'regular' or 'extra'

  // Activity Reports
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityStats, setActivityStats] = useState(null);
  const [activityFilters, setActivityFilters] = useState({
    user: '',
    action: '',
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [activityLoading, setActivityLoading] = useState(false);
  const [migratingNames, setMigratingNames] = useState(false);
  const [tzMigLoading, setTzMigLoading] = useState(false);
  const [tzMigResult, setTzMigResult] = useState(null);
  const [migrationResult, setMigrationResult] = useState(null);
  const [migrationStep, setMigrationStep] = useState('Iniciando...');

  // Admin Tickets (using existing states)
  const [ticketStats, setTicketStats] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState('');
  const [ticketFiles, setTicketFiles] = useState([]);

  // Admin Notifications (copiado de tickets)
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [adminNotificationView, setAdminNotificationView] = useState(null);
  const [adminNotificationForm, setAdminNotificationForm] = useState({
    title: '',
    message: '',
    targetUser: 'ALL'
  });
  
  // Histórico de Notificações - Estados para filtros e paginação
  const [notificationHistoryFilter, setNotificationHistoryFilter] = useState({
    pm: '',
    status: '',
    tipo: '',
    dateFrom: '',
    dateTo: ''
  });
  const [notificationHistoryPage, setNotificationHistoryPage] = useState(1);
  const [notificationHistoryTotal, setNotificationHistoryTotal] = useState(0);
  const [notifPage, setNotifPage] = useState(1);
  const [notifTotalPages, setNotifTotalPages] = useState(1);
  const [notifPageSize, setNotifPageSize] = useState(50);
  const [laborBillsOnly, setLaborBillsOnly] = useState(false);
  const [laborBillTab, setLaborBillTab] = useState('created'); // 'created' | 'sent'
  const [laborBillCounts, setLaborBillCounts] = useState({ created: 0, sent: 0 });
  const [supervisorOnly, setSupervisorOnly] = useState(false);
  const [supervisorTab, setSupervisorTab] = useState('all'); // 'all' | 'changed' | 'unchanged'
  const [supervisorCounts, setSupervisorCounts] = useState({ total: 0, changed: 0, unchanged: 0 });

  // User Notifications (copiado de tickets)  
  const [userNotifications, setUserNotifications] = useState([]);
  const [notificationView, setNotificationView] = useState(null);
  const [notificationQueue, setNotificationQueue] = useState([]); // Fila de notificações

  // Sino de Notificações (histórico)
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [allUsers, setAllUsers] = useState([]); // Lista dinâmica de usuários
  
  // Upload de vídeos de splash
  const [newVideoForm, setNewVideoForm] = useState({ name: '', file: null });
  const [videoUploading, setVideoUploading] = useState(false);
  const [videosList, setVideosList] = useState([]);
  const [previewingVideo, setPreviewingVideo] = useState(null); // Para preview modal
  
  // Quick ticket modal (Home) - agora com suporte a anexos
  const [showQuickTicketModal, setShowQuickTicketModal] = useState(false);
  const [quickTicketForm, setQuickTicketForm] = useState({ title: '', message: '' });
  const [quickTicketFiles, setQuickTicketFiles] = useState([]); // Anexos do Quick Ticket

  // PDF Viewer Modal States
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfModalData, setPdfModalData] = useState({ url: '', title: '' });
  const [pdfLoading, setPdfLoading] = useState(false);
  
  // Helper para abrir PDF com loading
  const openPdfWithLoading = async (data) => {
    try {
      setPdfLoading(true);
      
      // Se a URL é do backend (/api/files), baixar com autenticação
      if (data.url && (data.url.includes('/api/files/') || data.url.includes('/api/uploaded-files/'))) {
        const response = await axios.get(data.url, {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
        });
        
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        setPdfModalData({
          ...data,
          url: blobUrl
        });
      } else {
        // URL externa ou blob, usar diretamente
        setPdfModalData(data);
      }
      
      setShowPDFModal(true);
      setPdfLoading(false);
    } catch (error) {
      console.error('Erro ao carregar PDF:', error);
      toast.error('Erro ao carregar PDF');
      setPdfLoading(false);
    }
  };

  // Service Notes Modal States
  const [showServiceNotesModal, setShowServiceNotesModal] = useState(false);
  const [serviceNotesData, setServiceNotesData] = useState({ notes: '', title: '' });


  // Image Viewer Modal States
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageModalData, setImageModalData] = useState({ url: '', title: '' });

  // Office Viewer Modal States
  const [showOfficeModal, setShowOfficeModal] = useState(false);
  const [officeModalData, setOfficeModalData] = useState({ url: '', fileName: '' });

  // Video Viewer Modal States
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoModalData, setVideoModalData] = useState({ url: '', title: '' });

  // File Manager - Loading Progress State
  const [filePreviewLoading, setFilePreviewLoading] = useState(false);
  const [filePreviewProgress, setFilePreviewProgress] = useState(0);
  const [filePreviewName, setFilePreviewName] = useState('');

  // File Manager - Folder Icon Modal States
  const [showFolderIconModal, setShowFolderIconModal] = useState(false);
  const [selectedFolderForIcon, setSelectedFolderForIcon] = useState(null);
  const [selectedIconPreview, setSelectedIconPreview] = useState('');

  // File Manager - Properties Modal States
  const [showPropertiesModal, setShowPropertiesModal] = useState(false);
  const [selectedItemForProperties, setSelectedItemForProperties] = useState(null);

  // File Manager - Action Menu State (for [...] button)
  const [showActionMenu, setShowActionMenu] = useState(null); // ID do item com menu aberto
  
  // File Manager - Top Menu Bar State
  const [openTopMenu, setOpenTopMenu] = useState(null); // 'arquivo', 'editar', 'visualizar', 'ferramentas' ou null

  // Backup Progress State
  const [backupProgress, setBackupProgress] = useState(null); // {is_running, progress, current_step}
  const backupPollingIntervalRef = useRef(null); // useRef instead of useState to avoid closure bug

  // Restore Progress State
  const [restoreProgress, setRestoreProgress] = useState(null);
  const restorePollingIntervalRef = useRef(null); // useRef instead of useState to avoid closure bug

  // Backup Restore State
  const [availableBackups, setAvailableBackups] = useState([]);
  const [selectedBackupDate, setSelectedBackupDate] = useState('');
  
  // Dropbox Token Update Modal State
  const [showDropboxTokenModal, setShowDropboxTokenModal] = useState(false);
  const [newDropboxToken, setNewDropboxToken] = useState('');
  const [isUpdatingToken, setIsUpdatingToken] = useState(false);
  
  // Dropbox OAuth Setup Modal State
  const [showDropboxOAuthModal, setShowDropboxOAuthModal] = useState(false);
  const [dropboxAppKey, setDropboxAppKey] = useState('');
  const [dropboxAppSecret, setDropboxAppSecret] = useState('');
  const [dropboxAuthUrl, setDropboxAuthUrl] = useState('');
  const [dropboxAuthCode, setDropboxAuthCode] = useState('');
  const [oauthStep, setOauthStep] = useState(1); // 1: credentials, 2: authorize, 3: code
  const [oauthLoading, setOauthLoading] = useState(false);
  
  const [selectedBackupFile, setSelectedBackupFile] = useState('');
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreAllUsers, setRestoreAllUsers] = useState(true);
  const [selectedUsersForRestore, setSelectedUsersForRestore] = useState([]);
  
  // Backup Settings State (editable)
  const [backupSettings, setBackupSettings] = useState({
    backup_hour: '12',
    backup_minute: '0',
    backup_period: 'AM',
    retention_days: '1095',
    backup_frequency: 'daily',
    backup_timezone: 'America/New_York'
  });
  const [isEditingBackupSettings, setIsEditingBackupSettings] = useState(false);
  const [isSavingBackupSettings, setIsSavingBackupSettings] = useState(false);
  
  // Backup History State
  const [backupHistory, setBackupHistory] = useState([]);
  const [loadingBackupHistory, setLoadingBackupHistory] = useState(false);
  
  // Email Preview Modal State
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);
  
  // Email Image Upload Modal State
  const [showEmailImageUploadModal, setShowEmailImageUploadModal] = useState(false);
  const [emailImageFile, setEmailImageFile] = useState(null);
  const [emailImageUploading, setEmailImageUploading] = useState(false);
  const [uploadedEmailImageUrl, setUploadedEmailImageUrl] = useState('');
  
  // Email Editor Selection Ref
  const savedSelection = useRef(null);
  const emailEditorRef = useRef(null);

  // User Tickets Stats for Dashboard
  const [userTicketStats, setUserTicketStats] = useState(null);

  // Team Leader Management States
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [currentTeamLeader, setCurrentTeamLeader] = useState(null);
  const [teamLeaderLoading, setTeamLeaderLoading] = useState(false);
  const [showTeamLeaderViewModal, setShowTeamLeaderViewModal] = useState(false); // Modal Ver (read-only)
  const [showTeamLeaderEditModal, setShowTeamLeaderEditModal] = useState(false); // Modal Editar (com helpers)
  const [teamLeaderFormData, setTeamLeaderFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    date_of_birth: '',
    hire_date: '',
    position: '',
    driver_license_number: '',
    ssn_tax_id: ''
  });
  const [teamLeaderFiles, setTeamLeaderFiles] = useState({
    driver_license_file: null,
    lead_certificate_file: null,
    lead_firm_certificate_file: null,
    arbitration_file: null,
    contractor_file: null  // Independent Contractor Agreement
  });
  const [uploadingFiles, setUploadingFiles] = useState({});
  
  // Image URLs for thumbnails
  const [imageUrls, setImageUrls] = useState({
    driver_license_file: null,
    lead_certificate_file: null,
    lead_firm_certificate_file: null
  });
  
  // Helpers Management States (workers for each PM)
  const [helpers, setHelpers] = useState([]); // List of helpers for current PM
  const [helpersLoading, setHelpersLoading] = useState(false);
  const [showHelperForm, setShowHelperForm] = useState(false);
  const [editingHelper, setEditingHelper] = useState(null); // Helper being edited
  const [helperFormData, setHelperFormData] = useState({
    full_name: '',
    address: '',
    phone: '',
    hire_date: '',
    ssn_tax_id: '',
    speaks_english: false,
    is_team_translator: false
  });

  // Document Selection States (for Arbitration and Contractor)
  const [showArbitrationSelector, setShowArbitrationSelector] = useState(false);
  const [showContractorSelector, setShowContractorSelector] = useState(false);
  const [availableArbitrationDocs, setAvailableArbitrationDocs] = useState([]);
  const [availableContractorDocs, setAvailableContractorDocs] = useState([]);
  const [selectedArbitrationDoc, setSelectedArbitrationDoc] = useState(null);
  const [selectedContractorDoc, setSelectedContractorDoc] = useState(null);
  
  // Link Existing Documents to Helper States
  const [showLinkArbitrationModal, setShowLinkArbitrationModal] = useState(false);
  const [showLinkContractorModal, setShowLinkContractorModal] = useState(false);
  const [helperToLinkDoc, setHelperToLinkDoc] = useState(null); // {id, name}
  const [linkDocSearch, setLinkDocSearch] = useState('');
  const [linkingDocument, setLinkingDocument] = useState(false);
  
  // LSWP Formstack States
  const [showLSWPForm, setShowLSWPForm] = useState(false);
  const [lswpFormData, setLswpFormData] = useState({
    customer_first_name: '',
    customer_last_name: '',
    customer_email: '',
    job_address_line1: '',
    job_city: '',
    job_state: '',
    job_zip: '',
    num_windows: 0,
    num_doors: 0,
    signature_renovator: '',
    signature_customer: ''
  });
  const [showLSWPPreview, setShowLSWPPreview] = useState(false);
  const [lswpSubmitting, setLswpSubmitting] = useState(false);
  const renovatorSigPadRef = useRef(null);
  const customerSigPadRef = useRef(null);
  const docsButtonRef = useRef(null);
  const [hasRenovatorSig, setHasRenovatorSig] = useState(false);
  const [hasCustomerSig, setHasCustomerSig] = useState(false);

  // Team Leader Email States
  const [showTeamLeaderEmailModal, setShowTeamLeaderEmailModal] = useState(false);
  const [teamLeaderEmailData, setTeamLeaderEmailData] = useState({
    recipient_email: '',
    recipient_name: '',
    selected_documents: [], // Document types gerais do Team Leader
    selected_helpers: [], // IDs dos helpers selecionados
    helper_documents: {}, // {helper_id: {arbitration_id: "...", contractor_id: "..."}}
    message: ''
  });
  const [teamLeaderEmailLoading, setTeamLeaderEmailLoading] = useState(false);
  
  // Helper Document Selection States
  const [showHelperDocSelector, setShowHelperDocSelector] = useState(false);
  const [helperDocSelectorData, setHelperDocSelectorData] = useState({
    helper_id: null,
    helper_name: '',
    doc_type: null // 'arbitration' or 'contractor'
  });

  // Transfer Labor Bill States
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferBillId, setTransferBillId] = useState(null);
  const [transferBillData, setTransferBillData] = useState(null);
  const [transferTargetPM, setTransferTargetPM] = useState('PM1');
  const [transferLoading, setTransferLoading] = useState(false);

  // Order Number Validation States
  const [showOrderValidationModal, setShowOrderValidationModal] = useState(false);
  const [orderValidationData, setOrderValidationData] = useState({
    duplicateOrderNumber: '',
    existingBill: null,
    isService: false,
    pendingSave: false
  });

  // Manual Client Management States
  const [showManualClientModal, setShowManualClientModal] = useState(false);
  const [manualClientAction, setManualClientAction] = useState('add'); // 'add' or 'remove'
  const [manualClientData, setManualClientData] = useState({
    selectedReport: null,
    selectedTeam: 'PM1',
    selectedLaborBill: null,
    laborTotal: 0,
    finalValue: 0,
    expenses: 0,
    weekEnding: ''
  });
  const [availableLaborBills, setAvailableLaborBills] = useState([]);
  const [manualClientLoading, setManualClientLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientFilterTeam, setClientFilterTeam] = useState('ALL');

  // Edit Report States
  const [showEditReportModal, setShowEditReportModal] = useState(false);
  const [showReportBackchargeModal, setShowReportBackchargeModal] = useState(false);
  const [editReportData, setEditReportData] = useState({
    selectedReport: null,
    selectedEntry: null,
    editedValues: {
      customer: '',
      job_number: '',
      labor_total: 0,
      expenses: {},
      calculated_value: 0
    }
  });
  const [editReportLoading, setEditReportLoading] = useState(false);

  // Arbitration Files States
  const [arbitrationFiles, setArbitrationFiles] = useState([]);
  const [arbitrationLoading, setArbitrationLoading] = useState(false);
  const [showArbitrationForm, setShowArbitrationForm] = useState(false);
  const [showArbitrationEmailForm, setShowArbitrationEmailForm] = useState(false);
  const [showArbitrationViewer, setShowArbitrationViewer] = useState(false);
  const [showArbitrationImportModal, setShowArbitrationImportModal] = useState(false);
  const [showArbitrationSignModal, setShowArbitrationSignModal] = useState(false);
  const [showArbitrationEditModal, setShowArbitrationEditModal] = useState(false);
  const [arbitrationPdfUrl, setArbitrationPdfUrl] = useState(null);
  const [arbitrationPdfTitle, setArbitrationPdfTitle] = useState('');
  const [selectedArbitrationFileId, setSelectedArbitrationFileId] = useState(null);
  const [arbitrationImportFiles, setArbitrationImportFiles] = useState([]);
  const [arbitrationImportResults, setArbitrationImportResults] = useState([]);
  const [arbitrationFileToSign, setArbitrationFileToSign] = useState(null);
  const [arbitrationFileToEdit, setArbitrationFileToEdit] = useState(null);
  const [arbitrationSearch, setArbitrationSearch] = useState('');
  const [arbitrationFilterPM, setArbitrationFilterPM] = useState('ALL');
  const [arbitrationFilterStatus, setArbitrationFilterStatus] = useState('ALL');
  const [arbitrationSort, setArbitrationSort] = useState('date_desc');
  const [arbitrationEmailData, setArbitrationEmailData] = useState({
    supervisor_email: '',
    supervisor_name: '',
    selectedRecipients: [], // Array for multiple selection
    selectAll: false
  });
  
  // Multiple Arbitration Files Selection
  const [selectedArbitrationFiles, setSelectedArbitrationFiles] = useState([]);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [bulkEmailData, setBulkEmailData] = useState({
    selectedRecipients: [],
    selectAll: false
  });

  // Company Announcements States
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsExpanded, setAnnouncementsExpanded] = useState(true);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    priority: 'normal',
    expires_at: ''
  });
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [billAlerts, setBillAlerts] = useState([]);
  const [companyBillsHome, setCompanyBillsHome] = useState([]);
  const [companyAlertsHome, setCompanyAlertsHome] = useState([]);
  const [myCalendarAlerts, setMyCalendarAlerts] = useState([]);
  const [selectedBillDay, setSelectedBillDay] = useState(new Date().getDate());
  const [companyUnits, setCompanyUnits] = useState([]);
  const [homeBillUnitFilter, setHomeBillUnitFilter] = useState('all');
  const [homeBillViewFilter, setHomeBillViewFilter] = useState('all');
  const [homeBillViewItem, setHomeBillViewItem] = useState(null);
  const [homeBillMonth, setHomeBillMonth] = useState(new Date().getMonth());
  const [homeBillYear, setHomeBillYear] = useState(new Date().getFullYear());
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [sharedFile, setSharedFile] = useState(null);
  const [sharedFileType, setSharedFileType] = useState('');

  // Personal Documents States (Johnny)
  const [personalFolders, setPersonalFolders] = useState([]);
  const [personalDocuments, setPersonalDocuments] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  
  // Debug code removed - system working properly
  const [arbitrationFormData, setArbitrationFormData] = useState({
    helper_name: '',
    signature: '', // Will store base64 image data
    date: new Date().toISOString().split('T')[0]
  });
  const [hasSignature, setHasSignature] = useState(false); // Track if signature is drawn
  const signaturePadRef = useRef(null);
  
  // Track which helper is creating Arbitration/Contractor for auto-linking
  const [helperCreatingDocFor, setHelperCreatingDocFor] = useState(null); // {helper_id, helper_name, doc_type}
  
  // Independent Contractor States (duplicate of arbitration)
  const [contractorFiles, setContractorFiles] = useState([]);
  const [contractorLoading, setContractorLoading] = useState(false);
  const [showContractorForm, setShowContractorForm] = useState(false);
  const [showContractorEmailForm, setShowContractorEmailForm] = useState(false);
  const [showContractorViewer, setShowContractorViewer] = useState(false);
  const [showContractorSignModal, setShowContractorSignModal] = useState(false);
  const [showContractorImportModal, setShowContractorImportModal] = useState(false);
  const [contractorImportFiles, setContractorImportFiles] = useState([]);
  const [contractorImportResults, setContractorImportResults] = useState([]);
  const [contractorPdfUrl, setContractorPdfUrl] = useState(null);
  const [contractorPdfTitle, setContractorPdfTitle] = useState('');
  const [selectedContractorFileId, setSelectedContractorFileId] = useState(null);
  const [contractorFileToSign, setContractorFileToSign] = useState(null);
  const [contractorSearch, setContractorSearch] = useState('');
  const [contractorFilterPM, setContractorFilterPM] = useState('ALL');
  const [contractorFilterStatus, setContractorFilterStatus] = useState('ALL');
  const [contractorSort, setContractorSort] = useState('date_desc');

  // Lista de contratos Independent Contractor com filtros/busca/ordenação aplicados
  const filteredContractorFiles = useMemo(() => {
    const files = contractorFiles.filter(file => {
      const matchesSearch = !contractorSearch ||
        (file.helper_name || '').toLowerCase().includes(contractorSearch.toLowerCase());
      const matchesPM = contractorFilterPM === 'ALL' || file.pm_user === contractorFilterPM;
      const matchesStatus = contractorFilterStatus === 'ALL' ||
        (contractorFilterStatus === 'SIGNED' && file.signature && file.signature.startsWith('data:image')) ||
        (contractorFilterStatus === 'UNSIGNED' && (!file.signature || !file.signature.startsWith('data:image')));
      return matchesSearch && matchesPM && matchesStatus;
    });
    files.sort((a, b) => {
      switch (contractorSort) {
        case 'date_desc': return new Date(b.date) - new Date(a.date);
        case 'date_asc': return new Date(a.date) - new Date(b.date);
        case 'name_asc': return (a.helper_name || '').localeCompare(b.helper_name || '');
        case 'name_desc': return (b.helper_name || '').localeCompare(a.helper_name || '');
        case 'pm_asc': return (a.pm_user || '').localeCompare(b.pm_user || '');
        default: return 0;
      }
    });
    return files;
  }, [contractorFiles, contractorSearch, contractorFilterPM, contractorFilterStatus, contractorSort]);
  const [contractorEmailData, setContractorEmailData] = useState({
    supervisor_email: '',
    supervisor_name: '',
    selectedRecipients: [],
    selectAll: false
  });
  const [contractorFormData, setContractorFormData] = useState({
    helper_name: '',
    title: 'Helper',
    signature: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [selectedContractorFiles, setSelectedContractorFiles] = useState([]);
  const [showContractorEditModal, setShowContractorEditModal] = useState(false);
  const [contractorFileToEdit, setContractorFileToEdit] = useState(null);

  // Installation Docs States
  const [installationFolders, setInstallationFolders] = useState([]);
  const [installationFiles, setInstallationFiles] = useState([]);
  const [currentInstallationFolderId, setCurrentInstallationFolderId] = useState(null);
  const [installationFolderPath, setInstallationFolderPath] = useState([]); // Navigation history
  const [selectedInstallationFiles, setSelectedInstallationFiles] = useState([]);
  const [showNewInstallationFolderModal, setShowNewInstallationFolderModal] = useState(false);
  const [showInstallationUploadModal, setShowInstallationUploadModal] = useState(false);
  const [showInstallationViewer, setShowInstallationViewer] = useState(false);
  const [currentInstallationFile, setCurrentInstallationFile] = useState(null);
  const [newInstallationFolderName, setNewInstallationFolderName] = useState('');
  const [installationUploadFiles, setInstallationUploadFiles] = useState([]);
  const [installationLoading, setInstallationLoading] = useState(false);
  const [installationPdfLoading, setInstallationPdfLoading] = useState(null); // Track which file is loading

  // Daily Work States
  const [dailyWork, setDailyWork] = useState([]);
  const [dailyWorkSummary, setDailyWorkSummary] = useState(null);
  const [showDailyWorkUpload, setShowDailyWorkUpload] = useState(false);
  const [dailyWorkFile, setDailyWorkFile] = useState(null);
  
  // Schedule Calendar (NOVO - separado do Daily Work)
  const [scheduleJobs, setScheduleJobs] = useState([]); // Todos jobs do mês
  const [showScheduleMapModal, setShowScheduleMapModal] = useState(false);
  const [compareMapTeams, setCompareMapTeams] = useState([]);
  const [compareMapRange, setCompareMapRange] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date()); // Data selecionada
  const [selectedDayJobs, setSelectedDayJobs] = useState([]); // Jobs do dia selecionado
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [blockedDays, setBlockedDays] = useState([]); // Dias bloqueados
  const [viewAsTeam, setViewAsTeam] = useState('ALL'); // Filtro de visualização (ADMIN)
  const [batchAiProgress, setBatchAiProgress] = useState({ running: false }); // Batch AI progress
  const [showBlockDayModal, setShowBlockDayModal] = useState(false); // Modal de bloqueio
  const [showAddWorkModal, setShowAddWorkModal] = useState(false);
  const [selectedWork, setSelectedWork] = useState(null); // Para edição
  const [measureSource, setMeasureSource] = useState(null); // Medição que vira instalação (pré-preenche o modal)
  const [projectSource, setProjectSource] = useState(null); // Projeto vendido (Portal de Vendas) que vira instalação
  const [draggedJob, setDraggedJob] = useState(null); // Drag-to-merge: trabalho arrastado
  const [dragOverJobId, setDragOverJobId] = useState(null); // Drag-to-merge: card destacado no hover
  const [mergeConfirm, setMergeConfirm] = useState(null); // { source, target } para confirmar merge
  const [undoMerge, setUndoMerge] = useState(null); // { undoId } banner de desfazer após merge
  const undoTimerRef = useRef(null);
  const [dailyWorkLoading, setDailyWorkLoading] = useState(false);
  const [dailyWorkConfig, setDailyWorkConfig] = useState(null);
  // Estado para modal de seleção de data do Daily Work
  const [showDatePickerModal, setShowDatePickerModal] = useState(false);
  const [tempSelectedDate, setTempSelectedDate] = useState('');
  const [dailyWorkDateFilter, setDailyWorkDateFilter] = useState('');
  
  // Estados para Excel Online - Copy/Paste com verificação de duplicatas
  const [showExcelDataPasteModal, setShowExcelDataPasteModal] = useState(false);
  const [excelPastedData, setExcelPastedData] = useState('');
  const [excelWorkDate, setExcelWorkDate] = useState(''); // Data dos trabalhos
  
  // Estados para conflito de datas
  const [showDateConflictModal, setShowDateConflictModal] = useState(false);
  const [dateConflictInfo, setDateConflictInfo] = useState(null);
  const [pendingExcelData, setPendingExcelData] = useState(null);
  
  // Estados para verificação de duplicatas
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);


  const [pendingProcessData, setPendingProcessData] = useState(null);

  // Payroll System States
  const [payrollFile, setPayrollFile] = useState(null);
  const [payrollData, setPayrollData] = useState(null);
  const [backcharges, setBackcharges] = useState({}); // {teamName: [{valor, descricao, id}]}
  const [adicionais, setAdicionais] = useState({}); // {teamName: [{valor, descricao, id}]} — oposto do backcharge
  const [showBackchargeModal, setShowBackchargeModal] = useState(false);
  const [backchargeKind, setBackchargeKind] = useState('backcharge'); // 'backcharge' | 'adicional'
  const [backchargeTeam, setBackchargeTeam] = useState('');
  const [backchargeForm, setBackchargeForm] = useState({ valor: '', descricao: '' });
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [payrollWeek, setPayrollWeek] = useState(() => {
    // Pre-fill with next Friday
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 5 = Friday
    const daysUntilFriday = dayOfWeek <= 5 ? 5 - dayOfWeek : 7 - dayOfWeek + 5;
    const nextFriday = new Date(today);
    nextFriday.setDate(today.getDate() + daysUntilFriday);
    return nextFriday.toISOString().split('T')[0];
  });
  const [payrollReport, setPayrollReport] = useState(null);
  const [payrollReports, setPayrollReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [calculationResults, setCalculationResults] = useState(null);
  const [teamPercentages, setTeamPercentages] = useState([]);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamPercentage, setNewTeamPercentage] = useState('');
  const [payrollSubTab, setPayrollSubTab] = useState('upload');
  const [editingTeam, setEditingTeam] = useState(null);
  const [tempPercentage, setTempPercentage] = useState('');
  
  // Work Details States (apenas para visualização)
  const [showLaborBillDetails, setShowLaborBillDetails] = useState(false);
  const [selectedLaborBillDetails, setSelectedLaborBillDetails] = useState(null);
  const [editingLaborBillExpenses, setEditingLaborBillExpenses] = useState(false);

  // Manual Match States (nova implementação simples)
  const [showManualMatchModal, setShowManualMatchModal] = useState(false);
  const [currentUnmatchedEntry, setCurrentUnmatchedEntry] = useState(null);
  const [teamLaborBills, setTeamLaborBills] = useState([]);
  const [manualMatchLoading, setManualMatchLoading] = useState(false);
  const [payrollContext, setPayrollContext] = useState(null); // Track payroll context when editing bills
  
  // Estado para controlar exibição de labor bills no modal de match
  const [showAllLaborBillsInMatch, setShowAllLaborBillsInMatch] = useState(false); // Por padrão, mostrar apenas últimos 2 meses
  
  // Change Labor Bill States
  const [showChangeLaborBillModal, setShowChangeLaborBillModal] = useState(false);
  const [availableLaborBillsForChange, setAvailableLaborBillsForChange] = useState([]);
  const [changeLaborBillLoading, setChangeLaborBillLoading] = useState(false);
  
  // Estado para controlar exibição de labor bills no modal de trocar labor bill
  const [showAllLaborBillsInChange, setShowAllLaborBillsInChange] = useState(false); // Por padrão, mostrar apenas últimos 2 meses
  
  // Weekly Report States for PM users
  const [weeklyReportData, setWeeklyReportData] = useState(null);
  
  // Next Payment Estimate State (PM only)
  const [nextPaymentEstimate, setNextPaymentEstimate] = useState(null);
  const [currentWeekEstimate, setCurrentWeekEstimate] = useState(null);
  const [currentWeekExpanded, setCurrentWeekExpanded] = useState(false);
  const [expandedWeekPMs, setExpandedWeekPMs] = useState({});
  const [laborBillsCardExpanded, setLaborBillsCardExpanded] = useState(false);
  const [emailHistoryCardExpanded, setEmailHistoryCardExpanded] = useState(false);
  const [paymentEstimateLoading, setPaymentEstimateLoading] = useState(false);

  const [allPaymentEstimates, setAllPaymentEstimates] = useState(null);
  const [allPaymentEstimatesLoading, setAllPaymentEstimatesLoading] = useState(false);

  
  // State to track when to open modal after manual match
  // Team Division States
  const [showTeamDivisionModal, setShowTeamDivisionModal] = useState(false);
  const [currentEntryForDivision, setCurrentEntryForDivision] = useState(null);
  const [selectedTeamsForDivision, setSelectedTeamsForDivision] = useState([]);
  const [teamDivisionLoading, setTeamDivisionLoading] = useState(false);
  const [availableTeams, setAvailableTeams] = useState([]);


  // Confirm modals (Trash)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmDeleteBillId, setConfirmDeleteBillId] = useState(null);
  const [confirmDeleteText, setConfirmDeleteText] = useState('');
  const [adminConfirmDeleteOpen, setAdminConfirmDeleteOpen] = useState(false);
  const [adminDeleteTarget, setAdminDeleteTarget] = useState({ pm: null, id: null });
  const [adminConfirmDeleteText, setAdminConfirmDeleteText] = useState('');
  const [confirmDeleting, setConfirmDeleting] = useState(false);
  const [adminConfirmDeleting, setAdminConfirmDeleting] = useState(false);

  // Admin - Sistema (Pricing & Settings)
  const [pricingScope, setPricingScope] = useState('global'); // 'global' | 'pm'
  const [pricingPM, setPricingPM] = useState('PM1');
  const [pricingMap, setPricingMap] = useState({}); // { description: price }
  const [pdfSettings, setPdfSettings] = useState({ header_title: '' });
  const [emailDefaults, setEmailDefaults] = useState({ subject: '', body: '', body_html: '', bcc_sender: true });
  const [emailConfig, setEmailConfig] = useState({ sender_email: '', bcc_email: '' });
  const [settingsLoading, setSettingsLoading] = useState(false);
  // Crew auto-preenchimento (mapeamento PM -> Crew do PDF)
  const [crewOptions, setCrewOptions] = useState([]);
  const [crewPms, setCrewPms] = useState([]);
  const [crewMap, setCrewMap] = useState({});
  const [crewLoading, setCrewLoading] = useState(false);
  // FOM auto-preenchimento (mapeamento supervisor -> FOM Assigned + FOM Email)
  const [fomOptions, setFomOptions] = useState([]);
  const [fomSupervisors, setFomSupervisors] = useState([]);
  const [fomMap, setFomMap] = useState({});
  const [fomLoading, setFomLoading] = useState(false);
  // FOMs personalizados (Nome = FOM Assigned + E-mail) — add/editar/remover
  const [customFoms, setCustomFoms] = useState([]);
  const [customFomsLoading, setCustomFomsLoading] = useState(false);

  // Fluxo de supervisor no Salvar/Enviar do Labor Bill
  const [supervisorCtx, setSupervisorCtx] = useState({ detected_supervisor: '', detected: null, supervisors: [] });
  const [showSaveSupervisorModal, setShowSaveSupervisorModal] = useState(false);
  const [saveSupervisorMode, setSaveSupervisorMode] = useState('detected'); // 'detected' | 'choose'
  const [saveSupervisorChoice, setSaveSupervisorChoice] = useState('');
  const [pendingSupFields, setPendingSupFields] = useState(null);
  const [showEmailSupervisorModal, setShowEmailSupervisorModal] = useState(false);
  const [emailSupervisor, setEmailSupervisor] = useState({ name: '', email: '' });

  // ===== Preview banner (NUNCA mostrar em app Capacitor/TestFlight/App Store) =====
  useEffect(() => {
    // NUNCA mostrar se for app Capacitor (TestFlight ou App Store)
    if (window.Capacitor) {
      console.log('🚫 Capacitor app detectado - banner de preview desabilitado');
      return;
    }
    
    // Só mostrar em localhost web browser (desenvolvimento)
    const isPreviewEnv = typeof window !== 'undefined' && (/(^localhost$)|(^127\.)/.test(window.location.hostname) || String(window.location.port) === '3000');
    if (!isPreviewEnv) return;
    
    const el = document.createElement('div');
    el.setAttribute('id', 'preview-banner');
    el.setAttribute('role', 'note');
    el.setAttribute('aria-label', 'Ambiente de Teste');
    Object.assign(el.style, { position:'fixed', top:'8px', left:'8px', zIndex:9999, background:'#1e293b', color:'#fff', padding:'6px 10px', borderRadius:'8px', fontSize:'12px', boxShadow:'0 2px 8px rgba(0,0,0,0.25)' });
    el.textContent = 'Ambiente de Teste';
    document.body.appendChild(el);
    console.log('ℹ️ Preview banner mostrado (localhost web)');
    return () => { try { document.body.removeChild(el); } catch(_){} };
  }, []);

  // ===== Global loading overlay - ULTRA VISÍVEL E MODERNO =====
  useEffect(() => {
    const active = loading || payrollLoading || importLoading || backupLoading || settingsLoading || 
                   globalLoading || helpersLoading || teamLeaderLoading || arbitrationLoading || 
                   contractorLoading || installationLoading || dailyWorkLoading || lswpSubmitting || 
                   adminLoading || adminPMLoading || adminTicketsLoading;
    
    let overlay = document.getElementById('global-loading-overlay');
    
    if (active) {
      // Determinar mensagem baseada em qual loading está ativo
      let message = 'Carregando...';
      if (backupLoading) message = '📦 Gerando backup...';
      else if (importLoading) message = '📥 Importando dados...';
      else if (payrollLoading) message = '💰 Processando payroll...';
      else if (lswpSubmitting) message = '📋 Enviando para Formstack...';
      else if (helpersLoading) message = '👷 Carregando helpers...';
      else if (teamLeaderLoading) message = '👨‍💼 Carregando team leader...';
      else if (arbitrationLoading) message = '⚖️ Carregando arbitration...';
      else if (contractorLoading) message = '📋 Carregando contractor...';
      else if (installationLoading) message = '🔧 Carregando installation...';
      else if (dailyWorkLoading) message = '📅 Carregando daily work...';
      else if (adminLoading) message = '⚙️ Carregando admin data...';
      else if (settingsLoading) message = '⚙️ Salvando configurações...';
      else if (loading || globalLoading) message = loadingMessage || 'Carregando...';
      
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'global-loading-overlay';
        Object.assign(overlay.style, { 
          position: 'fixed', 
          inset: '0', 
          background: 'rgba(0, 0, 0, 0.6)', 
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          zIndex: '99999', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          transition: 'all 0.3s ease'
        });
        
        document.body.appendChild(overlay);
      }
      
      // Atualizar conteúdo com mensagem dinâmica
      overlay.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        ">
          <div style="
            width: 60px;
            height: 60px;
            border: 4px solid rgba(255, 255, 255, 0.2);
            border-top-color: #fff;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          "></div>
          <div style="
            color: #fff;
            font-size: 16px;
            font-weight: 500;
            letter-spacing: 0.5px;
            text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
          ">${message}</div>
        </div>
        <style>
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        </style>
      `;
    } else if (overlay) {
      try { document.body.removeChild(overlay); } catch(_){}
    }
    
    return () => {
      const el = document.getElementById('global-loading-overlay');
      if (el && !active) {
        try { document.body.removeChild(el); } catch(_){}
      }
    };
  }, [loading, payrollLoading, importLoading, backupLoading, settingsLoading, globalLoading, 
      helpersLoading, teamLeaderLoading, arbitrationLoading, contractorLoading, installationLoading, 
      dailyWorkLoading, lswpSubmitting, adminLoading, adminPMLoading, adminTicketsLoading, loadingMessage]);

  // ===== Mobile keyboard detection =====
  useEffect(() => {
    let initialViewportHeight = window.visualViewport?.height || window.innerHeight;
    
    const handleViewportChange = () => {
      const modal = document.querySelector('.keyboard-aware-scroll');
      if (!modal) return;
      
      const currentHeight = window.visualViewport?.height || window.innerHeight;
      const heightDifference = initialViewportHeight - currentHeight;
      
      // Teclado aberto se altura diminuiu mais de 100px (mais sensível para iPhone)
      if (heightDifference > 100) {
        console.log('🔤 Teclado iPhone detectado - altura do teclado:', heightDifference, 'px');
        
        // Mover modal exatamente o tamanho do teclado
        modal.style.setProperty('--keyboard-height', `${heightDifference}px`);
        modal.classList.add('keyboard-is-open');
      } else {
        console.log('🔤 Teclado iPhone fechado - centralizando modal');
        modal.style.removeProperty('--keyboard-height');
        modal.classList.remove('keyboard-is-open');
      }
    };
    
    // Detectar mudanças no viewport (quando teclado abre/fecha)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
    } else {
      // Fallback para browsers sem visualViewport
      window.addEventListener('resize', handleViewportChange);
    }
    
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      } else {
        window.removeEventListener('resize', handleViewportChange);
      }
    };
  }, []);

  // ===== Axios rate limit for generate-all-pdfs =====
  useEffect(() => {
    const id = axios.interceptors.request.use((config) => {
      try {
        if (config && config.method === 'post' && String(config.url||'').includes('/admin/payroll/generate-all-pdfs')) {
          const key = '__last_gen_all_pdfs__';
          const now = Date.now();
          const last = window[key] || 0;
          if (now - last < 10000) { // 10 segundos de throttling
            return Promise.reject({ response: { data: { detail: 'Aguarde alguns segundos antes de gerar novamente' } } });
          }
          window[key] = now;
        }
      } catch(_){}
      return config;
    });
    return () => { try { axios.interceptors.request.eject(id); } catch(_){} };
  }, []);

  // ===== CSV helpers =====
  const exportArrayToCSV = (rows, headers, filename) => {
    try {
      const csvRows = [];
      if (headers && headers.length) csvRows.push(headers.join(','));
      rows.forEach((r) => {
        const values = headers.map((h) => {
          const v = r[h] ?? '';
          const s = String(v).replace(/\"/g, '""');
          return `"${s}"`;
        });
        csvRows.push(values.join(','));
      });
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`CSV exportado: ${filename}`);
    } catch (e) { toast.error('Falha ao exportar CSV'); }
  };
  const exportPayrollReportsCSV = () => {
    const headers = ['id','week_ending','created_at','status','total_entries'];
    const rows = (payrollReports||[]).map((r)=>({
      id: r.id || r.report_id || '',
      week_ending: r.week_ending || r.month || '',
      created_at: r.created_at || '',
      status: r.status || '',
      total_entries: r.total_entries ?? ''
    }));
    exportArrayToCSV(rows, headers, `payroll_reports_${new Date().toISOString().slice(0,10)}.csv`);
  };
  const exportLaborBillsCSV = () => {
    const headers = ['job_name','order_number','install_date','total','paid','status'];
    const rows = (laborBills||[]).map((b)=>({
      job_name: b.job_name||'', order_number: b.order_number||'', install_date: b.install_date||'', total: b.grand_total ?? ((b.items||[]).reduce((s,it)=>s+(it.qty||0)*(it.unit_price||0),0)+(b.extra_items||[]).reduce((s,ex)=>s+(ex.qty||0)*(ex.unit_price||0),0)), paid: b.paid? 'yes':'no', status: b.status||'draft'
    }));
    exportArrayToCSV(rows, headers, `labor_bills_${new Date().toISOString().slice(0,10)}.csv`);
  };

  // ===== Pagination states (user bills) =====
  const [billsPage, setBillsPage] = useState(1);
  const BILLS_PER_PAGE = 10;
  const filteredUserBills = useMemo(() => {
    if (user?.role === 'admin') return laborBills;
    return laborBills.filter((b) => {
      const q = draftsSearch.trim().toLowerCase();
      if (!q) return true;
      return ((b.job_name || '').toLowerCase().includes(q) || (b.order_number || '').toLowerCase().includes(q));
    });
  }, [user?.role, laborBills, draftsSearch]);
  const totalBillsPages = useMemo(() => Math.max(1, Math.ceil((filteredUserBills.length || 0) / BILLS_PER_PAGE)), [filteredUserBills]);
  useEffect(() => { if (billsPage > totalBillsPages) setBillsPage(totalBillsPages); }, [totalBillsPages]);
  const paginatedUserBills = useMemo(() => {
    const start = (billsPage - 1) * BILLS_PER_PAGE;
    return filteredUserBills.slice(start, start + BILLS_PER_PAGE);
  }, [filteredUserBills, billsPage]);


  // Default bill form
  const getDefaultBillData = () => ({
    install_date: new Date().toISOString().split("T")[0],
    job_name: "",
    order_number: "",
    total_windows: 0,
    total_doors: 0,
    notes: "",
    additional_labor_cost: "",
    items: [],
    extra_items: [],
    schedule_job_id: null  // Vinculação com Schedule Job
  });
  const [formData, setFormData] = useState(getDefaultBillData());

  // Usar defaultItems memoizado para estabilidade
  const defaultItems = useMemo(() => getDefaultItems(), [leftColumnSubgroups, rightColumnSubgroups]);

  const buildDefaultPricingRows = useMemo(() => {
    const rows = [];
    leftColumnSubgroups.forEach((sg) => sg.items.forEach((it) => rows.push({ description: it.description, default_price: it.unit_price, price: it.unit_price })));
    rightColumnSubgroups.forEach((sg) => sg.items.forEach((it) => rows.push({ description: it.description, default_price: it.unit_price, price: it.unit_price })));
    return rows;
  }, []);

  const grandTotal = useMemo(() => {
    if (!currentBill?.items) return 0;
    const itemsTotal = currentBill.items.reduce((sum, item) => sum + (Number(item.qty) || 0) * (Number(item.unit_price) || 0), 0);
    const extrasTotal = (currentBill.extra_items || []).reduce((sum, extra) => sum + (Number(extra.qty) || 0) * (Number(extra.unit_price) || 0), 0);
    const additional = parseFloat(String(formData.additional_labor_cost ?? '').replace(/[^0-9.\-]/g, '')) || 0;
    return itemsTotal + extrasTotal + additional;
  }, [currentBill?.items, currentBill?.extra_items, formData.additional_labor_cost]);

  // Bootstrap session
  useEffect(() => {
    const bootstrap = async () => {
      const token = sessionStorage.getItem("access_token");
      const userInfo = sessionStorage.getItem("user_info");
      
      // Se já temos token e user info, usar direto sem chamada API
      if (token && userInfo && !user) {
        try {
          const info = JSON.parse(userInfo);
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          setUser(info);
          setIsAuthenticated(true);
          console.log('🔄 Bootstrap: usando dados do sessionStorage');
          return;
        } catch (e) {
          console.log('⚠️ Bootstrap: erro ao parse userInfo');
        }
      }
      
      // Se não temos dados, não fazer nada (deixar o usuário fazer login)
      if (!token || !userInfo) {
        console.log('🔑 Bootstrap: sem dados salvos, aguardando login');
        return;
      }
    };
    
    bootstrap();
  }, []);

  // Carrega o template do Labor Bill (catálogo dinâmico do Designer) após autenticar
  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/labor-bill-template`);
        const t = res.data;
        if (active && t && Array.isArray(t.left) && Array.isArray(t.right)) {
          setLeftColumnSubgroups(t.left);
          setRightColumnSubgroups(t.right);
        }
      } catch (e) { /* mantém defaults */ }
      try {
        // Ligação chave da IA → campo do Labor Bill (ajustável em Admin → Labor Bill)
        const m = await axios.get(`${API}/labor-bill/ai-field-mapping`);
        if (active && m.data?.map) setAiFieldMap(m.data.map);
      } catch (e) { /* mantém padrão */ }
    })();
    return () => { active = false; };
  }, [isAuthenticated]);

  // Warn unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // 📈 Rastreamento silencioso de acesso ao app (todos menos admin)
  const lastAccessPingRef = useRef(0);
  const trackAppAccess = useCallback(async () => {
    try {
      if (!user || user.role === 'admin') return;
      const token = sessionStorage.getItem('access_token');
      if (!token) return;
      const nowTs = Date.now();
      if (nowTs - lastAccessPingRef.current < 5 * 60 * 1000) return; // throttle 5min
      lastAccessPingRef.current = nowTs;
      await axios.post(`${API}/track-access`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { /* silencioso */ }
  }, [user]);

  // Registra ao entrar/abrir o app (login ou reload com sessão ativa)
  useEffect(() => {
    if (user && user.role !== 'admin') trackAppAccess();
  }, [user?.username]);

  // Registra quando o app volta ao primeiro plano
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') trackAppAccess(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [trackAppAccess]);

  // 🧭 Rastreamento silencioso de navegação (abas/sub-abas). Admin não é rastreado.
  const lastNavRef = useRef('');
  const trackNavigation = useCallback(async (label, tab) => {
    try {
      if (!user || user.role === 'admin' || !label) return;
      const token = sessionStorage.getItem('access_token');
      if (!token) return;
      if (lastNavRef.current === label) return; // evita duplicado consecutivo
      lastNavRef.current = label;
      await axios.post(`${API}/track-navigation`, { label, tab }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { /* silencioso */ }
  }, [user]);

  // 📌 Rastreamento silencioso de AÇÕES específicas (abrir PDF, abrir link externo, etc.).
  const trackEvent = useCallback(async (action, description, details = {}) => {
    try {
      if (!user || user.role === 'admin' || !action) return;
      const token = sessionStorage.getItem('access_token');
      if (!token) return;
      await axios.post(`${API}/track-event`, { action, description, details }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { /* silencioso */ }
  }, [user]);

  // Abre um link externo registrando o clique na trilha de acessos.
  const openTrackedLink = useCallback((url, action, description) => {
    trackEvent(action, description);
    openExternalLink(url);
  }, [trackEvent]);

  useEffect(() => {
    if (!isAuthenticated || !user || user.role === 'admin') return;
    const main = NAV_TAB_LABELS[activeTab] || activeTab;
    let sub = '';
    const subMap = NAV_SUBTAB_LABELS[activeTab];
    if (subMap) {
      if (activeTab === 'admin') sub = subMap[adminSubTab] || adminSubTab;
      else if (activeTab === 'arbitration') sub = subMap[arbitrationSubTab] || arbitrationSubTab;
      else if (activeTab === 'trucks') sub = subMap[trucksSubTab] || trucksSubTab;
      else if (activeTab === 'payroll') sub = subMap[payrollSubTab] || payrollSubTab;
    }
    const label = sub ? `${main} › ${sub}` : main;
    trackNavigation(label, activeTab);
  }, [activeTab, adminSubTab, arbitrationSubTab, trucksSubTab, payrollSubTab, isAuthenticated, user, trackNavigation]);

  // 📋 Completion Checklist embutido (sheet minimizável)
  const [formSheet, setFormSheet] = useState(null); // {title, url}
  // LEAD Form pré-preenchido; nome do Certified Renovator vem do cadastro do líder da equipe (PM1..)
  const openLeadForm = async (job) => {
    trackEvent('open_lead_form', 'Abriu o link LEAD Form');
    let renovatorName = null;
    const team = job?.team || (job?.teams || [])[0];
    if (team) {
      try { const { data } = await axios.get(`${API}/team-leader/${encodeURIComponent(team)}`); renovatorName = data?.full_name || null; } catch (_) { /* sem acesso: deixa para o instalador escolher */ }
    }
    setFormSheet({ title: 'LEAD Form (LSWP)', url: leadFormUrl(job, renovatorName) });
  };

  // 📅 Popup de alertas urgentes/vencidos no login (admin)
  const [alertsPopupOpen, setAlertsPopupOpen] = useState(false);
  const [urgentAlerts, setUrgentAlerts] = useState([]);
  const alertsPopupCheckedRef = useRef('');
  useEffect(() => {
    const checkUrgentAlerts = async () => {
      if (!user || user.role !== 'admin') return;
      if (alertsPopupCheckedRef.current === user.username) return;
      alertsPopupCheckedRef.current = user.username;
      try {
        const token = sessionStorage.getItem('access_token');
        const { data } = await axios.get(`${API}/admin/alerts/upcoming`, {
          params: { limit: 20 },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (data.urgent && data.urgent.length > 0) {
          setUrgentAlerts(data.urgent);
          setAlertsPopupOpen(true);
        }
      } catch (e) { /* silencioso */ }
    };
    checkUrgentAlerts();
  }, [user?.username]);



  const safeSetActiveTab = (value) => {
    if (isDirty && value !== "create") {
      const proceed = window.confirm("You have unsaved changes. Leave without saving?");
      if (!proceed) return;
      setIsDirty(false);
    }
    
    // Clear payroll context when navigating away from create tab
    if (value !== "create" && payrollContext) {
      clearPayrollContext();
    }
    
    setActiveTab(value);
    
    // Apenas rolar para o topo ao mudar de aba (não em iPhone para evitar jumps)
    const isIPhone = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!isIPhone) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Função para mudar período de Labor Bills e salvar preferência
  const changeLaborBillPeriod = (newPeriod) => {
    setLaborBillPeriod(newPeriod);
    // Salvar no localStorage por usuário
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(`labor_bill_period_${user?.username || 'default'}`, newPeriod);
      console.log(`💾 Período de Labor Bills salvo para ${user?.username}: ${newPeriod} dias`);
    }
    toast.success(`Filtro atualizado: ${getPeriodName(newPeriod, language)}`);
  };

  // Sort drafts function - corrigido para formato MM-DD-YYYY
  const sortDrafts = (bills) => {
    const sortedBills = [...bills];
    
    // Helper para converter qualquer formato de data para Date object
    const parseInstallDate = (dateStr) => {
      if (!dateStr) return new Date('1900-01-01');
      
      try {
        // Formato MM-DD-YYYY (após padronização)
        if (dateStr.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
          const [month, day, year] = dateStr.split('-');
          return new Date(year, parseInt(month) - 1, parseInt(day));
        }
        
        // Formato YYYY-MM-DD (antes da padronização)
        if (dateStr.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
          return new Date(dateStr);
        }
        
        // Formato MM/DD/YYYY ou M/D/YY
        if (dateStr.includes('/')) {
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            let [month, day, year] = parts;
            
            // Converter ano de 2 dígitos
            if (year.length === 2) {
              const yearInt = parseInt(year);
              year = yearInt < 50 ? `20${year}` : `19${year}`;
            }
            
            return new Date(year, parseInt(month) - 1, parseInt(day));
          }
        }
        
        // Fallback: tentar parse direto
        const parsed = new Date(dateStr);
        return isNaN(parsed.getTime()) ? new Date('1900-01-01') : parsed;
        
      } catch (e) {
        console.warn('⚠️ Date parse error for sort:', dateStr, e);
        return new Date('1900-01-01');
      }
    };
    
    switch (draftsSort) {
      case "date_desc":
        return sortedBills.sort((a, b) => {
          const dateA = parseInstallDate(a.install_date);
          const dateB = parseInstallDate(b.install_date);
          return dateB - dateA; // Newest first
        });
      
      case "date_asc":
        return sortedBills.sort((a, b) => {
          const dateA = parseInstallDate(a.install_date);
          const dateB = parseInstallDate(b.install_date);
          return dateA - dateB; // Oldest first
        });
      
      case "job_name_asc":
        return sortedBills.sort((a, b) => {
          const nameA = (a.job_name || '').toLowerCase();
          const nameB = (b.job_name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      
      case "job_name_desc":
        return sortedBills.sort((a, b) => {
          const nameA = (a.job_name || '').toLowerCase();
          const nameB = (b.job_name || '').toLowerCase();
          return nameB.localeCompare(nameA);
        });
      
      case "order_asc":
        return sortedBills.sort((a, b) => {
          const orderA = (a.order_number || '').toLowerCase();
          const orderB = (b.order_number || '').toLowerCase();
          return orderA.localeCompare(orderB);
        });
      
      case "order_desc":
        return sortedBills.sort((a, b) => {
          const orderA = (a.order_number || '').toLowerCase();
          const orderB = (b.order_number || '').toLowerCase();
          return orderB.localeCompare(orderA);
        });
      
      case "total_desc":
        return sortedBills.sort((a, b) => {
          const totalA = a.grand_total || 0;
          const totalB = b.grand_total || 0;
          return totalB - totalA; // Highest first
        });
      
      case "total_asc":
        return sortedBills.sort((a, b) => {
          const totalA = a.grand_total || 0;
          const totalB = b.grand_total || 0;
          return totalA - totalB; // Lowest first
        });
      
      default:
        return sortedBills;
    }
  };

  // Auth handlers
  const handleLogin = async (userInfo, credentials = null) => {
    console.log('🔄 App.js handleLogin chamado com:', userInfo);
    setIsAuthenticated(true);
    try {
      const token = sessionStorage.getItem("access_token");
      if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      console.log('🔍 Tentando chamar /me endpoint...');
      const { data } = await axios.get(`${API}/me`);
      console.log('✅ /me endpoint sucesso:', data);
      const info = { username: data.username, full_name: data.full_name, first_name: data.first_name, last_name: data.last_name, role: data.role };
      sessionStorage.setItem("user_info", JSON.stringify(info));
      setUser(info);
      console.log('✅ setUser chamado com dados /me:', info);
      
      // Save credentials securely if provided (from manual login ONLY)
      // Skip biometric setup for auto-login to avoid duplicate calls
      if (credentials && credentials.username && credentials.password && !credentials.isAutoLogin) {
        try {
          console.log('🔐 Offering biometric authentication setup...');
          
          // Ask user if they want to enable biometric login
          const enableBiometric = window.confirm(
            'Gostaria de usar Face ID ou Touch ID para próximos logins? ' +
            'Isso permitirá entrada automática sem digitar senha.'
          );
          
          if (enableBiometric) {
            console.log('🔐 User accepted biometric setup, testing biometric authentication...');
            
            // Test biometric authentication before saving
            const biometricSuccess = await performBiometricAuth({ username: credentials.username });
            
            if (biometricSuccess) {
              await saveUserCredentials(credentials.username, credentials.password);
              toast.success('✅ Face ID/Touch ID ativado! Próximos logins serão automáticos.');
            } else {
              toast.error('❌ Falha na autenticação biométrica. Login manual mantido.');
            }
          } else {
            toast.info('Login manual mantido. Pode ativar Face ID nas configurações depois.');
          }
        } catch (error) {
          console.error('⚠️ Failed to save credentials:', error);
          toast.error('Erro ao configurar Face ID. Login manual mantido.');
        }
      }
      
    } catch (e) {
      console.log('⚠️ /me endpoint falhou, usando userInfo do login:', userInfo);
      // fallback ao userInfo básico caso /me falhe
      setUser(userInfo);
      console.log('✅ setUser chamado com fallback:', userInfo);
      
      // Save credentials even with fallback (from manual login ONLY)
      // Skip biometric setup for auto-login to avoid duplicate calls
      if (credentials && credentials.username && credentials.password && !credentials.isAutoLogin) {
        try {
          console.log('🔐 Offering biometric authentication setup (fallback)...');
          
          // Ask user if they want to enable biometric login
          const enableBiometric = window.confirm(
            'Gostaria de usar Face ID ou Touch ID para próximos logins? ' +
            'Isso permitirá entrada automática sem digitar senha.'
          );
          
          if (enableBiometric) {
            console.log('🔐 User accepted biometric setup, testing biometric authentication...');
            
            // Test biometric authentication before saving
            const biometricSuccess = await performBiometricAuth({ username: credentials.username });
            
            if (biometricSuccess) {
              await saveUserCredentials(credentials.username, credentials.password);
              toast.success('✅ Face ID/Touch ID ativado! Próximos logins serão automáticos.');
            } else {
              toast.error('❌ Falha na autenticação biométrica. Login manual mantido.');
            }
          } else {
            toast.info('Login manual mantido. Pode ativar Face ID nas configurações depois.');
          }
        } catch (error) {
          console.error('⚠️ Failed to save credentials:', error);
          toast.error('Erro ao configurar Face ID. Login manual mantido.');
        }
      }
    }
    
    // Initialize push notifications for mobile app (Capacitor)
    try {
      console.log('🔔 Initializing push notifications...');
      const pushInitialized = await initializePushNotifications();
      if (pushInitialized) {
        console.log('✅ Push notifications ready');
      } else {
        console.log('ℹ️ Push notifications not available (web browser or permission denied)');
      }
    } catch (error) {
      console.log('ℹ️ Push notifications not available:', error.message);
    }
    
    console.log('🎉 handleLogin completado');
    
    // Load user permissions
    try {
      const { data: permsData } = await axios.get(`${API}/my-permissions`);
      setUserPermissions(permsData.permissions);
      console.log('🔐 Permissions loaded:', permsData.source);
    } catch (e) {
      console.log('Permissions not loaded, using defaults');
    }
    
    // Mostrar vídeo de entrada após QUALQUER login (manual ou Face ID)
    console.log('🎬 Chamando loadSplashVideos após login...');
    await loadSplashVideos();
    
    // Verificar tickets em aberto (ADMIN only) - MODAL DE NOTIFICAÇÃO
    if (userInfo.role === 'admin' || userInfo.username === 'ADMIN') {
      setTimeout(async () => {
        try {
          const { data: tickets } = await axios.get(`${API}/admin/tickets?status=open`);
          const openTickets = tickets || [];
          
          if (openTickets.length > 0) {
            console.log(`🎫 ADMIN: ${openTickets.length} tickets em aberto - criando notificações`);
            
            // Criar notificações temporárias para cada ticket (não salva no banco)
            const ticketNotifications = openTickets.map((ticket, index) => ({
              id: `ticket-${ticket.id}`,
              title: `🎫 Ticket em Aberto`,
              message: `"${ticket.title}" criado por ${ticket.pm_user}. Clique para resolver na aba Suporte.`,
              from_user: ticket.pm_user,
              created_at: ticket.created_at,
              type: 'ticket_alert'
            }));
            
            // Adicionar à fila de notificações (usa sistema existente)
            setNotificationQueue(ticketNotifications);
            
            // Mostrar primeira notificação
            if (ticketNotifications.length > 0) {
              setNotificationView(ticketNotifications[0]);
              setShowOldNotificationModal(true);
            }
          }
        } catch (error) {
          console.log('⚠️ Erro verificando tickets:', error.message);
        }
      }, 3000); // Aguardar 3s após login (depois do vídeo)
    }
  };

  const handleLogout = async () => {
    try {
      console.log('🚪 LOGOUT: Botão clicado');
      console.log('🚪 LOGOUT: Pedindo confirmação...');
      
      // Ask for confirmation
      const confirmed = await confirmLogout();
      console.log('🚪 LOGOUT: Confirmação =', confirmed);
      
      if (!confirmed) {
        console.log('❌ LOGOUT: Cancelado pelo usuário');
        return;
      }
      
      console.log('✅ LOGOUT: Confirmado - limpando dados...');
      
      // Clear secure credentials
      await clearSavedCredentials();
      console.log('🗑️ LOGOUT: Credenciais limpas');
      
      // Clear session storage
      sessionStorage.removeItem("access_token");
      sessionStorage.removeItem("user_info");
      // FCM token registration tracking removed (internal notifications only)
      delete axios.defaults.headers.common["Authorization"];
      console.log('🗑️ LOGOUT: Session storage limpo');
      
      // Clear state
      setUser(null);
      setIsAuthenticated(false);
      setLaborBills([]);
      setEmailRecipients([]);
      setHistory([]);
      setCurrentBill(null);
      console.log('🗑️ LOGOUT: Estados limpados');
      
      toast.success("Logout realizado. Credenciais removidas do dispositivo.");
      console.log('✅ LOGOUT: Completado com sucesso');
      
    } catch (error) {
      console.error('❌ LOGOUT: Erro durante logout:', error);
      toast.error("Erro ao fazer logout. Tente novamente.");
    }
  };

  // Push notification modal functions removed - using internal notifications only

  // Auto-login attempt on app startup
  useEffect(() => {
    const performAutoLogin = async () => {
      console.log('🚀 App startup - checking for auto-login...');
      setIsAutoLoggingIn(true);
      
      try {
        // Create a login function that works with the existing API
        const autoLoginFunction = async (username, password) => {
          console.log('🔐 Auto-login function called for:', username);
          
          try {
            const response = await axios.post(`${API}/login`, {
              username,
              password
            });
            
            if (response.status === 200 && response.data.access_token) {
              // Store token and call handleLogin (mark as auto-login)
              sessionStorage.setItem("access_token", response.data.access_token);
              await handleLogin(response.data, { isAutoLogin: true });
              return true;
            } else {
              console.log('❌ Auto-login API failed');
              return false;
            }
          } catch (error) {
            console.error('❌ Auto-login API error:', error);
            return false;
          }
        };
        
        // Attempt auto-login
        const autoLoginSuccess = await attemptAutoLogin(autoLoginFunction);
        
        if (autoLoginSuccess) {
          console.log('🎉 Auto-login successful!');
        } else {
          console.log('ℹ️ Auto-login not available - showing login screen');
        }
        
      } catch (error) {
        console.error('❌ Auto-login error:', error);
      } finally {
        setIsAutoLoggingIn(false);
      }
    };
    
    // Only attempt auto-login if not already authenticated AND not already attempted
    if (!isAuthenticated && !autoLoginAttempted) {
      setAutoLoginAttempted(true); // Mark as attempted to prevent duplicates
      performAutoLogin();
    } else {
      setIsAutoLoggingIn(false);
    }
  }, []); // Run only once on app startup

  // Instant notification system using localStorage events
  useEffect(() => {
    if (user?.username && isAuthenticated) {
      console.log('👤 Setting up INSTANT notifications for:', user.username, 'Role:', user.role);
      
      const handleStorageChange = (event) => {
        if (event.key === 'notification_queue' && event.newValue) {
          console.log('🔔 INSTANT: Notification queue updated, checking for new notifications...');
          
          try {
            const notifications = JSON.parse(event.newValue);
            
            // Find unseen notification for this user
            const unseenForMe = notifications.find(n => {
              // Check if this user has seen this notification
              const seenKey = `notification_seen_${n.id}_${user.username}`;
              const hasUserSeen = localStorage.getItem(seenKey) === 'true';
              
              if (hasUserSeen) return false;
              
              // Check recipients based on user role
              if (user.role === 'admin') {
                return n.recipients === 'admin' || n.event_type === 'pm_activity';
              } else {
                return n.recipients === user.username || n.recipients === 'all' || 
                       (n.recipients && n.recipients.includes(',') && n.recipients.split(',').map(r => r.trim()).includes(user.username));
              }
            });
            
            if (unseenForMe) {
              console.log('🎉 INSTANT NOTIFICATION for', user.username, ':', unseenForMe.title);
              // Push notification modal removed - using internal notifications only
              console.log('📱 Notification:', unseenForMe.title, '-', unseenForMe.message);
            }
          } catch (error) {
            console.log('❌ Storage event error:', error.message);
          }
        }
      };
      
      // Listen for localStorage changes (instant notifications)
      window.addEventListener('storage', handleStorageChange);
      
      // Also check for existing notifications on login
      const checkExistingNotifications = () => {
        try {
          const notificationQueue = JSON.parse(localStorage.getItem('notification_queue') || '[]');
          
          const unseenNotifications = notificationQueue.filter(n => {
            const seenKey = `notification_seen_${n.id}_${user.username}`;
            const hasUserSeen = localStorage.getItem(seenKey) === 'true';
            
            if (hasUserSeen) return false;
            
            if (user.role === 'admin') {
              return n.recipients === 'admin' || n.event_type === 'pm_activity';
            } else {
              return n.recipients === user.username || n.recipients === 'all' || 
                     (n.recipients && n.recipients.includes(',') && n.recipients.split(',').map(r => r.trim()).includes(user.username));
            }
          });
          
          if (unseenNotifications.length > 0) {
            const notification = unseenNotifications[0];
            console.log('📱 Login check: Showing notification for', user.username, ':', notification.title);
            // Push notification modal removed - using internal notifications only
            console.log('📱 Notification:', notification.title, '-', notification.message);
          }
        } catch (error) {
          console.log('❌ Existing notifications check error:', error.message);
        }
      };
      
      // Check for existing notifications after small delay
      setTimeout(checkExistingNotifications, 1000);
      
      // Store functions globally
      window.showNextNotificationFromQueue = checkExistingNotifications;
      
      // Cleanup
      return () => {
        window.removeEventListener('storage', handleStorageChange);
      };
    }
  }, [user?.username, user?.role, isAuthenticated]);

  // Auto-load data when admin enters Home tab
  useEffect(() => {
    if (activeTab === 'home' && user?.role === 'admin') {
      console.log('🏠 Admin entered Home tab - auto-loading weekly report summary');
      loadWeeklyReportSummary(false); // Don't show error toast for auto-load
      console.log('💰 Admin home - loading all payment estimates');
      loadAllPaymentEstimates();
    }
  }, [activeTab, user?.role]);

  // Load splash screen setting from backend on mount
  useEffect(() => {
    const loadSplashScreenSetting = async () => {
      try {
        const response = await axios.get(`${API}/settings/splash-screen`);
        const enabled = response.data.enabled;
        setSplashScreenEnabled(enabled);
        localStorage.setItem('splash_screen_enabled', enabled ? 'true' : 'false');
        console.log('🎬 Splash screen setting loaded from backend:', enabled);
      } catch (error) {
        console.log('⚠️ Could not load splash screen setting from backend, using localStorage');
      }
    };
    
    loadSplashScreenSetting();
  }, []);
  
  // Carregar waivers pendentes para PM
  useEffect(() => {
    if (isAuthenticated && user?.role !== 'admin' && user?.username) {
      loadPendingWaivers();
    }
  }, [isAuthenticated, user?.role, user?.username]);

  // Auto-load weekly report when PM enters reports tab
  useEffect(() => {
    if (activeTab === 'reports' && user?.role !== 'admin' && user?.username) {
      console.log('📊 PM entered reports tab - loading weekly report data');
      loadPMWeeklyReport();
    }
  }, [activeTab, user?.role, user?.username]);

  // Load image thumbnails when edit modal opens
  useEffect(() => {
    if (showTeamLeaderEditModal && currentTeamLeader?.username && teamLeaderFiles) {
      console.log('🖼️ Loading thumbnails for team leader documents');
      
      // Load thumbnails for all image documents
      ['driver_license_file', 'lead_certificate_file', 'lead_firm_certificate_file'].forEach(docType => {
        const doc = teamLeaderFiles[docType];
        if (doc?.file_type === 'image') {
          loadImageThumbnail(docType, (url) => 
            setImageUrls(prev => ({ ...prev, [docType]: url }))
          );
        }
      });
    }
  }, [showTeamLeaderEditModal, currentTeamLeader?.username, teamLeaderFiles?.driver_license_file, teamLeaderFiles?.lead_certificate_file, teamLeaderFiles?.lead_firm_certificate_file]);

  // Load image thumbnails when view modal opens
  useEffect(() => {
    if (showTeamLeaderViewModal && currentTeamLeader?.username && teamLeaderFiles) {
      console.log('🖼️ Loading thumbnails for team leader documents (view mode)');
      
      // Load thumbnails for all image documents
      ['driver_license_file', 'lead_certificate_file', 'lead_firm_certificate_file'].forEach(docType => {
        const doc = teamLeaderFiles[docType];
        if (doc?.file_type === 'image') {
          loadImageThumbnail(docType, (url) => 
            setImageUrls(prev => ({ ...prev, [docType]: url }))
          );
        }
      });
    }
  }, [showTeamLeaderViewModal, currentTeamLeader?.username, teamLeaderFiles?.driver_license_file, teamLeaderFiles?.lead_certificate_file, teamLeaderFiles?.lead_firm_certificate_file]);


  // Carregar dias bloqueados quando abrir o calendário
  useEffect(() => {
    if (isAuthenticated && activeTab === 'home') {
      loadBlockedDays();
    }
  }, [isAuthenticated, activeTab]);


  // Load helpers when PM enters helpers tab
  useEffect(() => {
    if (isAuthenticated && user && activeTab === 'arbitration' && arbitrationSubTab === 'helpers') {
      console.log('👷 Entered helpers tab');
      console.log('User:', user?.username, 'Role:', user?.role);
      
      if (user.role === 'admin') {
        // ADMIN carrega TODOS os helpers
        console.log('📥 ADMIN loading all helpers from all teams');
        (async () => {
          try {
            setHelpersLoading(true);
            const { data } = await axios.get(`${API}/admin/all-helpers`);
            setHelpers(data);
            console.log(`✅ Loaded ${data.length} helpers from all teams`);
          } catch (e) {
            console.error('Failed to load all helpers:', e);
            toast.error('Erro ao carregar funcionários');
          } finally {
            setHelpersLoading(false);
          }
        })();
      } else {
        // PM carrega apenas seus helpers
        console.log('📥 PM loading own helpers');
        loadMyHelpers();
      }
    }
  }, [activeTab, arbitrationSubTab, isAuthenticated, user]);

  // Debug - Monitorar abertura do menu lateral
  useEffect(() => {
    // Logs removed for production
  }, [sideMenuOpen, isScrolled]);

  // Load helpers for PM when entering home tab (for alerts)
  useEffect(() => {
    if (isAuthenticated && user && activeTab === 'home' && user.role !== 'admin') {
      console.log('🏠 PM entered home - checking helpers for alerts');
      loadMyHelpers();
    }
  }, [activeTab, isAuthenticated, user]);

  // Detectar scroll para mostrar/esconder menu lateral
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY || window.pageYOffset;
      setIsScrolled(scrollPosition > 50); // Menu some após 50px de scroll
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load available documents when edit modal opens
  useEffect(() => {
    if (showTeamLeaderEditModal && currentTeamLeader?.username) {
      loadAvailableDocuments(currentTeamLeader.username);
    }
  }, [showTeamLeaderEditModal, currentTeamLeader?.username]);

  // Auto-load weekly report summary when PM enters home tab
  useEffect(() => {
    if (activeTab === 'home' && user?.username) {
      if (user.role !== 'admin') {
        console.log('🏠 PM entered home tab - loading filtered weekly report summary for team:', user.username);
        loadWeeklyReportSummary(false); // Don't show error toast for auto-load
        
        // Carregar estimativa de próximo pagamento (PM only)
        loadNextPaymentEstimate();
        loadCurrentWeekEstimate();
      } else {
        console.log('🏠 Admin entered home tab - loading complete weekly report summary');
        loadWeeklyReportSummary(false);
        loadCurrentWeekEstimate();
      }
    }
  }, [activeTab, user?.role, user?.username]);
  // ===== TRUCK RENTAL FUNCTIONS =====
  const loadTrucks = async () => {
    try {
      const { data } = await axios.get(`${API}/trucks`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
      });
      setTrucks(data);
    } catch (error) {
      console.error('Erro ao carregar caminhões:', error);
    }
  };

  const loadMyRentals = async () => {
    try {
      const { data } = await axios.get(`${API}/rentals`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
      });
      setMyRentals(data);
      
      // Encontrar aluguel ativo
      const active = data.find(r => r.status === 'ativo');
      setActiveRental(active || null);
    } catch (error) {
      console.error('Erro ao carregar aluguéis:', error);
    }
  };

  // Load notifications
  const loadNotifications = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/unread`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
      });
      setUnreadNotifications(data);
      
      // Check for modal notifications on login (exclui a revisão do FOM: abre só no clique)
      const modalNotif = data.find(n => n.dados_extras?.show_modal && n.dados_extras?.type !== 'labor_bill_fom_review' && !n.lida);
      if (modalNotif) {
        setCurrentNotification(modalNotif);
        setShowNotificationModal(true);
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    const ex = notification.dados_extras || {};
    if (ex.type === 'labor_bill_fom_review') {
      setFomReview(ex);
      setShowFomReviewModal(true);
      handleMarkNotificationAsRead(notification);
      return;
    }
    // Verificar se é notificação de truck (novo sistema) ou antiga
    const isTruckNotification = notification.tipo || notification.type?.includes('truck_rental');
    const isTicketOrOld = notification.type === 'ticket_alert' || (!notification.tipo && notification.type);
    
    if (isTruckNotification && !isTicketOrOld) {
      // Notificação de trucks - usar modal novo
      setCurrentNotification(notification);
      setShowNotificationModal(true);
    } else {
      // Notificação antiga (ticket, mensagem) - usar modal antigo
      setNotificationView(notification);
      setShowOldNotificationModal(true);
    }
  };

  // Handle checkout request
  const handleRequestCheckout = async (rental) => {
    try {
      await axios.post(
        `${API}/rentals/${rental.id}/request-checkout`,
        {},
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
        }
      );
      
      toast.success(
        language === 'pt-BR'
          ? '✅ Solicitação de checkout enviada! Aguarde instruções do administrador.'
          : '✅ Checkout request sent! Wait for administrator instructions.'
      );
      
      loadMyRentals();
    } catch (error) {
      console.error('Erro ao solicitar checkout:', error);
      toast.error(
        language === 'pt-BR'
          ? 'Erro ao solicitar checkout'
          : 'Error requesting checkout'
      );
    }
  };

  // Handle approve from modal
  const handleApproveFromModal = async (notification) => {
    try {
      const rentalId = notification.rental_id;
      await axios.put(
        `${API}/rentals/${rentalId}/approve`,
        {},
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
        }
      );
      
      toast.success(language === 'pt-BR' ? '✅ Aluguel aprovado!' : '✅ Rental approved!');
      
      // Mark notification as read
      await axios.put(
        `${API}/notifications/${notification.id}/read`,
        {},
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
        }
      );
      
      setShowNotificationModal(false);
      loadMyRentals();
      loadTrucks();
      loadNotifications();
    } catch (error) {
      console.error('Erro:', error);
      toast.error(language === 'pt-BR' ? 'Erro ao aprovar' : 'Error approving');
    }
  };

  // Handle reject from modal
  const handleRejectFromModal = async (notification) => {
    const motivo = prompt(language === 'pt-BR' ? 'Motivo da rejeição:' : 'Rejection reason:');
    if (!motivo) return;
    
    try {
      const rentalId = notification.rental_id;
      await axios.put(
        `${API}/rentals/${rentalId}/reject`,
        { motivo },
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
        }
      );
      
      toast.success(language === 'pt-BR' ? 'Aluguel rejeitado' : 'Rental rejected');
      
      // Mark notification as read
      await axios.put(
        `${API}/notifications/${notification.id}/read`,
        {},
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
        }
      );
      
      setShowNotificationModal(false);
      loadMyRentals();
      loadTrucks();
      loadNotifications();
    } catch (error) {
      console.error('Erro:', error);
      toast.error(language === 'pt-BR' ? 'Erro ao rejeitar' : 'Error rejecting');
    }
  };

  // Handle view details from modal (for checkout)
  const handleViewDetailsFromModal = (notification) => {
    setShowNotificationModal(false);
    
    // Buscar o rental e truck dos dados da notificação
    const rental = notification.dados_extras?.rental;
    const truck = notification.dados_extras?.truck;
    
    if (rental && truck) {
      // Setar rental e truck selecionados
      setSelectedRental(rental);
      setSelectedTruck(truck);
      
      // Abrir modal de checkout
      setShowCheckOutModal(true);
    }
    
    // Navigate to trucks tab
    setActiveTab('trucks');
    setTrucksSubTab('dashboard');
    
    // Mark notification as read
    axios.put(
      `${API}/notifications/${notification.id}/read`,
      {},
      {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
      }
    ).catch(err => console.error('Erro ao marcar como lida:', err));
  };

  // Mark notification as read (for modals)
  const handleMarkNotificationAsRead = async (notification) => {
    try {
      await axios.put(
        `${API}/notifications/${notification.id}/read`,
        {},
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
        }
      );
      loadNotifications(); // Reload to update badge
    } catch (error) {
      console.error('Erro ao marcar como lida:', error);
    }
  };

  const handleRequestRental = (truck) => {
    setSelectedTruck(truck);
    setShowRentalRequestModal(true);
  };

  const handleRentalSuccess = () => {
    loadMyRentals();
    loadTrucks();
  };
  const handleViewTruckDocument = (docType, truck) => {
    const docUrl = docType === 'registration' 
      ? truck.documentos?.registration?.url 
      : truck.documentos?.seguro?.url;
    
    if (docUrl) {
      setTruckDocToView({ url: docUrl, type: docType, truck });
      setShowTruckDocModal(true);
    }
  };



  // Load trucks data when authenticated (adicionado após funções estarem definidas)
  useEffect(() => {
    if (isAuthenticated && user) {
      loadTrucks();
      loadMyRentals();
      loadNotifications();
    }
  }, [isAuthenticated, user]);

  // Ajustar sub-aba de Trucks baseado no role ao entrar na aba
  useEffect(() => {
    if (activeTab === 'trucks' && user) {
      // Se for primeira vez entrando (sub-aba ainda está no default)
      // PM: vai para 'history'
      // Admin: vai para 'dashboard'
      const isFirstTimeInTab = trucksSubTab === 'history'; // valor default
      
      if (isFirstTimeInTab && user.role === 'admin') {
        setTrucksSubTab('dashboard');
      }
      // Se for PM, já está em 'history' que é o correto
    }
  }, [activeTab, user]);
  
  // Navegação por teclado no calendário
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isAuthenticated || activeTab !== 'home') return;
      
      const moveDay = (days) => {
        const cur = selectedDate;
        const newDate = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + days);
        setSelectedDate(newDate);
        
        if (newDate.getMonth() !== selectedDate.getMonth()) {
          loadScheduleJobs(newDate.getMonth(), newDate.getFullYear());
        } else {
          updateSelectedDayJobs(newDate);
        }
      };
      
      if (e.key === 'ArrowLeft') { e.preventDefault(); moveDay(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); moveDay(1); }
      if (e.key === 'ArrowUp') { e.preventDefault(); moveDay(-7); }
      if (e.key === 'ArrowDown') { e.preventDefault(); moveDay(7); }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedDate, isAuthenticated, activeTab]);


  // Load trucks data when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      loadTrucks();
      loadMyRentals();
      loadNotifications();
      
      // Carregar calendário de schedule
      const today = new Date();
      loadScheduleJobs(today.getMonth(), today.getFullYear());
    }
  }, [isAuthenticated, user]);


  // Auto-load daily work when entering home tab
  useEffect(() => {
    if (activeTab === 'home' && user?.username) {
      console.log('📅 Loading daily work for home dashboard');
      
      if (user.role === 'admin') {
        // Admin sees all work
        loadDailyWork();
        loadDailyWorkSummary();
        loadDailyWorkConfig();
      } else {
        // PM users SEMPRE veem apenas trabalhos de data específica (não todos)
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
        
        console.log(`📅 PM user - loading work for today by default: ${todayStr}`);
        setDailyWorkDateFilter(todayStr);
        loadDailyWork(todayStr);  // SEMPRE com filtro de data para PMs
      }
    }
  }, [activeTab, user?.username]);

  // Load PM data
  useEffect(() => {
    if (isAuthenticated && user) { // Adicionar verificação do user
      console.log('👤 User authenticated, loading data for role:', user.role);
      loadLaborBills();
      loadEmailRecipients();
      loadHistory();
      loadEmailHistory(); // Carregar histórico de emails
      loadTrash();
      // Removed: loadArbitrationFiles() - carrega apenas quando acessar a aba Docs
    }
  }, [isAuthenticated, user]); // Adicionar user como dependência

  // REMOVIDO: Mobile keyboard fix duplicado que causava conflito

  // Executar migração quando modal abre
  useEffect(() => {
    if (migratingNames && !migrationResult) {
      (async () => {
        try {
          setMigrationStep('Corrigindo nomes dos usuários...');
          await new Promise(r => setTimeout(r, 500));
          setMigrationStep('Atualizando notificações e logs...');
          const { data } = await axios.post(`${API}/admin/migrate-notification-names`);
          setMigrationResult(data);
        } catch (e) {
          setMigrationResult({ users_fixed: 0, notifications_updated: 0, activity_logs_updated: 0, error: e.message });
        }
      })();
    }
  }, [migratingNames]);

  // Auto-carregar notificações e logs quando entra na aba Admin > Mensagens
  useEffect(() => {
    if (activeTab === 'admin' && adminSubTab === 'notificacoes') {
      loadAdminNotifications(1, notificationHistoryFilter);
    }
    if (activeTab === 'admin' && adminSubTab === 'mensagens') {
      loadAdminNotifications(1, notificationHistoryFilter);
    }
  }, [activeTab, adminSubTab]);

  const loadLaborBills = async () => {
    try {
      console.log('🔄 Loading Labor Bills...');
      console.log('👤 Current user role:', user?.role);
      console.log('👤 Current username:', user?.username);
      
      const res = await axios.get(`${API}/labor-bills`);
      console.log('📊 Labor Bills loaded:', res.data.length);
      
      // DEBUG: Verificar se campo "paid" está vindo do backend
      const paidFromDB = res.data.filter(b => b.paid === true);
      console.log('💰 Labor Bills com paid=true do backend:', paidFromDB.length);
      if (paidFromDB.length > 0) {
        console.log('💰 Exemplo de bill paid do backend:', paidFromDB[0]);
      }
      
      // Verificar quais labor bills foram processados no payroll (para todos os usuários)
      console.log('🔍 Checking payroll status...');
      try {
        // PMs podem ver dados do payroll através do endpoint específico
        const payrollEndpoint = user?.role === 'admin' 
          ? `${API}/admin/payroll/reports`
          : `${API}/payroll/user-reports`;
        
        console.log(`🔍 Using payroll endpoint: ${payrollEndpoint} for role: ${user?.role}`);
        
        const payrollRes = await axios.get(payrollEndpoint);
        const payrollReports = payrollRes.data || [];
        console.log('📊 Payroll response structure:', typeof payrollReports, Array.isArray(payrollReports));
        console.log('📊 Payroll reports loaded:', payrollReports.length || payrollReports.entries?.length || 0);
        
        // Criar set de IDs de labor bills que foram processados
        const processedLaborBillIds = new Set();
        
        if (user?.role === 'admin') {
          // Admin: processar de reports completos (array direto)
          payrollReports.forEach(report => {
            if (report.entries && Array.isArray(report.entries)) {
              report.entries.forEach(entry => {
                if (entry.found_match && entry.labor_bill_id) {
                  processedLaborBillIds.add(entry.labor_bill_id);
                  // console.log('💰 Found processed Labor Bill ID (admin):', entry.labor_bill_id);
                }
              });
            }
          });
        } else {
          // PM: processar de structure { reports: [...] }
          const reports = payrollReports.reports || [];
          console.log('📊 PM Reports structure:', reports.length, 'reports');
          
          reports.forEach(report => {
            if (report.entries && Array.isArray(report.entries)) {
              report.entries.forEach(entry => {
                if (entry.found_match && entry.labor_bill_id) {
                  processedLaborBillIds.add(entry.labor_bill_id);
                  // console.log('💰 Found processed Labor Bill ID (PM):', entry.labor_bill_id);
                }
              });
            }
          });
        }
        
        console.log('💰 Total processed Labor Bill IDs:', processedLaborBillIds.size);
        
        // Marcar labor bills como "paid" se foram processados no payroll OU marcados manualmente
        const updatedLaborBills = res.data.map(bill => {
          const isPayrollPaid = processedLaborBillIds.has(bill.id);
          const isManuallyPaid = bill.paid === true; // Check database field
          const finalPaidStatus = isPayrollPaid || isManuallyPaid;
          
          if (isPayrollPaid) {
            // console.log('✅ Marking as paid (payroll):', bill.id, bill.job_name);
          }
          if (isManuallyPaid) {
            // console.log('✅ Already marked as paid (manual):', bill.id, bill.job_name);
          }
          
          return {
            ...bill,
            paid: finalPaidStatus
          };
        });
        
        setLaborBills(updatedLaborBills);
        console.log(`📊 Status final: ${updatedLaborBills.filter(b => b.paid).length} labor bills marcados como pagos (payroll + manual)`);
        console.log(`📊 Payroll: ${processedLaborBillIds.size}, Manual: ${res.data.filter(b => b.paid === true).length}`);
        
      } catch (payrollError) {
        console.warn('⚠️ Não foi possível verificar status do payroll:', payrollError);
        // Se não conseguir verificar payroll, pelo menos preserve o status manual "paid"
        const updatedLaborBills = res.data.map(bill => ({
          ...bill,
          paid: bill.paid === true // Preserve manual paid status
        }));
        setLaborBills(updatedLaborBills);
      }
    } catch (e) {
      console.error('❌ Erro ao carregar labor bills:', e);
    }
  };
  const loadEmailRecipients = async () => {
    try {
      const res = await axios.get(`${API}/email-recipients`);
      setEmailRecipients(res.data);
    } catch (e) {}
  };
  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/labor-bills-history`);
      setHistory(res.data);
    } catch (e) {}
  };
  
  const loadEmailHistory = async () => {
    try {
      const token = sessionStorage.getItem('access_token');
      if (!token || !user) return;
      
      // Para usuários PM, carregar apenas seus próprios envios
      let endpoint = '';
      if (user.role === 'admin') {
        // Admin vê todos os envios
        endpoint = `${API}/admin/email-logs?limit=50`;
      } else {
        // PM vê apenas seus próprios envios
        endpoint = `${API}/email-history?limit=50`;
      }
      
      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data && Array.isArray(res.data)) {
        // Ordenar por data de envio (mais recente primeiro)
        const sortedEmails = res.data.sort((a, b) => {
          const dateA = new Date(a.sent_at || a.created_at);
          const dateB = new Date(b.sent_at || b.created_at);
          return dateB - dateA;
        });
        
        setEmailHistory(sortedEmails);
        console.log(`📧 Email history loaded for ${user.username}: ${sortedEmails.length} emails`);
      }
    } catch (e) {
      console.error('❌ Error loading email history:', e);
      setEmailHistory([]); // Set empty array on error
    }
  };
  
  // Helper function to detect mobile
  const isMobileDevice = () => {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;
  };
  
  // Helper function to open PDF in modal or download
  const handlePDFAction = async (url, title, forceDownload = false) => {
    if (forceDownload) {
      // Force download - para quando clicar em "Download"
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        // Adicionar extensão .pdf se não existir
        let filename = title || 'document';
        if (!filename.toLowerCase().endsWith('.pdf')) {
          filename += '.pdf';
        }
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        toast.success(language === 'pt-BR' ? 'Download iniciado' : 'Download started');
      } catch (error) {
        console.error('❌ Download error:', error);
        toast.error(language === 'pt-BR' ? 'Erro no download' : 'Download error');
      }
    } else {
      // SEMPRE abrir em modal (mobile ou desktop)
      setPdfModalData({ url, title });
      setShowPDFModal(true);
    }
  };
  
  // Exportar histórico de atividades
  const exportActivities = async (format) => {
    try {
      const token = sessionStorage.getItem('access_token');
      const params = new URLSearchParams();
      params.append('format', format);
      if (notificationHistoryFilter.dateFrom) params.append('from_date', notificationHistoryFilter.dateFrom);
      if (notificationHistoryFilter.dateTo) params.append('to_date', notificationHistoryFilter.dateTo);
      if (notificationHistoryFilter.pm) params.append('pm', notificationHistoryFilter.pm);
      if (notificationHistoryFilter.tipo) params.append('tipo', notificationHistoryFilter.tipo);
      
      const response = await axios.get(`${API}/admin/activities/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Criar download
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `atividades_${new Date().toISOString().slice(0,10)}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'pt-BR' ? `✅ ${format.toUpperCase()} baixado!` : `✅ ${format.toUpperCase()} downloaded!`);
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error(language === 'pt-BR' ? 'Erro ao exportar' : 'Export error');
    }
  };

  const loadTrash = async () => {
    try {
      const res = await axios.get(`${API}/trash`);
      setTrash(res.data);
    } catch (e) {}
  };

  // ===== FUNÇÕES DO CALENDÁRIO (NOVO - separado do Daily Work) =====
  
  const loadScheduleJobs = async (month, year) => {
    try {
      setCalendarLoading(true);
      const m = month !== undefined ? month : selectedDate.getMonth();
      const y = year !== undefined ? year : selectedDate.getFullYear();
      
      const { data } = await axios.get(`${API}/schedules`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
      });
      
      // Filtrar jobs do mês/ano usando string comparison (evita bug de timezone)
      const monthStr = String(m + 1).padStart(2, '0');
      const prefix = `${y}-${monthStr}`;
      
      const filtered = data.filter(job => {
        const d = job.date || '';
        return d.startsWith(prefix);
      });
      
      setScheduleJobs(filtered);
      
      // Atualizar jobs do dia selecionado
      updateSelectedDayJobs(selectedDate, filtered);
    } catch (error) {
      console.error('Erro ao carregar schedule:', error);
    } finally {
      setCalendarLoading(false);
    }
  };


  // ===== LABOR BILL AI FUNCTIONS =====
  
  // Enfileira um trabalho para o 2º IA (Labor Bill). Processa 1 por vez.
  // Se já estiver processando, o novo entra na fila e o contador vira 1/2, 1/3, ...
  const startLaborBillAI = (jobId) => {
    if (!jobId) return;
    // Evita duplicar o mesmo job na fila / em processamento
    if (aiQueueRef.current.includes(jobId) || aiProcessingJobId === jobId) return;
    aiQueueRef.current.push(jobId);
    aiQueueTotalRef.current += 1;
    setAiQueueInfo(`${aiQueueDoneRef.current + 1}/${aiQueueTotalRef.current}`);
    if (!aiQueueBusyRef.current) {
      runAiQueue();
    }
  };

  // Worker da fila: processa os jobs em sequência, sem travar se novos entrarem.
  const runAiQueue = async () => {
    if (aiQueueBusyRef.current) return;
    aiQueueBusyRef.current = true;
    setShowAIToast(true);
    setAiToastError(false);
    setAiToastComplete(false);

    while (aiQueueRef.current.length > 0) {
      const jobId = aiQueueRef.current.shift();
      setAiProcessingJobId(jobId);
      setAiToastProgress(0);
      setAiToastStep('Iniciando IA...');
      setAiToastComplete(false);
      setAiToastError(false);
      setAiQueueInfo(`${aiQueueDoneRef.current + 1}/${aiQueueTotalRef.current}`);

      try {
        console.log('🤖 IA Labor Bill (fila) job:', jobId, aiQueueDoneRef.current + 1, '/', aiQueueTotalRef.current);
        await axios.post(
          `${API}/schedules/${jobId}/process-labor-bill-ai`,
          {},
          { headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } }
        );
        await new Promise(r => setTimeout(r, 600)); // deixa a task iniciar no servidor
        await awaitLaborBillAIProgress(jobId);
      } catch (error) {
        console.error('❌ Erro no item da fila IA:', error);
        // Não trava a fila: marca erro breve e segue para o próximo
        setAiToastError(true);
        setAiToastStep('Erro neste trabalho — seguindo para o próximo');
        await new Promise(r => setTimeout(r, 1500));
        setAiToastError(false);
      }

      aiQueueDoneRef.current += 1;
    }

    // Fila esvaziada: finaliza o lote
    aiQueueBusyRef.current = false;
    aiQueueTotalRef.current = 0;
    aiQueueDoneRef.current = 0;
    setAiProcessingJobId(null);
    setAiQueueInfo('');
    setAiToastComplete(true);
    setTimeout(() => setShowAIToast(false), 3000);
  };

  // Acompanha o progresso de UM job até concluir/erro. Resolve a Promise ao terminar.
  const awaitLaborBillAIProgress = (jobId) => new Promise((resolve) => {
    let attempts = 0;
    const tick = async () => {
      try {
        const { data } = await axios.get(
          `${API}/schedules/${jobId}/labor-bill-ai-progress`,
          { headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } }
        );
        setAiToastProgress(data.progress || 0);
        if (data.current_step) setAiToastStep(data.current_step);

        if (data.error) { resolve(); return; }
        if ((data.progress || 0) >= 100) { resolve(); return; }

        attempts += 1;
        if (attempts < 200) {
          setTimeout(tick, 1000);
        } else {
          resolve(); // teto de segurança (~3min)
        }
      } catch (error) {
        attempts += 1;
        if (attempts < 10) {
          setTimeout(tick, 1500);
        } else {
          resolve();
        }
      }
    };
    tick();
  });
  
  const loadLaborBillDraft = async (jobId) => {
    if (!jobId) {
      console.log('ℹ️ Nenhum jobId fornecido para buscar draft');
      return;
    }
    
    try {
      setDraftLoading(true);
      console.log('🔍 Buscando AI data do job:', jobId);
      
      // Buscar job completo
      const { data: jobs } = await axios.get(
        `${API}/schedules`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } }
      );
      
      const job = jobs.find(j => j.id === jobId);
      
      if (!job) {
        console.log('⚠️ Job não encontrado');
        setLaborBillDraft(null);
        setAiFilledFields([]);
        return;
      }
      
      if (job.ai_processed && job.ai_labor_bill) {
        console.log('🤖 AI data encontrada no job!');
        console.log('📊 AI Fields:', job.ai_filled_fields);
        console.log('📊 Labor Data:', job.ai_labor_bill);
        
        // Criar draft sintético do job para compatibilidade
        const syntheticDraft = {
          status: 'ai_generated',
          ai_filled_fields: job.ai_filled_fields || [],
          labor_bill_data: job.ai_labor_bill || {},
          extra_labor: job.ai_extra_labor || [],
          ai_metadata: job.ai_metadata || {},
          pdf_extracted_data: job.ai_pdf_data || {}
        };
        
        setLaborBillDraft(syntheticDraft);
        
        // Atualizar formData com metadados
        setFormData(prev => ({
          ...prev,
          total_windows: job.ai_metadata?.total_windows || prev.total_windows,
          total_doors: job.ai_metadata?.total_doors || prev.total_doors
        }));
        
        // Atualizar currentBill com os dados da IA
        const draftData = job.ai_labor_bill || {};
        // Soma as quantidades da IA por item do catálogo ATUAL (Designer), ligando pelo qty_field
        // (várias chaves internas podem cair no mesmo item — ex.: casement + glider = Casement/Glider)
        const catalogItems = getDefaultItems();
        const qtyByDescription = {};
        Object.keys(draftData).forEach(key => {
          const q = Number(draftData[key]) || 0;
          const item = q > 0 ? findAiItem(catalogItems, key, aiFieldMap) : null;
          if (item) qtyByDescription[item.description] = (qtyByDescription[item.description] || 0) + q;
        });
        setAiFilledFields(Object.keys(qtyByDescription));
        
        setCurrentBill(prev => {
          const defaultItems = prev?.items || catalogItems;

          const updatedItems = defaultItems.map(item => {
            if (qtyByDescription[item.description] > 0) {
              return { ...item, qty: qtyByDescription[item.description] };
            }
            return item;
          });
          
          return {
            ...prev,
            items: updatedItems,
            extra_items: job.ai_extra_labor || prev?.extra_items || []
          };
        });
      } else {
        console.log('ℹ️ Job sem dados da IA');
        setLaborBillDraft(null);
        setAiFilledFields([]);
      }
    } catch (error) {
      console.error('❌ Erro ao buscar job:', error);
      setLaborBillDraft(null);
      setAiFilledFields([]);
    } finally {
      setDraftLoading(false);
    }
  };
  
  const convertDraftToItems = (draftData) => {
    const items = [];
    
    // Mapear campos do draft para items do Labor Bill
    const fieldMapping = {
      'unit_up_to_120': { description: 'Unit Up To 120 UI', category: 'left' },
      'unit_over_120': { description: 'Unit Over 120 UI', category: 'left' },
      'casement_up_to_120': { description: 'Casement up to 120', category: 'left' },
      'casement_over_120': { description: 'Casement over 120', category: 'left' },
      'glider_up_to_120': { description: 'Glider Up To 120 UI', category: 'left' },
      'glider_over_120': { description: 'Glider Over 120 UI', category: 'left' },
      'single_panel': { description: 'Single panel', category: 'left' },
      '2_panel_glider': { description: '2 panel glider', category: 'left' },
      'dbl_hinge_hinge_w_fixed_pnl': { description: 'Dbl Hinge/Hinge w/ Fixed PNL', category: 'left' },
      '8_door': { description: "8' Door", category: 'left' },
      'per_lf_over_8': { description: 'Per LF over 8\'', category: 'left' },
      'sidelights': { description: 'Sidelite', category: 'right' },
      'transom': { description: 'Transom', category: 'right' },
      'brickmold': { description: 'Brickmould', category: 'right' },
      'casing': { description: 'Casing Interior', category: 'right' },
      'replace_stops_l_trim': { description: 'Replace Stops or L Trim', category: 'right' },
      'lswp_opening': { description: 'LSWP/Opening', category: 'right' },
      'second_floor_open_interior': { description: '2nd Floor Open Interior', category: 'right' },
      'rr_vinyl_siding': { description: 'R&R Vinyl Siding', category: 'right' },
      'blinds_rr_storms': { description: 'Blinds R&R/Storms', category: 'right' },
      'trip_charge_50': { description: 'Trip Charge 50', category: 'left' },
      'trip_charge_75': { description: 'Trip Charge 75', category: 'left' },
      'trip_charge_100': { description: 'Trip Charge 100', category: 'left' },
      'trip_charge_250': { description: 'Trip Charge 250', category: 'left' },
      'minimum_job': { description: 'Minimum Job', category: 'right' },
      'minimum_daily': { description: 'Minimum Daily', category: 'right' }
    };
    
    // Converter cada campo com valor > 0
    Object.keys(draftData).forEach(field => {
      const qty = draftData[field];
      if (qty > 0 && fieldMapping[field]) {
        items.push({
          description: fieldMapping[field].description,
          qty: qty,
          unit_price: 0,
          total: 0,
          category: fieldMapping[field].category
        });
      }
    });
    
    return items;
  };
  
  // ===== JOB HOVER POPOVER FUNCTIONS =====
  
  const handleJobMouseEnter = async (jobs, event) => {
    console.log('🖱️ Mouse enter - jobs:', jobs.length);
    console.log('🖱️ User role:', user?.role, '| viewAsTeam:', viewAsTeam);
    
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    
    // PM sempre mostra (role pode ser 'user' ou começar com 'pm')
    // Admin mostra apenas se filtrado
    const shouldShow = user?.role !== 'admin' || 
                       (user?.role === 'admin' && viewAsTeam !== 'ALL');
    
    console.log('🖱️ shouldShow:', shouldShow);
    
    if (!shouldShow) return;
    
    // Filtrar jobs com PDF + SERVICEs com notas (balão do Scope of Work)
    const jobsList = Array.isArray(jobs) ? jobs : [jobs];
    const jobsWithPdf = jobsList.filter(j =>
      j.has_pdf || ((j.job_type || '').toUpperCase() === 'SERVICE' && j.service_notes)
    );
    console.log('Jobs com PDF/Service:', jobsWithPdf.length);
    
    if (jobsWithPdf.length === 0) return;
    
    // Calcular posições
    const rect = event.currentTarget.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;
    const popoverWidth = isMobile ? 280 : 350;
    
    const positions = jobsWithPdf.map((job, index) => {
      let x, y;
      
      if (isMobile) {
        // Mobile: Empilhar verticalmente 
        x = Math.max(10, (window.innerWidth - popoverWidth) / 2);
        // Altura generosa: 300px por popover (evita sobreposição)
        y = rect.bottom + 5 + (index * 300);
      } else {
        // Desktop: Lado a lado com 1px de espaço
        const desktopWidth = 340; // Largura FIXA para cálculo preciso
        x = rect.right + 10 + (index * (desktopWidth + 1));
        y = rect.top;
        
        if (x + desktopWidth > window.innerWidth) {
          x = rect.right + 10;
          y = rect.top + (index * 300);
        }
      }
      
      return { x, y };
    });
    
    setPopoverPositions(positions);
    setHoveredJobs(jobsWithPdf.map(j => j.id));
    setHoveredJobsData(jobsWithPdf.map(j => ({ ...j, _loading: true })));
    
    // Buscar dados
    try {
      const { data: allJobs } = await axios.get(`${API}/schedules`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
      });
      
      const updated = jobsWithPdf.map(job => {
        const full = allJobs.find(j => j.id === job.id) || job;
        if ((full.job_type || '').toUpperCase() === 'SERVICE' && full.service_notes) {
          return { ...full, _service: true };
        }
        if (full?.ai_processed && full?.ai_pdf_data) {
          return { ...full, ...full.ai_pdf_data };
        } else if (full?.has_pdf) {
          return { ...full, _processing: true };
        }
        return job;
      });
      
      setHoveredJobsData(updated);
    } catch (error) {
      console.error('Erro:', error);
    }
  };
  
  const handleJobMouseLeave = () => {
    const timer = setTimeout(() => {
      setHoveredJobs([]);
      setHoveredJobsData([]);
      hoverTimerRef.current = null;
    }, 300);
    hoverTimerRef.current = timer;
  };
  
  const handlePopoverMouseEnter = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };
  
  const handlePopoverMouseLeave = () => {
    setHoveredJobs([]);
    setHoveredJobsData([]);
  };
  
  const removeAIFieldHighlight = async (fieldDescription, jobId) => {
    // Remover campo de aiFilledFields localmente
    setAiFilledFields(prev => prev.filter(f => f !== fieldDescription));
    
    // Atualizar no backend (opcional, para persistir)
    if (jobId) {
      try {
        await axios.put(
          `${API}/labor-bills/draft/${jobId}/remove-ai-field`,
          fieldDescription,
          { headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}`, 'Content-Type': 'application/json' } }
        );
      } catch (error) {
        console.error('Erro ao remover destaque IA:', error);
      }
    }
  };
  
  // ===== OPEN IN MAPS FUNCTION =====
  
  const openInMaps = (address, city, zipCode) => {
    const fullAddress = `${address}${city ? `, ${city}` : ''}${zipCode ? `, ${zipCode}` : ''}`;
    
    // Verificar se é app nativo (Capacitor)
    const isNative = window.Capacitor && window.Capacitor.isNativePlatform();
    
    if (isNative) {
      const platform = window.Capacitor.getPlatform();
      
      if (platform === 'ios') {
        // iOS: Apple Maps (permite escolher app)
        const url = `maps://?address=${encodeURIComponent(fullAddress)}`;
        window.open(url, '_system');
      } else if (platform === 'android') {
        // Android: Geo protocol (abre sheet de escolha)
        const url = `geo:0,0?q=${encodeURIComponent(fullAddress)}`;
        window.open(url, '_system');
      }
    } else {
      // Browser: Google Maps web
      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
      window.open(url, '_blank');
    }
  };

  const loadBlockedDays = async () => {
    try {
      const { data } = await axios.get(`${API}/schedules/blocked-days`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
      });
      setBlockedDays(data || []);
      console.log('🔒 Dias bloqueados carregados:', data?.length || 0);
    } catch (error) {
      console.error('Erro ao carregar dias bloqueados:', error);
      setBlockedDays([]);
    }
  };

  // Fechar popover ao clicar fora ou scroll
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (hoveredJobs.length > 0 && !event.target.closest('[data-popover]') && !event.target.closest('button[data-calendar-day]')) {
        setHoveredJobs([]);
        setHoveredJobsData([]);
      }
    };
    const handleScroll = () => {
      if (hoveredJobs.length > 0) {
        setHoveredJobs([]);
        setHoveredJobsData([]);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [hoveredJobs]);

  const toggleDayBlock = async (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    
    // Verificar quantos PMs estão bloqueados nesse dia
    const blocksForDay = blockedDays.filter(d => d.date === dateStr);
    
    if (blocksForDay.length > 0) {
      // Dia tem bloqueios - perguntar qual desbloquear
      if (blocksForDay.length === 1 && blocksForDay[0].team !== 'ALL') {
        const team = blocksForDay[0].team;
        if (!window.confirm(`Desbloquear ${team}?`)) return;
        
        try {
          await axios.delete(`${API}/schedules/block-day/${dateStr}/${team}`, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
          });
          toast.success(`🔓 ${team} desbloqueado!`);
          await loadBlockedDays();
        } catch (error) {
          toast.error('Erro ao desbloquear');
        }
      } else {
        const options = blocksForDay.map(b => b.team).join(', ');
        const choice = window.prompt(`PMs bloqueados: ${options}\n\nDigite qual desbloquear (ou "ALL" para todos):`);
        
        if (!choice) return;
        
        try {
          await axios.delete(`${API}/schedules/block-day/${dateStr}/${choice.toUpperCase()}`, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
          });
          toast.success(`🔓 ${choice} desbloqueado!`);
          await loadBlockedDays();
        } catch (error) {
          toast.error('Erro ao desbloquear');
        }
      }
    } else {
      // Nenhum bloqueio - abrir modal para bloquear
      setShowBlockDayModal(true);
    }
  };

  const handleBlockDay = async (team, reason) => {
    const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
    
    try {
      await axios.post(`${API}/schedules/block-day`, {
        date: dateStr,
        team: team,
        reason: reason || undefined
      }, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
      });
      
      toast.success(`🔒 ${team} bloqueado!`);
      await loadBlockedDays();
      setShowBlockDayModal(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao bloquear';
      toast.error(errorMsg);
    }
  };



  const handleDeleteScheduleJob = async (jobId) => {
    try {
      const token = sessionStorage.getItem('access_token');
      await axios.delete(`${API}/schedules/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('✅ Trabalho removido com sucesso!');
      
      // Recarregar jobs do mês atual
      loadScheduleJobs(selectedDate.getMonth(), selectedDate.getFullYear());
    } catch (error) {
      console.error('Erro ao remover trabalho:', error);
      toast.error(getErrMsg(error, 'Erro ao remover trabalho'));
    }
  };

  // Refs para drag-to-merge por TOQUE (iPhone/iPad)
  const longPressTimerRef = useRef(null);
  const touchStartRef = useRef(null);
  const draggedJobRef = useRef(null);
  const dragOverJobIdRef = useRef(null);
  const scheduleJobsRef = useRef([]);
  useEffect(() => { scheduleJobsRef.current = scheduleJobs; }, [scheduleJobs]);

  const handleTouchDragMove = useCallback((e) => {
    if (!draggedJobRef.current) return;
    if (e.cancelable) e.preventDefault(); // bloqueia o scroll enquanto arrasta
    const t = e.touches && e.touches[0];
    if (!t) return;
    const el = document.elementFromPoint(t.clientX, t.clientY);
    const card = el && el.closest('[data-testid^="schedule-job-card-"]');
    const overId = card ? (card.getAttribute('data-testid') || '').replace('schedule-job-card-', '') : null;
    const newOver = (overId && overId !== draggedJobRef.current.id) ? overId : null;
    if (dragOverJobIdRef.current !== newOver) {
      dragOverJobIdRef.current = newOver;
      setDragOverJobId(newOver);
    }
  }, []);

  const handleTouchDragEnd = useCallback(() => {
    document.removeEventListener('touchmove', handleTouchDragMove);
    document.removeEventListener('touchend', handleTouchDragEnd);
    document.body.style.overflow = '';
    const src = draggedJobRef.current;
    const overId = dragOverJobIdRef.current;
    draggedJobRef.current = null;
    dragOverJobIdRef.current = null;
    setDraggedJob(null);
    setDragOverJobId(null);
    if (src && overId && overId !== src.id) {
      const target = (scheduleJobsRef.current || []).find(j => j.id === overId);
      if (target) setMergeConfirm({ source: src, target });
    }
  }, [handleTouchDragMove]);

  const startTouchLongPress = (job, e, isManager) => {
    if (!isManager) return;
    const t = e.touches && e.touches[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY };
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    // Segurar 2 segundos para "pegar" o card
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      draggedJobRef.current = job;
      dragOverJobIdRef.current = null;
      setDraggedJob(job);
      if (navigator.vibrate) { try { navigator.vibrate(40); } catch (err) {} }
      document.body.style.overflow = 'hidden';
      document.addEventListener('touchmove', handleTouchDragMove, { passive: false });
      document.addEventListener('touchend', handleTouchDragEnd, { passive: false });
    }, 2000);
  };

  const cancelTouchLongPressIfMoved = (e) => {
    if (longPressTimerRef.current && touchStartRef.current) {
      const t = e.touches && e.touches[0];
      if (t && (Math.abs(t.clientX - touchStartRef.current.x) > 12 || Math.abs(t.clientY - touchStartRef.current.y) > 12)) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }
  };

  const clearTouchLongPress = () => {
    if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
  };

  const handleUndoMerge = async (undoId) => {
    if (!undoId) return;
    try {
      const token = sessionStorage.getItem('access_token');
      await axios.post(`${API}/schedules/merge/undo`, { undo_id: undoId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (undoTimerRef.current) { clearTimeout(undoTimerRef.current); undoTimerRef.current = null; }
      setUndoMerge(null);
      toast.success(language === 'pt-BR' ? '↩️ Merge desfeito!' : '↩️ Merge undone!');
      await loadScheduleJobs(selectedDate.getMonth(), selectedDate.getFullYear());
    } catch (error) {
      console.error('Erro ao desfazer merge:', error);
      toast.error(getErrMsg(error, language === 'pt-BR' ? 'Erro ao desfazer' : 'Undo error'));
    }
  };

  const handleMergeJobs = async (source, target) => {
    if (!source || !target || source.id === target.id) return;
    try {
      const token = sessionStorage.getItem('access_token');
      const resp = await axios.post(`${API}/schedules/merge`, { source_id: source.id, target_id: target.id }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const undoId = resp?.data?.undo_id;
      setMergeConfirm(null);
      setDraggedJob(null);
      setDragOverJobId(null);
      await loadScheduleJobs(selectedDate.getMonth(), selectedDate.getFullYear());
      toast.success(language === 'pt-BR' ? '✅ Trabalhos mesclados!' : '✅ Jobs merged!');
      // Banner de "Desfazer" (próprio, dentro da árvore React) por 12s
      if (undoId) {
        setUndoMerge({ undoId });
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        undoTimerRef.current = setTimeout(() => { setUndoMerge(null); undoTimerRef.current = null; }, 12000);
      }
    } catch (error) {
      console.error('Erro ao mesclar trabalhos:', error);
      toast.error(getErrMsg(error, language === 'pt-BR' ? 'Erro ao mesclar trabalhos' : 'Merge error'));
    }
  };

  
  const updateSelectedDayJobs = (date, jobs = scheduleJobs) => {
    // Usar data local (não UTC) para evitar bug de timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    // Filtrar jobs que começam neste dia OU que incluem este dia (multi-dia)
    const dayJobs = jobs.filter(job => 
      job.date === dateStr || isJobInRange(job, dateStr)
    );
    
    // Ordenar por equipe (PM1, PM2...) e, dentro da equipe, por HORÁRIO de início (1º -> último)
    const timeToMin = (t) => {
      if (!t || !/^\d{1,2}:\d{2}/.test(t)) return 99999; // sem horário vai para o fim
      const [h, m] = t.split(':').map(Number);
      return (h || 0) * 60 + (m || 0);
    };
    const sortedJobs = dayJobs.sort((a, b) => {
      const numA = parseInt((a.team || '').replace('PM', '')) || 999;
      const numB = parseInt((b.team || '').replace('PM', '')) || 999;
      if (numA !== numB) return numA - numB;
      return timeToMin(a.time) - timeToMin(b.time);
    });
    
    setSelectedDayJobs(sortedJobs);
  };
  
  const selectDay = (day) => {
    const newDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
    setSelectedDate(newDate);
    updateSelectedDayJobs(newDate);
  };
  
  const changeMonth = (direction) => {
    const current = selectedDate;
    // Usar dia 1 para evitar bug de overflow (ex: 31 março → setMonth(abril) → 1 maio)
    const newDate = new Date(current.getFullYear(), current.getMonth() + direction, 1);
    setSelectedDate(newDate);
    loadScheduleJobs(newDate.getMonth(), newDate.getFullYear());
  };
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return days;
  };
  
  const isJobInRange = (job, dateStr) => {
    if (!job.duration_days || job.duration_days === 1) return false;
    
    // Usar parse manual para evitar bug de timezone
    const [sy, sm, sd] = job.date.split('-').map(Number);
    const [cy, cm, cd] = dateStr.split('-').map(Number);
    const jobStart = new Date(sy, sm - 1, sd);
    const jobEnd = new Date(sy, sm - 1, sd + job.duration_days - 1);
    const checkDate = new Date(cy, cm - 1, cd);
    
    return checkDate >= jobStart && checkDate <= jobEnd;
  };

  // Load activity logs
  const loadActivityLogs = async (page = 1, perPage = null, userFilter = null) => {
    try {
      setActivityLoading(true);
      const limit = perPage || activityFilters.limit || 50;
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      const filterUser = userFilter !== undefined ? userFilter : activityFilters.user;
      if (filterUser) params.append('user', filterUser);
      
      const { data } = await axios.get(`${API}/admin/activity-logs?${params}`);
      setActivityLogs(data.logs || []);
      setActivityFilters(prev => ({ ...prev, page, limit, total: data.total || 0, pages: data.pages || 0 }));
    } catch (e) {
      console.error('Activity logs error:', e);
      setActivityLogs([]);
    } finally {
      setActivityLoading(false);
    }
  };

  // Load activity statistics
  const loadActivityStats = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/activity-stats`);
      setActivityStats(data);
    } catch (e) {
      toast.error("Erro ao carregar estatísticas de atividade");
    }
  };

  // Funções de notificação interna removidas

  // ================================
  // NOTIFICATION SYSTEM (baseado em tickets)
  // ================================

  // Admin: criar notificação (EXATAMENTE copiado de create_ticket)
  const createNotification = async () => {
    if (!adminNotificationForm.title || !adminNotificationForm.message) {
      toast.error('Preencha título e mensagem');
      return;
    }

    try {
      // USAR FORMDATA EXATAMENTE IGUAL TICKETS
      const formData = new FormData();
      formData.append('title', adminNotificationForm.title);
      formData.append('text', adminNotificationForm.message);
      formData.append('target_user', adminNotificationForm.targetUser);
      
      await axios.post(`${API}/admin/notifications`, formData, { 
        headers: { 'Content-Type': 'multipart/form-data' } 
      });
      
      toast.success('📨 Notificação enviada com sucesso!');
      
      // Reset form
      setAdminNotificationForm({
        title: '',
        message: '',
        targetUser: 'ALL'
      });
      
      // Recarregar lista
      await loadAdminNotifications();
      
    } catch (error) {
      toast.error('Erro ao enviar notificação: ' + (error.response?.data?.detail || error.message));
    }
  };

  // User: carregar notificações (copiado de loadMyTickets)
  const loadMyNotifications = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`);
      setUserNotifications(data || []);
      
      // Filtrar apenas notificações não lidas
      const unreadNotifications = (data || []).filter(n => n.status === 'unread');
      
      // APENAS mensagens (message_thread) abrem o pop-up no login. As notificações de
      // ATIVIDADE (labor_bill_*, rental_*, payroll, etc.) ficam SOMENTE no sino/histórico
      // — não bloqueiam a tela com um modal (evita o "modal vazio" a cada login).
      const popupNotifications = unreadNotifications
        .filter(n => (n.tipo === 'message_thread' || n.type === 'message_thread'))
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      
      if (popupNotifications.length > 0) {
        // Atualizar fila de notificações (só mensagens)
        setNotificationQueue(popupNotifications);
        
        // Mostrar primeira mensagem da fila
        if (!showOldNotificationModal) {
          setNotificationView(popupNotifications[0]);
          setShowOldNotificationModal(true);
        }
      }
    } catch (error) {
      console.error('❌ Failed to load notifications:', error);
    }
  };

  // User: abrir notificação específica (copiado de openMyTicket)
  const openMyNotification = async (notification) => {
    try {
      console.log('🔔 Opening notification:', notification.id);
      const { data } = await axios.get(`${API}/notifications/${notification.id}`);
      console.log('✅ Notification data received:', data);
      setNotificationView(data);
      setShowOldNotificationModal(true);
    } catch (error) {
      console.error('❌ Error opening notification:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      // Não mostrar toast de erro - só log para debug
    }
  };

  // Mostrar próxima notificação da fila
  const showNextNotification = () => {
    // Remover a notificação atual da fila
    const remainingQueue = notificationQueue.slice(1);
    setNotificationQueue(remainingQueue);
    
    // Atualizar histórico do sino após marcar como lida
    loadNotificationHistory();
    
    // Se há mais notificações na fila, mostrar a próxima
    if (remainingQueue.length > 0) {
      setNotificationView(remainingQueue[0]);
      setShowOldNotificationModal(true);
    } else {
      // Não há mais notificações
      setShowOldNotificationModal(false);
      setNotificationView(null);
    }
  };

  // Marcar todas notificações como lidas ao abrir dropdown
  const markAllNotificationsAsRead = async () => {
    try {
      if (unreadCount > 0) {
        await axios.post(`${API}/notifications/mark-all-read`);
        
        // Atualizar estado local imediatamente
        const updatedHistory = notificationHistory.map(n => ({...n, status: 'read'}));
        setNotificationHistory(updatedHistory);
        setUnreadCount(0);
        
        console.log(`✅ Marcadas todas as notificações como lidas`);
      }
    } catch (error) {
      console.error('❌ Error marking notifications as read:', error);
    }
  };

  // Carregar todos os usuários do sistema
  const loadAllUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`);
      // Filtrar apenas usuários não-admin
      const pmUsers = (data || []).filter(user => user.role !== 'admin');
      setAllUsers(pmUsers);
    } catch (error) {
      console.error('❌ Failed to load users:', error);
    }
  };

  // Upload de vídeo para splash screen
  const uploadNewVideo = async () => {
    if (!newVideoForm.name || !newVideoForm.file) {
      toast.error('Preencha nome e selecione arquivo');
      return;
    }

    try {
      setVideoUploading(true);
      
      const formData = new FormData();
      formData.append('video_file', newVideoForm.file);
      formData.append('video_name', newVideoForm.name);
      
      // Garantir token de autenticação
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        toast.error('Token não encontrado. Faça login novamente.');
        return;
      }
      
      const { data } = await axios.post(`${API}/admin/splash-videos`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      toast.success(`🎬 ${data.message} (${data.size})`);
      
      // Reset form
      setNewVideoForm({ name: '', file: null });
      // Reset input file
      const fileInput = document.querySelector('input[type="file"][accept*="video"]');
      if (fileInput) fileInput.value = '';
      
      // Recarregar lista
      await loadVideosList();
      
    } catch (error) {
      toast.error('Erro ao enviar vídeo: ' + (error.response?.data?.detail || error.message));
    } finally {
      setVideoUploading(false);
    }
  };

  // Carregar lista de vídeos
  const loadVideosList = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/splash-videos`);
      setVideosList(data || []);
    } catch (error) {
      console.error('❌ Failed to load videos:', error);
    }
  };

  // Criar ticket rápido da Home
  const createQuickTicket = async () => {
    if (!quickTicketForm.title.trim()) {
      toast.error(language === 'pt-BR' ? 'Título é obrigatório' : 'Title is required');
      return;
    }

    try {
      console.log('🎫 Quick Ticket - Debug detalhado:');
      console.log('   Title:', quickTicketForm.title);
      console.log('   Message:', quickTicketForm.message);
      console.log('   API URL:', `${API}/tickets`);
      console.log('   Token exists:', !!sessionStorage.getItem('access_token'));
      console.log('   Axios headers:', axios.defaults.headers.common);

      // FormData sem header explícito
      const form = new FormData();
      form.append('title', quickTicketForm.title);
      form.append('text', quickTicketForm.message || '');
      
      // Adicionar arquivos anexados
      if (quickTicketFiles.length > 0) {
        quickTicketFiles.forEach((file) => {
          form.append('files', file);
        });
        console.log('   Files attached:', quickTicketFiles.length);
      }
      
      console.log('   FormData criado:');
      for (let [key, value] of form.entries()) {
        console.log(`     ${key}:`, value);
      }

      const response = await axios.post(`${API}/tickets`, form, {
        headers: {
          'Authorization': axios.defaults.headers.common['Authorization'],
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('✅ Sucesso:', response.data);
      toast.success(language === 'pt-BR' ? '✅ Ticket criado com sucesso!' : '✅ Ticket created successfully!');
      
      // Reset form
      setQuickTicketForm({ title: '', message: '' });
      setQuickTicketFiles([]);
      setShowQuickTicketModal(false);
      
      // Atualizar stats
      await loadUserTicketStats();
      
    } catch (error) {
      console.error('❌ ERRO DETALHADO Quick Ticket:');
      console.error('   Full error object:', error);
      console.error('   Error name:', error.name);
      console.error('   Error message:', error.message);
      console.error('   Response status:', error.response?.status);
      console.error('   Response data:', error.response?.data);
      console.error('   Response headers:', error.response?.headers);
      console.error('   Config URL:', error.config?.url);
      console.error('   Config method:', error.config?.method);
      console.error('   Config headers:', error.config?.headers);
      
      const errorDetail = error.response?.data?.detail || error.message;
      toast.error(language === 'pt-BR' ? `Erro: ${errorDetail}` : `Error: ${errorDetail}`);
    }
  };

  // Admin: carregar notificações paginadas
  const handleCleanupFomDuplicates = async () => {
    try {
      const { data: preview } = await axios.get(`${API}/admin/maintenance/fom-duplicates`);
      const toRemove = preview.duplicates_to_remove || 0;
      const toHide = preview.history_copies_to_hide || 0;
      const total = toRemove + toHide;
      if (total === 0) { toast.success('Nenhuma duplicata antiga encontrada. Tudo limpo! ✅'); return; }
      if (!window.confirm(`Foram encontrados ${total} registros antigos "FOM revisou" a ajustar:\n• ${toRemove} duplicatas serão removidas\n• ${toHide} cópias extras (ADMIN/PM) serão ocultadas do Histórico\n\nResultado: exatamente 1 registro por evento no Histórico. Ação irreversível. Continuar?`)) return;
      const { data: res } = await axios.post(`${API}/admin/maintenance/fom-duplicates`, {});
      toast.success(`Concluído: ${res.removed} removidas, ${res.hidden} ocultadas. ✅`);
      loadAdminNotifications(1, notificationHistoryFilter);
    } catch (e) {
      toast.error('Erro ao limpar duplicatas: ' + (e?.response?.data?.detail || e.message));
    }
  };


  const loadAdminNotifications = async (page = 1, filters = {}, perPage = null) => {
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('limit', String(perPage || notifPageSize));
      
      if (filters.pm) params.append('pm', filters.pm);
      if (filters.status) params.append('status', filters.status);
      if (filters.tipo) params.append('tipo', filters.tipo);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      
      const { data } = await axios.get(`${API}/admin/notifications?${params.toString()}`);
      
      // Suportar resposta paginada e legada
      if (data.items) {
        setAdminNotifications(data.items);
        setNotificationHistoryTotal(data.total || 0);
        setNotifPage(data.page || 1);
        setNotifTotalPages(data.pages || 1);
      } else {
        setAdminNotifications(data || []);
        setNotificationHistoryTotal(data?.length || 0);
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
      setAdminNotifications([]);
    }
  };

  // Aplicar filtros do histórico
  const applyNotificationFilters = () => {
    loadAdminNotifications(1, notificationHistoryFilter);
  };

  // Buscar totais de Labor Bills (Criados/Enviados) respeitando filtros de PM/data
  const loadLaborBillCounts = async () => {
    try {
      const params = new URLSearchParams();
      if (notificationHistoryFilter.pm) params.append('pm', notificationHistoryFilter.pm);
      if (notificationHistoryFilter.dateFrom) params.append('date_from', notificationHistoryFilter.dateFrom);
      if (notificationHistoryFilter.dateTo) params.append('date_to', notificationHistoryFilter.dateTo);
      const { data } = await axios.get(`${API}/admin/notifications/labor-bill-counts?${params.toString()}`);
      setLaborBillCounts({ created: data.created || 0, sent: data.sent || 0 });
    } catch (e) {
      setLaborBillCounts({ created: 0, sent: 0 });
    }
  };

  // Alternar filtro rápido "Só Labor Bills"
  const toggleLaborBillsOnly = () => {
    const next = !laborBillsOnly;
    setLaborBillsOnly(next);
    if (next) {
      setSupervisorOnly(false);
      setLaborBillTab('created');
      loadLaborBillCounts();
      loadAdminNotifications(1, { ...notificationHistoryFilter, tipo: 'labor_bill_created' });
    } else {
      loadAdminNotifications(1, { ...notificationHistoryFilter, tipo: '' });
    }
  };

  // Selecionar aba de Labor Bills (Criados / Enviados)
  const selectLaborBillTab = (tab) => {
    setLaborBillTab(tab);
    loadAdminNotifications(1, { ...notificationHistoryFilter, tipo: tab === 'sent' ? 'labor_bill_sent' : 'labor_bill_created' });
  };

  // Buscar totais de atividades do Supervisor (FOM) respeitando filtros de PM/data
  const loadSupervisorCounts = async () => {
    try {
      const params = new URLSearchParams();
      if (notificationHistoryFilter.pm) params.append('pm', notificationHistoryFilter.pm);
      if (notificationHistoryFilter.dateFrom) params.append('date_from', notificationHistoryFilter.dateFrom);
      if (notificationHistoryFilter.dateTo) params.append('date_to', notificationHistoryFilter.dateTo);
      const { data } = await axios.get(`${API}/admin/notifications/supervisor-counts?${params.toString()}`);
      setSupervisorCounts({ total: data.total || 0, changed: data.changed || 0, unchanged: data.unchanged || 0 });
    } catch (e) {
      setSupervisorCounts({ total: 0, changed: 0, unchanged: 0 });
    }
  };

  // Alternar filtro rápido "Só Supervisor"
  const toggleSupervisorOnly = () => {
    const next = !supervisorOnly;
    setSupervisorOnly(next);
    if (next) {
      setLaborBillsOnly(false);
      setSupervisorTab('all');
      loadSupervisorCounts();
      loadAdminNotifications(1, { ...notificationHistoryFilter, tipo: 'supervisor' });
    } else {
      loadAdminNotifications(1, { ...notificationHistoryFilter, tipo: '' });
    }
  };

  // Selecionar aba do Supervisor (Todos / Alterados / Sem alterações)
  const SUPERVISOR_TAB_TIPO = { all: 'supervisor', changed: 'supervisor_changed', unchanged: 'supervisor_unchanged' };
  const selectSupervisorTab = (tab) => {
    setSupervisorTab(tab);
    loadAdminNotifications(1, { ...notificationHistoryFilter, tipo: SUPERVISOR_TAB_TIPO[tab] });
  };

  // Tipo efetivo do histórico considerando os filtros rápidos ativos (usado na paginação)
  const effectiveHistoryTipo = () => {
    if (supervisorOnly) return SUPERVISOR_TAB_TIPO[supervisorTab];
    if (laborBillsOnly) return laborBillTab === 'sent' ? 'labor_bill_sent' : 'labor_bill_created';
    return notificationHistoryFilter.tipo || '';
  };

  // Limpar filtros do histórico
  const clearNotificationFilters = () => {
    setNotificationHistoryFilter({
      pm: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    });
    loadAdminNotifications(1, {});
  };

  // Carregar histórico para o sino (ADMIN vê todas, USER vê só suas)
  const loadNotificationHistory = async () => {
    try {
      let data;
      if (user?.role === 'admin') {
        // ADMIN vê apenas notificações PARA o ADMIN (não todas do sistema)
        const response = await axios.get(`${API}/notifications`); // Usar mesmo endpoint que usuários
        data = response.data;
      } else {
        // Usuário vê só as suas
        const response = await axios.get(`${API}/notifications`);
        data = response.data;
      }
      
      // Ordenar da mais recente para mais antiga (para o dropdown)
      const sortedHistory = (data || []).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      setNotificationHistory(sortedHistory);
      
      // Contar não lidas para o badge do sino
      const unreadCount = sortedHistory.filter(n => n.status === 'unread').length;
      setUnreadCount(unreadCount);
      
    } catch (error) {
      console.error('❌ Failed to load notification history:', error);
    }
  };

  // Polling para notificações (similar aos tickets)
  useEffect(() => {
    if (user && isAuthenticated) {
      // Carregar lista de usuários se for admin
      if (user.role === 'admin') {
        loadAllUsers();
      }
      
      // Carregar notificações para usuários E histórico para o sino
      if (user.role !== 'admin') {
        loadMyNotifications();
      }
      loadNotificationHistory(); // Para todos (ADMIN e usuários)
      
      // Auto-refresh removido - usuário controla atualizações manualmente
      // return () => clearInterval(interval); // Removido
    }
  }, [user, isAuthenticated]);

  // Carregar lista de vídeos quando acessar aba vídeos
  useEffect(() => {
    if (user?.role === 'admin' && activeTab === 'admin' && adminSubTab === 'videos') {
      loadVideosList();
    }
  }, [user?.role, activeTab, adminSubTab]);

  // Recarregar relatórios quando mudar mês selecionado
  useEffect(() => {
    if (user && isAuthenticated && selectedMonth) { // Só se selectedMonth estiver definido
      console.log(`🔄 useEffect triggered - Mês mudou para: ${selectedMonth}`);
      loadWeeklyReportSummary(false); // false = não mostrar toast de erro
    }
  }, [selectedMonth, user?.username]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotificationDropdown && !event.target.closest('.notification-dropdown')) {
        setShowNotificationDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotificationDropdown]);

  // ===== HELPER CONTROL FUNCTIONS =====

  // Carregar equipes helper
  const loadHelperTeams = async () => {
    try {
      setHelperLoading(true);
      const { data } = await axios.get(`${API}/helper-teams`);
      setHelperTeams(data || []);
    } catch (error) {
      console.error('Erro ao carregar equipes helper:', error);
      toast.error('Erro ao carregar equipes');
    } finally {
      setHelperLoading(false);
    }
  };

  // Carregar controles
  const loadHelperControls = async (filters = {}) => {
    try {
      setHelperLoading(true);
      const params = new URLSearchParams();
      if (filters.ano) params.append('ano', filters.ano);
      if (filters.mes) params.append('mes', filters.mes);
      if (filters.team_id) params.append('team_id', filters.team_id);
      
      const { data } = await axios.get(`${API}/helper-controls?${params}`);
      setHelperControls(data || []);
    } catch (error) {
      console.error('Erro ao carregar controles:', error);
      toast.error('Erro ao carregar controles');
    } finally {
      setHelperLoading(false);
    }
  };
  
  // Carregar relatório avançado da Visão Mensal
  const loadAdvancedMonthlyReport = async () => {
    try {
      setLoadingAdvancedReport(true);
      
      // Calcular datas baseado no período selecionado
      let dataInicio, dataFim;
      const hoje = new Date();
      
      if (monthlyViewPeriodType === 'custom') {
        dataInicio = monthlyViewCustomStart;
        dataFim = monthlyViewCustomEnd;
      } else if (monthlyViewPeriodType === 'month') {
        // Mês atual
        dataInicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
        dataFim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];
      } else {
        // Semanas
        const weeks = parseInt(monthlyViewPeriodType.replace('weeks', ''));
        const daysAgo = weeks * 7;
        dataInicio = new Date(hoje.getTime() - (daysAgo * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];
        dataFim = hoje.toISOString().split('T')[0];
      }
      
      // Preparar equipes
      const equipesParam = monthlyViewTeamFilter;
      
      const { data } = await axios.get(`${API}/helper-controls/advanced-report`, {
        params: {
          data_inicio: dataInicio,
          data_fim: dataFim,
          equipes: equipesParam
        }
      });
      
      setAdvancedMonthlyData(data);
      toast.success('Relatório gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao carregar relatório avançado:', error);
      toast.error('Erro ao carregar relatório');
    } finally {
      setLoadingAdvancedReport(false);
    }
  };
  
  // Visualizar PDF do relatório avançado no modal
  const viewAdvancedReportPDF = async () => {
    try {
      let dataInicio, dataFim;
      const hoje = new Date();
      
      if (monthlyViewPeriodType === 'custom') {
        dataInicio = monthlyViewCustomStart;
        dataFim = monthlyViewCustomEnd;
      } else if (monthlyViewPeriodType === 'month') {
        dataInicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
        dataFim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];
      } else {
        const weeks = parseInt(monthlyViewPeriodType.replace('weeks', ''));
        const daysAgo = weeks * 7;
        dataInicio = new Date(hoje.getTime() - (daysAgo * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];
        dataFim = hoje.toISOString().split('T')[0];
      }
      
      const equipesParam = monthlyViewTeamFilter;
      const equipesStr = equipesParam === 'all' ? 'Todas' : equipesParam;
      const pdfUrl = `${API}/helper-controls/advanced-report/export-pdf?data_inicio=${dataInicio}&data_fim=${dataFim}&equipes=${equipesParam}`;
      
      // Baixar PDF com autenticação e criar blob URL
      const response = await axios.get(pdfUrl, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
      });
      
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      
      setPdfModalData({
        url: blobUrl,
        title: `Relatório Helper - ${equipesStr} (${dataInicio} a ${dataFim})`
      });
      setShowPDFModal(true);
      toast.success('Abrindo PDF...');
    } catch (error) {
      console.error('Erro ao visualizar PDF:', error);
      toast.error('Erro ao abrir PDF');
    }
  };
  
  // Exportar/Compartilhar PDF do relatório avançado
  const exportAdvancedReportPDF = async () => {
    try {
      let dataInicio, dataFim;
      const hoje = new Date();
      
      if (monthlyViewPeriodType === 'custom') {
        dataInicio = monthlyViewCustomStart;
        dataFim = monthlyViewCustomEnd;
      } else if (monthlyViewPeriodType === 'month') {
        dataInicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
        dataFim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];
      } else {
        const weeks = parseInt(monthlyViewPeriodType.replace('weeks', ''));
        const daysAgo = weeks * 7;
        dataInicio = new Date(hoje.getTime() - (daysAgo * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];
        dataFim = hoje.toISOString().split('T')[0];
      }
      
      const equipesParam = monthlyViewTeamFilter;
      const equipesStr = equipesParam === 'all' ? 'Todas' : equipesParam;
      const filename = `Relatorio_Helper_${equipesStr}_${dataInicio}_a_${dataFim}.pdf`;
      const pdfUrl = `${API}/helper-controls/advanced-report/export-pdf?data_inicio=${dataInicio}&data_fim=${dataFim}&equipes=${equipesParam}`;
      
      // Baixar PDF com autenticação
      const response = await axios.get(pdfUrl, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
      });
      
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      
      // Tentar usar Web Share API
      if (navigator.share && navigator.canShare) {
        const file = new File([pdfBlob], filename, { type: 'application/pdf' });
        
        const shareData = {
          files: [file],
          title: 'Relatório Helper',
          text: `Relatório Helper - ${equipesStr} (${dataInicio} a ${dataFim})`
        };
        
        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          toast.success('PDF compartilhado!');
          return;
        }
      }
      
      // Fallback: Download direto se Share API não disponível
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error('Erro ao exportar PDF');
    }
  };


  // ===== WAIVER FUNCTIONS =====
  
  // Carregar configurações padrão da CONTRATANTE
  const loadWaiverSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/waiver-settings`);
      console.log('📄 Settings carregadas do backend:', data);
      console.log('📄 Tem pm_home?', !!data?.pm_home);
      console.log('📄 Tem bimcle?', !!data?.bimcle);
      console.log('📄 PM Home tem assinatura?', !!data?.pm_home?.contratante_assinatura);
      console.log('📄 Bimcle tem assinatura?', !!data?.bimcle?.contratante_assinatura);
      setWaiverSettings(data);
      return data;
    } catch (error) {
      console.error('Erro ao carregar configurações do waiver:', error);
      return null;
    }
  };
  
  // Carregar waivers pendentes (para PM)
  const loadPendingWaivers = async () => {
    try {
      // Carregar TODOS os waivers do PM (pending E signed)
      const { data } = await axios.get(`${API}/waiver-files/my-waivers`);
      setPendingWaivers(data || []);
      console.log('📄 Meus waivers carregados:', data);
    } catch (error) {
      console.error('Erro ao carregar waivers:', error);
      setPendingWaivers([]);
    }
  };
  
  // Carregar todos os waivers (para Admin mostrar status)
  const loadAllWaivers = async () => {
    try {
      const { data } = await axios.get(`${API}/waiver-files`);
      return data || [];
    } catch (error) {
      console.error('Erro ao carregar waivers:', error);
      return [];
    }
  };
  
  // Deletar waiver (Admin only)
  const handleDeleteWaiver = async (pm_user) => {
    if (!window.confirm(`Deletar waiver de ${pm_user}? Isso permitirá criar um novo.`)) {
      return;
    }
    
    try {
      // Buscar waiver do PM
      const { data: waiver } = await axios.get(`${API}/waiver-files/${pm_user}`);
      if (waiver && waiver.id) {
        await axios.delete(`${API}/waiver-files/${waiver.id}`);
        toast.success('Waiver deletado com sucesso!');
        await loadAllTeamLeaders();
      }
    } catch (error) {
      console.error('Erro ao deletar waiver:', error);
      toast.error('Erro ao deletar waiver');
    }
  };
  
  // Salvar configurações da CONTRATANTE (ambas empresas)
  const handleSaveWaiverSettings = async (pmHomePayload, bimclePayload) => {
    try {
      // Salvar PM Home
      await axios.post(`${API}/waiver-settings`, pmHomePayload);
      // Salvar Bimcle
      await axios.post(`${API}/waiver-settings`, bimclePayload);
      
      // Atualizar estado local
      setWaiverSettings({
        pm_home: pmHomePayload,
        bimcle: bimclePayload
      });
      
      setShowWaiverSettingsModal(false);
      toast.success('Configurações de ambas empresas salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error('Erro ao salvar configurações');
    }
  };
  
  // ADMIN: Criar e enviar waiver
  const handleCreateWaiver = async () => {
    const settings = await loadWaiverSettings();
    console.log('📄 Configurações carregadas:', settings);
    
    if (!settings || !settings.pm_home || !settings.bimcle) {
      toast.error('Configure os dados de AMBAS as empresas primeiro');
      return;
    }
    
    if (!settings.pm_home.contratante_assinatura || !settings.bimcle.contratante_assinatura) {
      toast.error('Assine os contratos de ambas as empresas primeiro');
      return;
    }
    
    setWaiverSettings(settings);
    setWaiverMode('admin');
    setCurrentWaiverPM(null);
    setWaiverData(null);
    setShowWaiverModal(true);
  };
  
  const handleSubmitWaiver = async (waiverPayload) => {
    try {
      if (waiverMode === 'admin') {
        // Admin criando e enviando - criar 2 waivers (uma para cada empresa)
        console.log('📝 Enviando waivers para 2 empresas');
        
        if (!waiverSettings || !waiverSettings.pm_home || !waiverSettings.bimcle) {
          toast.error('Configure os dados de ambas as empresas primeiro');
          return;
        }
        
        const pm_user = waiverPayload.pm_user;
        const hoje = new Date().toISOString().split('T')[0];
        
        // Filtrar apenas campos do SUBCONTRATADO do payload
        const subcontratadoData = {};
        Object.keys(waiverPayload).forEach(key => {
          if (key.startsWith('subcontratado_')) {
            subcontratadoData[key] = waiverPayload[key];
          }
        });
        
        console.log('📝 Dados do SUBCONTRATADO:', subcontratadoData);
        
        // Waiver 1: PM Home Remodeling
        const pmHomeWaiver = {
          pm_user,
          empresa: 'pm_home',
          contratante_nome: 'PM Home Remodeling',
          contratante_endereco: waiverSettings.pm_home.contratante_endereco,
          contratante_cidade_estado_cep: waiverSettings.pm_home.contratante_cidade_estado_cep,
          contratante_telefone: waiverSettings.pm_home.contratante_telefone,
          contratante_email: waiverSettings.pm_home.contratante_email,
          contratante_ein: waiverSettings.pm_home.contratante_ein,
          contratante_nome_cargo: waiverSettings.pm_home.contratante_nome_cargo,
          contratante_assinatura: waiverSettings.pm_home.contratante_assinatura,
          contratante_data: hoje,
          ...subcontratadoData  // Apenas dados do SUBCONTRATADO
        };
        
        // Waiver 2: Bimcle Construction
        const bimcleWaiver = {
          pm_user,
          empresa: 'bimcle',
          contratante_nome: 'Bimcle Construction',
          contratante_endereco: waiverSettings.bimcle.contratante_endereco,
          contratante_cidade_estado_cep: waiverSettings.bimcle.contratante_cidade_estado_cep,
          contratante_telefone: waiverSettings.bimcle.contratante_telefone,
          contratante_email: waiverSettings.bimcle.contratante_email,
          contratante_ein: waiverSettings.bimcle.contratante_ein,
          contratante_nome_cargo: waiverSettings.bimcle.contratante_nome_cargo,
          contratante_assinatura: waiverSettings.bimcle.contratante_assinatura,
          contratante_data: hoje,
          ...subcontratadoData  // Apenas dados do SUBCONTRATADO
        };
        
        console.log('📝 PM Home Waiver CORRIGIDO:', pmHomeWaiver);
        console.log('📝 Bimcle Waiver CORRIGIDO:', bimcleWaiver);
        
        // Criar ambos
        await axios.post(`${API}/waiver-files`, pmHomeWaiver);
        await axios.post(`${API}/waiver-files`, bimcleWaiver);
        
        toast.success('Waivers criados e enviados para o PM (2 empresas)!');
        setShowWaiverModal(false);
        await loadAllTeamLeaders();
      } else {
        // PM assinando - assinar TODOS os waivers pendentes
        const pendentes = pendingWaivers.filter(w => w.status === 'pending');
        
        if (pendentes.length === 0) {
          toast.error('Nenhum waiver pendente');
          return;
        }
        
        // Assinar todos com os mesmos dados
        for (const waiver of pendentes) {
          await axios.post(`${API}/waiver-files/${waiver.id}/sign`, waiverPayload);
        }
        
        toast.success(`${pendentes.length} contrato(s) assinado(s) com sucesso!`);
        setShowWaiverModal(false);
        setCurrentWaiverToSign(null);
        await loadPendingWaivers();
        
        // Abrir PDF do primeiro waiver assinado
        if (pendentes[0]) {
          const pdfUrl = `${API}/waiver-files/${pendentes[0].id}/export-pdf?lang=pt`;
          const response = await axios.get(pdfUrl, {
            responseType: 'blob',
            headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
          });
          
          const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
          setPdfModalData({
            url: blobUrl,
            title: `Waiver Assinado - ${pendentes[0].contratante_nome}`
          });
          setShowPDFModal(true);
        }
      }
    } catch (error) {
      console.error('Erro ao processar waiver:', error);
      toast.error(getErrMsg(error, 'Erro ao processar waiver'));
    }
  };
  
  // PM: Abrir waiver para assinar
  const handleSignWaiver = async (waiver) => {
    setCurrentWaiverToSign(waiver);
    setWaiverMode('pm');
    setWaiverData(waiver);
    setShowWaiverModal(true);
  };
  
  const handleViewWaiver = async (pm_user) => {
    try {
      const { data } = await axios.get(`${API}/waiver-files/${pm_user}`);
      if (data && data.id) {
        // Waiver existe - verificar status
        if (data.status === 'pending') {
          // Pendente - mostrar mensagem
          toast.info('Waiver enviado. Aguardando assinatura do PM.');
          return;
        }
        
        // Assinado - visualizar PDF
        const pdfUrl = `${API}/waiver-files/${data.id}/export-pdf`;
        const response = await axios.get(pdfUrl, {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
        });
        
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        setPdfModalData({
          url: blobUrl,
          title: `Waiver - ${pm_user}`
        });
        setShowPDFModal(true);
      } else {
        // Waiver não existe - criar novo para este PM específico
        const settings = await loadWaiverSettings();
        if (!settings) {
          toast.error('Configure os dados da CONTRATANTE primeiro');
          return;
        }
        setWaiverSettings(settings);
        setWaiverMode('admin');
        setCurrentWaiverPM(pm_user); // Já seta o PM específico
        setWaiverData(null);
        setShowWaiverModal(true);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // Não existe - criar para este PM
        const settings = await loadWaiverSettings();
        if (!settings) {
          toast.error('Configure os dados da CONTRATANTE primeiro');
          return;
        }
        setWaiverSettings(settings);
        setWaiverMode('admin');
        setCurrentWaiverPM(pm_user);
        setWaiverData(null);
        setShowWaiverModal(true);
      } else {
        toast.error('Erro ao buscar waiver');
      }
    }
  };


  // Criar/editar equipe
  const saveHelperTeam = async () => {
    try {
      setHelperLoading(true);
      
      if (!helperTeamForm.lider_nome || !helperTeamForm.equipe) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }
      
      let teamId;
      if (editingHelperTeam) {
        const res = await axios.put(`${API}/helper-teams/${editingHelperTeam.id}`, helperTeamForm);
        teamId = editingHelperTeam.id;
        if (res.data?.password_updated) {
          toast.success(`Equipe e login ${helperTeamForm.leader_username} atualizados!`);
        } else {
          toast.success('Equipe atualizada com sucesso!');
        }
      } else {
        const { data } = await axios.post(`${API}/helper-teams`, helperTeamForm);
        teamId = data.id;
        toast.success('Equipe criada com sucesso!');
        
        // Criar login do líder para equipe nova
        if (helperTeamForm.leader_username && helperTeamForm.leader_password) {
          try {
            await axios.post(`${API}/helper-teams/${teamId}/create-leader-login`, {
              username: helperTeamForm.leader_username,
              password: helperTeamForm.leader_password
            });
            toast.success(`Login ${helperTeamForm.leader_username} configurado!`);
          } catch (e) {
            toast.error('Erro ao configurar login: ' + (e.response?.data?.detail || ''));
          }
        }
      }
      
      setShowHelperTeamModal(false);
      setEditingHelperTeam(null);
      setHelperTeamForm({ lider_nome: '', lider_diaria: 0, helpers: [], equipe: 'PM1', leader_username: '', leader_password: '' });
      await loadHelperTeams();
    } catch (error) {
      console.error('Erro ao salvar equipe:', error);
      toast.error(getErrMsg(error, 'Erro ao salvar equipe'));
    } finally {
      setHelperLoading(false);
    }
  };

  // Deletar equipe
  const deleteHelperTeam = async (teamId) => {
    if (!confirm('Tem certeza que deseja deletar esta equipe?')) return;
    
    try {
      setHelperLoading(true);
      await axios.delete(`${API}/helper-teams/${teamId}`);
      toast.success('Equipe deletada com sucesso!');
      await loadHelperTeams();
    } catch (error) {
      console.error('Erro ao deletar equipe:', error);
      toast.error(getErrMsg(error, 'Erro ao deletar equipe'));
    } finally {
      setHelperLoading(false);
    }
  };

  // Criar controle semanal
  const createHelperControl = async () => {
    try {
      setHelperLoading(true);
      
      if (!helperControlForm.team_id || !helperControlForm.data_inicio || !helperControlForm.data_fim) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }
      
      await axios.post(`${API}/helper-controls`, helperControlForm);
      toast.success('Controle criado com sucesso!');
      
      setShowHelperControlModal(false);
      setHelperControlForm({
        team_id: '',
        ano: new Date().getFullYear(),
        semana: 1,
        data_inicio: '',
        data_fim: ''
      });
      await loadHelperControls();
    } catch (error) {
      console.error('Erro ao criar controle:', error);
      toast.error(getErrMsg(error, 'Erro ao criar controle'));
    } finally {
      setHelperLoading(false);
    }
  };

  // Deletar controle
  const deleteHelperControl = async (controlId) => {
    if (!confirm('Tem certeza que deseja deletar este controle?')) return;
    
    try {
      setHelperLoading(true);
      await axios.delete(`${API}/helper-controls/${controlId}`);
      toast.success('Controle deletado com sucesso!');
      await loadHelperControls();
      setSelectedHelperControl(null);
    } catch (error) {
      console.error('Erro ao deletar controle:', error);
      toast.error(getErrMsg(error, 'Erro ao deletar controle'));
    } finally {
      setHelperLoading(false);
    }
  };

  // Adicionar despesa manual
  const addManualExpense = async () => {
    if (!selectedHelperControl) {
      toast.error('Selecione um controle primeiro');
      return;
    }
    
    try {
      setHelperLoading(true);
      
      if (!helperExpenseForm.descricao || helperExpenseForm.valor <= 0) {
        toast.error('Preencha descrição e valor válido');
        return;
      }
      
      await axios.post(
        `${API}/helper-controls/${selectedHelperControl.id}/expenses/manual`,
        helperExpenseForm
      );
      
      toast.success('Despesa adicionada com sucesso!');
      setShowHelperExpenseModal(false);
      setHelperExpenseForm({ descricao: '', valor: 0 });
      
      // Recarregar detalhes completos do controle
      await loadHelperControlDetails(selectedHelperControl.id);
    } catch (error) {
      console.error('Erro ao adicionar despesa:', error);
      toast.error(getErrMsg(error, 'Erro ao adicionar despesa'));
    } finally {
      setHelperLoading(false);
    }
  };

  // Adicionar despesa com IA
  const addAIExpense = async () => {
    if (!selectedHelperControl) {
      toast.error('Selecione um controle primeiro');
      return;
    }
    
    if (!helperExpenseFile) {
      toast.error('Selecione um arquivo');
      return;
    }
    
    try {
      setHelperLoading(true);
      
      const formData = new FormData();
      formData.append('file', helperExpenseFile);
      
      const { data } = await axios.post(
        `${API}/helper-controls/${selectedHelperControl.id}/expenses/ai-extract`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      toast.success(`Despesa extraída com sucesso! Valor: R$${data.valor.toFixed(2)}`);
      setShowHelperExpenseModal(false);
      setHelperExpenseFile(null);
      
      // Recarregar detalhes completos do controle
      await loadHelperControlDetails(selectedHelperControl.id);
    } catch (error) {
      console.error('Erro ao extrair despesa com IA:', error);
      toast.error(getErrMsg(error, 'Erro ao extrair despesa'));
    } finally {
      setHelperLoading(false);
    }
  };

  // Deletar despesa
  const deleteHelperExpense = async (expenseId) => {
    if (!confirm('Tem certeza que deseja deletar esta despesa?')) return;
    
    try {
      setHelperLoading(true);
      await axios.delete(`${API}/helper-expenses/${expenseId}`);
      toast.success('Despesa deletada com sucesso!');
      
      // Recarregar detalhes completos do controle
      if (selectedHelperControl) {
        await loadHelperControlDetails(selectedHelperControl.id);
      }
    } catch (error) {
      console.error('Erro ao deletar despesa:', error);
      toast.error(getErrMsg(error, 'Erro ao deletar despesa'));
    } finally {
      setHelperLoading(false);
    }
  };

  // Calcular semana do ano
  const getWeekNumber = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // Obter início e fim da semana
  const getWeekDates = (year, week) => {
    const simple = new Date(year, 0, 1 + (week - 1) * 7);
    const dow = simple.getDay();
    const ISOweekStart = simple;
    if (dow <= 4)
      ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
    else
      ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
    
    const ISOweekEnd = new Date(ISOweekStart);
    ISOweekEnd.setDate(ISOweekStart.getDate() + 6);
    
    return {
      start: ISOweekStart.toISOString().split('T')[0],
      end: ISOweekEnd.toISOString().split('T')[0]
    };
  };

  // Carregar equipes quando entrar na sub-aba helper ou leaders
  useEffect(() => {
    if (user?.role === 'admin' && activeTab === 'admin' && (adminSubTab === 'helper' || adminSubTab === 'leaders')) {
      loadHelperTeams();
      if (adminSubTab === 'helper') loadHelperControls();
    }
  }, [user?.role, activeTab, adminSubTab]);

  // Tornar toasts clicáveis para fechar com EXPLOSÃO
  useEffect(() => {
    const handleToastClick = (e) => {
      const toastElement = e.target.closest('[data-sonner-toast]');
      
      if (toastElement && !toastElement.classList.contains('toast-exploding')) {
        // PARAR TUDO IMEDIATAMENTE
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Adicionar classe de explosão
        toastElement.classList.add('toast-exploding');
        
        // Esconder após animação
        setTimeout(() => {
          try {
            if (toastElement) {
              toastElement.style.display = 'none';
              toastElement.style.visibility = 'hidden';
            }
          } catch (err) {
            // Ignorar
          }
        }, 500);
      }
    };

    // Capture phase com passive: false
    document.addEventListener('click', handleToastClick, { capture: true, passive: false });
    return () => document.removeEventListener('click', handleToastClick, { capture: true });
  }, []);

  // Carregar detalhes completos do controle (com despesas, helpers avulsos, etc)
  const loadHelperControlDetails = async (controlId) => {
    try {
      setHelperLoading(true);
      const { data } = await axios.get(`${API}/helper-controls/${controlId}`);
      setSelectedHelperControl(data);
    } catch (error) {
      console.error('Erro ao carregar detalhes do controle:', error);
      toast.error('Erro ao carregar controle');
    } finally {
      setHelperLoading(false);
    }
  };

  // Controlar splash screen (apenas quando vídeo terminar naturalmente)
  useEffect(() => {
    // Fallback: se vídeo não terminar sozinho, forçar fechamento após 6 segundos
    const fallbackTimer = setTimeout(() => {
      if (showSplashScreen) {
        console.log('🎬 Fallback: Forçando fechamento do splash');
        setShowSplashScreen(false);
      }
    }, 6000);
    
    return () => clearTimeout(fallbackTimer);
  }, [showSplashScreen]);

  // Load activity statistics (existing)
  const loadActivityStats_old = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/activity-stats`);
      setActivityStats(data);
    } catch (e) {
      toast.error("Erro ao carregar estatísticas de atividade");
    }
  };

  // Load ticket statistics
  const loadTicketStats = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/tickets/stats`);
      setTicketStats(data);
    } catch (e) {
      toast.error("Erro ao carregar estatísticas de tickets");
    }
  };

  // Load user ticket stats for dashboard
  const loadUserTicketStats = async () => {
    if (!user || user.role === 'admin') return;
    try {
      console.log('🎫 Loading user ticket stats for:', user.username);
      const { data } = await axios.get(`${API}/tickets/stats`);
      console.log('📊 User ticket stats loaded:', data);
      console.log('📊 Stats breakdown:', data?.stats);
      console.log('📊 Recent tickets:', data?.recent_tickets?.length || 0);
      setUserTicketStats(data);
    } catch (e) {
      console.error('❌ Failed to load user ticket stats:', e);
      console.error('❌ Error response:', e.response?.data);
    }
  };

  // Team Leader Functions
  const loadAllTeamLeaders = async () => {
    if (!user || user.role !== 'admin') return;
    try {
      setTeamLeaderLoading(true);
      const { data } = await axios.get(`${API}/admin/team-leaders`);
      
      // Carregar waivers para marcar quais PMs já têm waiver assinado
      const waivers = await loadAllWaivers();
      console.log('📄 Waivers carregados:', waivers);
      
      const waiverMap = {};
      waivers.forEach(w => {
        waiverMap[w.pm_user] = {
          exists: true,
          status: w.status,
          id: w.id
        };
        console.log(`📄 Waiver de ${w.pm_user}: ${w.status}`);
      });
      
      // Adicionar dados do waiver aos leaders
      const leadersWithWaiver = data.map(leader => ({
        ...leader,
        waiver_file: waiverMap[leader.username]?.exists || false,
        waiver_status: waiverMap[leader.username]?.status || null,
        waiver_id: waiverMap[leader.username]?.id || null
      }));
      
      console.log('👥 Leaders com waiver:', leadersWithWaiver.filter(l => l.waiver_file).map(l => l.username));
      setTeamLeaders(leadersWithWaiver);
    } catch (e) {
      console.error('Failed to load team leaders:', e);
      toast.error('Erro ao carregar líderes de equipe');
    } finally {
      setTeamLeaderLoading(false);
    }
  };

  const loadMyTeamLeaderInfo = async () => {
    if (!user?.username) return;
    try {
      setTeamLeaderLoading(true);
      const { data } = await axios.get(`${API}/team-leader/${user.username}`);
      setCurrentTeamLeader(data);
      
      // Populate form data
      setTeamLeaderFormData({
        full_name: data.full_name || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        date_of_birth: data.date_of_birth || '',
        hire_date: data.hire_date || '',
        position: data.position || '',
        driver_license_number: data.driver_license_number || '',
        ssn_tax_id: data.ssn_tax_id || ''
      });
      
      // Set file references
      setTeamLeaderFiles({
        driver_license_file: data.driver_license_file,
        lead_certificate_file: data.lead_certificate_file,
        lead_firm_certificate_file: data.lead_firm_certificate_file,
        arbitration_file: data.arbitration_file,
        contractor_file: data.contractor_file
      });
      
      // Load image thumbnails for uploaded image files
      if (data.driver_license_file?.file_type === 'image') {
        loadImageThumbnail('driver_license_file', (url) => 
          setImageUrls(prev => ({ ...prev, driver_license_file: url }))
        );
      }
      if (data.lead_certificate_file?.file_type === 'image') {
        loadImageThumbnail('lead_certificate_file', (url) => 
          setImageUrls(prev => ({ ...prev, lead_certificate_file: url }))
        );
      }
      if (data.lead_firm_certificate_file?.file_type === 'image') {
        loadImageThumbnail('lead_firm_certificate_file', (url) => 
          setImageUrls(prev => ({ ...prev, lead_firm_certificate_file: url }))
        );
      }
      
    } catch (e) {
      console.error('Failed to load team leader info:', e);
      toast.error('Erro ao carregar informações pessoais');
    } finally {
      setTeamLeaderLoading(false);
    }
  };

  const updateMyTeamLeaderInfo = async () => {
    const targetUsername = currentTeamLeader?.username || user?.username;
    if (!targetUsername) return;
    
    try {
      setTeamLeaderLoading(true);
      
      if (user?.role === 'admin' && currentTeamLeader?.username) {
        // Admin updating another user's profile
        await axios.post(`${API}/admin/team-leaders/${currentTeamLeader.username}`, teamLeaderFormData);
      } else {
        // User updating their own profile
        await axios.put(`${API}/team-leader/${targetUsername}`, teamLeaderFormData);
      }
      
      toast.success('Informações atualizadas com sucesso!');
      
      // Se for um PM (PM1-PM10), sincronizar com tabela Users
      if (targetUsername.match(/^PM\d+$/)) {
        try {
          // Separar full_name em first_name e last_name
          const nameParts = (teamLeaderFormData.full_name || '').trim().split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          
          const userData = {
            first_name: firstName,
            last_name: lastName,
            phone: teamLeaderFormData.phone,
            address: teamLeaderFormData.address
          };
          
          console.log(`🔄 Sincronizando ${targetUsername} com Users:`, userData);
          
          // Update user profile
          await axios.put(`${API}/admin/users/${targetUsername}`, userData);
          
          console.log(`✅ User ${targetUsername} sincronizado`);
        } catch (syncError) {
          console.error('Erro ao sincronizar com Users:', syncError);
          // Não mostrar erro ao usuário, apenas log
        }
      }
      
      // Reload data based on context
      if (user?.role === 'admin' && currentTeamLeader?.username) {
        // Reload specific leader data for admin
        const { data } = await axios.get(`${API}/team-leader/${currentTeamLeader.username}`);
        setCurrentTeamLeader(data);
        await loadAllTeamLeaders(); // Refresh the grid
      } else {
        // Reload own data
        await loadMyTeamLeaderInfo();
      }
      
    } catch (e) {
      console.error('Failed to update team leader info:', e);
      toast.error(getErrMsg(e, 'Erro ao atualizar informações'));
    } finally {
      setTeamLeaderLoading(false);
    }
  };

  const uploadTeamLeaderDocument = async (documentType, file) => {
    const targetUsername = currentTeamLeader?.username || user?.username;
    if (!targetUsername || !file) return;
    
    try {
      setUploadingFiles(prev => ({ ...prev, [documentType]: true }));
      
      const formData = new FormData();
      formData.append('file', file);
      
      const { data } = await axios.post(`${API}/team-leader/${targetUsername}/upload/${documentType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${documentType.replace('_', ' ')} uploaded successfully!`);
      
      // Reload appropriate data without resetting form
      if (user?.role === 'admin' && currentTeamLeader?.username) {
        // Admin uploading for another user
        const { data: reloadedData } = await axios.get(`${API}/team-leader/${currentTeamLeader.username}`);
        setCurrentTeamLeader(reloadedData);
        setTeamLeaderFiles({
          driver_license_file: reloadedData.driver_license_file,
          lead_certificate_file: reloadedData.lead_certificate_file,
          lead_firm_certificate_file: reloadedData.lead_firm_certificate_file,
          arbitration_file: reloadedData.arbitration_file,
          contractor_file: reloadedData.contractor_file
        });
        
        // Load thumbnails for uploaded images without resetting form
        if (reloadedData.driver_license_file?.file_type === 'image') {
          loadImageThumbnail('driver_license_file', (url) => 
            setImageUrls(prev => ({ ...prev, driver_license_file: url }))
          );
        }
        if (reloadedData.lead_certificate_file?.file_type === 'image') {
          loadImageThumbnail('lead_certificate_file', (url) => 
            setImageUrls(prev => ({ ...prev, lead_certificate_file: url }))
          );
        }
        if (reloadedData.lead_firm_certificate_file?.file_type === 'image') {
          loadImageThumbnail('lead_firm_certificate_file', (url) => 
            setImageUrls(prev => ({ ...prev, lead_firm_certificate_file: url }))
          );
        }
      } else {
        // User uploading for themselves - only update files, preserve form data
        const { data: reloadedData } = await axios.get(`${API}/team-leader/${targetUsername}`);
        setTeamLeaderFiles({
          driver_license_file: reloadedData.driver_license_file,
          lead_certificate_file: reloadedData.lead_certificate_file,
          lead_firm_certificate_file: reloadedData.lead_firm_certificate_file,
          arbitration_file: reloadedData.arbitration_file,
          contractor_file: reloadedData.contractor_file
        });
        
        // Load thumbnails for uploaded images
        if (reloadedData.driver_license_file?.file_type === 'image') {
          loadImageThumbnail('driver_license_file', (url) => 
            setImageUrls(prev => ({ ...prev, driver_license_file: url }))
          );
        }
        if (reloadedData.lead_certificate_file?.file_type === 'image') {
          loadImageThumbnail('lead_certificate_file', (url) => 
            setImageUrls(prev => ({ ...prev, lead_certificate_file: url }))
          );
        }
        if (reloadedData.lead_firm_certificate_file?.file_type === 'image') {
          loadImageThumbnail('lead_firm_certificate_file', (url) => 
            setImageUrls(prev => ({ ...prev, lead_firm_certificate_file: url }))
          );
        }
      }
      
    } catch (e) {
      console.error('Failed to upload document:', e);
      toast.error(`Erro ao fazer upload: ${getErrMsg(e, 'Falha no upload')}`);
    } finally {
      setUploadingFiles(prev => ({ ...prev, [documentType]: false }));
    }
  };

  const downloadTeamLeaderDocument = async (documentType) => {
    const targetUsername = currentTeamLeader?.username || user?.username;
    if (!targetUsername) return;
    
    try {
      const response = await axios.get(`${API}/team-leader/${targetUsername}/download/${documentType}`, {
        responseType: 'blob'
      });
      
      // Get filename from document info
      const fileInfo = teamLeaderFiles[documentType];
      const filename = fileInfo?.original_filename || `${documentType}_${targetUsername}`;
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Download de ${documentType.replace('_', ' ')} concluído!`);
      
    } catch (e) {
      console.error('Failed to download document:', e);
      toast.error('Erro ao fazer download do documento');
    }
  };
  // Function to load image with authentication for thumbnail preview
  const loadImageThumbnail = async (documentType, setImageUrl) => {
    const targetUsername = currentTeamLeader?.username || user?.username;
    if (!targetUsername) {
      console.error('❌ No target username for image thumbnail');
      return;
    }
    
    try {
      console.log(`🖼️ Loading thumbnail for ${documentType} of user ${targetUsername}`);
      const response = await axios.get(`${API}/team-leader/${targetUsername}/download/${documentType}`, {
        responseType: 'blob'
      });
      
      console.log(`✅ Image response: ${response.data.size} bytes, type: ${response.data.type}`);
      
      // Create blob URL for image preview
      const imageUrl = window.URL.createObjectURL(response.data);
      setImageUrl(imageUrl);
      console.log(`✅ Loaded thumbnail for ${documentType} of ${targetUsername}: ${imageUrl}`);
      
    } catch (e) {
      console.error(`❌ Failed to load image thumbnail for ${documentType} of ${targetUsername}:`, e);
      console.error(`❌ Error response:`, e.response?.data);
      console.error(`❌ Error status:`, e.response?.status);
      setImageUrl(null);
    }
  };

  // Download document and return blob URL for modal viewing
  const getDocumentBlobUrl = async (documentType) => {
    const targetUsername = currentTeamLeader?.username || user?.username;
    if (!targetUsername) return null;
    
    try {
      const response = await axios.get(`${API}/team-leader/${targetUsername}/download/${documentType}`, {
        responseType: 'blob'
      });
      
      return window.URL.createObjectURL(response.data);
    } catch (e) {
      console.error(`Failed to load document ${documentType}:`, e);
      toast.error('Erro ao carregar documento');
      return null;
    }
  };

  // Format phone number to US format: +1(XXX)XXX-XXXX
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');
    
    // Don't format if empty
    if (!cleaned) return '';
    
    // Handle different lengths
    if (cleaned.length <= 3) {
      return `+1(${cleaned}`;
    } else if (cleaned.length <= 6) {
      return `+1(${cleaned.slice(0, 3)})${cleaned.slice(3)}`;
    } else if (cleaned.length <= 10) {
      return `+1(${cleaned.slice(0, 3)})${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else {
      // Limit to 10 digits
      return `+1(${cleaned.slice(0, 3)})${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  // Helpers Functions (Workers for each PM)
  const loadTeamHelpers = async (pmUsername) => {
    if (!pmUsername) return;
    try {
      setHelpersLoading(true);
      const { data } = await axios.get(`${API}/admin/team-leaders/${pmUsername}/helpers`);
      setHelpers(data);
      console.log(`✅ Loaded ${data.length} helpers for ${pmUsername}`);
    } catch (e) {
      console.error(`Failed to load helpers for ${pmUsername}:`, e);
      toast.error('Erro ao carregar funcionários');
    } finally {
      setHelpersLoading(false);
    }
  };

  const saveHelper = async () => {
    if (!helperFormData.full_name.trim()) {
      toast.error('Nome completo é obrigatório');
      return;
    }

    // Validate translator logic
    if (helperFormData.is_team_translator && !helperFormData.speaks_english) {
      toast.error('Funcionário precisa falar inglês para ser tradutor da equipe');
      return;
    }

    try {
      setHelpersLoading(true);
      
      if (editingHelper) {
        // Update existing helper
        await axios.put(
          `${API}/admin/team-leaders/${currentTeamLeader.username}/helpers/${editingHelper.id}`,
          helperFormData
        );
        toast.success('Funcionário atualizado com sucesso!');
      } else {
        // Create new helper
        await axios.post(
          `${API}/admin/team-leaders/${currentTeamLeader.username}/helpers`,
          helperFormData
        );
        toast.success('Funcionário adicionado com sucesso!');
      }

      // Reload helpers list
      await loadTeamHelpers(currentTeamLeader.username);
      
      // Reset form
      setHelperFormData({
        full_name: '',
        address: '',
        phone: '',
        hire_date: '',
        ssn_tax_id: '',
        speaks_english: false,
        is_team_translator: false
      });
      setEditingHelper(null);
      setShowHelperForm(false);
      
    } catch (e) {
      console.error('Failed to save helper:', e);
      toast.error(e.response?.data?.detail || 'Erro ao salvar funcionário');
    } finally {
      setHelpersLoading(false);
    }
  };

  const deleteHelper = async (helperId, helperName) => {
    const confirmed = window.confirm(`Tem certeza que deseja remover ${helperName} da equipe?`);
    if (!confirmed) return;

    try {
      setHelpersLoading(true);
      await axios.delete(`${API}/admin/team-leaders/${currentTeamLeader.username}/helpers/${helperId}`);
      toast.success('Funcionário removido com sucesso!');
      
      // Reload helpers list
      await loadTeamHelpers(currentTeamLeader.username);
    } catch (e) {
      console.error('Failed to delete helper:', e);
      toast.error('Erro ao remover funcionário');
    } finally {
      setHelpersLoading(false);
    }
  };

  const startEditHelper = (helper) => {
    setEditingHelper(helper);
    setHelperFormData({
      full_name: helper.full_name || '',
      address: helper.address || '',
      phone: helper.phone || '',
      hire_date: helper.hire_date || '',
      ssn_tax_id: helper.ssn_tax_id || '',
      speaks_english: helper.speaks_english || false,
      is_team_translator: helper.is_team_translator || false
    });
    setShowHelperForm(true);
  };

  const cancelHelperForm = () => {
    setHelperFormData({
      full_name: '',
      address: '',
      phone: '',
      hire_date: '',
      ssn_tax_id: '',
      speaks_english: false,
      is_team_translator: false
    });
    setEditingHelper(null);
    setShowHelperForm(false);
  };

  // PM Functions to manage own helpers
  const loadMyHelpers = async () => {
    if (!user?.username) return;
    try {
      setHelpersLoading(true);
      const { data } = await axios.get(`${API}/my-helpers`);
      setHelpers(data);
      console.log(`✅ Loaded ${data.length} own helpers`);
    } catch (e) {
      console.error('Failed to load own helpers:', e);
      toast.error('Erro ao carregar funcionários');
    } finally {
      setHelpersLoading(false);
    }
  };

  const saveMyHelper = async () => {
    if (!helperFormData.full_name.trim()) {
      toast.error('Nome completo é obrigatório');
      return;
    }

    if (helperFormData.is_team_translator && !helperFormData.speaks_english) {
      toast.error('Funcionário precisa falar inglês para ser tradutor da equipe');
      return;
    }

    try {
      setHelpersLoading(true);
      
      if (editingHelper) {
        // Update existing helper
        await axios.put(`${API}/my-helpers/${editingHelper.id}`, helperFormData);
        toast.success('Funcionário atualizado com sucesso!');
      } else {
        // Create new helper
        await axios.post(`${API}/my-helpers`, helperFormData);
        toast.success('Funcionário adicionado com sucesso!');
      }

      // Reload helpers list
      await loadMyHelpers();
      
      // Reset form
      setHelperFormData({
        full_name: '',
        address: '',
        phone: '',
        hire_date: '',
        ssn_tax_id: '',
        speaks_english: false,
        is_team_translator: false
      });
      setEditingHelper(null);
      setShowHelperForm(false);
      
    } catch (e) {
      console.error('Failed to save helper:', e);
      toast.error(e.response?.data?.detail || 'Erro ao salvar funcionário');
    } finally {
      setHelpersLoading(false);
    }
  };

  const deleteMyHelper = async (helperId, helperName) => {
    const confirmed = window.confirm(`Tem certeza que deseja remover ${helperName} da equipe?`);
    if (!confirmed) return;

    try {
      setHelpersLoading(true);
      await axios.delete(`${API}/my-helpers/${helperId}`);
      toast.success('Funcionário removido com sucesso!');
      
      // Reload helpers list
      await loadMyHelpers();
    } catch (e) {
      console.error('Failed to delete helper:', e);
      toast.error('Erro ao remover funcionário');
    } finally {
      setHelpersLoading(false);
    }
  };
  
  // Funções para vincular documentos existentes aos helpers
  const openLinkArbitrationModal = async (helper) => {
    setHelperToLinkDoc({ id: helper.id, name: helper.full_name });
    setLinkDocSearch('');
    
    // Carregar todos os arbitration files
    try {
      const { data } = await axios.get(`${API}/arbitration-files`);
      setAvailableArbitrationDocs(data);
      console.log(`📋 Carregados ${data.length} Arbitration files para seleção`);
    } catch (e) {
      console.error('Erro ao carregar Arbitration files:', e);
      toast.error('Erro ao carregar documentos');
    }
    
    setShowLinkArbitrationModal(true);
  };
  
  const openLinkContractorModal = async (helper) => {
    setHelperToLinkDoc({ id: helper.id, name: helper.full_name });
    setLinkDocSearch('');
    
    // Carregar todos os contractor files
    try {
      const { data } = await axios.get(`${API}/contractor-files`);
      setAvailableContractorDocs(data);
      console.log(`📋 Carregados ${data.length} Contractor files para seleção`);
    } catch (e) {
      console.error('Erro ao carregar Contractor files:', e);
      toast.error('Erro ao carregar documentos');
    }
    
    setShowLinkContractorModal(true);
  };
  
  const linkArbitrationToHelper = async (documentId) => {
    if (!helperToLinkDoc) return;
    
    try {
      setLinkingDocument(true);
      
      await axios.put(`${API}/my-helpers/${helperToLinkDoc.id}`, {
        arbitration_agreement_id: documentId
      });
      
      toast.success(`✅ Arbitration vinculado a ${helperToLinkDoc.name}!`);
      
      // Recarregar helpers
      await loadMyHelpers();
      
      // Fechar modal
      setShowLinkArbitrationModal(false);
      setHelperToLinkDoc(null);
      
    } catch (e) {
      console.error('Erro ao vincular Arbitration:', e);
      toast.error('Erro ao vincular documento');
    } finally {
      setLinkingDocument(false);
    }
  };
  
  const linkContractorToHelper = async (documentId) => {
    if (!helperToLinkDoc) return;
    
    try {
      setLinkingDocument(true);
      
      await axios.put(`${API}/my-helpers/${helperToLinkDoc.id}`, {
        contractor_agreement_id: documentId
      });
      
      toast.success(`✅ Contractor vinculado a ${helperToLinkDoc.name}!`);
      
      // Recarregar helpers
      await loadMyHelpers();
      
      // Fechar modal
      setShowLinkContractorModal(false);
      setHelperToLinkDoc(null);
      
    } catch (e) {
      console.error('Erro ao vincular Contractor:', e);
      toast.error('Erro ao vincular documento');
    } finally {
      setLinkingDocument(false);
    }
  };
  
  // LSWP Formstack Functions
  const submitLSWPToFormstack = async () => {
    // Validações
    if (!lswpFormData.customer_first_name.trim()) {
      toast.error('Nome do cliente é obrigatório');
      return;
    }
    if (!lswpFormData.customer_email.trim()) {
      toast.error('Email do cliente é obrigatório');
      return;
    }
    if (!lswpFormData.job_address_line1.trim()) {
      toast.error('Endereço do trabalho é obrigatório');
      return;
    }
    if (!hasRenovatorSig) {
      toast.error('Assinatura do Renovator é obrigatória');
      return;
    }
    if (!hasCustomerSig) {
      toast.error('Assinatura do Cliente é obrigatória');
      return;
    }
    
    try {
      setLswpSubmitting(true);
      
      // Capturar assinaturas
      const renovatorSig = renovatorSigPadRef.current?.toDataURL();
      const customerSig = customerSigPadRef.current?.toDataURL();
      
      const payload = {
        ...lswpFormData,
        signature_renovator: renovatorSig,
        signature_customer: customerSig
      };
      
      console.log('📤 Enviando LSWP para Formstack...', payload);
      
      const { data } = await axios.post(`${API}/formstack/submit-lswp`, payload);
      
      toast.success('✅ Formulário LSWP enviado com sucesso para Formstack!');
      
      // Mostrar confirmação
      console.log('Formstack Response:', data);
      
      // Resetar formulário
      setLswpFormData({
        customer_first_name: '',
        customer_last_name: '',
        customer_email: '',
        job_address_line1: '',
        job_city: '',
        job_state: '',
        job_zip: '',
        num_windows: 0,
        num_doors: 0,
        signature_renovator: '',
        signature_customer: ''
      });
      
      setHasRenovatorSig(false);
      setHasCustomerSig(false);
      renovatorSigPadRef.current?.clear();
      customerSigPadRef.current?.clear();
      
      setShowLSWPPreview(false);
      setShowLSWPForm(false);
      
    } catch (e) {
      console.error('Erro ao enviar LSWP:', e);
      const errorMsg = e.response?.data?.detail || 'Erro ao enviar formulário';
      toast.error(errorMsg);
    } finally {
      setLswpSubmitting(false);
    }
  };

  // Load available Arbitration and Contractor documents for selection
  const loadAvailableDocuments = async (pmUsername) => {
    if (!pmUsername) return;
    
    try {
      console.log(`📥 Carregando TODOS os documentos disponíveis no sistema...`);
      
      // Load ALL arbitration files (não filtrar por PM)
      const { data: arbitrationData } = await axios.get(`${API}/arbitration-files`);
      console.log(`📋 Total arbitration files no sistema: ${arbitrationData.length}`);
      
      setAvailableArbitrationDocs(arbitrationData); // TODOS, não apenas do PM
      
      // Load ALL contractor files (não filtrar por PM)
      const { data: contractorData } = await axios.get(`${API}/contractor-files`);
      console.log(`📋 Total contractor files no sistema: ${contractorData.length}`);
      
      setAvailableContractorDocs(contractorData); // TODOS, não apenas do PM
      
      console.log(`✅ Loaded ${arbitrationData.length} arbitration and ${contractorData.length} contractor docs (TODOS os PMs)`);
    } catch (e) {
      console.error('Failed to load available documents:', e);
      toast.error('Erro ao carregar documentos disponíveis');
    }
  };

  // Attach selected document to Team Leader
  const attachDocumentToLeader = async (docType, docId, docTitle) => {
    if (!currentTeamLeader?.username) return;
    
    try {
      setUploadingFiles(prev => ({ ...prev, [docType]: true }));
      
      console.log(`📎 Attaching ${docType} document ID ${docId} to ${currentTeamLeader.username}`);
      
      // Download the PDF from the arbitration/contractor file
      const endpoint = docType === 'arbitration_file' 
        ? `/arbitration-files/${docId}/download`
        : `/contractor-files/${docId}/download`;
      
      console.log(`🔽 Downloading from: ${API}${endpoint}`);
      
      const response = await axios.get(`${API}${endpoint}`, {
        responseType: 'blob'
      });
      
      console.log(`✅ Downloaded blob: ${response.data.size} bytes, type: ${response.data.type}`);
      
      // Create a File object from the blob
      const file = new File([response.data], `${docTitle}.pdf`, { type: 'application/pdf' });
      
      console.log(`📤 Uploading as file: ${file.name}, size: ${file.size}`);
      
      // Upload as if it was a normal file
      await uploadTeamLeaderDocument(docType, file);
      
      // Close selector
      if (docType === 'arbitration_file') {
        setShowArbitrationSelector(false);
        setSelectedArbitrationDoc(null);
      } else {
        setShowContractorSelector(false);
        setSelectedContractorDoc(null);
      }
      
      toast.success('Documento anexado com sucesso!');
      console.log(`✅ Document attached successfully`);
    } catch (e) {
      console.error('Failed to attach document:', e);
      console.error('Error details:', e.response?.data);
      toast.error(e.response?.data?.detail || 'Erro ao anexar documento');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [docType]: false }));
    }
  };

  // Send Team Leader Email
  const sendTeamLeaderEmail = async () => {
    if (!teamLeaderEmailData.recipient_email || !teamLeaderEmailData.recipient_name) {
      toast.error('Preencha nome e email do destinatário');
      return;
    }

    if (teamLeaderEmailData.selected_documents.length === 0 && teamLeaderEmailData.selected_helpers.length === 0) {
      toast.error('Selecione pelo menos um documento ou funcionário para enviar');
      return;
    }

    try {
      setTeamLeaderEmailLoading(true);
      
      const payload = {
        pm_username: currentTeamLeader.username,
        recipient_name: teamLeaderEmailData.recipient_name,
        recipient_email: teamLeaderEmailData.recipient_email,
        selected_documents: teamLeaderEmailData.selected_documents,
        selected_helpers: teamLeaderEmailData.selected_helpers,
        helper_documents: teamLeaderEmailData.helper_documents,
        message: teamLeaderEmailData.message
      };
      
      console.log('📧 Enviando email:', payload);
      
      await axios.post(`${API}/team-leader/send-email`, payload);
      
      toast.success(`✅ Email enviado para ${teamLeaderEmailData.recipient_name}!`);
      
      // Fechar modal
      setShowTeamLeaderEmailModal(false);
      
      // Resetar form
      setTeamLeaderEmailData({
        recipient_email: '',
        recipient_name: '',
        selected_documents: [],
        selected_helpers: [],
        helper_documents: {},
        message: ''
      });
      
    } catch (e) {
      console.error('Erro ao enviar email:', e);
      toast.error(e.response?.data?.detail || 'Erro ao enviar email');
    } finally {
      setTeamLeaderEmailLoading(false);
    }
  };

  // ADMIN edit helper in Helpers tab
  const saveHelperAsAdmin = async () => {
    if (!helperFormData.full_name.trim()) {
      toast.error('Nome completo é obrigatório');
      return;
    }

    if (helperFormData.is_team_translator && !helperFormData.speaks_english) {
      toast.error('Funcionário precisa falar inglês para ser tradutor da equipe');
      return;
    }

    if (!editingHelper) {
      toast.error('Erro: Helper não identificado');
      return;
    }

    try {
      setHelpersLoading(true);
      
      // Update helper as ADMIN
      await axios.put(
        `${API}/admin/team-leaders/${editingHelper.pm_user}/helpers/${editingHelper.id}`,
        helperFormData
      );
      
      toast.success('Funcionário atualizado com sucesso!');

      // Reload all helpers
      const { data } = await axios.get(`${API}/admin/all-helpers`);
      setHelpers(data);
      
      // Reset form
      setHelperFormData({
        full_name: '',
        address: '',
        phone: '',
        hire_date: '',
        ssn_tax_id: '',
        speaks_english: false,
        is_team_translator: false
      });
      setEditingHelper(null);
      setShowHelperForm(false);
      
    } catch (e) {
      console.error('Failed to save helper:', e);
      toast.error(e.response?.data?.detail || 'Erro ao salvar funcionário');
    } finally {
      setHelpersLoading(false);
    }
  };

  // Arbitration Files Functions
  const loadArbitrationFiles = async () => {
    if (!user?.username) return;
    try {
      setArbitrationLoading(true);
      const { data } = await axios.get(`${API}/arbitration-files`);
      setArbitrationFiles(data);
    } catch (e) {
      console.error('Failed to load arbitration files:', e);
      toast.error('Erro ao carregar arquivos de arbitragem');
    } finally {
      setArbitrationLoading(false);
    }
  };

  const createArbitrationFile = async () => {
    if (!arbitrationFormData.helper_name) {
      toast.error('Preencha o nome do helper');
      return;
    }
    


    if (!arbitrationFormData.date) {
      toast.error('Selecione uma data');
      return;
    }
    
    // Check if signature is drawn
    if (!hasSignature || !signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      toast.error('Por favor, assine no campo de assinatura');
      return;
    }
    
    try {
      setArbitrationLoading(true);
      
      // Get signature as base64 image
      const signatureDataURL = signaturePadRef.current.toDataURL();
      
      const response = await axios.post(`${API}/arbitration-files`, {
        helper_name: arbitrationFormData.helper_name,
        date: arbitrationFormData.date,
        signature: signatureDataURL, // Store as base64 image
        pm_user: user.username
      });
      
      toast.success('Arquivo de arbitragem criado com sucesso!');
      setShowArbitrationForm(false);
      
      // Se foi criado a partir de um helper, vincular automaticamente
      if (helperCreatingDocFor && helperCreatingDocFor.doc_type === 'arbitration') {
        try {
          const arbitrationId = response.data.id;
          console.log(`🔗 Vinculando arbitration ${arbitrationId} ao helper ${helperCreatingDocFor.helper_id}`);
          
          await axios.post(`${API}/my-helpers/${helperCreatingDocFor.helper_id}/link-arbitration/${arbitrationId}`);
          
          toast.success(`Arbitration vinculado a ${helperCreatingDocFor.helper_name}!`);
          
          // Recarregar helpers para mostrar vínculo
          await loadMyHelpers();
          
          // Voltar para aba Helpers
          setActiveTab('arbitration');
          setArbitrationSubTab('helpers');
        } catch (linkError) {
          console.error('Erro ao vincular arbitration:', linkError);
          toast.error('Arbitration criado mas não vinculado. Vincule manualmente.');
        } finally {
          setHelperCreatingDocFor(null);
        }
      }
      
      // Reset form
      setArbitrationFormData({
        helper_name: '',
        signature: '',
        date: new Date().toISOString().split('T')[0]
      });
      
      // Clear signature pad and state
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
      }
      setHasSignature(false);
      
      await loadArbitrationFiles();
      
    } catch (e) {
      console.error('Failed to create arbitration file:', e);
      toast.error(getErrMsg(e, 'Erro ao criar arquivo de arbitragem'));
    } finally {
      setArbitrationLoading(false);
    }
  };

  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setHasSignature(false);
    }
  };

  const onSignatureEnd = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      setHasSignature(true);
    } else {
      setHasSignature(false);
    }
  };

  const downloadArbitrationFile = async (fileId) => {
    try {
      const response = await axios.get(`${API}/arbitration-files/${fileId}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `arbitration_file_${fileId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download do arquivo de arbitragem concluído!');
      
    } catch (e) {
      console.error('Failed to download arbitration file:', e);
      toast.error('Erro ao fazer download do arquivo');
    }
  };

  const sendArbitrationToSupervisor = async () => {
    if (!selectedArbitrationFileId || arbitrationEmailData.selectedRecipients.length === 0) {
      toast.error(language === 'pt-BR' ? 'Selecione pelo menos um destinatário' : 'Select at least one recipient');
      return;
    }
    
    try {
      setArbitrationLoading(true);
      
      // Send to all selected recipients
      const sendPromises = arbitrationEmailData.selectedRecipients.map(async (recipient) => {
        return await axios.post(`${API}/arbitration-files/${selectedArbitrationFileId}/send-email`, {
          arbitration_file_id: selectedArbitrationFileId,
          supervisor_email: recipient.email,
          supervisor_name: recipient.name
        });
      });
      
      await Promise.all(sendPromises);
      
      const recipientCount = arbitrationEmailData.selectedRecipients.length;
      toast.success(language === 'pt-BR' ? 
        `Arquivo enviado para ${recipientCount} destinatário${recipientCount > 1 ? 's' : ''}!` :
        `File sent to ${recipientCount} recipient${recipientCount > 1 ? 's' : ''}!`
      );
      
      setShowArbitrationEmailForm(false);
      setArbitrationEmailData({ 
        supervisor_email: '', 
        supervisor_name: '',
        selectedRecipients: [],
        selectAll: false
      });
      
    } catch (e) {
      console.error('Failed to send arbitration email:', e);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao enviar email' : 'Error sending email'));
    } finally {
      setArbitrationLoading(false);
    }
  };

  const sendContractorToSupervisor = async () => {
    console.log('🔧 sendContractorToSupervisor called');
    console.log('📋 selectedContractorFileId:', selectedContractorFileId);
    console.log('📋 contractorEmailData.selectedRecipients:', contractorEmailData.selectedRecipients);
    
    if (!selectedContractorFileId || contractorEmailData.selectedRecipients.length === 0) {
      toast.error(language === 'pt-BR' ? 'Selecione pelo menos um destinatário' : 'Select at least one recipient');
      return;
    }
    
    try {
      setContractorLoading(true);
      console.log('🔄 Starting email send to', contractorEmailData.selectedRecipients.length, 'recipients');
      
      // Send to all selected recipients
      const sendPromises = contractorEmailData.selectedRecipients.map(async (recipient) => {
        console.log('📧 Sending to:', recipient.email);
        return await axios.post(`${API}/contractor-files/${selectedContractorFileId}/send-email`, {
          contractor_file_id: selectedContractorFileId,
          supervisor_email: recipient.email,
          supervisor_name: recipient.name
        });
      });
      
      const results = await Promise.all(sendPromises);
      console.log('✅ All emails sent successfully:', results.length);
      
      const recipientCount = contractorEmailData.selectedRecipients.length;
      toast.success(language === 'pt-BR' ? 
        `Contrato enviado para ${recipientCount} destinatário${recipientCount > 1 ? 's' : ''}!` :
        `Contract sent to ${recipientCount} recipient${recipientCount > 1 ? 's' : ''}!`
      );
      
      setShowContractorEmailForm(false);
      setContractorEmailData({ 
        supervisor_email: '', 
        supervisor_name: '',
        selectedRecipients: [],
        selectAll: false
      });
      
    } catch (e) {
      console.error('❌ Failed to send contractor email:', e);
      console.error('❌ Error details:', e.response?.data);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao enviar email' : 'Error sending email'));
    } finally {
      setContractorLoading(false);
    }
  };

  // Edit Contractor File Name (Admin only) - COMPLETELY REWRITTEN
  const editContractorFileNameFixed = async () => {
    console.log('🔧🔧🔧 CONTRACTOR EDIT FUNCTION CALLED (REWRITTEN VERSION)');
    console.log('📋 contractorFileToEdit ID:', contractorFileToEdit?.id);
    console.log('📋 contractorFileToEdit helper_name:', contractorFileToEdit?.helper_name);
    
    if (!contractorFileToEdit?.id || !contractorFileToEdit?.helper_name?.trim()) {
      console.log('❌ Missing contractor data');
      toast.error('Dados do contractor inválidos');
      return;
    }

    if (user?.role !== 'admin') {
      console.log('❌ Not admin');
      toast.error('Acesso negado');
      return;
    }

    try {
      setContractorLoading(true);
      
      // EXPLICIT CONTRACTOR ENDPOINT
      const CONTRACTOR_EDIT_URL = `${API}/admin/contractor-files/${contractorFileToEdit.id}/edit-name`;
      const CONTRACTOR_PAYLOAD = { 
        new_name: contractorFileToEdit.helper_name.trim() 
      };
      
      console.log('🎯🎯🎯 SENDING CONTRACTOR REQUEST:');
      console.log('  URL:', CONTRACTOR_EDIT_URL);
      console.log('  Payload:', CONTRACTOR_PAYLOAD);
      console.log('  File ID:', contractorFileToEdit.id);
      console.log('  New Name:', contractorFileToEdit.helper_name);

      const response = await axios({
        method: 'PUT',
        url: CONTRACTOR_EDIT_URL,
        data: CONTRACTOR_PAYLOAD,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`
        }
      });
      
      console.log('✅✅✅ CONTRACTOR SUCCESS:', response.data);

      toast.success(language === 'pt-BR' ? 'Nome do contractor salvo!' : 'Contractor name saved!');
      
      // Close modal and clear state
      setShowContractorEditModal(false);
      setContractorFileToEdit(null);

      // Reload contractor files
      await loadContractorFiles();

    } catch (error) {
      console.error('❌❌❌ CONTRACTOR EDIT ERROR:', error);
      console.error('❌ Error response data:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error config URL:', error.config?.url);
      
      toast.error(`Erro: ${error.response?.data?.detail || error.message}`);
    } finally {
      setContractorLoading(false);
    }
  };

  // Send Multiple Contractor Files
  const sendMultipleContractorFiles = async () => {
    if (selectedContractorFiles.length === 0 || bulkEmailData.selectedRecipients.length === 0) {
      toast.error(language === 'pt-BR' ? 
        'Selecione pelo menos um arquivo e um destinatário' : 
        'Select at least one file and one recipient'
      );
      return;
    }
    
    try {
      setContractorLoading(true);
      
      // Send each contractor file to all selected recipients
      const sendPromises = [];
      
      for (const file of selectedContractorFiles) {
        for (const recipient of bulkEmailData.selectedRecipients) {
          sendPromises.push(
            axios.post(`${API}/contractor-files/${file.id}/send-email`, {
              contractor_file_id: file.id,
              supervisor_email: recipient.email,
              supervisor_name: recipient.name
            })
          );
        }
      }
      
      await Promise.all(sendPromises);
      
      const fileCount = selectedContractorFiles.length;
      const recipientCount = bulkEmailData.selectedRecipients.length;
      
      toast.success(language === 'pt-BR' ? 
        `${fileCount} contrato${fileCount > 1 ? 's' : ''} enviado${fileCount > 1 ? 's' : ''} para ${recipientCount} destinatário${recipientCount > 1 ? 's' : ''}!` :
        `${fileCount} contract${fileCount > 1 ? 's' : ''} sent to ${recipientCount} recipient${recipientCount > 1 ? 's' : ''}!`
      );
      
      setShowBulkEmailModal(false);
      setSelectedContractorFiles([]);
      setBulkEmailData({
        selectedRecipients: [],
        selectAll: false
      });
      
    } catch (e) {
      console.error('Failed to send multiple contractor files:', e);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao enviar arquivos' : 'Error sending files'));
    } finally {
      setContractorLoading(false);
    }
  };

  const viewArbitrationFile = async (fileId, fileName = 'Arbitration File') => {
    if (!fileId) return;
    
    try {
      console.log('🔍 Opening arbitration PDF in modal:', fileId);
      
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        toast.error('Token não encontrado. Faça login novamente.');
        return;
      }
      
      // Fetch PDF with authorization
      const response = await fetch(`${API}/arbitration-files/${fileId}/view`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }
      
      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error('PDF file is empty');
      }
      
      const pdfUrl = URL.createObjectURL(blob);
      
      // Abrir em modal
      handlePDFAction(pdfUrl, fileName, false);
      toast.success(language === 'pt-BR' ? 'PDF de arbitragem aberto' : 'Arbitration PDF opened');
      
      console.log('✅ Arbitration PDF opened in modal');
      
    } catch (e) {
      console.error('❌ Failed to view arbitration file:', e);
      toast.error(`Erro ao visualizar PDF: ${e.message}`);
    }
  };

  const closeArbitrationViewer = () => {
    setShowArbitrationViewer(false);
    if (arbitrationPdfUrl) {
      URL.revokeObjectURL(arbitrationPdfUrl);
      setArbitrationPdfUrl(null);
    }
    setArbitrationPdfTitle('');
  };

  // Transfer Labor Bill between PMs (Admin only)
  const openTransferModal = (bill) => {
    setTransferBillId(bill.id);
    setTransferBillData(bill);
    setTransferTargetPM('PM1'); // Default to PM1 instead of ADMIN
    setShowTransferModal(true);
  };

  const transferLaborBill = async () => {
    if (!transferBillId || !transferTargetPM) {
      toast.error('Selecione um PM de destino');
      return;
    }

    if (transferBillData?.pm_user === transferTargetPM) {
      toast.error('O Labor Bill já pertence a este PM');
      return;
    }

    try {
      setTransferLoading(true);

      await axios.put(`${API}/admin/labor-bills/${transferBillId}/transfer`, {
        target_pm: transferTargetPM
      });

      toast.success(`Labor Bill transferido para ${transferTargetPM} com sucesso!`);
      setShowTransferModal(false);
      setTransferBillId(null);
      setTransferBillData(null);
      
      // Reload current labor bills to reflect changes
      await loadLaborBills();

    } catch (e) {
      console.error('Failed to transfer labor bill:', e);
      toast.error(getErrMsg(e, 'Erro ao transferir Labor Bill'));
    } finally {
      setTransferLoading(false);
    }
  };

  // Cleanup orphaned Labor Bills (Admin only)
  const cleanupOrphanedLaborBills = async () => {
    if (!user || user.role !== 'admin') return;
    
    if (!window.confirm('Mover todos os Labor Bills sem equipe para a lixeira do ADMIN?')) {
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await axios.post(`${API}/admin/cleanup/orphaned-labor-bills`);
      
      if (response.data.moved_count > 0) {
        toast.success(`${response.data.moved_count} Labor Bills órfãos movidos para lixeira do ADMIN`);
        
        // Show details of moved bills
        const billNames = response.data.bills_moved.map(b => b.job_name).join(', ');
        console.log('📋 Bills movidos:', billNames);
        
        // Reload current data if viewing ADMIN
        if (selectedPM === 'ADMIN') {
          await loadAdminPMData('ADMIN');
        }
        
        // Reload main labor bills view
        await loadLaborBills();
      } else {
        toast.info('Nenhum Labor Bill órfão encontrado para limpar');
      }
      
    } catch (e) {
      console.error('Failed to cleanup orphaned labor bills:', e);
      toast.error(getErrMsg(e, 'Erro ao limpar Labor Bills órfãos'));
    } finally {
      setLoading(false);
    }
  };

  // Mark Labor Bill as paid (Admin only)
  const markLaborBillAsPaid = async (billId) => {
    if (!user || user.role !== 'admin') return;
    
    try {
      setLoading(true);
      
      // Find the bill to get PM info
      const bill = laborBills.find(b => b.id === billId);
      if (!bill) {
        toast.error('Labor Bill não encontrado');
        return;
      }
      
      const pmUser = bill.pm_user || 'PM1';
      
      await axios.put(`${API}/admin/pm/${pmUser}/labor-bills/${billId}/mark-paid`);
      
      // Update the bill in the current state immediately
      setLaborBills(prevBills => 
        prevBills.map(b => 
          b.id === billId 
            ? { ...b, paid: true, paid_at: new Date().toISOString(), paid_by: user.username }
            : b
        )
      );
      
      toast.success('Labor Bill marcado como pago!');
      console.log('✅ Updated Labor Bill paid status in frontend state');
      
    } catch (e) {
      console.error('Failed to mark labor bill as paid:', e);
      toast.error(getErrMsg(e, 'Erro ao marcar como pago'));
    } finally {
      setLoading(false);
    }
  };

  // Validate Order Number for duplicates
  const validateOrderNumber = async (orderNumber) => {
    if (!orderNumber || orderNumber.trim() === '') return true;
    
    try {
      // SKIP validation if editing existing Labor Bill (has ID)
      if (currentBill?.id) {
        console.log('🔧 Skipping duplicate validation - editing existing Labor Bill:', currentBill.id);
        return true;
      }
      
      // Check for existing Labor Bill with same order number (only for NEW bills)
      const response = await axios.get(`${API}/labor-bills/check-order/${encodeURIComponent(orderNumber)}`);
      
      if (response.data.exists) {
        // Found duplicate - show modal to ask if it's a service
        setOrderValidationData({
          duplicateOrderNumber: orderNumber,
          existingBill: response.data.existing_bill,
          isService: false,
          pendingSave: true
        });
        setShowOrderValidationModal(true);
        return false; // Prevent save until user resolves
      }
      
      return true; // No duplicate found
      
    } catch (e) {
      console.error('Error validating order number:', e);
      // If validation fails, allow save (fail open)
      return true;
    }
  };

  const handleOrderValidationResponse = async (isService) => {
    if (isService) {
      // Add 'S' suffix to order number
      const newOrderNumber = `${orderValidationData.duplicateOrderNumber}S`;
      
      // Check if the 'S' version also exists
      const serviceExists = await validateOrderNumber(newOrderNumber);
      if (!serviceExists) {
        // If 'S' version also exists, we'll handle it in the modal
        return;
      }
      
      // Update form data with 'S' suffix
      setFormData(prev => ({
        ...prev,
        order_number: newOrderNumber
      }));
      
      toast.success(`Order # alterado para ${newOrderNumber} (Service)`);
      setShowOrderValidationModal(false);
      
      // Proceed with save
      await proceedWithSave();
      
    } else {
      // User said it's not a service - don't allow duplicate
      toast.error('Order # já existe! Altere o número da ordem ou marque como Service.');
      setShowOrderValidationModal(false);
    }
  };

  const proceedWithSave = async () => {
    // This will be the actual save function after validation
    await saveLaborBillWithoutValidation(pendingSupFields || {});
    setPendingSupFields(null);
  };

  // Delete Team Leader Document (Admin only)
  const downloadWeeklyReportPDF = async (weekData) => {
    if (!weekData || !user?.username) {
      toast.error('Dados da semana não encontrados');
      return;
    }

    try {
      console.log('📄 Generating Weekly Report PDF for:', weekData);
      
      const response = await axios.post(`${API}/weekly-report/generate-pdf`, {
        week_ending: weekData.week_ending,
        team_name: user.username,
        entries: weekData.entries,
        total_jobs: weekData.total_jobs || weekData.entries?.length || 0,
        total_labor: weekData.total_labor,
        total_expenses: weekData.total_expenses,
        total_final: weekData.total_final
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `weekly_report_${user.username}_${weekData.week_ending}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Relatório da semana ${formatDate(weekData.week_ending)} baixado!`);
      
    } catch (e) {
      console.error('Error downloading weekly report PDF:', e);
      toast.error(getErrMsg(e, 'Erro ao gerar PDF do relatório semanal'));
    }
  };

  const deleteTeamLeaderDocument = async (documentType) => {
    const targetUsername = currentTeamLeader?.username || user?.username;
    if (!targetUsername || user?.role !== 'admin') return;
    
    if (!window.confirm(`Deletar documento ${documentType.replace('_', ' ')}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/team-leader/${targetUsername}/document/${documentType}`);
      
      toast.success(`Documento ${documentType.replace('_', ' ')} deletado com sucesso!`);
      
      // Clear the file from state
      setTeamLeaderFiles(prev => ({
        ...prev,
        [documentType]: null
      }));
      
      // Clear image URL if it was an image
      if (imageUrls[documentType]) {
        URL.revokeObjectURL(imageUrls[documentType]);
        setImageUrls(prev => ({
          ...prev,
          [documentType]: null
        }));
      }
      
      // Reload data
      if (currentTeamLeader?.username) {
        const { data } = await axios.get(`${API}/team-leader/${currentTeamLeader.username}`);
        setCurrentTeamLeader(data);
      }
      
    } catch (e) {
      console.error('Failed to delete document:', e);
      toast.error(getErrMsg(e, 'Erro ao deletar documento'));
    }
  };

  const calculateClientValues = async (laborBill) => {
    if (!laborBill) return;
    
    try {
      console.log('🧮 Calculating values for labor bill:', laborBill.job_name);
      
      // Get team percentage (20% default)
      const teamPercentage = 0.20; // 20%
      
      // Labor total from the Labor Bill
      const laborTotal = laborBill.grand_total || 0;
      
      // Get expenses from the Labor Bill items and extra_items
      let totalExpenses = 0;
      
      // Calculate expenses from items
      if (laborBill.items) {
        laborBill.items.forEach(item => {
          if (item.description && (
            item.description.toLowerCase().includes('trip') ||
            item.description.toLowerCase().includes('hotel') ||
            item.description.toLowerCase().includes('food') ||
            item.description.toLowerCase().includes('expense')
          )) {
            totalExpenses += (item.qty || 0) * (item.unit_price || 0);
          }
        });
      }
      
      // Calculate expenses from extra_items
      if (laborBill.extra_items) {
        laborBill.extra_items.forEach(item => {
          totalExpenses += (item.qty || 0) * (item.unit_price || 0);
        });
      }
      
      // Apply formula: (Total Labor - Despesa - 20%) + Despesa
      // Ou seja: (Labor - Expenses) * (1 - 0.20) + Expenses
      const laborMinusExpenses = laborTotal - totalExpenses;
      const afterTeamPercentage = laborMinusExpenses * (1 - teamPercentage);
      const finalValue = afterTeamPercentage + totalExpenses;
      
      console.log('🧮 Calculation breakdown:');
      console.log(`  Labor Total: $${laborTotal.toFixed(2)}`);
      console.log(`  Expenses: $${totalExpenses.toFixed(2)}`);
      console.log(`  Labor - Expenses: $${laborMinusExpenses.toFixed(2)}`);
      console.log(`  After 20% reduction: $${afterTeamPercentage.toFixed(2)}`);
      console.log(`  Final Value: $${finalValue.toFixed(2)}`);
      
      // Update manual client data
      setManualClientData(prev => ({
        ...prev,
        laborTotal: laborTotal,
        expenses: totalExpenses,
        finalValue: finalValue
      }));
      
    } catch (e) {
      console.error('Error calculating client values:', e);
      toast.error('Erro ao calcular valores');
    }
  };

  // Manual Client Management Functions
  const loadAvailableLaborBills = async () => {
    try {
      setManualClientLoading(true);
      
      // Get all labor bills from all teams
      const allBills = [];
      for (const pm of pmOptions) {
        try {
          const { data } = await axios.get(`${API}/admin/pm/${pm}/labor-bills`);
          const pmBills = data.map(bill => ({ ...bill, pm_user: pm }));
          allBills.push(...pmBills);
        } catch (e) {
          console.error(`Failed to load bills for ${pm}:`, e);
        }
      }
      
      setAvailableLaborBills(allBills);
      console.log(`✅ Loaded ${allBills.length} total labor bills for manual client management`);
      
    } catch (e) {
      console.error('Failed to load available labor bills:', e);
      toast.error('Erro ao carregar Labor Bills disponíveis');
    } finally {
      setManualClientLoading(false);
    }
  };

  const addClientToPayrollTeam = async () => {
    if (!manualClientData.selectedLaborBill || !manualClientData.selectedTeam || !manualClientData.weekEnding) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      setManualClientLoading(true);

      const response = await axios.post(`${API}/admin/payroll/manual-add-client`, {
        labor_bill_id: manualClientData.selectedLaborBill.id,
        target_team: manualClientData.selectedTeam,
        week_ending: manualClientData.weekEnding,
        labor_total: manualClientData.laborTotal,
        calculated_value: manualClientData.finalValue,
        expenses_total: manualClientData.expenses
      });

      toast.success(`Cliente ${manualClientData.selectedLaborBill.job_name} adicionado à equipe ${manualClientData.selectedTeam}!`);
      setShowManualClientModal(false);
      
      // Reset form
      setManualClientData({
        selectedReport: null,
        selectedTeam: 'PM1',
        selectedLaborBill: null,
        laborTotal: 0,
        finalValue: 0,
        expenses: 0,
        weekEnding: ''
      });
      setClientSearch('');
      setClientFilterTeam('ALL');
      
      // Reload payroll reports to reflect changes
      await loadPayrollReports();
      
    } catch (e) {
      console.error('Failed to add client to team:', e);
      toast.error(getErrMsg(e, 'Erro ao adicionar cliente à equipe'));
    } finally {
      setManualClientLoading(false);
    }
  };

  const removeClientFromPayrollTeam = async () => {
    if (!manualClientData.selectedReport || !manualClientData.selectedLaborBill) {
      toast.error('Selecione um relatório e um cliente');
      return;
    }

    console.log('🗑️ Remove client data:', {
      report_id: manualClientData.selectedReport.id,
      labor_bill_id: manualClientData.selectedLaborBill.labor_bill_id,
      client_name: manualClientData.selectedLaborBill.customer
    });

    try {
      setManualClientLoading(true);

      const response = await axios.post(`${API}/admin/payroll/manual-remove-client`, {
        report_id: manualClientData.selectedReport.id,
        labor_bill_id: manualClientData.selectedLaborBill.labor_bill_id
      });

      console.log('✅ Remove response:', response.data);
      toast.success(`Cliente ${manualClientData.selectedLaborBill.customer} removido do relatório!`);
      setShowManualClientModal(false);
      
      // Reset form
      setManualClientData({
        selectedReport: null,
        selectedTeam: 'PM1',
        selectedLaborBill: null,
        laborTotal: 0,
        finalValue: 0,
        expenses: 0,
        weekEnding: ''
      });
      
      // Reload payroll reports to reflect changes
      await loadPayrollReports();
      
    } catch (e) {
      console.error('❌ Failed to remove client from team:', e);
      console.error('❌ Error response:', e.response?.data);
      toast.error(getErrMsg(e, 'Erro ao remover cliente da equipe'));
    } finally {
      setManualClientLoading(false);
    }
  };

  // Arbitration Import Functions
  const importArbitrationFiles = async () => {
    if (!arbitrationImportFiles || arbitrationImportFiles.length === 0) {
      toast.error('Selecione pelo menos um arquivo para importar');
      return;
    }

    try {
      setArbitrationLoading(true);
      const results = [];

      for (const file of arbitrationImportFiles) {
        try {
          const formData = new FormData();
          formData.append('file', file);

          const response = await axios.post(`${API}/admin/arbitration-files/import`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          results.push({
            filename: file.name,
            success: true,
            data: response.data
          });

        } catch (e) {
          results.push({
            filename: file.name,
            success: false,
            error: getErrMsg(e, 'Erro na importação')
          });
        }
      }

      setArbitrationImportResults(results);
      const successCount = results.filter(r => r.success).length;
      
      if (successCount > 0) {
        toast.success(`${successCount} arquivos importados com sucesso!`);
        await loadArbitrationFiles(); // Reload files
      }

      if (results.some(r => !r.success)) {
        toast.warning('Alguns arquivos falharam na importação');
      }

    } catch (e) {
      console.error('Failed to import arbitration files:', e);
      toast.error('Erro geral na importação');
    } finally {
      setArbitrationLoading(false);
    }
  };

  const signArbitrationFile = async () => {
    if (!arbitrationFileToSign || !hasSignature) {
      toast.error(language === 'pt-BR' ? 'Assinatura é obrigatória' : 'Signature is required');
      return;
    }

    try {
      setArbitrationLoading(true);

      // Get signature as base64 image
      const signatureDataURL = signaturePadRef.current.toDataURL();

      // Use general endpoint that all PMs can access
      const endpoint = user?.role === 'admin' 
        ? `${API}/admin/arbitration-files/${arbitrationFileToSign.id}/sign`
        : `${API}/arbitration-files/${arbitrationFileToSign.id}/sign`;

      const response = await axios.put(endpoint, {
        signature: signatureDataURL
      });

      toast.success(language === 'pt-BR' ? 'Arbitration File assinado com sucesso!' : 'Arbitration File signed successfully!');
      setShowArbitrationSignModal(false);
      setArbitrationFileToSign(null);
      setHasSignature(false);
      
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
      }

      await loadArbitrationFiles(); // Reload to show updated status

    } catch (e) {
      console.error('Failed to sign arbitration file:', e);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao assinar arquivo' : 'Error signing file'));
    } finally {
      setArbitrationLoading(false);
    }
  };

  // Edit Report Functions
  const updateReportEntry = async () => {
    if (!editReportData.selectedReport || !editReportData.selectedEntry) {
      toast.error(language === 'pt-BR' ? 'Selecione um relatório e entrada' : 'Select a report and entry');
      return;
    }

    try {
      setEditReportLoading(true);

      const response = await axios.put(`${API}/admin/payroll/edit-entry`, {
        report_id: editReportData.selectedReport.id,
        entry_id: editReportData.selectedEntry.id,
        updated_values: editReportData.editedValues
      });

      toast.success(language === 'pt-BR' ? 'Entrada do relatório atualizada com sucesso!' : 'Report entry updated successfully!');
      
      // Update the report in the current state immediately
      setPayrollReports(prevReports => 
        prevReports.map(report => {
          if (report.id === editReportData.selectedReport.id) {
            const updatedEntries = report.entries.map(entry =>
              entry.id === editReportData.selectedEntry.id 
                ? {...entry, ...editReportData.editedValues, updated_at: new Date().toISOString()}
                : entry
            );
            return {...report, entries: updatedEntries, updated_at: new Date().toISOString()};
          }
          return report;
        })
      );
      
      // Update the selected report in the edit modal to reflect changes
      setEditReportData(prev => ({
        ...prev,
        selectedReport: {
          ...prev.selectedReport,
          entries: prev.selectedReport.entries.map(entry =>
            entry.id === prev.selectedEntry.id 
              ? {...entry, ...prev.editedValues}
              : entry
          )
        }
      }));
      
      console.log('✅ Report updated in frontend state immediately');
      
    } catch (e) {
      console.error('Failed to update report entry:', e);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao atualizar entrada' : 'Error updating entry'));
    } finally {
      setEditReportLoading(false);
    }
  };

  const selectEntryForEdit = (entry) => {
    console.log('📝 Selecting entry for edit:', entry);
    console.log('💰 Entry expenses:', entry.expenses);
    
    setEditReportData(prev => ({
      ...prev,
      selectedEntry: entry,
      editedValues: {
        customer: entry.customer || '',
        job_number: entry.job_number || '',
        labor_total: entry.labor_total || 0,
        expenses: entry.expenses || {},
        calculated_value: entry.calculated_value || 0
      }
    }));
  };

  // Recalculate final value when expenses or labor change
  const recalculateFinalValue = (laborTotal, expenses) => {
    // Apply formula: (Total Labor - Despesas - 20%) + Despesas
    const totalExpenses = Object.values(expenses || {}).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    const laborMinusExpenses = laborTotal - totalExpenses;
    const afterTeamPercentage = laborMinusExpenses * 0.8; // 20% reduction
    const finalValue = afterTeamPercentage + totalExpenses;
    
    console.log('🧮 Recalculating final value:');
    console.log(`  Labor Total: $${laborTotal.toFixed(2)}`);
    console.log(`  Total Expenses: $${totalExpenses.toFixed(2)}`);
    console.log(`  After 20% reduction: $${afterTeamPercentage.toFixed(2)}`);
    console.log(`  Final Value: $${finalValue.toFixed(2)}`);
    
    return finalValue;
  };

  // Edit Arbitration File Name (Admin only)
  const editArbitrationFileName = async () => {
    if (!arbitrationFileToEdit || user?.role !== 'admin') return;

    try {
      setArbitrationLoading(true);

      const response = await axios.put(`${API}/admin/arbitration-files/${arbitrationFileToEdit.id}/edit-name`, {
        new_name: arbitrationFileToEdit.helper_name
      });

      toast.success(language === 'pt-BR' ? 'Nome atualizado com sucesso!' : 'Name updated successfully!');
      setShowArbitrationEditModal(false);
      setArbitrationFileToEdit(null);

      await loadArbitrationFiles(); // Reload to show updated name

    } catch (e) {
      console.error('Failed to edit arbitration file name:', e);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao editar nome' : 'Error editing name'));
    } finally {
      setArbitrationLoading(false);
    }
  };

  // Send Multiple Arbitration Files
  const sendMultipleArbitrationFiles = async () => {
    if (selectedArbitrationFiles.length === 0 || bulkEmailData.selectedRecipients.length === 0) {
      toast.error(language === 'pt-BR' ? 
        'Selecione pelo menos um arquivo e um destinatário' : 
        'Select at least one file and one recipient'
      );
      return;
    }
    
    try {
      setArbitrationLoading(true);
      
      // Send multiple files to all selected recipients
      const response = await axios.post(`${API}/arbitration-files/send-multiple`, {
        arbitration_file_ids: selectedArbitrationFiles.map(f => f.id),
        recipients: bulkEmailData.selectedRecipients.map(r => ({
          email: r.email,
          name: r.name
        }))
      });
      
      const fileCount = selectedArbitrationFiles.length;
      const recipientCount = bulkEmailData.selectedRecipients.length;
      
      toast.success(language === 'pt-BR' ? 
        `${fileCount} arquivo${fileCount > 1 ? 's' : ''} enviado${fileCount > 1 ? 's' : ''} para ${recipientCount} destinatário${recipientCount > 1 ? 's' : ''}!` :
        `${fileCount} file${fileCount > 1 ? 's' : ''} sent to ${recipientCount} recipient${recipientCount > 1 ? 's' : ''}!`
      );
      
      setShowBulkEmailModal(false);
      setSelectedArbitrationFiles([]);
      setBulkEmailData({
        selectedRecipients: [],
        selectAll: false
      });
      
    } catch (e) {
      console.error('Failed to send multiple arbitration files:', e);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao enviar arquivos' : 'Error sending files'));
    } finally {
      setArbitrationLoading(false);
    }
  };

  // Personal Documents Functions (Johnny only)
  const loadPersonalFolders = useCallback(async () => {
    if (user?.role !== 'personal') return;
    try {
      setDocumentsLoading(true);
      const { data } = await axios.get(`${API}/personal/folders`);
      setPersonalFolders(data);
    } catch (e) {
      console.error('Failed to load personal folders:', e);
      toast.error(language === 'pt-BR' ? 'Erro ao carregar pastas' : 'Error loading folders');
    } finally {
      setDocumentsLoading(false);
    }
  }, [user?.role, language]);

  const loadPersonalDocuments = useCallback(async () => {
    if (user?.role !== 'personal') return;
    try {
      setDocumentsLoading(true);
      const { data } = await axios.get(`${API}/personal/documents`);
      const filteredDocs = currentFolderId 
        ? data.filter(doc => doc.folder_id === currentFolderId)
        : data.filter(doc => !doc.folder_id);
      setPersonalDocuments(filteredDocs);
    } catch (e) {
      console.error('Failed to load personal documents:', e);
      toast.error(language === 'pt-BR' ? 'Erro ao carregar documentos' : 'Error loading documents');
    } finally {
      setDocumentsLoading(false);
    }
  }, [user?.role, currentFolderId, language]);

  const createPersonalFolder = useCallback(async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(`${API}/personal/folders`, {
        name: newFolderName,
        parent_folder_id: currentFolderId
      });
      toast.success(language === 'pt-BR' ? 'Pasta criada com sucesso!' : 'Folder created successfully!');
      setShowNewFolderModal(false);
      setNewFolderName('');
      await loadPersonalFolders();
    } catch (e) {
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao criar pasta' : 'Error creating folder'));
    }
  }, [newFolderName, currentFolderId, language, loadPersonalFolders]);

  const uploadPersonalDocuments = useCallback(async () => {
    if (uploadFiles.length === 0) return;
    try {
      setDocumentsLoading(true);
      
      for (const file of uploadFiles) {
        const formData = new FormData();
        formData.append('file', file);
        if (currentFolderId) formData.append('folder_id', currentFolderId);
        
        await axios.post(`${API}/personal/documents/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success(language === 'pt-BR' ? 
        `${uploadFiles.length} documento(s) enviado(s) com sucesso!` :
        `${uploadFiles.length} document(s) uploaded successfully!`
      );
      
      setShowDocumentUpload(false);
      setUploadFiles([]);
      await loadPersonalDocuments();
      
    } catch (e) {
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao enviar documentos' : 'Error uploading documents'));
    } finally {
      setDocumentsLoading(false);
    }
  }, [uploadFiles, currentFolderId, language, loadPersonalDocuments]);

  // Button handlers with flushSync for immediate state updates
  const handleOpenNewFolder = useCallback(() => {
    console.log('📁 New folder button clicked - current state:', showNewFolderModal);
    setShowNewFolderModal(true);
    console.log('📁 State set to true');
  }, [showNewFolderModal]);

  const handleOpenUpload = useCallback(() => {
    console.log('📤 Upload button clicked - current state:', showDocumentUpload);
    setShowDocumentUpload(true);
    console.log('📤 State set to true');
  }, [showDocumentUpload]);

  // Load personal data when user logs in
  useEffect(() => {
    if (isAuthenticated && user?.role === 'personal') {
      loadPersonalFolders();
      loadPersonalDocuments();
    }
  }, [isAuthenticated, user, currentFolderId, loadPersonalFolders, loadPersonalDocuments]);

  // Debug modal states for personal user
  useEffect(() => {
    if (user?.role === 'personal') {
      console.log('🔍 Modal states changed:', {
        showNewFolderModal,
        showDocumentUpload,
        showDocumentViewer
      });
    }
  }, [showNewFolderModal, showDocumentUpload, showDocumentViewer, user?.role]);

  // Company Announcements Functions
  const loadAnnouncements = async () => {
    try {
      setAnnouncementsLoading(true);
      const { data } = await axios.get(`${API}/announcements`);
      setAnnouncements(data);
      
      // Carregar alertas de contas para ADMIN
      if (user?.role === 'admin') {
        try {
          const { data: alerts } = await axios.get(`${API}/admin/company-bills/alerts`);
          setBillAlerts(alerts || []);
          const { data: allBills } = await axios.get(`${API}/admin/company-bills`);
          setCompanyBillsHome(allBills || []);
          const { data: allAlerts } = await axios.get(`${API}/admin/company-alerts`);
          setCompanyAlertsHome(allAlerts || []);
          const { data: unitsData } = await axios.get(`${API}/admin/company-units`);
          setCompanyUnits(unitsData || []);
          try {
            const { data: myAl } = await axios.get(`${API}/admin/alerts`, { params: { status: 'pendente' } });
            setMyCalendarAlerts(myAl.alerts || []);
          } catch (e) {}
        } catch (e) {}
      }
    } catch (e) {
      console.error('Failed to load announcements:', e);
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  // Admin: marca uma Conta a Pagar como paga direto do modal da Home
  const markHomeBillPaid = async (bill) => {
    if (!bill?.id) return;
    try {
      await axios.post(`${API}/admin/company-bills/${bill.id}/mark-paid`);
      toast.success('Conta marcada como paga!');
      setHomeBillViewItem(null);
      await loadAnnouncements();
    } catch (e) {
      toast.error('Erro ao marcar como paga');
    }
  };

  // Admin: marca o backlog de Labor Bills (nunca enviados) como já enviados ao supervisor
  const markAllSupervisorSent = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/labor-bills/ack-supervisor-sent`, {});
      await loadLaborBills();
      toast.success(
        language === 'pt-BR'
          ? `${data.modified} Labor Bill(s) marcados como já enviados ao supervisor`
          : `${data.modified} Labor Bill(s) marked as already sent`,
        {
          duration: 12000,
          action: {
            label: language === 'pt-BR' ? 'Desfazer' : 'Undo',
            onClick: () => undoMarkSupervisorSent(),
          },
        }
      );
    } catch (e) {
      console.error('markAllSupervisorSent failed', e);
      toast.error(language === 'pt-BR' ? 'Falha ao marcar como enviados' : 'Failed to mark as sent');
    }
  };

  // Admin: desfaz a última marcação em massa (reverte só o último lote)
  const undoMarkSupervisorSent = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/labor-bills/undo-ack-supervisor-sent`, {});
      await loadLaborBills();
      if ((data.reverted || 0) > 0) {
        toast.success(
          language === 'pt-BR'
            ? `${data.reverted} marcação(ões) revertida(s) — bills voltaram para "não enviado"`
            : `${data.reverted} mark(s) reverted`
        );
      } else {
        toast.info(language === 'pt-BR' ? 'Nada para desfazer' : 'Nothing to undo');
      }
    } catch (e) {
      console.error('undoMarkSupervisorSent failed', e);
      toast.error(language === 'pt-BR' ? 'Falha ao desfazer' : 'Failed to undo');
    }
  };

  // Admin: identifica bills já enviados no passado (via histórico) e preenche o badge verde
  const reconcileSentFromHistory = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/labor-bills/reconcile-sent`, {});
      await loadLaborBills();
      toast.success(
        language === 'pt-BR'
          ? `${data.updated} Labor Bill(s) identificados como já enviados (a partir do histórico)`
          : `${data.updated} Labor Bill(s) reconciled as already sent`
      );
    } catch (e) {
      console.error('reconcileSentFromHistory failed', e);
      toast.error(language === 'pt-BR' ? 'Falha ao reconciliar envios' : 'Failed to reconcile');
    }
  };



  const createAnnouncement = async () => {
    if (!announcementForm.title.trim() || !announcementForm.content.trim()) {
      toast.error(language === 'pt-BR' ? 'Título e conteúdo são obrigatórios' : 'Title and content are required');
      return;
    }

    try {
      setAnnouncementsLoading(true);
      
      console.log('📢 Creating announcement:', announcementForm);
      
      if (editingAnnouncement) {
        // Update existing
        await axios.put(`${API}/admin/announcements/${editingAnnouncement.id}`, announcementForm);
        toast.success(language === 'pt-BR' ? 'Aviso atualizado!' : 'Announcement updated!');
        console.log('✅ Announcement updated');
      } else {
        // Create new
        const response = await axios.post(`${API}/admin/announcements`, announcementForm);
        toast.success(language === 'pt-BR' ? 'Aviso criado!' : 'Announcement created!');
        console.log('✅ Announcement created:', response.data);
      }
      
      setShowAnnouncementModal(false);
      setEditingAnnouncement(null);
      setAnnouncementForm({
        title: '',
        content: '',
        priority: 'normal',
        expires_at: ''
      });
      
      console.log('🔄 Reloading announcements...');
      const { data: updatedAnnouncements } = await axios.get(`${API}/announcements`);
      console.log('🔄 Fresh announcements loaded:', updatedAnnouncements.length);
      setAnnouncements(updatedAnnouncements);
      console.log('✅ Announcements state updated immediately');
      
    } catch (e) {
      console.error('❌ Error creating announcement:', e);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao salvar aviso' : 'Error saving announcement'));
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  const deleteAnnouncement = async (announcementId) => {
    // Use custom modal instead of window.confirm for iPad compatibility
    const announcementToDelete = announcements.find(a => a.id === announcementId);
    setAnnouncementToDelete(announcementToDelete);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteAnnouncement = async () => {
    if (!announcementToDelete) return;

    try {
      const response = await axios.delete(`${API}/admin/announcements/${announcementToDelete.id}`);
      
      toast.success(language === 'pt-BR' ? 'Aviso deletado!' : 'Announcement deleted!');
      
      setShowDeleteConfirmModal(false);
      setAnnouncementToDelete(null);
      
      const { data: updatedAnnouncements } = await axios.get(`${API}/announcements`);
      setAnnouncements(updatedAnnouncements);
      
    } catch (e) {
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao deletar aviso' : 'Error deleting announcement'));
    }
  };

  // Load announcements when user logs in
  useEffect(() => {
    if (isAuthenticated && user && user.role !== 'personal') {
      loadAnnouncements();
    }
  }, [isAuthenticated, user]);

  // Independent Contractor Functions (duplicate of arbitration)
  const viewContractorFile = async (fileId, fileName = 'Contractor File') => {
    if (!fileId) return;
    
    try {
      console.log('🔍 Opening contractor PDF in modal:', fileId);
      
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        toast.error(language === 'pt-BR' ? 'Token não encontrado. Faça login novamente.' : 'Token not found. Please login again.');
        return;
      }
      
      // Fetch PDF with authorization
      const response = await fetch(`${API}/contractor-files/${fileId}/view`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error('PDF file is empty');
      }
      
      const pdfUrl = URL.createObjectURL(blob);
      
      // Abrir em modal (não em nova aba)
      handlePDFAction(pdfUrl, fileName, false);
      toast.success(language === 'pt-BR' ? 'PDF do contrato aberto' : 'Contract PDF opened');
      
      console.log('✅ Contractor PDF opened in modal');
      
    } catch (e) {
      console.error('Failed to view contractor file:', e);
      toast.error(`${language === 'pt-BR' ? 'Erro ao visualizar contrato' : 'Error viewing contract'}: ${e.message}`);
    }
  };

  const closeContractorViewer = () => {
    setShowContractorViewer(false);
    if (contractorPdfUrl) {
      URL.revokeObjectURL(contractorPdfUrl);
      setContractorPdfUrl(null);
    }
    setContractorPdfTitle('');
  };

  const downloadContractorFile = async (fileId) => {
    try {
      const token = sessionStorage.getItem('access_token');
      const response = await fetch(`${API}/contractor-files/${fileId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Forçar download real
      handlePDFAction(url, `contractor_file_${fileId}.pdf`, true);
      
    } catch (e) {
      console.error('Failed to download contractor file:', e);
      toast.error(language === 'pt-BR' ? 'Erro ao fazer download do contrato' : 'Error downloading contract');
    }
  };

  // Load contractor files (use same data as arbitration for now)
  const loadContractorFiles = async () => {
    try {
      setContractorLoading(true);
      console.log('📋 Loading CONTRACTOR files from /contractor-files endpoint...');
      const { data } = await axios.get(`${API}/contractor-files`); // SEPARATE endpoint
      console.log('📋 Contractor files response:', data.length, 'files');
      console.log('📋 Contractor data:', data);
      setContractorFiles(data);
      console.log(`📋 Updated contractorFiles state with ${data.length} files (SEPARATE from arbitration)`);
    } catch (e) {
      console.error('❌ Failed to load contractor files:', e);
      console.error('❌ Error response:', e.response?.data);
      toast.error(language === 'pt-BR' ? 'Erro ao carregar contratos' : 'Error loading contracts');
    } finally {
      setContractorLoading(false);
    }
  };

  // Create contractor file (duplicate arbitration functionality)
  const createContractorFile = async () => {
    if (!contractorFormData.helper_name) {
      toast.error(language === 'pt-BR' ? 'Preencha o nome do contractor' : 'Fill in contractor name');
      return;
    }
    
    if (!contractorFormData.date) {
      toast.error(language === 'pt-BR' ? 'Selecione uma data' : 'Select a date');
      return;
    }
    
    try {
      setContractorLoading(true);
      
      const response = await axios.post(`${API}/contractor-files`, {
        helper_name: contractorFormData.helper_name,
        title: contractorFormData.title,
        date: contractorFormData.date,
        signature: '', // No signature needed for contractor
        pm_user: user.username
      });
      
      toast.success(language === 'pt-BR' ? 'Contrato criado com sucesso!' : 'Contract created successfully!');
      setShowContractorForm(false);
      
      // Se foi criado a partir de um helper, vincular automaticamente
      if (helperCreatingDocFor && helperCreatingDocFor.doc_type === 'contractor') {
        try {
          const contractorId = response.data.id;
          console.log(`🔗 Vinculando contractor ${contractorId} ao helper ${helperCreatingDocFor.helper_id}`);
          
          await axios.post(`${API}/my-helpers/${helperCreatingDocFor.helper_id}/link-contractor/${contractorId}`);
          
          toast.success(`Contractor vinculado a ${helperCreatingDocFor.helper_name}!`);
          
          // Recarregar helpers para mostrar vínculo
          await loadMyHelpers();
          
          // Voltar para aba Helpers
          setActiveTab('arbitration');
          setArbitrationSubTab('helpers');
        } catch (linkError) {
          console.error('Erro ao vincular contractor:', linkError);
          toast.error('Contractor criado mas não vinculado. Vincule manualmente.');
        } finally {
          setHelperCreatingDocFor(null);
        }
      }
      
      // Reset form
      setContractorFormData({
        helper_name: '',
        title: 'Helper',
        signature: '',
        date: new Date().toISOString().split('T')[0]
      });
      
      await loadContractorFiles();
      
    } catch (e) {
      console.error('Failed to create contractor file:', e);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao criar contrato' : 'Error creating contract'));
    } finally {
      setContractorLoading(false);
    }
  };

  // Load contractor files when needed
  useEffect(() => {
    if (isAuthenticated && user && activeTab === 'arbitration' && arbitrationSubTab === 'contractor') {
      console.log('📋 Loading contractor files for contractor sub-tab');
      loadContractorFiles(); // Load ONLY contractor files
    }
    if (isAuthenticated && user && activeTab === 'arbitration' && arbitrationSubTab === 'files') {
      console.log('⚖️ Loading arbitration files for arbitration sub-tab');
      loadArbitrationFiles(); // Load ONLY arbitration files
    }
  }, [isAuthenticated, user, arbitrationSubTab, activeTab]);

  // Import contractor files function
  const importContractorFiles = async () => {
    if (!contractorImportFiles || contractorImportFiles.length === 0) {
      toast.error(language === 'pt-BR' ? 'Selecione pelo menos um arquivo para importar' : 'Select at least one file to import');
      return;
    }

    try {
      setContractorLoading(true);
      const results = [];

      for (const file of contractorImportFiles) {
        try {
          const formData = new FormData();
          formData.append('file', file);

          const response = await axios.post(`${API}/admin/contractor-files/import`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          results.push({
            filename: file.name,
            success: true,
            data: response.data
          });

        } catch (e) {
          results.push({
            filename: file.name,
            success: false,
            error: getErrMsg(e, 'Erro na importação')
          });
        }
      }

      setContractorImportResults(results);
      const successCount = results.filter(r => r.success).length;
      
      if (successCount > 0) {
        toast.success(language === 'pt-BR' ? 
          `${successCount} contratos importados com sucesso!` :
          `${successCount} contracts imported successfully!`
        );
        await loadContractorFiles(); // Reload contractor files
      }

      if (results.some(r => !r.success)) {
        toast.warning(language === 'pt-BR' ? 'Alguns arquivos falharam na importação' : 'Some files failed to import');
      }

    } catch (e) {
      console.error('Failed to import contractor files:', e);
      toast.error(language === 'pt-BR' ? 'Erro geral na importação' : 'General import error');
    } finally {
      setContractorLoading(false);
    }
  };

  // Delete arbitration file function (separate from announcements)
  const deleteArbitrationFile = async (fileId, fileName) => {
    try {
      await axios.delete(`${API}/admin/arbitration-files/${fileId}`);
      toast.success(language === 'pt-BR' ? 'Arquivo de arbitragem deletado!' : 'Arbitration file deleted!');
      await loadArbitrationFiles();
    } catch (e) {
      console.error('Error deleting arbitration file:', e);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao deletar arquivo' : 'Error deleting file'));
    }
  };

  // Delete contractor file function (separate)
  const deleteContractorFile = async (fileId, fileName) => {
    try {
      await axios.delete(`${API}/admin/contractor-files/${fileId}`);
      toast.success(language === 'pt-BR' ? 'Contrato deletado!' : 'Contract deleted!');
      await loadContractorFiles();
    } catch (e) {
      console.error('Error deleting contractor file:', e);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao deletar contrato' : 'Error deleting contract'));
    }
  };

  // Installation Docs Functions
  const loadInstallationFolders = async () => {
    try {
      setInstallationLoading(true);
      const { data } = await axios.get(`${API}/installation-docs/folders`);
      setInstallationFolders(data);
      console.log(`🔧 Loaded ${data.length} installation folders`);
    } catch (e) {
      console.error('Failed to load installation folders:', e);
      toast.error(language === 'pt-BR' ? 'Erro ao carregar pastas' : 'Error loading folders');
    } finally {
      setInstallationLoading(false);
    }
  };

  const loadInstallationFiles = async () => {
    try {
      setInstallationLoading(true);
      const { data } = await axios.get(`${API}/installation-docs/files`);
      setInstallationFiles(data); // Store all files, filtering will be done in rendering
      console.log(`🔧 Loaded ${data.length} installation files`);
    } catch (e) {
      console.error('Failed to load installation files:', e);
      toast.error(language === 'pt-BR' ? 'Erro ao carregar arquivos' : 'Error loading files');
    } finally {
      setInstallationLoading(false);
    }
  };

  const createInstallationFolder = async () => {
    if (!newInstallationFolderName.trim()) return;
    try {
      await axios.post(`${API}/installation-docs/folders`, {
        name: newInstallationFolderName,
        parent_folder_id: currentInstallationFolderId
      });
      toast.success(language === 'pt-BR' ? 'Pasta criada com sucesso!' : 'Folder created successfully!');
      setShowNewInstallationFolderModal(false);
      setNewInstallationFolderName('');
      await loadInstallationFolders();
    } catch (e) {
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao criar pasta' : 'Error creating folder'));
    }
  };

  const uploadInstallationFiles = async () => {
    if (installationUploadFiles.length === 0) return;
    try {
      setInstallationLoading(true);
      
      console.log('📤 Uploading files to folder:', currentInstallationFolderId || 'Root');
      
      for (const file of installationUploadFiles) {
        const formData = new FormData();
        formData.append('file', file);
        
        // IMPORTANT: Pass current folder ID
        if (currentInstallationFolderId) {
          formData.append('folder_id', currentInstallationFolderId);
          console.log('📁 Adding file to folder:', currentInstallationFolderId);
        } else {
          console.log('📁 Adding file to root (no folder selected)');
        }
        
        const response = await axios.post(`${API}/installation-docs/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        console.log('📤 Upload response:', response.data);
      }
      
      toast.success(language === 'pt-BR' ? 
        `${installationUploadFiles.length} arquivo(s) enviado(s) com sucesso!` :
        `${installationUploadFiles.length} file(s) uploaded successfully!`
      );
      
      setShowInstallationUploadModal(false);
      setInstallationUploadFiles([]);
      
      console.log('🔄 Reloading installation files after upload...');
      await loadInstallationFiles();
      
    } catch (e) {
      console.error('❌ Error uploading installation files:', e);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao enviar arquivos' : 'Error uploading files'));
    } finally {
      setInstallationLoading(false);
    }
  };

  // Load installation data when tab changes - BOTH Admin and PMs
  useEffect(() => {
    if (isAuthenticated && user && activeTab === 'arbitration' && arbitrationSubTab === 'installation') {
      console.log(`🔧 Loading installation docs for ${user.role} on activeTab=${activeTab} subTab=${arbitrationSubTab}...`);
      loadInstallationFolders();
      loadInstallationFiles();
    }
  }, [isAuthenticated, user, arbitrationSubTab, activeTab]);

  // Navigation functions for Installation Docs
  const navigateToInstallationFolder = (folderId) => {
    // Add CURRENT folder to path before navigating to new folder
    if (currentInstallationFolderId) {
      const currentFolder = installationFolders.find(f => f.id === currentInstallationFolderId);
      if (currentFolder) {
        setInstallationFolderPath(prev => [...prev, {
          id: currentInstallationFolderId,
          name: currentFolder.name,
          parent_folder_id: currentFolder.parent_folder_id
        }]);
        console.log('📁 Added current folder to path:', currentFolder.name);
      }
    }
    
    setCurrentInstallationFolderId(folderId);
    console.log('📁 Navigated to folder:', folderId);
  };

  const navigateBackInstallation = () => {
    console.log('⬅️ Navigate back called');
    console.log('⬅️ Current path length:', installationFolderPath.length);
    console.log('⬅️ Current folder ID:', currentInstallationFolderId);
    console.log('⬅️ Path contents:', installationFolderPath);
    
    if (installationFolderPath.length > 0) {
      // Get the previous folder from path
      const previousFolder = installationFolderPath[installationFolderPath.length - 1];
      console.log('⬅️ Going back to previous folder:', previousFolder.name, 'ID:', previousFolder.id);
      
      // Set current folder to previous folder
      setCurrentInstallationFolderId(previousFolder.id);
      
      // Remove last item from path
      setInstallationFolderPath(prev => {
        const newPath = prev.slice(0, -1);
        console.log('⬅️ New path after going back:', newPath);
        return newPath;
      });
    } else {
      // Go to root
      console.log('⬅️ No path history, going to root');
      setCurrentInstallationFolderId(null);
      setInstallationFolderPath([]);
    }
  };

  // Get file count for a specific folder
  const getInstallationFileCount = (folderId) => {
    const count = installationFiles.filter(f => f.folder_id === folderId).length;
    console.log(`📊 Folder ${folderId} has ${count} files`);
    return count;
  };

  // Delete installation folder function
  const deleteInstallationFolder = async (folderId, folderName) => {
    console.log('🗑️ deleteInstallationFolder called:', folderId, folderName);
    try {
      await axios.delete(`${API}/installation-docs/folders/${folderId}`);
      toast.success(language === 'pt-BR' ? `Pasta "${folderName}" deletada!` : `Folder "${folderName}" deleted!`);
      await loadInstallationFolders();
    } catch (e) {
      console.error('Error deleting installation folder:', e);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao deletar pasta' : 'Error deleting folder'));
    }
  };

  // Rename installation folder function
  const renameInstallationFolder = async (folderId, oldName) => {
    console.log('✏️ renameInstallationFolder called:', folderId, oldName);
    const newName = prompt(language === 'pt-BR' ? 'Novo nome da pasta:' : 'New folder name:', oldName);
    if (newName && newName.trim() && newName !== oldName) {
      try {
        await axios.put(`${API}/installation-docs/folders/${folderId}`, {
          name: newName.trim()
        });
        toast.success(language === 'pt-BR' ? 'Pasta renomeada!' : 'Folder renamed!');
        await loadInstallationFolders();
      } catch (e) {
        console.error('Error renaming folder:', e);
        toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao renomear pasta' : 'Error renaming folder'));
      }
    }
  };

  // Delete installation file function
  const deleteInstallationFile = async (fileId, fileName) => {
    console.log('🗑️ deleteInstallationFile called:', fileId, fileName);
    try {
      await axios.delete(`${API}/installation-docs/${fileId}`);
      toast.success(language === 'pt-BR' ? `Arquivo "${fileName}" deletado!` : `File "${fileName}" deleted!`);
      await loadInstallationFiles();
    } catch (e) {
      console.error('Error deleting installation file:', e);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao deletar arquivo' : 'Error deleting file'));
    }
  };

  // Deletar trabalho individual (apenas ADMIN)
  const deleteWorkItem = async (workId, customerName) => {
    if (!window.confirm(`Deletar trabalho de ${customerName}?\n\nIsso afetará todos os usuários PM também.`)) {
      return;
    }

    try {
      toast.info(`🗑️ Deletando trabalho de ${customerName}...`);
      
      const { data } = await axios.delete(`${API}/admin/daily-work/${workId}`);
      
      toast.success(`✅ Trabalho de ${customerName} deletado!`);
      toast.info('📢 Trabalho removido para todos os usuários PM também');
      
      // Recarregar dados
      await loadDailyWork(dailyWorkDateFilter);
      await loadDailyWorkSummary();
      
    } catch (e) {
      console.error('Failed to delete work item:', e);
      toast.error('Erro ao deletar trabalho: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ---------- DAILY WORK FUNCTIONS ----------
  
  const loadDailyWork = async (dateFilter = null) => {
    try {
      setDailyWorkLoading(true);
      const params = new URLSearchParams();
      if (dateFilter) {
        params.append('date_filter', dateFilter);
      }
      
      const { data } = await axios.get(`${API}/daily-work?${params}`);
      setDailyWork(data);
      
      console.log(`📊 Loaded ${data.length} daily work items for ${user?.username} (filter: ${dateFilter || 'none'})`);
      console.log('📋 First work item structure:', data[0]);
      
      if (data.length === 0) {
        console.log('⚠️ No work items returned - may need to check query filters');
        console.log('🔍 User role:', user?.role);
        console.log('🔍 Username:', user?.username);
        console.log('🔍 Date filter:', dateFilter);
      }
      
    } catch (e) {
      console.error('Failed to load daily work:', e);
      console.error('❌ Error details:', e.response?.data);
      toast.error(language === 'pt-BR' ? 'Erro ao carregar trabalhos diários' : 'Error loading daily work');
    } finally {
      setDailyWorkLoading(false);
    }
  };

  const loadDailyWorkSummary = async () => {
    if (!user || user.role !== 'admin') return;
    try {
      const { data } = await axios.get(`${API}/admin/daily-work/summary`);
      setDailyWorkSummary(data);
      console.log(`📊 Loaded daily work summary: ${data.total_work_items} total items`);
    } catch (e) {
      console.error('Failed to load daily work summary:', e);
    }
  };

  const loadDailyWorkConfig = async () => {
    if (!user || user.role !== 'admin') return;
    try {
      const { data } = await axios.get(`${API}/admin/daily-work/config`);
      setDailyWorkConfig(data);
      setDailyWorkFile(data.excel_url); // Pre-populate URL field
      console.log(`⚙️ Loaded daily work config: ${data.excel_url ? 'URL configured' : 'No URL set'}`);
    } catch (e) {
      console.error('Failed to load daily work config:', e);
    }
  };

  const uploadDailyWorkExcel = async () => {
    if (!dailyWorkFile) {
      toast.error(language === 'pt-BR' ? 'Digite a URL do Excel' : 'Enter Excel URL');
      return;
    }

    // Verificar se a URL é da Microsoft e pode precisar de autenticação
    if (dailyWorkFile.includes('microsoft.com') || dailyWorkFile.includes('sharepoint.com') || dailyWorkFile.includes('office.com')) {
      toast.info('🔐 Detectada URL Microsoft - Iniciando processo de autenticação...');
      startMicrosoftAuthentication();
      return;
    }

    // Para URLs não Microsoft, processar normalmente
    await processDailyWorkExcel();
  };

  // Iniciar processo simples - Apenas modal para colar dados
  const startMicrosoftAuthentication = async () => {
    try {
      // Definir data padrão (hoje + 1 dia para trabalhos futuros)
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setExcelWorkDate(tomorrow.toISOString().split('T')[0]);
      
      // Abrir modal para colar dados diretamente
      setShowExcelDataPasteModal(true);
      
      toast.success('📋 Modal aberto - cole os dados do Excel Online');
      
    } catch (e) {
      console.error('Failed to open Excel paste modal:', e);
      toast.error('Erro ao abrir modal');
    }
  };

  // Processar dados colados - COM LOGS COMPLETOS PARA DEBUG
  const processExcelPastedData = async (skipDateCheck = false, confirmedDate = null) => {
    console.log('🔵 STEP 1: Function called', { skipDateCheck, confirmedDate });
    
    if (!excelPastedData.trim()) {
      console.log('🔴 STEP 1-FAIL: No data pasted');
      toast.error('Cole os dados do Excel Online');
      return;
    }

    if (!excelWorkDate && !confirmedDate) {
      console.log('🔴 STEP 1-FAIL: No date selected');
      toast.error('Selecione a data dos trabalhos');
      return;
    }

    console.log('🔵 STEP 2: Converting data to array');
    const lines = excelPastedData.trim().split('\n');
    const rows = lines.map(line => line.split('\t'));
    console.log('🔵 STEP 2-OK: Rows parsed', { totalRows: rows.length, firstRowCols: rows[0]?.length });

    try {
      const finalDate = confirmedDate || excelWorkDate;
      console.log('🔵 STEP 3: Preparing request', { finalDate, skipDateCheck, rowCount: rows.length });
      
      toast.info('🔄 Processando dados colados...');
      
      console.log('🔵 STEP 4: Sending POST to /schedules/import-pasted');
      const response = await axios.post(`${API}/schedules/import-pasted`, {
        excel_data: rows,
        work_date: finalDate,
        skip_date_check: skipDateCheck
      });
      
      console.log('🟢 STEP 5: Response received', { 
        status: response.status,
        created: response.data?.stats?.created,
        filtered: response.data?.stats?.filtered
      });
      
      const data = response.data;
      
      console.log('🔵 STEP 6: Showing success message');
      toast.success(
        `✅ ${data.message}\n` +
        `Criados: ${data.stats.created} | Atualizados: ${data.stats.updated} | Filtrados (não-PM): ${data.stats.filtered}`
      );
      
      console.log('🔵 STEP 7: Clearing form and closing modals');
      setExcelPastedData('');
      setExcelWorkDate('');
      setShowExcelDataPasteModal(false);
      setShowDateConflictModal(false);
      setDateConflictInfo(null);
      setPendingExcelData(null);
      
      console.log('🔵 STEP 8: Reloading calendar', { month: selectedDate.getMonth(), year: selectedDate.getFullYear() });
      await loadScheduleJobs(selectedDate.getMonth(), selectedDate.getFullYear());
      console.log('🟢 STEP 9: Calendar reloaded successfully!');
      
    } catch (err) {
      console.log('🔴 ERROR CAUGHT:', { 
        status: err?.response?.status,
        hasDetail: !!err?.response?.data?.detail
      });
      
      const is409 = err?.response?.status === 409;
      const errDetail = err?.response?.data?.detail;
      
      if (is409 && errDetail?.error_type === 'date_conflict') {
        console.log('🟡 STEP ERROR-409: Date conflict detected', {
          modalDate: errDetail.modal_date,
          excelDate: errDetail.excel_date
        });
        
        setDateConflictInfo({
          modal_date: errDetail.modal_date,
          excel_date: errDetail.excel_date,
          excel_date_formatted: errDetail.excel_date_formatted,
          message: errDetail.message
        });
        setPendingExcelData({ rows, originalDate: excelWorkDate });
        setShowDateConflictModal(true);
        
        console.log('🟡 STEP ERROR-409-OK: Conflict modal state set');
        toast.warning('⚠️ Conflito de datas detectado!');
        return;
      }
      
      console.log('🔴 STEP ERROR-GENERIC: Non-409 error', {
        status: err?.response?.status,
        message: err?.message
      });
      
      const msg = err?.response?.data?.detail?.message || 
                  (typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '') ||
                  err?.message || 
                  'Erro desconhecido';
      toast.error('❌ Erro: ' + msg);
    }
  };

  // Executar processamento final
  const executeExcelProcessing = async (rows, workDate, action = 'add') => {
    try {
      toast.info('🤖 Processando dados...');
      
      // Enviar para backend processar com ação específica
      const { data } = await axios.post(`${API}/admin/daily-work/process-extracted`, {
        excel_data: rows,
        source_url: dailyWorkFile || 'manual_paste',
        work_date: workDate,
        duplicate_action: action  // 'add', 'replace'
      });
      
      toast.success(`✅ Dados processados: ${data.processed_records} trabalhos para ${workDate}!`);
      toast.info(`👥 Equipes encontradas: ${data.teams_identified?.join(', ') || 'PMs processados'}`);
      
      // Limpar e fechar modais
      setExcelPastedData('');
      setExcelWorkDate('');
      setShowExcelDataPasteModal(false);
      setShowDuplicateModal(false);
      setDuplicateInfo(null);
      setPendingProcessData(null);
      
      // Recarregar dados para ADMIN e notificar sobre espelhamento para PMs
      setDailyWorkDateFilter(workDate);
      await loadDailyWork(workDate);
      await loadDailyWorkSummary();
      
      // Atualizar calendário de schedule com novos trabalhos
      const workDateObj = new Date(workDate);
      await loadScheduleJobs(workDateObj.getMonth(), workDateObj.getFullYear());
      toast.success(`📅 Calendário atualizado com ${data.schedule_jobs_created || 0} trabalhos!`);
      
      // Broadcast toast info sobre espelhamento para PMs
      toast.success('📡 Dados automaticamente disponibilizados para todos os usuários PM!');
      
      // Log para debug
      console.log('✅ Dados processados e espelhados para PMs:', data.teams_identified);
      
    } catch (e) {
      console.error('Failed to execute Excel processing:', e);
      toast.error('Erro ao processar dados: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Tratar duplicatas - substituir
  const handleDuplicatesReplace = async () => {
    if (!pendingProcessData) return;
    
    toast.info('🔄 Substituindo trabalhos existentes...');
    await executeExcelProcessing(pendingProcessData.rows, pendingProcessData.excelWorkDate, 'replace');
  };

  // Tratar duplicatas - continuar (adicionar)
  const handleDuplicatesContinue = async () => {
    if (!pendingProcessData) return;
    
    toast.info('➕ Adicionando trabalhos sem remover existentes...');
    await executeExcelProcessing(pendingProcessData.rows, pendingProcessData.excelWorkDate, 'add');
  };

  // Processar Excel (com ou sem autenticação)
  const processDailyWorkExcel = async () => {
    try {
      setDailyWorkLoading(true);
      
      const { data } = await axios.post(`${API}/admin/daily-work/upload`, {
        excel_url: dailyWorkFile  // Now it's a URL string, not a file
      });

      toast.success(language === 'pt-BR' ? 
        `Excel processado: ${data.processed_records} trabalhos distribuídos!` :
        `Excel processed: ${data.processed_records} work items distributed!`
      );

      setShowDailyWorkUpload(false);
      setDailyWorkFile(null);
      
      // Reload data
      await loadDailyWork();
      await loadDailyWorkSummary();
      
    } catch (e) {
      console.error('Failed to upload daily work Excel:', e);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao processar Excel' : 'Error processing Excel'));
    } finally {
      setDailyWorkLoading(false);
    }
  };

  const syncDailyWork = async () => {
    try {
      setDailyWorkLoading(true);
      
      const { data } = await axios.post(`${API}/schedules/import-excel`);
      
      toast.success(language === 'pt-BR' ? 
        `Import concluído: ${data.stats.created} criados, ${data.stats.updated} atualizados!` :
        `Import completed: ${data.stats.created} created, ${data.stats.updated} updated!`
      );
      
      // Reload schedule calendar
      await loadScheduleJobs(selectedDate.getMonth(), selectedDate.getFullYear());
      
    } catch (e) {
      console.error('Failed to import Excel to schedule:', e);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro na importação' : 'Error importing'));
    } finally {
      setDailyWorkLoading(false);
    }
  };

  // Padronizar datas no ambiente de PRODUÇÃO
  const standardizeProductionLaborBillDates = async () => {
    if (!window.confirm(
      '🚀 ATENÇÃO: Isso irá padronizar as datas de TODOS os Labor Bills no BANCO DE PRODUÇÃO (DEPLOYED)!\n\n' +
      'Esta operação afeta o ambiente REAL com dados dos clientes.\n\n' +
      'Tem certeza que deseja continuar?'
    )) {
      return;
    }

    const password = prompt('Digite a senha de produção para confirmar:');
    if (password !== 'ADMIN2025') {
      toast.error('❌ Senha incorreta!');
      return;
    }

    try {
      setLoading(true);
      toast.info('🚀 Conectando ao banco de PRODUÇÃO e padronizando datas...');
      
      const { data } = await axios.put(`${API}/admin/standardize-production-dates`, {
        confirmation: 'PRODUCTION_UPDATE_CONFIRMED'
      });
      
      toast.success(`🎉 PRODUÇÃO: ${data.updated_count} Labor Bills atualizados no ambiente Deployed!`);
      toast.warning('⚠️ Mudanças aplicadas no banco de PRODUÇÃO - visíveis no ambiente Deployed');
      
      // Mostrar relatório detalhado
      if (data.format_analysis && Object.keys(data.format_analysis).length > 0) {
        console.log('📊 PRODUÇÃO - Formatos convertidos:', data.format_analysis);
        toast.info(`📋 Formatos convertidos no ambiente de produção`);
      }
      
    } catch (e) {
      console.error('Failed to standardize production dates:', e);
      toast.error('❌ Erro ao acessar PRODUÇÃO: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  // Debug histórico de emails (temporário)
  const debugEmailHistory = async () => {
    try {
      setLoading(true);
      toast.info('🔍 Analisando estrutura dos emails antigos...');
      
      const { data } = await axios.get(`${API}/admin/debug-email-history`);
      
      console.log('🔍 DEBUG EMAIL STRUCTURE:', data);
      
      toast.info(`📊 Análise: ${data.total_problem_emails} emails problemáticos encontrados`);
      toast.info(`📋 Campos disponíveis: ${data.available_fields?.slice(0, 5).join(', ')}...`);
      
      // Mostrar detalhes no console
      if (data.sample_emails) {
        console.log('📧 SAMPLE EMAILS:', data.sample_emails);
      }
      if (data.search_tests) {
        console.log('🔍 SEARCH TESTS:', data.search_tests);
      }
      
    } catch (e) {
      console.error('Failed to debug email history:', e);
      toast.error('Erro ao debugar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  // Corrigir histórico de emails antigos
  const fixEmailHistorySnapshots = async () => {
    if (!window.confirm(
      'Corrigir histórico de emails antigos?\n\n' +
      'Isso tentará reconstruir informações dos emails enviados anteriormente ' +
      'buscando dados nos Labor Bills originais.\n\n' +
      'Deseja continuar?'
    )) {
      return;
    }

    try {
      setLoading(true);
      toast.info('🔧 Corrigindo histórico de emails antigos...');
      
      const { data } = await axios.post(`${API}/admin/fix-email-history-snapshots`);
      
      toast.success(`✅ Correção concluída: ${data.fixed_count} emails corrigidos!`);
      
      if (data.errors_count > 0) {
        toast.warning(`⚠️ ${data.errors_count} emails não puderam ser corrigidos (Labor Bill não encontrado)`);
      }
      
      // Recarregar histórico após correção
      await loadEmailHistory();
      
    } catch (e) {
      console.error('Failed to fix email history:', e);
      toast.error('Erro ao corrigir histórico: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  // Padronizar formato de datas de TODOS os Labor Bills
  const standardizeAllLaborBillDates = async () => {
    if (!window.confirm(
      'ATENÇÃO: Isso irá padronizar as datas de TODOS os Labor Bills no banco de dados para formato MM-DD-YYYY.\n\n' +
      'Esta operação afeta TODOS os usuários PM e não pode ser desfeita.\n\n' +
      'Deseja continuar?'
    )) {
      return;
    }

    try {
      setLoading(true);
      toast.info('📅 Iniciando padronização de datas de todos os Labor Bills...');
      
      const { data } = await axios.put(`${API}/admin/standardize-labor-bill-dates`);
      
      toast.success(`✅ Padronização concluída: ${data.updated_count} Labor Bills atualizados!`);
      
      if (data.updated_count === 0) {
        toast.info(`📊 Análise: ${data.total_processed} Labor Bills encontrados, formatos já estão corretos`);
        
        // Mostrar debug dos formatos encontrados
        if (data.format_analysis && Object.keys(data.format_analysis).length > 0) {
          console.log('📅 Formatos de data encontrados:', data.format_analysis);
          const formats = Object.keys(data.format_analysis);
          toast.info(`📋 Formatos encontrados: ${formats.slice(0, 3).join(', ')}${formats.length > 3 ? '...' : ''}`);
        }
      } else {
        toast.info('📢 Todas as datas agora estão no formato MM-DD-YYYY para todos os usuários');
      }
      
      // Recarregar dados para mostrar datas atualizadas
      if (selectedPM && selectedPM !== 'ADMIN') {
        await loadAdminPMData(selectedPM);
      }
      
    } catch (e) {
      console.error('Failed to standardize labor bill dates:', e);
      toast.error('Erro ao padronizar datas: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const clearDailyWork = async () => {
    if (!window.confirm(language === 'pt-BR' ? 
      'Tem certeza que deseja limpar todos os trabalhos diários? Isso afetará todos os usuários PM também.' :
      'Are you sure you want to clear all daily work? This will affect all PM users too.'
    )) return;

    try {
      setDailyWorkLoading(true);
      
      // Se há filtro de data, limpar apenas dessa data
      let confirmMessage = '';
      if (dailyWorkDateFilter) {
        confirmMessage = `Limpando trabalhos da data: ${dailyWorkDateFilter}`;
      } else {
        confirmMessage = 'Limpando TODOS os trabalhos diários';
      }
      
      toast.info(confirmMessage);
      
      const { data } = await axios.delete(`${API}/admin/daily-work/clear`, {
        data: { date_filter: dailyWorkDateFilter || null }  // Enviar filtro de data se existir
      });

      toast.success(language === 'pt-BR' ? 
        `${data.deleted_count} trabalhos removidos (todos os usuários)` :
        `${data.deleted_count} work items removed (all users)`
      );
      
      // Recarregar dados para ADMIN
      await loadDailyWork();
      await loadDailyWorkSummary();
      
      // Toast para informar que PMs também foram afetados
      toast.info('📢 Dados removidos para todos os usuários PM também');
      
    } catch (e) {
      console.error('Failed to clear daily work:', e);
      toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao limpar dados' : 'Error clearing data'));
    } finally {
      setDailyWorkLoading(false);
    }
  };

  // Load team percentages for payroll
  const loadTeamPercentages = async () => {
    if (!user || user.role !== 'admin') return;
    try {
      const { data } = await axios.get(`${API}/admin/team-percentages`);
      setTeamPercentages(data);
    } catch (e) {
      console.error('Failed to load team percentages:', e);
    }
  };

  // Load payroll reports
  const loadPayrollReports = async () => {
    if (!user || user.role !== 'admin') return;
    try {
      const { data } = await axios.get(`${API}/admin/payroll/reports`);
      setPayrollReports(data);
    } catch (e) {
      console.error('Failed to load payroll reports:', e);
    }
  };

  // Update ticket status
  const updateTicketStatus = async (ticketId, newStatus) => {
    try {
      await axios.put(`${API}/admin/tickets/${ticketId}/status`, { status: newStatus });
      toast.success(`Status alterado para ${newStatus}`);
      await loadAdminTickets();
      if (selectedTicket?.id === ticketId) {
        const updated = adminTickets.find(t => t.id === ticketId);
        if (updated) setSelectedTicket({...updated, status: newStatus});
      }
    } catch (e) {
      toast.error("Erro ao alterar status do ticket");
    }
  };

  // Create/Edit bill actions
  const startNewBill = async () => {
    await startNewBillWithPrefill();
  };

  const startNewBillWithPrefill = async (prefill) => {
    try {
      const { data } = await axios.get(`${API}/pricing/effective`);
      const eff = data?.prices || {};
      
      // Criar items com qty VAZIO (não 0) para facilitar edição
      const items = getDefaultItems().map((it) => ({ 
        ...it, 
        qty: '',  // Campo vazio (não 0)
        unit_price: (eff[it.description] ?? it.unit_price) 
      }));
      setCurrentBill({ id: undefined, items, extra_items: prefill?.extra_items || [] });
      const base = getDefaultBillData();
      const form = prefill ? { ...base, ...prefill } : base;
      setFormData(form);
      setIsDirty(false);
      setActiveTab('create');
      
      // Verificar se tem schedule_job_id para buscar draft da IA (medição não tem draft)
      // IMPORTANTE: Chamar DEPOIS de setar currentBill para não ser sobrescrito
      const jobId = prefill?.bill_kind === 'MEASURE' ? null : prefill?.schedule_job_id;
      
      if (jobId) {
        // Tentar buscar draft da IA (vai atualizar currentBill se encontrar)
        await loadLaborBillDraft(jobId);
      }
      
    } catch (e) {
      const base = getDefaultBillData();
      const form = prefill ? { ...base, ...prefill } : base;
      // Criar items com qty VAZIO
      const items = getDefaultItems();
      setCurrentBill({ id: undefined, items, extra_items: prefill?.extra_items || [] });
      setFormData(form);
      setIsDirty(false);
      setActiveTab('create');
    }
  };

  // Labor Bill de um trabalho do calendário: medição (bill_kind MEASURE) nunca se mistura com o da instalação do mesmo Order
  const findJobLaborBill = (job) => {
    const isMeasure = job.job_type === 'MEASURE';
    return laborBills.find((b) => !b.deleted && (isMeasure ? b.bill_kind === 'MEASURE' : b.bill_kind !== 'MEASURE')
      && ((job.order_number && b.order_number === job.order_number) || (isMeasure && b.schedule_job_id === job.id)));
  };

  const openJobLaborBill = async (job) => {
    const existingBill = findJobLaborBill(job);
    if (existingBill) {
      toast.info(`📄 Abrindo PDF do Labor Bill #${job.order_number || ''}...`);
      await viewLaborBillPDF(existingBill.id);
      return;
    }
    const orderNumber = (job.order_number && job.order_number !== 'None') ? job.order_number : '';
    if (job.job_type === 'MEASURE') {
      // Medição: itens zerados + "Additional Labor Cost" = valor do preset "Measure" (Admin → Extra Labor presets) e Notes "Measure: Cliente - Order"
      let fee = 100;
      try {
        const { data } = await axios.get(`${API}/extra-labor-presets`);
        const preset = (data?.presets || []).find((p) => /^\s*measure\s*$/i.test(p.description || ''));
        if (preset && Number(preset.unit_price) > 0) fee = Number(preset.unit_price);
      } catch (e) { /* usa $100 */ }
      await startNewBillWithPrefill({
        install_date: job.date || new Date().toISOString().split("T")[0],
        job_name: job.customer_name || '',
        order_number: orderNumber,
        total_windows: 0,
        total_doors: 0,
        pm_code: '',
        schedule_job_id: job.id,
        bill_kind: 'MEASURE',
        additional_labor_cost: String(fee),
        notes: `Measure: ${`${job.customer_name || ''}${orderNumber ? ` - ${orderNumber}` : ''}`.trim()}`,
      });
      toast.success(`📏 Labor Bill de medição iniciado para ${job.customer_name} ($${fee})`);
      return;
    }
    await startNewBillWithPrefill({
      install_date: job.date || new Date().toISOString().split("T")[0],
      job_name: job.customer_name || '',
      order_number: orderNumber,
      total_windows: job.windows || 0,
      total_doors: job.doors || 0,
      pm_code: job.team || '',
      schedule_job_id: job.id
    });
    toast.success(`💼 Labor Bill iniciado para ${job.customer_name}`);
  };

  const saveLaborBill = async (extraFields = {}) => {
    // Preserva os campos do supervisor (fom_assigned/fom_email) para o caminho de
    // validação de order# (proceedWithSave usa pendingSupFields)
    setPendingSupFields(extraFields || {});
    // Validate order number first
    const isValid = await validateOrderNumber(formData.order_number);
    if (!isValid) {
      // Validation modal will handle the rest
      return;
    }
    
    // If validation passes, proceed with save
    await saveLaborBillWithoutValidation(extraFields || {});
    setPendingSupFields(null);
  };

  const saveLaborBillWithoutValidation = async (extraFields = {}) => {
    if (loading) return; // Guard contra múltiplos cliques
    setLoading(true);
    
    // Use admin endpoint if user is admin, otherwise use regular endpoint
    const endpoint = currentBill?.id
      ? (user?.role === 'admin' 
          ? `${API}/admin/labor-bills/${currentBill.id}`
          : `${API}/labor-bills/${currentBill.id}`)
      : `${API}/labor-bills`;
    
    try {
      // Converter qty vazio para 0 antes de enviar ao backend
      const itemsToSend = (currentBill?.items || defaultItems).map(item => ({
        ...item,
        qty: item.qty === '' ? 0 : parseInt(item.qty) || 0
      }));
      
      const extrasToSend = (currentBill?.extra_items || []).map(extra => ({
        ...extra,
        qty: extra.qty === '' ? 0 : parseInt(extra.qty) || 0
      }));
      
      const billData = {
        ...formData,
        ...(extraFields || {}),
        items: itemsToSend,
        extra_items: extrasToSend,
        schedule_job_id: formData.schedule_job_id || currentBill?.schedule_job_id  // Incluir vinculação
      };
      
      console.log('💾 Salvando Labor Bill:', billData);
      console.log('💾 Items count:', billData.items?.length || 0);
      console.log('💾 Schedule Job ID:', billData.schedule_job_id);
        
      const res = currentBill?.id
        ? await axios.put(endpoint, billData)
        : await axios.post(endpoint, billData);
      
      const savedBill = res.data;
      console.log('✅ Labor Bill salvo:', savedBill);
      
      // Garantir que items sempre existam ao atualizar currentBill
      if (!savedBill.items || savedBill.items.length === 0) {
        console.warn('⚠️ Labor Bill retornou sem items, usando items salvos');
        savedBill.items = billData.items;
      }
      
      setCurrentBill(savedBill);
      setIsDirty(false);
      
      // Recarregar schedule jobs para atualizar vinculação (com mês/ano correto)
      await loadScheduleJobs(selectedDate.getMonth(), selectedDate.getFullYear());
      await loadLaborBills();
      
      toast.success(
        language === 'pt-BR' 
          ? '✅ Labor Bill salvo! Veja na aba "Labor Bills"' 
          : '✅ Draft saved! Check "Labor Bills" tab',
        { duration: 4000 }
      );
      
      // If PM user created new Labor Bill, trigger instant notification for ADMIN
      if (user?.role !== 'admin' && !currentBill?.id) {
        console.log('📬 PM created new Labor Bill - triggering instant ADMIN notification');
        
        // Add to localStorage queue for instant modal
        const notificationData = {
          id: Date.now() + Math.random(),
          title: '🔔 Novo Labor Bill Criado',
          message: `${user.username} criou Labor Bill: ${billData.job_name || 'Untitled'}`,
          from: user.username,
          timestamp: Date.now(),
          recipients: 'admin',
          event_type: 'labor_bill_created',
          seen: false
        };
        
        // Get existing queue and add notification
        const existingQueue = JSON.parse(localStorage.getItem('notification_queue') || '[]');
        existingQueue.push(notificationData);
        localStorage.setItem('notification_queue', JSON.stringify(existingQueue));
        
        console.log('✅ Instant notification added to queue for ADMIN');
      }
    } catch (e) {
      console.error("Erro ao salvar:", e);
      console.error("Resposta do servidor:", e.response?.data);
      console.error("Status:", e.response?.status);
      console.error("Endpoint usado:", endpoint);
      toast.error(`Falha ao salvar: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateItemQuantity = (index, qty) => {
    if (!currentBill) return;
    let updated = [...currentBill.items];
    
    // Validate index to prevent undefined access
    if (index < 0 || index >= updated.length) {
      console.error(`Invalid index ${index} for items array with length ${updated.length}`);
      return;
    }
    
    // Ensure the item exists before updating
    if (!updated[index]) {
      console.error(`Item at index ${index} is undefined`);
      return;
    }
    
    // Aceitar vazio ou número
    updated[index].qty = qty === '' ? '' : Math.max(0, parseInt(qty) || 0);
    setCurrentBill({ ...currentBill, items: updated });
    setIsDirty(true);
    // 🤖 Usuário editou: tira o destaque "IA" do item
    const desc = updated[index].description;
    if (aiFilledFields.includes(desc)) setAiFilledFields((prev) => prev.filter((f) => f !== desc));
  };

  const updateExtraItem = (index, field, value) => {
    const extras = [...(currentBill?.extra_items || [])];
    
    // Validate index to prevent undefined access
    if (index < 0 || index >= extras.length) {
      console.error(`Invalid index ${index} for extra_items array with length ${extras.length}`);
      return;
    }
    
    // Ensure the item exists before updating
    if (!extras[index]) {
      console.error(`Extra item at index ${index} is undefined`);
      return;
    }
    
    if (field === "qty") extras[index].qty = parseInt(value) || 0;
    else if (field === "unit_price") extras[index].unit_price = parseFloat(value) || 0;
    else extras[index].description = value;
    setCurrentBill({ ...(currentBill || { items: defaultItems }), extra_items: extras });
    setIsDirty(true);
  };

  const addExtraItem = () => {
    // Salvar posição do scroll antes de adicionar item (prevenir jump no iOS)
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    
    const extras = [...(currentBill?.extra_items || [])];
    extras.push({ description: "", qty: 0, unit_price: 0, total: 0 });
    setCurrentBill({ ...(currentBill || { items: defaultItems }), extra_items: extras });
    setIsDirty(true);
    
    // Restaurar posição do scroll (especialmente importante no iOS)
    setTimeout(() => {
      window.scrollTo(scrollX, scrollY);
    }, 0);
  };

  const removeExtraItem = (idx) => {
    const extras = [...(currentBill?.extra_items || [])];
    extras.splice(idx, 1);
    setCurrentBill({ ...(currentBill || { items: defaultItems }), extra_items: extras });
    setIsDirty(true);
  };

  const updateItemQuantityByDescription = (description, qty) => {
    if (!currentBill) return;
    let updated = [...currentBill.items];
    
    // Find item by description
    const itemIndex = updated.findIndex(item => item.description === description);
    
    if (itemIndex === -1) {
      console.error(`Item with description "${description}" not found`);
      return;
    }
    
    // Aceitar vazio ou número
    updated[itemIndex].qty = qty === '' ? '' : Math.max(0, parseInt(qty) || 0);
    setCurrentBill({ ...currentBill, items: updated });
    setIsDirty(true);
    
    // 🤖 Remover destaque azul quando usuário edita
    if (aiFilledFields.includes(description)) {
      setAiFilledFields(prev => prev.filter(f => f !== description));
    }
  };

  // Linha do formulário (template do Designer) ↔ item do bill: pelo campo do molde (estável) ou pelo nome.
  // Se o bill foi criado antes de uma mudança no Designer (nome novo/item novo), o item é criado/renomeado ao editar.
  const findBillItemIndex = (items, tplItem) => {
    if (!items || !tplItem) return -1;
    if (tplItem.qty_field) {
      const i = items.findIndex((it) => it.qty_field && it.qty_field === tplItem.qty_field);
      if (i !== -1) return i;
    }
    return items.findIndex((it) => it.description === tplItem.description);
  };
  const updateTemplateItemQty = (tplItem, category, qty) => {
    if (!currentBill) return;
    const items = [...(currentBill.items || [])];
    let idx = findBillItemIndex(items, tplItem);
    if (idx === -1) {
      items.push({ description: tplItem.description, qty: '', unit_price: tplItem.unit_price, total: 0, category, qty_field: tplItem.qty_field || null, total_field: tplItem.total_field || null });
      idx = items.length - 1;
    } else if (items[idx].description !== tplItem.description) {
      items[idx] = { ...items[idx], description: tplItem.description, qty_field: items[idx].qty_field || tplItem.qty_field || null, total_field: items[idx].total_field || tplItem.total_field || null };
    }
    items[idx] = { ...items[idx], qty: qty === '' ? '' : Math.max(0, parseInt(qty) || 0) };
    setCurrentBill({ ...currentBill, items });
    setIsDirty(true);
    if (aiFilledFields.includes(tplItem.description)) setAiFilledFields((prev) => prev.filter((f) => f !== tplItem.description));
  };

  // Normaliza um bill salvo contra o template ATUAL do Designer:
  // - item renomeado cai na linha certa (pelo qty_field; soma se duas linhas antigas caírem na mesma);
  // - item sem linha no template e Extra Labor legado viram "Additional Labor Cost" + linha nas Notes.
  // Assim nenhum valor fica escondido somando no total sem o instalador ver/apagar.
  const normalizeBillToTemplate = (bill) => {
    const tpl = getDefaultItems();
    const src = (bill?.items || []).map((it) => ({ ...it }));
    const used = new Set();
    const items = tpl.map((t) => {
      let qty = 0; let price = null; let any = false;
      src.forEach((it, idx) => {
        if (used.has(idx)) return;
        const sameField = t.qty_field && it.qty_field && it.qty_field === t.qty_field;
        const sameName = (it.description || '').trim() === t.description;
        if (sameField || sameName) {
          used.add(idx); any = true;
          qty += Number(it.qty) || 0;
          if (price === null && it.unit_price != null && it.unit_price !== '') price = Number(it.unit_price);
        }
      });
      return { ...t, qty: any ? qty : '', unit_price: price !== null ? price : t.unit_price, total: 0 };
    });
    const legacyLines = []; let legacyValue = 0;
    src.forEach((it, idx) => {
      const q = Number(it.qty) || 0;
      if (used.has(idx) || q <= 0) return;
      const up = Number(it.unit_price) || 0;
      legacyValue += q * up;
      legacyLines.push(`${it.description} x${q} @ $${up.toFixed(2)} = $${(q * up).toFixed(2)}`);
    });
    (bill?.extra_items || []).forEach((e) => {
      const q = Number(e.qty) || 0; const up = Number(e.unit_price) || 0;
      if (q <= 0 && !(e.description || '').trim()) return;
      legacyValue += q * up;
      legacyLines.push(`${e.description} x${q} @ $${up.toFixed(2)} = $${(q * up).toFixed(2)}`);
    });
    let notes = bill?.notes || '';
    let additionalLaborCost = bill?.additional_labor_cost || '';
    const changed = legacyLines.length > 0;
    if (changed) {
      const cur = parseFloat(String(additionalLaborCost).replace(/[^0-9.\-]/g, '')) || 0;
      additionalLaborCost = (cur + legacyValue).toFixed(2);
      const block = 'Extra labor: ' + legacyLines.join('; ');
      if (!notes.includes(block)) notes = notes ? `${notes}\n${block}` : block;
    }
    return { items, notes, additionalLaborCost, changed };
  };

  const loadLaborBill = async (billId) => {
    try {
      // Use admin endpoint if user is admin, otherwise use regular endpoint
      const endpoint = user?.role === 'admin' 
        ? `${API}/admin/labor-bills/${billId}`
        : `${API}/labor-bills/${billId}`;
        
      const res = await axios.get(endpoint);
      const bill = res.data;
      
      console.log('🔍 Loaded Labor Bill:', bill);
      console.log('🔍 Items count:', bill.items?.length || 0);
      
      // Garantir que items sempre existam
      if (!bill.items || bill.items.length === 0) {
        console.warn('⚠️ Labor Bill sem items, usando defaultItems');
        bill.items = defaultItems;
      }
      
      // Garantir que extra_items existam
      if (!bill.extra_items) {
        bill.extra_items = [];
      }
      
      // Alinha o bill ao template ATUAL do Designer: nada fica escondido somando no total.
      const norm = normalizeBillToTemplate(bill);
      setCurrentBill({ ...bill, items: norm.items, extra_items: [] });
      setFormData({
        install_date: bill.install_date,
        job_name: bill.job_name,
        order_number: bill.order_number,
        total_windows: bill.total_windows,
        total_doors: bill.total_doors,
        notes: norm.notes,
        additional_labor_cost: norm.additionalLaborCost,
        schedule_job_id: bill.schedule_job_id
      });
      // FIX: Ir para aba create/edit mas preservar contexto de edição
      setActiveTab("create"); // Precisa ir para aba de edição
      setIsDirty(norm.changed);
      if (norm.changed) {
        toast.info(language === 'pt-BR'
          ? 'Itens antigos sem linha no Labor Bill foram movidos para "Additional Labor Cost" + Notes. Confira e salve.'
          : 'Old items with no line on the Labor Bill were moved to "Additional Labor Cost" + Notes. Review and save.');
      }
      
      // Log para debug
      console.log('🔧 Loading Labor Bill for EDIT:', billId, bill);
    } catch (e) {
      toast.error("Falha ao carregar");
    }
  };

  // Carrega o PDF source (da work order) sempre que abrir/editar um Labor Bill
  useEffect(() => {
    const jobId = currentBill?.schedule_job_id || formData?.schedule_job_id;
    console.log('[FloatingPDF] useEffect — jobId resolved:', jobId, '| currentBill.id:', currentBill?.id, '| activeTab:', activeTab);
    if (!jobId) {
      setSourcePdfUrl(null);
      setSourcePdfTitle('');
      setShowFloatingPdf(false);
      setFloatingPdfMinimized(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: jobs } = await axios.get(`${API}/schedules`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
        });
        if (cancelled) return;
        const job = jobs.find(j => j.id === jobId);
        console.log('[FloatingPDF] Job lookup:', job ? { id: job.id, has_pdf: job.has_pdf, draft_pdf_url: job.draft_pdf_url, customer: job.customer_name } : 'NOT FOUND');
        if (job?.draft_pdf_url) {
          const url = `${API}/files/${job.draft_pdf_url}`;
          setSourcePdfUrl(url);
          setSourcePdfTitle(`${job.customer_name || 'Work Order'}${job.order_number ? ' - ' + job.order_number : ''}`);
          console.log('[FloatingPDF] ✅ PDF source ready:', url);
        } else {
          setSourcePdfUrl(null);
          setSourcePdfTitle('');
          console.log('[FloatingPDF] ❌ Job has no draft_pdf_url');
        }
      } catch (e) {
        console.warn('[FloatingPDF] Falha ao buscar source PDF:', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [currentBill?.schedule_job_id, formData?.schedule_job_id, currentBill?.id, activeTab]);

  const loadLaborBillFromPayroll = async (billId) => {
    try {
      // Save current payroll context before switching to edit
      if (payrollReport) {
        setPayrollContext({
          report: payrollReport,
          activeTab: activeTab,
          adminSubTab: adminSubTab
        });
      }
      
      // Load the labor bill for editing
      await loadLaborBill(billId);
      
      toast.info('📝 Editando Labor Bill do Payroll. Use "Voltar" para retornar.');
    } catch (e) {
      toast.error("Erro ao carregar Labor Bill para edição");
    }
  };

  const returnToPayrollContext = () => {
    if (payrollContext) {
      // Restore payroll context
      setPayrollReport(payrollContext.report);
      setActiveTab(payrollContext.activeTab);
      setAdminSubTab(payrollContext.adminSubTab);
      
      // Clear context
      setPayrollContext(null);
      
      toast.success('✅ Retornado ao relatório de Payroll');
    }
  };

  const downloadPayrollPDF = async (reportId, teamName) => {
    try {
      console.log('🔍 Download PDF Debug:', { reportId, teamName });
      
      if (!reportId || reportId === 'undefined') {
        toast.error('ID do relatório não encontrado. Recarregue a página e tente novamente.');
        return;
      }
      
      const response = await axios.get(`${API}/admin/payroll/${reportId}/generate-pdf/${teamName}`, {
        responseType: 'blob'  // Important for PDF download
      });
      
      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payroll_${teamName}_${reportId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`PDF do relatório ${teamName} baixado com sucesso!`);
    } catch (e) {
      console.error('Error downloading payroll PDF:', e);
      toast.error(getErrMsg(e, 'Erro ao baixar PDF do relatório'));
    }
  };

  // Recalcular Resumo por Equipe baseado nos entries atuais
  const recalculateTeamSummary = () => {
    if (!selectedReport || !selectedReport.entries) {
      toast.error('Nenhum relatório selecionado');
      return;
    }

    try {
      // Agrupar entries por team_name (SÓ found_match)
      const foundEntries = selectedReport.entries.filter(entry => entry.found_match);
      const entriesByTeam = foundEntries.reduce((groups, entry) => {
        const team = entry.team_name || 'Sem Equipe';
        if (!groups[team]) groups[team] = [];
        groups[team].push(entry);
        return groups;
      }, {});

      // Calcular totais por equipe (MESMA LÓGICA da seção "Clientes Encontrados por Equipe")
      const newTeamSummary = Object.keys(entriesByTeam).map(teamName => {
        const teamEntries = entriesByTeam[teamName];
        
        // Calcular totais
        const teamTotals = teamEntries.reduce((totals, entry) => {
          const totalExpenses = entry.expenses ? 
            Object.values(entry.expenses).reduce((sum, val) => sum + (val || 0), 0) : 0;
          return {
            totalExpenses: totals.totalExpenses + totalExpenses,
            totalLabor: totals.totalLabor + (entry.labor_total || 0),
            totalFinal: totals.totalFinal + (entry.calculated_value || 0)
          };
        }, { totalExpenses: 0, totalLabor: 0, totalFinal: 0 });
        
        // Buscar porcentagem configurada
        const configuredTeam = teamPercentages.find(tp => tp.team_name === teamName);
        const percentage = configuredTeam ? configuredTeam.percentage : 20;
        
        // Calcular lucro (lógica 0%)
        const lucro_empresa = percentage === 0 
          ? teamTotals.totalLabor 
          : teamTotals.totalLabor - teamTotals.totalFinal;
        
        return {
          team_name: teamName,
          total_jobs: teamEntries.length,
          total_entries: teamEntries.length,
          total_labor: teamTotals.totalLabor,
          total_expenses: teamTotals.totalExpenses,
          total_final_value: teamTotals.totalFinal,
          total_final: teamTotals.totalFinal,
          lucro_empresa: lucro_empresa
        };
      });

      // Atualizar selectedReport.team_summary
      setSelectedReport({
        ...selectedReport,
        team_summary: newTeamSummary
      });
      
      toast.success('✅ Resumo por Equipe atualizado com base nos valores atuais!');
    } catch (error) {
      console.error('Erro ao recalcular resumo:', error);
      toast.error('Erro ao atualizar resumo por equipe');
    }
  };

  // Salvar relatório atualizado quando fechar o modal
  const handleCloseReportModal = async () => {
    // Se o relatório foi modificado (tem team_summary atualizado), salvar no backend
    if (selectedReport && selectedReport.id) {
      try {
        // Atualizar apenas o team_summary no backend
        await axios.put(`${API}/admin/payroll/${selectedReport.id}/update-summary`, {
          team_summary: selectedReport.team_summary
        });
        
        // Recarregar lista de relatórios para refletir mudanças
        await loadPayrollReports();
        
        // Recarregar dados da Home (Weekly Report Summary e Payment Estimates)
        if (user?.role === 'admin') {
          loadWeeklyReportSummary(false); // false = não mostrar toast de erro
          loadAllPaymentEstimates();
        }
      } catch (error) {
        console.error('Erro ao salvar relatório:', error);
        // Não mostrar erro ao usuário pois é um save silencioso
      }
    }
    
    setSelectedReport(null);
  };

  // Exportar Manual para PDF
  const exportManualToPDF = (manualType) => {
    try {
      // Abrir diálogo de impressão que permite salvar como PDF
      window.print();
      toast.success(language === 'pt-BR' ? 
        'Use "Salvar como PDF" na janela de impressão' : 
        'Use "Save as PDF" in the print dialog'
      );
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error(language === 'pt-BR' ? 
        'Erro ao exportar PDF' : 
        'Error exporting PDF'
      );
    }
  };




  const clearPayrollContext = () => {
    setPayrollContext(null);
  };

  // Helper function to sort teams by PM number
  const sortTeamsByPMNumber = (teams) => {
    return teams.sort((a, b) => {
      const extractPMNumber = (teamName) => {
        const match = teamName.match(/PM(\d+)/i);
        return match ? parseInt(match[1]) : 999; // Non-PM teams go to end
      };
      
      const aNum = extractPMNumber(a.team_name || a);
      const bNum = extractPMNumber(b.team_name || b);
      return aNum - bNum;
    });
  };

  // Payroll Functions
  const processPayrollExcel = async () => {
    if (!payrollFile) {
      toast.error('Selecione um arquivo Excel primeiro');
      return;
    }
    
    if (!payrollWeek || payrollWeek === '') {
      toast.error('Selecione uma Week Ending Date primeiro');
      return;
    }

    // Validate date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(payrollWeek)) {
      toast.error('Formato de data inválido. Use YYYY-MM-DD');
      return;
    }

    console.log('📤 Sending payroll data:', {
      file: payrollFile.name,
      week_ending: payrollWeek,
      date_format: 'YYYY-MM-DD'
    });

    const formData = new FormData();
    formData.append('file', payrollFile);
    formData.append('week_ending', payrollWeek);  // Already in YYYY-MM-DD format from input[type="date"]

    try {
      setPayrollLoading(true);
      toast.info('Processando Excel...');
      const { data } = await axios.post(`${API}/admin/payroll/process-excel`, formData);
      toast.success('Excel processado com sucesso!');
      
      // Usar o campo correto que contém os dados
      setPayrollData(data.preview_data?.processed_entries || []);
      
      // IMPORTANTE: Usar o objeto correto do report que tem week_ending
      const reportData = data.preview_data?.report || {};
      reportData.is_preview = true;
      reportData.team_groups = data.team_groups || {};
      reportData.team_summary = data.team_summary || [];
      // Store preview_data WITHOUT circular reference (don't include report inside itself)
      reportData.preview_data = {
        processed_entries: data.preview_data?.processed_entries || [],
        report: { id: reportData.id, week_ending: reportData.week_ending }
      };
      
      setPayrollReport(reportData);
    } catch (e) {
      toast.error(getErrMsg(e, 'Erro ao processar Excel'));
    } finally {
      setPayrollLoading(false);
    }
  };

  const calculatePayroll = async () => {
    if (!payrollData || payrollData.length === 0) {
      toast.error('Nenhum dado para calcular');
      return;
    }

    try {
      setPayrollLoading(true);
      toast.info('Calculando payroll...');
      const { data } = await axios.post(`${API}/admin/payroll/calculate`, {
        entries: payrollData
      });
      toast.success('Payroll calculado com sucesso!');
      setPayrollReport(data);
    } catch (e) {
      toast.error(getErrMsg(e, 'Erro ao calcular payroll'));
    } finally {
      setPayrollLoading(false);
    }
  };

  const addTeamPercentage = async () => {
    if (!newTeamName || !newTeamPercentage) {
      toast.error('Preencha nome e porcentagem da equipe');
      return;
    }

    try {
      await axios.post(`${API}/admin/team-percentages`, {
        team_name: newTeamName,
        percentage: parseFloat(newTeamPercentage) // Manter 0-100
      });
      toast.success('Equipe adicionada com sucesso!');
      setNewTeamName('');
      setNewTeamPercentage('');
      loadTeamPercentages();
    } catch (e) {
      toast.error(getErrMsg(e, 'Erro ao adicionar equipe'));
    }
  };

  const deleteTeamPercentage = async (teamName) => {
    try {
      await axios.delete(`${API}/admin/team-percentages/${teamName}`);
      toast.success('Equipe removida com sucesso!');
      loadTeamPercentages();
    } catch (e) {
      toast.error(getErrMsg(e, 'Erro ao remover equipe'));
    }
  };

  const updateTeamPercentage = async (teamName, newPercentage) => {
    try {
      await axios.post(`${API}/admin/team-percentages`, {
        team_name: teamName,
        percentage: parseFloat(newPercentage)
      });
      toast.success('Porcentagem atualizada com sucesso!');
      setEditingTeam(null);
      setTempPercentage('');
      await loadTeamPercentages();
      
      // Debug e recálculo automático
      console.log('🔄 Tentando recalcular...', { payrollReport: !!payrollReport, reportId: payrollReport?.id });
      
      if (payrollReport && payrollReport.id) {
        toast.info('⏳ Recalculando valores...');
        await recalculatePayrollWithNewPercentages();
      } else {
        console.log('❌ Não foi possível recalcular - sem report ID');
      }
    } catch (e) {
      toast.error(getErrMsg(e, 'Erro ao atualizar porcentagem'));
    }
  };

  // Função para recalcular payroll com novas porcentagens
  const recalculatePayrollWithNewPercentages = async () => {
    try {
      setPayrollLoading(true);
      console.log('🔄 Chamando endpoint recalculate para report:', payrollReport.id);
      
      // Usar o endpoint correto de recálculo
      const { data } = await axios.post(`${API}/admin/payroll/${payrollReport.id}/recalculate`);
      
      console.log('✅ Recálculo concluído, atualizando report:', data);
      
      // Atualizar o report com novos cálculos
      setPayrollReport(data);
      toast.success('✅ Valores recalculados automaticamente!');
    } catch (e) {
      console.error('❌ Erro ao recalcular:', e);
      toast.error(`Erro ao recalcular: ${getErrMsg(e, 'Falha na API')}`);
    } finally {
      setPayrollLoading(false);
    }
  };

  const generateAllPayrollPDFs = async () => {
    if (!payrollReports || payrollReports.length === 0) {
      toast.error('Nenhum relatório para gerar PDF');
      return;
    }

    try {
      setPayrollLoading(true);
      toast.info('Gerando PDF consolidado...');
      
      const latestReport = payrollReports[0];
      console.log('🔍 Generating consolidated PDF for report:', latestReport.id);
      
      // Use the new consolidated PDF endpoint
      const response = await axios.get(`${API}/admin/payroll/${latestReport.id}/generate-consolidated-pdf`, {
        responseType: 'blob'
      });
      
      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payroll_consolidado_${latestReport.week_ending || 'report'}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('✅ PDF consolidado baixado com sucesso! Contém todas as equipes em um único arquivo.');
    } catch (e) {
      console.error('Error generating consolidated PDF:', e);
      toast.error(getErrMsg(e, 'Erro ao gerar PDF consolidado'));
    } finally {
      setPayrollLoading(false);
    }
  };
  // Team Division Functions
  const openTeamDivisionModal = (workEntry) => {
    setCurrentEntryForDivision(workEntry);
    // 👥 Multi-equipe: pré-seleciona as equipes sugeridas do Labor Bill; senão, a equipe atual
    const suggested = (workEntry.suggested_teams && workEntry.suggested_teams.length > 1)
      ? workEntry.suggested_teams
      : [workEntry.team_name];
    setSelectedTeamsForDivision(suggested);
    loadAvailableTeamsForDivision(suggested);
    setShowTeamDivisionModal(true);
  };

  const loadAvailableTeamsForDivision = async (extraTeams = []) => {
    try {
      const base = (payrollReport && payrollReport.team_summary)
        ? payrollReport.team_summary.map(team => team.team_name)
        : [];
      const merged = Array.from(new Set([...base, ...(extraTeams || [])])).filter(Boolean);
      setAvailableTeams(sortTeamsByPMNumber(merged));
    } catch (e) {
      console.error('Error loading teams for division:', e);
      toast.error('Erro ao carregar equipes disponíveis');
    }
  };

  const handleTeamSelectionForDivision = (teamName) => {
    setSelectedTeamsForDivision(prev => {
      if (prev.includes(teamName)) {
        // Remove team if already selected (but keep at least 1)
        return prev.length > 1 ? prev.filter(t => t !== teamName) : prev;
      } else {
        // Add team to selection
        return [...prev, teamName];
      }
    });
  };

  const confirmTeamDivision = async () => {
    if (!currentEntryForDivision || selectedTeamsForDivision.length < 2) {
      toast.error('Selecione pelo menos 2 equipes para dividir o trabalho');
      return;
    }

    try {
      setTeamDivisionLoading(true);
      
      console.log('🔍 Division Debug:', {
        entryId: currentEntryForDivision.id,
        selectedTeams: selectedTeamsForDivision,
        hasPreviewData: !!payrollReport?.preview_data
      });
      
      const response = await axios.post(`${API}/admin/payroll/entries/${currentEntryForDivision.id}/divide-teams`, {
        teams: selectedTeamsForDivision,
        preview_data: payrollReport?.preview_data ? {
          processed_entries: payrollReport.preview_data.processed_entries,
          report: {
            id: payrollReport.preview_data.report?.id,
            week_ending: payrollReport.preview_data.report?.week_ending
          }
        } : null
      });

      console.log('✅ Division response:', response.data);
      
      toast.success(`Trabalho dividido entre ${selectedTeamsForDivision.length} equipes com sucesso!`);
      setShowTeamDivisionModal(false);

      // Update payroll report with response data
      if (response.data && response.data.team_groups && response.data.team_summary) {
        console.log('🔄 Updating payrollReport with division data');
        console.log('New team_groups:', response.data.team_groups);
        
        setPayrollReport(prev => ({
          ...prev,
          team_groups: response.data.team_groups,
          team_summary: response.data.team_summary,
          preview_data: response.data.updated_preview_data  // Update preview data too
        }));
        
        // Also update payrollData for immediate interface refresh
        if (response.data.team_groups) {
          const updatedEntries = [];
          Object.entries(response.data.team_groups).forEach(([teamName, teamData]) => {
            if (teamData.work_entries) {
              teamData.work_entries.forEach(entry => {
                updatedEntries.push({
                  ...entry,
                  team_name: teamName
                });
              });
            }
          });
          
          if (updatedEntries.length > 0) {
            console.log('🔄 Updating payrollData with divided entries');
            setPayrollData(updatedEntries);
          }
        }
        
        // Force interface refresh
        setTimeout(() => {
          console.log('🔄 Forcing interface refresh after division');
        }, 200);
      } else {
        console.log('⚠️ No team_groups in division response');
      }

      // Close the details modal since the work entry has been modified
      setShowLaborBillDetails(false);

    } catch (e) {
      console.error('Error dividing work between teams:', e);
      toast.error(getErrMsg(e, 'Erro ao dividir trabalho entre equipes'));
    } finally {
      setTeamDivisionLoading(false);
    }
  };

  // View Labor Bill function com suporte admin
  const viewLaborBill = async (billId) => {
    try {
      setLoading(true);
      let bill;
      
      if (user?.role === 'admin') {
        // Para admin, encontrar o bill nos dados já carregados
        bill = laborBills.find(b => b.id === billId);
        if (bill) {
          setViewBillData(bill);
          setViewBillModalOpen(true);
        } else {
          throw new Error('Labor Bill não encontrado');
        }
      } else {
        const { data } = await axios.get(`${API}/labor-bills/${billId}`);
        setViewBillData(data);
        setViewBillModalOpen(true);
      }
    } catch (e) {
      toast.error("Falha ao carregar Labor Bill para visualização");
    } finally {
      setLoading(false);
    }
  };

  // Download PDF function com suporte admin  
  const downloadPDF = async (billId) => {
    try {
      setLoading(true);
      const _b = laborBills.find(b => b.id === billId);
      trackEvent('download_labor_bill_pdf', `Baixou o PDF do Labor Bill${_b?.job_name ? ': ' + _b.job_name : ''}`, { bill_id: billId });
      let res;
      
      if (user?.role === 'admin') {
        // Para admin, precisamos determinar qual PM criou este bill
        const bill = laborBills.find(b => b.id === billId);
        const pmUser = bill?.pm_user || 'PM1'; // fallback para PM1 se não encontrar
        res = await axios.get(`${API}/admin/pm/${pmUser}/labor-bills/${billId}/pdf?t=${Date.now()}`);
      } else {
        res = await axios.get(`${API}/labor-bills/${billId}/pdf?t=${Date.now()}`);
      }
      
      const { pdf_data, filename } = res.data;
      const byteCharacters = window.atob(pdf_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("PDF baixado!");
    } catch (e) {
      toast.error("Falha ao baixar PDF");
    } finally {
      setLoading(false);
    }
  };

  const copyFomLink = async (billId) => {
    try {
      const res = await axios.post(`${API}/labor-bills/${billId}/fom-link`);
      const url = `${window.location.origin}${res.data.path}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success(language === 'pt-BR' ? 'Link do FOM copiado! Envie ao supervisor.' : 'FOM link copied! Send it to the supervisor.');
      } catch (_e) {
        window.prompt(language === 'pt-BR' ? 'Copie o link do FOM:' : 'Copy the FOM link:', url);
      }
    } catch (e) {
      toast.error(language === 'pt-BR' ? 'Falha ao gerar link do FOM' : 'Failed to generate FOM link');
    }
  };


  const viewLaborBillPDF = async (billId) => {
    try {
      setPdfLoading(true);
      const _b = laborBills.find(b => b.id === billId);
      trackEvent('open_labor_bill_pdf', `Abriu o PDF do Labor Bill${_b?.job_name ? ': ' + _b.job_name : ''}`, { bill_id: billId });
      let res;
      
      if (user?.role === 'admin') {
        const bill = laborBills.find(b => b.id === billId);
        const pmUser = bill?.pm_user || 'PM1';
        res = await axios.get(`${API}/admin/pm/${pmUser}/labor-bills/${billId}/pdf?t=${Date.now()}`);
      } else {
        res = await axios.get(`${API}/labor-bills/${billId}/pdf?t=${Date.now()}`);
      }
      
      const { pdf_data, filename } = res.data;
      const byteCharacters = window.atob(pdf_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      
      // Open in modal instead of download
      openPdfWithLoading({ url, title: filename || 'Labor Bill PDF' });
      setPdfLoading(false);
    } catch (e) {
      setPdfLoading(false);
      toast.error("Falha ao carregar PDF");
    }
  };


  const loadSupervisorContext = async (scheduleJobId, orderNumber) => {
    try {
      const params = new URLSearchParams();
      if (scheduleJobId) params.append('schedule_job_id', scheduleJobId);
      if (orderNumber) params.append('order_number', orderNumber);
      const { data } = await axios.get(`${API}/labor-bill/supervisor-context?${params.toString()}`);
      const ctx = data || { detected_supervisor: '', detected: null, supervisors: [] };
      setSupervisorCtx(ctx);
      return ctx;
    } catch (e) {
      console.error('loadSupervisorContext', e);
      const fallback = { detected_supervisor: '', detected: null, supervisors: [] };
      setSupervisorCtx(fallback);
      return fallback;
    }
  };

  // Abre o modal de confirmação de supervisor antes de salvar
  const openSaveSupervisorModal = async () => {
    if (loading) return;
    const jobId = formData.schedule_job_id || currentBill?.schedule_job_id;
    const ordNum = formData.order_number || currentBill?.order_number;
    const ctx = await loadSupervisorContext(jobId, ordNum);
    const detected = ctx?.detected;
    if (detected && detected.supervisor) {
      setSaveSupervisorMode('detected');
      setSaveSupervisorChoice(detected.supervisor);
    } else {
      setSaveSupervisorMode('choose');
      setSaveSupervisorChoice('');
    }
    setShowSaveSupervisorModal(true);
  };

  // Confirma o supervisor escolhido e salva o Labor Bill com FOM Assigned + FOM Email
  const confirmSaveWithSupervisor = async (supervisorName) => {
    let entry = null;
    if (supervisorCtx.detected && supervisorCtx.detected.supervisor === supervisorName) {
      entry = supervisorCtx.detected;
    }
    if (!entry) {
      entry = (supervisorCtx.supervisors || []).find((s) => s.supervisor === supervisorName);
    }
    const supFields = {
      supervisor: supervisorName,
      fom_assigned: entry?.fom_assigned || '',
      fom_email: entry?.fom_email || '',
    };
    setFormData((prev) => ({ ...prev, ...supFields }));
    setShowSaveSupervisorModal(false);
    if (!supFields.fom_email) {
      toast.warning(
        `Supervisor "${supervisorName}" salvo, mas sem e-mail configurado. Configure o FOM Email na sub-aba "📄 Labor Bill".`,
        { duration: 6000 }
      );
    }
    await saveLaborBill(supFields);
  };

  // Fluxo de envio de e-mail: pergunta se envia pro supervisor salvo ou escolhe destinatários
  const openSendEmailFlow = (billArg) => {
    const bill = billArg || currentBill;
    if (!bill?.id) return toast.error('Salve o rascunho primeiro');
    const supEmail = (bill?.fom_email || '').trim();
    const supName = bill?.supervisor || bill?.fom_assigned || '';
    if (supEmail) {
      setEmailSupervisor({ name: supName, email: supEmail });
      setShowEmailSupervisorModal(true);
    } else {
      if (emailRecipients.length === 0) return toast.error('Cadastre destinatários primeiro');
      setSelectedRecipients([]);
      setShowEmailDialog(true);
    }
  };

  const sendToSavedSupervisor = async () => {
    if (!currentBill?.id) return;
    try {
      setLoading(true);
      await axios.post(`${API}/send-email`, {
        labor_bill_id: currentBill.id,
        recipient_ids: [],
        extra_emails: [emailSupervisor.email],
        app_origin: window.location.origin,
      });
      toast.success(`E-mail enviado para ${emailSupervisor.name || emailSupervisor.email}`);
      setShowEmailSupervisorModal(false);
      await loadLaborBills();
      await loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao enviar e-mail');
    } finally {
      setLoading(false);
    }
  };

  const sendLaborBill = async () => {
    if (!currentBill?.id) return toast.error("Salve o rascunho primeiro");
    openSendEmailFlow(currentBill);
  };

  const toggleRecipientSelection = (recipientId) => {
    setSelectedRecipients((prev) => (prev.includes(recipientId) ? prev.filter((id) => id !== recipientId) : [...prev, recipientId]));
  };

  const addEmailRecipient = async () => {
    if (!newRecipient.name || !newRecipient.email) return toast.error("Preencha nome e e-mail");
    try {
      await axios.post(`${API}/email-recipients`, newRecipient);
      setNewRecipient({ name: "", email: "" });
      await loadEmailRecipients();
      toast.success("Destinatário adicionado!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao adicionar destinatário");
    }
  };

  const deleteEmailRecipient = async (recipientId) => {
    try {
      await axios.delete(`${API}/email-recipients/${recipientId}`);
      await loadEmailRecipients();
      toast.success("Destinatário removido!");
    } catch (e) {
      toast.error("Falha ao remover destinatário");
    }
  };

  const toggleRecipientFinance = async (recipientId, value, reload) => {
    try {
      await axios.patch(`${API}/email-recipients/${recipientId}`, { is_finance: value });
      toast.success(value ? "Marcado como Financeiro" : "Financeiro removido");
      if (reload) { await reload(); } else { await loadEmailRecipients(); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao atualizar destinatário");
    }
  };

  const deleteLaborBill = async (billId) => {
    if (!window.confirm("Mover para Lixeira?")) return;
    try {
      setLoading(true);
      
      // Buscar o labor bill para pegar o schedule_job_id
      const bill = laborBills.find(b => b.id === billId);
      const scheduleJobId = bill?.schedule_job_id;
      
      // Use different endpoint based on user role
      if (user?.role === 'admin') {
        // For admin, we need to find which PM owns this bill first
        const pmUser = bill?.pm_user || 'PM1'; // fallback
        await axios.delete(`${API}/admin/pm/${pmUser}/labor-bills/${billId}`);
      } else {
        // For regular users, use the standard endpoint
        await axios.delete(`${API}/labor-bills/${billId}`);
      }
      
      await loadLaborBills();
      await loadHistory();
      await loadTrash();
      
      // Se tinha schedule_job vinculado, recarregar calendário
      if (scheduleJobId) {
        await loadScheduleJobs(selectedDate.getMonth(), selectedDate.getFullYear());
        console.log('🔗 Vinculação removida - calendário recarregado');
      }
      
      toast.success("Movido para Lixeira");
      if (currentBill?.id === billId) {
        setCurrentBill({ id: undefined, items: defaultItems, extra_items: [] });
        setFormData(getDefaultBillData());
        setActiveTab("drafts");
      }
    } catch (e) {
      console.error("Error deleting labor bill:", e);
      console.error("Response:", e.response?.data);
      toast.error(`Falha ao excluir: ${getErrMsg(e, 'Erro desconhecido')}`);
    } finally {
      setLoading(false);
    }
  };

  // Trash
  const restoreBill = async (billId) => {
    try {
      await axios.post(`${API}/trash/${billId}/restore`);
      toast.success("Restaurado");
      await loadLaborBills();
      await loadHistory();
      await loadTrash();
    } catch (e) {
      toast.error("Falha ao restaurar");
    }
  };

  // Confirm dialogs helpers (Trash)
  const openConfirmDelete = (billId) => { setConfirmDeleteBillId(billId); setConfirmDeleteText(''); setConfirmDeleteOpen(true); };
  const confirmHardDelete = async () => {
    if (!confirmDeleteBillId) return;
    try {
      setConfirmDeleting(true);
      await axios.delete(`${API}/trash/${confirmDeleteBillId}`);
      toast.success("Apagado permanentemente");
      await loadTrash();
    } catch (e) {
      toast.error(getErrMsg(e, "Falha ao apagar permanentemente"));
    } finally {
      setConfirmDeleting(false);
      setConfirmDeleteOpen(false);
      setConfirmDeleteBillId(null);
      setConfirmDeleteText('');
    }
  };

  const openAdminConfirmDelete = (pm, id) => { setAdminDeleteTarget({ pm, id }); setAdminConfirmDeleteOpen(true); };
  const confirmAdminHardDelete = async () => {
    if (!adminDeleteTarget?.pm || !adminDeleteTarget?.id) return;
    try {
      await axios.delete(`${API}/admin/pm/${adminDeleteTarget.pm}/trash/${adminDeleteTarget.id}`);
      toast.success("Apagado permanentemente");
      await loadAdminPMData(adminDeleteTarget.pm);
    } catch (e) {
      toast.error(getErrMsg(e, "Falha ao apagar permanentemente"));
    } finally {
      setAdminConfirmDeleteOpen(false);
      setAdminDeleteTarget({ pm: null, id: null });
    }
  };

  const hardDeleteBill = async (billId) => {
    openConfirmDelete(billId);
  };

  // Backup
  const exportBackup = async () => {
    try {
      setBackupLoading(true);
      const res = await axios.get(`${API}/backup/export`);
      const stamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0];
      const fname = `PM-${user?.username || 'PM'}-${stamp}.json`;
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      
      // Usar função universal de download
      await downloadFile(blob, fname, 'application/json');
    } catch (e) {
      toast.error("Falha ao gerar backup");
    } finally {
      setBackupLoading(false);
    }
  };

  // Download backup file with authentication using fetch (more reliable for blobs)
  const downloadBackupFile = async (filename) => {
    try {
      // Get token from sessionStorage (not localStorage) and use correct key
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        toast.error('Sessão expirada. Faça login novamente.');
        return;
      }

      console.log(`🔄 Iniciando download de ${filename}...`);
      
      // Use fetch instead of axios for blob downloads
      const response = await fetch(`${API}/admin/backup/download/${filename}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      console.log(`✅ Download bem-sucedido: ${filename}`);
      
      // Get the blob and use universal download function
      const blob = await response.blob();
      await downloadFile(blob, filename, 'application/json');
      
    } catch (e) {
      console.error(`❌ Erro ao baixar ${filename}:`, e);
      
      if (e.message.includes('401')) {
        toast.error('Sessão expirada. Faça login novamente.');
      } else if (e.message.includes('403')) {
        toast.error('Acesso negado. Apenas administradores podem baixar backups.');
      } else if (e.message.includes('404')) {
        toast.error(`Arquivo ${filename} não encontrado no servidor.`);
      } else {
        toast.error(`Erro ao baixar ${filename}: ${e.message}`);
      }
    }
  };

  const onBackupFileChange = (e) => {
    const file = e.target.files?.[0];
    setBackupFile(file || null);
  };
  // Admin > Sistema helpers
  const loadAdminPricing = async (scope, pm) => {
    try {
      setSettingsLoading(true);
      const { data } = await axios.get(`${API}/admin/settings/pricing`, { params: { scope, pm } });
      setPricingMap(data?.prices || {});
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao carregar preços'));
    } finally {
      setSettingsLoading(false);
    }
  };
  const saveAdminPricing = async () => {
    try {
      setSettingsLoading(true);
      await axios.put(`${API}/admin/settings/pricing`, { prices: pricingMap }, { params: { scope: pricingScope, pm: pricingPM } });
      toast.success('Preços salvos');
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao salvar preços'));
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadCrewMapping = async () => {
    try {
      const [opts, map] = await Promise.all([
        axios.get(`${API}/admin/labor-bill/crew-options`),
        axios.get(`${API}/admin/settings/crew-map`),
      ]);
      setCrewOptions(opts.data?.crew_options || []);
      setCrewPms(opts.data?.pms || []);
      setCrewMap(map.data?.map || {});
    } catch (e) {
      console.error('loadCrewMapping', e);
    }
  };

  const saveCrewMapping = async () => {
    try {
      setCrewLoading(true);
      await axios.put(`${API}/admin/settings/crew-map`, { map: crewMap });
      toast.success('Mapeamento de Crew salvo');
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao salvar mapeamento de Crew'));
    } finally {
      setCrewLoading(false);
    }
  };

  const loadFomMapping = async () => {
    try {
      const [opts, map] = await Promise.all([
        axios.get(`${API}/admin/labor-bill/fom-options`),
        axios.get(`${API}/admin/settings/fom-map`),
      ]);
      setFomOptions(opts.data?.fom_options || []);
      setFomSupervisors(opts.data?.supervisors || []);
      setFomMap(map.data?.map || {});
    } catch (e) {
      console.error('loadFomMapping', e);
    }
  };

  const updateFomEntry = (supervisor, field, value) => {
    setFomMap((prev) => ({
      ...prev,
      [supervisor]: { ...(prev[supervisor] || { fom_assigned: '', fom_email: '' }), [field]: value },
    }));
  };

  const saveFomMapping = async () => {
    try {
      setFomLoading(true);
      await axios.put(`${API}/admin/settings/fom-map`, { map: fomMap });
      toast.success('Mapeamento de FOM salvo');
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao salvar mapeamento de FOM'));
    } finally {
      setFomLoading(false);
    }
  };

  // FOMs personalizados (Nome = FOM Assigned + E-mail)
  const loadCustomFoms = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/labor-bill/custom-foms`);
      setCustomFoms(data?.foms || []);
    } catch (e) {
      console.error('loadCustomFoms', e);
    }
  };

  const addCustomFom = () => setCustomFoms((prev) => [...prev, { name: '', email: '' }]);

  const updateCustomFom = (idx, field, value) =>
    setCustomFoms((prev) => prev.map((f, i) => (i === idx ? { ...f, [field]: value } : f)));

  const removeCustomFom = (idx) => setCustomFoms((prev) => prev.filter((_, i) => i !== idx));

  const saveCustomFoms = async () => {
    try {
      setCustomFomsLoading(true);
      const clean = customFoms
        .map((f) => ({ name: (f.name || '').trim(), email: (f.email || '').trim() }))
        .filter((f) => f.name);
      await axios.put(`${API}/admin/labor-bill/custom-foms`, { foms: clean });
      setCustomFoms(clean);
      toast.success('Novos FOM salvos');
      loadFomMapping();
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao salvar novos FOM'));
    } finally {
      setCustomFomsLoading(false);
    }
  };

  const loadAdminPdfSettings = async (scope, pm) => {
    try {
      setSettingsLoading(true);
      const { data } = await axios.get(`${API}/admin/settings/pdf`, { params: { scope, pm } });
      setPdfSettings({ header_title: data?.header_title || '' });
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao carregar PDF settings'));
    } finally {
      setSettingsLoading(false);
    }
  };
  const saveAdminPdfSettings = async () => {
    try {
      setSettingsLoading(true);
      await axios.put(`${API}/admin/settings/pdf`, pdfSettings, { params: { scope: pricingScope, pm: pricingPM } });
      toast.success('Configurações de PDF salvas');
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao salvar PDF settings'));
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadAdminEmailDefaults = async (scope, pm) => {
    try {
      setSettingsLoading(true);
      console.log('📥 CARREGANDO EMAIL DEFAULTS:');
      console.log('   Scope:', scope, 'PM:', pm);
      
      const { data } = await axios.get(`${API}/admin/settings/email`, { params: { scope, pm } });
      
      console.log('📧 Dados recebidos do backend:');
      console.log('   Assunto:', data?.subject || 'VAZIO');
      console.log('   BCC:', data?.bcc_sender);
      console.log('   body_html:', data?.body_html ? data.body_html.substring(0, 200) + '...' : 'VAZIO');
      console.log('   body_html total:', data?.body_html?.length || 0, 'caracteres');
      
      setEmailDefaults({
        subject: data?.subject || '',
        body: data?.body || '',
        body_html: data?.body_html || '',
        bcc_sender: typeof data?.bcc_sender === 'boolean' ? data.bcc_sender : true,
      });
      
      console.log('✅ Email defaults carregados no estado');
    } catch (e) {
      console.error('❌ Erro ao carregar:', e);
      toast.error(getErrMsg(e, "Erro ao carregar defaults de email"));
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadEmailConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/settings/email-config`);
      setEmailConfig({
        sender_email: data.sender_email || '',
        bcc_email: data.bcc_email || ''
      });
    } catch (e) {
      console.error('Erro ao carregar configurações de email:', e);
      toast.error('Erro ao carregar configurações de email');
    }
  };

  const saveEmailConfig = async () => {
    try {
      setSettingsLoading(true);
      await axios.post(`${API}/admin/settings/email-config`, emailConfig);
      toast.success('✅ Configurações de email salvas com sucesso!');
    } catch (e) {
      console.error('Erro ao salvar configurações:', e);
      toast.error(getErrMsg(e, 'Erro ao salvar configurações de email'));
    } finally {
      setSettingsLoading(false);
    }
  };


  // Support (PM) state & helpers
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportView, setSupportView] = useState(null);
  const [supportReply, setSupportReply] = useState('');
  const [supportFiles, setSupportFiles] = useState([]);
  const [supportNewOpen, setSupportNewOpen] = useState(false);
  const [supportNewTitle, setSupportNewTitle] = useState('');
  const [supportNewText, setSupportNewText] = useState('');
  const [supportNewPriority, setSupportNewPriority] = useState('medium');
  const [supportNewFiles, setSupportNewFiles] = useState([]);

  const loadMyTickets = async () => {
    try {
      console.log('🎫 Loading my tickets for user:', user?.username);
      const { data } = await axios.get(`${API}/tickets`);
      console.log('📊 Tickets loaded:', data?.length || 0, 'tickets');
      console.log('🎫 Tickets details:', data?.map(t => ({ id: t.id, title: t.title, pm_user: t.pm_user })));
      setSupportTickets(data || []);
    } catch (e) {
      console.error('❌ Error loading tickets:', e);
      console.error('❌ Response data:', e.response?.data);
      toast.error(getErrMsg(e, 'Falha ao carregar tickets'));
    }
  };
  const openMyTicket = async (t) => {
    try {
      const { data } = await axios.get(`${API}/tickets/${t.id}`);
      setSupportView(data);
      setSupportReply('');
      setSupportFiles([]);
      // Atualizar stats após visualizar (pode ter marcado como lido)
      await loadUserTicketStats();
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao abrir ticket'));
    }
  };
  const replyMyTicket = async () => {
    if (!supportView?.id) return;
    const form = new FormData();
    form.append('text', supportReply || '');
    (supportFiles || []).forEach((f) => form.append('files', f));
    try {
      await axios.post(`${API}/tickets/${supportView.id}/messages`, form, {
        headers: {
          'Authorization': axios.defaults.headers.common['Authorization'],
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Mensagem enviada');
      await openMyTicket({ id: supportView.id });
      await loadMyTickets();
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao enviar mensagem'));
    }
  };
  const createMyTicket = async () => {
    if (!supportNewTitle.trim()) return toast.error('Informe um título');
    const form = new FormData();
    form.append('title', supportNewTitle);
    form.append('text', supportNewText || '');
    (supportNewFiles || []).forEach((f) => form.append('files', f));
    try {
      await axios.post(`${API}/tickets`, form, {
        headers: {
          'Authorization': axios.defaults.headers.common['Authorization'],
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Ticket aberto');
      setSupportNewOpen(false);
      setSupportNewTitle(''); setSupportNewText(''); setSupportNewFiles([]);
      await loadMyTickets();
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao abrir ticket'));
    }
  };

  useEffect(() => {
    if (activeTab === 'support' && user?.role !== 'admin') {
      loadMyTickets();
    }
  }, [activeTab, user?.role]);

  const saveAdminEmailDefaults = async () => {
    try {
      setSettingsLoading(true);
      
      console.log('💾 SALVANDO EMAIL DEFAULTS:');
      console.log('   Scope: global, PM: PM1');
      console.log('   Assunto:', emailDefaults.subject);
      console.log('   BCC:', emailDefaults.bcc_sender);
      console.log('   body_html (primeiros 200 chars):', emailDefaults.body_html.substring(0, 200));
      console.log('   body_html (total):', emailDefaults.body_html.length, 'caracteres');
      
      await axios.put(`${API}/admin/settings/email`, emailDefaults, { params: { scope: 'global', pm: 'PM1' } });
      
      console.log('✅ Salvamento concluído com sucesso!');
      toast.success('✅ Padrões de e-mail salvos!');
    } catch (e) {
      console.error('❌ Erro ao salvar:', e);
      toast.error(getErrMsg(e, 'Falha ao salvar e-mail defaults'));
    } finally {
      setSettingsLoading(false);
    }
  };

  // Auto load settings when switching scope/pm or when opening Sistema/Labor Bill tab
  useEffect(() => {
    if (user?.role === 'admin' && activeTab === 'admin' && (adminSubTab === 'sistema' || adminSubTab === 'laborbill')) {
      loadAdminPricing(pricingScope, pricingPM);
      loadAdminPdfSettings(pricingScope, pricingPM);
      loadAdminEmailDefaults(pricingScope, pricingPM);
    }
  }, [user?.role, activeTab, adminSubTab, pricingScope, pricingPM]);

  // Carrega o mapeamento de Crew ao abrir a aba Labor Bill
  useEffect(() => {
    if (user?.role === 'admin' && activeTab === 'admin' && adminSubTab === 'laborbill') {
      loadCrewMapping();
      loadFomMapping();
      loadCustomFoms();
      loadEmailRecipients();
    }
  }, [user?.role, activeTab, adminSubTab]);
  
  // Load team percentages when entering payroll tab
  useEffect(() => {
    if (user?.role === 'admin' && activeTab === 'payroll') {
      loadTeamPercentages();
    }
  }, [user?.role, activeTab]);
  
  const importBackup = async () => {
    if (!backupFile) return toast.error("Selecione um arquivo JSON");
    if (importMode === 'replace' && !confirmReplace) return toast.error("Confirme a substituição dos dados");
    try {
      setBackupLoading(true);
      const text = await backupFile.text();
      const data = JSON.parse(text);
      await axios.post(`${API}/backup/import?mode=${importMode}`, data);
      toast.success("Backup importado");
      await loadLaborBills();
      await loadHistory();
      await loadTrash();
      setShowUserDialog(false);
    } catch (e) {
      toast.error("Falha ao importar backup");
    } finally {
      setBackupLoading(false);
    }
  };

  // Open settings (sem dados de admin)
  const handleOpenUserSettings = async () => {
    try {
      setProfileLoading(true);
      const { data } = await axios.get(`${API}/user/profile`);
      setProfile({ username: data.username, first_name: data.first_name || "", last_name: data.last_name || "", phone: data.phone || "", address: data.address || "" });
      setPw({ current: "", next: "" });
      setShowUserDialog(true);
    } catch (e) {
      toast.error("Falha ao carregar perfil");
    } finally {
      setProfileLoading(false);
    }
  };

  // Payroll Helper Functions
  
  // Helper function to format dates from YYYY-MM-DD to MM/DD/YYYY
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      // Handle YYYY-MM-DD or YYYY-MM-DD HH:MM:SS format
      const datePart = dateStr.split(' ')[0];
      const [year, month, day] = datePart.split('-');
      return `${month}/${day}/${year}`;
    } catch {
      return dateStr; // Return original if parsing fails
    }
  };

  // Helper function to format datetime/timestamp to MM/DD/YYYY HH:MM (New York)
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      return formatNyDateTime(dateStr);
    } catch {
      return dateStr;
    }
  };

  // Carregar Labor Bills da equipe específica
  const loadAvailableLaborBillsForChange = async (teamName) => {
    try {
      setChangeLaborBillLoading(true);
      console.log('🔍 Loading available Labor Bills for team:', teamName);
      
      const { data } = await axios.get(`${API}/admin/labor-bills-by-team/${teamName}`);
      setAvailableLaborBillsForChange(data);
      console.log(`✅ Found ${data.length} Labor Bills for team ${teamName}`);
      
    } catch (error) {
      console.error('❌ Error loading Labor Bills for change:', error);
      toast.error('Erro ao carregar Labor Bills para troca');
      setAvailableLaborBillsForChange([]);
    } finally {
      setChangeLaborBillLoading(false);
    }
  };

  const handleChangeLaborBill = async (newLaborBillId) => {
    try {
      setChangeLaborBillLoading(true);
      
      if (!selectedLaborBillDetails?.workEntry?.id) {
        throw new Error('Work Entry não encontrada');
      }
      
      console.log('🔄 Changing Labor Bill from', selectedLaborBillDetails.workEntry.labor_bill_id, 'to', newLaborBillId);
      
      // Serializar preview_data de forma segura para evitar referências circulares
      let safePreviewData = null;
      try {
        if (payrollReport?.preview_data?.processed_entries) {
          console.log('📊 Using PREVIEW data for change');
          
          try {
            // Tentar serialização direta primeiro
            const cleanData = JSON.parse(JSON.stringify(payrollReport.preview_data));
            safePreviewData = cleanData;
            console.log('🔒 Direct serialization successful for change');
          } catch (directError) {
            console.warn('⚠️ Direct serialization failed for change, using manual approach:', directError.message);
            
            // Se falhar, construir manualmente
            safePreviewData = {
              processed_entries: payrollReport.preview_data.processed_entries.map(entry => ({
                id: entry.id,
                job_number: entry.job_number,
                customer: entry.customer,
                labor_total: entry.labor_total,
                team_name: entry.team_name,
                found_match: entry.found_match,
                labor_bill_id: entry.labor_bill_id,
                match_type: entry.match_type,
                calculated_value: entry.calculated_value,
                expenses: entry.expenses || {},
                date: entry.date,
                pay_date: entry.pay_date,
                price_discrepancy: entry.price_discrepancy,
                excel_labor: entry.excel_labor,
                labor_bill_total: entry.labor_bill_total
              })),
              report: {
                week_ending: payrollReport.preview_data.report?.week_ending,
                id: payrollReport.preview_data.report?.id
              }
            };
            console.log('🔒 Manual serialization completed for change');
          }
        }
      } catch (serializationError) {
        console.error('❌ Error serializing preview_data for change:', serializationError);
        safePreviewData = null;
      }
      
      // Chamar endpoint para trocar o Labor Bill
      const response = await axios.post(`${API}/admin/payroll/change-labor-bill`, {
        work_entry_id: selectedLaborBillDetails.workEntry.id,
        new_labor_bill_id: newLaborBillId,
        preview_data: safePreviewData
      });
      
      if (response.data) {
        // Atualizar dados locais
        if (payrollData && Array.isArray(payrollData)) {
          const updatedData = payrollData.map(entry => {
            if (entry.id === selectedLaborBillDetails.workEntry.id) {
              return {
                ...entry,
                labor_bill_id: newLaborBillId,
                calculated_value: response.data.calculated_value || entry.calculated_value
              };
            }
            return entry;
          });
          setPayrollData(updatedData);
        }
        
        // Atualizar payrollReport se necessário
        if (payrollReport?.entries) {
          const updatedEntries = payrollReport.entries.map(entry => {
            if (entry.id === selectedLaborBillDetails.workEntry.id) {
              return {
                ...entry,
                labor_bill_id: newLaborBillId,
                calculated_value: response.data.calculated_value || entry.calculated_value
              };
            }
            return entry;
          });
          
          setPayrollReport(prev => ({
            ...prev,
            entries: updatedEntries,
            team_groups: response.data.team_groups || prev.team_groups
          }));
        }
        
        toast.success(`✅ Labor Bill trocado com sucesso!`);
        setShowChangeLaborBillModal(false);
        setShowLaborBillDetails(false);
        
      }
      
    } catch (error) {
      console.error('❌ Error changing Labor Bill:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Erro ao trocar Labor Bill';
      toast.error(`Erro: ${errorMessage}`);
    } finally {
      setChangeLaborBillLoading(false);
    }
  };

  const loadTeamLaborBills = async (teamName) => {
    try {
      console.log(`🔍 Loading Labor Bills for team: ${teamName}`);
      
      // Carregar Labor Bills filtrados pela equipe
      const { data } = await axios.get(`${API}/admin/team-labor-bills/${teamName}`);
      setTeamLaborBills(data);
      
      console.log(`✅ Found ${data.length} Labor Bills for team ${teamName}`);
    } catch (e) {
      console.error('❌ Error loading team labor bills:', e);
      toast.error(`Erro ao carregar Labor Bills da equipe ${teamName}`);
      setTeamLaborBills([]);
    }
  };

  // Load Weekly Report data for PM user
  const loadPMWeeklyReport = async () => {
    try {
      if (!user?.username) return;
      
      setWeeklyReportLoading(true);
      console.log('📊 Loading Weekly Report for PM:', user.username);
      
      // Buscar relatórios de payroll específicos da equipe do PM
      const response = await axios.get(`${API}/pm/weekly-report/${user.username}`);
      
      if (response.data) {
        // Agrupar entries por semana
        const weeklyGroups = {};
        
        response.data.entries.forEach(entry => {
          const weekEnding = entry.week_ending;
          if (!weeklyGroups[weekEnding]) {
            weeklyGroups[weekEnding] = {
              week_ending: weekEnding,
              total_jobs: 0,
              total_labor: 0,
              total_expenses: 0,
              total_final: 0,
              entries: []
            };
          }
          
          weeklyGroups[weekEnding].entries.push(entry);
          weeklyGroups[weekEnding].total_jobs += 1;
          weeklyGroups[weekEnding].total_labor += entry.labor_total || 0;
          weeklyGroups[weekEnding].total_expenses += entry.expenses_total || 0;
          weeklyGroups[weekEnding].total_final += entry.final_value || 0;
        });
        
        // Converter para array e ordenar por data (mais recente primeiro)
        const weeklyData = Object.values(weeklyGroups).sort((a, b) => 
          new Date(b.week_ending) - new Date(a.week_ending)
        );
        
        // Adicionar backcharges e adicionais a cada semana
        const weeklyBackcharges = response.data.weekly_backcharges || {};
        const weeklyAdicionais = response.data.weekly_adicionais || {};
        weeklyData.forEach(week => {
          const bcs = weeklyBackcharges[week.week_ending] || [];
          week.backcharges = bcs;
          week.backcharges_total = bcs.reduce((s, b) => s + (b.valor || 0), 0);
          const ads = weeklyAdicionais[week.week_ending] || [];
          week.adicionais = ads;
          week.adicionais_total = ads.reduce((s, a) => s + (a.valor || 0), 0);
          week.total_final = week.total_final - week.backcharges_total + week.adicionais_total;
        });
        
        setWeeklyReportData({
          ...response.data,
          weeklyData: weeklyData
        });
        
        console.log('✅ Weekly Report loaded and grouped by weeks for PM:', user.username);
        console.log('📊 Found', weeklyData.length, 'weeks with data');
      } else {
        console.warn('⚠️ No weekly report data found for PM:', user.username);
        setWeeklyReportData({
          weeklyData: []
        });
      }
      
    } catch (error) {
      console.error('❌ Error loading PM Weekly Report:', error);
      // Se não há endpoint específico, vamos mostrar dados vazios por enquanto
      setWeeklyReportData({
        weeklyData: []
      });
    } finally {
      setWeeklyReportLoading(false);
    }
  };

  // Função para fazer match manual simples - com useCallback
  const handleManualMatch = useCallback(async (laborBillId) => {
    if (!currentUnmatchedEntry) return;
    
    try {
      setManualMatchLoading(true);
      
      console.log('🔗 Creating manual match:', {
        workEntryId: currentUnmatchedEntry.id,
        laborBillId: laborBillId,
        teamName: currentUnmatchedEntry.team_name
      });
      
      console.log('🚀 Starting API call...');
      
      // Serializar preview_data de forma segura para evitar referências circulares
      let safePreviewData = null;
      try {
        // Primeiro, verificar se temos dados em preview
        if (payrollReport?.preview_data?.processed_entries) {
          console.log('📊 Using PREVIEW data with', payrollReport.preview_data.processed_entries.length, 'entries');
          
          try {
            // Tentar serialização direta primeiro
            const cleanData = JSON.parse(JSON.stringify(payrollReport.preview_data));
            safePreviewData = cleanData;
            console.log('🔒 Direct serialization successful');
          } catch (directError) {
            console.warn('⚠️ Direct serialization failed, using manual approach:', directError.message);
            
            // Se falhar, construir manualmente
            safePreviewData = {
              processed_entries: payrollReport.preview_data.processed_entries.map(entry => ({
                id: entry.id,
                job_number: entry.job_number,
                customer: entry.customer,
                labor_total: entry.labor_total,
                team_name: entry.team_name,
                found_match: entry.found_match,
                labor_bill_id: entry.labor_bill_id,
                match_type: entry.match_type,
                calculated_value: entry.calculated_value,
                expenses: entry.expenses || {},
                date: entry.date,
                pay_date: entry.pay_date, // Campo obrigatório para WeeklyWorkEntry
                price_discrepancy: entry.price_discrepancy,
                excel_labor: entry.excel_labor,
                labor_bill_total: entry.labor_bill_total
              })),
              report: {
                week_ending: payrollReport.preview_data.report?.week_ending,
                id: payrollReport.preview_data.report?.id
              }
            };
            console.log('🔒 Manual serialization completed');
          }
        } 
        // Se não temos preview_data, verificar se temos dados de relatório salvo
        else if (payrollReport?.entries) {
          console.log('📊 Using SAVED REPORT data with', payrollReport.entries.length, 'entries');
          
          // Criar estrutura de preview_data a partir dos dados salvos
          safePreviewData = {
            processed_entries: payrollReport.entries.map(entry => ({
              id: entry.id,
              job_number: entry.job_number,
              customer: entry.customer,
              labor_total: entry.labor_total,
              team_name: entry.team_name,
              found_match: entry.found_match,
              labor_bill_id: entry.labor_bill_id,
              match_type: entry.match_type,
              calculated_value: entry.calculated_value,
              expenses: entry.expenses || {},
              date: entry.date,
              pay_date: entry.pay_date, // Campo obrigatório para WeeklyWorkEntry
              price_discrepancy: entry.price_discrepancy,
              excel_labor: entry.excel_labor,
              labor_bill_total: entry.labor_bill_total
            })),
            report: {
              week_ending: payrollReport.week_ending,
              id: payrollReport.id
            }
          };
          console.log('🔒 Created preview_data from saved report');
        }
        else {
          console.warn('⚠️ No data found in payrollReport');
          console.log('📊 Available payrollReport keys:', Object.keys(payrollReport || {}));
        }
      } catch (serializationError) {
        console.error('❌ Error serializing preview_data:', serializationError);
        safePreviewData = null;
      }
      
      // Criar payload de forma explícita para evitar problemas
      const requestPayload = {
        work_entry_id: String(currentUnmatchedEntry.id),
        labor_bill_id: String(laborBillId),
        preview_data: safePreviewData
      };
      
      console.log('📡 Sending request with payload keys:', Object.keys(requestPayload));
      if (safePreviewData?.processed_entries) {
        console.log('📡 Preview data entries count:', safePreviewData.processed_entries.length);
        console.log('📡 Preview data valid: true');
      } else {
        console.log('📡 Preview data structure:', safePreviewData);
        console.log('📡 Preview data valid: false');
      }
      
      // Verificar se temos dados válidos antes de enviar
      if (!safePreviewData || !safePreviewData.processed_entries || !Array.isArray(safePreviewData.processed_entries) || safePreviewData.processed_entries.length === 0) {
        console.error('❌ Validation failed:', {
          hasPreviewData: !!safePreviewData,
          hasProcessedEntries: !!safePreviewData?.processed_entries,
          isArray: Array.isArray(safePreviewData?.processed_entries),
          length: safePreviewData?.processed_entries?.length
        });
        throw new Error('Dados de preview inválidos ou vazios. Reprocesse o Excel antes de fazer manual match.');
      }
      
      // Chamar o endpoint para criar o match manual
      const response = await axios.post(`${API}/admin/payroll/create-manual-match`, requestPayload);
      
      console.log('📦 API response received, processing...');
      
      if (response.data) {
        console.log('✅ Manual match response received');
        console.log('🔍 Response data keys:', Object.keys(response.data));
        console.log('🔍 RESPONSE COMPLETA:', JSON.stringify(response.data, null, 2));
        console.log('🔍 Updated entry:', response.data.updated_entry);
        console.log('🔍 Calculated value:', response.data.calculated_value);
        
        // Verificar se response.data tem estrutura válida
        if (!response.data.team_groups || typeof response.data.team_groups !== 'object') {
          console.error('❌ Invalid response structure:', response.data);
          throw new Error('Response inválida do servidor');
        }
        
        console.log('🔄 Starting state updates...');
        
        // Atualizar apenas o necessário
        setPayrollReport(prev => {
          console.log('📝 Updating payrollReport state...');
          return {
            ...prev,
            team_groups: response.data.team_groups,
            team_summary: response.data.team_summary,
            preview_data: response.data.updated_preview_data
          };
        });
        
        console.log('🔄 Starting payrollData conversion...');
        
        // Converter team_groups para payrollData de forma segura - evitar referências circulares
        const updatedEntries = [];
        try {
          Object.entries(response.data.team_groups).forEach(([teamName, teamData]) => {
            if (teamData?.work_entries && Array.isArray(teamData.work_entries)) {
              teamData.work_entries.forEach(entry => {
                if (entry && typeof entry === 'object') {
                  // Construir objeto explicitamente para evitar referências circulares
                  const safeEntry = {
                    id: entry.id,
                    job_number: entry.job_number,
                    customer: entry.customer,
                    labor_total: Number(entry.labor_total) || 0,
                    team_name: teamName,
                    found_match: Boolean(entry.found_match),
                    labor_bill_id: entry.labor_bill_id || null,
                    match_type: entry.match_type || null,
                    calculated_value: Number(entry.calculated_value) || 0,
                    expenses: entry.expenses && typeof entry.expenses === 'object' ? entry.expenses : {},
                    price_discrepancy: Boolean(entry.price_discrepancy),
                    excel_labor: Number(entry.excel_labor) || 0,
                    labor_bill_total: Number(entry.labor_bill_total) || 0
                  };
                  updatedEntries.push(safeEntry);
                }
              });
            }
          });
          
          if (updatedEntries.length > 0) {
            console.log('🔄 Updating payrollData state...');
            console.log('🔍 Sample updated entry:', updatedEntries.find(e => e.id === currentUnmatchedEntry?.id));
            setPayrollData(updatedEntries);
            console.log('✅ PayrollData updated successfully');
          }
        } catch (conversionError) {
          console.error('❌ Error converting team_groups:', conversionError);
          throw new Error('Erro ao processar dados do servidor');
        }
        
        console.log('🎉 Finalizing manual match...');
        
        toast.success(`✅ Match criado! Cliente "${currentUnmatchedEntry.customer}" associado com sucesso.`);
        setShowManualMatchModal(false);
        setCurrentUnmatchedEntry(null);
        setTeamLaborBills([]);
        
        console.log('✅ Manual match completed successfully');
      }
      
    } catch (e) {
      console.error('❌ Error creating manual match:', e);
      
      // Log específico para diferentes tipos de erro
      if (e.name === 'RangeError' && e.message.includes('Maximum call stack size exceeded')) {
        console.error('🚨 CALL STACK ERROR: Still occurring during axios request');
        toast.error('Erro de recursão infinita - contate o suporte técnico');
      } else if (e.code === 'ERR_NETWORK') {
        console.error('🌐 NETWORK ERROR:', e.message);
        toast.error('Erro de rede - verifique sua conexão');
      } else if (e.response) {
        console.error('🔥 SERVER ERROR:', e.response.status, e.response.data);
        const errorMessage = e.response.data?.detail || e.response.data?.message || 'Erro do servidor';
        toast.error(`Erro do servidor: ${errorMessage}`);
      } else {
        console.error('❓ UNKNOWN ERROR:', e.name, e.message);
        toast.error(`Erro desconhecido: ${e.message}`);
      }
    } finally {
      setManualMatchLoading(false);
    }
  }, [currentUnmatchedEntry]);

  const handleWorkEntryClick = async (workEntry) => {
    try {
      console.log('🔍 Work Entry clicked:', workEntry);
      console.log('🔍 found_match:', workEntry.found_match, 'labor_bill_id:', workEntry.labor_bill_id);
      
      if (workEntry.found_match && workEntry.labor_bill_id) {
        console.log('✅ Entry has match - opening Labor Bill details');
        // Para entries ENCONTRADAS: mostrar detalhes do Labor Bill
        const localBill = laborBills.find(bill => bill.id === workEntry.labor_bill_id);
        
        if (localBill) {
          console.log('✅ Found Labor Bill locally:', localBill);
          setSelectedLaborBillDetails({
            workEntry: workEntry,
            laborBill: localBill
          });
          setShowLaborBillDetails(true);
        } else {
          // Se não encontrar localmente, tentar API
          console.log('🔍 Labor Bill not found locally, trying API...');
          toast.info('Carregando detalhes do Labor Bill...');
          
          try {
            console.log('🔍 Fetching labor bill details for ID:', workEntry.labor_bill_id);
            const { data } = await axios.get(`${API}/labor-bills/${workEntry.labor_bill_id}`);
            setSelectedLaborBillDetails({
              workEntry: workEntry,
              laborBill: data
            });
            setShowLaborBillDetails(true);
          } catch (apiError) {
            console.error('❌ API Error fetching labor bill:', {
              labor_bill_id: workEntry.labor_bill_id,
              customer: workEntry.customer,
              error: apiError.response?.data || apiError.message
            });
            
            // Automaticamente resetar o match órfão
            try {
              console.log('🔧 Auto-fixing orphaned match for:', workEntry.customer);
              
              // Encontrar este entry nos dados atuais e resetar o match
              let wasFixed = false;
              
              // Se em modo preview
              if (payrollData && Array.isArray(payrollData)) {
                const updatedData = payrollData.map(entry => {
                  if (entry.id === workEntry.id) {
                    console.log('🔧 Resetting orphaned match in preview data');
                    wasFixed = true;
                    return {
                      ...entry,
                      found_match: false,
                      labor_bill_id: null,
                      match_type: null,
                      calculated_value: 0
                    };
                  }
                  return entry;
                });
                setPayrollData(updatedData);
              }
              
              // Se em relatório salvo, atualizar payrollReport
              if (payrollReport?.entries && Array.isArray(payrollReport.entries)) {
                const updatedEntries = payrollReport.entries.map(entry => {
                  if (entry.id === workEntry.id) {
                    console.log('🔧 Resetting orphaned match in report data');
                    wasFixed = true;
                    return {
                      ...entry,
                      found_match: false,
                      labor_bill_id: null,
                      match_type: null,
                      calculated_value: 0
                    };
                  }
                  return entry;
                });
                
                setPayrollReport(prev => ({
                  ...prev,
                  entries: updatedEntries
                }));
              }
              
              if (wasFixed) {
                toast.success(
                  `✅ Match órfão removido para "${workEntry.customer}". ` +
                  `Agora você pode fazer uma nova correspondência manual.`,
                  { duration: 6000 }
                );
              } else {
                toast.error(
                  `Labor Bill não encontrado para "${workEntry.customer}". ` +
                  `ID: ${workEntry.labor_bill_id.substring(0, 8)}... ` +
                  `Este Labor Bill foi deletado ou não pertence a você.`,
                  { duration: 6000 }
                );
              }
              
            } catch (resetError) {
              console.error('❌ Error auto-fixing orphaned match:', resetError);
              toast.error(
                `Labor Bill não encontrado para "${workEntry.customer}". ` +
                `ID: ${workEntry.labor_bill_id.substring(0, 8)}... ` +
                `Este Labor Bill foi deletado ou não pertence a você.`,
                { duration: 6000 }
              );
            }
          }
        }
        
      } else {
        // Para entries NÃO ENCONTRADAS: abrir modal de manual match
        console.log('❌ Entry is orphaned - opening manual match modal');
        console.log('🔧 Opening manual match modal for:', workEntry);
        setCurrentUnmatchedEntry(workEntry);
        await loadTeamLaborBills(workEntry.team_name);
        setShowManualMatchModal(true);
      }
    } catch (e) {
      console.error('❌ Error handling work entry click:', e);
      toast.error(`Erro: ${getErrMsg(e, 'Falha ao processar work entry')}`);
    }
  };

  const updateWorkEntryExpenses = async (entryId, newExpenses) => {
    try {
      // Para mode preview, não podemos atualizar expenses diretamente via API
      // porque os dados estão em memória, não salvos no banco
      if (payrollReport && payrollReport.is_preview) {
        console.log('🔄 Atualizando expenses no modo preview (local)');
        
        // Atualizar localmente no payrollData
        const updatedPayrollData = payrollData.map(entry => {
          if (entry.id === entryId) {
            // Recalcular valor final com novas expenses
            const totalExpenses = Object.values(newExpenses).reduce((sum, val) => sum + val, 0);
            const laborValue = entry.labor_total || 0;
            
            // Usar a mesma lógica de cálculo do backend
            // Assumindo 20% de desconto padrão se não tiver team configurada
            const percentage = 20.0; // Pode ser melhorado buscando da configuração real
            const percentageMultiplier = (100.0 - percentage) / 100.0;
            const calculated_value = (laborValue - totalExpenses) * percentageMultiplier + totalExpenses;
            
            return {
              ...entry,
              expenses: newExpenses,
              calculated_value: calculated_value
            };
          }
          return entry;
        });
        
        setPayrollData(updatedPayrollData);
        
        // Atualizar também o modal se estiver aberto
        if (selectedLaborBillDetails && selectedLaborBillDetails.workEntry.id === entryId) {
          const updatedEntry = updatedPayrollData.find(e => e.id === entryId);
          if (updatedEntry) {
            setSelectedLaborBillDetails(prev => ({
              ...prev,
              workEntry: {
                ...prev.workEntry,
                expenses: newExpenses,
                calculated_value: updatedEntry.calculated_value
              }
            }));
          }
        }
        
        toast.success('Despesas atualizadas com sucesso! (modo preview)');
        return;
      }
      
      // Para dados salvos, usar o endpoint normal
      if (!payrollReport?.id) {
        toast.error('Report ID não encontrado. Não é possível atualizar despesas.');
        return;
      }
      
      const response = await axios.post(`${API}/admin/payroll/${payrollReport.id}/update-expenses`, {
        work_entry_id: entryId,
        expenses: newExpenses
      });
      
      toast.success('Despesas atualizadas com sucesso!');
      
      // Use response data if it has the correct structure
      if (response.data && response.data.team_groups && response.data.team_summary) {
        console.log('✅ Using response data from expense update');
        setPayrollReport(prev => ({
          ...prev, // Keep all existing data
          team_groups: response.data.team_groups,
          team_summary: response.data.team_summary
        }));
        
        // CORREÇÃO: Atualizar também selectedReport se estiver aberto
        if (selectedReport && selectedReport.id === payrollReport.id) {
          setSelectedReport(prev => {
            // Atualizar o entry específico nos entries
            const updatedEntries = prev.entries.map(entry => {
              if (entry.id === entryId) {
                return {
                  ...entry,
                  expenses: newExpenses,
                  calculated_value: response.data.updated_calculated_value || entry.calculated_value
                };
              }
              return entry;
            });
            
            return {
              ...prev,
              entries: updatedEntries,
              team_summary: response.data.team_summary,
              team_groups: response.data.team_groups
            };
          });
        }
        
        // Update the selected work entry details if modal is open
        if (selectedLaborBillDetails && selectedLaborBillDetails.workEntry.id === entryId) {
          setSelectedLaborBillDetails(prev => ({
            ...prev,
            workEntry: {
              ...prev.workEntry,
              expenses: newExpenses
            }
          }));
        }
        
        return; // Exit early with success
      }
      
      // If response data is incomplete, don't change anything
      console.log('⚠️ Response data incomplete, preserving current interface');
      
      // Still update the modal if it's open
      if (selectedLaborBillDetails && selectedLaborBillDetails.workEntry.id === entryId) {
        setSelectedLaborBillDetails(prev => ({
          ...prev,
          workEntry: {
            ...prev.workEntry,
            expenses: newExpenses
          }
        }));
      }
      
      toast.info('✅ Despesas salvas! A interface foi preservada.');
      
    } catch (e) {
      console.error('❌ Error updating expenses:', e);
      toast.error(getErrMsg(e, 'Erro ao atualizar despesas'));
    }
  };

  // Save Payroll to Database Function
  const savePayrollToDatabase = async (forceAction = null) => {
    if (!payrollReport || !payrollReport.is_preview) {
      toast.error('Nenhum dado de preview para salvar ou dados já foram salvos');
      return;
    }

    try {
      setSaveLoading(true);
      
      console.log('💾 Salvando dados de payroll no banco de dados...');
      
      // Create a clean copy of processed entries to avoid circular references
      const cleanProcessedEntries = payrollReport.preview_data?.processed_entries?.map(entry => {
        // Create a deep clean copy of the entry to avoid circular references
        const cleanEntry = {};
        
        // Copy all primitive properties
        for (const [key, value] of Object.entries(entry || {})) {
          if (value !== null && value !== undefined) {
            if (typeof value === 'object' && !Array.isArray(value)) {
              // For objects like expenses, create a clean copy
              cleanEntry[key] = { ...value };
            } else {
              // For primitives and arrays, copy directly
              cleanEntry[key] = value;
            }
          }
        }
        
        return cleanEntry;
      }) || [];
      
      // Clean team_summary and team_groups to avoid circular references
      const cleanTeamSummary = payrollReport.team_summary ? 
        JSON.parse(JSON.stringify(payrollReport.team_summary, (key, value) => {
          // Remove circular references and complex objects
          if (typeof value === 'object' && value !== null) {
            if (value._id || value.$oid) return undefined; // Remove MongoDB ObjectIds
            if (typeof value === 'function') return undefined; // Remove functions
          }
          return value;
        })) : {};
        
      const cleanTeamGroups = payrollReport.team_groups ? 
        JSON.parse(JSON.stringify(payrollReport.team_groups, (key, value) => {
          // Remove circular references and complex objects
          if (typeof value === 'object' && value !== null) {
            if (value._id || value.$oid) return undefined; // Remove MongoDB ObjectIds
            if (typeof value === 'function') return undefined; // Remove functions
          }
          return value;
        })) : {};
      
      // Use the updated endpoint with conflict detection
      const requestData = {
        report: {
          id: payrollReport.id,
          week_ending: payrollReport.week_ending,
          created_by: user.username,
          created_at: new Date().toISOString(),
          // Include all other report fields (cleaned)
          total_entries: payrollReport.total_entries,
          matched_entries: payrollReport.matched_entries,
          team_summary: cleanTeamSummary,
          team_groups: cleanTeamGroups,
          is_preview: false,
          status: 'completed'
        },
        processed_entries: cleanProcessedEntries
      };
      
      // Add force_action if specified
      if (forceAction) {
        requestData.force_action = forceAction;
      }
      
      console.log('📤 Sending request data:', {
        report_fields: Object.keys(requestData.report),
        entries_count: cleanProcessedEntries.length,
        first_entry_fields: cleanProcessedEntries[0] ? Object.keys(cleanProcessedEntries[0]) : [],
        force_action: forceAction
      });
      
      const response = await axios.post(`${API}/admin/payroll/save-to-database`, requestData);
      
      // Handle conflict response
      if (response.data.status === 'conflict') {
        setConflictData(response.data);
        setConflictModalOpen(true);
        return;
      }
      
      if (response.data.status === 'success') {
        // Atualizar o payrollReport para indicar que foi salvo
        setPayrollReport(prev => ({
          ...prev,
          is_preview: false,
          status: 'completed'
        }));
        
        const actionMessage = forceAction 
          ? `✅ Dados salvos com sucesso! (${forceAction === 'overwrite' ? 'Sobrescrever' : 'Adicionar mudanças'})`
          : '✅ Dados salvos no banco de dados com sucesso!';
        
        toast.success(actionMessage);
        
        // Recarregar lista de reports e summary
        if (user?.username === 'ADMIN') {
          loadPayrollReports();
          loadWeeklyReportSummary();
        }
      }
      
    } catch (e) {
      console.error('❌ Error saving payroll to database:', e);
      toast.error(getErrMsg(e, 'Erro ao salvar no banco de dados'));
    } finally {
      setSaveLoading(false);
    }
  };

  // Load Weekly Report Summary from database
  const loadWeeklyReportSummary = async (showErrorToast = true, forceMonth = null) => {
    try {
      setWeeklyReportLoading(true);
      const monthToUse = forceMonth || selectedMonth;
      console.log('🔄 Loading weekly report summary from database...');
      console.log('👤 Current user:', user?.username, 'Role:', user?.role);
      console.log('📅 Mês a ser usado:', monthToUse, '(forceMonth:', forceMonth, ', selectedMonth:', selectedMonth, ')');
      
      // Verificar se o usuário está disponível
      if (!user || !user.username) {
        console.warn('⚠️ User not available yet, skipping load');
        setWeeklyReportLoading(false);
        return;
      }
      
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        if (showErrorToast) {
          toast.error('Token não encontrado. Faça login novamente.');
        }
        return;
      }
      
      // Use different endpoints for admin vs PM users
      if (user?.role === 'admin') {
        console.log(`👑 Admin user - filtrando por mês: ${monthToUse}`);
        
        // Usar endpoint da variável de resumos ao invés de calcular
        const { data: allWeeklyData } = await axios.get(`${API}/admin/weekly-summaries`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log(`📊 Dados recebidos: ${allWeeklyData?.length || 0} semanas totais`);
        
        if (!allWeeklyData || allWeeklyData.length === 0) {
          setWeeklyReportSummary({
            weeklyData: [],
            reportsCount: 0,
            availableMonths: []
          });
          return;
        }
        
        // ====== LÓGICA SIMPLES: Extrair mês de cada week_ending ======
        const monthsSet = new Set();
        let latestWeekEnding = null; // Para encontrar a última semana processada
        
        allWeeklyData.forEach(week => {
          if (week.week_ending) {
            // Extrair apenas YYYY-MM do week_ending
            const monthKey = week.week_ending.substring(0, 7); // '2025-10-03' → '2025-10'
            monthsSet.add(monthKey);
            
            // Encontrar a última semana (week_ending mais recente)
            if (!latestWeekEnding || week.week_ending > latestWeekEnding) {
              latestWeekEnding = week.week_ending;
            }
          }
        });
        
        const availableMonths = Array.from(monthsSet).sort().reverse(); // Mais recente primeiro
        console.log(`📅 Meses disponíveis:`, availableMonths);
        console.log(`📅 Última semana processada:`, latestWeekEnding);
        
        // Definir mês da última semana como padrão
        let actualSelectedMonth = monthToUse;
        if (!monthToUse && latestWeekEnding) {
          // Usar o mês da última semana processada (não alfabético)
          const monthOfLatestWeek = latestWeekEnding.substring(0, 7);
          actualSelectedMonth = monthOfLatestWeek;
          setSelectedMonth(monthOfLatestWeek);
          console.log(`📅 Mês inicial definido (da última semana): ${monthOfLatestWeek}`);
        } else if (!monthToUse && availableMonths.length > 0) {
          // Fallback caso não tenha week_ending
          actualSelectedMonth = availableMonths[0];
          setSelectedMonth(availableMonths[0]);
          console.log(`📅 Mês inicial definido (fallback): ${availableMonths[0]}`);
        }
        
        // ====== FILTRAR SIMPLES: Checar se week_ending começa com o mês selecionado ======
        const filteredWeeklyData = allWeeklyData
          .filter(week => {
            if (!week.week_ending) return false;
            const weekMonth = week.week_ending.substring(0, 7); // Extrair YYYY-MM
            // Filtrar por mês se especificado
            return weekMonth === actualSelectedMonth;
          })
          .map(week => ({
            // Converter campos snake_case para camelCase para compatibilidade com frontend
            weekEnding: week.week_ending,
            total_jobs: week.total_jobs,
            total_labor: week.total_labor,
            total_final: week.total_final,
            total_expenses: week.total_expenses,
            lucro: week.lucro
          }));
        
        console.log(`👑 Admin - Mostrando ${filteredWeeklyData.length} semanas do mês ${actualSelectedMonth}`);
        console.log(`📊 Dados filtrados:`, filteredWeeklyData.map(w => w.weekEnding));
        
        setWeeklyReportSummary({
          weeklyData: filteredWeeklyData,
          reportsCount: filteredWeeklyData.length,
          availableMonths
        });
        
      } else if (user?.role === 'daily_leader') {
        console.log('📊 Daily leader - loading weekly reports');
        const { data: dlReports } = await axios.get(`${API}/daily-leader/weekly-reports`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!dlReports || dlReports.length === 0) {
          setWeeklyReportSummary({ weeklyData: [], reportsCount: 0, availableMonths: [] });
          return;
        }
        
        const monthsSet = new Set();
        dlReports.forEach(r => {
          if (r.data_fim) monthsSet.add(r.data_fim.substring(0, 7));
        });
        const availableMonths = Array.from(monthsSet).sort().reverse();
        
        let actualMonth = monthToUse || availableMonths[0] || '';
        if (!monthToUse && availableMonths.length > 0) {
          setSelectedMonth(availableMonths[0]);
        }
        
        const filtered = dlReports.filter(r => r.data_fim && r.data_fim.substring(0, 7) === actualMonth);
        
        setWeeklyReportSummary({
          weeklyData: filtered.map(r => ({
            weekEnding: r.data_fim,
            semana: r.semana,
            ano: r.ano,
            data_inicio: r.data_inicio,
            data_fim: r.data_fim,
            dias_trabalhados: r.dias_trabalhados,
            valor_diaria: r.valor_diaria,
            valor_trabalho: r.valor_trabalho,
            valor_despesas: r.valor_despesas,
            helpers: r.helpers,
            helpers_avulsos: r.helpers_avulsos,
            helpers_total: r.helpers_total,
            valor_total: r.valor_total,
            isDailyLeader: true
          })),
          reportsCount: filtered.length,
          availableMonths
        });
        
      } else {
        console.log(`📅 PM - Mês a filtrar: ${monthToUse}`);
        
        // Use the PM-specific endpoint that returns already filtered data
        const { data: pmReportData } = await axios.get(`${API}/pm/weekly-report/${user.username}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!pmReportData || !pmReportData.entries || pmReportData.entries.length === 0) {
          console.log('⚠️ No data found for PM:', user.username);
          setWeeklyReportSummary({
            weeklyData: [],
            reportsCount: 0,
            availableMonths: []
          });
          return;
        }
        
        // Group entries by week_ending + detectar meses disponíveis
        const weeklyGroups = {};
        const monthsSet = new Set();
        let latestWeekEnding = null;
        
        pmReportData.entries.forEach(entry => {
          const weekEnding = entry.week_ending;
          
          // Detectar meses disponíveis (LÓGICA SIMPLES)
          if (weekEnding) {
            const monthKey = weekEnding.substring(0, 7); // '2025-10-03' → '2025-10'
            monthsSet.add(monthKey);
            
            // Encontrar última semana
            if (!latestWeekEnding || weekEnding > latestWeekEnding) {
              latestWeekEnding = weekEnding;
            }
          }
          
          if (!weeklyGroups[weekEnding]) {
            weeklyGroups[weekEnding] = {
              total_jobs: 0,
              total_final: 0,
              total_expenses: 0,
              weekEnding: weekEnding
            };
          }
          
          // Only count matched/found entries
          if (entry.found_match) {
            weeklyGroups[weekEnding].total_jobs += 1;
            weeklyGroups[weekEnding].total_final += entry.final_value || entry.labor_total || 0;
            weeklyGroups[weekEnding].total_expenses += entry.expenses_total || 0;
          }
        });
        
        const availableMonths = Array.from(monthsSet).sort().reverse();
        console.log(`📅 PM - Meses disponíveis:`, availableMonths);
        console.log(`📅 PM - Última semana:`, latestWeekEnding);
        
        // Definir mês inicial se não estiver definido
        let actualSelectedMonth = monthToUse;
        if (!monthToUse && latestWeekEnding) {
          const monthOfLatestWeek = latestWeekEnding.substring(0, 7);
          actualSelectedMonth = monthOfLatestWeek;
          setSelectedMonth(monthOfLatestWeek);
          console.log(`📅 PM - Mês inicial definido: ${monthOfLatestWeek}`);
        } else if (!monthToUse && availableMonths.length > 0) {
          actualSelectedMonth = availableMonths[0];
          setSelectedMonth(availableMonths[0]);
        }
        
        // Filtrar semanas pelo mês selecionado (LÓGICA SIMPLES)
        const allWeeks = Object.values(weeklyGroups);
        const filteredWeeks = allWeeks.filter(week => {
          if (!week.weekEnding) return false;
          const weekMonth = week.weekEnding.substring(0, 7); // Extrair YYYY-MM
          const matches = weekMonth === actualSelectedMonth;
          console.log(`📅 PM - Semana ${week.weekEnding} → ${weekMonth} ${matches ? '✅' : '❌'} filtro ${actualSelectedMonth}`);
          return matches;
        });
        
        console.log(`🔒 PM - Mostrando ${filteredWeeks.length} semanas do mês ${actualSelectedMonth}`);
        
        // Convert to array (no lucro calculation needed)
        const weeklyData = filteredWeeks;
        
        // Aplicar backcharges e adicionais ao total_final de cada semana
        const weeklyBackcharges = pmReportData.weekly_backcharges || {};
        const weeklyAdicionais = pmReportData.weekly_adicionais || {};
        weeklyData.forEach(week => {
          const bcs = weeklyBackcharges[week.weekEnding] || [];
          const bcTotal = bcs.reduce((s, b) => s + (b.valor || 0), 0);
          const adTotal = (weeklyAdicionais[week.weekEnding] || []).reduce((s, a) => s + (a.valor || 0), 0);
          if (bcTotal > 0 || adTotal > 0) {
            week.total_final = week.total_final - bcTotal + adTotal;
          }
        });
        
        // Sort by week ending date (newest first)
        weeklyData.sort((a, b) => new Date(b.weekEnding) - new Date(a.weekEnding));
        
        console.log(`🔒 PM ${user.username} - Mês ${actualSelectedMonth}: ${weeklyData.length} semanas`);
        
        setWeeklyReportSummary({
          weeklyData,
          reportsCount: weeklyData.length,
          availableMonths
        });
      }
      
      console.log('✅ Weekly report summary loaded successfully');
      
      // Calcular resumo mensal e anual
      calculateMonthlyYearlySummary();
      
    } catch (e) {
      console.error('❌ Error loading weekly report summary:', e);
      if (showErrorToast) {
        toast.error('Erro ao carregar resumo semanal do banco de dados');
      }
      
      // Set empty data on error
      setWeeklyReportSummary({
        weeklyData: [],
        reportsCount: 0
      });
    } finally {
      setWeeklyReportLoading(false);
    }
  };  // Fechar loadWeeklyReportSummary


  // Helper functions para calcular períodos dinamicamente (DEVEM vir ANTES de loadCrewsReport)
  const getLastNWeeksFromAvailable = (n) => {
    return crewsAvailableWeeks.slice(0, Math.min(n, crewsAvailableWeeks.length));
  };

  const getLastCompleteMonth = () => {
    // Pegar o mês anterior completo
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const yearMonth = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
    
    // Filtrar semanas que pertencem a esse mês
    return crewsAvailableWeeks.filter(week => week.startsWith(yearMonth));
  };

  const getWeeksForMonth = (yearMonth) => {
    return crewsAvailableWeeks.filter(week => week.startsWith(yearMonth));
  };

  const getLastCompleteQuarter = () => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1; // 1-12
    const currentQuarter = Math.ceil(currentMonth / 3);
    const year = now.getFullYear();
    
    // Se estamos no meio de um trimestre, pegar o anterior completo
    let targetQuarter = currentQuarter - 1;
    let targetYear = year;
    
    if (targetQuarter === 0) {
      targetQuarter = 4;
      targetYear = year - 1;
    }
    
    // Meses do trimestre
    const quarterMonths = {
      1: ['01', '02', '03'],
      2: ['04', '05', '06'],
      3: ['07', '08', '09'],
      4: ['10', '11', '12']
    };
    
    const months = quarterMonths[targetQuarter];
    const weeksInQuarter = crewsAvailableWeeks.filter(week => {
      const weekYearMonth = week.substring(0, 7);
      return months.some(month => weekYearMonth === `${targetYear}-${month}`);
    });
    
    return weeksInQuarter;
  };

  const getWeeksForQuarter = (quarterStr) => {
    // quarterStr formato: 'Q4/2025'
    const [q, year] = quarterStr.split('/');
    const quarter = parseInt(q.replace('Q', ''));
    
    const quarterMonths = {
      1: ['01', '02', '03'],
      2: ['04', '05', '06'],
      3: ['07', '08', '09'],
      4: ['10', '11', '12']
    };
    
    const months = quarterMonths[quarter];
    return crewsAvailableWeeks.filter(week => {
      const weekYearMonth = week.substring(0, 7);
      return months.some(month => weekYearMonth === `${year}-${month}`);
    });
  };

  const getLastCompleteYear = () => {
    const now = new Date();
    const lastYear = String(now.getFullYear() - 1);
    return crewsAvailableWeeks.filter(week => week.startsWith(lastYear));
  };

  const getWeeksForYear = (year) => {
    return crewsAvailableWeeks.filter(week => week.startsWith(year));
  };


  // Exportar relatório de Crews (Excel / PDF) — client-side
  const exportCrewsExcel = async () => {
    try {
      if (!crewsReportData?.teams?.length) { toast.error('Nada para exportar'); return; }
      const XLSX = await import('xlsx');
      const period = crewsReportData.period_label || 'periodo';
      const teams = crewsReportData.teams.filter(t => crewsReportSelectedTeams.includes(t.team_name));
      const resumo = teams.map(t => ({
        Equipe: t.team_name, Trabalhos: t.total_jobs, 'Total Labor': t.total_labor,
        Despesas: t.total_expenses, Backcharge: t.backcharge_total || 0, Adicional: t.adicional_total || 0,
        'Total Final (PM)': t.total_final, 'Lucro (Empresa)': t.lucro_empresa, '%': t.percentage
      }));
      resumo.push({
        Equipe: 'TOTAL', Trabalhos: crewsReportData.total_jobs, 'Total Labor': crewsReportData.total_labor,
        Despesas: crewsReportData.total_expenses, Backcharge: crewsReportData.total_backcharges || 0, Adicional: crewsReportData.total_adicionais || 0,
        'Total Final (PM)': crewsReportData.total_final, 'Lucro (Empresa)': crewsReportData.lucro_empresa, '%': ''
      });
      const bills = [];
      teams.forEach(t => (t.labor_bills || []).forEach(b => {
        bills.push({
          Equipe: t.team_name, Data: b.date || '', Tipo: b.type === 'backcharge' ? 'Backcharge' : 'Trabalho',
          Cliente: b.type === 'backcharge' ? (b.descricao || b.customer) : b.customer,
          'Job#': b.job_number || '', Labor: b.labor_total || 0, Despesas: b.expenses || 0, Final: b.final_value || 0
        });
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(resumo), 'Resumo');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(bills), 'Bills');
      if (crewsReportData.weeks_breakdown?.length) {
        const semanas = crewsReportData.weeks_breakdown.map(w => ({
          'Semana (trabalho)': w.work_start ? `${w.work_start} a ${w.work_end}` : '',
          Pgto: w.week_ending, Trabalhos: w.jobs, Labor: w.total_labor, Despesas: w.total_expenses,
          Backcharge: w.backcharge_total, 'Total Final': w.total_final, Lucro: w.lucro
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(semanas), 'Semanas');
      }
      // Quebra POR EQUIPE x SEMANA (usada no modo "Por Semana")
      const weekMeta = {};
      (crewsReportData.weeks_breakdown || []).forEach(w => { weekMeta[w.week_ending] = { work_start: w.work_start, work_end: w.work_end }; });
      const porEquipeSemana = [];
      teams.forEach(t => {
        const weeks = buildTeamWeekly(t, weekMeta);
        weeks.forEach(w => {
          porEquipeSemana.push({
            Equipe: t.team_name,
            'Semana (trabalho)': w.work_start ? `${w.work_start} a ${w.work_end}` : '',
            Pgto: w.week_ending, Trabalhos: w.trabalhos, Labor: w.total_labor, Despesas: w.total_expenses,
            Backcharge: w.backcharge_total, Adicional: w.adicional_total || 0, 'Total Final (PM)': w.total_final, 'Lucro (Empresa)': w.lucro_empresa, '%': w.percentage
          });
        });
        // subtotal por equipe
        porEquipeSemana.push({
          Equipe: `${t.team_name} — TOTAL`, 'Semana (trabalho)': '', Pgto: '',
          Trabalhos: t.total_jobs, Labor: t.total_labor, Despesas: t.total_expenses,
          Backcharge: t.backcharge_total || 0, 'Total Final (PM)': t.total_final, 'Lucro (Empresa)': t.lucro_empresa, '%': t.percentage
        });
      });
      if (porEquipeSemana.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(porEquipeSemana), 'Equipe x Semana');
      }
      XLSX.writeFile(wb, `crews_${String(period).replace(/[^\w-]+/g, '_')}.xlsx`);
      toast.success('Excel exportado!');
    } catch (e) { console.error(e); toast.error('Falha ao exportar Excel'); }
  };

  const exportCrewsPDF = async () => {
    try {
      if (!crewsReportData?.teams?.length) { toast.error('Nada para exportar'); return; }
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const period = crewsReportData.period_label || '';
      let y = 40;
      doc.setFontSize(16); doc.text('Relatório de Equipes (Crews)', 40, y); y += 20;
      doc.setFontSize(10); doc.setTextColor(100);
      doc.text(`Período: ${period}  ·  Base: ${crewsReportData.basis === 'payment' ? 'Data de Pagamento' : 'Data do Trabalho'}`, 40, y); y += 24;
      doc.setTextColor(0);
      const teams = crewsReportData.teams.filter(t => crewsReportSelectedTeams.includes(t.team_name));
      const money = (n) => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

      // ===== MODO POR SEMANA: uma tabela por equipe (Semana a Semana) =====
      if (crewsGroupBy === 'week') {
        const weekMeta = {};
        (crewsReportData.weeks_breakdown || []).forEach(w => { weekMeta[w.week_ending] = { work_start: w.work_start, work_end: w.work_end }; });
        const short = (iso) => { if (!iso) return ''; const p = String(iso).split('-'); return `${p[1]}/${p[2]}`; };
        const wcols = [
          { h: 'Semana', x: 40, w: 130, align: 'left' },
          { h: 'Trab.', x: 170, w: 45, align: 'right' },
          { h: 'Labor', x: 215, w: 85, align: 'right' },
          { h: 'Despesas', x: 300, w: 85, align: 'right' },
          { h: 'Backcharge', x: 385, w: 90, align: 'right' },
          { h: 'Total Final', x: 475, w: 95, align: 'right' },
          { h: 'Lucro', x: 570, w: 95, align: 'right' },
        ];
        const drawWHeader = () => {
          doc.setFontSize(8); doc.setFont(undefined, 'bold');
          wcols.forEach(c => doc.text(c.h, c.align === 'right' ? c.x + c.w : c.x, y, { align: c.align }));
          doc.setFont(undefined, 'normal'); y += 5; doc.line(40, y, 665, y); y += 11;
        };
        teams.forEach(t => {
          const weeks = buildTeamWeekly(t, weekMeta);
          if (y > 500) { doc.addPage(); y = 40; }
          doc.setFontSize(11); doc.setFont(undefined, 'bold');
          doc.text(`${t.team_name}  (${t.percentage || 0}%)`, 40, y); doc.setFont(undefined, 'normal'); y += 14;
          drawWHeader();
          doc.setFontSize(8);
          weeks.forEach(w => {
            if (y > 540) { doc.addPage(); y = 40; drawWHeader(); doc.setFontSize(8); }
            const label = w.work_start ? `${short(w.work_start)} – ${short(w.work_end)}` : `Pgto ${w.week_ending}`;
            const row = [label, String(w.trabalhos), money(w.total_labor), money(w.total_expenses), (w.backcharge_total > 0 ? '- ' + money(w.backcharge_total) : '-'), money(w.total_final), money(w.lucro_empresa)];
            row.forEach((val, i) => { const c = wcols[i]; doc.text(String(val), c.align === 'right' ? c.x + c.w : c.x, y, { align: c.align }); });
            y += 13;
          });
          y += 2; doc.line(40, y, 665, y); y += 12; doc.setFont(undefined, 'bold');
          const trow = ['TOTAL', String(t.total_jobs), money(t.total_labor), money(t.total_expenses), (t.backcharge_total > 0 ? '- ' + money(t.backcharge_total) : '-'), money(t.total_final), money(t.lucro_empresa)];
          trow.forEach((val, i) => { const c = wcols[i]; doc.text(String(val), c.align === 'right' ? c.x + c.w : c.x, y, { align: c.align }); });
          doc.setFont(undefined, 'normal'); y += 26;
        });
        doc.save(`crews_semana_${String(period).replace(/[^\w-]+/g, '_')}.pdf`);
        toast.success('PDF (por semana) exportado!');
        return;
      }

      const cols = [
        { h: 'Equipe', x: 40, w: 90, align: 'left' },
        { h: 'Trab.', x: 130, w: 45, align: 'right' },
        { h: 'Labor', x: 175, w: 90, align: 'right' },
        { h: 'Despesas', x: 265, w: 90, align: 'right' },
        { h: 'Backcharge', x: 355, w: 90, align: 'right' },
        { h: 'Total Final', x: 445, w: 95, align: 'right' },
        { h: 'Lucro', x: 540, w: 95, align: 'right' },
        { h: '%', x: 635, w: 40, align: 'right' },
      ];
      const drawHeader = () => {
        doc.setFontSize(9); doc.setFont(undefined, 'bold');
        cols.forEach(c => doc.text(c.h, c.align === 'right' ? c.x + c.w : c.x, y, { align: c.align }));
        doc.setFont(undefined, 'normal'); y += 6; doc.line(40, y, 675, y); y += 12;
      };
      drawHeader();
      teams.forEach(t => {
        if (y > 540) { doc.addPage(); y = 40; drawHeader(); }
        const row = [t.team_name, String(t.total_jobs), money(t.total_labor), money(t.total_expenses), (t.backcharge_total > 0 ? '- ' + money(t.backcharge_total) : '-'), money(t.total_final), money(t.lucro_empresa), (t.percentage || 0) + '%'];
        row.forEach((val, i) => { const c = cols[i]; doc.text(String(val), c.align === 'right' ? c.x + c.w : c.x, y, { align: c.align }); });
        y += 16;
      });
      y += 4; doc.line(40, y, 675, y); y += 14; doc.setFont(undefined, 'bold');
      const tot = ['TOTAL', String(crewsReportData.total_jobs), money(crewsReportData.total_labor), money(crewsReportData.total_expenses), (crewsReportData.total_backcharges > 0 ? '- ' + money(crewsReportData.total_backcharges) : '-'), money(crewsReportData.total_final), money(crewsReportData.lucro_empresa), ''];
      tot.forEach((val, i) => { const c = cols[i]; doc.text(String(val), c.align === 'right' ? c.x + c.w : c.x, y, { align: c.align }); });
      doc.save(`crews_${String(period).replace(/[^\w-]+/g, '_')}.pdf`);
      toast.success('PDF exportado!');
    } catch (e) { console.error(e); toast.error('Falha ao exportar PDF'); }
  };

  // Rótulo curto de mês "YYYY-MM" -> "Set/2025"
  const fmtMonthYm = (ym) => {
    if (!ym) return '';
    const [y, m] = ym.split('-');
    const nm = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'][parseInt(m)] || m;
    return `${nm}/${y}`;
  };

  // Comparar 2 meses (cada um em Trabalho e Pagamento) — busca 4 relatórios em paralelo
  const loadCrewsCompare = async () => {
    if (!crewsCompareMonthA || !crewsCompareMonthB) return;
    try {
      setCrewsCompareLoading(true);
      const token = sessionStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const fetchOne = (month, basis) =>
        axios.get(`${API}/admin/crews-report`, { params: { period_type: 'month', month, basis }, headers }).then(r => r.data);
      const [aWork, aPay, bWork, bPay] = await Promise.all([
        fetchOne(crewsCompareMonthA, 'work'),
        fetchOne(crewsCompareMonthA, 'payment'),
        fetchOne(crewsCompareMonthB, 'work'),
        fetchOne(crewsCompareMonthB, 'payment'),
      ]);
      setCrewsCompareData({ aWork, aPay, bWork, bPay });
    } catch (e) {
      console.error(e);
      toast.error('Falha ao comparar meses');
    } finally {
      setCrewsCompareLoading(false);
    }
  };

  // Load Crews Report (ADMIN only)
  const loadCrewsReport = async () => {
    // Permitir ADMIN e usuários normais (PMs têm role 'user')
    console.log('🔍 loadCrewsReport called - user role:', user?.role);
    if (!user?.role) {
      console.log('❌ No user role - returning');
      return;
    }
    
    console.log('✅ User role check passed - proceeding...');
    
    try {
      setCrewsReportLoading(true);
      
      console.log('📊 Current state:', {
        crewsReportPeriodType,
        crewsMonthlyMode,
        crewsAvailableWeeks: crewsAvailableWeeks.length,
        crewsReportSelectedWeek
      });
      
      // Determinar quais semanas usar baseado no período E modo selecionado
      let weeksToLoad = [];
      let modeDescription = '';
      let monthParam = null;
      let calendarRange = null;
      
      if (crewsReportPeriodType === 'week' && crewsReportSelectedWeek) {
        // Semana específica
        weeksToLoad = [crewsReportSelectedWeek];
        modeDescription = `Semana Específica: ${crewsReportSelectedWeek}`;
      } else if (crewsReportPeriodType === 'month') {
        // Mensal
        if (crewsMonthlyMode === 'chooseMonth' && crewsSelectedMonthOption) {
          // Mês específico: backend agrupa por data do trabalho OU pagamento (basis)
          monthParam = crewsSelectedMonthOption;
          modeDescription = `Mensal: ${crewsSelectedMonthOption} (${crewsSumBasis === 'payment' ? 'pagamento' : 'trabalho'})`;
        } else {
          // Padrão: Últimas 4 Semanas (por data de pagamento)
          weeksToLoad = getLastNWeeksFromAvailable(4);
          modeDescription = 'Mensal: Últimas 4 Semanas';
        }
      } else if (crewsReportPeriodType === 'quarter') {
        // Trimestral - 3 sub-opções
        if (crewsQuarterlyMode === 'last12weeks') {
          weeksToLoad = getLastNWeeksFromAvailable(12);
          modeDescription = 'Trimestral: Últimas 12 Semanas';
        } else if (crewsQuarterlyMode === 'lastCompleteQuarter') {
          weeksToLoad = getLastCompleteQuarter();
          modeDescription = 'Trimestral: Último Trimestre Completo';
        } else if (crewsQuarterlyMode === 'chooseQuarter' && crewsSelectedQuarterOption) {
          const [q, yr] = crewsSelectedQuarterOption.split('/');
          const qn = parseInt(q.replace('Q', ''));
          const sm = (qn - 1) * 3; // mês inicial (0-based)
          const pad = (n) => String(n).padStart(2, '0');
          const lastDay = new Date(parseInt(yr), sm + 3, 0).getDate();
          calendarRange = { start: `${yr}-${pad(sm + 1)}-01`, end: `${yr}-${pad(sm + 3)}-${pad(lastDay)}` };
          modeDescription = `Trimestral: ${crewsSelectedQuarterOption} (${crewsSumBasis === 'payment' ? 'pagamento' : 'trabalho'})`;
        }
      } else if (crewsReportPeriodType === 'year') {
        // Anual - 3 sub-opções
        if (crewsAnnualMode === 'last52weeks') {
          weeksToLoad = getLastNWeeksFromAvailable(52);
          modeDescription = 'Anual: Últimas 52 Semanas';
        } else if (crewsAnnualMode === 'lastCompleteYear') {
          weeksToLoad = getLastCompleteYear();
          modeDescription = 'Anual: Ano Completo Anterior';
        } else if (crewsAnnualMode === 'chooseYear' && crewsSelectedYearOption) {
          const yr = crewsSelectedYearOption;
          calendarRange = { start: `${yr}-01-01`, end: `${yr}-12-31` };
          modeDescription = `Anual: ${yr} (${crewsSumBasis === 'payment' ? 'pagamento' : 'trabalho'})`;
        }
      } else if (crewsReportPeriodType === 'custom' && crewsReportStartDate && crewsReportEndDate) {
        // Range customizado - filtrar semanas entre as datas
        const start = new Date(crewsReportStartDate);
        const end = new Date(crewsReportEndDate);
        weeksToLoad = crewsAvailableWeeks.filter(week => {
          const weekDate = new Date(week);
          return weekDate >= start && weekDate <= end;
        });
        modeDescription = `Customizado: ${crewsReportStartDate} a ${crewsReportEndDate}`;
      }
      
      console.log('='.repeat(80));
      console.log(`📅 MODO SELECIONADO: ${modeDescription}`);
      console.log(`📊 Total de semanas disponíveis no sistema: ${crewsAvailableWeeks.length}`);
      console.log(`📋 Todas as semanas disponíveis:`, crewsAvailableWeeks);
      console.log(`✅ Semanas que serão carregadas (${weeksToLoad.length}):`, weeksToLoad);
      console.log('='.repeat(80));
      
      // Construir params para o backend
      let params = {};
      const applyBasis = ['month', 'quarter', 'year'].includes(crewsReportPeriodType);

      // Intervalo real de trabalho a partir das semanas selecionadas (usa metadados reais)
      const workRangeFromWeeks = (weeks) => {
        let ws = null, we = null;
        weeks.forEach(w => {
          const meta = crewsWeekMeta[w];
          const s = (meta && meta.work_start) || w;
          const e = (meta && meta.work_end) || w;
          if (!ws || s < ws) ws = s;
          if (!we || e > we) we = e;
        });
        return { ws, we };
      };

      if (monthParam) {
        // Mês específico por data do trabalho ou pagamento
        params.period_type = 'month';
        params.month = monthParam;
        params.basis = crewsSumBasis;
      } else if (calendarRange) {
        // Trimestre/Ano específico (calendário) respeitando a base escolhida
        if (crewsSumBasis === 'work') {
          params.period_type = crewsReportPeriodType;
          params.work_start = calendarRange.start;
          params.work_end = calendarRange.end;
          params.basis = 'work';
        } else {
          params.period_type = 'custom';
          params.start_date = calendarRange.start;
          params.end_date = calendarRange.end;
          params.basis = 'payment';
        }
      } else if (crewsReportPeriodType === 'week' && weeksToLoad.length === 1) {
        // Semana específica (folha de pagamento)
        params.period_type = 'week';
        params.week_ending = weeksToLoad[0];
      } else if (weeksToLoad.length >= 1) {
        if (applyBasis && crewsSumBasis === 'work') {
          // Converter as semanas (pagamento) para o intervalo real de trabalho
          const { ws, we } = workRangeFromWeeks(weeksToLoad);
          params.period_type = crewsReportPeriodType;
          params.work_start = ws;
          params.work_end = we;
          params.basis = 'work';
        } else if (weeksToLoad.length === 1) {
          params.period_type = 'week';
          params.week_ending = weeksToLoad[0];
        } else {
          params.period_type = 'custom';
          params.start_date = weeksToLoad[weeksToLoad.length - 1];
          params.end_date = weeksToLoad[0];
          params.basis = 'payment';
        }
      } else {
        // Fallback
        params.period_type = 'month';
      }
      
      console.log('📡 Making API request to /admin/crews-report with params:', params);
      
      const { data } = await axios.get(`${API}/admin/crews-report`, { params });
      
      console.log('✅ API Response received:', {
        total_teams: data.total_teams,
        total_jobs: data.total_jobs,
        is_pm_view: data.is_pm_view,
        teams_count: data.teams?.length
      });
      
      setCrewsReportData(data);
      
      // NÃO sobrescrever crewsAvailableWeeks aqui!
      // As semanas disponíveis para gráficos devem ser TODAS, não só as do relatório atual
      // A função loadAvailableWeeksForCharts() já carrega todas as semanas corretamente
      
      // Auto-selecionar todas as equipes se nenhuma selecionada
      if (crewsReportSelectedTeams.length === 0 && data && data.teams) {
        console.log('🎯 Auto-selecting teams:', data.teams.map(t => t.team_name));
        setCrewsReportSelectedTeams(data.teams.map(t => t.team_name));
      }
      
      console.log('📊 Crews Report loaded:', data);
    } catch (e) {
      console.error('❌ Erro ao carregar crews report:', e);
      toast.error('Erro ao carregar relatório de equipes');
    } finally {
      setCrewsReportLoading(false);
    }
  };

  // Load available weeks for charts
  const loadAvailableWeeksForCharts = async () => {
    // Permitir todos os usuários autenticados (PMs têm role 'user')
    if (!user?.role) return;
    
    try {
      console.log('📅 Buscando TODAS as semanas disponíveis nos relatórios...');
      const { data } = await axios.get(`${API}/admin/crews-report/available-weeks`);
      
      console.log('📦 RESPOSTA DO BACKEND (available-weeks):');
      console.log('   Total retornado:', data.weeks?.length);
      console.log('   Primeira semana retornada (índice 0):', data.weeks?.[0]);
      console.log('   Segunda semana retornada (índice 1):', data.weeks?.[1]);
      console.log('   Terceira semana retornada (índice 2):', data.weeks?.[2]);
      console.log('   Última semana retornada:', data.weeks?.[data.weeks?.length - 1]);
      console.log('   TODAS as semanas:', data.weeks);
      
      const weeksCount = data.weeks?.length || 0;
      console.log(`✅ CARREGADAS ${weeksCount} SEMANAS DISPONÍVEIS`);
      console.log(`   Primeira semana: ${data.weeks?.[data.weeks.length - 1] || 'N/A'}`);
      console.log(`   Última semana: ${data.weeks?.[0] || 'N/A'}`);
      
      setCrewsAvailableWeeks(data.weeks || []);
      setCrewsWeekMeta(data.week_meta || {});
      setCrewsWorkMonths(data.work_months || []);
      setCrewsPayMonths(data.pay_months || []);
      
      // Extrair meses, trimestres e anos disponíveis das semanas
      if (data.weeks && data.weeks.length > 0) {
        // Extrair meses únicos (formato YYYY-MM)
        const monthsSet = new Set();
        const quartersSet = new Set();
        const yearsSet = new Set();
        
        data.weeks.forEach(week => {
          const yearMonth = week.substring(0, 7); // '2025-11-14' -> '2025-11'
          const year = week.substring(0, 4); // '2025-11-14' -> '2025'
          const month = parseInt(week.substring(5, 7)); // '2025-11-14' -> 11
          
          monthsSet.add(yearMonth);
          yearsSet.add(year);
          
          // Determinar trimestre (Q1=Jan-Mar, Q2=Abr-Jun, Q3=Jul-Set, Q4=Out-Dez)
          const quarter = Math.ceil(month / 3);
          quartersSet.add(`Q${quarter}/${year}`);
        });
        
        setCrewsAvailableMonths(Array.from(monthsSet).sort((a, b) => b.localeCompare(a)));
        setCrewsAvailableQuarters(Array.from(quartersSet).sort((a, b) => b.localeCompare(a)));
        setCrewsAvailableYears(Array.from(yearsSet).sort((a, b) => b.localeCompare(a)));
        
        console.log('📅 Meses disponíveis:', Array.from(monthsSet).sort((a, b) => b.localeCompare(a)));
        console.log('📅 Trimestres disponíveis:', Array.from(quartersSet).sort((a, b) => b.localeCompare(a)));
        console.log('📅 Anos disponíveis:', Array.from(yearsSet).sort((a, b) => b.localeCompare(a)));
      }
      
      toast.success(`📅 ${weeksCount} semanas carregadas para seleção!`);
    } catch (e) {
      console.error('❌ Erro ao carregar semanas disponíveis:', e);
      toast.error('Erro ao carregar semanas disponíveis');
    }
  };

  // Load data for multiple selected weeks
  const loadWeeksDataForCharts = async (weeks) => {
    // Permitir todos os usuários autenticados
    if (!user?.role || !weeks || weeks.length === 0) return;
    
    try {
      setLoadingChartsData(true);
      const newWeeksData = {};
      
      // Buscar dados de cada semana
      for (const week of weeks) {
        try {
          const { data } = await axios.get(`${API}/admin/crews-report`, {
            params: { period_type: 'week', week_ending: week }
          });
          newWeeksData[week] = data;
          console.log(`📊 Loaded data for week ${week}:`, data);
        } catch (e) {
          console.error(`Erro ao carregar semana ${week}:`, e);
        }
      }
      
      setWeeksDataForCharts(newWeeksData);
      console.log(`✅ Loaded data for ${Object.keys(newWeeksData).length} weeks`);
    } catch (e) {
      console.error('Erro ao carregar dados das semanas:', e);
      toast.error('Erro ao carregar dados para gráficos');
    } finally {
      setLoadingChartsData(false);
    }
  };

  // Toggle week selection for charts
  const toggleWeekSelection = (week) => {
    setSelectedWeeksForCharts(prev => {
      if (prev.includes(week)) {
        return prev.filter(w => w !== week);
      } else {
        return [...prev, week];
      }
    });
  };

  // Select last N weeks for charts
  const selectLastNWeeks = (n) => {
    console.log(`🔧 selectLastNWeeks chamado com n=${n}`);
    console.log(`🔧 Total de semanas disponíveis: ${crewsAvailableWeeks.length}`);
    console.log(`🔧 Semanas disponíveis:`, crewsAvailableWeeks);
    
    const lastWeeks = crewsAvailableWeeks.slice(0, n);
    
    console.log(`🔧 Selecionando ${lastWeeks.length} semanas:`, lastWeeks);
    
    setSelectedWeeksForCharts(lastWeeks);
  };

  // Load weeks data when selection changes
  useEffect(() => {
    if (selectedWeeksForCharts.length > 0 && crewsReportViewMode === 'charts') {
      loadWeeksDataForCharts(selectedWeeksForCharts);
    }
  }, [selectedWeeksForCharts]);

  // Load available weeks when entering charts view
  useEffect(() => {
    if (activeTab === 'crews' && user?.role && crewsReportViewMode === 'charts' && crewsAvailableWeeks.length === 0) {
      loadAvailableWeeksForCharts();
    }
  }, [crewsReportViewMode, activeTab, user?.role]);



  // ==================== FILE MANAGER FUNCTIONS ====================

  // Load files and folders in current path
  const loadFileManagerItems = async (path = fileManagerCurrentPath) => {
    if (user?.role !== 'admin') return;
    
    try {
      setFileManagerLoading(true);
      const { data } = await axios.get(`${API}/admin/files`, { params: { path } });
      setFileManagerItems(data.items || []);
      console.log(`📁 Loaded ${data.items?.length} items in ${path}`);
    } catch (e) {
      console.error('Erro ao carregar arquivos:', e);
      toast.error('Erro ao carregar arquivos');
    } finally {
      setFileManagerLoading(false);
    }
  };

  // Create new folder
  const createFolder = async () => {
    if (!fileManagerNewFolderName.trim()) {
      toast.error('Digite um nome para a pasta');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/files/folder`, null, {
        params: { name: fileManagerNewFolderName, parent_path: fileManagerCurrentPath }
      });
      toast.success('Pasta criada com sucesso');
      setShowCreateFolderModal(false);
      setFileManagerNewFolderName('');
      loadFileManagerItems();
    } catch (e) {
      console.error('Erro ao criar pasta:', e);
      toast.error(getErrMsg(e, 'Erro ao criar pasta'));
    }
  };

  // Upload files
  const uploadFileManagerFiles = async (files, replaceExisting = false) => {
    if (!files || files.length === 0) return;
    
    setFileManagerUploadingFiles(Array.from(files));
    const failedFiles = [];
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        setFileManagerUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
        
        await axios.post(
          `${API}/admin/files/upload?parent_path=${encodeURIComponent(fileManagerCurrentPath)}&replace=${replaceExisting}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setFileManagerUploadProgress(prev => ({ ...prev, [file.name]: percentCompleted }));
            }
          }
        );
        
        setFileManagerUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
        
      } catch (e) {
        console.error(`Erro ao enviar ${file.name}:`, e);
        
        // Verificar se é erro de duplicata (409)
        if (e.response?.status === 409) {
          const confirmReplace = window.confirm(
            `⚠️ Já existe um arquivo com o nome "${file.name}" nesta pasta.\n\n` +
            `Deseja substituir o arquivo existente?`
          );
          
          if (confirmReplace) {
            // Tentar novamente com replace=true
            try {
              const formData = new FormData();
              formData.append('file', file);
              
              await axios.post(
                `${API}/admin/files/upload?parent_path=${encodeURIComponent(fileManagerCurrentPath)}&replace=true`,
                formData,
                {
                  headers: { 'Content-Type': 'multipart/form-data' },
                  onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setFileManagerUploadProgress(prev => ({ ...prev, [file.name]: percentCompleted }));
                  }
                }
              );
              
              setFileManagerUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
            } catch (retryError) {
              console.error(`Erro ao substituir ${file.name}:`, retryError);
              failedFiles.push(file.name);
              toast.error(`Erro ao substituir ${file.name}`);
            }
          } else {
            toast.info(`Upload de "${file.name}" cancelado`);
          }
        } else {
          failedFiles.push(file.name);
          toast.error(`Erro ao enviar ${file.name}: ${getErrMsg(e)}`);
        }
      }
    }
    
    const successCount = files.length - failedFiles.length;
    if (successCount > 0) {
      toast.success(`${successCount} arquivo(s) enviado(s) com sucesso`);
    }
    
    setFileManagerUploadingFiles([]);
    setFileManagerUploadProgress({});
    setShowFileUploadModal(false);
    loadFileManagerItems();
  };

  // Delete item
  const deleteFileManagerItem = async (itemId, itemName) => {
    if (!window.confirm(`Deletar "${itemName}"?\n\nEsta ação não pode ser desfeita.`)) return;
    
    try {
      await axios.delete(`${API}/admin/files/${itemId}`);
      toast.success('Item deletado com sucesso');
      setFileManagerSelectedItems([]);
      loadFileManagerItems();
    } catch (e) {
      console.error('Erro ao deletar:', e);
      toast.error(getErrMsg(e, 'Erro ao deletar item'));
    }
  };

  // Rename item
  const renameFileManagerItem = async () => {
    if (!fileManagerRenameNewName.trim()) {
      toast.error('Digite um novo nome');
      return;
    }
    
    try {
      await axios.put(`${API}/admin/files/${fileManagerRenameItemId}/rename`, null, {
        params: { new_name: fileManagerRenameNewName }
      });
      toast.success('Item renomeado com sucesso');
      setShowRenameModal(false);
      setFileManagerRenameItemId(null);
      setFileManagerRenameNewName('');
      loadFileManagerItems();
    } catch (e) {
      console.error('Erro ao renomear:', e);
      toast.error(getErrMsg(e, 'Erro ao renomear item'));
    }
  };

  // Generate share link
  const generateShareLink = async (item) => {
    try {
      const { data } = await axios.post(`${API}/admin/files/${item.id}/share-link`, null, {
        params: { expiry_days: 7 }
      });
      
      // Corrigir URL se vier como None
      let shareUrl = data.url;
      if (shareUrl.startsWith('None/')) {
        shareUrl = shareUrl.replace('None', window.location.origin);
      }
      
      setGeneratedShareLink(shareUrl);
      setShareLinkItem(item);
      setShowShareLinkModal(true);
      console.log('🔗 Share link generated:', shareUrl);
    } catch (e) {
      console.error('Erro ao gerar link:', e);
      toast.error(getErrMsg(e, 'Erro ao gerar link'));
    }
  };

  // Download file
  const downloadFileManagerFile = async (fileId, fileName) => {
    try {
      const response = await axios.get(`${API}/admin/files/${fileId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Download iniciado');
    } catch (e) {
      console.error('Erro ao baixar arquivo:', e);
      toast.error('Erro ao baixar arquivo');
    }
  };

  // Navigate to folder
  const navigateToFolder = (folderPath) => {
    setFileManagerCurrentPath(folderPath);
    setFileManagerSelectedItems([]);
  };

  // Preview file (images, PDFs, Videos, Office)
  const previewFileManagerFile = async (item) => {
    try {
      setFilePreviewLoading(true);
      setFilePreviewProgress(0);
      setFilePreviewName(item.name);
      
      // Para arquivos Office, buscar conteúdo e renderizar localmente
      if (item.file_type?.includes('word') || 
          item.file_type?.includes('document') ||
          item.file_type?.includes('excel') ||
          item.file_type?.includes('spreadsheet')) {
        
        // Buscar conteúdo do arquivo com progresso
        const { data } = await axios.get(`${API}/admin/files/${item.id}/preview`, {
          onDownloadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || item.size));
            setFilePreviewProgress(percentCompleted);
          }
        });
        
        // Converter base64 para blob URL
        const binaryString = atob(data.content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: data.file_type });
        const blobUrl = URL.createObjectURL(blob);
        
        setFilePreviewLoading(false);
        setOfficeModalData({ url: blobUrl, fileName: item.name, itemId: item.id });
        setShowOfficeModal(true);
        return;
      }
      
      // PowerPoint não suportado - oferecer download
      if (item.file_type?.includes('presentation')) {
        setFilePreviewLoading(false);
        if (window.confirm('PowerPoint não tem preview. Deseja baixar o arquivo?')) {
          downloadFileManagerFile(item.id, item.name);
        }
        return;
      }
      
      // Preview de vídeos
      if (item.file_type?.includes('video')) {
        const { data } = await axios.get(`${API}/admin/files/${item.id}/preview`, {
          onDownloadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || item.size));
            setFilePreviewProgress(percentCompleted);
          }
        });
        
        // Converter base64 para blob URL
        const binaryString = atob(data.content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: data.file_type });
        const videoUrl = URL.createObjectURL(blob);
        
        setFilePreviewLoading(false);
        setVideoModalData({ url: videoUrl, title: item.name });
        setShowVideoModal(true);
        return;
      }
      
      // Buscar conteúdo do arquivo para outros tipos com progresso
      const { data } = await axios.get(`${API}/admin/files/${item.id}/preview`, {
        onDownloadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || item.size));
          setFilePreviewProgress(percentCompleted);
        }
      });
      
      // Preview de imagens (incluindo DNG)
      if (item.file_type?.includes('image') || item.file_type?.includes('dng') || item.name?.toLowerCase().endsWith('.dng')) {
        // Para DNG e outros formatos RAW, avisar que pode não renderizar perfeitamente
        if (item.file_type?.includes('dng') || item.name?.toLowerCase().endsWith('.dng')) {
          toast.info('Imagem DNG carregada (preview pode ter limitações no navegador)');
        }
        
        const imageUrl = `data:${data.file_type};base64,${data.content}`;
        setFilePreviewLoading(false);
        setImageModalData({ url: imageUrl, title: item.name });
        setShowImageModal(true);
      } else if (item.file_type?.includes('pdf')) {
        // Preview de PDF - converter base64 para Blob URL (mais confiável)
        const binaryString = atob(data.content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(blob);
        
        setFilePreviewLoading(false);
        setPdfModalData({ url: pdfUrl, title: item.name });
        setShowPDFModal(true);
      } else {
        // Outros tipos: fazer download
        setFilePreviewLoading(false);
        toast.info(`Tipo de arquivo: ${data.file_type || 'desconhecido'}. Iniciando download...`);
        downloadFileManagerFile(item.id, item.name);
      }
    } catch (e) {
      console.error('Erro ao visualizar arquivo:', e);
      setFilePreviewLoading(false);
      toast.error(`Erro ao visualizar arquivo: ${e.response?.data?.detail || e.message}`);
    }
  };

  // Go back to parent folder
  const goBackFolder = () => {
    if (fileManagerCurrentPath === '/') return;
    const parts = fileManagerCurrentPath.split('/').filter(p => p);
    parts.pop();
    const newPath = parts.length > 0 ? `/${parts.join('/')}` : '/';
    navigateToFolder(newPath);
  };

  // Toggle item selection
  const toggleFileSelection = (itemId) => {
    setFileManagerSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  // Delete selected items
  const deleteSelectedItems = async () => {
    if (fileManagerSelectedItems.length === 0) return;
    
    if (!window.confirm(`Deletar ${fileManagerSelectedItems.length} item(s)?\n\nEsta ação não pode ser desfeita.`)) return;
    
    try {
      for (const itemId of fileManagerSelectedItems) {
        await axios.delete(`${API}/admin/files/${itemId}`);
      }
      toast.success(`${fileManagerSelectedItems.length} item(s) deletado(s)`);
      setFileManagerSelectedItems([]);
      loadFileManagerItems();
    } catch (e) {
      console.error('Erro ao deletar itens:', e);
      toast.error('Erro ao deletar itens');
    }
  };

  // Copy selected items to clipboard
  const copyToClipboard = () => {
    setFileManagerClipboard({ items: [...fileManagerSelectedItems], action: 'copy' });
    toast.info(`${fileManagerSelectedItems.length} item(s) copiado(s)`);
  };

  // Cut selected items to clipboard
  const cutToClipboard = () => {
    setFileManagerClipboard({ items: [...fileManagerSelectedItems], action: 'cut' });
    toast.info(`${fileManagerSelectedItems.length} item(s) cortado(s)`);
  };

  // Paste items from clipboard
  const pasteFromClipboard = async () => {
    if (fileManagerClipboard.items.length === 0) return;
    
    const failedItems = [];
    
    try {
      for (const itemId of fileManagerClipboard.items) {
        const item = fileManagerItems.find(i => i.id === itemId);
        const itemName = item?.name || itemId;
        
        try {
          if (fileManagerClipboard.action === 'copy') {
            await axios.post(`${API}/admin/files/${itemId}/copy`, null, {
              params: { destination_path: fileManagerCurrentPath, replace: false }
            });
          } else if (fileManagerClipboard.action === 'cut') {
            await axios.put(`${API}/admin/files/${itemId}/move`, null, {
              params: { new_parent_path: fileManagerCurrentPath }
            });
          }
        } catch (e) {
          // Verificar se é erro de duplicata (409)
          if (e.response?.status === 409) {
            const confirmReplace = window.confirm(
              `⚠️ Já existe um item com o nome "${itemName}" no destino.\n\n` +
              `Deseja substituir o item existente?`
            );
            
            if (confirmReplace) {
              // Tentar novamente com replace=true
              try {
                if (fileManagerClipboard.action === 'copy') {
                  await axios.post(`${API}/admin/files/${itemId}/copy`, null, {
                    params: { destination_path: fileManagerCurrentPath, replace: true }
                  });
                } else if (fileManagerClipboard.action === 'cut') {
                  await axios.put(`${API}/admin/files/${itemId}/move`, null, {
                    params: { new_parent_path: fileManagerCurrentPath, replace: true }
                  });
                }
              } catch (retryError) {
                console.error('Erro ao substituir:', retryError);
                failedItems.push(itemName);
              }
            } else {
              toast.info(`Operação cancelada para "${itemName}"`);
            }
          } else {
            console.error('Erro ao colar:', e);
            failedItems.push(itemName);
          }
        }
      }
      
      const successCount = fileManagerClipboard.items.length - failedItems.length;
      
      if (successCount > 0) {
        toast.success(`${successCount} item(s) ${fileManagerClipboard.action === 'copy' ? 'copiado(s)' : 'movido(s)'}`);
      }
      
      if (failedItems.length > 0) {
        toast.error(`Falha ao processar: ${failedItems.join(', ')}`);
      }
      
      if (fileManagerClipboard.action === 'cut') {
        setFileManagerClipboard({ items: [], action: '' });
      }
      
      setFileManagerSelectedItems([]);
      loadFileManagerItems();
    } catch (e) {
      console.error('Erro ao colar:', e);
      toast.error('Erro ao colar itens');
    }
  };

  // Load file manager items when entering tab
  useEffect(() => {
    if (activeTab === 'admin' && adminSubTab === 'arquivos' && user?.role === 'admin') {
      loadFileManagerItems();
    }
  }, [activeTab, adminSubTab, fileManagerCurrentPath]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Close action menu [...] if clicking outside
      if (showActionMenu && !e.target.closest('td')) {
        setShowActionMenu(null);
      }
      
      // Close top menu if clicking outside the menu bar
      if (openTopMenu) {
        const clickedInsideMenuBar = e.target.closest('.bg-white.border.border-gray-300.rounded-lg');
        if (!clickedInsideMenuBar) {
          setOpenTopMenu(null);
        }
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showActionMenu, openTopMenu]);

  // Create default folders on first load
  const createDefaultFolders = async () => {
    const defaultFolders = ['Contratos', 'Propostas', 'Fotos', 'Documentos'];
    
    try {
      for (const folderName of defaultFolders) {
        await axios.post(`${API}/admin/files/folder`, null, {
          params: { name: folderName, parent_path: '/' }
        }).catch(() => {}); // Ignorar se já existe
      }
      console.log('📁 Default folders created');
    } catch (e) {
      console.log('Default folders may already exist');
    }
  };

  // Upload custom icon for folder
  const uploadCustomFolderIcon = async (folderId, iconFile) => {
    try {
      const formData = new FormData();
      formData.append('icon', iconFile);
      
      await axios.post(`${API}/admin/files/${folderId}/custom-icon`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Ícone personalizado atualizado!');
      setShowFolderIconModal(false);
      setSelectedFolderForIcon(null);
      setSelectedIconPreview('');
      loadFileManagerItems();
    } catch (e) {
      console.error('Erro ao fazer upload do ícone:', e);
      toast.error(getErrMsg(e, 'Erro ao fazer upload do ícone'));
    }
  };

  // Galeria de ícones pré-definidos (emojis)
  const iconGallery = ['📁', '📂', '📄', '📊', '📈', '📋', '🎨', '🎬', '🎵', '🖼️', '💼', '🏠', '⚙️', '🔒', '📦', '💰', '📝', '📷', '🎯', '🌟'];

  // Set emoji icon for folder (stored as custom_icon in DB)
  const setEmojiIconForFolder = async (folderId, emoji) => {
    try {
      // Criar uma imagem SVG do emoji
      const svgContent = `
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
          <text x="50%" y="50%" text-anchor="middle" dominant-baseline="central" font-size="48">${emoji}</text>
        </svg>
      `;
      
      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      await uploadCustomFolderIcon(folderId, new File([blob], 'icon.svg', { type: 'image/svg+xml' }));
    } catch (e) {
      console.error('Erro ao definir ícone emoji:', e);
      toast.error('Erro ao definir ícone');
    }
  };

  // Check backup progress (polling)
  const checkBackupProgress = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/backup/progress`);
      
      console.log('🔄 Progresso do backup:', data.progress + '%', '-', data.current_step);
      
      setBackupProgress(data);
      
      // ⚠️ PARAR IMEDIATAMENTE se tiver ERRO
      if (data.error) {
        if (backupPollingIntervalRef.current) {
          console.log('🛑 ERRO detectado - parando polling IMEDIATAMENTE');
          clearInterval(backupPollingIntervalRef.current);
          backupPollingIntervalRef.current = null;
        }
        
        console.error('❌ BACKUP FALHOU:', data.error);
        toast.error(`Backup falhou: ${data.error}`);
        return; // SAI DA FUNÇÃO!
      }
      
      // ✅ PARAR se backup NÃO está rodando (is_running = false)
      if (!data.is_running) {
        if (backupPollingIntervalRef.current) {
          console.log('🛑 Backup não está rodando - parando polling');
          clearInterval(backupPollingIntervalRef.current);
          backupPollingIntervalRef.current = null;
        }
        
        if (data.progress === 100) {
          console.log('✅ BACKUP FINALIZADO COM SUCESSO!');
          console.log('⏰ Horário de conclusão:', formatNyDateTime());
          toast.success('✅ Backup concluído com sucesso!');
        } else {
          console.log('⚠️ Backup parou sem chegar a 100% (verificar backend)');
        }
        return; // SAI DA FUNÇÃO!
      }
      
    } catch (e) {
      // ERRO DE REQUISIÇÃO (520, timeout, etc.) - PARAR POLLING!
      console.error('❌ ERRO DE REQUISIÇÃO - Backend pode ter crashado:', e.message);
      
      if (backupPollingIntervalRef.current) {
        console.log('🛑 Parando polling devido a erro de requisição');
        clearInterval(backupPollingIntervalRef.current);
        backupPollingIntervalRef.current = null;
      }
      
      // Atualizar card com erro
      setBackupProgress({
        is_running: false,
        progress: 0,
        current_step: `Erro: ${e.message}`,
        error: e.message
      });
      
      toast.error('❌ Backup falhou - Backend indisponível');
    }
  };

  // Start backup with progress tracking
  // Update Dropbox Token
  const updateDropboxToken = async () => {
    if (!newDropboxToken.trim()) {
      toast.error('Por favor, insira um token válido');
      return;
    }
    
    if (!window.confirm('Atualizar o Token do Dropbox?\n\nIsso irá substituir o token atual.')) {
      return;
    }
    
    setIsUpdatingToken(true);
    
    try {
      console.log('🔑 Atualizando Token Dropbox...');
      const { data } = await axios.post(`${API}/admin/backup/update-dropbox-token`, {
        token: newDropboxToken
      });
      
      console.log('✅ Token atualizado e testado:', data);
      
      // Backend já testou a conexão!
      if (data.connection_test === 'success') {
        toast.success(`🎉 Token atualizado e conectado! ${data.account_email}`);
      } else {
        toast.success('✅ Token salvo! Verifique a conexão.');
      }
      
      setShowDropboxTokenModal(false);
      setNewDropboxToken('');
      
    } catch (e) {
      console.error('❌ Erro ao atualizar token:', e);
      toast.error(getErrMsg(e, 'Erro ao atualizar token'));
    } finally {
      setIsUpdatingToken(false);
    }
  };

  
  // Dropbox OAuth Setup Functions
  const saveDropboxOAuthCredentials = async () => {
    if (!dropboxAppKey.trim() || !dropboxAppSecret.trim()) {
      toast.error('Por favor, preencha App Key e App Secret');
      return;
    }
    
    setOauthLoading(true);
    
    try {
      await axios.post(`${API}/admin/backup/save-dropbox-oauth-credentials`, {
        app_key: dropboxAppKey,
        app_secret: dropboxAppSecret
      });
      
      toast.success('✅ Credenciais salvas com sucesso!');
      setOauthStep(2);
      
    } catch (e) {
      console.error('❌ Erro ao salvar credenciais:', e);
      toast.error(getErrMsg(e, 'Erro ao salvar credenciais'));
    } finally {
      setOauthLoading(false);
    }
  };
  
  const generateDropboxAuthUrl = async () => {
    setOauthLoading(true);
    
    try {
      const { data } = await axios.get(`${API}/admin/backup/generate-dropbox-auth-url`);
      
      setDropboxAuthUrl(data.authorization_url);
      toast.success('✅ Link de autorização gerado!');
      setOauthStep(3);
      
    } catch (e) {
      console.error('❌ Erro ao gerar link:', e);
      toast.error(getErrMsg(e, 'Erro ao gerar link de autorização'));
    } finally {
      setOauthLoading(false);
    }
  };
  
  const exchangeDropboxCode = async () => {
    if (!dropboxAuthCode.trim()) {
      toast.error('Por favor, insira o código de autorização');
      return;
    }
    
    setOauthLoading(true);
    
    try {
      const { data } = await axios.post(`${API}/admin/backup/exchange-dropbox-code`, {
        code: dropboxAuthCode
      });
      
      toast.success(`🎉 ${data.message}`);
      toast.success(`✅ Conectado: ${data.account_email}`);
      
      // Fechar modal e resetar
      setShowDropboxOAuthModal(false);
      setOauthStep(1);
      setDropboxAppKey('');
      setDropboxAppSecret('');
      setDropboxAuthUrl('');
      setDropboxAuthCode('');
      
    } catch (e) {
      console.error('❌ Erro ao trocar código:', e);
      toast.error(getErrMsg(e, 'Erro ao configurar refresh token'));
    } finally {
      setOauthLoading(false);
    }
  };

  
  // Load Backup Settings
  const loadBackupSettings = async () => {
    try {
      console.log('🔧 DEBUG - Carregando configurações de backup...');
      const { data } = await axios.get(`${API}/admin/backup/settings`);
      
      console.log('🔧 DEBUG - Resposta do servidor:', data);
      
      // Converter de 24h para 12h com AM/PM
      const hour24 = data.backup_hour || 0;
      let hour12 = hour24 % 12;
      if (hour12 === 0) hour12 = 12;
      const period = hour24 >= 12 ? 'PM' : 'AM';
      
      console.log('🔧 DEBUG - Conversão de horário:');
      console.log(`   Hora 24h recebida: ${hour24}`);
      console.log(`   Hora 12h convertida: ${hour12}`);
      console.log(`   Período: ${period}`);
      
      const newSettings = {
        backup_hour: String(hour12),
        backup_minute: String(data.backup_minute || '0'),
        backup_period: period,
        retention_days: String(data.retention_days || '1095'),
        backup_frequency: data.backup_frequency || 'daily',
        backup_timezone: data.backup_timezone || 'America/New_York'
      };
      
      console.log('🔧 DEBUG - Configurações finais que serão aplicadas:', newSettings);
      
      setBackupSettings(newSettings);
      
      console.log('✅ Configurações carregadas com sucesso!');
    } catch (e) {
      console.error('❌ Erro ao carregar configurações:', e);
    }
  };
  
  // Save Backup Settings
  const saveBackupSettings = async () => {
    console.log('🔧 DEBUG - Iniciando salvamento de configurações...');
    console.log('🔧 DEBUG - Valores atuais:', backupSettings);
    
    if (!window.confirm('Salvar configurações de backup?\n\nO backend será reiniciado para aplicar as mudanças.')) {
      console.log('🔧 DEBUG - Usuário cancelou');
      return;
    }
    
    setIsSavingBackupSettings(true);
    
    try {
      // Converter de 12h AM/PM para 24h
      let hour24 = parseInt(backupSettings.backup_hour);
      if (backupSettings.backup_period === 'PM' && hour24 !== 12) {
        hour24 += 12;
      } else if (backupSettings.backup_period === 'AM' && hour24 === 12) {
        hour24 = 0;
      }
      
      const payload = {
        backup_hour: hour24,
        backup_minute: parseInt(backupSettings.backup_minute),
        retention_days: parseInt(backupSettings.retention_days),
        backup_frequency: backupSettings.backup_frequency,
        backup_timezone: backupSettings.backup_timezone
      };
      
      console.log('🔧 DEBUG - Payload sendo enviado:', payload);
      console.log('🔧 DEBUG - URL:', `${API}/admin/backup/update-settings`);
      
      const { data } = await axios.post(`${API}/admin/backup/update-settings`, payload);
      
      console.log('✅ Configurações salvas com sucesso:', data);
      toast.success('✅ Configurações salvas! Aguarde o reload...');
      setIsEditingBackupSettings(false);
      
      // Aguardar hot reload do backend (dispara ao criar arquivo trigger)
      setTimeout(async () => {
        let attempts = 0;
        const maxAttempts = 3;
        
        while (attempts < maxAttempts) {
          try {
            await loadBackupSettings();
            console.log('✅ Configurações recarregadas com sucesso!');
            toast.success('✅ Configurações atualizadas!');
            break;
          } catch (e) {
            attempts++;
            console.log(`⚠️ Tentativa ${attempts}/${maxAttempts} - aguardando reload...`);
            if (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 2000));
            }
          }
        }
      }, 4000); // Aguarda 4 segundos para hot reload completar
      
    } catch (e) {
      console.error('❌ Erro ao salvar configurações:', e);
      toast.error(getErrMsg(e, 'Erro ao salvar configurações'));
    } finally {
      setIsSavingBackupSettings(false);
    }
  };
  
  // Load backup settings when entering admin tab
  // Load backup settings when entering Sistema subtab
  useEffect(() => {
    if (activeTab === 'admin' && adminSubTab === 'sistema' && user?.role === 'admin') {
      console.log('🔧 DEBUG - useEffect disparado: carregando configurações de backup...');
      loadBackupSettings();
      loadBackupHistory();
    }
  }, [activeTab, adminSubTab, user?.role]);

  
  // Load email defaults when entering emails sub-tab
  useEffect(() => {
    if (activeTab === 'admin' && adminSubTab === 'emails' && user?.role === 'admin') {
      loadAdminEmailDefaults('global', 'PM1');
      loadEmailConfig();
    }
  }, [activeTab, adminSubTab, user?.role]);

  
  // Load Backup History
  const loadBackupHistory = async () => {
    setLoadingBackupHistory(true);
    try {
      const { data } = await axios.get(`${API}/admin/backup/history`);
      setBackupHistory(data.history || []);
    } catch (e) {
      console.error('❌ Erro ao carregar histórico:', e);
    } finally {
      setLoadingBackupHistory(false);
    }
  };
  
  // Upload Email Image
  const uploadEmailImage = async () => {
    if (!emailImageFile) {
      toast.error('Selecione uma imagem primeiro');
      return;
    }
    
    setEmailImageUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', emailImageFile);
      formData.append('path', '/Email_Images');
      
      const { data } = await axios.post(`${API}/admin/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Gerar URL pública para a imagem
      const imageUrl = `${API}/admin/files/${data.id}/preview`;
      setUploadedEmailImageUrl(imageUrl);
      
      // Inserir imagem no editor
      document.execCommand('insertImage', false, imageUrl);
      
      toast.success('✅ Imagem inserida no email!');
      setShowEmailImageUploadModal(false);
      setEmailImageFile(null);
      
    } catch (e) {
      console.error('❌ Erro ao fazer upload:', e);
      toast.error('Erro ao fazer upload da imagem');
    } finally {
      setEmailImageUploading(false);
    }
  };
  
  // Helper: Save selection
  const saveEmailSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      savedSelection.current = selection.getRangeAt(0);
    }
  };
  
  // Helper: Restore selection
  const restoreEmailSelection = () => {
    const selection = window.getSelection();
    if (savedSelection.current) {
      selection.removeAllRanges();
      selection.addRange(savedSelection.current);
    }
  };
  
  // Apply color to selected text
  const applyTextColor = (color) => {
    restoreEmailSelection();
    document.execCommand('foreColor', false, color);
    emailEditorRef.current?.focus();
  };
  
  // Apply background color to selected text
  const applyBackgroundColor = (color) => {
    restoreEmailSelection();
    document.execCommand('backColor', false, color);
    emailEditorRef.current?.focus();
  };
  
  // Apply font size to selected text
  const applyFontSize = (size) => {
    restoreEmailSelection();
    document.execCommand('fontSize', false, size);
    emailEditorRef.current?.focus();
  };

  const startBackupWithProgress = async () => {
    if (!window.confirm('Executar backup completo agora?\n\nIsso pode levar alguns minutos.')) return;
    
    try {
      console.log('🚀 BACKUP INICIADO:', formatNyDateTime());
      console.log('⏰ Horário de início:', new Date().toISOString());
      
      // SETAR ESTADO INICIAL DO CARD IMEDIATAMENTE (antes do request!)
      setBackupProgress({
        is_running: true,
        progress: 0,
        current_step: 'Iniciando backup...',
        error: null
      });
      
      // Iniciar backup
      await axios.post(`${API}/admin/backup/run-now`);
      
      toast.info('🔄 Backup iniciado em background...');
      
      // Iniciar polling a cada 2 segundos
      const interval = setInterval(checkBackupProgress, 2000);
      backupPollingIntervalRef.current = interval; // useRef ao invés de useState
      
      // Checar imediatamente
      setTimeout(checkBackupProgress, 500);
      
    } catch (e) {
      console.error('❌ ERRO AO INICIAR BACKUP:', e);
      setBackupProgress(null); // Limpar card se erro ao iniciar
      toast.error(getErrMsg(e, 'Erro ao iniciar backup'));
    }
  };

  // Load available backups from Dropbox
  const loadAvailableBackups = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/backup/list-available`);
      setAvailableBackups(data.backups || []);
      toast.success(`📦 ${data.total_dates} backups disponíveis`);
    } catch (e) {
      console.error('Erro ao listar backups:', e);
      toast.error('Erro ao carregar lista de backups');
    }
  };

  // Check restore progress (polling)
  const checkRestoreProgress = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/backup/restore-progress`);
      
      console.log('🔄 Progresso da restauração:', data.progress + '%', '-', data.current_step);
      
      setRestoreProgress(data);
      
      // ⚠️ PARAR IMEDIATAMENTE se tiver ERRO
      if (data.error) {
        if (restorePollingIntervalRef.current) {
          console.log('🛑 ERRO detectado - parando polling de restauração');
          clearInterval(restorePollingIntervalRef.current);
          restorePollingIntervalRef.current = null;
        }
        
        console.error('❌ RESTAURAÇÃO FALHOU:', data.error);
        toast.error(`Restauração falhou: ${data.error}`);
        return;
      }
      
      // ✅ PARAR se restauração NÃO está rodando (is_running = false)
      if (!data.is_running) {
        if (restorePollingIntervalRef.current) {
          console.log('🛑 Restauração não está rodando - parando polling');
          clearInterval(restorePollingIntervalRef.current);
          restorePollingIntervalRef.current = null;
        }
        
        if (data.progress === 100) {
          console.log('✅ RESTAURAÇÃO CONCLUÍDA COM SUCESSO!');
          toast.success('✅ Backup restaurado! Recarregando...');
          setTimeout(() => window.location.reload(), 2000);
        } else {
          console.log('⚠️ Restauração parou sem chegar a 100%');
        }
        return;
      }
      
    } catch (e) {
      console.warn('⚠️ Erro ao verificar progresso de restauração:', e.message);
      
      if (restorePollingIntervalRef.current) {
        clearInterval(restorePollingIntervalRef.current);
        restorePollingIntervalRef.current = null;
      }
    }
  };

  // Restore backup
  const restoreBackup = async () => {
    console.log('🔍 DEBUG - Estado antes de restaurar:');
    console.log('  selectedBackupDate:', selectedBackupDate);
    console.log('  selectedBackupFile:', selectedBackupFile);
    console.log('  restoreAllUsers:', restoreAllUsers);
    console.log('  selectedUsersForRestore:', selectedUsersForRestore);
    
    if (!selectedBackupDate || !selectedBackupFile) {
      toast.error('Selecione um backup para restaurar');
      return;
    }
    
    const confirmMsg = restoreAllUsers 
      ? `⚠️ ATENÇÃO!\n\nIsso vai RESTAURAR TODOS OS DADOS do backup de ${selectedBackupDate}.\n\nTodos os dados atuais serão SUBSTITUÍDOS.\n\nDeseja continuar?`
      : `Restaurar dados dos usuários selecionados (${selectedUsersForRestore.length}) do backup de ${selectedBackupDate}?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      console.log('🚀 RESTAURAÇÃO INICIADA:', formatNyDateTime());
      
      // SETAR ESTADO INICIAL DO PROGRESSO IMEDIATAMENTE (antes do request!)
      setRestoreProgress({
        is_running: true,
        progress: 0,
        current_step: 'Iniciando restauração...',
        error: null
      });
      
      // Preparar payload correto - GARANTIR que tudo está definido
      const payload = {
        backup_date: String(selectedBackupDate),
        file_name: String(selectedBackupFile),
        selected_users: Array.isArray(selectedUsersForRestore) ? selectedUsersForRestore : [],
        restore_all: Boolean(restoreAllUsers)
      };
      
      console.log('📤 PAYLOAD SENDO ENVIADO:', JSON.stringify(payload, null, 2));
      
      // Iniciar restauração
      await axios.post(`${API}/admin/backup/restore`, payload);
      
      toast.info('🔄 Restauração iniciada em background...');
      setShowRestoreModal(false);
      
      // Iniciar polling a cada 2 segundos
      const interval = setInterval(checkRestoreProgress, 2000);
      restorePollingIntervalRef.current = interval; // useRef ao invés de useState
      
      // Checar imediatamente após 500ms
      setTimeout(checkRestoreProgress, 500);
      
    } catch (e) {
      console.error('❌ ERRO NA RESTAURAÇÃO:', e);
      console.error('📋 Response data:', e.response?.data);
      console.error('📋 Response status:', e.response?.status);
      console.error('📋 Full error:', e);
      setRestoreProgress(null); // Limpar estado se erro ao iniciar
      toast.error(getErrMsg(e, 'Erro ao restaurar backup'));
    }
  };


  // Auto-load Crews Report when ADMIN or PM enters crews tab
  useEffect(() => {
    if (activeTab === 'crews' && user?.role) {
      console.log('👥 User entered Crews tab - loading available weeks and report');
      loadAvailableWeeksForCharts(); // Carregar semanas disponíveis PRIMEIRO
      loadCrewsReport();
    }
  }, [activeTab, user?.role]);


  // Auto-reload Crews Report when filters change
  useEffect(() => {
    if (activeTab === 'crews' && user?.role && crewsReportData) {
      // Recarregar quando mudar período, semana, ou datas
      console.log('🔄 Filter changed - reloading crews report');
      loadCrewsReport();
    }
  }, [
    crewsReportPeriodType, 
    crewsReportSelectedWeek, 
    crewsReportSelectedMonth, 
    crewsReportStartDate, 
    crewsReportEndDate,
    crewsMonthlyMode,
    crewsQuarterlyMode,
    crewsAnnualMode,
    crewsSelectedMonthOption,
    crewsSelectedQuarterOption,
    crewsSelectedYearOption,
    crewsSumBasis
  ]);

  // Comparar Meses: recarrega quando abre o modal e ambos os meses estão escolhidos
  useEffect(() => {
    if (showCompareModal && crewsCompareMonthA && crewsCompareMonthB) {
      loadCrewsCompare();
    }
    // eslint-disable-next-line
  }, [showCompareModal, crewsCompareMonthA, crewsCompareMonthB]);

  // Force chart metric to total_jobs for non-admin users
  useEffect(() => {
    if (user?.role && user.role !== 'admin') {
      // PMs só podem ver métrica de trabalhos
      if (crewsChartMetric !== 'total_jobs') {
        console.log('🔒 PM user - forcing chart metric to total_jobs');
        setCrewsChartMetric('total_jobs');
      }
    }
  }, [user?.role, crewsChartMetric]);

  // Force sort by to total_jobs or total_final for non-admin users
  useEffect(() => {
    if (user?.role && user.role !== 'admin') {
      // PMs só podem ordenar por total_jobs ou total_final
      if (crewsReportSortBy !== 'total_jobs' && crewsReportSortBy !== 'total_final') {
        console.log('🔒 PM user - forcing sort by to total_jobs');
        setCrewsReportSortBy('total_jobs');
      }
    }
  }, [user?.role, crewsReportSortBy]);



  // Load Next Payment Estimate (PM only)
  const loadNextPaymentEstimate = async () => {
    if (user?.role === 'admin') return;
    try {
      setPaymentEstimateLoading(true);
      if (user?.role === 'daily_leader') {
        const { data } = await axios.get(`${API}/daily-leader/next-payment`);
        setNextPaymentEstimate(data);
      } else {
        const { data } = await axios.get(`${API}/payroll/next-payment-estimate`);
        setNextPaymentEstimate(data);
      }
    } catch (e) {} finally { setPaymentEstimateLoading(false); }
  };

  const loadCurrentWeekEstimate = async () => {
    try {
      if (user?.role === 'admin') {
        const { data } = await axios.get(`${API}/admin/payroll/current-week-all`);
        setCurrentWeekEstimate(data);
      } else if (user?.role === 'daily_leader') {
        const { data } = await axios.get(`${API}/daily-leader/current-week`);
        setCurrentWeekEstimate(data);
      } else {
        const { data } = await axios.get(`${API}/payroll/current-week-estimate`);
        setCurrentWeekEstimate(data);
      }
    } catch (e) {}
  };

  
  // Calcular resumo mensal e anual baseado nos dados carregados
  const calculateMonthlyYearlySummary = async (forceMonth = null) => {
    try {
      const token = sessionStorage.getItem('access_token');

      if (!token) return;
      
      // Daily Leader: usar endpoint específico
      if (user?.role === 'daily_leader') {
        const { data: dlReports } = await axios.get(`${API}/daily-leader/weekly-reports`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!dlReports || dlReports.length === 0) {
          setMonthlyYearlySummary({
            month: { total_jobs: 0, total_labor: 0, total_final: 0, lucro: 0, total_expenses: 0 },
            year: { total_jobs: 0, total_labor: 0, total_final: 0, lucro: 0, total_expenses: 0 }
          });
          return;
        }
        
        const monthsSet = new Set();
        let latestMonth = null;
        dlReports.forEach(r => {
          if (r.data_fim) {
            const mk = r.data_fim.substring(0, 7);
            monthsSet.add(mk);
            if (!latestMonth || r.data_fim > latestMonth) latestMonth = r.data_fim;
          }
        });
        
        let targetMonth = forceMonth || selectedMonthForSummary;
        if (!targetMonth && latestMonth) {
          targetMonth = latestMonth.substring(0, 7);
          setSelectedMonthForSummary(targetMonth);
        }
        const targetYear = targetMonth ? targetMonth.substring(0, 4) : new Date().getFullYear().toString();
        
        let monthTotal = 0, monthExpenses = 0, monthWeeks = 0;
        let yearTotal = 0, yearExpenses = 0, yearWeeks = 0;
        
        dlReports.forEach(r => {
          const rMonth = r.data_fim?.substring(0, 7);
          const rYear = r.data_fim?.substring(0, 4);
          if (rMonth === targetMonth) {
            monthTotal += r.valor_total || 0;
            monthExpenses += r.valor_despesas || 0;
            monthWeeks++;
          }
          if (rYear === targetYear) {
            yearTotal += r.valor_total || 0;
            yearExpenses += r.valor_despesas || 0;
            yearWeeks++;
          }
        });
        
        setMonthlyYearlySummary({
          month: { total_jobs: monthWeeks, total_labor: monthTotal - monthExpenses, total_final: monthTotal, lucro: 0, total_expenses: monthExpenses },
          year: { total_jobs: yearWeeks, total_labor: yearTotal - yearExpenses, total_final: yearTotal, lucro: 0, total_expenses: yearExpenses },
          availableMonths: Array.from(monthsSet).sort().reverse()
        });
        return;
      }
      
      // Buscar todos os dados (sem filtro de mês)
      const { data: allWeeklyData } = await axios.get(
        user?.role === 'admin' 
          ? `${API}/admin/weekly-summaries` 
          : `${API}/pm/weekly-report/${user.username}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!allWeeklyData || allWeeklyData.length === 0) {
        setMonthlyYearlySummary({
          month: { total_jobs: 0, total_labor: 0, total_final: 0, lucro: 0, total_expenses: 0 },
          year: { total_jobs: 0, total_labor: 0, total_final: 0, lucro: 0, total_expenses: 0 }
        });
        return;
      }
      
      // Detectar meses disponíveis para o dropdown
      const monthsSet = new Set();
      let latestMonth = null;
      
      if (user?.role === 'admin') {
        allWeeklyData.forEach(week => {
          if (week.week_ending) {
            const monthKey = week.week_ending.substring(0, 7);
            monthsSet.add(monthKey);
            if (!latestMonth || week.week_ending > latestMonth) {
              latestMonth = week.week_ending;
            }
          }
        });
      } else {
        if (allWeeklyData.entries) {
          allWeeklyData.entries.forEach(entry => {
            if (entry.week_ending) {
              const monthKey = entry.week_ending.substring(0, 7);
              monthsSet.add(monthKey);
              if (!latestMonth || entry.week_ending > latestMonth) {
                latestMonth = entry.week_ending;
              }
            }
          });
        }
      }
      
      // Definir mês selecionado se não estiver definido
      let targetMonth = forceMonth || selectedMonthForSummary;
      if (!targetMonth && latestMonth) {
        targetMonth = latestMonth.substring(0, 7);
        setSelectedMonthForSummary(targetMonth);
      }
      
      const targetYear = targetMonth ? targetMonth.substring(0, 4) : new Date().getFullYear().toString();
      
      let monthJobs = 0, monthLabor = 0, monthFinal = 0, monthLucro = 0, monthExpenses = 0;
      let yearJobs = 0, yearLabor = 0, yearFinal = 0, yearLucro = 0, yearExpenses = 0;
      
      // Para ADMIN: usar weekly_summaries
      if (user?.role === 'admin') {
        allWeeklyData.forEach(week => {
          const weekMonth = week.week_ending?.substring(0, 7);
          const weekYear = week.week_ending?.substring(0, 4);
          
          if (weekMonth === targetMonth) {
            monthJobs += week.total_jobs || 0;
            monthLabor += week.total_labor || 0;
            monthFinal += week.total_final || 0;
            monthLucro += week.lucro || 0;
            monthExpenses += week.total_expenses || 0;
          }
          
          if (weekYear === targetYear) {
            yearJobs += week.total_jobs || 0;
            yearLabor += week.total_labor || 0;
            yearFinal += week.total_final || 0;
            yearLucro += week.lucro || 0;
            yearExpenses += week.total_expenses || 0;
          }
        });
      } else {
        // Para PM: processar entries
        if (allWeeklyData.entries) {
          const weeklyGroups = {};
          
          allWeeklyData.entries.forEach(entry => {
            const weekEnding = entry.week_ending;
            if (!weekEnding || !entry.found_match) return;
            
            if (!weeklyGroups[weekEnding]) {
              weeklyGroups[weekEnding] = { total_jobs: 0, total_final: 0 };
            }
            
            weeklyGroups[weekEnding].total_jobs += 1;
            weeklyGroups[weekEnding].total_final += entry.final_value || entry.labor_total || 0;
          });
          
          // Subtrair backcharges e somar adicionais por semana
          const weeklyBackcharges = allWeeklyData.weekly_backcharges || {};
          Object.entries(weeklyBackcharges).forEach(([weekEnding, bcs]) => {
            if (Array.isArray(bcs) && weeklyGroups[weekEnding]) {
              const bcTotal = bcs.reduce((s, b) => s + (b.valor || 0), 0);
              weeklyGroups[weekEnding].total_final -= bcTotal;
            }
          });
          Object.entries(allWeeklyData.weekly_adicionais || {}).forEach(([weekEnding, ads]) => {
            if (Array.isArray(ads) && weeklyGroups[weekEnding]) {
              weeklyGroups[weekEnding].total_final += ads.reduce((s, a) => s + (a.valor || 0), 0);
            }
          });
          
          Object.entries(weeklyGroups).forEach(([weekEnding, data]) => {
            const weekMonth = weekEnding.substring(0, 7);
            const weekYear = weekEnding.substring(0, 4);
            
            if (weekMonth === targetMonth) {
              monthJobs += data.total_jobs;
              monthFinal += data.total_final;
            }
            
            if (weekYear === targetYear) {
              yearJobs += data.total_jobs;
              yearFinal += data.total_final;
            }
          });
        }
      }
      
      setMonthlyYearlySummary({
        month: { total_jobs: monthJobs, total_labor: monthLabor, total_final: monthFinal, lucro: monthLucro, total_expenses: monthExpenses },
        year: { total_jobs: yearJobs, total_labor: yearLabor, total_final: yearFinal, lucro: yearLucro, total_expenses: yearExpenses },
        availableMonths: Array.from(monthsSet).sort().reverse()
      });
      
      console.log('✅ Monthly/Yearly summary calculated:', {
        targetMonth,
        targetYear,
        month: { jobs: monthJobs, labor: monthLabor, final: monthFinal, lucro: monthLucro, expenses: monthExpenses },
        year: { jobs: yearJobs, labor: yearLabor, final: yearFinal, lucro: yearLucro, expenses: yearExpenses }
      });
      
    } catch (error) {
      console.error('❌ Error calculating monthly/yearly summary:', error);
    }
  };

  // Save Report Only Function (apenas clientes encontrados)


  // Load ALL Payment Estimates (ADMIN only)
  const loadAllPaymentEstimates = async () => {
    try {
      setAllPaymentEstimatesLoading(true);
      console.log('💰 [ADMIN] Requisitando estimativas consolidadas...');
      
      const { data } = await axios.get(`${API}/admin/all-payment-estimates`);
      
      console.log('=' .repeat(80));
      console.log('📊 ESTIMATIVAS CONSOLIDADAS:');
      console.log('   Data de Pagamento:', data.payment_date_formatted);
      console.log('   Semana:', data.work_week_start, 'a', data.work_week_end);
      console.log('   Total Geral:', data.total_general);
      data.estimates.forEach((est, idx) => {
        console.log(`   ${idx + 1}. ${est.pm_user}: ${est.total_jobs} jobs - $${est.estimated_payment}`);
      });
      console.log('=' .repeat(80));
      
      setAllPaymentEstimates(data);
    } catch (e) {
      console.error('❌ ERRO:', e);
      toast.error('Erro ao carregar estimativas');
    } finally {
      setAllPaymentEstimatesLoading(false);
    }
  };

  const saveReportOnly = async (forceAction = null) => {
    if (!payrollReport || !payrollReport.is_preview) {
      toast.error('Nenhum dado de preview para salvar ou dados já foram salvos');
      return;
    }

    try {
      setSaveReportLoading(true);
      
      console.log('💾 Salvando relatório no banco de dados...');
      
      // Build entries from payrollData (source of truth - has UPDATED expenses)
      // payrollData is where all user edits are stored during preview
      let cleanProcessedEntries = [];
      
      if (payrollData && payrollData.length > 0) {
        cleanProcessedEntries = payrollData.map(entry => {
          const cleanEntry = {};
          for (const [key, value] of Object.entries(entry || {})) {
            if (value !== null && value !== undefined) {
              if (typeof value === 'object' && !Array.isArray(value)) {
                cleanEntry[key] = { ...value };
              } else {
                cleanEntry[key] = value;
              }
            }
          }
          return cleanEntry;
        });
      }
      
      // Rebuild team_groups from payrollData (has updated expenses)
      const freshTeamGroups = {};
      cleanProcessedEntries.forEach(entry => {
        const team = entry.team_name || 'Unknown';
        if (!freshTeamGroups[team]) freshTeamGroups[team] = { work_entries: [] };
        freshTeamGroups[team].work_entries.push(entry);
      });
      const cleanTeamGroups = JSON.parse(JSON.stringify(freshTeamGroups));
      const cleanTeamSummary = payrollReport.team_summary ?
        JSON.parse(JSON.stringify(payrollReport.team_summary, (key, value) => {
          if (typeof value === 'object' && value !== null && (value._id || value.$oid)) return undefined;
          return value;
        })) : {};
      
      // Use the updated endpoint with conflict detection
      const requestData = {
        report: {
          id: payrollReport.id,
          week_ending: payrollReport.week_ending,
          created_by: user.username,
          created_at: new Date().toISOString(),
          // Include all other report fields (cleaned)
          total_entries: payrollReport.total_entries,
          matched_entries: payrollReport.matched_entries,
          team_summary: cleanTeamSummary,
          team_groups: cleanTeamGroups,
          is_preview: false,
          status: 'completed'
        },
        processed_entries: cleanProcessedEntries
      };
      // Include backcharges and adicionais in report
      requestData.report.backcharges = backcharges;
      requestData.report.adicionais = adicionais;
      
      // Add force_action if specified
      if (forceAction) {
        requestData.force_action = forceAction;
      }
      
      console.log('📤 Sending report data:', {
        report_fields: Object.keys(requestData.report),
        entries_count: cleanProcessedEntries.length,
        first_entry_fields: cleanProcessedEntries[0] ? Object.keys(cleanProcessedEntries[0]) : [],
        force_action: typeof forceAction === 'string' ? forceAction : 'none'
      });
      
      const response = await axios.post(`${API}/admin/payroll/save-to-database`, requestData);
      
      // Handle conflict response
      if (response.data.status === 'conflict') {
        setConflictData(response.data);
        setConflictModalOpen(true);
        return;
      }
      
      if (response.data.status === 'success') {
        // Atualizar o payrollReport para indicar que foi salvo
        setPayrollReport(prev => ({
          ...prev,
          is_preview: false,
          status: 'completed'
        }));
        
        const actionMessage = forceAction 
          ? `✅ Relatório salvo com sucesso! (${forceAction === 'overwrite' ? 'Sobrescrever' : 'Adicionar mudanças'})`
          : '✅ Relatório salvo no banco de dados com sucesso!';
        
        toast.success(actionMessage);
        
        // Recarregar lista de reports e summary
        if (user?.username === 'ADMIN') {
          loadPayrollReports();
          loadWeeklyReportSummary();
        }
      }
      
    } catch (e) {
      console.error('❌ Error saving report:', e);
      toast.error(getErrMsg(e, 'Erro ao salvar relatório'));
    } finally {
      setSaveReportLoading(false);
    }
  };

  // Auto-load admin data when accessing admin tab
  useEffect(() => {
    if (activeTab === 'admin' && user?.role === 'admin') {
      loadAdminUsers();
      loadTicketStats();
      loadAllTeamLeaders(); // Load team leaders for admin
      // Removed: loadArbitrationFiles() - carrega apenas quando necessário na aba Docs
      
      if (adminSubTab === 'dados' && selectedPM) {
        loadAdminPMData(selectedPM);
      }
      
      if (adminSubTab === 'emails' && selectedPM) {
        loadAdminPMRecipients(selectedPM);
      }
    }
    // Load payroll data when accessing payroll tab
    if (user?.role === 'admin' && activeTab === 'payroll') {
      loadTeamPercentages();
      if (payrollSubTab === 'reports') {
        loadPayrollReports();
      }
    }
  }, [activeTab, user?.role, adminSubTab, selectedPM, payrollSubTab]);

  const loadAdminPMData = async (pm) => {
    try {
      setAdminPMLoading(true);
      const [d1, d2, d3, d4] = await Promise.all([
        axios.get(`${API}/admin/pm/${pm}/labor-bills`),
        axios.get(`${API}/admin/pm/${pm}/labor-bills-history`),
        axios.get(`${API}/admin/pm/${pm}/trash`),
        axios.get(`${API}/admin/email-logs`, { params: { pm } }),
      ]);
      setAdminPMDrafts(d1.data || []);
      setAdminPMHistory(d2.data || []);
      setAdminPMTrash(d3.data || []);
      setAdminPMEmailLogs(d4.data || []);
    } catch (e) {
      toast.error(getErrMsg(e, "Falha ao carregar dados do PM"));
    } finally {
      setAdminPMLoading(false);
    }
  };

  const loadAdminPMRecipients = async (pm) => {
    try {
      const { data } = await axios.get(`${API}/admin/pm/${pm}/email-recipients`);
      setAdminPMRecipients(data || []);
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao listar destinatários do PM'));
    }
  };
  const adminAddRecipientToPM = async (pm) => {
    if (!adminNewRecipient.name || !adminNewRecipient.email) return toast.error('Preencha nome e e-mail');
    try {
      await axios.post(`${API}/admin/pm/${pm}/email-recipients`, { name: adminNewRecipient.name, email: adminNewRecipient.email });
      setAdminNewRecipient({ name: '', email: '' });
      await loadAdminPMRecipients(pm);
      toast.success('Destinatário adicionado ao PM');
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao adicionar destinatário ao PM'));
    }
  };
  const adminAddRecipientsBulk = async () => {
    if (!adminBulkRecipient.name || !adminBulkRecipient.email || (adminBulkRecipient.pms||[]).length === 0) {
      return toast.error('Informe nome, email e selecione ao menos um PM');
    }
    try {
      await axios.post(`${API}/admin/email-recipients/bulk`, adminBulkRecipient);
      setAdminBulkRecipient({ name: '', email: '', pms: [] });
      await loadAdminPMRecipients(selectedPM);
      toast.success('Destinatário adicionado globalmente');
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha no cadastro em massa'));
    }
  };
  const adminDeleteRecipientFromPM = async (pm, rid) => {
    try {
      await axios.delete(`${API}/admin/pm/${pm}/email-recipients/${rid}`);
      await loadAdminPMRecipients(pm);
      toast.success('Destinatário removido');
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao remover destinatário'));
    }
  };
  const adminSaveRecipientEdit = async () => {
    if (!editingRecipient?.name || !editingRecipient?.email) return toast.error('Preencha nome e e-mail');
    try {
      await axios.patch(`${API}/email-recipients/${editingRecipient.id}`, {
        name: editingRecipient.name.trim(),
        email: editingRecipient.email.trim(),
      });
      setEditingRecipient(null);
      await loadAdminPMRecipients(selectedPM);
      toast.success('Destinatário atualizado');
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao atualizar destinatário'));
    }
  };

  const adminDownloadPMpdf = async (pm, billId) => {
    try {
      const res = await axios.get(`${API}/admin/pm/${pm}/labor-bills/${billId}/pdf?t=${Date.now()}`);
      const { pdf_data, filename } = res.data;
      const byteArray = Uint8Array.from(atob(pdf_data), c => c.charCodeAt(0));
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(getErrMsg(e, "Falha ao baixar PDF"));
    }
  };

  const adminRestorePMTrash = async (pm, id) => {
    try {
      await axios.post(`${API}/admin/pm/${pm}/trash/${id}/restore`);
      toast.success("Restaurado");
      await loadAdminPMData(pm);
    } catch (e) {
      toast.error(getErrMsg(e, "Falha ao restaurar"));
    }
  };

  const adminDeletePMTrash = async (pm, id) => {
    if (!window.confirm("Apagar definitivamente?")) return;
    try {
      await axios.delete(`${API}/admin/pm/${pm}/trash/${id}`);
      toast.success("Apagado permanentemente");
      await loadAdminPMData(pm);
    } catch (e) {
      toast.error(getErrMsg(e, "Falha ao apagar permanentemente"));
    }
  };

  // Admin > Suporte helpers
  const loadAdminTickets = async () => {
    try {
      setAdminTicketsLoading(true);
      const { data } = await axios.get(`${API}/admin/tickets`, { params: { pm: adminTicketFilter.pm || undefined, status: adminTicketFilter.status || undefined } });
      setAdminTickets(data || []);
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao carregar tickets'));
    } finally {
      setAdminTicketsLoading(false);
    }
  };
  const openAdminTicket = async (t) => {
    try {
      const { data } = await axios.get(`${API}/admin/tickets/${t.id}`);
      setAdminTicketView(data);
      setAdminTicketReply('');
      setAdminTicketFiles([]);
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao abrir ticket'));
    }
  };
  const replyAdminTicket = async () => {
    if (!adminTicketView?.id) return;
    const form = new FormData();
    form.append('text', adminTicketReply || '');
    (adminTicketFiles || []).forEach((f) => form.append('files', f));
    try {
      await axios.post(`${API}/admin/tickets/${adminTicketView.id}/messages`, form, {
        headers: {
          'Authorization': axios.defaults.headers.common['Authorization'],
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Resposta enviada');
      await openAdminTicket({ id: adminTicketView.id });
      await loadAdminTickets();
      setAdminTicketReply('');
      setAdminTicketFiles([]);
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao responder'));
    }
  };
  const updateAdminTicketStatus = async (status) => {
    if (!adminTicketView?.id) return;
    try {
      await axios.put(`${API}/admin/tickets/${adminTicketView.id}/status`, { status });
      toast.success('Status atualizado');
      await openAdminTicket({ id: adminTicketView.id });
      await loadAdminTickets();
    } catch (e) {
      toast.error(getErrMsg(e, 'Falha ao atualizar status'));
    }
  };

  useEffect(() => {
    if (user?.role === 'admin' && activeTab === 'admin' && adminSubTab === 'suporte') {
      loadAdminTickets();
    }
  }, [user?.role, activeTab, adminSubTab, adminTicketFilter.pm, adminTicketFilter.status]);

  // Load push notifications data when accessing notifications tab
  useEffect(() => {
    if (user?.role === 'admin' && activeTab === 'admin' && adminSubTab === 'notificacoes') {
      loadPushNotificationConfigs();
      loadPushNotificationHistory();
      loadPushNotificationStatus();
    }
  }, [user?.role, activeTab, adminSubTab]);

  // Load activity reports when accessing reports tab OR admin home
  useEffect(() => {
    if (user?.role === 'admin' && activeTab === 'admin' && adminSubTab === 'relatorios') {
      loadActivityLogs();
      loadActivityStats();
    }
    if (user?.role === 'admin' && activeTab === 'home') {
      loadActivityLogs();
      loadActivityStats();
      loadTicketStats();
      loadPayrollReports(); // Load payroll reports for home summary
    }
    if (user?.role === 'admin' && activeTab === 'admin' && adminSubTab === 'suporte') {
      loadAdminTickets();
      loadTicketStats();
    }
  }, [user?.role, activeTab, adminSubTab]);

  // Reload activity logs when filters change (for admin home and reports)
  useEffect(() => {
    if (user?.role === 'admin' && (
      (activeTab === 'admin' && adminSubTab === 'relatorios') || 
      activeTab === 'home'
    )) {
      loadActivityLogs();
    }
  }, [activityFilters.user, activityFilters.action, activityFilters.page]);

  // Load support tickets when accessing support tab
  useEffect(() => {
    if (user?.role !== 'admin' && activeTab === 'support') {
      loadMyTickets();
    }
  }, [user?.role, activeTab]);

  // Load user ticket stats for dashboard
  useEffect(() => {
    if (user && user.role !== 'admin') {
      loadUserTicketStats();
    }
  }, [user?.username, activeTab]);

  // Admin API calls
  const loadAdminUsers = async () => {
    if (user?.role !== 'admin') return;
    try {
      setAdminLoading(true);
      const { data } = await axios.get(`${API}/admin/users`);
      setAdminUsers(data);
      try {
        const r = await axios.get(`${API}/admin/settings/min-native-version`);
        setMinNativeVersion(r.data.version || '');
        setMinNativeVersionInput(r.data.version || '');
      } catch (e) { /* ignore */ }
    } catch (e) {
      toast.error("Falha ao listar usuários");
    } finally {
      setAdminLoading(false);
    }
  };

  const saveMinNativeVersion = async () => {
    try {
      const v = minNativeVersionInput.trim();
      await axios.put(`${API}/admin/settings/min-native-version`, { version: v });
      setMinNativeVersion(v);
      toast.success(v ? `Versão mínima salva: v${v}` : 'Alerta de versão desativado');
    } catch (e) {
      toast.error('Erro ao salvar versão mínima');
    }
  };

  const createAdminUser = async () => {
    if (!adminCreate.username || !adminCreate.password) return toast.error("Preencha usuário e senha");
    try {
      setAdminLoading(true);
      await axios.post(`${API}/admin/users`, adminCreate);
      toast.success("Usuário criado");
      setAdminCreate({ username: "", password: "", role: "user", first_name: "", last_name: "", phone: "", address: "" });
      setAdminCreateOpen(false);
      await loadAdminUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao criar usuário");
    } finally {
      setAdminLoading(false);
    }
  };

  const openEditAdminUser = (u) => {
    setAdminEdit(u);
    setAdminEditData({ full_name: u.full_name || "", first_name: u.first_name || "", last_name: u.last_name || "", phone: u.phone || "", address: u.address || "", role: u.role || 'user', is_active: !!u.is_active });
    setAdminEditOpen(true);
  };

  const saveEditAdminUser = async () => {
    if (!adminEdit) return;
    try {
      setAdminLoading(true);
      
      // Update user data
      const fullName = `${adminEditData.first_name || ''} ${adminEditData.last_name || ''}`.trim();
      await axios.put(`${API}/admin/users/${adminEdit.username}`, {
        ...adminEditData,
        full_name: fullName || adminEditData.full_name,
      });
      
      // Se for um PM (PM1-PM10), sincronizar com Team Leaders
      if (adminEdit.username.match(/^PM\d+$/)) {
        const fullName = `${adminEditData.first_name || ''} ${adminEditData.last_name || ''}`.trim();
        
        const teamLeaderData = {
          full_name: fullName,
          phone: adminEditData.phone,
          address: adminEditData.address
        };
        
        console.log(`🔄 Sincronizando ${adminEdit.username} com Team Leaders:`, teamLeaderData);
        
        // Update Team Leader profile
        await axios.post(`${API}/admin/team-leaders/${adminEdit.username}`, teamLeaderData);
        
        console.log(`✅ Team Leader ${adminEdit.username} sincronizado`);
      }
      
      toast.success("Usuário atualizado e sincronizado com Team Leaders");
      setAdminEdit(null);
      await loadAdminUsers();
      
      // Reload team leaders if on that tab
      if (adminSubTab === 'leaders') {
        await loadAllTeamLeaders();
      }
    } catch (e) {
      console.error('Erro ao atualizar usuário:', e);
      toast.error(e.response?.data?.detail || "Falha ao atualizar usuário");
    } finally {
      setAdminLoading(false);
    }
  };

  const resetAdminPassword = async (username) => {
    const np = window.prompt(`Nova senha para ${username}`);
    if (!np) return;
    try {
      setAdminLoading(true);
      await axios.post(`${API}/admin/users/${username}/reset-password`, { new_password: np });
      toast.success("Senha redefinida");
    } catch (e) {
      toast.error("Falha ao redefinir senha");
    } finally {
      setAdminLoading(false);
    }
  };

  const toggleAdminActive = async (u) => {
    try {
      setAdminLoading(true);
      await axios.post(`${API}/admin/users/${u.username}/toggle-active`, true === u.is_active ? { is_active: false } : { is_active: true });
      toast.success("Status atualizado");
      await loadAdminUsers();
    } catch (e) {
      toast.error(getErrMsg(e, "Falha ao atualizar status"));
    } finally {
      setAdminLoading(false);
    }
  };

  const deleteAdminUser = async (u) => {
    if (!u?.username) return toast.error("Usuário inválido");
    if (!window.confirm(`Apagar o usuário ${u.username}?`)) return;
    try {
      setAdminLoading(true);
      toast.info(`Apagando ${u.username}...`);
      try {
        await axios.delete(`${API}/admin/users/${u.username}`);
      } catch (err) {
        await axios.post(`${API}/admin/users/${u.username}/delete`);
      }
      toast.success("Usuário apagado");
      await loadAdminUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao apagar usuário");
    } finally {
      setAdminLoading(false);
    }
  };

  // Admin - Push Notifications functions
  const loadPushNotificationConfigs = async () => {
    if (user?.role !== 'admin') return;
    try {
      setPushNotificationLoading(true);
      const { data } = await axios.get(`${API}/admin/push-notifications/config`);
      setPushNotificationConfigs(data);
    } catch (e) {
      toast.error("Falha ao carregar configurações de notificação");
    } finally {
      setPushNotificationLoading(false);
    }
  };

  const loadPushNotificationHistory = async () => {
    if (user?.role !== 'admin') return;
    try {
      const { data } = await axios.get(`${API}/admin/push-notifications/history?limit=50`);
      setPushNotificationHistory(data);
    } catch (e) {
      toast.error("Falha ao carregar histórico de notificações");
    }
  };

  const loadPushNotificationStatus = async () => {
    if (user?.role !== 'admin') return;
    try {
      const { data } = await axios.get(`${API}/admin/push-notification-status`);
      setPushNotificationStatus(data);
    } catch (e) {
      toast.error("Falha ao carregar status do Firebase");
    }
  };

  const updatePushNotificationConfig = async (eventType, configData) => {
    if (user?.role !== 'admin') return;
    try {
      setPushNotificationLoading(true);
      await axios.put(`${API}/admin/push-notifications/config/${eventType}`, configData);
      toast.success("Configuração atualizada");
      await loadPushNotificationConfigs();
    } catch (e) {
      toast.error("Falha ao atualizar configuração");
    } finally {
      setPushNotificationLoading(false);
    }
  };

  const sendManualPushNotification = async () => {
    if (user?.role !== 'admin') return;
    if (!manualNotificationForm.title || !manualNotificationForm.message) {
      return toast.error("Preencha título e mensagem");
    }
    try {
      setPushNotificationLoading(true);
      
      // ADMIN sends normal notification (NO modal for admin)
      const { data } = await axios.post(`${API}/admin/push-notifications/send`, manualNotificationForm);
      toast.success(`Notificação enviada para ${data.recipients_count} usuário(s)`);
      
      // Store notification in persistent queue for PM to receive later
      const notificationData = {
        id: Date.now() + Math.random(),
        title: manualNotificationForm.title,
        message: manualNotificationForm.message,
        from: 'Admin',
        timestamp: Date.now(),
        recipients: manualNotificationForm.recipients,
        seen: false
      };
      
      // Get existing notification queue
      const existingQueue = JSON.parse(localStorage.getItem('notification_queue') || '[]');
      
      // Add new notification to queue
      existingQueue.push(notificationData);
      
      // Save updated queue
      localStorage.setItem('notification_queue', JSON.stringify(existingQueue));
      
      console.log('📬 Notification added to persistent queue:', notificationData.title);
      
      setManualNotificationForm({ title: '', message: '', recipients: 'all' });
      await loadPushNotificationHistory();
      
    } catch (e) {
      toast.error("Falha ao enviar notificação: " + (e.response?.data?.detail || e.message));
    } finally {
      setPushNotificationLoading(false);
    }
  };

  // Admin - Import functions
  const handleImportFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setImportFile(file);
      setImportPreviewData(null);
      setImportEditData(null);
    } else if (file) {
      toast.error("Apenas arquivos PDF são aceitos");
      event.target.value = '';
    }
  };

  const previewImportPDF = async () => {
    if (!importFile) return toast.error("Selecione um arquivo PDF");
    
    try {
      setImportLoading(true);
      const formData = new FormData();
      formData.append('file', importFile);

      // Step 1: Get preview data
      const { data: previewData } = await axios.post(`${API}/admin/import/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportPreviewData(previewData);
      
      // Update PM dropdown if PM was extracted from PDF
      if (previewData.pm_code) {
        setImportPM(previewData.pm_code);
      }
      
      setImportEditData({
        install_date: previewData.install_date || '',
        job_name: previewData.job_name || '',
        order_number: previewData.order_number || '',
        pm_code: previewData.pm_code || importPM,
        total_windows: parseInt(previewData.total_windows) || 0,
        total_doors: parseInt(previewData.total_doors) || 0,
        items: previewData.items || [],
        extra_items: previewData.extra_items || [],
        notes: previewData.notes || ''
      });

      // Step 2: Automatic comparison and commit if totals match
      const calculatedTotal = (previewData.items || []).reduce((sum, item) => sum + item.total, 0) + 
                             (previewData.extra_items || []).reduce((sum, item) => sum + item.total, 0);
      const pdfTotal = previewData.grand_total || 0;

      if (Math.abs(calculatedTotal - pdfTotal) < 0.01) {
        // Totals match exactly - auto import
        const request = {
          install_date: previewData.install_date || '',
          job_name: previewData.job_name || '',
          order_number: previewData.order_number || '',
          pm_code: previewData.pm_code || importPM,
          total_windows: parseInt(previewData.total_windows) || 0,
          total_doors: parseInt(previewData.total_doors) || 0,
          items: previewData.items || [],
          extra_items: previewData.extra_items || [],
          notes: previewData.notes || '',
          grand_total: previewData.grand_total
        };

        const { data: commitData } = await axios.post(`${API}/admin/import/commit-auto`, request);
        
        if (commitData.auto_committed) {
          toast.success(`✅ PDF importado automaticamente para ${previewData.pm_code || importPM} - Totais conferem: $${pdfTotal.toFixed(2)}`);
          // Clear form
          setImportFile(null);
          setImportPreviewData(null);
          setImportEditData(null);
          setImportAutoCommitResults(null);
          const fileInput = document.getElementById('import-file-input');
          if (fileInput) fileInput.value = '';
        } else {
          // Fallback to manual review (shouldn't happen but safety net)
          setImportAutoCommitResults({ single: commitData });
          toast.warning("Erro na importação automática - revise os dados");
        }
      } else {
        // Totals don't match - show for manual review
        setImportAutoCommitResults({ 
          single: {
            success: false,
            auto_committed: false,
            message: `Totais não conferem. PDF: $${pdfTotal.toFixed(2)}, Calculado: $${calculatedTotal.toFixed(2)}, Diferença: $${Math.abs(pdfTotal - calculatedTotal).toFixed(2)}`,
            pdf_total: pdfTotal,
            calculated_total: calculatedTotal,
            difference: Math.abs(pdfTotal - calculatedTotal)
          }
        });
        toast.warning(`⚠️ Totais não conferem - revise os dados (Diferença: $${Math.abs(pdfTotal - calculatedTotal).toFixed(2)})`);
      }
      
    } catch (e) {
      toast.error(getErrMsg(e, "Falha ao analisar PDF"));
    } finally {
      setImportLoading(false);
    }
  };

  const commitImportPDF = async () => {
    if (!importEditData) {
      toast.error("Nenhum dado para importar");
      return;
    }

    try {
      setImportCommitLoading(true);
      
      // Use PM code from PDF data (not the dropdown selection)
      const commitData = {
        ...importEditData,
        pm_code: importEditData.pm_code || importPM,
        total_windows: parseInt(importEditData.total_windows) || 0,
        total_doors: parseInt(importEditData.total_doors) || 0
      };

      await axios.post(`${API}/admin/import/commit`, commitData);
      
      toast.success(`Labor Bill importado com sucesso para ${importEditData.pm_code || importPM}`);
      
      // Reset form
      setImportFile(null);
      setImportPreviewData(null);
      setImportEditData(null);
      
      // Clear file input
      const fileInput = document.getElementById('import-file-input');
      if (fileInput) {
        fileInput.value = '';
      }
      
    } catch (e) {
      toast.error(getErrMsg(e, "Erro ao importar Labor Bill"));
    } finally {
      setImportCommitLoading(false);
    }
  };

  const updateImportEditData = (field, value) => {
    setImportEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateImportItem = (index, field, value) => {
    setImportEditData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const updateImportExtraItem = (index, field, value) => {
    setImportEditData(prev => ({
      ...prev,
      extra_items: prev.extra_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Calculate total sum of items for comparison
  const calculateImportItemsTotal = () => {
    if (!importEditData) return 0;
    const itemsTotal = (importEditData.items || []).reduce((sum, item) => sum + (item.total || 0), 0);
    const extraItemsTotal = (importEditData.extra_items || []).reduce((sum, item) => sum + (item.total || 0), 0);
    return itemsTotal + extraItemsTotal;
  };

  // Remove item from import data
  const removeImportItem = (index) => {
    setImportEditData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Remove extra item from import data
  const removeImportExtraItem = (index) => {
    setImportEditData(prev => ({
      ...prev,
      extra_items: prev.extra_items.filter((_, i) => i !== index)
    }));
  };

  // Add new item to import data
  const addNewImportItem = (itemTemplate) => {
    const newItem = {
      description: itemTemplate.description,
      qty: 1,
      unit_price: itemTemplate.unit_price,
      total: itemTemplate.unit_price,
      category: itemTemplate.category || "left",
      matched: true
    };
    
    setImportEditData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  // Add new extra item to import data
  const addNewImportExtraItem = (description = "") => {
    const newExtraItem = {
      description: description || "Novo Item Extra",
      qty: 1,
      unit_price: 0,
      total: 0
    };
    
    setImportEditData(prev => ({
      ...prev,
      extra_items: [...prev.extra_items, newExtraItem]
    }));
  };

  // Empty Trash Functions
  const emptyTrash = async () => {
    try {
      setEmptyTrashLoading(true);
      
      // Use PM-specific endpoint when in admin data view
      const endpoint = selectedPM 
        ? `${API}/admin/pm/${selectedPM}/trash/empty`
        : `${API}/admin/trash/empty`;
      
      console.log(`🗑️ Emptying trash for: ${selectedPM || 'all'} using endpoint: ${endpoint}`);
      console.log(`🗑️ Current PM: ${selectedPM}, User: ${user?.username}, Role: ${user?.role}`);
      
      if (!selectedPM) {
        toast.error('Nenhum PM selecionado');
        return;
      }
      
      const { data } = await axios.delete(endpoint);
      console.log('✅ Trash empty response:', data);
      
      toast.success(data.message || `Lixeira de ${selectedPM} esvaziada com sucesso`);
      setEmptyTrashOpen(false);
      setEmptyTrashConfirmText('');
      
      // Reload data for selected PM
      console.log(`🔄 Reloading data for ${selectedPM}`);
      await loadAdminPMData(selectedPM);
      
    } catch (e) {
      console.error('❌ Error emptying trash:', e);
      console.error('❌ Error response:', e.response?.data);
      console.error('❌ Error status:', e.response?.status);
      toast.error(getErrMsg(e, 'Falha ao esvaziar lixeira'));
    } finally {
      setEmptyTrashLoading(false);
    }
  };

  // Multiple PDF Functions
  const handleImportMultipleFiles = (event) => {
    const files = Array.from(event.target.files);
    setImportFiles(files);
    setImportMultiplePreviewData(null);
    setImportMultipleEditData([]);
    setImportAutoCommitResults(null);
  };

  const previewMultiplePDFs = async () => {
    if (!importFiles || importFiles.length === 0) return toast.error("Selecione arquivos PDF");
    
    try {
      setImportLoading(true);
      const formData = new FormData();
      importFiles.forEach(file => {
        formData.append('files', file);
      });

      // Step 1: Get preview data for all PDFs
      const { data } = await axios.post(`${API}/admin/import/preview-multiple`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportMultiplePreviewData(data);
      
      // Step 2: Prepare edit data and check for auto-import
      const editData = data.results.map((result, index) => ({
        filename: result.filename,
        success: result.success,
        error: result.error,
        data: result.success ? {
          install_date: result.data.install_date || new Date().toISOString().split('T')[0],
          job_name: result.data.job_name || `Job-${Date.now()}-${index}`,
          order_number: result.data.order_number || `ORD-${Date.now()}-${index}`,
          pm_code: result.data.pm_code || importPM,
          total_windows: parseInt(result.data.total_windows) || 0,
          total_doors: parseInt(result.data.total_doors) || 0,
          items: result.data.items || [],
          extra_items: result.data.extra_items || [],
          notes: result.data.notes || '',
          grand_total: result.data.grand_total || 0
        } : null
      }));
      setImportMultipleEditData(editData);
      
      // Step 3: Auto-process PDFs with exact totals
      const successfulPDFs = editData.filter(item => item.success && item.data);
      let autoCommitRequests = [];
      let manualReviewPDFs = [];
      
      for (const pdfData of successfulPDFs) {
        const calculatedTotal = (pdfData.data.items || []).reduce((sum, item) => sum + item.total, 0) + 
                               (pdfData.data.extra_items || []).reduce((sum, item) => sum + item.total, 0);
        const pdfTotal = pdfData.data.grand_total || 0;
        
        if (Math.abs(calculatedTotal - pdfTotal) < 0.01) {
          // Totals match - prepare for auto-commit
          autoCommitRequests.push({
            filename: pdfData.filename,
            data: {
              install_date: pdfData.data.install_date || new Date().toISOString().split('T')[0],
              job_name: pdfData.data.job_name || `Job-${Date.now()}`,
              order_number: pdfData.data.order_number || `ORD-${Date.now()}`,
              pm_code: pdfData.data.pm_code || importPM,
              total_windows: parseInt(pdfData.data.total_windows) || 0,
              total_doors: parseInt(pdfData.data.total_doors) || 0,
              items: pdfData.data.items || [],
              extra_items: pdfData.data.extra_items || [],
              notes: pdfData.data.notes || '',
              grand_total: pdfData.data.grand_total || 0
            }
          });
        } else {
          // Totals don't match - needs manual review
          manualReviewPDFs.push({
            filename: pdfData.filename,
            pdf_total: pdfTotal,
            calculated_total: calculatedTotal,
            difference: Math.abs(pdfTotal - calculatedTotal)
          });
        }
      }
      
      // Step 4: Process auto-commits
      if (autoCommitRequests.length > 0) {
        try {
          const requests = autoCommitRequests.map(item => item.data);
          const { data: commitData } = await axios.post(`${API}/admin/import/commit-multiple`, requests);
          
          const autoCommittedCount = commitData.results.filter(r => r.result.auto_committed).length;
          
          if (autoCommittedCount > 0) {
            toast.success(`✅ ${autoCommittedCount} PDF(s) importados automaticamente (totais conferem)`);
          }
          
          setImportAutoCommitResults(commitData);
        } catch (error) {
          toast.error("Erro na importação automática de alguns PDFs");
        }
      }
      
      // Step 5: Show summary
      if (manualReviewPDFs.length > 0) {
        const totalDifference = manualReviewPDFs.reduce((sum, pdf) => sum + pdf.difference, 0);
        toast.warning(`⚠️ ${manualReviewPDFs.length} PDF(s) precisam de revisão manual (diferenças nos totais)`);
        
        // Store manual review info for display
        setImportAutoCommitResults(prev => ({
          ...prev,
          manualReview: manualReviewPDFs
        }));
      }
      
      if (autoCommitRequests.length === 0 && manualReviewPDFs.length === 0) {
        toast.info(`${data.results.filter(r => r.success).length} PDFs analisados com sucesso`);
      }
      
    } catch (e) {
      toast.error(getErrMsg(e, "Falha ao analisar PDFs"));
    } finally {
      setImportLoading(false);
    }
  };

  const commitMultiplePDFs = async () => {
    if (!importMultipleEditData || importMultipleEditData.length === 0) return;
    
    try {
      setImportCommitLoading(true);
      const requests = importMultipleEditData
        .filter(item => item.success && item.data)
        .map(item => ({
          install_date: item.data.install_date,
          job_name: item.data.job_name,
          order_number: item.data.order_number,
          pm_code: item.data.pm_code || importPM,
          total_windows: parseInt(item.data.total_windows) || 0,
          total_doors: parseInt(item.data.total_doors) || 0,
          items: item.data.items,
          extra_items: item.data.extra_items,
          notes: item.data.notes,
          grand_total: item.data.grand_total
        }));

      const { data } = await axios.post(`${API}/admin/import/commit-multiple`, requests);
      setImportAutoCommitResults(data);
      
      const successCount = data.results.filter(r => r.result.success).length;
      const autoCommitCount = data.results.filter(r => r.result.auto_committed).length;
      
      if (autoCommitCount > 0) {
        toast.success(`${autoCommitCount} PDFs importados automaticamente (totais exatos)`);
      }
      if (successCount > autoCommitCount) {
        toast.warning(`${successCount - autoCommitCount} PDFs processados mas com diferenças nos totais`);
      }
    } catch (e) {
      toast.error(getErrMsg(e, "Falha ao importar PDFs"));
    } finally {
      setImportCommitLoading(false);
    }
  };

  // Funções para seleção de itens da lista
  const loadAvailableItems = () => {
    const allItems = [];
    
    // Adicionar itens principais (leftColumn + rightColumn)
    leftColumnSubgroups.forEach((subgroup) => {
      subgroup.items.forEach((item) => {
        allItems.push({
          ...item,
          category: 'main',
          subgroup: subgroup.title
        });
      });
    });
    
    rightColumnSubgroups.forEach((subgroup) => {
      subgroup.items.forEach((item) => {
        allItems.push({
          ...item,
          category: 'main',
          subgroup: subgroup.title
        });
      });
    });
    
    // Adicionar itens extras comuns (Trip Charges, etc.)
    const extraItems = [
      { description: "Trip Charge 50", unit_price: 50.0, category: 'extra', subgroup: 'Project Extras' },
      { description: "Trip Charge 75", unit_price: 75.0, category: 'extra', subgroup: 'Project Extras' },
      { description: "Trip Charge 100", unit_price: 100.0, category: 'extra', subgroup: 'Project Extras' },
      { description: "Hotel", unit_price: 0.0, category: 'extra', subgroup: 'Extra Labor' },
      { description: "Food", unit_price: 0.0, category: 'extra', subgroup: 'Extra Labor' },
      { description: "Gas", unit_price: 0.0, category: 'extra', subgroup: 'Extra Labor' },
      { description: "Materials", unit_price: 0.0, category: 'extra', subgroup: 'Extra Labor' }
    ];
    
    allItems.push(...extraItems);
    
    setAvailableItems(allItems);
  };

  const openItemSelector = (mode) => {
    loadAvailableItems();
    setItemSelectionMode(mode);
    setItemSearchTerm(''); // Limpar busca
    setShowItemSelectionModal(true);
  };

  // Filtrar itens baseado no termo de busca
  const filterItemsBySearch = (items, searchTerm) => {
    if (!searchTerm.trim()) return items;
    
    const term = searchTerm.toLowerCase();
    return items.filter(item => 
      item.description.toLowerCase().includes(term) ||
      item.subgroup?.toLowerCase().includes(term)
    );
  };

  const selectItemFromList = (selectedItem) => {
    // Check if we're editing multiple PDFs or single PDF
    if (editingMultiplePdfIndex !== null) {
      // Multiple PDF editing mode
      const newData = [...importMultipleEditData];
      
      if (itemSelectionMode === 'main') {
        const currentItems = newData[editingMultiplePdfIndex].data.items || [];
        currentItems.push({
          description: selectedItem.description,
          qty: 1,
          unit_price: selectedItem.unit_price,
          total: selectedItem.unit_price,
          category: selectedItem.category,
          subgroup: selectedItem.subgroup
        });
        newData[editingMultiplePdfIndex].data.items = currentItems;
      } else {
        const currentExtraItems = newData[editingMultiplePdfIndex].data.extra_items || [];
        currentExtraItems.push({
          description: selectedItem.description,
          qty: 1,
          unit_price: selectedItem.unit_price,
          total: selectedItem.unit_price,
          category: selectedItem.category,
          subgroup: selectedItem.subgroup
        });
        newData[editingMultiplePdfIndex].data.extra_items = currentExtraItems;
      }
      
      setImportMultipleEditData(newData);
    } else {
      // Single PDF editing mode
      if (itemSelectionMode === 'main') {
        const currentItems = importEditData?.items || [];
        currentItems.push({
          description: selectedItem.description,
          qty: 1,
          unit_price: selectedItem.unit_price,
          total: selectedItem.unit_price,
          category: selectedItem.category,
          subgroup: selectedItem.subgroup
        });
        setImportEditData(prev => ({
          ...prev,
          items: currentItems
        }));
      } else {
        const currentExtraItems = importEditData?.extra_items || [];
        currentExtraItems.push({
          description: selectedItem.description,
          qty: 1,
          unit_price: selectedItem.unit_price,
          total: selectedItem.unit_price,
          category: selectedItem.category,
          subgroup: selectedItem.subgroup
        });
        setImportEditData(prev => ({
          ...prev,
          extra_items: currentExtraItems
        }));
      }
    }
    
    setShowItemSelectionModal(false);
    toast.success(`Item "${selectedItem.description}" adicionado com sucesso!`);
  };

  // Funções para edição individual de PDFs múltiplos
  const openMultiplePdfEditor = (index) => {
    setEditingMultiplePdfIndex(index);
    setShowMultiplePdfEditModal(true);
  };

  const closeMultiplePdfEditor = () => {
    setEditingMultiplePdfIndex(null);
    setShowMultiplePdfEditModal(false);
  };

  const saveAndImportMultiplePdf = async () => {
    if (editingMultiplePdfIndex === null) return;
    
    try {
      setImportCommitLoading(true);
      
      const currentData = importMultipleEditData[editingMultiplePdfIndex].data;
      
      // Prepare import request with data validation
      const request = {
        install_date: currentData.install_date || new Date().toISOString().split('T')[0],
        job_name: currentData.job_name || `Job-${Date.now()}`,
        order_number: currentData.order_number || `ORD-${Date.now()}`,
        pm_code: currentData.pm_code || importPM,
        total_windows: parseInt(currentData.total_windows) || 0,
        total_doors: parseInt(currentData.total_doors) || 0,
        items: (currentData.items || []).map(item => ({
          description: item.description || 'Item sem descrição',
          qty: parseInt(item.qty) || 0, // 0 is valid
          unit_price: parseFloat(item.unit_price) || 0.0, // 0 is valid
          total: parseFloat(item.total) || 0.0, // 0 is valid
          category: item.category || 'left'
        })),
        extra_items: (currentData.extra_items || []).map(item => ({
          description: item.description || 'Item extra sem descrição',
          qty: parseInt(item.qty) || 0, // 0 is valid
          unit_price: parseFloat(item.unit_price) || 0.0, // 0 is valid
          total: parseFloat(item.total) || 0.0 // 0 is valid
        })),
        notes: currentData.notes || '',
        grand_total: parseFloat(currentData.grand_total) || 0
      };

      console.log('🔍 Request data being sent:', JSON.stringify(request, null, 2));

      // Import the labor bill
      await axios.post(`${API}/admin/import/commit`, request);
      
      toast.success(`✅ PDF "${importMultipleEditData[editingMultiplePdfIndex].filename}" importado com sucesso!`);
      
      // Remove this PDF from the list
      const newEditData = importMultipleEditData.filter((_, i) => i !== editingMultiplePdfIndex);
      setImportMultipleEditData(newEditData);
      
      // Close modal
      closeMultiplePdfEditor();
      
      // Check if there are more PDFs to edit
      if (newEditData.filter(f => f.success).length > 0) {
        toast.info(`${newEditData.filter(f => f.success).length} PDF(s) restantes para editar`);
      } else {
        toast.success('🎉 Todos os PDFs foram importados com sucesso!');
        // Clear all data
        setImportFiles([]);
        setImportMultiplePreviewData(null);
        setImportMultipleEditData([]);
        setImportAutoCommitResults(null);
      }
      
    } catch (e) {
      toast.error(`Erro ao importar PDF: ${getErrMsg(e)}`);
    } finally {
      setImportCommitLoading(false);
    }
  };

  // Calculate total for multiple PDF editing
  const calculateMultiplePdfTotal = () => {
    if (editingMultiplePdfIndex === null) return 0;
    
    const currentData = importMultipleEditData[editingMultiplePdfIndex].data;
    const itemsTotal = (currentData.items || []).reduce((sum, item) => sum + (item.total || 0), 0);
    const extraItemsTotal = (currentData.extra_items || []).reduce((sum, item) => sum + (item.total || 0), 0);
    
    return itemsTotal + extraItemsTotal;
  };

  // Update item in multiple PDF data
  const updateMultiplePdfItem = (itemIndex, field, value) => {
    if (editingMultiplePdfIndex === null) return;
    
    const newEditData = [...importMultipleEditData];
    const currentItems = [...(newEditData[editingMultiplePdfIndex].data.items || [])];
    
    if (!currentItems[itemIndex]) {
      currentItems[itemIndex] = { description: '', qty: 1, unit_price: 0, total: 0 };
    }
    
    currentItems[itemIndex] = {
      ...currentItems[itemIndex],
      [field]: field === 'qty' || field === 'unit_price' || field === 'total' ? parseFloat(value) || 0 : value
    };
    
    // Recalculate total for this item
    if (field === 'qty' || field === 'unit_price') {
      currentItems[itemIndex].total = currentItems[itemIndex].qty * currentItems[itemIndex].unit_price;
    }
    
    newEditData[editingMultiplePdfIndex].data.items = currentItems;
    setImportMultipleEditData(newEditData);
  };

  // Update extra item in multiple PDF data
  const updateMultiplePdfExtraItem = (itemIndex, field, value) => {
    if (editingMultiplePdfIndex === null) return;
    
    const newEditData = [...importMultipleEditData];
    const currentExtraItems = [...(newEditData[editingMultiplePdfIndex].data.extra_items || [])];
    
    if (!currentExtraItems[itemIndex]) {
      currentExtraItems[itemIndex] = { 
        description: '', 
        qty: 1, // Required by ExtraItem model
        unit_price: 0, // Required by ExtraItem model
        total: 0 
      };
    }
    
    currentExtraItems[itemIndex] = {
      ...currentExtraItems[itemIndex],
      [field]: field === 'total' || field === 'qty' || field === 'unit_price' ? parseFloat(value) || 0 : value
    };
    
    // Recalculate total for extra item if qty or unit_price changed
    if (field === 'qty' || field === 'unit_price') {
      currentExtraItems[itemIndex].total = currentExtraItems[itemIndex].qty * currentExtraItems[itemIndex].unit_price;
    }
    
    newEditData[editingMultiplePdfIndex].data.extra_items = currentExtraItems;
    setImportMultipleEditData(newEditData);
  };

  // Enhanced commit with auto-comparison
  const commitImportPDFWithAutoComparison = async () => {
    if (!importEditData) return;
    
    try {
      setImportCommitLoading(true);
      const request = {
        install_date: importEditData.install_date,
        job_name: importEditData.job_name,
        order_number: importEditData.order_number,
        pm_code: importEditData.pm_code || importPM,
        total_windows: parseInt(importEditData.total_windows) || 0,
        total_doors: parseInt(importEditData.total_doors) || 0,
        items: importEditData.items,
        extra_items: importEditData.extra_items,
        notes: importEditData.notes,
        grand_total: importPreviewData.grand_total
      };

      const { data } = await axios.post(`${API}/admin/import/commit-auto`, request);
      
      if (data.auto_committed) {
        toast.success(data.message);
        // Clear form
        setImportFile(null);
        setImportPreviewData(null);
        setImportEditData(null);
        const fileInput = document.getElementById('import-file-input');
        if (fileInput) fileInput.value = '';
      } else {
        // Show detailed comparison
        setImportAutoCommitResults({ single: data });
        toast.warning("Totais não conferem - verifique os detalhes");
      }
    } catch (e) {
      toast.error(getErrMsg(e, "Falha ao importar PDF"));
    } finally {
      setImportCommitLoading(false);
    }
  };

  // Auto-login loading screen
  if (isAutoLoggingIn) {
    console.log('🔄 APP: Renderizando Auto-login loading');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 mobile-container keyboard-adjust">
        <div className="text-center space-y-6 p-8 keyboard-adjust">
          <img 
            src={LOGO_URL} 
            alt="PM Home Remodeling" 
            className="w-20 h-20 mx-auto rounded-lg shadow-lg"
          />
          <h2 className="text-2xl font-semibold text-gray-800">PM Home Remodeling</h2>
          <div className="space-y-4">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <h3 className="text-xl font-semibold text-gray-700">Autenticando...</h3>
            <p className="text-gray-600">Use Face ID ou Touch ID para entrar</p>
            <div className="text-4xl animate-pulse">👤</div>
          </div>
          <p className="text-sm text-gray-500">
            Aguardando confirmação biométrica
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('🔍 APP: Renderizando Login component - isAuthenticated:', isAuthenticated);
    return <Login onLogin={handleLogin} language={language} changeLanguage={changeLanguage} t={t} />;
  }

  // Personal User Interface - Complete System
  if (user?.role === 'personal') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
        {/* Personal User Header */}
        <header className="bg-white border-b sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto px-2 sm:px-4 py-1 sm:py-2 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <img 
                src={LOGO_URL} 
                alt="Logo" 
                className="h-6 sm:h-8 w-auto flex-shrink-0"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/icon-192x192.png';
                }}
              />
              <div className="flex flex-col min-w-0 flex-1">
                <h1 className="text-xs sm:text-sm md:text-lg font-semibold text-slate-800 leading-tight truncate">
                  {language === 'pt-BR' ? 'Portal Pessoal' : 'Personal Portal'}
                </h1>
              </div>
            </div>
            
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Language Switcher */}
              <div className="flex gap-1 mr-2">
                <button
                  onClick={() => changeLanguage('pt-BR')}
                  className={`w-6 h-6 rounded border transition-all ${
                    language === 'pt-BR' ? 'border-green-500 shadow-sm' : 'border-gray-300'
                  }`}
                  title="Português"
                >
                  🇧🇷
                </button>
                <button
                  onClick={() => changeLanguage('en-US')}
                  className={`w-6 h-6 rounded border transition-all ${
                    language === 'en-US' ? 'border-blue-500 shadow-sm' : 'border-gray-300'
                  }`}
                  title="English"
                >
                  🇺🇸
                </button>
              </div>
              
              <NotificationBell 
                language={language}
                onNotificationClick={handleNotificationClick}
              />
              
              <Badge className="text-xs px-1 py-0.5 sm:px-2 sm:py-1">
                {user?.username}
              </Badge>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleOpenUserSettings} 
                className="p-1 sm:p-2"
                title={t('userSettings')}
              >
                <User className="h-3 w-3 sm:h-4 sm:w-4" />
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout} 
                className="p-1 sm:p-2"
                title={t('signOut')}
              >
                <LogOut className="h-3 w-3 sm:h-4 sm:w-4" />
              </Button>
            </div>
          </div>
          
          {/* Linha 2: Versão + Idiomas + Config (iPhone/iPad/Mobile) */}
          <div className="flex items-center justify-center gap-3 mt-2 pt-2 border-t border-gray-100 md:hidden">
            <span className="text-xs text-gray-500">
              v.{WEB_APP_VERSION}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => changeLanguage('pt-BR')}
                className={`w-5 h-5 rounded border text-xs ${
                  language === 'pt-BR' ? 'border-green-500 shadow-sm' : 'border-gray-300'
                }`}
              >
                🇧🇷
              </button>
              <button
                onClick={() => changeLanguage('en-US')}
                className={`w-5 h-5 rounded border text-xs ${
                  language === 'en-US' ? 'border-blue-500 shadow-sm' : 'border-gray-300'
                }`}
              >
                🇺🇸
              </button>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowConfigDialog(true)}
              className="p-1 h-6"
            >
              <Settings className="h-3 w-3" />
            </Button>
          </div>
      </header>

      {/* Menu Lateral (Sidebar) - Sempre disponível */}
      <>
        {/* Overlay */}
        {sideMenuOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            style={{ zIndex: 9998 }}
            onClick={() => setSideMenuOpen(false)}
          />
        )}
        
        {/* Sidebar */}
        <div 
          className={`fixed left-0 top-0 h-full w-64 bg-white shadow-2xl transform transition-transform duration-300 ${
            sideMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          style={{ 
            zIndex: 9999,
            visibility: 'visible',
            display: 'block'
          }}
          onClick={(e) => {
            e.stopPropagation();
            console.log('🎯 Menu clicado (não deve fechar)');
          }}
        >
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold text-gray-800">
                Menu {sideMenuOpen ? '✅ ABERTO' : '❌ FECHADO'}
              </h2>
              <button
                onClick={() => setSideMenuOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-2 space-y-1">
              {/* Menu Items baseado no usuário */}
              {user?.role === 'personal' ? (
                <button
                  onClick={() => {
                    setActiveTab('documents');
                    setSideMenuOpen(false);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                    activeTab === 'documents' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                  }`}
                >
                  <span className="text-xl">📁</span>
                  <span className="font-medium">{language === 'pt-BR' ? 'Documentos' : 'Documents'}</span>
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setActiveTab('home');
                      setSideMenuOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                      activeTab === 'home' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xl">🏠</span>
                    <span className="font-medium">{t('home')}</span>
                  </button>
                  
                  {user?.role !== 'daily_leader' && (
                  <button
                    onClick={() => {
                      setActiveTab('drafts');
                      setSideMenuOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                      activeTab === 'drafts' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xl">📋</span>
                    <span className="font-medium">Labor</span>
                  </button>
                  )}
                  
                  {user?.role === 'daily_leader' && (
                    <button
                      onClick={() => {
                        setActiveTab('report');
                        setSideMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                        activeTab === 'report' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-xl">📊</span>
                      <span className="font-medium">Report</span>
                    </button>
                  )}
                  
                  {user?.role !== 'admin' && user?.role !== 'daily_leader' && (
                    <button
                      onClick={() => {
                        setActiveTab('crews');
                        setSideMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                        activeTab === 'crews' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-xl">👥</span>
                      <span className="font-medium">Crews</span>
                    </button>
                  )}
                  
                  {user?.role === 'admin' && (
                    <button
                      onClick={() => {
                        setActiveTab('payroll');
                        setSideMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                        activeTab === 'payroll' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-xl">💰</span>
                      <span className="font-medium">{t('payroll')}</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      setActiveTab('arbitration');
                      setArbitrationSubTab('files');
                      setSideMenuOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                      activeTab === 'arbitration' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xl">📄</span>
                    <span className="font-medium">Docs</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setActiveTab('support');
                      setSideMenuOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                      activeTab === 'support' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xl">💬</span>
                    <span className="font-medium">{t('support')}</span>
                  </button>
                  
                  {user?.role === 'admin' && (
                    <button
                      onClick={() => {
                        setActiveTab('admin');
                        setSideMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                        activeTab === 'admin' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-xl">⚙️</span>
                      <span className="font-medium">{t('admin')}</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      setActiveTab('config');
                      setSideMenuOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                      activeTab === 'config' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xl">🔧</span>
                    <span className="font-medium">{t('config')}</span>
                  </button>
                </>
              )}
            </div>
            
            {/* Rodapé do Menu com Logout */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-gray-50">
              <button
                onClick={() => {
                  handleLogout();
                  setSideMenuOpen(false);
                }}
                className="w-full px-4 py-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                <span className="font-medium">{t('signOut')}</span>
              </button>
            </div>
          </div>
      </>

      {/* Personal User Content */}
        <div className="max-w-7xl mx-auto px-1 sm:px-2 md:px-4 py-1 sm:py-2 md:py-4">
          <Toaster richColors position="top-right" closeButton duration={4000} expand visibleToasts={3} />

          <Tabs value={activeTab} onValueChange={safeSetActiveTab} className="w-full">
            
            {/* Personal Navigation Menu */}
            <TabsList className="grid w-full grid-cols-1 h-14 bg-white border rounded-lg p-0.5">
              <TabsTrigger 
                value="documents" 
                className="flex flex-col justify-center items-center text-xs p-0.5 h-full min-h-0 data-[state=active]:bg-green-50"
              >
                <span className="text-sm">📁</span>
                <span className="text-xs leading-none">{language === 'pt-BR' ? 'Documentos' : 'Documents'}</span>
              </TabsTrigger>
            </TabsList>

            {/* Documents Tab */}
            <TabsContent value="documents" className="mt-2 sm:mt-4 md:mt-6">
              <Card className="border-0 sm:border shadow-none sm:shadow-sm">
                <CardHeader className="p-3 sm:p-4 md:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="text-2xl">📁</div>
                      <div>
                        <CardTitle className="text-base sm:text-lg md:text-xl font-bold text-gray-800">
                          {t('welcome')}, {user?.first_name || user?.username}
                        </CardTitle>
                        <div className="text-sm text-gray-600">
                          {language === 'pt-BR' ? 'Gerencie seus documentos pessoais' : 'Manage your personal documents'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setShowDocumentUpload(true)}
                        className="bg-blue-600 hover:bg-blue-700"
                        size="sm"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {language === 'pt-BR' ? 'Adicionar' : 'Add Document'}
                      </Button>
                      <Button
                        onClick={() => setShowNewFolderModal(true)}
                        variant="outline"
                        size="sm"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {language === 'pt-BR' ? 'Nova Pasta' : 'New Folder'}
                      </Button>
                      {selectedDocuments.length > 0 && (
                        <Button
                          onClick={async () => {
                            if (window.confirm(language === 'pt-BR' ? 
                              `Deletar ${selectedDocuments.length} documento(s) selecionado(s)?` :
                              `Delete ${selectedDocuments.length} selected document(s)?`
                            )) {
                              try {
                                // Delete selected documents
                                for (const doc of selectedDocuments) {
                                  await axios.delete(`${API}/personal/documents/${doc.id}`);
                                }
                                toast.success(language === 'pt-BR' ? 
                                  `${selectedDocuments.length} documento(s) deletado(s)!` :
                                  `${selectedDocuments.length} document(s) deleted!`
                                );
                                setSelectedDocuments([]);
                                await loadPersonalDocuments();
                              } catch (e) {
                                toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao deletar documentos' : 'Error deleting documents'));
                              }
                            }
                          }}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {language === 'pt-BR' ? 'Remover' : 'Remove'} ({selectedDocuments.length})
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 md:p-6">
                  
                  {/* Folders Grid */}
                  {personalFolders.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        📂 {language === 'pt-BR' ? 'Pastas' : 'Folders'}
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
                        {personalFolders.map((folder) => (
                          <div 
                            key={folder.id} 
                            className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 hover:bg-yellow-100 cursor-pointer transition-colors group relative"
                            onClick={() => setCurrentFolderId(folder.id)}
                          >
                            <div className="text-center">
                              <div className="text-2xl mb-2">📁</div>
                              <div className="text-sm font-medium text-gray-800 truncate">
                                {folder.name}
                              </div>
                            </div>
                            {/* Folder Actions */}
                            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(language === 'pt-BR' ? 
                                    `Deletar pasta "${folder.name}"?` : 
                                    `Delete folder "${folder.name}"?`
                                  )) {
                                    // Delete folder logic
                                  }
                                }}
                                className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Documents Grid */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                        📄 {language === 'pt-BR' ? 'Documentos' : 'Documents'}
                        {currentFolderId && (
                          <Button
                            onClick={() => setCurrentFolderId(null)}
                            variant="outline"
                            size="sm"
                            className="text-xs"
                          >
                            ← {language === 'pt-BR' ? 'Voltar' : 'Back'}
                          </Button>
                        )}
                      </h3>
                      {personalDocuments.length > 0 && (
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedDocuments.length === personalDocuments.length && personalDocuments.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDocuments([...personalDocuments]);
                              } else {
                                setSelectedDocuments([]);
                              }
                            }}
                            className="text-blue-600"
                          />
                          <span className="text-blue-600 font-medium">
                            {language === 'pt-BR' ? 'Selecionar Todos' : 'Select All'}
                          </span>
                        </label>
                      )}
                    </div>

                    {personalDocuments.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                        <div className="text-gray-400 mb-4 text-4xl">📄</div>
                        <h3 className="font-medium text-gray-700 mb-2">
                          {language === 'pt-BR' ? 'Nenhum documento encontrado' : 'No documents found'}
                        </h3>
                        <p className="text-gray-500 text-sm mb-4">
                          {language === 'pt-BR' ? 
                            'Comece fazendo upload dos seus documentos pessoais.' :
                            'Start by uploading your personal documents.'
                          }
                        </p>
                        <Button
                          onClick={() => setShowDocumentUpload(true)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          {language === 'pt-BR' ? 'Adicionar Primeiro Documento' : 'Add First Document'}
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {personalDocuments.map((doc) => (
                          <div key={doc.id} className={`bg-white border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer relative ${
                            selectedDocuments.some(d => d.id === doc.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                          }`}>
                            
                            {/* Checkbox */}
                            <div className="absolute top-2 left-2 z-10">
                              <input
                                type="checkbox"
                                checked={selectedDocuments.some(d => d.id === doc.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  if (e.target.checked) {
                                    setSelectedDocuments(prev => [...prev, doc]);
                                  } else {
                                    setSelectedDocuments(prev => prev.filter(d => d.id !== doc.id));
                                  }
                                }}
                                className="text-blue-600 bg-white rounded border-2 shadow-sm"
                              />
                            </div>

                            {/* Thumbnail */}
                            <div 
                              className="h-32 bg-gray-100 flex items-center justify-center relative"
                              onClick={() => {
                                setCurrentDocument(doc);
                                setShowDocumentViewer(true);
                              }}
                            >
                              {doc.file_type === 'image' ? (
                                <img
                                  src={`${API}/personal/documents/${doc.id}/thumbnail`}
                                  alt={doc.original_filename}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : doc.file_type === 'pdf' ? (
                                <div className="text-center">
                                  <div className="text-3xl text-red-500 mb-2">📄</div>
                                  <div className="text-xs text-gray-600">PDF</div>
                                </div>
                              ) : (
                                <div className="text-center">
                                  <div className="text-3xl text-gray-500 mb-2">📄</div>
                                  <div className="text-xs text-gray-600">{doc.file_type.toUpperCase()}</div>
                                </div>
                              )}
                              
                              {/* Fallback for image errors */}
                              <div className="hidden w-full h-full bg-gray-200 items-center justify-center">
                                <div className="text-center">
                                  <div className="text-2xl text-gray-400 mb-1">📄</div>
                                  <div className="text-xs text-gray-500">{doc.file_type.toUpperCase()}</div>
                                </div>
                              </div>
                            </div>

                            {/* Document Info */}
                            <div className="p-3">
                              <div className="font-medium text-sm text-gray-800 truncate mb-1">
                                {doc.original_filename}
                              </div>
                              <div className="text-xs text-gray-500">
                                {(doc.file_size / 1024).toFixed(1)} KB
                              </div>
                              <div className="text-xs text-gray-400">
                                {new Date(doc.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}
                              </div>
                            </div>

                            {/* Quick Actions */}
                            <div className="px-3 pb-3">
                              <div className="flex gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const link = document.createElement('a');
                                    link.href = `${API}/personal/documents/${doc.id}/download`;
                                    link.download = doc.original_filename;
                                    link.click();
                                    toast.success(language === 'pt-BR' ? 'Download iniciado' : 'Download started');
                                  }}
                                  className="flex-1 text-xs"
                                  title={language === 'pt-BR' ? 'Download' : 'Download'}
                                >
                                  <Download className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (window.confirm(language === 'pt-BR' ? 
                                      `Deletar ${doc.original_filename}?` : 
                                      `Delete ${doc.original_filename}?`
                                    )) {
                                      try {
                                        await axios.delete(`${API}/personal/documents/${doc.id}`);
                                        toast.success(language === 'pt-BR' ? 'Documento deletado!' : 'Document deleted!');
                                        await loadPersonalDocuments();
                                      } catch (e) {
                                        toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao deletar' : 'Error deleting'));
                                      }
                                    }
                                  }}
                                  className="flex-1 text-xs text-red-600 hover:text-red-700"
                                  title={language === 'pt-BR' ? 'Deletar' : 'Delete'}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* New Folder Modal */}
        {showNewFolderModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
            style={{ zIndex: 9999 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                setShowNewFolderModal(false);
              }
            }}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-4 text-green-600">
                📁 {language === 'pt-BR' ? 'Nova Pasta' : 'New Folder'}
              </h3>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {language === 'pt-BR' ? 'Nome da Pasta' : 'Folder Name'} *
                  </label>
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder={language === 'pt-BR' ? 'Digite o nome...' : 'Enter name...'}
                    className="w-full border rounded px-3 py-2"
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newFolderName.trim()) {
                        createPersonalFolder();
                      }
                    }}
                  />
                </div>
              </div>
              
              <div className="flex gap-2 mt-4">
                <button
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setShowNewFolderModal(false);
                    setNewFolderName('');
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded"
                >
                  {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    if (newFolderName.trim()) {
                      createPersonalFolder();
                    } else {
                      alert(language === 'pt-BR' ? 'Digite um nome para a pasta' : 'Enter a folder name');
                    }
                  }}
                  className="bg-green-600 text-white px-4 py-2 rounded disabled:opacity-50"
                  disabled={!newFolderName.trim()}
                >
                  📁 {language === 'pt-BR' ? 'Criar Pasta' : 'Create Folder'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Document Upload Modal */}
        {showDocumentUpload && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
            style={{ zIndex: 9999 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                setShowDocumentUpload(false);
              }
            }}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 shadow-2xl"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-4 text-blue-600">
                📤 {language === 'pt-BR' ? 'Adicionar Documentos' : 'Add Documents'}
              </h3>
              
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>📋 {language === 'pt-BR' ? 'Upload de Documentos' : 'Document Upload'}</strong> - 
                    {language === 'pt-BR' ? 'Selecione um ou múltiplos arquivos' : 'Select one or multiple files'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {language === 'pt-BR' ? 'Selecionar Arquivos' : 'Select Files'}:
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setUploadFiles(files);
                    }}
                    className="w-full border rounded px-3 py-2"
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                  {uploadFiles.length > 0 && (
                    <div className="mt-2 text-sm text-gray-600">
                      {uploadFiles.length} {language === 'pt-BR' ? 'arquivo(s) selecionado(s)' : 'file(s) selected'}
                    </div>
                  )}
                </div>

                {/* Files Preview */}
                {uploadFiles.length > 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-32 overflow-auto">
                    <h5 className="font-medium text-gray-800 mb-2">
                      📋 {language === 'pt-BR' ? 'Arquivos' : 'Files'}:
                    </h5>
                    <div className="space-y-1">
                      {uploadFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between text-xs">
                          <span className="truncate">{file.name}</span>
                          <span className="text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>💡 {language === 'pt-BR' ? 'Dica' : 'Tip'}:</strong>
                  </p>
                  <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                    <li>• {language === 'pt-BR' ? 'Tamanho máximo: 10MB por arquivo' : 'Maximum size: 10MB per file'}</li>
                    <li>• {language === 'pt-BR' ? 'Formatos: Imagens, PDFs, Documentos' : 'Formats: Images, PDFs, Documents'}</li>
                    <li>• {language === 'pt-BR' ? 'Miniaturas automáticas para visualização' : 'Automatic thumbnails for preview'}</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex gap-2 mt-4">
                <button
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setShowDocumentUpload(false);
                    setUploadFiles([]);
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded"
                >
                  {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    if (uploadFiles.length > 0) {
                      uploadPersonalDocuments();
                    } else {
                      alert(language === 'pt-BR' ? 'Selecione arquivos primeiro' : 'Select files first');
                    }
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
                  disabled={uploadFiles.length === 0 || documentsLoading}
                >
                  {documentsLoading ? 
                    `⏳ ${language === 'pt-BR' ? 'Enviando...' : 'Uploading...'}` :
                    `📤 ${language === 'pt-BR' ? 'Enviar' : 'Upload'} ${uploadFiles.length > 0 ? `(${uploadFiles.length})` : ''}`
                  }
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const badgeText = user?.full_name ? `${user.full_name} (${user.username})` : user?.username;

  // Derived trash filtered
  const filteredTrash = (trash || []).filter((b) => {
    const q = trashSearch.trim().toLowerCase();
    if (!q) return true;
    return ((b.job_name || '').toLowerCase().includes(q) || (b.order_number || '').toLowerCase().includes(q));
  }).sort((a, b) => {
    if (trashSort === 'job_asc' || trashSort === 'job_desc') {
      const an = (a.job_name || '').toLowerCase();
      const bn = (b.job_name || '').toLowerCase();
      if (an < bn) return trashSort === 'job_asc' ? -1 : 1;
      if (an > bn) return trashSort === 'job_asc' ? 1 : -1;
      return 0;
    }
    const ad = a.deleted_at ? new Date(a.deleted_at).getTime() : 0;
    const bd = b.deleted_at ? new Date(b.deleted_at).getTime() : 0;
    if (ad < bd) return trashSort === 'deleted_asc' ? -1 : 1;
    if (ad > bd) return trashSort === 'deleted_asc' ? 1 : -1;
    return 0;
  });

  return (
    <>
      {/* Vídeo de Entrada do Site - Melhor Qualidade */}
      {showSplashScreen && (
        <div 
          className={`fixed inset-0 z-50 splash-container flex items-center justify-center bg-black overflow-hidden cursor-pointer ${
            splashAnimation === 'entering' ? 'animate-fade-in' : 'animate-fade-out'
          }`}
          onClick={() => {
            console.log('👆 Clique detectado - fechando splash screen');
            setShowSplashScreen(false);
          }}
        >
          
          {/* Vídeo autoplay obrigatório */}
          {selectedVideo && (
            <video 
              autoPlay
              muted 
              playsInline
              preload="auto"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ 
                filter: 'contrast(1.1) brightness(1.05) saturate(1.1)',
                imageRendering: 'crisp-edges'
              }}
              onEnded={() => {
                console.log('🎬 Vídeo terminou naturalmente');
                setShowSplashScreen(false);
              }}
              onError={() => {
                console.log('❌ Erro no vídeo - fechando splash');
                setShowSplashScreen(false);
              }}
            >
              <source src={selectedVideo} type="video/mp4" />
            </video>
          )}
          
          {/* Botão pequeno de pular introdução */}
          <button
            onClick={() => {
              console.log('⏭️ Usuário pulou introdução');
              setShowSplashScreen(false);
            }}
            className="absolute top-4 right-4 bg-black/30 hover:bg-black/50 text-white text-xs px-3 py-1 rounded-full transition-all duration-300 backdrop-blur-sm"
          >
            ⏭️ Pular
          </button>
          
        </div>
      )}
      
      {/* Show Native Test Page if enabled */}
      {showNativeTest && (
        <NativeTestPage 
          user={user} 
          setShowNativeTest={setShowNativeTest}
        />
      )}

      {/* Main App - Hidden when Native Test is active */}
      {!showNativeTest && (
        <div 
          className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 mobile-container"
          style={{ paddingTop: (isIPhoneWeb || headerStickyMode) && headerHeight ? `${headerHeight}px` : 0 }}
        >
      {/* Header - Responsivo com Hamburger Menu */}
      <header 
        className="bg-white border-b sticky top-0 z-50 shadow-sm"
        style={{
          position: isIPhoneWeb ? 'fixed' : 'sticky',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          WebkitPosition: isIPhoneWeb ? 'fixed' : 'sticky',
          paddingTop: 'env(safe-area-inset-top, 0px)'
        }}
      >
        <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 sm:py-3">
          {/* Linha 1: Logo + Nome + Ações */}
          <div className="flex items-center justify-between gap-2">
            {/* Hamburger Menu - Aparece quando scrolled OU sempre no mobile */}
            {isScrolled && (
              <button
                onClick={() => {
                  console.log('🍔 Hamburger clicado! Abrindo menu lateral...');
                  setSideMenuOpen(true);
                }}
                className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Abrir menu"
              >
                <div className="space-y-1">
                  <div className="w-5 h-0.5 bg-gray-700"></div>
                  <div className="w-5 h-0.5 bg-gray-700"></div>
                  <div className="w-5 h-0.5 bg-gray-700"></div>
                </div>
              </button>
            )}
            
            <div className="flex items-center gap-2 min-w-0 flex-1">
            <img 
              src={LOGO_URL} 
              alt="PM Logo" 
              className="h-7 sm:h-10 md:h-12 w-auto flex-shrink-0"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/icon-192x192.png';
              }}
            />
              <div className="flex flex-col min-w-0 flex-1">
                <h1 className="text-sm sm:text-lg md:text-xl font-semibold text-slate-800 leading-tight truncate">
                  PM Home Remodeling
                </h1>
              </div>
            </div>
          
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Language Switcher */}
            <div className="flex gap-1 mr-2">
              <button
                onClick={() => changeLanguage('pt-BR')}
                className={`w-6 h-6 rounded border transition-all ${
                  language === 'pt-BR' ? 'border-green-500 shadow-sm' : 'border-gray-300'
                }`}
                title="Português"
              >
                🇧🇷
              </button>
              <button
                onClick={() => changeLanguage('en-US')}
                className={`w-6 h-6 rounded border transition-all ${
                  language === 'en-US' ? 'border-blue-500 shadow-sm' : 'border-gray-300'
                }`}
                title="English"
              >
                🇺🇸
              </button>
            </div>
            
            {/* Botão Download Projeto (ADMIN only) */}
            {user?.role === 'admin' && (
              <div className="mr-2">
                <button
                  onClick={async () => {
                    try {
                      setBackupLoading(true);
                      const token = sessionStorage.getItem('access_token');
                      const response = await fetch(`${API}/admin/download-project`, {
                        headers: {
                          'Authorization': `Bearer ${token}`
                        }
                      });
                      
                      if (response.ok) {
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'PM_Labor_Bills_Complete.zip';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(url);
                        toast.success('📥 Download iniciado!');
                      } else {
                        toast.error('Erro no download');
                      }
                    } catch (error) {
                      toast.error('Erro: ' + error.message);
                    } finally {
                      setBackupLoading(false);
                    }
                  }}
                  className="w-8 h-8 rounded-full border border-gray-200 transition-all hover:border-blue-300 hover:bg-blue-50 bg-white flex items-center justify-center shadow-sm"
                  title="Download Projeto Completo"
                  disabled={backupLoading}
                >
                  <span className="text-base">{backupLoading ? '⏳' : '📥'}</span>
                </button>
              </div>
            )}
            
            
            <NotificationBell 
              language={language}
              onNotificationClick={handleNotificationClick}
            />
            
            {/* Username Badge - Compacto */}
            <Badge className="text-xs px-1 py-0.5 sm:px-2 sm:py-1">
              {user?.username.length > 4 && window.innerWidth < 640 
                ? user?.username.slice(0, 3)
                : user?.username
              }
            </Badge>
            
            {/* Settings Button - Ícone compacto */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleOpenUserSettings} 
              className="p-1 sm:p-2"
              title={t('userSettings')}
            >
              <User className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>

            {/* Logout Button - Ícone compacto */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleLogout} 
              className="p-1 sm:p-2"
              title={t('signOut')}
            >
              <LogOut className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
          </div>
        </div>
        {/* Relógio + data (NY) em linha própria (evita sobrepor ícones no mobile) */}
        <div className={`mt-1.5 flex ${isIPhone ? 'justify-center' : ''}`}>
          <NyClock />
        </div>
      </div>
      </header>

      {/* Menu Lateral (Sidebar) - PM/ADMIN */}
      <>
        {/* Overlay */}
        {sideMenuOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            style={{ zIndex: 9998 }}
            onClick={() => setSideMenuOpen(false)}
          />
        )}
        
        {/* Sidebar */}
        <div 
          className={`fixed left-0 top-0 h-full w-64 bg-white shadow-2xl transform transition-transform duration-300 ${
            sideMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          style={{ 
            zIndex: 9999,
            visibility: 'visible',
            display: 'block'
          }}
        >
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold text-gray-800">Menu</h2>
            <button
              onClick={() => setSideMenuOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="p-2 space-y-1">
            {/* Menu Items */}
            <button
              onClick={() => {
                setActiveTab('home');
                setSideMenuOpen(false);
              }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                activeTab === 'home' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
              }`}
            >
              <span className="text-xl">🏠</span>
              <span className="font-medium">{t('home')}</span>
            </button>
            
            {user?.role !== 'daily_leader' && (
            <button
              onClick={() => {
                setActiveTab('drafts');
                setSideMenuOpen(false);
              }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                activeTab === 'drafts' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
              }`}
            >
              <span className="text-xl">📋</span>
              <span className="font-medium">Labor</span>
            </button>
            )}
            
            {user?.role === 'daily_leader' && (
              <button
                onClick={() => {
                  setActiveTab('report');
                  setSideMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                  activeTab === 'report' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                }`}
              >
                <span className="text-xl">📊</span>
                <span className="font-medium">Report</span>
              </button>
            )}

            {user?.role !== 'admin' && user?.role !== 'daily_leader' && (
              <button
                onClick={() => {
                  setActiveTab('reports');
                  setSideMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                  activeTab === 'reports' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                }`}
              >
                <span className="text-xl">📊</span>
                <span className="font-medium">Report</span>
              </button>
            )}
            
            {user?.role !== 'admin' && user?.role !== 'daily_leader' && (
              <button
                onClick={() => {
                  setActiveTab('crews');
                  setSideMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                  activeTab === 'crews' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                }`}
              >
                <span className="text-xl">👥</span>
                <span className="font-medium">Crews</span>
              </button>
            )}
            
            {user?.role === 'admin' && (
              <button
                onClick={() => {
                  setActiveTab('payroll');
                  setSideMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                  activeTab === 'payroll' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                }`}
              >
                <span className="text-xl">💰</span>
                <span className="font-medium">{t('payroll')}</span>
              </button>
            )}
            
            {/* Docs com Submenu */}
            <div>
              <button
                onClick={() => {
                  setActiveTab('arbitration');
                  // Não fechar menu, apenas marcar como ativo
                }}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                  activeTab === 'arbitration' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                }`}
              >
                <span className="text-xl">📄</span>
                <span className="font-medium">Docs</span>
              </button>
              
              {/* Submenu Docs - Aparece quando Docs está ativo */}
              {activeTab === 'arbitration' && (
                <div className="ml-6 mt-1 space-y-1 border-l-2 border-blue-200 pl-2">
                  <button
                    onClick={() => {
                      setArbitrationSubTab('files');
                      setSideMenuOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded text-sm transition-colors flex items-center gap-2 ${
                      arbitrationSubTab === 'files' ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span>⚖️</span>
                    <span>Arbitration</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setArbitrationSubTab('contractor');
                      setSideMenuOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded text-sm transition-colors flex items-center gap-2 ${
                      arbitrationSubTab === 'contractor' ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span>📋</span>
                    <span>Contractor</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setArbitrationSubTab('seguro');
                      setSideMenuOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded text-sm transition-colors flex items-center gap-2 ${
                      arbitrationSubTab === 'seguro' ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span>🛡️</span>
                    <span>Helper WC</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setArbitrationSubTab('installation');
                      setSideMenuOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded text-sm transition-colors flex items-center gap-2 ${
                      arbitrationSubTab === 'installation' ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span>🔧</span>
                    <span>Installation</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setArbitrationSubTab('helpers');
                      setSideMenuOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded text-sm transition-colors flex items-center gap-2 ${
                      arbitrationSubTab === 'helpers' ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span>👷</span>
                    <span>Helpers</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setArbitrationSubTab('manual');
                      setSideMenuOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded text-sm transition-colors flex items-center gap-2 ${
                      arbitrationSubTab === 'manual' ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span>📖</span>
                    <span>Manual de Instruções</span>
                  </button>
                </div>
              )}
            </div>
            
            <button
              onClick={() => {
                setActiveTab('support');
                setSideMenuOpen(false);
              }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                activeTab === 'support' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
              }`}
            >
              <span className="text-xl">💬</span>
              <span className="font-medium">{t('support')}</span>
            </button>
            
            {user?.role === 'admin' && (
              <button
                onClick={() => {
                  setActiveTab('admin');
                  setSideMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                  activeTab === 'admin' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                }`}
              >
                <span className="text-xl">⚙️</span>
                <span className="font-medium">{t('admin')}</span>
              </button>
            )}
          </div>
          
          {/* Rodapé do Menu com Logout */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-gray-50">
            <button
              onClick={() => {
                handleLogout();
                setSideMenuOpen(false);
              }}
              className="w-full px-4 py-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              <span className="font-medium">{t('signOut')}</span>
            </button>
          </div>
        </div>
      </>

      {/* Container Principal - Espaçamento otimizado */}
      <div className="max-w-7xl mx-auto px-1 sm:px-2 md:px-4 py-1 sm:py-2 md:py-4">
        <Toaster richColors position="top-right" closeButton duration={4000} expand visibleToasts={3} />

        <AlertsLoginPopup
          open={alertsPopupOpen}
          alerts={urgentAlerts}
          onClose={() => setAlertsPopupOpen(false)}
          onManage={() => { setAlertsPopupOpen(false); safeSetActiveTab('admin'); setAdminSubTab('alertas'); }}
        />


        <Tabs value={activeTab} onValueChange={safeSetActiveTab} className="w-full">
          
          {/* Espaçamento entre header e menu */}
          <div className="mb-2 sm:mb-4"></div>
          {/* Navigation Menu - Esconde quando scrolled */}
          {!isScrolled && (
            (user?.role === 'admin' || user?.role === 'general_manager') ? (
            // Menu para ADMIN/GM - Sem scroll, itens ajustados
            <TabsList className={`grid w-full h-14 sm:h-18 bg-white border rounded-lg p-0.5 sm:p-1 gap-0.5 sm:gap-1`} style={{gridTemplateColumns: `repeat(${[hasPermission('tabs','home'), hasPermission('tabs','labor') && user?.role !== 'daily_leader', hasPermission('tabs','crews') && user?.role !== 'daily_leader', hasPermission('tabs','payroll') && user?.role !== 'daily_leader', user?.role === 'daily_leader', true, hasPermission('tabs','admin'), hasPermission('tabs','push'), hasPermission('tabs','trucks')].filter(Boolean).length}, minmax(0, 1fr))`}}>
              <TabsTrigger 
                value="home" 
                className="flex flex-col justify-center items-center text-xs h-full data-[state=active]:bg-blue-50 rounded"
              >
                <span className="text-sm sm:text-lg">🏠</span>
                <span className="text-[10px] sm:text-xs">Home</span>
              </TabsTrigger>
              {hasPermission('tabs', 'labor') && user?.role !== 'daily_leader' && (
              <TabsTrigger 
                value="drafts" 
                className="flex flex-col justify-center items-center text-xs h-full data-[state=active]:bg-blue-50 rounded"
              >
                <span className="text-sm sm:text-lg">📋</span>
                <span className="text-[10px] sm:text-xs">Labor</span>
              </TabsTrigger>
              )}
              {hasPermission('tabs', 'crews') && user?.role !== 'daily_leader' && (
              <TabsTrigger 
                value="crews" 
                className="flex flex-col justify-center items-center text-xs h-full data-[state=active]:bg-blue-50 rounded"
              >
                <span className="text-sm sm:text-lg">👥</span>
                <span className="text-[10px] sm:text-xs">Crews</span>
              </TabsTrigger>
              )}
              {hasPermission('tabs', 'payroll') && user?.role !== 'daily_leader' && (
              <TabsTrigger 
                value="payroll" 
                className="flex flex-col justify-center items-center text-xs h-full data-[state=active]:bg-blue-50 rounded"
              >
                <span className="text-sm sm:text-lg">💰</span>
                <span className="text-[10px] sm:text-xs">Payroll</span>
              </TabsTrigger>
              )}
              {user?.role === 'daily_leader' && (
              <TabsTrigger 
                value="report" 
                className="flex flex-col justify-center items-center text-xs h-full data-[state=active]:bg-blue-50 rounded"
              >
                <span className="text-sm sm:text-lg">📊</span>
                <span className="text-[10px] sm:text-xs">Report</span>
              </TabsTrigger>
              )}
              
              {/* Docs Tab with Dropdown for Admin */}
              <div className="relative h-full flex items-center justify-center" style={{ touchAction: 'none' }}>
                <button
                  ref={docsButtonRef}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('📄 Docs clicked - toggling submenu. Current:', arbitrationSubmenu);
                    setArbitrationSubmenu(!arbitrationSubmenu);
                  }}
                  className={`w-full h-full flex flex-col justify-center items-center hover:bg-gray-50 transition-colors rounded px-1 ${
                    activeTab === 'arbitration' ? 'bg-blue-50 text-blue-700' : ''
                  }`}
                  style={{ fontSize: '10px', touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                >
                  <span className="text-sm sm:text-lg mb-0.5">📄</span>
                  <span className="text-[10px] sm:text-xs leading-tight">Docs</span>
                  <span className={`text-xs transition-transform leading-none ${arbitrationSubmenu ? 'rotate-180' : ''}`}>▼</span>
                </button>
                
                {/* Admin Docs Dropdown - Fixed for iOS */}
                {arbitrationSubmenu && docsButtonRef.current && (
                  <>
                    {/* Backdrop para fechar dropdown ao clicar fora */}
                    <div 
                      className="fixed inset-0 z-[9998]"
                      onClick={() => setArbitrationSubmenu(false)}
                      style={{ background: 'transparent' }}
                    />
                    <div 
                      className="fixed bg-white border border-gray-200 rounded-md shadow-lg z-[9999] w-40"
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        top: `${docsButtonRef.current.getBoundingClientRect().bottom + 4}px`,
                        left: `${docsButtonRef.current.getBoundingClientRect().left}px`,
                        touchAction: 'manipulation'
                      }}
                    >
                      <button
                        onClick={() => {
                          setActiveTab('arbitration');
                          setArbitrationSubTab('files');
                          setArbitrationSubmenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                          arbitrationSubTab === 'files' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                        style={{ touchAction: 'manipulation' }}
                      >
                        ⚖️ Arbitration
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('arbitration');
                          setArbitrationSubTab('contractor');
                          setArbitrationSubmenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                          arbitrationSubTab === 'contractor' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                        style={{ touchAction: 'manipulation' }}
                      >
                        📋 Contractor
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('arbitration');
                          setArbitrationSubTab('seguro');
                          setArbitrationSubmenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                          arbitrationSubTab === 'seguro' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                        style={{ touchAction: 'manipulation' }}
                      >
                        🛡️ Helper WC
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('arbitration');
                          setArbitrationSubTab('installation');
                          setArbitrationSubmenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                          arbitrationSubTab === 'installation' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                        style={{ touchAction: 'manipulation' }}
                      >
                        🔧 Installation
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('arbitration');
                          setArbitrationSubTab('helpers');
                          setArbitrationSubmenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                          arbitrationSubTab === 'helpers' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                        style={{ touchAction: 'manipulation' }}
                      >
                        👷 Helpers
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('arbitration');
                          setArbitrationSubTab('lswp');
                          setArbitrationSubmenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                          arbitrationSubTab === 'lswp' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                        style={{ touchAction: 'manipulation' }}
                      >
                        📋 LSWP Formstack
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('arbitration');
                          setArbitrationSubTab('manual');
                          setArbitrationSubmenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                          arbitrationSubTab === 'manual' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                        style={{ touchAction: 'manipulation' }}
                      >
                        📖 Manual de Instruções
                      </button>
                    </div>
                  </>
                )}
              </div>
              
              {hasPermission('tabs', 'admin') && (
              <TabsTrigger 
                value="admin" 
                className="flex flex-col justify-center items-center text-xs h-full data-[state=active]:bg-blue-50 rounded"
              >
                <span className="text-sm sm:text-lg">⚙️</span>
                <span className="text-[10px] sm:text-xs">Admin</span>
              </TabsTrigger>
              )}
              {hasPermission('tabs', 'push') && (
              <TabsTrigger 
                value="push" 
                className="flex flex-col justify-center items-center text-xs h-full data-[state=active]:bg-blue-50 rounded"
              >
                <span className="text-sm sm:text-lg">🔔</span>
                <span className="text-[10px] sm:text-xs">Push</span>
              </TabsTrigger>
              )}
              {hasPermission('tabs', 'trucks') && (
              <TabsTrigger 
                value="trucks" 
                className="flex flex-col justify-center items-center text-xs h-full data-[state=active]:bg-blue-50 rounded"
              >
                <span className="text-sm sm:text-lg">🚛</span>
                <span className="text-[10px] sm:text-xs">Trucks</span>
              </TabsTrigger>
              )}
            </TabsList>
          ) : (
            // Menu para PM - Layout Simples e Limpo
            <TabsList className={`grid w-full h-16 bg-white border rounded-lg p-1 gap-1 ${user?.role === 'daily_leader' ? 'grid-cols-5' : 'grid-cols-7'}`}>
              <TabsTrigger 
                value="home" 
                className="flex flex-col justify-center items-center text-xs h-full data-[state=active]:bg-blue-50 rounded"
              >
                <span className="text-sm">🏠</span>
                <span className="text-xs">{t('home')}</span>
              </TabsTrigger>
              {user?.role !== 'daily_leader' && (
              <TabsTrigger 
                value="drafts" 
                className="flex flex-col justify-center items-center text-xs h-full data-[state=active]:bg-blue-50 rounded"
              >
                <span className="text-sm">📋</span>
                <span className="text-xs">Labor</span>
              </TabsTrigger>
              )}
              {user?.role === 'daily_leader' ? (
              <TabsTrigger 
                value="report" 
                className="flex flex-col justify-center items-center text-xs h-full data-[state=active]:bg-blue-50 rounded"
              >
                <span className="text-sm">📊</span>
                <span className="text-xs">Report</span>
              </TabsTrigger>
              ) : (
              <TabsTrigger 
                value="reports" 
                className="flex flex-col justify-center items-center text-xs h-full data-[state=active]:bg-blue-50 rounded"
              >
                <span className="text-sm">📊</span>
                <span className="text-xs">Report</span>
              </TabsTrigger>
              )}
              {user?.role !== 'daily_leader' && (
              <TabsTrigger 
                value="crews" 
                className="flex flex-col justify-center items-center text-xs h-full data-[state=active]:bg-blue-50 rounded"
              >
                <span className="text-sm">👥</span>
                <span className="text-xs">Crews</span>
              </TabsTrigger>
              )}
              
              {/* Docs Tab with Simple Dropdown */}
              <div className="relative h-full">
                <button
                  onClick={() => {
                    setArbitrationSubmenu(!arbitrationSubmenu);
                    if (!arbitrationSubmenu) {
                      setActiveTab('arbitration');
                    }
                  }}
                  className={`w-full h-full flex flex-col justify-center items-center text-xs hover:bg-gray-50 transition-colors rounded ${
                    activeTab === 'arbitration' ? 'bg-blue-50 text-blue-700' : ''
                  }`}
                >
                  <span className="text-sm">📄</span>
                  <span className="text-xs">Docs</span>
                  <span className={`text-xs transition-transform ${arbitrationSubmenu ? 'rotate-180' : ''}`}>▼</span>
                </button>
                
                {/* Simple Dropdown */}
                {arbitrationSubmenu && (
                  <div 
                    className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-md z-50 w-36"
                    onMouseLeave={() => setArbitrationSubmenu(false)}
                  >
                    <button
                      onClick={() => {
                        setArbitrationSubTab('files');
                        setArbitrationSubmenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                        arbitrationSubTab === 'files' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      ⚖️ Arbitration
                    </button>
                    <button
                      onClick={() => {
                        setArbitrationSubTab('contractor');
                        setArbitrationSubmenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                        arbitrationSubTab === 'contractor' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      📋 Contractor
                    </button>
                    <button
                      onClick={() => {
                        setArbitrationSubTab('seguro');
                        setArbitrationSubmenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                        arbitrationSubTab === 'seguro' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      🛡️ Helper WC
                    </button>
                    <button
                      onClick={() => {
                        setArbitrationSubTab('installation');
                        setArbitrationSubmenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                        arbitrationSubTab === 'installation' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      🔧 Installation
                    </button>
                    <button
                      onClick={() => {
                        setArbitrationSubTab('helpers');
                        setArbitrationSubmenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                        arbitrationSubTab === 'helpers' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      👷 Helpers
                    </button>
                    <button
                      onClick={() => {
                        setArbitrationSubTab('lswp');
                        setArbitrationSubmenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                        arbitrationSubTab === 'lswp' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      📋 LSWP Formstack
                    </button>
                    <button
                      onClick={() => {
                        setArbitrationSubTab('manual');
                        setArbitrationSubmenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                        arbitrationSubTab === 'manual' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      📖 Manual de Instruções
                    </button>
                  </div>
                )}
              </div>
              
              <TabsTrigger 
                value="support" 
                className="flex flex-col justify-center items-center text-xs h-full data-[state=active]:bg-blue-50 rounded"
              >
                <span className="text-sm">🛠️</span>
                <span className="text-xs">{t('support')}</span>
              </TabsTrigger>

              <TabsTrigger 
                value="trucks" 
                className="flex flex-col justify-center items-center text-xs h-full data-[state=active]:bg-blue-50 rounded"
              >
                <span className="text-sm">🚛</span>
                <span className="text-xs">{language === 'pt-BR' ? 'Trucks' : 'Trucks'}</span>
              </TabsTrigger>
            </TabsList>
          )
          )}

          {/* Home - Ultra Responsivo */}
          <TabsContent value="home" className="mt-2 sm:mt-4 md:mt-6">
            <Card className="border-0 sm:border shadow-none sm:shadow-sm">
              <CardContent className="p-0">
                {/* Alertas de Helpers Pendentes - PM Only */}
                {user?.role !== 'admin' && (
                  <div className="mb-4 space-y-2">
                    {/* Card: Waivers Pendentes (pode ter 2 - uma de cada empresa) */}
                    {pendingWaivers.filter(w => w.status === 'pending').length > 0 && (
                      <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-300">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-2xl">⚠️</span>
                                <h3 className="font-bold text-red-800 text-lg">
                                  Contratos Pendentes de Assinatura
                                </h3>
                              </div>
                              <p className="text-red-700 mb-3">
                                Você precisa assinar o <b>Termo de Responsabilidade / Acknowledgement Waiver</b> para <b>({pendingWaivers.filter(w => w.status === 'pending').map(w => w.contratante_nome).join(' e ')})</b>.
                              </p>
                              <div className="bg-white bg-opacity-60 rounded p-3 text-sm space-y-1">
                                {pendingWaivers.filter(w => w.status === 'pending').map(w => (
                                  <p key={w.id}>
                                    • <b>{w.contratante_nome}</b> - <span className="text-orange-600">Aguardando assinatura</span>
                                  </p>
                                ))}
                              </div>
                            </div>
                            <Button 
                              onClick={() => {
                                const firstPending = pendingWaivers.find(w => w.status === 'pending');
                                if (firstPending) handleSignWaiver(firstPending);
                              }}
                              className="bg-red-600 hover:bg-red-700 flex-shrink-0"
                            >
                              ✍️ Assinar Contratos
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    
                    {/* Alerta: Sem helpers cadastrados */}

                {/* Active Rental Card - Shows if PM has active rental */}
                {user?.role !== 'admin' && activeRental && (() => {
                  const rentalTruck = trucks.find(t => t.id === activeRental.truck_id);
                  return rentalTruck ? (
                    <ActiveRentalCard 
                      rental={activeRental}
                      truck={rentalTruck}
                      language={language}
                      compact={true}
                      onRequestCheckout={() => handleRequestCheckout(activeRental)}
                      onAddUpdate={() => {
                        setSelectedRental(activeRental);
                        setShowRentalUpdateModal(true);
                      }}
                      onViewHistory={() => {
                        setSelectedRental(activeRental);
                        setSelectedTruck(rentalTruck);
                        setShowRentalHistoryModal(true);
                      }}
                    />
                  ) : null;
                })()}

                {/* Approved Rental Card - Awaiting Check-in */}
                {user?.role !== 'admin' && (() => {
                  const approvedRentals = myRentals.filter(r => r.status === 'aprovado');
                  return approvedRentals.length > 0 ? (
                    <div className="mb-4">
                      {approvedRentals.map(rental => {
                        const rentalTruck = trucks.find(t => t.id === rental.truck_id);
                        if (!rentalTruck) return null;
                        
                        return (
                          <Card key={rental.id} className="border-l-4 border-l-emerald-500 border-zinc-200 mb-2 hover:shadow-lg transition-shadow">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <p className="text-xs font-semibold text-emerald-600 mb-1 tracking-tight">
                                    ✅ {language === 'pt-BR' ? 'Aprovado - Fazer Check-in' : 'Approved - Do Check-in'}
                                  </p>
                                  <p className="font-semibold text-sm text-zinc-900">{rentalTruck.modelo} ({rentalTruck.placa})</p>
                                  <p className="text-xs text-zinc-500 mt-1">
                                    {language === 'pt-BR' ? 'Início:' : 'Start:'} {rental.data_inicio_solicitada}
                                  </p>
                                </div>
                                <Button 
                                  onClick={() => {
                                    setSelectedRental(rental);
                                    setSelectedTruck(rentalTruck);
                                    setShowCheckInModal(true);
                                  }}
                                  size="sm"
                                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold"
                                >
                                  ✅ {language === 'pt-BR' ? 'Check-in' : 'Check-in'}
                                </Button>
                              </div>
                              <div className="bg-emerald-50 border border-emerald-200 p-2 rounded text-xs text-emerald-700">
                                💡 {language === 'pt-BR' 
                                  ? 'Faça o check-in para iniciar' 
                                  : 'Do check-in to start'}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  ) : null;
                })()}

                    {helpers.length === 0 && user?.role !== 'daily_leader' && (
                      <div className="bg-white border-l-4 border-l-rose-500 border border-zinc-200 rounded-lg p-4 cursor-pointer hover:shadow-lg transition-all"
                        onClick={() => {
                          setActiveTab('arbitration');
                          setArbitrationSubTab('helpers');
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-md">
                            <User className="h-5 w-5 text-white stroke-[1.5]" />
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-sm text-zinc-900 mb-1 tracking-tight">
                              {language === 'pt-BR' ? 'Ação Necessária: Cadastrar Helpers' : 'Action Required: Register Helpers'}
                            </div>
                            <p className="text-xs text-zinc-500 leading-relaxed">
                              {language === 'pt-BR' 
                                ? 'Cadastre helpers na sua equipe' 
                                : 'Register helpers in your team'}
                            </p>
                          </div>
                          <ArrowLeft className="h-4 w-4 text-zinc-400 rotate-180" />
                        </div>
                      </div>
                    )}

                    {/* Alerta: Helpers sem documentos */}
                    {helpers.length > 0 && helpers.some(h => !h.arbitration_agreement_id || !h.contractor_agreement_id) && (
                      <div className="bg-white border-l-4 border-l-amber-500 border border-zinc-200 rounded-lg p-4 cursor-pointer hover:shadow-lg transition-all"
                        onClick={() => {
                          setActiveTab('arbitration');
                          setArbitrationSubTab('helpers');
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-md">
                            <FileText className="h-5 w-5 text-white stroke-[1.5]" />
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-sm text-zinc-900 mb-1 tracking-tight">
                              {language === 'pt-BR' ? 'Documentos Pendentes' : 'Pending Documents'}
                            </div>
                            <p className="text-xs text-zinc-500 leading-relaxed">
                              {language === 'pt-BR' 
                                ? `${helpers.filter(h => !h.arbitration_agreement_id || !h.contractor_agreement_id).length} helper(s) sem documentos`
                                : `${helpers.filter(h => !h.arbitration_agreement_id || !h.contractor_agreement_id).length} helper(s) missing docs`}
                            </p>
                            <div className="mt-2 space-y-0.5">
                              {helpers.filter(h => !h.arbitration_agreement_id || !h.contractor_agreement_id).slice(0, 3).map(h => (
                                <div key={h.id} className="text-xs text-amber-600">
                                  • {h.full_name}
                                </div>
                              ))}
                            </div>
                          </div>
                          <ArrowLeft className="h-4 w-4 text-zinc-400 rotate-180" />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {(user?.role === 'admin' || user?.role === 'general_manager') ? (
                  // ADMIN/GM DASHBOARD
                  <div className="space-y-6">
                    {/* Header Central - Menor e mais compacto */}
                    <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border border-zinc-200 rounded-xl p-4 text-center shadow-sm">
                      <div className="flex items-center justify-center gap-3">
                        <img 
                          src={LOGO_URL} 
                          alt="PM Logo" 
                          className="w-10 h-10"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/icon-192x192.png';
                          }}
                        />
                        <div className="text-left">
                          <h1 className="text-lg font-bold text-zinc-900 tracking-tight leading-none">
                            PM Home Remodeling
                          </h1>
                          <p className="text-sm text-zinc-600 mt-0.5 font-medium">
                            {t('welcome')}, {language === 'pt-BR' ? 'Administrador' : 'Administrator'}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Cards de Atalhos Rápidos - Grid 3 Colunas - PALETA PREMIUM */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                      
                      {/* Card 1: Gerenciador de Arquivos - Compacto */}
                      <div 
                        className="bg-white border border-zinc-200 rounded-lg p-3 cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 group"
                        onClick={() => {
                          setActiveTab('admin');
                          setAdminSubTab('arquivos');
                        }}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center group-hover:scale-105 transition-transform shadow-md">
                            <FileText className="h-5 w-5 text-white stroke-[1.5]" />
                          </div>
                          <h3 className="font-semibold text-sm text-zinc-900 tracking-tight">Arquivos</h3>
                          <p className="text-xs text-zinc-500 leading-relaxed hidden sm:block">
                            Upload e organize
                          </p>
                          <div className="flex gap-1 text-xs mt-1">
                            <span className="bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded font-medium">100MB</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Card 2: Tickets - Compacto */}
                      <div 
                        className="bg-white border border-zinc-200 rounded-lg p-3 cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 group"
                        onClick={() => {
                          setActiveTab('admin');
                          setAdminSubTab('suporte');
                        }}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center group-hover:scale-105 transition-transform shadow-md">
                            <Ticket className="h-5 w-5 text-white stroke-[1.5]" />
                          </div>
                          <h3 className="font-semibold text-sm text-zinc-900 tracking-tight">Tickets</h3>
                          <div className="flex gap-1 w-full justify-center mt-1">
                            <div className="bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5 flex-1 text-center">
                              <span className="text-rose-700 font-semibold text-xs">
                                {adminTickets.filter(t => t.status === 'open').length}
                              </span>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 flex-1 text-center">
                              <span className="text-amber-700 font-semibold text-xs">
                                {adminTickets.filter(t => t.status === 'pending').length}
                              </span>
                            </div>
                            <div className="bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 flex-1 text-center">
                              <span className="text-emerald-700 font-semibold text-xs">
                                {adminTickets.filter(t => t.status === 'resolved' || t.status === 'closed').length}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Card 3: Mensagens - Compacto */}
                      <div 
                        className="bg-white border border-zinc-200 rounded-lg p-3 cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 group"
                        onClick={() => {
                          setActiveTab('admin');
                          setAdminSubTab('mensagens');
                        }}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center group-hover:scale-105 transition-transform shadow-md">
                            <MessageSquare className="h-5 w-5 text-white stroke-[1.5]" />
                          </div>
                          <h3 className="font-semibold text-sm text-zinc-900 tracking-tight">Mensagens</h3>
                          <div className="flex flex-col gap-1 w-full mt-1">
                            <div className="bg-zinc-50 border border-zinc-200 rounded px-2 py-0.5">
                              <span className="text-zinc-700 font-semibold text-xs">
                                {pushNotificationHistory.length}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Card 4: Cofre */}
                      <div 
                        className="bg-white border border-zinc-200 rounded-lg p-3 cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 group"
                        onClick={() => {
                          setActiveTab('admin');
                          setAdminSubTab('cofre');
                        }}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center group-hover:scale-105 transition-transform shadow-md">
                            <span className="text-white text-lg">🔒</span>
                          </div>
                          <span className="text-xs font-medium text-zinc-700 text-center">Cofre</span>
                        </div>
                      </div>
                    </div>
                    {/* 💵 Contas a Pagar (admin) — substituiu "Próximos Alertas" */}
                    {user?.role === 'admin' && (
                      <div className="col-span-full">
                        <div className="rounded-xl border border-amber-200 border-l-4 border-l-amber-500 bg-white shadow-sm p-4" data-testid="home-bills-card">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2.5">
                              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-md">
                                <Wallet className="h-5 w-5 text-white stroke-[1.5]" />
                              </div>
                              <div>
                                <span className="font-semibold text-slate-800 block leading-tight">{language === 'pt-BR' ? 'Contas a Pagar' : 'Bills to Pay'}</span>
                                <span className="text-xs text-slate-500">{language === 'pt-BR' ? 'Vencendo em breve' : 'Due soon'}</span>
                              </div>
                              {billAlerts.length > 0 && (
                                <Badge className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold px-2 py-0.5 ml-1">{billAlerts.length}</Badge>
                              )}
                            </div>
                            <button onClick={() => { safeSetActiveTab('admin'); setAdminSubTab('contas'); }} data-testid="home-bills-manage-btn" className="text-xs text-amber-700 hover:underline font-medium">{language === 'pt-BR' ? 'Ver todas' : 'View all'}</button>
                          </div>
                          {billAlerts.length === 0 ? (
                            <div className="text-sm text-slate-500 py-2" data-testid="home-bills-empty">{language === 'pt-BR' ? 'Nenhuma conta a vencer nos próximos 7 dias 🎉' : 'No bills due in the next 7 days 🎉'}</div>
                          ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {[...billAlerts].sort((a, b) => {
                                const rank = { overdue: 0, urgent: 1, warning: 2 };
                                const ra = rank[a.alert_type] ?? 3, rb = rank[b.alert_type] ?? 3;
                                if (ra !== rb) return ra - rb;
                                return (a.next_due_date || '').localeCompare(b.next_due_date || '');
                              }).map((alert) => (
                                <div key={alert.id} data-testid={`home-bill-${alert.id}`} onClick={() => setHomeBillViewItem(alert)}
                                  className={`border rounded-lg p-2.5 cursor-pointer transition-all hover:shadow-sm ${
                                    alert.alert_type === 'overdue' ? 'bg-red-50 border-red-300' :
                                    alert.alert_type === 'urgent' ? 'bg-orange-50 border-orange-300' :
                                    'bg-yellow-50 border-yellow-300'
                                  }`}>
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="font-semibold text-sm text-zinc-800 truncate">{BILL_CATEGORY_ICONS[alert.category] || '📌'} {alert.name}</span>
                                    <Badge className={`text-[10px] flex-shrink-0 ${
                                      alert.alert_type === 'overdue' ? 'bg-red-600 text-white' :
                                      alert.alert_type === 'urgent' ? 'bg-orange-500 text-white' :
                                      'bg-yellow-500 text-white'
                                    }`}>
                                      {alert.alert_type === 'overdue' ? (language === 'pt-BR' ? 'VENCIDA' : 'OVERDUE') : alert.alert_type === 'urgent' ? '3d' : '7d'}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-gray-600 mt-1 font-medium">
                                    ${alert.value?.toFixed(2)} · {new Date(alert.next_due_date + 'T12:00:00').toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Central de Avisos - Compacto */}
                    {hasPermission('home_cards', 'central_avisos') && (
                    <div 
                      className={`bg-white border-l-4 border-l-indigo-500 border border-zinc-200 rounded-lg p-4 cursor-pointer transition-all duration-300 hover:shadow-lg ${
                        announcementsExpanded ? 'col-span-full' : ''
                      }`}
                      onClick={() => setAnnouncementsExpanded(!announcementsExpanded)}
                      style={{
                        minHeight: announcementsExpanded ? '250px' : '70px'
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md">
                            <MessageSquare className="h-5 w-5 text-white stroke-[1.5]" />
                          </div>
                          <h4 className="font-semibold text-base text-zinc-900 tracking-tight">
                            {language === 'pt-BR' ? 'Central de Avisos' : 'Announcements'}
                          </h4>
                        </div>
                        <div className="flex items-center gap-2">
                          {announcements.length > 0 && (
                            <Badge className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold px-2 py-0.5">
                              {announcements.length}
                            </Badge>
                          )}
                          <span className="text-zinc-400 text-sm">
                            {announcementsExpanded ? '▼' : '▶'}
                          </span>
                        </div>
                      </div>
                      
                      {!announcementsExpanded ? (
                        <div className="text-zinc-500 text-base leading-relaxed">
                          {announcements.length === 0 ? 
                            (language === 'pt-BR' ? 'Nenhum aviso - Clique para gerenciar' : 'No announcements - Click to manage') :
                            (language === 'pt-BR' ? `${announcements.length} aviso(s) - Clique para gerenciar` : `${announcements.length} announcement(s) - Click to manage`)
                          }
                        </div>
                      ) : (
                        <div className="space-y-4 mt-6">
                          <div className="flex justify-end mb-4">
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingAnnouncement(null);
                                setAnnouncementForm({
                                  title: '',
                                  content: '',
                                  priority: 'normal',
                                  expires_at: ''
                                });
                                setShowAnnouncementModal(true);
                              }}
                              size="sm"
                              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-4 py-2"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              {language === 'pt-BR' ? 'Novo Aviso' : 'New Announcement'}
                            </Button>
                          </div>
                          
                          <UnsentBillsAlert
                            bills={laborBills}
                            user={user}
                            language={language}
                            onOpenBill={(id) => loadLaborBill(id)}
                            onGoToLabor={() => setActiveTab('drafts')}
                            onMarkAllSent={markAllSupervisorSent}
                            onReconcile={reconcileSentFromHistory}
                          />
                          {announcements.length === 0 ? (
                            <div className="text-center py-12 text-zinc-400">
                              <div className="text-5xl mb-3">📢</div>
                              <div className="text-lg font-semibold text-zinc-600">{language === 'pt-BR' ? 'Nenhum aviso criado ainda' : 'No announcements created yet'}</div>
                              <p className="text-sm mt-2 text-zinc-500 leading-relaxed">{language === 'pt-BR' ? 'Crie o primeiro aviso para a equipe' : 'Create the first announcement for the team'}</p>
                            </div>
                          ) : (
                            <div>
                              {/* Avisos normais */}
                              <div className="space-y-3 max-h-72 overflow-auto">
                              {announcements.map((announcement) => (
                                <div key={announcement.id} className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 hover:bg-white hover:border-zinc-300 transition-all">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-2">
                                        <h5 className="font-bold text-base text-zinc-900">{announcement.title}</h5>
                                        <Badge className={`text-xs font-semibold ${
                                          announcement.priority === 'urgent' ? 'bg-rose-50 border border-rose-300 text-rose-700' :
                                          announcement.priority === 'high' ? 'bg-amber-50 border border-amber-300 text-amber-700' :
                                          announcement.priority === 'normal' ? 'bg-indigo-50 border border-indigo-300 text-indigo-700' :
                                          'bg-zinc-50 border border-zinc-300 text-zinc-700'
                                        }`}>
                                          {announcement.priority === 'urgent' ? '🚨' :
                                          announcement.priority === 'urgent' ? '🚨' :
                                          announcement.priority === 'high' ? '⚠️' :
                                          announcement.priority === 'normal' ? 'ℹ️' : '📝'}
                                        </Badge>
                                      </div>
                                      <p className="text-zinc-700 text-sm">{announcement.content}</p>
                                      <div className="text-xs text-zinc-500 mt-2">
                                        {language === 'pt-BR' ? 'Criado em' : 'Created on'} {formatDateTime(announcement.created_at)}
                                      </div>
                                    </div>
                                    <div className="flex gap-1 ml-3">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingAnnouncement(announcement);
                                          setAnnouncementForm({
                                            title: announcement.title,
                                            content: announcement.content,
                                            priority: announcement.priority,
                                            expires_at: announcement.expires_at ? announcement.expires_at.split('T')[0] : ''
                                          });
                                          setShowAnnouncementModal(true);
                                        }}
                                        className="text-indigo-600 hover:text-indigo-700 border-zinc-300 hover:border-indigo-400 h-6 w-6 p-0"
                                      >
                                        <Edit className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => {
                                          console.log('🗑️ Delete button clicked (PM view), stopping propagation');
                                          e.stopPropagation();
                                          console.log('🗑️ About to call deleteAnnouncement with ID:', announcement.id);
                                          deleteAnnouncement(announcement.id);
                                        }}
                                        className="text-rose-600 hover:text-rose-700 border-zinc-300 hover:border-rose-400 h-6 w-6 p-0"
                                        title={language === 'pt-BR' ? 'Deletar aviso' : 'Delete announcement'}
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    )}

                    {/* NOVO: Calendário de Schedule - Compacto */}
                    {/* IMPORTANTE: ADMIN vê TODOS trabalhos | PM vê apenas seus trabalhos (filtrado por pm_team) */}
                    <div className="bg-white border border-zinc-200 rounded-lg p-3 sm:p-4 shadow-sm">
                      {/* Header - Responsivo */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md">
                            <CalendarDays className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white stroke-[1.5]" />
                          </div>
                          <h3 className="font-semibold text-sm sm:text-base text-zinc-900 tracking-tight">
                            {language === 'pt-BR' ? 'Calendário' : 'Schedule'}
                          </h3>
                        </div>
                        
                        <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end flex-wrap">
                          {(user?.role === 'admin' || user?.role === 'general_manager') && (
                            <>
                              {/* Filtro de Visualização */}
                              <Select value={viewAsTeam} onValueChange={setViewAsTeam}>
                                <SelectTrigger className="w-36 h-7 text-xs" data-testid="view-as-team-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="ALL">👁️ TODOS</SelectItem>
                                  <SelectItem value="MEASURE" data-testid="view-as-team-measure">📏 Measure</SelectItem>
                                  <SelectItem value="PM1">PM1</SelectItem>
                                  <SelectItem value="PM2">PM2</SelectItem>
                                  <SelectItem value="PM3">PM3</SelectItem>
                                  <SelectItem value="PM4">PM4</SelectItem>
                                  <SelectItem value="PM5">PM5</SelectItem>
                                  <SelectItem value="PM6">PM6</SelectItem>
                                  <SelectItem value="PM7">PM7</SelectItem>
                                  <SelectItem value="PM8">PM8</SelectItem>
                                  <SelectItem value="PM9">PM9</SelectItem>
                                  <SelectItem value="PM10">PM10</SelectItem>
                                </SelectContent>
                              </Select>
                              
                              {/* Botão Atualizar Calendário */}
                              <Button
                                onClick={async () => {
                                  try {
                                    await loadScheduleJobs();
                                    await loadBlockedDays();
                                    toast.success(language === 'pt-BR' ? '✅ Calendário atualizado!' : '✅ Calendar updated!');
                                  } catch (error) {
                                    toast.error(language === 'pt-BR' ? 'Erro ao atualizar' : 'Error updating');
                                  }
                                }}
                                variant="outline"
                                size="sm"
                                className="h-7 w-7 p-0 border-zinc-300"
                                title={language === 'pt-BR' ? 'Atualizar calendário' : 'Refresh calendar'}
                              >
                                <RefreshCw className="h-3.5 w-3.5" />
                              </Button>
                              
                              <Button
                                onClick={() => setShowExcelDataPasteModal(true)}
                                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs"
                                size="sm"
                              >
                                <Upload className="h-3 w-3 sm:mr-1.5" />
                                <span className="hidden sm:inline">{language === 'pt-BR' ? 'Colar Dados Excel' : 'Paste Excel Data'}</span>
                                <span className="sm:hidden ml-1">{language === 'pt-BR' ? 'Excel' : 'Excel'}</span>
                              </Button>
                              
                              {/* Botão IA Popover - Processar PDFs em lote */}
                              <Button
                                onClick={async () => {
                                  try {
                                    setBatchAiProgress({ running: true, total: 0, processed: 0, current_job: 'Iniciando...', errors: 0 });
                                    const m = selectedDate.getMonth() + 1;
                                    const y = selectedDate.getFullYear();
                                    await axios.post(`${API}/schedules/batch-ai-process`, { month: m, year: y });
                                    toast.info('IA iniciada em background. Acompanhe o progresso no canto inferior.');
                                    // Poll progress
                                    const interval = setInterval(async () => {
                                      try {
                                        const { data } = await axios.get(`${API}/schedules/batch-ai-progress`);
                                        setBatchAiProgress(data);
                                        if (!data.running) {
                                          clearInterval(interval);
                                          if (data.total > 0) {
                                            toast.success(`IA concluída: ${data.processed - (data.errors || 0)}/${data.total} processados`);
                                            loadScheduleJobs(selectedDate.getMonth(), selectedDate.getFullYear());
                                          }
                                        }
                                      } catch (e) { clearInterval(interval); }
                                    }, 3000);
                                  } catch (e) {
                                    toast.error('Erro ao iniciar IA');
                                    setBatchAiProgress({ running: false });
                                  }
                                }}
                                disabled={batchAiProgress?.running}
                                className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs"
                                size="sm"
                                data-testid="batch-ai-btn"
                              >
                                {batchAiProgress?.running ? (
                                  <Loader2 className="h-3 w-3 animate-spin sm:mr-1.5" />
                                ) : (
                                  <span className="sm:mr-1">🤖</span>
                                )}
                                <span className="hidden sm:inline">IA Popover</span>
                                <span className="sm:hidden ml-1">IA</span>
                              </Button>
                            </>
                          )}
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-xs border-zinc-300 px-1.5 sm:px-2 py-1 h-7 sm:h-auto"
                              onClick={() => changeMonth(-1)}
                            >
                              ←
                            </Button>
                            <span className="text-xs sm:text-sm font-semibold text-zinc-900 mx-1 sm:mx-2 whitespace-nowrap">
                              {selectedDate.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })}
                            </span>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-xs border-zinc-300 px-1.5 sm:px-2 py-1 h-7 sm:h-auto"
                              onClick={() => changeMonth(1)}
                            >
                              →
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              data-testid="admin-open-map-btn"
                              className="text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50 px-2 py-1 h-7 sm:h-auto font-semibold ml-1"
                              onClick={() => setShowScheduleMapModal(true)}
                              title="Mapa dos trabalhos"
                            >
                              🗺️ {language === 'pt-BR' ? 'Mapa' : 'Map'}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Grid 2 Colunas: iPad+ lado a lado, celular empilhado */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        
                        {/* Calendário - Largura fixa em todas as resoluções */}
                        <div 
                          className="w-full"
                          onMouseLeave={() => {
                            // Fechar popovers ao sair do calendário
                            setHoveredJobs([]);
                            setHoveredJobsData([]);
                          }}
                        >
                          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 h-auto">
                            {/* Dias da Semana */}
                            <div className="grid grid-cols-7 gap-1 mb-2">
                              {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((day, i) => (
                                <div key={i} className="text-center text-[10px] font-semibold text-zinc-500 py-1">
                                  {day}
                                </div>
                              ))}
                            </div>

                            {/* Dias */}
                            <div className="grid grid-cols-7 gap-1">
                              {getDaysInMonth(selectedDate).map((day, idx) => {
                                if (!day) {
                                  return <div key={`empty-${idx}`} className="aspect-square"></div>;
                                }
                                
                                const dayDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
                                // Usar data local para evitar bug de timezone
                                const year = dayDate.getFullYear();
                                const month = String(dayDate.getMonth() + 1).padStart(2, '0');
                                const dayNum = String(dayDate.getDate()).padStart(2, '0');
                                const dateStr = `${year}-${month}-${dayNum}`;
                                
                                // Filtrar jobs baseado no viewAsTeam
                                const allDayJobs = scheduleJobs.filter(j => 
                                  j.date === dateStr || isJobInRange(j, dateStr)
                                );
                                const dayJobs = viewAsTeam === 'ALL' 
                                  ? allDayJobs 
                                  : viewAsTeam === 'MEASURE'
                                    ? allDayJobs.filter(j => j.job_type === 'MEASURE')
                                    : allDayJobs.filter(j => j.team === viewAsTeam || (j.teams || []).includes(viewAsTeam));
                                
                                const isSelected = selectedDate.getDate() === day;
                                const hasJobsWithoutPdf = dayJobs.some(j => !j.has_pdf && j.source === 'daily_work_import');
                                const blocksForDay = blockedDays.filter(d => d.date === dateStr);
                                const isBlocked = blocksForDay.length > 0;
                                const blockedCount = blocksForDay.find(d => d.team === 'ALL') ? '∞' : blocksForDay.length;
                                
                                // Se visualizando como PM, calcular janelas/portas
                                const showWindowsDoors = viewAsTeam !== 'ALL' && viewAsTeam !== 'MEASURE';
                                const totalWindows = dayJobs.reduce((sum, job) => sum + (job.windows || 0), 0);
                                const totalDoors = dayJobs.reduce((sum, job) => sum + (job.doors || 0), 0);
                                const hasService = dayJobs.some(j => j.job_type === 'SERVICE');
                                const hasMeasure = dayJobs.some(j => j.job_type === 'MEASURE');
                                const allMeasured = hasMeasure && dayJobs.filter(j => j.job_type === 'MEASURE').every(j => j.measured_done);
                                
                                return (
                                  <button
                                    key={day}
                                    data-calendar-day="true"
                                    onClick={() => selectDay(day)}
                                    onMouseEnter={(e) => {
                                      console.log('🔥 MOUSE ENTER NO DIA!', dayJobs.length, 'jobs');
                                      if (dayJobs.length > 0) {
                                        console.log('🔥 Tem jobs, chamando handleJobMouseEnter...');
                                        // Cancelar timer
                                        if (hoverTimerRef.current) {
                                          clearTimeout(hoverTimerRef.current);
                                          hoverTimerRef.current = null;
                                        }
                                        // Passar TODOS os jobs do dia
                                        handleJobMouseEnter(dayJobs, e);
                                      } else {
                                        console.log('⚠️ Sem jobs no dia');
                                        // Sair de dia com jobs para dia sem jobs: fechar popover
                                        setHoveredJobs([]);
                                        setHoveredJobsData([]);
                                      }
                                    }}
                                    onMouseLeave={handleJobMouseLeave}
                                    onDoubleClick={() => {
                                      if (user?.role === 'admin' || user?.role === 'general_manager') {
                                        if (dayJobs.length > 0) {
                                          setSelectedWork(dayJobs[0]);
                                        } else {
                                          setSelectedWork(null);
                                        }
                                        setShowAddWorkModal(true);
                                      }
                                    }}
                                    className={`
                                      aspect-square rounded-md border text-xs font-medium transition-all relative flex flex-col items-center justify-center
                                      ${isSelected ? 'bg-indigo-500 text-white border-indigo-600' : 'bg-white border-zinc-200 text-zinc-700 hover:border-indigo-300 hover:bg-indigo-50'}
                                    `}
                                  >
                                    <span>{day}</span>
                                    {hasMeasure && (
                                      <span className={`absolute top-0 left-0.5 text-[8px] font-bold leading-none ${allMeasured ? 'text-emerald-600' : 'text-purple-600'}`} title={allMeasured ? 'Medição feita' : 'Medição'} data-testid={`day-measure-${day}`}>{allMeasured ? 'M✓' : 'M'}</span>
                                    )}
                                    
                                    {/* Modo PM: Mostrar janelas/portas ou S */}
                                    {showWindowsDoors && dayJobs.length > 0 && (
                                      <div className="flex gap-1 text-[10px] mt-0.5 font-semibold">
                                        {hasService && (
                                          <span className="text-emerald-600">S</span>
                                        )}
                                        {totalDoors > 0 && <span>🚪{totalDoors}</span>}
                                        {totalWindows > 0 && <span>🪟{totalWindows}</span>}
                                      </div>
                                    )}
                                    
                                    {isBlocked && (
                                      <div className="absolute top-0 right-0 text-[10px]">
                                        🔒{blockedCount !== '∞' && blockedCount > 1 ? <sup className="text-[8px] font-bold">{blockedCount}</sup> : ''}
                                      </div>
                                    )}
                                    
                                    {/* Modo TODOS: Mostrar bolinha verde */}
                                    {!showWindowsDoors && dayJobs.length > 0 && (
                                      <div className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2">
                                        <div className={`w-1 h-1 rounded-full ${
                                          isSelected ? 'bg-white' : hasJobsWithoutPdf ? 'bg-amber-500' : 'bg-emerald-500'
                                        }`}></div>
                                      </div>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        {/* Área de Trabalho - Largura fixa */}
                        <div className="space-y-3 w-full md:w-auto">
                          {/* Header */}
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-sm text-zinc-900">
                                {selectedDate.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })}
                                {viewAsTeam !== 'ALL' && (
                                  <span className="text-xs text-indigo-600 ml-2">({viewAsTeam})</span>
                                )}
                              </h4>
                              <p className="text-[10px] text-zinc-500">
                                {selectedDate.toLocaleDateString('pt-BR', { weekday: 'long' })}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => openTrackedLink(EXTERNAL_LINKS.REORDER_FORM, 'open_reorder_form', 'Abriu o link Reorder Form')}
                                title="Abrir Reorder Formstack"
                                data-testid="btn-reorder-formstack"
                                className="text-[10px] font-semibold px-2 py-1 rounded border border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100 whitespace-nowrap"
                              >
                                🔄 Reorder
                              </button>
                              <Badge className="bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs">
                                {selectedDayJobs.length}
                              </Badge>
                              {(user?.role === 'admin' || user?.role === 'general_manager') && (
                                <>
                                  {(() => {
                                    const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
                                    const blocksForDay = blockedDays.filter(d => d.date === dateStr);
                                    const hasBlockAll = blocksForDay.some(d => d.team === 'ALL');
                                    
                                    return (
                                      <>
                                        <Button
                                          size="sm"
                                          onClick={() => {
                                            setSelectedWork(null);
                                            setShowAddWorkModal(true);
                                          }}
                                          disabled={hasBlockAll}
                                          className="h-6 w-6 p-0 bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                                          title={hasBlockAll ? 'Dia bloqueado para TODOS' : 'Adicionar trabalho'}
                                        >
                                          <Plus className="h-3 w-3" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          onClick={() => toggleDayBlock(selectedDate)}
                                          className={`h-6 w-6 p-0 ${
                                            blocksForDay.length > 0
                                              ? 'bg-red-600 hover:bg-red-700'
                                              : 'bg-gray-600 hover:bg-gray-700'
                                          } text-white`}
                                          title={blocksForDay.length > 0 ? `${blocksForDay.length} PM(s) bloqueado(s)` : 'Bloquear PM'}
                                        >
                                          {blocksForDay.length > 0 ? '🔒' : '🔓'}
                                        </Button>
                                      </>
                                    );
                                  })()}
                                </>
                              )}
                            </div>
                          </div>

                          {/* Aviso de PMs Bloqueados */}
                          {(() => {
                            const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
                            const blocksForDay = blockedDays.filter(d => d.date === dateStr);
                            
                            if (blocksForDay.length > 0) {
                              const hasAll = blocksForDay.some(d => d.team === 'ALL');
                              const teams = hasAll ? ['TODOS'] : blocksForDay.map(d => d.team);
                              
                              return (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-2">
                                  <p className="text-xs font-semibold text-red-700">
                                    🔒 Bloqueado: {teams.join(', ')}
                                  </p>
                                  {blocksForDay[0].reason && (
                                    <p className="text-xs text-red-600 mt-0.5">
                                      {blocksForDay[0].reason}
                                    </p>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          })()}

                          {/* Jobs */}
                          <div className="space-y-2 max-h-[600px] overflow-auto">
                            {(() => {
                              // Filtrar jobs pelo viewAsTeam
                              const filteredJobs = viewAsTeam === 'ALL' 
                                ? selectedDayJobs 
                                : viewAsTeam === 'MEASURE'
                                  ? selectedDayJobs.filter(j => j.job_type === 'MEASURE')
                                  : selectedDayJobs.filter(j => j.team === viewAsTeam || (j.teams || []).includes(viewAsTeam));
                              const duplicateIds = getDuplicateJobIds(filteredJobs.filter(j => j.job_type !== 'MEASURE'));
                              
                              return filteredJobs.length === 0 ? (
                                <div className="text-center py-8 text-zinc-400">
                                  <CalendarDays className="h-10 w-10 mx-auto mb-2 text-zinc-300" />
                                  <p className="text-xs text-zinc-500">
                                    {language === 'pt-BR' ? 'Nenhum trabalho' : 'No jobs'}
                                  </p>
                                </div>
                              ) : (
                                filteredJobs.map(job => {
                                const isManager = user?.role === 'admin' || user?.role === 'general_manager';
                                const isMeasureJob = job.job_type === 'MEASURE';
                                return (
                                <div
                                  key={job.id}
                                  draggable={isManager}
                                  data-testid={`schedule-job-card-${job.id}`}
                                  data-job-type={job.job_type || ''}
                                  onDragStart={(e) => {
                                    if (!isManager) return;
                                    setDraggedJob(job);
                                    e.dataTransfer.effectAllowed = 'move';
                                    try { e.dataTransfer.setData('text/plain', job.id); } catch (err) {}
                                  }}
                                  onDragOver={(e) => {
                                    if (isManager && draggedJob && draggedJob.id !== job.id) {
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = 'move';
                                      if (dragOverJobId !== job.id) setDragOverJobId(job.id);
                                    }
                                  }}
                                  onDragLeave={() => {
                                    if (dragOverJobId === job.id) setDragOverJobId(null);
                                  }}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    if (isManager && draggedJob && draggedJob.id !== job.id) {
                                      setMergeConfirm({ source: draggedJob, target: job });
                                    }
                                    setDragOverJobId(null);
                                  }}
                                  onDragEnd={() => { setDraggedJob(null); setDragOverJobId(null); }}
                                  className={`rounded p-2 hover:bg-white hover:shadow transition-all text-xs cursor-pointer border ${
                                    dragOverJobId === job.id
                                      ? 'ring-2 ring-indigo-500 border-indigo-400 bg-indigo-50'
                                      : isMeasureJob ? 'bg-purple-50/60 border-purple-200' : 'bg-zinc-50 border-zinc-200'
                                  } ${draggedJob?.id === job.id ? 'opacity-40' : ''}`}
                                  onDoubleClick={() => {
                                    if (isManager) {
                                      setSelectedWork(job);
                                      setShowAddWorkModal(true);
                                    }
                                  }}
                                  onTouchStart={(e) => startTouchLongPress(job, e, isManager)}
                                  onTouchMove={cancelTouchLongPressIfMoved}
                                  onTouchEnd={clearTouchLongPress}
                                  style={draggedJob?.id === job.id ? { touchAction: 'none' } : undefined}
                                >
                                  {isManager && dragOverJobId === job.id && draggedJob && draggedJob.id !== job.id && (
                                    <div className="text-[10px] font-semibold text-indigo-700 mb-1 flex items-center gap-1">
                                      🔀 {language === 'pt-BR' ? 'Solte para mesclar aqui' : 'Drop to merge here'}
                                    </div>
                                  )}
                                  <div className="flex justify-between mb-1">
                                    <div className="flex items-center gap-1 flex-1 flex-wrap">
                                      <span className="font-semibold text-zinc-900">{job.customer_name}</span>
                                      {isManager && !isMeasureJob && job.measure_job_id && (
                                        <Badge
                                          data-testid={`measured-badge-${job.id}`}
                                          title={language === 'pt-BR' ? 'Instalação de um trabalho que você mediu' : 'Install of a job you measured'}
                                          className="bg-purple-100 border border-purple-400 text-purple-800 text-[9px] px-1 py-0 font-semibold"
                                        >
                                          📏 {language === 'pt-BR' ? 'Trabalho medido' : 'Measured job'}
                                        </Badge>
                                      )}
                                      {isManager && !isMeasureJob && job.sales_request_id && (
                                        <Badge
                                          data-testid={`sales-badge-${job.id}`}
                                          title={language === 'pt-BR' ? 'Instalação de um projeto vendido no Portal de Vendas' : 'Install of a project sold in the Sales Portal'}
                                          className="bg-teal-100 border border-teal-400 text-teal-800 text-[9px] px-1 py-0 font-semibold"
                                        >
                                          🛒 {language === 'pt-BR' ? 'Venda' : 'Sale'}{job.sales_contract_number ? ` · ${job.sales_contract_number}` : ''}
                                        </Badge>
                                      )}
                                      {duplicateIds.has(job.id) && (
                                        <Badge
                                          data-testid={`dup-warning-${job.id}`}
                                          title={language === 'pt-BR' ? 'Possível duplicado — arraste para mesclar' : 'Possible duplicate — drag to merge'}
                                          className="bg-amber-100 border border-amber-400 text-amber-800 text-[9px] px-1 py-0 font-semibold"
                                        >
                                          ⚠️ {language === 'pt-BR' ? 'Duplicado?' : 'Duplicate?'}
                                        </Badge>
                                      )}
                                      {job.duration_days > 1 && (
                                        <Badge className="bg-indigo-50 border border-indigo-200 text-indigo-700 text-[9px] px-1 py-0">
                                          {job.duration_days}d
                                        </Badge>
                                      )}
                                      {job.year_built && parseInt(job.year_built) < 1978 && (
                                        <Badge className="bg-yellow-100 border border-yellow-400 text-yellow-800 text-[9px] px-1 py-0 font-semibold">
                                          LEAD ⚠️
                                        </Badge>
                                      )}
                                      {!job.has_pdf && job.source === 'daily_work_import' && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (user?.role === 'admin' || user?.role === 'general_manager') {
                                              // Criar input file temporário
                                              const input = document.createElement('input');
                                              input.type = 'file';
                                              input.accept = '.pdf';
                                              input.onchange = async (evt) => {
                                                const file = evt.target.files[0];
                                                if (!file) return;
                                                
                                                try {
                                                  const token = sessionStorage.getItem('access_token');
                                                  const uploadFormData = new FormData();
                                                  uploadFormData.append('file', file);
                                                  
                                                  const res = await axios.post(`${API}/trucks/upload-document`, uploadFormData, {
                                                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                                                  });
                                                  
                                                  // Atualizar job com PDF
                                                  await axios.patch(`${API}/schedules/${job.id}`, {
                                                    draft_pdf_url: res.data.file_id,
                                                    has_pdf: true
                                                  });
                                                  
                                                  toast.success('✅ PDF adicionado!');
                                                  loadScheduleJobs(selectedDate.getMonth(), selectedDate.getFullYear());
                                                } catch (error) {
                                                  console.error('Erro:', error);
                                                  toast.error('Erro ao fazer upload');
                                                }
                                              };
                                              input.click();
                                            }
                                          }}
                                          className="bg-amber-50 border border-amber-200 text-amber-700 text-[9px] px-1 py-0 rounded hover:bg-amber-100 transition-colors"
                                          title="Click para adicionar PDF"
                                        >
                                          ⚠️ PDF
                                        </button>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      {isMeasureJob ? (
                                        <>
                                        <Badge data-testid={`measure-badge-${job.id}`} className="text-[10px] bg-purple-600 border border-purple-700 text-white font-semibold">
                                          📏 MEASURE
                                        </Badge>
                                        {job.measured_done && (
                                          <Badge data-testid={`measured-done-badge-${job.id}`} className="text-[10px] bg-emerald-600 border border-emerald-700 text-white font-semibold" title={`Medido${job.measured_done_at ? ` em ${new Date(job.measured_done_at).toLocaleDateString('pt-BR')}` : ''}${job.measured_done_by ? ` por ${job.measured_done_by}` : ''}`}>
                                            ✓ Medido
                                          </Badge>
                                        )}
                                        </>
                                      ) : job.teams && job.teams.length > 1 ? (
                                        <Badge data-testid={`multiteam-badge-${job.id}`} className="text-[10px] bg-amber-100 border border-amber-300 text-amber-800 font-semibold">
                                          👥 {job.teams.join(', ')}
                                        </Badge>
                                      ) : (
                                        <Badge className={`text-[10px] ${
                                          job.team === 'PM1' ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' :
                                          job.team === 'PM2' ? 'bg-amber-50 border border-amber-200 text-amber-700' :
                                          job.team === 'PM3' ? 'bg-rose-50 border border-rose-200 text-rose-700' :
                                          'bg-indigo-50 border border-indigo-200 text-indigo-700'
                                        }`}>
                                          {job.team}
                                        </Badge>
                                      )}
                                      {(user?.role === 'admin' || user?.role === 'general_manager') && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const isMulti = job.teams && job.teams.length > 1;
                                            const msg = isMulti
                                              ? `Este é um trabalho MULTI-EQUIPE (${job.teams.join(', ')}). Remover vai apagá-lo de TODAS as equipes. Continuar?`
                                              : `Deseja remover o trabalho "${job.customer_name}"?`;
                                            if (window.confirm(msg)) {
                                              handleDeleteScheduleJob(job.id);
                                            }
                                          }}
                                          className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-0.5 transition-colors"
                                          title="Remover trabalho"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Layout 2 Colunas: Trabalho (Esquerda) + Logística (Direita) */}
                                  {/* Mobile: Empilhado (Trabalho em cima) | Desktop: Lado a lado */}
                                  <div className={`grid grid-cols-1 ${isMeasureJob ? '' : 'md:grid-cols-2'} gap-2 mt-2`}>
                                    {/* Coluna Esquerda: Trabalho (MEASURE: medição, sem logística) */}
                                    <div className={`${isMeasureJob ? 'bg-purple-50 border-purple-200' : 'bg-zinc-100 border-zinc-200'} border rounded p-1.5`}>
                                      <div className={`text-[9px] font-semibold mb-0.5 ${isMeasureJob ? 'text-purple-800' : 'text-zinc-800'}`}>
                                        {isMeasureJob ? (language === 'pt-BR' ? '📏 Medição' : '📏 Measurement') : `🔧 Trabalho${job.job_type ? ` - ${job.job_type}` : ''}`}
                                      </div>
                                      <div className="text-[10px] text-zinc-700 space-y-0.5">
                                        {job.order_number && <div>📋 #{job.order_number}</div>}
                                        {job.tech_measure && <div>📏 {job.tech_measure}</div>}
                                        {(job.address || job.city) && (
                                          <button
                                            onClick={() => openInMaps(job.address, job.city, job.zip_code)}
                                            className="text-xs text-blue-600 hover:text-blue-800 underline cursor-pointer text-left block"
                                          >
                                            📍 {job.address}{job.city ? `, ${job.city}` : ''}{job.zip_code ? `, ${job.zip_code}` : ''}
                                          </button>
                                        )}
                                        {(job.time || user?.role === 'admin' || user?.role === 'general_manager') && (
                                          <ScheduleTimeEditor
                                            job={job}
                                            isAdmin={user?.role === 'admin' || user?.role === 'general_manager'}
                                            onSaved={() => {
                                              loadScheduleJobs(selectedDate.getMonth(), selectedDate.getFullYear());
                                              toast.success(language === 'pt-BR' ? '✅ Horário atualizado' : '✅ Time updated');
                                            }}
                                          />
                                        )}
                                        {job.year_built && <div>🏠 {job.year_built}</div>}
                                        {job.customer_email && <EmailWithCopy email={job.customer_email} />}
                                        {(job.doors > 0 || job.windows > 0) && <div>🚪 {job.doors || 0} • 🪟 {job.windows || 0}</div>}
                                        {!job.address && !job.time && !job.doors && !job.windows && (
                                          <div className="text-[9px] text-zinc-400 italic">Não preenchido</div>
                                        )}
                                        {isMeasureJob && job.measure_notes && <div className="whitespace-pre-wrap text-purple-900 bg-white/70 border border-purple-100 rounded p-1 mt-1" data-testid={`measure-notes-${job.id}`}>📝 {job.measure_notes}</div>}
                                        {isMeasureJob && job.measure_files?.length > 0 && (
                                          <div className="flex flex-wrap gap-1 mt-1" data-testid={`measure-files-${job.id}`}>
                                            {job.measure_files.map(f => (
                                              <a key={f.file_id} href={`${API}/files/${f.file_id}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 rounded-full border border-purple-300 bg-white px-1.5 py-0.5 text-[9px] text-purple-800 hover:bg-purple-100" title={f.filename}>📎 {f.filename}</a>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Coluna Direita: Logística (não existe na medição) */}
                                    {!isMeasureJob && (
                                    <div className="bg-cyan-50 border border-cyan-200 rounded p-1.5">
                                      <div className="text-[9px] font-semibold text-cyan-800 mb-0.5">🚚 Logística</div>
                                      <div className="text-[10px] text-cyan-700 space-y-0.5">
                                        {job.load_city && <div>📍 {job.load_city}{job.load_door ? ` - Porta ${job.load_door}` : ''}</div>}
                                        {job.load_time && <div>⏰ {job.load_time}</div>}
                                        {job.supervisor && <div>👤 {job.supervisor}</div>}
                                        {job.project_coordinator && <div>📊 {job.project_coordinator}</div>}
                                        {job.amount_due && <div>💰 ${job.amount_due}</div>}
                                        {(job.financing || job.payment_type) && (
                                          <div className="text-[9px]">
                                            {job.financing
                                              ? `🏦 ${job.payment_type && job.payment_type.toLowerCase().includes('cash') ? `Cash & ${job.financing}` : job.financing}`
                                              : job.payment_type}
                                          </div>
                                        )}
                                        {!job.load_city && !job.load_time && !job.supervisor && !job.project_coordinator && (
                                          <div className="text-[9px] text-cyan-400 italic">Não preenchido</div>
                                        )}
                                      </div>
                                    </div>
                                    )}
                                  </div>
                                  
                                  {/* Botões de Ação */}
                                  <div className="flex gap-2 mt-2 flex-wrap">
                                    {/* PDF (INSTALL) */}
                                    {job.job_type === 'INSTALL' && job.draft_pdf_url && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openPdfWithLoading({
                                            url: `${API}/files/${job.draft_pdf_url}`,
                                            title: `Installation Package - ${job.customer_name}`
                                          });
                                          setShowPDFModal(true);
                                        }}
                                        className="text-indigo-600 hover:text-indigo-700 text-[10px] flex items-center gap-1"
                                      >
                                        📄 PDF
                                      </button>
                                    )}
                                    
                                    {/* Notas (SERVICE) */}
                                    {job.job_type === 'SERVICE' && job.service_notes && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setServiceNotesData({
                                            notes: job.service_notes,
                                            title: `${job.customer_name} - ${job.order_number || 'Service'}`
                                          });
                                          setShowServiceNotesModal(true);
                                        }}
                                        className="text-emerald-600 hover:text-emerald-700 text-[10px] flex items-center gap-1"
                                      >
                                        📝 Notas
                                      </button>
                                    )}
                                    
                                    {/* Criar/Abrir Labor Bill (instalação/serviço) ou Labor Bill de medição (Extra "Cliente - Order" com o valor do preset Measure) */}
                                    {user?.role !== 'daily_leader' && (
                                    <button
                                      onClick={async (e) => { e.stopPropagation(); await openJobLaborBill(job); }}
                                      className={`${findJobLaborBill(job) ? 'text-green-600 hover:text-green-700' : 'text-blue-600 hover:text-blue-700'} text-[10px] flex items-center gap-1 font-semibold`}
                                      data-testid={`btn-labor-bill-${job.id}`}
                                      title={isMeasureJob ? 'Labor Bill da medição' : 'Labor Bill'}
                                    >
                                      {findJobLaborBill(job) ? '✅ Ver Labor Bill' : '💼 Labor Bill'}
                                    </button>
                                    )}
                                    {/* Measure Information (medição) ou Completion Checklist (instalação) — sempre visível */}
                                    {job.job_type === 'MEASURE' ? (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); trackEvent('open_measure_information', 'Abriu o link Measure Information'); setFormSheet({ title: 'Measure Information', url: EXTERNAL_LINKS.MEASURE_INFORMATION }); }}
                                      className="text-purple-600 hover:text-purple-700 text-[10px] flex items-center gap-1 font-semibold"
                                      data-testid={`btn-measure-information-${job.id}`}
                                      title="Abrir Measure Information"
                                    >
                                      📏 Measure Information
                                    </button>
                                    ) : (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); trackEvent('open_completion_checklist', 'Abriu o link Completion Checklist'); setFormSheet({ title: 'Completion Checklist', url: completionChecklistUrl(job) }); }}
                                      className="text-purple-600 hover:text-purple-700 text-[10px] flex items-center gap-1 font-semibold"
                                      data-testid={`btn-completion-checklist-${job.id}`}
                                      title="Abrir Completion Checklist"
                                    >
                                      🌐 Completion Checklist
                                    </button>
                                    )}
                                    {/* LEAD Form — só se year_built < 1978 */}
                                    {job.year_built && parseInt(job.year_built) < 1978 && (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); openLeadForm(job); }}
                                        className="text-orange-600 hover:text-orange-700 text-[10px] flex items-center gap-1 font-semibold"
                                        data-testid={`btn-lead-form-${job.id}`}
                                        title="Abrir LSWP Process (LEAD)"
                                      >
                                        🎯 LEAD Form
                                      </button>
                                    )}
                                  </div>
                                </div>
                                ); })
                            );
                          })()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 📏 Trabalhos medidos (Admin/GM) — card minimizável; ✔️ quando a instalação já entrou no schedule */}
                    <MeasuredJobsCard
                      language={language}
                      refreshKey={scheduleJobs}
                      billsKey={laborBills}
                      onEdit={(m) => { setMeasureSource(null); setProjectSource(null); setSelectedWork(m); setShowAddWorkModal(true); }}
                      onCreateInstall={(m) => { setSelectedWork(null); setProjectSource(null); setMeasureSource(m); setShowAddWorkModal(true); }}
                      onOpenBill={(m) => openJobLaborBill(m)}
                      onChanged={() => loadScheduleJobs(selectedDate.getMonth(), selectedDate.getFullYear())}
                    />

                    {/* 🤝 Projetos vendidos (Portal de Vendas) — contrato assinado → fábrica → pronto → schedule dos instaladores */}
                    <SoldProjectsCard
                      language={language}
                      refreshKey={scheduleJobs}
                      onSchedule={(p) => { setSelectedWork(null); setMeasureSource(null); setProjectSource(p); setShowAddWorkModal(true); }}
                    />

                    {/* 📋 Atividades Labor Bills & Supervisor (admin) — abaixo do Calendário, acima de Contas a Pagar */}
                    {user?.role === 'admin' && (
                      <DashboardActivityFeed language={language} />
                    )}

                    {/* Calendário de Contas a Pagar - Layout igual ao de trabalhos */}
                    {user?.role === 'admin' && (() => {
                      const alertsAsItems = companyAlertsHome.map(a => ({ ...a, next_due_date: a.expiry_date, frequency: 'once', value: 0, item_type: 'alert' }));
                      const myAlertsAsItems = myCalendarAlerts.map(a => ({ ...a, name: a.title, next_due_date: a.alert_date, frequency: 'once', value: 0, item_type: 'my_alert' }));
                      const allHomeItems = [
                        ...(homeBillViewFilter === 'alerts' ? [] : companyBillsHome),
                        ...(homeBillViewFilter === 'bills' ? [] : alertsAsItems),
                        ...(homeBillViewFilter === 'bills' ? [] : myAlertsAsItems)
                      ];
                      const filteredHomeItems = homeBillUnitFilter === 'all' ? allHomeItems : allHomeItems.filter(b => (b.unit_id || '') === homeBillUnitFilter);
                      return (
                      <div className="bg-white border border-zinc-200 rounded-lg p-3 sm:p-4 shadow-sm">
                        {/* Header */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center shadow-md">
                              <BarChart3 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white stroke-[1.5]" />
                            </div>
                            <h3 className="font-semibold text-sm sm:text-base text-zinc-900 tracking-tight">
                              Contas a Pagar
                            </h3>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-zinc-500">Custo mensal: <b className="text-orange-600">${(() => {
                              const t = companyBillsHome.reduce((s, b) => {
                                if (b.frequency === 'monthly') return s + (b.value || 0);
                                if (b.frequency === 'annual') return s + (b.value || 0) / 12;
                                if (b.frequency === 'quarterly') return s + (b.value || 0) / 3;
                                if (b.frequency === 'weekly') return s + (b.value || 0) * 4.33;
                                return s;
                              }, 0);
                              return t.toLocaleString('en-US', {minimumFractionDigits: 2});
                            })()}</b></span>
                            <select value={homeBillViewFilter} onChange={e => setHomeBillViewFilter(e.target.value)} className="border rounded px-2 py-1 text-xs h-7">
                              <option value="all">Tudo</option>
                              <option value="bills">Contas</option>
                              <option value="alerts">Alertas</option>
                            </select>
                            <select value={homeBillUnitFilter} onChange={e => setHomeBillUnitFilter(e.target.value)} className="border rounded px-2 py-1 text-xs h-7">
                              <option value="all">Todas</option>
                              {companyUnits.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setActiveTab('admin'); setAdminSubTab('contas'); }}>
                              Ver todas
                            </Button>
                          </div>
                        </div>

                        {/* Grid 2 Colunas igual ao de trabalhos */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Calendário esquerda */}
                          <div className="w-full">
                            <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 h-auto">
                              {/* Navegação de mês */}
                              <div className="flex items-center justify-between mb-2">
                                <button onClick={() => { if (homeBillMonth === 0) { setHomeBillMonth(11); setHomeBillYear(homeBillYear - 1); } else setHomeBillMonth(homeBillMonth - 1); }} className="text-zinc-400 hover:text-zinc-700 px-1">←</button>
                                <span className="text-xs font-semibold text-zinc-700 capitalize">{new Date(homeBillYear, homeBillMonth).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</span>
                                <button onClick={() => { if (homeBillMonth === 11) { setHomeBillMonth(0); setHomeBillYear(homeBillYear + 1); } else setHomeBillMonth(homeBillMonth + 1); }} className="text-zinc-400 hover:text-zinc-700 px-1">→</button>
                              </div>
                              <div className="grid grid-cols-7 gap-1 mb-2">
                                {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => (
                                  <div key={i} className="text-center text-[10px] font-semibold text-zinc-500 py-1">{d}</div>
                                ))}
                              </div>
                              <div className="grid grid-cols-7 gap-1">
                                {(() => {
                                  const now = new Date();
                                  const year = homeBillYear;
                                  const month = homeBillMonth;
                                  const daysInMonth = new Date(year, month + 1, 0).getDate();
                                  const firstDay = new Date(year, month, 1).getDay();
                                  const todayDay = now.getDate();
                                  const todayMonth = now.getMonth();
                                  const todayYear = now.getFullYear();
                                  const todayStr = now.toISOString().slice(0, 10);
                                  
                                  const billsByDay = getBillsForMonth(filteredHomeItems, year, month);
                                  
                                  const cells = [];
                                  for (let i = 0; i < firstDay; i++) cells.push(null);
                                  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
                                  
                                  return cells.map((day, idx) => {
                                    if (!day) return <div key={`e-${idx}`} className="aspect-square" />;
                                    const dayBills = billsByDay[day] || [];
                                    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                                    const isSelected = day === selectedBillDay;
                                    const isOverdue = dayBills.length > 0 && dateStr < todayStr;
                                    
                                    return (
                                      <button
                                        key={day}
                                        onClick={() => setSelectedBillDay(day)}
                                        className={`
                                          aspect-square rounded-md border text-xs font-medium transition-all relative flex flex-col items-center justify-center
                                          ${isSelected ? 'bg-indigo-500 text-white border-indigo-600' : 'bg-white border-zinc-200 text-zinc-700 hover:border-indigo-300 hover:bg-indigo-50'}
                                        `}
                                      >
                                        <span>{day}</span>
                                        {dayBills.length > 0 && (
                                          <span className={`text-[8px] leading-none flex gap-0.5 ${isSelected ? 'opacity-80' : ''}`}>
                                            {dayBills.map((b, bi) => {
                                              const ds = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                                              const unit = companyUnits.find(u => u.id === b.unit_id);
                                              if (b.paid_dates && b.paid_dates[ds]) return <span key={bi}>✅</span>;
                                              if (b.item_type === 'my_alert') return <span key={bi} className="inline-flex items-center justify-center w-4.5 h-4.5 rounded-full border-2 border-indigo-500 bg-indigo-50 p-0.5">{MY_ALERT_ICONS_HOME[b.category] || '📅'}</span>;
                                              if (b.item_type === 'alert') return <span key={bi} className="inline-flex items-center justify-center w-4.5 h-4.5 rounded-full border-2 border-yellow-500 bg-yellow-50 p-0.5">{BILL_ALERT_ICONS[b.category] || '⚠️'}</span>;
                                              if (unit) return <span key={bi} className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[7px] font-bold" style={{ backgroundColor: unit.bg_color, color: unit.text_color, border: `1.5px solid ${unit.border_color}` }}>{BILL_CATEGORY_ICONS[b.category] || '📌'}</span>;
                                              return <span key={bi}>{BILL_CATEGORY_ICONS[b.category] || '📌'}</span>;
                                            })}
                                          </span>
                                        )}
                                      </button>
                                    );
                                  });
                                })()}
                              </div>
                            </div>
                          </div>

                          {/* Área de detalhes direita */}
                          <div className="space-y-3 w-full md:w-auto">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-sm text-zinc-900">
                                  {new Date(homeBillYear, homeBillMonth).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                                </h4>
                                <p className="text-[10px] text-zinc-500">
                                  {filteredHomeItems.length} item(s) no mês
                                </p>
                              </div>
                            </div>
                            <div className="space-y-1.5 max-h-[350px] overflow-y-auto">
                            {(() => {
                              const bMap = getBillsForMonth(filteredHomeItems, homeBillYear, homeBillMonth);
                              // Coletar todos os itens do mês com seus dias
                              const allMonthItems = [];
                              Object.keys(bMap).sort((a,b) => Number(a) - Number(b)).forEach(day => {
                                bMap[day].forEach(b => {
                                  if (!allMonthItems.some(x => x.item.id === b.id && x.day === Number(day))) {
                                    allMonthItems.push({ day: Number(day), item: b });
                                  }
                                });
                              });
                              
                              if (allMonthItems.length === 0) return (
                                <div className="flex flex-col items-center justify-center py-8 text-zinc-400">
                                  <CalendarDays className="h-8 w-8 mb-2 opacity-30" />
                                  <span className="text-sm">Nenhum item neste mês</span>
                                </div>
                              );
                              
                              return allMonthItems.map(({ day, item: b }) => {
                                const ds = `${homeBillYear}-${String(homeBillMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                                const isPaid = b.paid_dates && b.paid_dates[ds];
                                const isMyAlert = b.item_type === 'my_alert';
                                const isAlert = b.item_type === 'alert' || isMyAlert;
                                const isHighlighted = day === selectedBillDay;
                                const unit = companyUnits.find(u => u.id === b.unit_id);
                                return (
                                <div key={b.id + ds} onClick={() => setHomeBillViewItem(b)} 
                                  className={`p-2.5 cursor-pointer transition-all ${
                                    isHighlighted ? 'ring-2 ring-indigo-500 shadow-md' : 'hover:shadow-sm'
                                  } ${
                                    isMyAlert ? 'border-2 border-dashed border-indigo-300 rounded-2xl bg-indigo-50/30' :
                                    isAlert ? 'border-2 border-dashed border-purple-300 rounded-2xl bg-purple-50/30' :
                                    isPaid ? 'border rounded-lg bg-green-50 border-green-200' :
                                    unit ? 'border rounded-lg' :
                                    'border rounded-lg bg-zinc-50 border-zinc-200'
                                  }`}
                                  style={!isAlert && !isPaid && unit ? { backgroundColor: unit.bg_color, borderColor: unit.border_color, borderLeftWidth: '4px' } : {}}
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1">
                                        <span className="text-[10px] text-zinc-400 font-mono w-5">{day}</span>
                                        <h5 className="font-semibold text-xs truncate" style={!isAlert && !isPaid && unit ? { color: unit.text_color } : {}}>
                                          {isPaid ? '✅' : isMyAlert ? (MY_ALERT_ICONS_HOME[b.category] || '📅') : isAlert ? (BILL_ALERT_ICONS[b.category] || '⚠️') : (BILL_CATEGORY_ICONS[b.category] || '📌')} {b.name}
                                        </h5>
                                        {unit && <span className="text-[9px] px-1 rounded" style={{ backgroundColor: unit.border_color, color: '#fff' }}>{unit.name}</span>}
                                      </div>
                                      <p className="text-[10px] text-zinc-500 ml-6">
                                        {isAlert ? b.category : `${b.category} | ${b.frequency === 'daily' ? 'Diária' : b.frequency === 'monthly' ? 'Mensal' : b.frequency === 'annual' ? 'Anual' : b.frequency === 'quarterly' ? 'Trim.' : 'Semanal'}`}
                                      </p>
                                    </div>
                                    {!isAlert && <span className={`font-bold text-xs flex-shrink-0 ${isPaid ? 'text-green-600 line-through' : 'text-orange-600'}`}>${b.value?.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>}
                                  </div>
                                </div>
                                );
                              });
                            })()}
                            </div>
                          </div>
                        </div>
                      </div>
                      );
                    })()}


                    {/* Cards de Caminhões - Grid Dinâmico - PALETA PREMIUM */}
                    {(myRentals.filter(r => r.status === 'pendente').length > 0 || myRentals.filter(r => r.status === 'ativo').length > 0 || trucks.filter(t => t.status === 'manutencao').length > 0) && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Card: Pedidos Pendentes - Compacto */}
                        {myRentals.filter(r => r.status === 'pendente').length > 0 && (
                          <div 
                            className="bg-white border-l-4 border-l-amber-500 border border-zinc-200 rounded-lg p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 group"
                            onClick={() => {
                              setActiveTab('trucks');
                              setTrucksSubTab('requests');
                            }}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center group-hover:scale-105 transition-transform shadow-md">
                                  <TruckIcon className="h-5 w-5 text-white stroke-[1.5]" />
                                </div>
                                <div>
                                  <h3 className="font-semibold text-sm text-zinc-900 tracking-tight">
                                    {language === 'pt-BR' ? 'Pedidos Pendentes' : 'Pending Requests'}
                                  </h3>
                                  <p className="text-xs text-zinc-500 mt-0.5">
                                    {language === 'pt-BR' ? 'Aguardando aprovação' : 'Awaiting approval'}
                                  </p>
                                </div>
                              </div>
                              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                                <span className="text-xl font-bold text-amber-700">
                                  {myRentals.filter(r => r.status === 'pendente').length}
                                </span>
                              </div>
                            </div>
                            <div className="border-t border-zinc-100 pt-2">
                              <p className="text-xs text-zinc-500 flex items-center gap-1">
                                <ArrowLeft className="h-3 w-3 rotate-180" />
                                {language === 'pt-BR' ? 'Clique para revisar' : 'Click to review'}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Card: Aluguéis Ativos - Compacto */}
                        {myRentals.filter(r => r.status === 'ativo').length > 0 && (
                          <div 
                            className="bg-white border-l-4 border-l-emerald-500 border border-zinc-200 rounded-lg p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 group"
                            onClick={() => {
                              setActiveTab('trucks');
                              setTrucksSubTab('dashboard');
                            }}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center group-hover:scale-105 transition-transform shadow-md">
                                  <CheckCircle className="h-5 w-5 text-white stroke-[1.5]" />
                                </div>
                                <div>
                                  <h3 className="font-semibold text-sm text-zinc-900 tracking-tight">
                                    {language === 'pt-BR' ? 'Aluguéis Ativos' : 'Active Rentals'}
                                  </h3>
                                  <p className="text-xs text-zinc-500 mt-0.5">
                                    {language === 'pt-BR' ? 'Em andamento' : 'In progress'}
                                  </p>
                                </div>
                              </div>
                              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">
                                <span className="text-xl font-bold text-emerald-700">
                                  {myRentals.filter(r => r.status === 'ativo').length}
                                </span>
                              </div>
                            </div>
                            <div className="border-t border-zinc-100 pt-2 flex justify-between items-center">
                              <p className="text-xs text-zinc-500">
                                {language === 'pt-BR' ? 'Ver detalhes' : 'View details'}
                              </p>
                              <span className="text-xs font-semibold text-emerald-600">
                                {language === 'pt-BR' ? 'Ver Todos →' : 'View All →'}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Card: Caminhões em Manutenção - Compacto */}
                    {trucks.filter(t => t.status === 'manutencao').length > 0 && (
                      <div 
                        className="bg-white border-l-4 border-l-orange-500 border border-zinc-200 rounded-lg p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 group"
                        onClick={() => {
                          setActiveTab('trucks');
                          setTrucksSubTab('fleet');
                        }}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center group-hover:scale-105 transition-transform shadow-md">
                              <Wrench className="h-5 w-5 text-white stroke-[1.5]" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-sm text-zinc-900 tracking-tight">
                                {language === 'pt-BR' ? 'Em Manutenção' : 'In Maintenance'}
                              </h3>
                              <p className="text-xs text-zinc-500 mt-0.5">
                                {language === 'pt-BR' ? 'Indisponíveis' : 'Unavailable'}
                              </p>
                            </div>
                          </div>
                          <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-1.5">
                            <span className="text-xl font-bold text-orange-700">
                              {trucks.filter(t => t.status === 'manutencao').length}
                            </span>
                          </div>
                        </div>
                        <div className="border-t border-zinc-100 pt-2">
                          <p className="text-xs text-zinc-500">
                            {language === 'pt-BR' ? 'Ver detalhes' : 'View details'}
                          </p>
                        </div>
                      </div>
                    )}

                      {/* Grid: Semana Atual + Estimativa Próximo Pagamento */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {hasPermission('home_cards', 'proximo_pagamento') && (
                    <div className="bg-white border border-zinc-200 rounded-lg shadow-sm">
                      <div className="p-4 border-b border-zinc-100">
                        <h3 className="font-semibold text-base text-zinc-900 tracking-tight flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md">
                            <span className="text-white text-sm">💰</span>
                          </div>
                          Estimativa Próximo Pagamento
                        </h3>
                      </div>
                      <div className="px-4 pb-4 pt-3">
                        {allPaymentEstimatesLoading ? (
                          <div className="text-center py-8 text-gray-500">
                            <div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                            <p className="text-sm">Calculando estimativas...</p>
                          </div>
                        ) : allPaymentEstimates ? (
                          <div className="space-y-4">
                            {/* Data de Pagamento */}
                            <div className="bg-white rounded-lg p-3 border border-green-200">
                              <div className="text-xs text-gray-600 mb-1">Data de Pagamento</div>
                              <div className="text-lg font-bold text-green-700">
                                {allPaymentEstimates.payment_date && new Date(allPaymentEstimates.payment_date + 'T12:00:00').toLocaleDateString('pt-BR', { timeZone: 'America/New_York', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Semana: {allPaymentEstimates.work_week_start && new Date(allPaymentEstimates.work_week_start + 'T12:00:00').toLocaleDateString('pt-BR', { timeZone: 'America/New_York', day: 'numeric', month: 'short' })} a {allPaymentEstimates.work_week_end && new Date(allPaymentEstimates.work_week_end + 'T12:00:00').toLocaleDateString('pt-BR', { timeZone: 'America/New_York', day: 'numeric', month: 'short' })}
                              </div>
                            </div>

                            {/* Lista de PMs - expandível */}
                            <div className="bg-white rounded-lg border border-green-200 p-4">
                              <h4 className="font-semibold text-gray-800 mb-3">Estimativas por PM</h4>
                              <div className="space-y-1">
                                {allPaymentEstimates.estimates.map((est, idx) => (
                                  <div key={idx}>
                                    <div className="flex items-center justify-between py-2 border-b border-gray-100 cursor-pointer hover:bg-gray-50 rounded px-1"
                                      onClick={() => setExpandedWeekPMs(prev => ({...prev, ['next_'+est.pm_user]: !prev['next_'+est.pm_user]}))}
                                    >
                                      <div className="flex items-center gap-3">
                                        <span className={`text-xs text-gray-400 transition-transform ${expandedWeekPMs['next_'+est.pm_user] ? 'rotate-90' : ''}`}>▶</span>
                                        <span className="font-medium text-green-700">{est.pm_user}:</span>
                                        <span className="text-sm text-gray-600">{est.total_jobs} trabalhos</span>
                                      </div>
                                      <div className="text-right">
                                        <div className="font-bold text-green-700">{formatMoney(est.estimated_payment)}</div>
                                        <div className="text-xs text-gray-500">{est.percentage}%</div>
                                      </div>
                                    </div>
                                    {expandedWeekPMs['next_'+est.pm_user] && est.bills && (
                                      <div className="ml-6 mt-1 mb-2 bg-gray-50 rounded-lg p-3 space-y-1">
                                        {est.bills.map((bill, bi) => (
                                          <div key={bi} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0 text-xs">
                                            <div>
                                              <span className="font-medium text-gray-700">{bill.job_name}</span>
                                              <span className="text-gray-400 ml-2">{formatNyDate(bill.install_date)}</span>
                                            </div>
                                            <div className="text-right">
                                              <span className="text-gray-400 line-through mr-2">{formatMoney(bill.labor_total)}</span>
                                              <span className="font-bold text-green-700">{formatMoney(bill.estimated_value || bill.labor_total)}</span>
                                            </div>
                                          </div>
                                        ))}
                                        <div className="flex justify-between pt-2 border-t border-gray-200 text-xs font-semibold">
                                          <span className="text-gray-700">Total {est.pm_user}</span>
                                          <div className="text-right">
                                            <span className="text-green-700">{formatMoney(est.estimated_payment)}</span>
                                            <span className="text-purple-600 ml-3">Lucro: {formatMoney(est.profit)}</span>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Total Geral */}
                            <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-4 border-2 border-green-400">
                              <div className="flex items-center justify-between">
                                <span className="font-bold text-gray-800 text-lg">TOTAL GERAL:</span>
                                <span className="font-bold text-green-700 text-2xl">{formatMoney(allPaymentEstimates.total_general)}</span>
                              </div>
                              <div className="text-xs text-gray-600 mt-1">{allPaymentEstimates.total_pms} PMs</div>
                            </div>

                            {/* Lucro */}
                            <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg p-4 border-2 border-purple-400">
                              <div className="flex items-center justify-between">
                                <span className="font-bold text-gray-800 text-lg">LUCRO (Empresa):</span>
                                <span className="font-bold text-purple-700 text-2xl">
                                  {formatMoney(allPaymentEstimates.estimates.reduce((sum, est) => sum + (est.profit || 0), 0))}
                                </span>
                              </div>
                              <div className="text-xs text-gray-600 mt-1">Comissão/Desconto total</div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <p className="text-sm">Carregando...</p>
                          </div>
                        )}
                      </div>
                    </div>
                    )}

                    {hasPermission('home_cards', 'semana_atual') && (
                    <div className="bg-white border border-zinc-200 rounded-lg shadow-sm">
                      <div className="p-4 border-b border-zinc-100">
                        <h3 className="font-semibold text-base text-zinc-900 tracking-tight flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
                            <span className="text-white text-sm">📋</span>
                          </div>
                          Semana Atual em Andamento
                        </h3>
                      </div>

                      <div className="px-4 pb-4 pt-3">
                        {!currentWeekEstimate || (currentWeekEstimate.total_jobs === 0 && !(currentWeekEstimate.pms?.length > 0)) ? (
                          <div className="text-center py-8 text-gray-500">
                            <p className="text-sm">Nenhum trabalho nesta semana ainda</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {/* Data de Pagamento */}
                            <div className="bg-white rounded-lg p-3 border border-blue-200">
                              <div className="text-xs text-gray-600 mb-1">📅 Data de Pagamento</div>
                              <div className="text-lg font-bold text-blue-700">
                                {currentWeekEstimate.payment_date && new Date(currentWeekEstimate.payment_date + 'T12:00:00').toLocaleDateString('pt-BR', { timeZone: 'America/New_York', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Semana: {currentWeekEstimate.work_week_start && new Date(currentWeekEstimate.work_week_start + 'T12:00:00').toLocaleDateString('pt-BR', { timeZone: 'America/New_York', day: 'numeric', month: 'short' })} a {currentWeekEstimate.work_week_end && new Date(currentWeekEstimate.work_week_end + 'T12:00:00').toLocaleDateString('pt-BR', { timeZone: 'America/New_York', day: 'numeric', month: 'short' })}
                              </div>
                            </div>

                            {/* Lista de PMs (ADMIN) - minimizáveis */}
                            {currentWeekEstimate.pms && currentWeekEstimate.pms.length > 0 && (
                              <div className="bg-white rounded-lg border border-blue-200 p-4">
                                <h4 className="font-semibold text-gray-800 mb-3">Estimativas por PM</h4>
                                <div className="space-y-1">
                                  {currentWeekEstimate.pms.map((pm, idx) => (
                                    <div key={idx}>
                                      <div className="flex items-center justify-between py-2 border-b border-gray-100 cursor-pointer hover:bg-gray-50 rounded px-1"
                                        onClick={() => setExpandedWeekPMs(prev => ({...prev, [pm.pm_user]: !prev[pm.pm_user]}))}
                                      >
                                        <div className="flex items-center gap-3">
                                          <span className={`text-xs text-gray-400 transition-transform ${expandedWeekPMs[pm.pm_user] ? 'rotate-90' : ''}`}>▶</span>
                                          <span className="font-medium text-blue-700">{pm.pm_user}:</span>
                                          <span className="text-sm text-gray-600">{pm.total_jobs} trabalhos</span>
                                        </div>
                                        <div className="text-right">
                                          <div className="font-bold text-blue-700">{formatMoney(pm.estimated_payment)}</div>
                                          <div className="text-xs text-gray-500">{pm.percentage}%</div>
                                        </div>
                                      </div>
                                      {expandedWeekPMs[pm.pm_user] && pm.bills && (
                                        <div className="ml-6 mt-1 mb-2 bg-gray-50 rounded-lg p-3 space-y-1">
                                          {pm.bills.map((bill, bi) => (
                                            <div key={bi} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0 text-xs">
                                              <div>
                                                <span className="font-medium text-gray-700">{bill.job_name}</span>
                                                <span className="text-gray-400 ml-2">{formatNyDate(bill.install_date)}</span>
                                              </div>
                                              <div className="text-right">
                                                <span className="text-gray-400 line-through mr-2">{formatMoney(bill.labor_total)}</span>
                                                <span className="font-bold text-blue-700">{formatMoney(bill.estimated_value || bill.labor_total)}</span>
                                              </div>
                                            </div>
                                          ))}
                                          <div className="flex justify-between pt-2 border-t border-gray-200 text-xs font-semibold">
                                            <span className="text-gray-700">Total {pm.pm_user}</span>
                                            <div className="text-right">
                                              <span className="text-blue-700">{formatMoney(pm.estimated_payment)}</span>
                                              <span className="text-purple-600 ml-3">Lucro: {formatMoney(pm.profit)}</span>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Lista de Bills (PM) */}
                            {currentWeekEstimate.bills && currentWeekEstimate.bills.length > 0 && !currentWeekEstimate.pms && (
                              <div className="bg-white rounded-lg border border-blue-200 p-4">
                                <h4 className="font-semibold text-gray-800 mb-3">📋 Trabalhos da Semana</h4>
                                <div className="space-y-2">
                                  {currentWeekEstimate.bills.map((bill, i) => (
                                    <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                                      <div className="flex items-center gap-3">
                                        <span className="font-medium text-gray-700">{bill.job_name}</span>
                                        <span className="text-xs text-gray-500">{formatNyDate(bill.install_date)}</span>
                                      </div>
                                      <div className="font-bold text-blue-700">{formatMoney(bill.labor_total)}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Total Geral */}
                            <div className="bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg p-4 border-2 border-blue-400">
                              <div className="flex items-center justify-between">
                                <span className="font-bold text-gray-800 text-lg">TOTAL GERAL:</span>
                                <span className="font-bold text-blue-700 text-2xl">{formatMoney(currentWeekEstimate.total_payment || currentWeekEstimate.estimated_payment)}</span>
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                {currentWeekEstimate.total_jobs} trabalhos | Despesas: {formatMoney(currentWeekEstimate.total_expenses)}
                              </div>
                            </div>

                            {/* Lucro (Empresa) */}
                            {currentWeekEstimate.pms && (
                              <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg p-4 border-2 border-purple-400">
                                <div className="flex items-center justify-between">
                                  <span className="font-bold text-gray-800 text-lg">LUCRO (Empresa):</span>
                                  <span className="font-bold text-purple-700 text-2xl">
                                    {formatMoney(currentWeekEstimate.pms.reduce((sum, pm) => sum + (pm.profit || 0), 0))}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  Comissão/Desconto total
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    )}
                    </div>

                    {hasPermission('home_cards', 'resumo_semanal') && (
                    <div className="bg-white border border-zinc-200 rounded-lg p-4 shadow-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                        <h3 className="font-semibold text-base text-zinc-900 tracking-tight flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md">
                            <span className="text-white text-sm">💰</span>
                          </div>
                          {t('weeklyReportSummary')}
                        </h3>
                        
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                          {/* Dropdown de Mês */}
                          {weeklyReportSummary.availableMonths && weeklyReportSummary.availableMonths.length > 0 && (
                            <select
                              value={selectedMonth}
                              onChange={(e) => {
                                const newMonth = e.target.value;
                                console.log('📅 Dropdown onChange - Novo mês selecionado:', newMonth);
                                setSelectedMonth(newMonth);
                                loadWeeklyReportSummary(true, newMonth);
                              }}
                              className="border border-zinc-300 rounded-lg px-3 py-1 text-sm bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                            >
                              {weeklyReportSummary.availableMonths.map(month => {
                                // month = '2025-10', '2025-09', etc.
                                const [year, monthNum] = month.split('-');
                                const monthNames = language === 'pt-BR' ? 
                                  ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                                   'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'] :
                                  ['January', 'February', 'March', 'April', 'May', 'June',
                                   'July', 'August', 'September', 'October', 'November', 'December'];
                                const monthName = `${monthNames[parseInt(monthNum) - 1]} de ${year}`;
                                return (
                                  <option key={month} value={month}>
                                    {monthName}
                                  </option>
                                );
                              })}
                            </select>
                          )}
                          
                          <div className="flex gap-2">
                            <Button 
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                loadWeeklyReportSummary();
                              }} 
                              variant="outline" 
                              size="sm"
                              disabled={weeklyReportLoading}
                              className="h-8 w-8 p-0 flex items-center justify-center"
                              title={language === 'pt-BR' ? 'Atualizar' : 'Refresh'}
                            >
                              {weeklyReportLoading ? (
                                <div className="animate-spin h-3 w-3 border border-gray-300 border-t-gray-600 rounded-full"></div>
                              ) : (
                                <>🔄</>
                              )}
                            </Button>
                            
                            {user?.role === 'admin' && (
                              <>
                                {/* Botões compactos apenas com ícones */}
                                <Button 
                                  type="button"
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    try {
                                      setWeeklyReportLoading(true);
                                      const { data } = await axios.post(`${API}/admin/populate-weekly-summaries`);
                                      toast.success(`✅ ${data.summaries_created} resumos criados na variável`);
                                      // Recarregar após popular
                                      setTimeout(() => {
                                        loadWeeklyReportSummary();
                                      }, 1000);
                                    } catch (error) {
                                      toast.error('Erro ao popular variável');
                                    } finally {
                                      setWeeklyReportLoading(false);
                                    }
                                  }} 
                                  variant="secondary" 
                                  size="sm"
                                  disabled={weeklyReportLoading}
                                  className="h-8 w-8 p-0 flex items-center justify-center"
                                  title={language === 'pt-BR' ? 'Popular Variável' : 'Populate Variable'}
                                >
                                  📦
                                </Button>
                                
                                <Button 
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    safeSetActiveTab('payroll');
                                    // Automatically switch to Reports sub-tab
                                    setTimeout(() => {
                                      setPayrollSubTab('reports');
                                    }, 100);
                                  }} 
                                  variant="outline" 
                                  size="sm"
                                  className="h-8 w-8 p-0 flex items-center justify-center"
                                  title={language === 'pt-BR' ? 'Ver Relatórios' : 'View Reports'}
                                >
                                  👁️
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-base text-zinc-600 mb-6 flex items-center gap-3 font-medium">
                        <div className="w-1 h-6 bg-gradient-to-b from-emerald-500 to-teal-600 rounded-full"></div>
                        {(() => {
                          if (!selectedMonth) return 'Carregando...';
                          const [year, month] = selectedMonth.split('-');
                          const monthNames = [
                            'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                            'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
                          ];
                          const monthName = `${monthNames[parseInt(month) - 1]} de ${year}`;
                          return `${language === 'pt-BR' ? 'Dados de' : 'Data from'} ${monthName} • ${weeklyReportSummary.weeklyData?.length || 0} ${language === 'pt-BR' ? 'semanas' : 'weeks'}`;
                        })()}
                      </div>
                      
                      {/* Render weekly sections */}
                      {weeklyReportSummary.weeklyData && weeklyReportSummary.weeklyData.length > 0 ? (
                        weeklyReportSummary.weeklyData.map((week, index) => (
                          <div key={`week-${week.weekEnding}-${index}`} className="mb-6 pb-6 border-b border-zinc-100 last:border-b-0">
                            {/* Week title */}
                            <div className="text-md font-bold text-zinc-900 mb-3 flex items-center gap-2">
                              <div className="w-1 h-5 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full"></div>
                              {language === 'pt-BR' ? 'Semana' : 'Week'}: {formatDate(week.weekEnding)}
                            </div>
                            
                            {/* Cards de estatísticas por semana */}
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-3 mb-4">
                              <div className="bg-white rounded-lg p-2 sm:p-4 border-l-4 border-blue-500">
                                <div className="flex items-center gap-1 sm:gap-2">
                                  <div className="text-blue-500 text-base sm:text-lg">👷</div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-blue-700 text-xs sm:text-sm truncate">
                                      {language === 'pt-BR' ? 'Total trabalhos' : 'Total Jobs'}
                                    </div>
                                    <div className="text-lg sm:text-2xl font-bold text-blue-600">{week.total_jobs || 0}</div>
                                  </div>
                                </div>
                              </div>
                              <div className="bg-white rounded-lg p-2 sm:p-4 border-l-4 border-green-500">
                                <div className="flex items-center gap-1 sm:gap-2">
                                  <div className="text-green-500 text-base sm:text-lg">💰</div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-green-700 text-xs sm:text-sm truncate">{t('totalLabor')}</div>
                                    <div className="text-lg sm:text-2xl font-bold text-green-600">{formatMoney(week.total_labor || 0)}</div>
                                  </div>
                                </div>
                              </div>
                              <div className="bg-white rounded-lg p-2 sm:p-4 border-l-4 border-purple-500">
                                <div className="flex items-center gap-1 sm:gap-2">
                                  <div className="text-purple-500 text-base sm:text-lg">💜</div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-purple-700 text-xs sm:text-sm truncate">{t('totalFinal')}</div>
                                    <div className="text-lg sm:text-2xl font-bold text-purple-600">{formatMoney(week.total_final || 0)}</div>
                                  </div>
                                </div>
                              </div>
                              <div className="bg-white rounded-lg p-2 sm:p-4 border-l-4 border-orange-500">
                                <div className="flex items-center gap-1 sm:gap-2">
                                  <div className="text-orange-500 text-base sm:text-lg">📊</div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-orange-700 text-xs sm:text-sm truncate">{t('profit')}</div>
                                    <div className="text-lg sm:text-2xl font-bold text-orange-600">
                                      {formatMoney(week.lucro || 0)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="bg-white rounded-lg p-2 sm:p-4 border-l-4 border-red-500">
                                <div className="flex items-center gap-1 sm:gap-2">
                                  <div className="text-red-500 text-base sm:text-lg">💸</div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-red-700 text-xs sm:text-sm truncate">
                                      {language === 'pt-BR' ? 'Total Despesas' : 'Total Expenses'}
                                    </div>
                                    <div className="text-lg sm:text-2xl font-bold text-red-600">{formatMoney(week.total_expenses || 0)}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          {language === 'pt-BR' ? 
                            'Nenhum relatório encontrado. Clique "🔄 Atualizar" para carregar dados do banco.' :
                            'No reports found. Click "🔄 Refresh" to load data from database.'
                          }
                        </div>
                      )}

                    </div>
                    )}

                    {hasPermission('home_cards', 'resumo_mensal') && (
                    <div className="bg-white border border-zinc-200 rounded-lg p-4 shadow-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                        <h3 className="font-semibold text-base text-zinc-900 tracking-tight flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md">
                            <span className="text-white text-sm">📈</span>
                          </div>
                          {language === 'pt-BR' ? 'Resumo Mensal/Anual' : 'Monthly/Yearly Summary'}
                        </h3>
                        
                        {/* Dropdown de Mês */}
                        {monthlyYearlySummary.availableMonths && monthlyYearlySummary.availableMonths.length > 0 && (
                          <div className="flex items-center gap-2">
                            <select
                              value={selectedMonthForSummary}
                              onChange={(e) => {
                                const newMonth = e.target.value;
                                console.log('📅 Resumo Mensal ADMIN - Novo mês:', newMonth);
                                setSelectedMonthForSummary(newMonth);
                                calculateMonthlyYearlySummary(newMonth);
                              }}
                              className="border border-zinc-300 rounded-lg px-3 py-1 text-sm bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                            >
                              {monthlyYearlySummary.availableMonths.map(month => {
                                const [year, monthNum] = month.split('-');
                                const monthNames = language === 'pt-BR' ? 
                                  ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                                   'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'] :
                                  ['January', 'February', 'March', 'April', 'May', 'June',
                                   'July', 'August', 'September', 'October', 'November', 'December'];
                                const monthName = `${monthNames[parseInt(monthNum) - 1]} de ${year}`;
                                return (
                                  <option key={month} value={month}>
                                    {monthName}
                                  </option>
                                );
                              })}
                            </select>
                            
                            <Button 
                              type="button"
                              onClick={() => calculateMonthlyYearlySummary(selectedMonthForSummary)}
                              variant="outline" 
                              size="sm"
                              className="flex items-center gap-2"
                            >
                              🔄 {t('update')}
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      {/* Resumo do Mês Selecionado */}
                      <div className="mb-4">
                        <div className="text-sm font-bold text-zinc-900 mb-3 flex items-center gap-2">
                          <div className="w-1 h-5 bg-gradient-to-b from-emerald-500 to-teal-600 rounded-full"></div>
                          {selectedMonthForSummary ? (() => {
                            const [year, monthNum] = selectedMonthForSummary.split('-');
                            const monthNames = [
                              'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                              'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
                            ];
                            return `${monthNames[parseInt(monthNum) - 1]} de ${year}`.toUpperCase();
                          })() : 'MÊS ATUAL'}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                          <div className="bg-white rounded-lg p-3 border-l-4 border-blue-500 shadow-sm">
                            <div className="text-blue-700 text-[10px] mb-1">Total Trabalhos</div>
                            <div className="text-xl font-bold text-blue-600">{monthlyYearlySummary.month.total_jobs}</div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border-l-4 border-green-500 shadow-sm">
                            <div className="text-green-700 text-[10px] mb-1">Total Labor</div>
                            <div className="text-xl font-bold text-green-600">{formatMoney(monthlyYearlySummary.month.total_labor)}</div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border-l-4 border-purple-500 shadow-sm">
                            <div className="text-purple-700 text-[10px] mb-1">Total Final</div>
                            <div className="text-xl font-bold text-purple-600">{formatMoney(monthlyYearlySummary.month.total_final)}</div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border-l-4 border-yellow-500 shadow-sm">
                            <div className="text-yellow-700 text-[10px] mb-1">Lucro</div>
                            <div className="text-xl font-bold text-yellow-600">{formatMoney(monthlyYearlySummary.month.lucro)}</div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border-l-4 border-orange-500 shadow-sm">
                            <div className="text-orange-700 text-[10px] mb-1">Total Despesas</div>
                            <div className="text-xl font-bold text-orange-600">{formatMoney(monthlyYearlySummary.month.total_expenses)}</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Resumo do Ano do Mês Selecionado */}
                      <div>
                        <div className="text-sm font-semibold text-emerald-800 mb-3">
                          📊 {selectedMonthForSummary ? selectedMonthForSummary.substring(0, 4) : new Date().getFullYear()}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                          <div className="bg-white rounded-lg p-3 border-l-4 border-blue-600 shadow-sm">
                            <div className="text-blue-700 text-[10px] mb-1">Total Trabalhos</div>
                            <div className="text-xl font-bold text-blue-600">{monthlyYearlySummary.year.total_jobs}</div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border-l-4 border-green-600 shadow-sm">
                            <div className="text-green-700 text-[10px] mb-1">Total Labor</div>
                            <div className="text-xl font-bold text-green-600">{formatMoney(monthlyYearlySummary.year.total_labor)}</div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border-l-4 border-purple-600 shadow-sm">
                            <div className="text-purple-700 text-[10px] mb-1">Total Final</div>
                            <div className="text-xl font-bold text-purple-600">{formatMoney(monthlyYearlySummary.year.total_final)}</div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border-l-4 border-yellow-600 shadow-sm">
                            <div className="text-yellow-700 text-[10px] mb-1">Lucro</div>
                            <div className="text-xl font-bold text-yellow-600">{formatMoney(monthlyYearlySummary.year.lucro)}</div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border-l-4 border-orange-600 shadow-sm">
                            <div className="text-orange-700 text-[10px] mb-1">Total Despesas</div>
                            <div className="text-xl font-bold text-orange-600">{formatMoney(monthlyYearlySummary.year.total_expenses)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    )}



                    {/* Card "Trabalhos Diários" removido - funcionalidade movida para botão no Calendário */}
                    {/* Card de Logs removido da Home - movido para aba Admin */}
                  </div>
                ) : (
                  // USER HOME - Redesenhado
                  <div className="space-y-4">
                    {/* Header Central PM - Igual ao Admin */}
                    <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border border-zinc-200 rounded-xl p-4 text-center shadow-sm">
                      <div className="flex items-center justify-center gap-3">
                        <img 
                          src={LOGO_URL} 
                          alt="PM Logo" 
                          className="w-10 h-10"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/icon-192x192.png';
                          }}
                        />
                        <div className="text-left">
                          <h1 className="text-lg font-bold text-zinc-900 tracking-tight leading-none">
                            PM Home Remodeling
                          </h1>
                          <p className="text-sm text-zinc-600 mt-0.5 font-medium">
                            {t('welcome')}, {user?.first_name || user?.username || (language === 'pt-BR' ? 'Usuário' : 'User')} {user?.last_name || ''}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <>

                    {/* Central de Avisos PM */}
                    <div 
                      className={`bg-white border-l-4 border-l-indigo-500 border border-zinc-200 rounded-lg p-4 cursor-pointer transition-all duration-300 hover:shadow-lg ${
                        announcementsExpanded ? 'col-span-full' : ''
                      }`}
                      onClick={() => setAnnouncementsExpanded(!announcementsExpanded)}
                      style={{
                        minHeight: announcementsExpanded ? '250px' : '70px'
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md">
                            <MessageSquare className="h-5 w-5 text-white stroke-[1.5]" />
                          </div>
                          <h4 className="font-semibold text-base text-zinc-900 tracking-tight">
                            {language === 'pt-BR' ? 'Central de Avisos' : 'Announcements'}
                          </h4>
                        </div>
                        <div className="flex items-center gap-2">
                          {announcements.length > 0 && (
                            <Badge className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold px-2 py-0.5">
                              {announcements.length}
                            </Badge>
                          )}
                          <span className="text-zinc-400 text-sm">
                            {announcementsExpanded ? '▼' : '▶'}
                          </span>
                        </div>
                      </div>
                      
                      {!announcementsExpanded ? (
                        <div className="text-zinc-500 text-sm mt-1">
                          {announcements.length === 0 ? 
                            (language === 'pt-BR' ? 'Nenhum aviso no momento' : 'No announcements') :
                            (language === 'pt-BR' ? `${announcements.length} aviso(s)` : `${announcements.length} announcement(s)`)}
                        </div>
                      ) : (
                        <div className="space-y-3 mt-4">
                          {user?.role === 'admin' && (
                            <div className="flex justify-end mb-4">
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingAnnouncement(null);
                                  setAnnouncementForm({
                                    title: '',
                                    content: '',
                                    priority: 'normal',
                                    expires_at: ''
                                  });
                                  setShowAnnouncementModal(true);
                                }}
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                {language === 'pt-BR' ? 'Novo Aviso' : 'New Announcement'}
                              </Button>
                            </div>
                          )}
                          
                          <UnsentBillsAlert
                            bills={laborBills}
                            user={user}
                            language={language}
                            onOpenBill={(id) => loadLaborBill(id)}
                            onGoToLabor={() => setActiveTab('drafts')}
                            onMarkAllSent={markAllSupervisorSent}
                            onReconcile={reconcileSentFromHistory}
                          />
                          {announcements.length === 0 ? (
                            <div className="text-center py-8 text-zinc-400">
                              <div className="text-4xl mb-2">📢</div>
                              <div className="text-zinc-600">{language === 'pt-BR' ? 'Nenhum aviso no momento' : 'No announcements'}</div>
                            </div>
                          ) : (
                            <div className="space-y-3 max-h-[600px] overflow-auto">
                              {announcements.map((announcement) => (
                                <div key={announcement.id} className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 hover:bg-white transition-colors">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <h5 className="font-semibold text-sm text-zinc-900">{announcement.title}</h5>
                                        <Badge className={`text-xs font-semibold ${
                                          announcement.priority === 'urgent' ? 'bg-rose-50 border border-rose-300 text-rose-700' :
                                          announcement.priority === 'high' ? 'bg-amber-50 border border-amber-300 text-amber-700' :
                                          announcement.priority === 'normal' ? 'bg-indigo-50 border border-indigo-300 text-indigo-700' :
                                          'bg-zinc-50 border border-zinc-300 text-zinc-700'
                                        }`}>
                                          {announcement.priority === 'urgent' ? '🚨' :
                                           announcement.priority === 'high' ? '⚠️' :
                                           announcement.priority === 'normal' ? 'ℹ️' : '📝'}
                                        </Badge>
                                      </div>
                                      <p className="text-zinc-600 text-sm">{announcement.content}</p>
                                      <div className="text-xs text-zinc-500 mt-2">
                                        {formatDateTime(announcement.created_at)}
                                      </div>
                                    </div>
                                    <div className="flex gap-1 ml-3">
                                      {/* Botão X para todos os usuários dispensarem */}
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            const token = sessionStorage.getItem('access_token');
                                            await axios.post(
                                              `${API}/announcements/${announcement.id}/dismiss`,
                                              {},
                                              { headers: { Authorization: `Bearer ${token}` } }
                                            );
                                            // Recarregar avisos
                                            loadAnnouncements();
                                            toast.success(language === 'pt-BR' ? 'Aviso dispensado' : 'Announcement dismissed');
                                          } catch (error) {
                                            console.error('❌ Error dismissing announcement:', error);
                                            toast.error(language === 'pt-BR' ? 'Erro ao dispensar aviso' : 'Error dismissing announcement');
                                          }
                                        }}
                                        className="text-gray-400 hover:text-white h-6 w-6 p-0"
                                        title={language === 'pt-BR' ? 'Dispensar aviso' : 'Dismiss announcement'}
                                      >
                                        ✕
                                      </Button>
                                      {user?.role === 'admin' && (
                                        <>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setEditingAnnouncement(announcement);
                                              setAnnouncementForm({
                                                title: announcement.title,
                                                content: announcement.content,
                                                priority: announcement.priority,
                                                expires_at: announcement.expires_at ? announcement.expires_at.split('T')[0] : ''
                                              });
                                              setShowAnnouncementModal(true);
                                            }}
                                            className="text-blue-400 hover:text-blue-300 border-blue-400 h-6 w-6 p-0"
                                          >
                                            <Edit className="h-3 w-3" />
                                          </Button>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                              console.log('🗑️ Delete button clicked, stopping propagation');
                                              e.stopPropagation();
                                              console.log('🗑️ About to call deleteAnnouncement with ID:', announcement.id);
                                              deleteAnnouncement(announcement.id);
                                            }}
                                            className="text-red-400 hover:text-red-300 border-red-400 h-6 w-6 p-0"
                                            title={language === 'pt-BR' ? 'Deletar aviso' : 'Delete announcement'}
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* CALENDÁRIO DE SCHEDULE - PM (Mesmo do Admin) */}
                    <div className="bg-white border border-zinc-200 rounded-lg p-3 sm:p-4 shadow-sm">
                      {/* Header - Responsivo */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md">
                            <CalendarDays className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white stroke-[1.5]" />
                          </div>
                          <h3 className="font-semibold text-sm sm:text-base text-zinc-900 tracking-tight">
                            {language === 'pt-BR' ? 'Calendário' : 'Schedule'}
                          </h3>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-xs border-zinc-300 px-1.5 sm:px-2 py-1 h-7 sm:h-auto"
                            onClick={() => changeMonth(-1)}
                          >
                            ←
                          </Button>
                          <span className="text-xs sm:text-sm font-semibold text-zinc-900 mx-1 sm:mx-2 whitespace-nowrap">
                            {selectedDate.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })}
                          </span>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-xs border-zinc-300 px-1.5 sm:px-2 py-1 h-7 sm:h-auto"
                            onClick={() => changeMonth(1)}
                          >
                            →
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid="pm-open-map-btn"
                            className="text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50 px-2 py-1 h-7 sm:h-auto font-semibold ml-1"
                            onClick={() => setShowScheduleMapModal(true)}
                            title="Mapa dos trabalhos"
                          >
                            🗺️ {language === 'pt-BR' ? 'Mapa' : 'Map'}
                          </Button>
                        </div>
                      </div>

                      {/* Grid 2 Colunas: iPad+ lado a lado, celular empilhado */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        
                        {/* Calendário PM - Largura fixa em todas as resoluções */}
                        <div className="w-full">
                          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 h-auto">
                            <div className="grid grid-cols-7 gap-1 mb-2">
                              {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => (
                                <div key={i} className="text-center text-[10px] font-semibold text-zinc-500 py-1">{d}</div>
                              ))}
                            </div>
                            <div className="grid grid-cols-7 gap-1">
                              {getDaysInMonth(selectedDate).map((day, idx) => {
                                if (!day) {
                                  return <div key={`empty-${idx}`} className="aspect-square"></div>;
                                }
                                
                                const dayDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
                                // Usar data local para evitar bug de timezone
                                const year = dayDate.getFullYear();
                                const month = String(dayDate.getMonth() + 1).padStart(2, '0');
                                const dayNum = String(dayDate.getDate()).padStart(2, '0');
                                const dateStr = `${year}-${month}-${dayNum}`;
                                const dayJobs = scheduleJobs.filter(j => 
                                  j.date === dateStr || isJobInRange(j, dateStr)
                                );
                                const isSelected = selectedDate.getDate() === day;
                                
                                // Para PM: verificar se ELE está bloqueado nesse dia
                                const pmUsername = user?.username;
                                const blocksForThisPM = blockedDays.filter(d => 
                                  d.date === dateStr && (d.team === pmUsername || d.team === 'ALL')
                                );
                                const isBlockedForPM = blocksForThisPM.length > 0;
                                
                                // Calcular total de janelas e portas do PM neste dia
                                const totalWindows = dayJobs.reduce((sum, job) => sum + (job.windows || 0), 0);
                                const totalDoors = dayJobs.reduce((sum, job) => sum + (job.doors || 0), 0);
                                const hasService = dayJobs.some(j => j.job_type === 'SERVICE');
                                
                                return (
                                  <button
                                    key={day}
                                    data-calendar-day="true"
                                    onClick={() => selectDay(day)}
                                    onMouseEnter={(e) => {
                                      console.log('🔥 PM CALENDAR - Mouse enter!', dayJobs.length);
                                      if (dayJobs.length > 0) {
                                        if (hoverTimerRef.current) {
                                          clearTimeout(hoverTimerRef.current);
                                          hoverTimerRef.current = null;
                                        }
                                        handleJobMouseEnter(dayJobs, e);
                                      } else {
                                        setHoveredJobs([]);
                                        setHoveredJobsData([]);
                                      }
                                    }}
                                    onTouchStart={(e) => {
                                      console.log('📱 PM CALENDAR - Touch!', dayJobs.length);
                                      if (dayJobs.length > 0) {
                                        if (hoverTimerRef.current) {
                                          clearTimeout(hoverTimerRef.current);
                                          hoverTimerRef.current = null;
                                        }
                                        handleJobMouseEnter(dayJobs, e);
                                      }
                                    }}
                                    onMouseLeave={handleJobMouseLeave}
                                    className={`
                                      aspect-square rounded-md border text-xs font-medium transition-all relative flex flex-col items-center justify-center
                                      ${isSelected ? 'bg-indigo-500 text-white border-indigo-600' : 'bg-white border-zinc-200 text-zinc-700 hover:border-indigo-300 hover:bg-indigo-50'}
                                    `}
                                  >
                                    <span>{day}</span>
                                    
                                    {/* Contagem de janelas e portas, ou S para SERVICE */}
                                    {dayJobs.length > 0 && (
                                      <div className="flex gap-1 text-[10px] mt-0.5 font-semibold">
                                        {hasService && (
                                          <span className="text-emerald-600">S</span>
                                        )}
                                        {(totalDoors > 0 || totalWindows > 0) && (
                                          <>
                                            {totalDoors > 0 && <span>🚪{totalDoors}</span>}
                                            {totalWindows > 0 && <span>🪟{totalWindows}</span>}
                                          </>
                                        )}
                                      </div>
                                    )}
                                    
                                    {isBlockedForPM && (
                                      <div className="absolute top-0 right-0 text-[10px]">🔒</div>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3 w-full md:w-auto">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-sm text-zinc-900">
                                {selectedDate.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })}
                              </h4>
                              <p className="text-[10px] text-zinc-500">
                                {selectedDate.toLocaleDateString('pt-BR', { weekday: 'long' })}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => openTrackedLink(EXTERNAL_LINKS.REORDER_FORM, 'open_reorder_form', 'Abriu o link Reorder Form')}
                                title="Abrir Reorder Formstack"
                                data-testid="btn-reorder-formstack-pm"
                                className="text-[10px] font-semibold px-2 py-1 rounded border border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100 whitespace-nowrap"
                              >
                                🔄 Reorder
                              </button>
                              <Badge className="bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs">
                                {selectedDayJobs.length}
                              </Badge>
                            </div>
                          </div>

                          {/* Aviso de Bloqueio (PM View) */}
                          {(() => {
                            const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
                            const pmUsername = user?.username;
                            
                            // Filtrar bloqueios que afetam este PM
                            const blocksForThisPM = blockedDays.filter(d => 
                              d.date === dateStr && (d.team === pmUsername || d.team === 'ALL')
                            );
                            
                            if (blocksForThisPM.length > 0) {
                              const hasAll = blocksForThisPM.some(d => d.team === 'ALL');
                              const message = hasAll ? 'TODOS os PMs' : pmUsername;
                              const reason = blocksForThisPM[0].reason;
                              
                              return (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-2 mt-2">
                                  <p className="text-xs font-semibold text-red-700">
                                    🔒 {language === 'pt-BR' ? 'Bloqueado:' : 'Blocked:'} {message}
                                  </p>
                                  {reason && (
                                    <p className="text-xs text-red-600 mt-0.5">
                                      {reason}
                                    </p>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          })()}

                          <div className="space-y-2 max-h-[600px] overflow-auto">
                            {selectedDayJobs.length === 0 ? (
                              <div className="text-center py-8 text-zinc-400">
                                <CalendarDays className="h-10 w-10 mx-auto mb-2 text-zinc-300" />
                                <p className="text-xs text-zinc-500">
                                  {language === 'pt-BR' ? 'Nenhum trabalho' : 'No jobs'}
                                </p>
                              </div>
                            ) : (
                              selectedDayJobs.map(job => (
                                <div
                                  key={job.id}
                                  className="bg-zinc-50 border border-zinc-200 rounded p-2 hover:bg-white hover:shadow transition-all text-xs"
                                >
                                  <div className="flex justify-between mb-1">
                                    <div className="flex items-center gap-1 flex-1 flex-wrap">
                                      <span className="font-semibold text-zinc-900">{job.customer_name}</span>
                                      {job.duration_days > 1 && (
                                        <Badge className="bg-indigo-50 border border-indigo-200 text-indigo-700 text-[9px] px-1 py-0">
                                          {job.duration_days}d
                                        </Badge>
                                      )}
                                      {job.year_built && parseInt(job.year_built) < 1978 && (
                                        <Badge className="bg-yellow-100 border border-yellow-400 text-yellow-800 text-[9px] px-1 py-0 font-semibold">
                                          LEAD ⚠️
                                        </Badge>
                                      )}
                                    </div>
                                    {job.teams && job.teams.length > 1 ? (
                                      <Badge data-testid={`multiteam-badge-${job.id}`} className="bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-semibold">
                                        👥 {job.teams.join(', ')}
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10px]">
                                        {job.team}
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  {/* Layout 2 Colunas: Trabalho (Esquerda) + Logística (Direita) */}
                                  {/* Mobile: Empilhado (Trabalho em cima) | Desktop: Lado a lado */}
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                                    {/* Coluna Esquerda: Trabalho */}
                                    <div className="bg-zinc-100 border border-zinc-200 rounded p-1.5">
                                      <div className="text-[9px] font-semibold text-zinc-800 mb-0.5">
                                        🔧 Trabalho{job.job_type ? ` - ${job.job_type}` : ''}
                                      </div>
                                      <div className="text-[10px] text-zinc-700 space-y-0.5">
                                        {job.order_number && <div>📋 #{job.order_number}</div>}
                                        {job.tech_measure && <div>📏 {job.tech_measure}</div>}
                                        {(job.address || job.city) && (
                                          <button
                                            onClick={() => openInMaps(job.address, job.city, job.zip_code)}
                                            className="text-xs text-blue-600 hover:text-blue-800 underline cursor-pointer text-left block"
                                          >
                                            📍 {job.address}{job.city ? `, ${job.city}` : ''}{job.zip_code ? `, ${job.zip_code}` : ''}
                                          </button>
                                        )}
                                        {(job.time || user?.role === 'admin' || user?.role === 'general_manager') && (
                                          <ScheduleTimeEditor
                                            job={job}
                                            isAdmin={user?.role === 'admin' || user?.role === 'general_manager'}
                                            onSaved={() => {
                                              loadScheduleJobs(selectedDate.getMonth(), selectedDate.getFullYear());
                                              toast.success(language === 'pt-BR' ? '✅ Horário atualizado' : '✅ Time updated');
                                            }}
                                          />
                                        )}
                                        {job.year_built && <div>🏠 {job.year_built}</div>}
                                        {job.customer_email && <EmailWithCopy email={job.customer_email} />}
                                        {(job.doors > 0 || job.windows > 0) && <div>🚪 {job.doors || 0} • 🪟 {job.windows || 0}</div>}
                                        {!job.address && !job.time && !job.doors && !job.windows && (
                                          <div className="text-[9px] text-zinc-400 italic">Não preenchido</div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Coluna Direita: Logística */}
                                    <div className="bg-cyan-50 border border-cyan-200 rounded p-1.5">
                                      <div className="text-[9px] font-semibold text-cyan-800 mb-0.5">🚚 Logística</div>
                                      <div className="text-[10px] text-cyan-700 space-y-0.5">
                                        {job.load_city && <div>📍 {job.load_city}{job.load_door ? ` - Porta ${job.load_door}` : ''}</div>}
                                        {job.load_time && <div>⏰ {job.load_time}</div>}
                                        {job.supervisor && <div>👤 {job.supervisor}</div>}
                                        {job.project_coordinator && <div>📊 {job.project_coordinator}</div>}
                                        {job.amount_due && <div>💰 ${job.amount_due}</div>}
                                        {(job.financing || job.payment_type) && (
                                          <div className="text-[9px]">
                                            {job.financing
                                              ? `🏦 ${job.payment_type && job.payment_type.toLowerCase().includes('cash') ? `Cash & ${job.financing}` : job.financing}`
                                              : job.payment_type}
                                          </div>
                                        )}
                                        {!job.load_city && !job.load_time && !job.supervisor && !job.project_coordinator && (
                                          <div className="text-[9px] text-cyan-400 italic">Não preenchido</div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Botões de Ação - PM View */}
                                  <div className="flex gap-2 mt-2 flex-wrap">
                                    {/* PDF (INSTALL) */}
                                    {job.job_type === 'INSTALL' && job.draft_pdf_url && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openPdfWithLoading({
                                            url: `${API}/files/${job.draft_pdf_url}`,
                                            title: `Installation Package - ${job.customer_name}`
                                          });
                                          setShowPDFModal(true);
                                        }}
                                        className="text-indigo-600 hover:text-indigo-700 text-[10px]"
                                      >
                                        📄 PDF
                                      </button>
                                    )}
                                    
                                    {/* Notas (SERVICE) */}
                                    {job.job_type === 'SERVICE' && job.service_notes && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setServiceNotesData({
                                            notes: job.service_notes,
                                            title: `${job.customer_name} - ${job.order_number || 'Service'}`
                                          });
                                          setShowServiceNotesModal(true);
                                        }}
                                        className="text-emerald-600 hover:text-emerald-700 text-[10px]"
                                      >
                                        📝 Notas
                                      </button>
                                    )}
                                    
                                    {/* Criar/Abrir Labor Bill (instalação/serviço) ou Labor Bill de medição */}
                                    {user?.role !== 'daily_leader' && (
                                    <button
                                      onClick={async (e) => { e.stopPropagation(); await openJobLaborBill(job); }}
                                      className={`${findJobLaborBill(job) ? 'text-green-600 hover:text-green-700' : 'text-blue-600 hover:text-blue-700'} text-[10px] font-semibold`}
                                      data-testid={`btn-labor-bill-${job.id}`}
                                    >
                                      {findJobLaborBill(job) ? '✅ Ver Labor Bill' : '💼 Labor Bill'}
                                    </button>
                                    )}
                                    {/* Measure Information (medição) ou Completion Checklist (instalação) — sempre visível */}
                                    {job.job_type === 'MEASURE' ? (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); trackEvent('open_measure_information', 'Abriu o link Measure Information'); setFormSheet({ title: 'Measure Information', url: EXTERNAL_LINKS.MEASURE_INFORMATION }); }}
                                      className="text-purple-600 hover:text-purple-700 text-[10px] flex items-center gap-1 font-semibold"
                                      data-testid={`btn-measure-information-${job.id}`}
                                      title="Abrir Measure Information"
                                    >
                                      📏 Measure Information
                                    </button>
                                    ) : (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); trackEvent('open_completion_checklist', 'Abriu o link Completion Checklist'); setFormSheet({ title: 'Completion Checklist', url: completionChecklistUrl(job) }); }}
                                      className="text-purple-600 hover:text-purple-700 text-[10px] flex items-center gap-1 font-semibold"
                                      data-testid={`btn-completion-checklist-${job.id}`}
                                      title="Abrir Completion Checklist"
                                    >
                                      🌐 Completion Checklist
                                    </button>
                                    )}
                                    {/* LEAD Form — só se year_built < 1978 */}
                                    {job.year_built && parseInt(job.year_built) < 1978 && (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); openLeadForm(job); }}
                                        className="text-orange-600 hover:text-orange-700 text-[10px] flex items-center gap-1 font-semibold"
                                        data-testid={`btn-lead-form-${job.id}`}
                                        title="Abrir LSWP Process (LEAD)"
                                      >
                                        🎯 LEAD Form
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Info Warehouse removido - dados estão no Calendário */}

                    {/* Tickets de Suporte - Redesenhado */}
                    <div className="flex justify-center">
                    <div 
                      className="bg-white border border-zinc-200 rounded-lg p-4 shadow-sm w-full max-w-xl"
                      draggable="true"
                      onDragStart={(e) => handleCardDragStart(e, 'tickets')}
                      onDragEnd={handleCardDragEnd}
                      onDragOver={(e) => handleCardDragOver(e)}
                      onDragEnter={(e) => handleCardDragEnter(e, 'tickets')}
                      onDrop={(e) => handleCardDrop(e, 'tickets')}
                    >
                      <div className="resize-icon" title="Arraste para redimensionar"></div>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-md">
                            <Ticket className="h-4 w-4 text-white stroke-[1.5]" />
                          </div>
                          <h4 className="font-semibold text-base text-zinc-900 tracking-tight">
                            {t('supportTickets')}
                          </h4>
                        </div>
                        <Button
                          onClick={() => setShowQuickTicketModal(true)}
                          size="sm"
                          className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-xs font-semibold"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          {language === 'pt-BR' ? 'Novo' : 'New'}
                        </Button>
                      </div>
                      
                      {/* Stats compactos */}
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="bg-rose-50 border border-rose-200 rounded p-2 text-center">
                          <div className="text-rose-700 font-bold text-lg">
                            {userTicketStats?.stats?.open || 0}
                          </div>
                          <div className="text-[10px] text-rose-600 leading-tight">{t('open')}</div>
                        </div>
                        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-center">
                          <div className="text-amber-700 font-bold text-lg">
                            {userTicketStats?.stats?.pending || 0}
                          </div>
                          <div className="text-[10px] text-amber-600 leading-tight">{t('pending')}</div>
                        </div>
                        <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
                          <div className="text-emerald-700 font-bold text-lg">
                            {userTicketStats?.stats?.resolved || 0}
                          </div>
                          <div className="text-[10px] text-emerald-600 leading-tight">{t('resolved')}</div>
                        </div>
                      </div>
                      
                      {/* Lista de tickets recentes */}
                      <div className="bg-white rounded-lg border divide-y max-h-60 overflow-auto">
                        {userTicketStats?.recent_tickets?.length > 0 ? (
                          userTicketStats.recent_tickets.slice(0, 3).map((ticket) => (
                            <div key={ticket.id} className="p-2 hover:bg-gray-50 cursor-pointer" onClick={() => {
                              safeSetActiveTab('support');
                            }}>
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-800 truncate">
                                    {ticket.subject || ticket.title}
                                  </div>
                                  <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                                    {ticket.description || ticket.message || ticket.subject || ticket.title || 'Sem descrição'}
                                  </div>
                                  <div className="text-xs text-gray-400 mt-1">
                                    📅 {new Date(ticket.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}
                                  </div>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${
                                  ticket.status === 'open' ? 'bg-red-100 text-red-700' :
                                  ticket.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-green-100 text-green-700'
                                }`}>
                                  {ticket.status === 'open' ? t('open') : ticket.status === 'pending' ? t('pending') : t('resolved')}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            {language === 'pt-BR' ? 'Nenhum ticket ainda' : 'No tickets yet'}
                          </div>
                        )}
                      </div>
                      
                      {/* Botão Ver Todos */}
                      <Button 
                        onClick={() => safeSetActiveTab('support')}
                        variant="outline"
                        size="sm"
                        className="w-full mt-2 text-xs"
                      >
                        {language === 'pt-BR' ? 'Ver Todos os Tickets' : 'View All Tickets'}
                      </Button>
                    </div>
                    </div>

                    {/* Grid 2 colunas: Resumo Semanal + Resumo Mensal/Anual - flexbox para reordenação */}
                    <div className="flex flex-col gap-4 cards-container lg:grid lg:grid-cols-2">


                      {/* Card: Estimativa Pagamento PM */}
                      {hasPermission('home_cards', 'proximo_pagamento') && (
                      <div className={`bg-white border border-zinc-200 rounded-lg shadow-sm resizable-card ${!paymentEstimateExpanded ? 'card-minimized' : ''}`}>
                        <div className="p-4 border-b border-zinc-100 cursor-pointer" onClick={() => setPaymentEstimateExpanded(!paymentEstimateExpanded)}>
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-base text-zinc-900 tracking-tight flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md">
                                <span className="text-white text-sm">💰</span>
                              </div>
                              Próximo Pagamento
                            </h3>
                            <span className={`text-zinc-400 text-sm transition-transform ${paymentEstimateExpanded ? 'rotate-180' : ''}`}>▼</span>
                          </div>
                        </div>
                        {paymentEstimateExpanded && (
                        <div className="px-4 pb-4 pt-3">
                          {!nextPaymentEstimate || (nextPaymentEstimate.total_jobs === 0 && nextPaymentEstimate.total_days === 0 && (!nextPaymentEstimate.backcharges || nextPaymentEstimate.backcharges.length === 0)) ? (
                            <div className="text-center py-8 text-gray-500">
                              <p className="text-sm">Nenhum trabalho na semana anterior</p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <div className="bg-white rounded-lg p-3 border border-green-200">
                                <div className="text-xs text-gray-600 mb-1">Data de Pagamento</div>
                                <div className="text-lg font-bold text-green-700">
                                  {nextPaymentEstimate.payment_date && new Date(nextPaymentEstimate.payment_date + 'T12:00:00').toLocaleDateString('pt-BR', { timeZone: 'America/New_York', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  Semana: {nextPaymentEstimate.work_week_start && new Date(nextPaymentEstimate.work_week_start + 'T12:00:00').toLocaleDateString('pt-BR', { timeZone: 'America/New_York', day: 'numeric', month: 'short' })} a {nextPaymentEstimate.work_week_end && new Date(nextPaymentEstimate.work_week_end + 'T12:00:00').toLocaleDateString('pt-BR', { timeZone: 'America/New_York', day: 'numeric', month: 'short' })}
                                </div>
                              </div>

                              {/* Daily Leader: Líder + Helpers + Despesas */}
                              {nextPaymentEstimate.total_days !== undefined && (
                                <div className="bg-white rounded-lg border border-green-200 p-3 sm:p-4">
                                  <h4 className="font-semibold text-gray-800 mb-2 text-sm">👨‍💼 Líder</h4>
                                  <div className="flex justify-between py-2 border-b border-gray-100">
                                    <span className="text-sm text-gray-600">Dias Trabalhados</span>
                                    <span className="font-bold">{nextPaymentEstimate.total_days}</span>
                                  </div>
                                  <div className="flex justify-between py-2 border-b border-gray-100">
                                    <span className="text-sm text-gray-600">Diária</span>
                                    <span className="font-bold">{formatMoney(nextPaymentEstimate.daily_rate)}</span>
                                  </div>
                                  <div className="flex justify-between py-2 border-b border-gray-100">
                                    <span className="text-sm text-gray-600">Subtotal Líder</span>
                                    <span className="font-bold text-blue-600">{formatMoney(nextPaymentEstimate.estimated_payment)}</span>
                                  </div>

                                  {/* Helpers Fixos */}
                                  {nextPaymentEstimate.helpers && nextPaymentEstimate.helpers.length > 0 && (
                                    <>
                                      <h4 className="font-semibold text-gray-800 mt-4 mb-2 text-sm">👷 Helpers</h4>
                                      {nextPaymentEstimate.helpers.map((h, i) => (
                                        <div key={i} className="py-2 border-b border-gray-100">
                                          <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-700 font-medium">{h.nome}</span>
                                            <span className="font-bold text-green-600">{formatMoney(h.total)}</span>
                                          </div>
                                          <div className="text-xs text-gray-500 mt-0.5">
                                            {h.dias} dias × {formatMoney(h.diaria)}/dia
                                          </div>
                                        </div>
                                      ))}
                                    </>
                                  )}

                                  {/* Helpers Avulsos */}
                                  {nextPaymentEstimate.helpers_avulsos && nextPaymentEstimate.helpers_avulsos.length > 0 && (
                                    <>
                                      <h4 className="font-semibold text-gray-800 mt-4 mb-2 text-sm">🔧 Helpers Avulsos</h4>
                                      {nextPaymentEstimate.helpers_avulsos.map((h, i) => (
                                        <div key={i} className="py-2 border-b border-gray-100">
                                          <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-700 font-medium">{h.nome}</span>
                                            <span className="font-bold text-orange-500">{formatMoney(h.total)}</span>
                                          </div>
                                          <div className="text-xs text-gray-500 mt-0.5">
                                            {h.dias} dias × {formatMoney(h.diaria)}/dia
                                          </div>
                                        </div>
                                      ))}
                                    </>
                                  )}

                                  {/* Despesas */}
                                  <div className="flex justify-between py-2 border-b border-gray-100 mt-3">
                                    <span className="text-sm text-gray-600 font-medium">Despesas</span>
                                    <span className="font-bold text-orange-600">{formatMoney(nextPaymentEstimate.expenses || 0)}</span>
                                  </div>
                                </div>
                              )}

                              {/* PM: bills */}
                              {nextPaymentEstimate.bills && nextPaymentEstimate.bills.length > 0 && (
                                <div className="bg-white rounded-lg border border-green-200 p-4">
                                  <h4 className="font-semibold text-gray-800 mb-3">📋 Trabalhos da Semana</h4>
                                  <div className="space-y-2">
                                    {nextPaymentEstimate.bills.map((bill, i) => (
                                      <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                                        <div className="flex items-center gap-3">
                                          <span className="font-medium text-gray-700">{bill.job_name}</span>
                                          <span className="text-xs text-gray-500">{formatNyDate(bill.install_date)}</span>
                                        </div>
                                        <div className="font-bold text-green-700">{formatMoney(bill.estimated_value || bill.net_value || bill.labor_total)}</div>
                                      </div>
                                    ))}
                                  </div>
                                  {/* Backcharges */}
                                  {nextPaymentEstimate.backcharges && nextPaymentEstimate.backcharges.length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-gray-100">
                                      {nextPaymentEstimate.backcharges.map((bc, i) => (
                                        <div key={i} className="flex items-center justify-between py-1.5 bg-yellow-50 rounded px-2 mb-1">
                                          <span className="text-sm text-yellow-800">⚠️ {bc.descricao}</span>
                                          <span className="font-bold text-red-600">-{formatMoney(bc.valor)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {/* Adicionais */}
                                  {nextPaymentEstimate.adicionais && nextPaymentEstimate.adicionais.length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-gray-100" data-testid="next-payment-adicionais">
                                      {nextPaymentEstimate.adicionais.map((ad, i) => (
                                        <div key={i} className="flex items-center justify-between py-1.5 bg-green-50 rounded px-2 mb-1">
                                          <span className="text-sm text-green-800">💰 {ad.descricao}</span>
                                          <span className="font-bold text-green-700">+{formatMoney(ad.valor)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-4 border-2 border-green-400">
                                <div className="flex items-center justify-between">
                                  <span className="font-bold text-gray-800 text-lg">TOTAL:</span>
                                  <span className="font-bold text-green-700 text-2xl">{formatMoney(nextPaymentEstimate.total || nextPaymentEstimate.estimated_payment)}</span>
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  {nextPaymentEstimate.total_days !== undefined
                                    ? `Líder: ${formatMoney(nextPaymentEstimate.estimated_payment)}${nextPaymentEstimate.helpers_total > 0 ? ` + Helpers: ${formatMoney(nextPaymentEstimate.helpers_total)}` : ''}${nextPaymentEstimate.expenses > 0 ? ` + Despesas: ${formatMoney(nextPaymentEstimate.expenses)}` : ''}`
                                    : `${nextPaymentEstimate.total_jobs} trabalhos | Despesas: ${formatMoney(nextPaymentEstimate.total_expenses)} | ${nextPaymentEstimate.percentage}%`
                                  }
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        )}
                      </div>
                      )}

                      {/* Card: Semana Atual PM - escondido para daily_leader */}
                      {hasPermission('home_cards', 'semana_atual') && user?.role !== 'daily_leader' && (
                      <div className={`bg-white border border-zinc-200 rounded-lg shadow-sm resizable-card ${!currentWeekExpanded ? 'card-minimized' : ''}`}>
                        <div className="p-4 border-b border-zinc-100 cursor-pointer" onClick={() => setCurrentWeekExpanded(!currentWeekExpanded)}>
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-base text-zinc-900 tracking-tight flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
                                <span className="text-white text-sm">📋</span>
                              </div>
                              Semana Atual em Andamento
                            </h3>
                            <span className={`text-zinc-400 text-sm transition-transform ${currentWeekExpanded ? 'rotate-180' : ''}`}>▼</span>
                          </div>
                        </div>
                        {currentWeekExpanded && (
                        <div className="px-4 pb-4 pt-3">
                          {!currentWeekEstimate || (currentWeekEstimate.total_jobs === 0 && currentWeekEstimate.total_days === 0) ? (
                            <div className="text-center py-8 text-gray-500">
                              <p className="text-sm">Nenhum trabalho nesta semana ainda</p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <div className="bg-white rounded-lg p-3 border border-blue-200">
                                <div className="text-xs text-gray-600 mb-1">📅 Data de Pagamento</div>
                                <div className="text-lg font-bold text-blue-700">
                                  {currentWeekEstimate.payment_date && new Date(currentWeekEstimate.payment_date + 'T12:00:00').toLocaleDateString('pt-BR', { timeZone: 'America/New_York', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  Semana: {currentWeekEstimate.work_week_start && new Date(currentWeekEstimate.work_week_start + 'T12:00:00').toLocaleDateString('pt-BR', { timeZone: 'America/New_York', day: 'numeric', month: 'short' })} a {currentWeekEstimate.work_week_end && new Date(currentWeekEstimate.work_week_end + 'T12:00:00').toLocaleDateString('pt-BR', { timeZone: 'America/New_York', day: 'numeric', month: 'short' })}
                                </div>
                              </div>

                              {/* Daily Leader: Líder + Helpers + Despesas */}
                              {currentWeekEstimate.total_days !== undefined && (
                                <div className="bg-white rounded-lg border border-blue-200 p-3 sm:p-4">
                                  <h4 className="font-semibold text-gray-800 mb-2 text-sm">👨‍💼 Líder</h4>
                                  <div className="flex justify-between py-2 border-b border-gray-100">
                                    <span className="text-sm text-gray-600">Dias Trabalhados</span>
                                    <span className="font-bold">{currentWeekEstimate.total_days}</span>
                                  </div>
                                  <div className="flex justify-between py-2 border-b border-gray-100">
                                    <span className="text-sm text-gray-600">Diária</span>
                                    <span className="font-bold">{formatMoney(currentWeekEstimate.daily_rate)}</span>
                                  </div>
                                  <div className="flex justify-between py-2 border-b border-gray-100">
                                    <span className="text-sm text-gray-600">Subtotal Líder</span>
                                    <span className="font-bold text-blue-600">{formatMoney(currentWeekEstimate.estimated_payment)}</span>
                                  </div>

                                  {/* Helpers Fixos */}
                                  {currentWeekEstimate.helpers && currentWeekEstimate.helpers.length > 0 && (
                                    <>
                                      <h4 className="font-semibold text-gray-800 mt-4 mb-2 text-sm">👷 Helpers</h4>
                                      {currentWeekEstimate.helpers.map((h, i) => (
                                        <div key={i} className="py-2 border-b border-gray-100">
                                          <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-700 font-medium">{h.nome}</span>
                                            <span className="font-bold text-green-600">{formatMoney(h.total)}</span>
                                          </div>
                                          <div className="text-xs text-gray-500 mt-0.5">
                                            {h.dias} dias × {formatMoney(h.diaria)}/dia
                                          </div>
                                        </div>
                                      ))}
                                    </>
                                  )}

                                  {/* Helpers Avulsos */}
                                  {currentWeekEstimate.helpers_avulsos && currentWeekEstimate.helpers_avulsos.length > 0 && (
                                    <>
                                      <h4 className="font-semibold text-gray-800 mt-4 mb-2 text-sm">🔧 Helpers Avulsos</h4>
                                      {currentWeekEstimate.helpers_avulsos.map((h, i) => (
                                        <div key={i} className="py-2 border-b border-gray-100">
                                          <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-700 font-medium">{h.nome}</span>
                                            <span className="font-bold text-orange-500">{formatMoney(h.total)}</span>
                                          </div>
                                          <div className="text-xs text-gray-500 mt-0.5">
                                            {h.dias} dias × {formatMoney(h.diaria)}/dia
                                          </div>
                                        </div>
                                      ))}
                                    </>
                                  )}

                                  {/* Despesas */}
                                  <div className="flex justify-between py-2 border-b border-gray-100 mt-3">
                                    <span className="text-sm text-gray-600 font-medium">Despesas</span>
                                    <span className="font-bold text-orange-600">{formatMoney(currentWeekEstimate.expenses || 0)}</span>
                                  </div>
                                </div>
                              )}

                              {/* PM: bills */}
                              {currentWeekEstimate.bills && currentWeekEstimate.bills.length > 0 && (
                                <div className="bg-white rounded-lg border border-blue-200 p-4">
                                  <h4 className="font-semibold text-gray-800 mb-3">Trabalhos da Semana</h4>
                                  <div className="space-y-2">
                                    {currentWeekEstimate.bills.map((bill, i) => (
                                      <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                                        <div className="flex items-center gap-3">
                                          <span className="font-medium text-gray-700">{bill.job_name}</span>
                                          <span className="text-xs text-gray-500">{formatNyDate(bill.install_date)}</span>
                                        </div>
                                        <div className="font-bold text-blue-700">{formatMoney(bill.estimated_value || bill.labor_total)}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className="bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg p-4 border-2 border-blue-400">
                                <div className="flex items-center justify-between">
                                  <span className="font-bold text-gray-800 text-lg">TOTAL:</span>
                                  <span className="font-bold text-blue-700 text-2xl">{formatMoney(currentWeekEstimate.total || currentWeekEstimate.estimated_payment)}</span>
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  {currentWeekEstimate.total_days !== undefined
                                    ? `Líder: ${formatMoney(currentWeekEstimate.estimated_payment)}${currentWeekEstimate.helpers_total > 0 ? ` + Helpers: ${formatMoney(currentWeekEstimate.helpers_total)}` : ''}${currentWeekEstimate.expenses > 0 ? ` + Despesas: ${formatMoney(currentWeekEstimate.expenses)}` : ''}`
                                    : `${currentWeekEstimate.total_jobs} trabalhos | Despesas: ${formatMoney(currentWeekEstimate.total_expenses)} | ${currentWeekEstimate.percentage}%`
                                  }
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        )}
                      </div>
                      )}
                      {/* Resumo Semanal PM - Compacto */}
                      {hasPermission('home_cards', 'resumo_semanal') && (
                      <div 
                        className={`bg-white border border-zinc-200 rounded-lg shadow-sm resizable-card ${!weeklyReportExpanded ? 'card-minimized' : ''} ${dragOverCardId === 'weekly-report' ? 'drag-over shadow-lg' : ''}`}
                        style={{ order: cardOrder.indexOf('weekly-report') }}
                        draggable="true"
                        onDragStart={(e) => handleCardDragStart(e, 'weekly-report')}
                        onDragEnd={handleCardDragEnd}
                        onDragOver={(e) => handleCardDragOver(e)}
                        onDragEnter={(e) => handleCardDragEnter(e, 'weekly-report')}
                        onDrop={(e) => handleCardDrop(e, 'weekly-report')}
                        data-card-id="weekly-report"
                      >
                        <div className="resize-icon" title="Arraste para redimensionar"></div>
                        <div 
                          className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-50 transition-colors border-b border-zinc-100"
                          onClick={() => setWeeklyReportExpanded(!weeklyReportExpanded)}
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md">
                              <span className="text-white text-sm">💰</span>
                            </div>
                            <h3 className="font-semibold text-base text-zinc-900 tracking-tight">
                              {t('weeklyReportSummary')}
                            </h3>
                          </div>
                          <span className={`text-zinc-400 text-sm transition-transform ${weeklyReportExpanded ? 'rotate-180' : ''}`}>
                            ▼
                          </span>
                        </div>
                        
                        {weeklyReportExpanded && (
                          <div className="p-4">
                            {/* Dropdown de Mês para usuários PM */}
                            {weeklyReportSummary.availableMonths && weeklyReportSummary.availableMonths.length > 0 && (
                              <div className="flex items-center gap-2 mb-3">
                                <select
                                  value={selectedMonth}
                                  onChange={(e) => {
                                    const newMonth = e.target.value;
                                    console.log('📅 PM Dropdown onChange - Novo mês selecionado:', newMonth);
                                    setSelectedMonth(newMonth);
                                    loadWeeklyReportSummary(true, newMonth);
                                  }}
                                  className="border border-blue-300 rounded px-3 py-1 text-sm bg-white focus:border-blue-500 mobile-select"
                                >
                                  {weeklyReportSummary.availableMonths.map(month => {
                                    const [year, monthNum] = month.split('-');
                                    const monthName = new Date(year, monthNum - 1).toLocaleDateString('pt-BR', { month: 'long', 
                                      year: 'numeric' });
                                    return (
                                      <option key={month} value={month}>
                                        {monthName.charAt(0).toUpperCase() + monthName.slice(1)}
                                    </option>
                                  );
                                })}
                                </select>
                                
                                {/* Botão Atualizar para PMs também */}
                                <Button 
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    loadWeeklyReportSummary();
                                  }} 
                                  variant="outline" 
                                  size="sm"
                                  disabled={weeklyReportLoading}
                                  className="h-8 w-8 p-0 flex items-center justify-center"
                                  title={language === 'pt-BR' ? 'Atualizar' : 'Refresh'}
                                >
                                  {weeklyReportLoading ? (
                                    <div className="animate-spin h-3 w-3 border border-gray-300 border-t-gray-600 rounded-full"></div>
                                  ) : (
                                    <>🔄</>
                                  )}
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {weeklyReportExpanded && (
                          <>
                            <div className="text-xs text-blue-700 mb-4 px-4">
                              {(() => {
                                const [year, month] = selectedMonth.split('-');
                                const monthName = new Date(year, month - 1).toLocaleDateString('pt-BR', { month: 'long', 
                                  year: 'numeric' });
                                return `${language === 'pt-BR' ? 'Equipe' : 'Team'} ${user?.username || 'PM'} • ${monthName.charAt(0).toUpperCase() + monthName.slice(1)} • ${weeklyReportSummary.weeklyData?.length || 0} ${language === 'pt-BR' ? 'semanas' : 'weeks'}`;
                              })()}
                            </div>
                            
                            <div className="px-4 pb-4">
                              {/* Render weekly sections */}
                              {weeklyReportSummary.weeklyData && weeklyReportSummary.weeklyData.length > 0 ? (
                                weeklyReportSummary.weeklyData.map((week, index) => (
                            <div key={`user-week-${week.weekEnding}-${index}`} className="mb-3">
                              {/* Week title */}
                              <div className="text-sm font-semibold text-blue-800 mb-2">
                                📅 {week.isDailyLeader 
                                  ? `Semana ${week.semana}/${week.ano}: ${week.data_inicio} - ${week.data_fim}`
                                  : `${language === 'pt-BR' ? 'Semana' : 'Week'}: ${formatDate(week.weekEnding)}`
                                }
                              </div>
                              
                              {week.isDailyLeader ? (
                                /* Daily Leader format: Líder + Helpers + Despesas */
                                <div className="bg-white rounded-lg border border-blue-200 p-3 space-y-1">
                                  <div className="flex justify-between py-1 border-b border-gray-100">
                                    <span className="text-xs text-gray-600">👨‍💼 Líder ({week.dias_trabalhados} dias × {formatMoney(week.valor_diaria)})</span>
                                    <span className="font-bold text-blue-600 text-sm">{formatMoney(week.valor_trabalho)}</span>
                                  </div>
                                  {week.helpers && week.helpers.map((h, i) => (
                                    <div key={i} className="flex justify-between py-1 border-b border-gray-100">
                                      <span className="text-xs text-gray-600">👷 {h.nome} ({h.dias} dias × {formatMoney(h.diaria)})</span>
                                      <span className="font-bold text-green-600 text-sm">{formatMoney(h.total)}</span>
                                    </div>
                                  ))}
                                  {week.helpers_avulsos && week.helpers_avulsos.map((h, i) => (
                                    <div key={i} className="flex justify-between py-1 border-b border-gray-100">
                                      <span className="text-xs text-gray-600">🔧 {h.nome} ({h.dias} dias × {formatMoney(h.diaria)})</span>
                                      <span className="font-bold text-orange-500 text-sm">{formatMoney(h.total)}</span>
                                    </div>
                                  ))}
                                  <div className="flex justify-between py-1 border-b border-gray-100">
                                    <span className="text-xs text-gray-600 font-medium">Despesas</span>
                                    <span className="font-bold text-orange-600 text-sm">{formatMoney(week.valor_despesas)}</span>
                                  </div>
                                  <div className="flex justify-between py-1.5 mt-1 bg-green-50 rounded px-2">
                                    <span className="text-sm font-bold text-gray-800">Total</span>
                                    <span className="font-bold text-green-700">{formatMoney(week.valor_total)}</span>
                                  </div>
                                </div>
                              ) : (
                              /* PM format: Jobs + Total + Despesas */
                              <div className="grid grid-cols-3 gap-2">
                                <div className="bg-white rounded-lg p-2 border-l-4 border-blue-500 shadow-sm">
                                  <div className="text-blue-700 text-[10px] mb-1">
                                    {language === 'pt-BR' ? 'Trabalhos' : 'Jobs'}
                                  </div>
                                  <div className="text-lg font-bold text-blue-600">{week.total_jobs || 0}</div>
                                </div>
                                <div className="bg-white rounded-lg p-2 border-l-4 border-purple-500 shadow-sm">
                                  <div className="text-purple-700 text-[10px] mb-1">Total</div>
                                  <div className="text-lg font-bold text-purple-600">{formatMoney(week.total_final || 0)}</div>
                                </div>
                                <div className="bg-white rounded-lg p-2 border-l-4 border-orange-500 shadow-sm">
                                  <div className="text-orange-700 text-[10px] mb-1">{t('expenses')}</div>
                                  <div className="text-lg font-bold text-orange-600">{formatMoney(week.total_expenses || 0)}</div>
                                </div>
                              </div>
                              )}
                            </div>
                          ))
                              ) : (
                                <div className="text-center py-4 text-gray-500 text-sm">
                                  Nenhum relatório encontrado.
                                </div>
                              )}

                              {/* Botão Ver Mais */}
                              <Button 
                                onClick={() => safeSetActiveTab(user?.role === 'daily_leader' ? 'report' : 'reports')}
                                variant="outline"
                                size="sm"
                                className="w-full mt-2"
                              >
                                {language === 'pt-BR' ? 'Ver Relatório Completo' : 'View Full Report'}
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                      )}
                      
                      {/* Resumo Mensal PM - Compacto */}
                      {hasPermission('home_cards', 'resumo_mensal') && (
                      <div 
                        className={`bg-white border border-zinc-200 rounded-lg shadow-sm resizable-card ${!monthlyYearlyExpanded ? 'card-minimized' : ''} ${dragOverCardId === 'monthly-yearly' ? 'drag-over shadow-lg' : ''}`}
                        style={{ order: cardOrder.indexOf('monthly-yearly') }}
                        draggable="true"
                        onDragStart={(e) => handleCardDragStart(e, 'monthly-yearly')}
                        onDragEnd={handleCardDragEnd}
                        onDragOver={(e) => handleCardDragOver(e)}
                        onDragEnter={(e) => handleCardDragEnter(e, 'monthly-yearly')}
                        onDrop={(e) => handleCardDrop(e, 'monthly-yearly')}
                        data-card-id="monthly-yearly"
                      >
                        <div className="resize-icon" title="Arraste para redimensionar"></div>
                        <div 
                          className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-50 transition-colors border-b border-zinc-100"
                          onClick={() => setMonthlyYearlyExpanded(!monthlyYearlyExpanded)}
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md">
                              <span className="text-white text-sm">📈</span>
                            </div>
                            <h3 className="font-semibold text-base text-zinc-900 tracking-tight">
                              {language === 'pt-BR' ? 'Resumo Mensal' : 'Monthly Summary'}
                            </h3>
                          </div>
                          <span className={`text-zinc-400 text-sm transition-transform ${monthlyYearlyExpanded ? 'rotate-180' : ''}`}>
                            ▼
                          </span>
                        </div>
                        
                        {monthlyYearlyExpanded && (
                          <div className="p-4">
                            {/* Dropdown de Mês */}
                            {monthlyYearlySummary.availableMonths && monthlyYearlySummary.availableMonths.length > 0 && (
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                                <div className="flex items-center gap-2">
                                  <select
                                    value={selectedMonthForSummary}
                                    onChange={(e) => {
                                      const newMonth = e.target.value;
                                      console.log('📅 Resumo Mensal - Novo mês selecionado:', newMonth);
                                      setSelectedMonthForSummary(newMonth);
                                      calculateMonthlyYearlySummary(newMonth);
                                    }}
                                    className="border border-green-300 rounded px-3 py-1 text-sm bg-white focus:border-green-500"
                                  >
                                    {monthlyYearlySummary.availableMonths.map(month => {
                                      const [year, monthNum] = month.split('-');
                                      const monthName = new Date(year, monthNum - 1).toLocaleDateString('pt-BR', { month: 'long', 
                                        year: 'numeric' });
                                      return (
                                        <option key={month} value={month}>
                                          {monthName.charAt(0).toUpperCase() + monthName.slice(1)}
                                        </option>
                                      );
                                    })}
                                  </select>
                                  
                                  <Button 
                                    type="button"
                                    onClick={() => calculateMonthlyYearlySummary(selectedMonthForSummary)}
                                    variant="outline" 
                                    size="sm"
                                    className="flex items-center gap-2"
                                  >
                                    🔄 {t('update')}
                                  </Button>
                                </div>
                              </div>
                            )}
                            
                            {/* Resumo do Mês Selecionado */}
                            <div className="mb-4">
                              <div className="text-sm font-semibold text-green-800 mb-2">
                                📅 {selectedMonthForSummary ? (() => {
                                  const [year, monthNum] = selectedMonthForSummary.split('-');
                                  return new Date(year, monthNum - 1).toLocaleDateString('pt-BR', { month: 'long', 
                                    year: 'numeric' }).toUpperCase();
                                })() : 'MÊS ATUAL'}
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="bg-white rounded-lg p-3 border-l-4 border-green-500 shadow-sm">
                                  <div className="text-green-700 text-xs mb-1">
                                    {user?.role === 'daily_leader' ? 'Semanas' : (language === 'pt-BR' ? 'Total Trabalhos' : 'Total Jobs')}
                                  </div>
                                  <div className="text-2xl font-bold text-green-600">
                                    {monthlyYearlySummary.month.total_jobs}
                                  </div>
                                </div>
                                <div className="bg-white rounded-lg p-3 border-l-4 border-emerald-500 shadow-sm">
                                  <div className="text-emerald-700 text-xs mb-1">
                                    {language === 'pt-BR' ? 'Total Final' : 'Total Final'}
                                  </div>
                                  <div className="text-2xl font-bold text-emerald-600">
                                    {formatMoney(monthlyYearlySummary.month.total_final)}
                                  </div>
                                </div>
                                {monthlyYearlySummary.month.total_expenses > 0 && (
                                <div className="bg-white rounded-lg p-3 border-l-4 border-orange-500 shadow-sm">
                                  <div className="text-orange-700 text-xs mb-1">Despesas</div>
                                  <div className="text-2xl font-bold text-orange-600">
                                    {formatMoney(monthlyYearlySummary.month.total_expenses)}
                                  </div>
                                </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Resumo do Ano do Mês Selecionado */}
                            <div>
                              <div className="text-sm font-semibold text-green-800 mb-2">
                                📊 {selectedMonthForSummary ? selectedMonthForSummary.substring(0, 4) : new Date().getFullYear()}
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="bg-white rounded-lg p-3 border-l-4 border-teal-500 shadow-sm">
                                  <div className="text-teal-700 text-xs mb-1">
                                    {user?.role === 'daily_leader' ? 'Semanas' : (language === 'pt-BR' ? 'Total Trabalhos' : 'Total Jobs')}
                                  </div>
                                  <div className="text-2xl font-bold text-teal-600">
                                    {monthlyYearlySummary.year.total_jobs}
                                  </div>
                                </div>
                                <div className="bg-white rounded-lg p-3 border-l-4 border-cyan-500 shadow-sm">
                                  <div className="text-cyan-700 text-xs mb-1">
                                    {language === 'pt-BR' ? 'Total Final' : 'Total Final'}
                                  </div>
                                  <div className="text-2xl font-bold text-cyan-600">
                                    {formatMoney(monthlyYearlySummary.year.total_final)}
                                  </div>
                                </div>
                                {monthlyYearlySummary.year.total_expenses > 0 && (
                                <div className="bg-white rounded-lg p-3 border-l-4 border-orange-500 shadow-sm">
                                  <div className="text-orange-700 text-xs mb-1">Despesas</div>
                                  <div className="text-2xl font-bold text-orange-600">
                                    {formatMoney(monthlyYearlySummary.year.total_expenses)}
                                  </div>
                                </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-6 cards-container">
                      {/* Todos os cards agora podem ser reordenados via CSS order */}
                      
                      {/* Últimos Labor Bills - APENAS PM */}
                      {hasPermission('home_cards', 'ultimos_labor') && user?.role !== 'admin' && user?.role !== 'daily_leader' && (
                      <div 
                        className={`bg-white rounded-lg border shadow-sm resizable-card ${dragOverCardId === 'labor-bills' ? 'drag-over' : ''}`}
                        style={{ order: cardOrder.indexOf('labor-bills'), minWidth: '300px', flex: '1' }}
                        draggable="true"
                        onDragStart={(e) => handleCardDragStart(e, 'labor-bills')}
                        onDragEnd={handleCardDragEnd}
                        onDragOver={(e) => handleCardDragOver(e)}
                        onDragEnter={(e) => handleCardDragEnter(e, 'labor-bills')}
                        onDrop={(e) => handleCardDrop(e, 'labor-bills')}
                        data-card-id="labor-bills"
                      >
                        <div className="p-3 border-b cursor-pointer" onClick={() => setLaborBillsCardExpanded(!laborBillsCardExpanded)}>
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-gray-800 text-sm">
                              {language === 'pt-BR' ? 'Últimos Labor Bills' : 'Recent Labor Bills'}
                            </h4>
                            <span className={`text-zinc-400 text-sm transition-transform ${laborBillsCardExpanded ? 'rotate-180' : ''}`}>▼</span>
                          </div>
                        </div>
                        {laborBillsCardExpanded && (
                        <div className="divide-y">
                          {/* Labor Bills dinâmicos com botões funcionais */}
                          {laborBills.slice(0, 5).map((bill) => (
                            <div key={bill.id} className="p-3 hover:bg-gray-50">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="font-medium mb-1 text-sm">{bill.job_name || (language === 'pt-BR' ? 'Sem nome' : 'No name')}</div>
                                  <div className="text-xs text-gray-600">
                                    {language === 'pt-BR' ? 'Ordem' : 'Order'}: {bill.order_number || '-'} • {language === 'pt-BR' ? 'Data' : 'Date'}: {formatNyDate(bill.install_date)}
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  {/* Botão Ver PDF */}
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="px-2 py-1"
                                    data-testid="view-labor-bill-pdf-btn"
                                    onClick={() => viewLaborBillPDF(bill.id)}
                                    title={language === 'pt-BR' ? 'Ver PDF' : 'View PDF'}
                                  >
                                    <span className="text-base">📄</span>
                                  </Button>
                                  {/* Botão Editar */}
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="px-2 py-1"
                                    data-testid="edit-labor-bill-btn"
                                    onClick={() => loadLaborBill(bill.id)}
                                    title={language === 'pt-BR' ? 'Editar' : 'Edit'}
                                  >
                                    <Edit className="h-3.5 w-3.5" />
                                  </Button>
                                  {/* Botão Enviar */}
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="px-2 py-1"
                                    onClick={() => {
                                      loadLaborBill(bill.id);
                                      setTimeout(() => {
                                        openSendEmailFlow();
                                      }, 500);
                                    }}
                                    title={language === 'pt-BR' ? 'Enviar' : 'Send'}
                                  >
                                    <span className="text-base">📧</span>
                                  </Button>
                                  {/* Botão Download */}
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="px-2 py-1"
                                    onClick={() => downloadPDF(bill.id)}
                                    title="Download PDF"
                                  >
                                    <span className="text-base">⬇️</span>
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                          {laborBills.length === 0 && (
                            <div className="p-6 text-center text-gray-500">
                              <div className="text-2xl mb-2">📋</div>
                              <div className="text-sm">
                                {language === 'pt-BR' ? 'Nenhum Labor Bill encontrado' : 'No Labor Bills found'}
                              </div>
                            </div>
                          )}
                        </div>
                        )}
                      </div>

                      )}


                      {/* Histórico Completo de Envios - APENAS PM */}
                      {hasPermission('home_cards', 'historico_envios') && user?.role !== 'admin' && user?.role !== 'daily_leader' && (
                      <div 
                        className={`bg-white rounded-lg border shadow-sm resizable-card ${dragOverCardId === 'email-history' ? 'drag-over' : ''}`}
                        style={{ order: cardOrder.indexOf('email-history'), minWidth: '300px', flex: '1' }}
                        draggable="true"
                        onDragStart={(e) => handleCardDragStart(e, 'email-history')}
                        onDragEnd={handleCardDragEnd}
                        onDragOver={(e) => handleCardDragOver(e)}
                        onDragEnter={(e) => handleCardDragEnter(e, 'email-history')}
                        onDrop={(e) => handleCardDrop(e, 'email-history')}
                        data-card-id="email-history"
                      >
                        <div className="p-3 border-b cursor-pointer" onClick={() => setEmailHistoryCardExpanded(!emailHistoryCardExpanded)}>
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-gray-800 text-sm">
                              Histórico de Envios
                            </h4>
                            <span className={`text-zinc-400 text-sm transition-transform ${emailHistoryCardExpanded ? 'rotate-180' : ''}`}>▼</span>
                          </div>
                        </div>
                        {emailHistoryCardExpanded && (<>
                        
                        {/* Contador de emails */}
                        <div className="px-3 py-2 bg-blue-50 border-b">

                          <div className="text-xs text-blue-700">
                            📊 {emailHistory?.length || 0} {language === 'pt-BR' ? 'envios registrados' : 'registered emails'}
                            {emailHistory?.filter(e => e.status === 'sent').length > 0 && (
                              <span className="ml-2">• {emailHistory.filter(e => e.status === 'sent').length} {language === 'pt-BR' ? 'confirmados' : 'confirmed'}</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="divide-y max-h-80 overflow-auto">
                          {emailHistory && emailHistory.length > 0 ? (
                            emailHistory.map((email, index) => (
                              <div key={email.id || index} className="p-3 hover:bg-gray-50">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="font-medium text-sm text-gray-800">
                                      👤 {
                                        email.snapshot?.job_name || 
                                        email.job_name || 
                                        email.customer ||
                                        email.pm_first_name ||
                                        'Cliente não informado'
                                      }
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                      📧 Para: <strong>{
                                        email.recipients?.[0]?.name || 
                                        email.last_sent_to_names?.[0] ||
                                        email.recipients?.[0]?.email || 
                                        email.last_sent_to?.[0] ||
                                        'Destinatário não informado'
                                      }</strong>
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      ✉️ Email: {
                                        email.recipients?.[0]?.email || 
                                        email.last_sent_to?.[0] ||
                                        'N/A'
                                      }
                                    </div>
                                    <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                                      <span>📅 {formatNyDateTime(email.sent_at || email.created_at) || email.sent_at_str || ''}</span>
                                      <span className={`px-2 py-1 rounded text-xs ${
                                        email.status === 'sent' ? 'bg-green-100 text-green-800' : 
                                        email.status === 'failed' ? 'bg-red-100 text-red-800' :
                                        'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {email.status === 'sent' ? '✅ Enviado' :
                                         email.status === 'failed' ? '❌ Falhou' :
                                         '⏳ Pendente'}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="text-right text-xs">
                                    <div className="text-gray-500">
                                      Order: {email.snapshot?.order_number || email.order_number || 'N/A'}
                                    </div>
                                    {(email.snapshot?.grand_total || email.grand_total) && (
                                      <div className="font-medium text-green-600">
                                        ${parseFloat(email.snapshot?.grand_total || email.grand_total || 0).toFixed(2)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-6 text-center text-gray-500">
                              <div className="text-2xl mb-2">📧</div>
                              <div className="text-sm">
                                {language === 'pt-BR' ? 'Nenhum envio ainda' : 'No sends yet'}
                              </div>
                            </div>
                          )}
                        </div>
                        </>)}
                      </div>

                      )}
                      
                      {/* Card de Logs de Atividade removido - deve aparecer apenas na aba Admin */}
                    </div>
                    </>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Create Tab - Otimizado Mobile/Tablet */}
          <TabsContent value="create" className="mt-4 sm:mt-6" style={{ display: activeTab === 'create' ? 'block' : 'none' }}>
            <Card>
              <CardHeader className="pb-3 sm:pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-2 sm:gap-3">
                    {payrollContext ? (
                      <Button variant="outline" size="sm" onClick={returnToPayrollContext} className="text-xs">
                        <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> {language === 'pt-BR' ? 'Voltar' : 'Back'}
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => safeSetActiveTab('drafts')} className="text-xs">
                        <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> {language === 'pt-BR' ? 'Voltar' : 'Back'}
                      </Button>
                    )}
                    <CardTitle className="text-base sm:text-lg">
                      {payrollContext ? 
                        (language === 'pt-BR' ? 'Editando Labor Bill' : 'Editing Labor Bill') : 
                        (language === 'pt-BR' ? 'Novo Labor Bill' : 'New Labor Bill')
                      }
                      {formData.bill_kind === 'MEASURE' && <Badge className="ml-2 bg-purple-100 border border-purple-300 text-purple-800 text-[10px] align-middle" data-testid="labor-bill-measure-badge">📏 {language === 'pt-BR' ? 'Medição' : 'Measure'}</Badge>}
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6">
                {/* Selo do Supervisor — conferência rápida antes de salvar */}
                {(() => {
                  const supName = formData.supervisor || currentBill?.supervisor || formData.fom_assigned || currentBill?.fom_assigned || '';
                  const supEmail = formData.fom_email || currentBill?.fom_email || '';
                  return (
                    <div
                      data-testid="editor-supervisor-seal"
                      className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border px-3 py-2 text-sm ${supName ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}
                    >
                      {supName ? (
                        <>
                          <span className="font-semibold">👤 Supervisor: {supName}</span>
                          {supEmail ? (
                            <span className="text-green-700 break-all">· ✉️ {supEmail}</span>
                          ) : (
                            <span className="text-amber-700">· ✉️ {language === 'pt-BR' ? 'sem e-mail configurado' : 'no email configured'}</span>
                          )}
                        </>
                      ) : (
                        <span>👤 {language === 'pt-BR' ? 'Nenhum supervisor selecionado — escolha ao salvar' : 'No supervisor selected — choose on save'}</span>
                      )}
                    </div>
                  );
                })()}
                {/* Informações Básicas - Grid Responsivo */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 p-3 sm:p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label htmlFor="install_date" className="text-sm">{t('installDate')}</Label>
                    <Input 
                      id="install_date" 
                      type="date" 
                      value={formData.install_date} 
                      onChange={(e) => { setFormData({ ...formData, install_date: e.target.value }); setIsDirty(true); }} 
                      className="mt-1 text-sm" 
                    />
                  </div>
                  <div>
                    <Label htmlFor="job_name" className="text-sm">{t('jobName')}</Label>
                    <Input 
                      id="job_name" 
                      value={formData.job_name} 
                      onChange={(e) => { setFormData({ ...formData, job_name: e.target.value }); setIsDirty(true); }} 
                      className="mt-1 text-sm" 
                      placeholder={language === 'pt-BR' ? 'Nome do trabalho' : 'Job name'}
                    />
                  </div>
                  <div>
                    <Label htmlFor="order_number" className="text-sm">{t('orderNumber')}</Label>
                    <Input 
                      id="order_number" 
                      value={formData.order_number} 
                      onChange={(e) => { setFormData({ ...formData, order_number: e.target.value }); setIsDirty(true); }} 
                      className="mt-1 text-sm" 
                      placeholder={language === 'pt-BR' ? 'Número da ordem' : 'Order number'}
                    />
                  </div>
                  <div>
                    <Label htmlFor="total_windows" className="text-sm">{t('totalWindows')}</Label>
                    <Input 
                      id="total_windows" 
                      type="number" 
                      value={formData.total_windows} 
                      onChange={(e) => { setFormData({ ...formData, total_windows: parseInt(e.target.value) || 0 }); setIsDirty(true); }} 
                      className="mt-1 text-sm" 
                      min="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="total_doors" className="text-sm">{t('totalDoors')}</Label>
                    <Input 
                      id="total_doors" 
                      type="number" 
                      value={formData.total_doors} 
                      onChange={(e) => { setFormData({ ...formData, total_doors: parseInt(e.target.value) || 0 }); setIsDirty(true); }} 
                      className="mt-1 text-sm" 
                      min="0"
                    />
                  </div>
                </div>

                {/* Banner IA - Labor Bill pré-preenchido */}
                {laborBillDraft && laborBillDraft.status === 'ai_generated' && (
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-300 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">🤖</span>
                      <div className="flex-1">
                        <p className="font-bold text-purple-900 text-lg">
                          {language === 'pt-BR' 
                            ? 'Labor Bill pré-preenchido pelo IA através do Installation Package (PDF)'
                            : 'Labor Bill pre-filled by AI through Installation Package (PDF)'
                          }
                        </p>
                        <p className="text-sm text-purple-700 mt-1">
                          ✓ {aiFilledFields.length} campos preenchidos •
                          {laborBillDraft.ai_metadata?.total_windows || 0} janelas, {laborBillDraft.ai_metadata?.total_doors || 0} portas
                          {laborBillDraft.ai_metadata?.is_lead && ' • 🔴 LEAD JOB'}
                        </p>
                        <p className="text-xs text-purple-600 mt-1 italic">
                          {language === 'pt-BR'
                            ? 'Campos em azul foram preenchidos pela IA. Revise e edite se necessário.'
                            : 'Fields in blue were filled by AI. Review and edit if needed.'
                          }
                        </p>
                        {laborBillDraft.ai_notes && (
                          <p className="text-xs text-purple-600 mt-1">
                            📝 {laborBillDraft.ai_notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="text-sm text-slate-600 italic bg-blue-50 p-3 rounded">
                  Unless otherwise specified, charges are per opening and include installation of drip cap, seal pan, interior stop trim, foam, sealant, capping, or L-trim finish.
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left column */}
                  <div>
                    <h3 className="font-semibold text-xl mb-4 text-slate-700">Installation & Windows</h3>
                    <div className="space-y-4">
                      {/* "Extra Labor" não existe no Labor Bill oficial: extras vão em "Additional Labor Cost" + Notes */}
                      {leftColumnSubgroups.filter((sg) => sg.title !== "Extra Labor" && sg.special !== "extra_labor").map((subgroup, subIndex) => (
                        <div key={subIndex} className="bg-white rounded-lg border shadow-sm">
                          <div className="bg-slate-200 px-4 py-2 rounded-t-lg">
                            <h4 className="font-semibold text-slate-800">{subgroup.title}</h4>
                          </div>
                          <div className="p-3 space-y-2">
                            {subgroup.title === "Extra Labor" ? (
                              <>
                                {(currentBill?.extra_items || []).map((extra, extraIndex) => (
                                  <div key={`extra-${extraIndex}`} className="grid grid-cols-12 gap-2 items-center p-2 bg-amber-50 rounded">
                                    <div className="col-span-5">
                                      <Input type="text" placeholder="Extra labor description" value={extra.description} onChange={(e) => updateExtraItem(extraIndex, 'description', e.target.value)} className="w-full text-sm" />
                                    </div>
                                    <div className="col-span-2">
                                      <Input type="number" placeholder="Qty" value={extra.qty} onChange={(e) => updateExtraItem(extraIndex, 'qty', e.target.value)} className="w-full text-center h-8 text-sm" min="0" />
                                    </div>
                                    <div className="col-span-2">
                                      <Input type="number" placeholder="Price" value={extra.unit_price} onChange={(e) => updateExtraItem(extraIndex, 'unit_price', e.target.value)} className="w-full text-center h-8 text-sm" step="0.01" min="0" />
                                    </div>
                                    <div className="col-span-2 text-sm text-center font-medium">{(extra.qty * extra.unit_price).toFixed(0)}</div>
                                    <div className="col-span-1 flex justify-center">
                                      <Button type="button" variant="outline" size="sm" onClick={() => removeExtraItem(extraIndex)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                                <div className="mt-2">
                                  <Button type="button" onClick={addExtraItem} className="bg-green-600 hover:bg-green-700 text-sm">
                                    <Plus className="h-4 w-4 mr-2" /> Add Extra Labor
                                  </Button>
                                </div>

                                {/* Dropdown de presets pré-configurados (admin gerencia inline) */}
                                <div className="mt-2">
                                  <ExtraLaborPresetsDropdown
                                    isAdmin={user?.role === 'admin'}
                                    language={language}
                                    onSelectPreset={(preset) => {
                                      const newExtra = {
                                        description: preset.description,
                                        qty: 1,
                                        unit_price: preset.unit_price,
                                        total: preset.unit_price,
                                      };
                                      setCurrentBill(prev => ({
                                        ...(prev || { items: defaultItems }),
                                        extra_items: [...((prev?.extra_items) || []), newExtra],
                                      }));
                                      setIsDirty(true);
                                    }}
                                  />
                                </div>
                              </>
                            ) : (
                              <>
                                {subgroup.items.map((item, itemIndex) => {
                                  const billIdx = findBillItemIndex(currentBill?.items, item);
                                  const billItem = billIdx !== -1 ? currentBill.items[billIdx] : undefined;
                                  const qty = billItem?.qty || 0;
                                  const price = (billItem?.unit_price ?? item.unit_price);
                                  const isAIFilled = aiFilledFields.includes(item.description);
                                  
                                  // Debug apenas para primeiros 3 items
                                  if (itemIndex < 3) {
                                    console.log(`🔍 Item: ${item.description}, isAIFilled: ${isAIFilled}, qty: ${qty}, aiFields:`, aiFilledFields);
                                  }
                                  
                                  // Show item even if not found, but protect the update function
                                  return (
                                    <div key={itemIndex} className="grid grid-cols-12 gap-2 items-center p-2 hover:bg-slate-50 rounded">
                                      <div className="col-span-6 text-sm text-slate-700 flex items-center gap-2">
                                        {item.description}
                                        {isAIFilled && (
                                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">IA</span>
                                        )}
                                      </div>
                                      <div className="col-span-2">
                                        <Input 
                                          type="number" 
                                          value={qty} 
                                          onChange={(e) => updateTemplateItemQty(item, 'left', e.target.value)} 
                                          className={`h-8 text-center text-sm ${
                                            isAIFilled ? 'bg-blue-50 border-blue-400 focus:bg-white transition-colors' : ''
                                          }`}
                                          min="0" 
                                        />
                                      </div>
                                      <div className="col-span-2 text-sm text-center">${price}</div>
                                      <div className="col-span-2 text-sm text-center font-medium">{qty ? (qty * price).toFixed(0) : ''}</div>
                                    </div>
                                  );
                                })}
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right column */}
                  <div>
                    <h3 className="font-semibold text-xl mb-4 text-slate-700">Modifications & Extras</h3>
                    <div className="space-y-4">
                      {rightColumnSubgroups.map((subgroup, subIndex) => (
                        <div key={subIndex} className="bg-white rounded-lg border shadow-sm">
                          <div className="bg-slate-200 px-4 py-2 rounded-t-lg">
                            <h4 className="font-semibold text-slate-800">{subgroup.title}</h4>
                          </div>
                          <div className="p-3 space-y-2">
                            {subgroup.items.map((item, itemIndex) => {
                              const billIdx = findBillItemIndex(currentBill?.items, item);
                              const billItem = billIdx !== -1 ? currentBill.items[billIdx] : undefined;
                              const qty = billItem?.qty || 0;
                              const isAIFilled = aiFilledFields.includes(item.description);
                              // Show item even if not found, but protect the update function
                              return (
                                <div key={itemIndex} className="grid grid-cols-12 gap-2 items-center p-2 hover:bg-slate-50 rounded">
                                  <div className="col-span-6 text-sm text-slate-700 flex items-center gap-2">
                                    {item.description}
                                    {isAIFilled && (
                                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">IA</span>
                                    )}
                                  </div>
                                  <div className="col-span-2">
                                    <Input 
                                      type="number" 
                                      value={qty} 
                                      onChange={(e) => updateTemplateItemQty(item, 'right', e.target.value)} 
                                      className={`h-8 text-center text-sm ${
                                        isAIFilled ? 'bg-blue-50 border-blue-400 focus:bg-white transition-colors' : ''
                                      }`}
                                      min="0" 
                                    />
                                  </div>
                                  <div className="col-span-2 text-sm text-center">${item.unit_price}</div>
                                  <div className="col-span-2 text-sm text-center font-medium">{qty ? (qty * item.unit_price).toFixed(0) : ''}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 text-white p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold">TOTAL</span>
                    <span className="text-2xl font-bold">${grandTotal.toLocaleString()}</span>
                  </div>
                </div>

                <div className="bg-white rounded-lg border shadow-sm p-4 space-y-3" data-testid="labor-bill-additional-section">
                  <div>
                    <Label htmlFor="additional_labor_cost">Additional Labor Cost ($)</Label>
                    <Input
                      id="additional_labor_cost"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={formData.additional_labor_cost ?? ''}
                      onChange={(e) => { setFormData({ ...formData, additional_labor_cost: e.target.value }); setIsDirty(true); }}
                      className="mt-1 max-w-xs"
                      placeholder="0.00"
                      data-testid="labor-bill-additional-labor-cost"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {language === 'pt-BR'
                        ? 'Trabalho extra que não está na lista: coloque o valor aqui e explique nas Notes. Entra no Labor Total e aparece igual no Labor Bill do supervisor.'
                        : 'Extra work not on the list: enter the amount here and explain it in the Notes. Counts in the Labor Total and shows the same way on the supervisor\'s Labor Bill.'}
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea id="notes" value={formData.notes || ''} onChange={(e) => { setFormData({ ...formData, notes: e.target.value }); setIsDirty(true); }} className="mt-1" rows="3" placeholder={language === 'pt-BR' ? 'Explique o trabalho extra, detalhes do serviço etc. (aparece no Labor Bill do supervisor)' : 'Explain extra work, job details, etc. (shown on the supervisor\'s Labor Bill)'} data-testid="labor-bill-notes" />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <Button onClick={openSaveSupervisorModal} disabled={loading} data-testid="labor-bill-save-btn" className="bg-green-600 hover:bg-green-700 w-full sm:w-auto">
                    <Save className="h-4 w-4 mr-2" />
                    {loading ? t('saving') : (language === 'pt-BR' ? 'Salvar Rascunho' : 'Save Draft')}
                  </Button>
                  {currentBill?.id && (
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                      <Button onClick={() => downloadPDF(currentBill.id)} disabled={loading} variant="outline" className="w-full sm:w-auto bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700">
                        <Download className="h-4 w-4 mr-2" />
                        {language === 'pt-BR' ? 'Download PDF' : 'Download PDF'}
                      </Button>
                      <Button onClick={sendLaborBill} disabled={loading} data-testid="labor-bill-send-email-btn" variant="outline" className="w-full sm:w-auto bg-green-50 hover:bg-green-100 border-green-200 text-green-700">
                        <Send className="h-4 w-4 mr-2" />
                        {language === 'pt-BR' ? 'Enviar Email' : 'Send Email'}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Floating "PDF" button + FloatingPDFViewer (visíveis enquanto edita Labor Bill com PDF source) */}
            {sourcePdfUrl && (
              <>
                {(!showFloatingPdf || floatingPdfMinimized) && (
                  <button
                    data-testid="floating-pdf-trigger-btn"
                    onClick={() => { setShowFloatingPdf(true); setFloatingPdfMinimized(false); }}
                    style={{
                      position: 'fixed',
                      right: 24,
                      bottom: 24,
                      zIndex: 99997,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '18px 28px',
                      borderRadius: 999,
                      border: 'none',
                      color: '#fff',
                      fontSize: 18,
                      fontWeight: 800,
                      letterSpacing: 0.5,
                      background: 'linear-gradient(135deg, #2563eb 0%, #6366f1 60%, #8b5cf6 100%)',
                      boxShadow: '0 14px 32px -6px rgba(99,102,241,0.6), 0 6px 16px rgba(0,0,0,0.3)',
                      cursor: 'pointer',
                      animation: 'pdfBtnPulse 2.4s ease-in-out infinite',
                    }}
                  >
                    <FileSearch className="h-7 w-7" />
                    <span>PDF</span>
                    <style>{`
                      @keyframes pdfBtnPulse {
                        0%, 100% { box-shadow: 0 14px 32px -6px rgba(99,102,241,0.6), 0 6px 16px rgba(0,0,0,0.3); transform: scale(1); }
                        50%      { box-shadow: 0 18px 40px -4px rgba(139,92,246,0.8), 0 8px 20px rgba(0,0,0,0.35); transform: scale(1.04); }
                      }
                    `}</style>
                  </button>
                )}
                {showFloatingPdf && !floatingPdfMinimized && (
                  <FloatingPDFViewer
                    pdfUrl={sourcePdfUrl}
                    title={sourcePdfTitle || 'Work Order PDF'}
                    onMinimize={() => setFloatingPdfMinimized(true)}
                    onClose={() => { setShowFloatingPdf(false); setFloatingPdfMinimized(false); }}
                  />
                )}
              </>
            )}
          </TabsContent>
          <TabsContent value="drafts" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-lg sm:text-xl">{t('laborBills')}</CardTitle>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
                    {/* Filter Checkbox for Paid Clients */}
                    <label className="flex items-center gap-2 text-xs sm:text-sm">
                      <input 
                        type="checkbox" 
                        checked={showOnlyPaid}
                        onChange={(e) => setShowOnlyPaid(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-gray-700">
                        💰 {language === 'pt-BR' ? 'Apenas pagos' : 'Paid only'}
                      </span>
                    </label>
                    {/* Filter Checkbox: enviados ao supervisor */}
                    <label className="flex items-center gap-2 text-xs sm:text-sm">
                      <input 
                        type="checkbox" 
                        data-testid="filter-sent-supervisor-checkbox"
                        checked={showOnlySentToSupervisor}
                        onChange={(e) => setShowOnlySentToSupervisor(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-gray-700">
                        📤 {language === 'pt-BR' ? 'Enviados ao supervisor' : 'Sent to supervisor'}
                      </span>
                    </label>
                    {/* Sort Dropdown */}
                    <div className="w-full sm:w-48">
                      <Select value={draftsSort} onValueChange={setDraftsSort}>
                        <SelectTrigger className="w-full text-xs sm:text-sm">
                          <SelectValue placeholder={language === 'pt-BR' ? 'Ordenar por...' : 'Sort by...'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="date_desc">
                            📅 {language === 'pt-BR' ? 'Data (Recente)' : 'Date (Recent)'}
                          </SelectItem>
                          <SelectItem value="date_asc">
                            📅 {language === 'pt-BR' ? 'Data (Antigo)' : 'Date (Old)'}
                          </SelectItem>
                          <SelectItem value="job_name_asc">
                            🏠 {language === 'pt-BR' ? 'Job (A-Z)' : 'Job (A-Z)'}
                          </SelectItem>
                          <SelectItem value="job_name_desc">
                            🏠 {language === 'pt-BR' ? 'Job (Z-A)' : 'Job (Z-A)'}
                          </SelectItem>
                          <SelectItem value="order_asc">
                            📋 {language === 'pt-BR' ? 'Order (A-Z)' : 'Order (A-Z)'}
                          </SelectItem>
                          <SelectItem value="order_desc">
                            📋 {language === 'pt-BR' ? 'Order (Z-A)' : 'Order (Z-A)'}
                          </SelectItem>
                          <SelectItem value="total_desc">
                            💰 {language === 'pt-BR' ? 'Total (Maior)' : 'Total (Higher)'}
                          </SelectItem>
                          <SelectItem value="total_asc">
                            💰 {language === 'pt-BR' ? 'Total (Menor)' : 'Total (Lower)'}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Search Input */}
                    <div className="w-full sm:w-72">
                      <Input 
                        placeholder={language === 'pt-BR' ? 'Buscar Job/Order #' : 'Search Job/Order #'} 
                        value={draftsSearch} 
                        onChange={(e) => setDraftsSearch(e.target.value)} 
                        className="text-xs sm:text-sm"
                      />
                    </div>
                    {/* New Labor Bill Button - apenas para usuários regulares */}
                    {user?.role !== 'admin' && (
                      <Button onClick={startNewBill} className="bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm" size="sm">
                        <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" /> 
                        {language === 'pt-BR' ? 'Novo Bill' : 'New Bill'}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Para ADMIN - Filtro por PM e Agrupar por equipes */}
                {user?.role === 'admin' ? (
                  <div className="space-y-6">
                    {/* Filtro por PM */}
                    <div className="bg-gray-50 border rounded-lg p-4">
                      <div className="flex items-center gap-4">
                        <Label className="font-semibold">🔍 Filtrar por PM:</Label>
                        <select 
                          className="border rounded px-3 py-2 flex-1 max-w-xs"
                          value={laborBillPMFilter}
                          onChange={(e) => setLaborBillPMFilter(e.target.value)}
                        >
                          <option value="ALL">Todos os PMs</option>
                          {pmOptions.filter(pm => pm.startsWith('PM')).map(pm => (
                            <option key={pm} value={pm}>{pm}</option>
                          ))}
                        </select>
                        <div className="text-sm text-gray-600">
                          {laborBillPMFilter === 'ALL' ? 
                            `Mostrando: Todos os PMs (${laborBills.length} total)` :
                            `Mostrando: ${laborBills.filter(b => b.pm_user === laborBillPMFilter).length} Labor Bills do ${laborBillPMFilter}`
                          }
                        </div>
                      </div>
                    </div>
                    
                    {(() => {
                      // Filtrar por PM primeiro, depois aplicar outros filtros
                      let billsToShow = laborBills;
                      
                      // Aplicar filtro por PM se selecionado
                      if (laborBillPMFilter !== 'ALL') {
                        billsToShow = billsToShow.filter(b => b.pm_user === laborBillPMFilter);
                      }
                      
                      // Aplicar filtros de busca e pagamento
                      const filteredBills = billsToShow.filter((b) => {
                        const q = draftsSearch.trim().toLowerCase();
                        const matchesSearch = !q || ((b.job_name || '').toLowerCase().includes(q) || (b.order_number || '').toLowerCase().includes(q));
                        const matchesPaidFilter = !showOnlyPaid || (b.paid === true);
                        const matchesSentFilter = !showOnlySentToSupervisor || !!b.last_sent_supervisor;
                        return matchesSearch && matchesPaidFilter && matchesSentFilter;
                      });

                      // Separar bills por data usando período configurável
                      const { recent, old } = separateLaborBillsByDate(filteredBills, parseInt(laborBillPeriod));

                      // Agrupar por pm_user dentro de cada seção
                      const groupBillsByTeam = (bills) => {
                        const teamGroups = {};
                        bills.forEach((bill) => {
                          const team = bill.pm_user || 'Sem Equipe';
                          if (!teamGroups[team]) {
                            teamGroups[team] = [];
                          }
                          teamGroups[team].push(bill);
                        });
                        return teamGroups;
                      };

                      const recentTeamGroups = groupBillsByTeam(recent);
                      const oldTeamGroups = groupBillsByTeam(old);

                      return (
                        <div className="space-y-4">
                          {/* Filtro de Período */}
                          <div className="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
                            <Label className="text-sm font-semibold text-blue-800">
                              📅 Período:
                            </Label>
                            <select
                              value={laborBillPeriod}
                              onChange={(e) => changeLaborBillPeriod(e.target.value)}
                              className="border border-blue-300 rounded px-3 py-2 text-sm bg-white focus:border-blue-500 focus:ring-blue-100"
                            >
                              <option value="7">🗓️ Última Semana</option>
                              <option value="15">📅 Últimos 15 Dias</option>
                              <option value="30">📆 Último Mês</option>
                              <option value="60">📊 Últimos 2 Meses</option>
                              <option value="90">📈 Últimos 3 Meses</option>
                              <option value="180">📉 Últimos 6 Meses</option>
                            </select>
                            <span className="text-xs text-blue-700">
                              ({recent.length} {language === 'pt-BR' ? 'recentes' : 'recent'})
                            </span>
                          </div>

                          {/* Seção com nome dinâmico */}
                          <div className="border border-blue-200 rounded-lg overflow-hidden">
                            <div 
                              className="bg-blue-50 p-4 cursor-pointer hover:bg-blue-100 transition-colors"
                              onClick={() => setRecentBillsExpanded(!recentBillsExpanded)}
                            >
                              <div className="flex items-center justify-between">
                                <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                                  📝 {getPeriodName(laborBillPeriod, language)} ({recent.length})
                                </h3>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-blue-600">
                                    {recentBillsExpanded ? 
                                      (language === 'pt-BR' ? 'Clique para minimizar' : 'Click to minimize') : 
                                      (language === 'pt-BR' ? 'Clique para expandir' : 'Click to expand')
                                    }
                                  </span>
                                  <span className={`text-blue-600 transition-transform ${recentBillsExpanded ? 'rotate-180' : ''}`}>
                                    ▼
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {recentBillsExpanded && (
                              <div className="p-4 bg-white">
                                {user?.role === 'admin' ? (
                                  // ADMIN: Layout agrupado por equipes
                                  <div className="space-y-6">
                                    {Object.entries(recentTeamGroups).sort(([teamA], [teamB]) => sortPMNames(teamA, teamB)).map(([team, teamBills]) => (
                                      <div key={team}>
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <span className="text-blue-600 font-semibold text-lg">👥 {team}</span>
                                              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm">{teamBills.length} bills</span>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* Labor Bills da equipe */}
                                        <div className="p-4 space-y-3">
                                          {sortDrafts(teamBills).map((bill) => {
                                            const total = (typeof bill?.grand_total === 'number' && !Number.isNaN(bill.grand_total))
                                              ? bill.grand_total
                                              : ((bill?.items || []).reduce((s, it) => s + (it.qty || 0) * (it.unit_price || 0), 0) + (bill?.extra_items || []).reduce((s, ex) => s + (ex.qty || 0) * (ex.unit_price || 0), 0));
                                            
                                            const isPaid = bill.paid === true;
                                            
                                            return (
                                              <div key={bill.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border rounded-lg bg-white gap-3">
                                                <div className="cursor-pointer flex-1" onClick={() => viewLaborBillPDF(bill.id)}>
                                                  <div className="flex items-center gap-2">
                                                    <div className="font-medium text-sm sm:text-base">{bill.job_name || 'Sem nome'}</div>
                                                    {isPaid && (
                                                      <div className="bg-green-100 border border-green-300 rounded px-2 py-1 text-xs flex items-center gap-1">
                                                        <span className="text-green-800 font-medium">💰 Paid</span>
                                                      </div>
                                                    )}
                                                  </div>
                                                  <div className="text-gray-600 text-xs sm:text-sm">{bill.order_number} • {formatNyDate(bill.install_date)} • {formatMoney(total)}</div>
                                            <BillTeamBadges bill={bill} />
                                            <SentSupervisorBadge bill={bill} />
                                                </div>
                                                <div className="flex flex-wrap gap-1 sm:gap-2">
                                                  <Button size="sm" variant="outline" onClick={() => loadLaborBill(bill.id)} className="text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                                    <Edit className="h-3 w-3 sm:mr-1" />
                                                    <span className="hidden sm:inline">{t('edit')}</span>
                                                  </Button>
                                                  <Button size="sm" variant="outline" onClick={() => { setCompareBill(bill); setShowOldCompareModal(true); }} data-testid={`compare-bill-btn-${bill.id}`} className="text-purple-600 hover:text-purple-700 text-xs px-2 py-1 flex-1 sm:flex-none min-w-0" title="Comparar antigo x novo">
                                                    <FileSearch className="h-3 w-3 sm:mr-1" />
                                                    <span className="hidden sm:inline">Comparar</span>
                                                  </Button>
                                                  {user?.role === 'admin' && (
                                                    <>
                                                      <Button 
                                                        size="sm" 
                                                        variant="outline" 
                                                        onClick={() => markLaborBillAsPaid(bill.id)} 
                                                        className="text-green-600 hover:text-green-700 text-xs px-2 py-1 flex-1 sm:flex-none min-w-0"
                                                        title={t('markAsPaid')}
                                                        disabled={bill.paid}
                                                      >
                                                        <span className="text-xs">
                                                          {bill.paid ? '💰' : '💰'}
                                                        </span>
                                                        <span className="hidden sm:inline ml-1">
                                                          {bill.paid ? t('paid') : (language === 'pt-BR' ? 'Marcar' : 'Mark')}
                                                        </span>
                                                      </Button>
                                                      <Button 
                                                        size="sm" 
                                                        variant="outline" 
                                                        onClick={() => openTransferModal(bill)} 
                                                        className="text-orange-600 hover:text-orange-700 text-xs px-2 py-1 flex-1 sm:flex-none min-w-0"
                                                        title={t('transfer')}
                                                      >
                                                        <ArrowLeft className="h-3 w-3 sm:mr-1" />
                                                        <span className="hidden sm:inline">Transfer</span>
                                                      </Button>
                                                    </>
                                                  )}
                                                  <Button size="sm" variant="outline" onClick={() => { setCurrentBill(bill); setSelectedRecipients([]); setShowEmailDialog(true); }} className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                                    <Send className="h-3 w-3 sm:mr-1" />
                                                    <span className="hidden sm:inline">Email</span>
                                                  </Button>
                                                  <Button size="sm" variant="outline" onClick={() => deleteLaborBill(bill.id)} className="text-red-600 hover:text-red-700 text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                                    <Trash2 className="h-3 w-3 sm:mr-1" />
                                                    <span className="hidden sm:inline">Del</span>
                                                  </Button>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ))}
                                    {recent.length === 0 && <p className="text-center text-slate-500 py-8">Nenhum labor bill dos últimos 2 meses</p>}
                                  </div>
                                ) : (
                                  // PM: Layout original para usuários regulares
                                  <div className="space-y-3">
                                    {sortDrafts(recent).map((bill) => {
                                      const total = (typeof bill?.grand_total === 'number' && !Number.isNaN(bill.grand_total))
                                        ? bill.grand_total
                                        : ((bill?.items || []).reduce((s, it) => s + (it.qty || 0) * (it.unit_price || 0), 0) + (bill?.extra_items || []).reduce((s, ex) => s + (ex.qty || 0) * (ex.unit_price || 0), 0));
                                      
                                      const isPaid = bill.paid === true;
                                      
                                      return (
                                        <div key={bill.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border rounded-lg bg-white gap-3">
                                          <div className="cursor-pointer flex-1" onClick={() => viewLaborBillPDF(bill.id)}>
                                            <div className="flex items-center gap-2">
                                              <div className="font-medium text-sm sm:text-base">{bill.job_name || 'Sem nome'}</div>
                                              {isPaid && (
                                                <div className="bg-green-100 border border-green-300 rounded px-2 py-1 text-xs flex items-center gap-1">
                                                  <span className="text-green-800 font-medium">💰 Paid</span>
                                                </div>
                                              )}
                                            </div>
                                            <div className="text-gray-600 text-xs sm:text-sm">{bill.order_number} • {formatNyDate(bill.install_date)} • {formatMoney(total)}</div>
                                            <BillTeamBadges bill={bill} />
                                            <SentSupervisorBadge bill={bill} />
                                          </div>
                                          <div className="flex flex-wrap gap-1 sm:gap-2">
                                            <Button size="sm" variant="outline" onClick={() => loadLaborBill(bill.id)} className="text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                              <Edit className="h-3 w-3 sm:mr-1" />
                                              <span className="hidden sm:inline">Editar</span>
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => { setCompareBill(bill); setShowOldCompareModal(true); }} data-testid={`compare-bill-btn-${bill.id}`} className="text-purple-600 hover:text-purple-700 text-xs px-2 py-1 flex-1 sm:flex-none min-w-0" title="Comparar antigo x novo">
                                              <FileSearch className="h-3 w-3 sm:mr-1" />
                                              <span className="hidden sm:inline">Comparar</span>
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => { setCurrentBill(bill); setSelectedRecipients([]); setShowEmailDialog(true); }} className="text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                              <Send className="h-3 w-3 sm:mr-1" />
                                              <span className="hidden sm:inline">Email</span>
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => deleteLaborBill(bill.id)} className="text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                              <Trash2 className="h-3 w-3 sm:mr-1" />
                                              <span className="hidden sm:inline">Del</span>
                                            </Button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                    {recent.length === 0 && <p className="text-center text-slate-500 py-8">Nenhum labor bill dos últimos 2 meses</p>}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Seção Anteriores */}
                          <div className="border border-amber-200 rounded-lg overflow-hidden">
                            <div 
                              className="bg-amber-50 p-4 cursor-pointer hover:bg-amber-100 transition-colors"
                              onClick={() => setOldBillsExpanded(!oldBillsExpanded)}
                            >
                              <div className="flex items-center justify-between">
                                <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                                  📅 {language === 'pt-BR' ? 'Anteriores' : 'Previous'} ({old.length})
                                </h3>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-amber-600">
                                    {oldBillsExpanded ? 
                                      (language === 'pt-BR' ? 'Clique para minimizar' : 'Click to minimize') : 
                                      (language === 'pt-BR' ? 'Clique para expandir' : 'Click to expand')
                                    }
                                  </span>
                                  <span className={`text-amber-600 transition-transform ${oldBillsExpanded ? 'rotate-180' : ''}`}>
                                    ▼
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {oldBillsExpanded && (
                              <div className="p-4 bg-white">
                                {user?.role === 'admin' ? (
                                  // ADMIN: Layout agrupado por equipes  
                                  <div className="space-y-6">
                                    {Object.entries(oldTeamGroups).sort(([teamA], [teamB]) => sortPMNames(teamA, teamB)).map(([team, teamBills]) => (
                                      <div key={team}>
                                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <span className="text-amber-600 font-semibold text-lg">👥 {team}</span>
                                              <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded text-sm">{teamBills.length} bills</span>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* Labor Bills da equipe */}
                                        <div className="p-4 space-y-3">
                                          {sortDrafts(teamBills).map((bill) => {
                                            const total = (typeof bill?.grand_total === 'number' && !Number.isNaN(bill.grand_total))
                                              ? bill.grand_total
                                              : ((bill?.items || []).reduce((s, it) => s + (it.qty || 0) * (it.unit_price || 0), 0) + (bill?.extra_items || []).reduce((s, ex) => s + (ex.qty || 0) * (ex.unit_price || 0), 0));
                                            
                                            const isPaid = bill.paid === true;
                                            
                                            return (
                                              <div key={bill.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border rounded-lg bg-white gap-3">
                                                <div className="cursor-pointer flex-1" onClick={() => viewLaborBillPDF(bill.id)}>
                                                  <div className="flex items-center gap-2">
                                                    <div className="font-medium text-sm sm:text-base">{bill.job_name || 'Sem nome'}</div>
                                                    {isPaid && (
                                                      <div className="bg-green-100 border border-green-300 rounded px-2 py-1 text-xs flex items-center gap-1">
                                                        <span className="text-green-800 font-medium">💰 Paid</span>
                                                      </div>
                                                    )}
                                                  </div>
                                                  <div className="text-gray-600 text-xs sm:text-sm">{bill.order_number} • {formatNyDate(bill.install_date)} • {formatMoney(total)}</div>
                                            <BillTeamBadges bill={bill} />
                                            <SentSupervisorBadge bill={bill} />
                                                </div>
                                                <div className="flex flex-wrap gap-1 sm:gap-2">
                                                  <Button size="sm" variant="outline" onClick={() => loadLaborBill(bill.id)} className="text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                                    <Edit className="h-3 w-3 sm:mr-1" />
                                                    <span className="hidden sm:inline">{t('edit')}</span>
                                                  </Button>
                                                  <Button size="sm" variant="outline" onClick={() => { setCompareBill(bill); setShowOldCompareModal(true); }} data-testid={`compare-bill-btn-${bill.id}`} className="text-purple-600 hover:text-purple-700 text-xs px-2 py-1 flex-1 sm:flex-none min-w-0" title="Comparar antigo x novo">
                                                    <FileSearch className="h-3 w-3 sm:mr-1" />
                                                    <span className="hidden sm:inline">Comparar</span>
                                                  </Button>
                                                  {user?.role === 'admin' && (
                                                    <>
                                                      <Button 
                                                        size="sm" 
                                                        variant="outline" 
                                                        onClick={() => markLaborBillAsPaid(bill.id)} 
                                                        className="text-green-600 hover:text-green-700 text-xs px-2 py-1 flex-1 sm:flex-none min-w-0"
                                                        title={t('markAsPaid')}
                                                        disabled={bill.paid}
                                                      >
                                                        <span className="text-xs">
                                                          {bill.paid ? '💰' : '💰'}
                                                        </span>
                                                        <span className="hidden sm:inline ml-1">
                                                          {bill.paid ? t('paid') : (language === 'pt-BR' ? 'Marcar' : 'Mark')}
                                                        </span>
                                                      </Button>
                                                      <Button 
                                                        size="sm" 
                                                        variant="outline" 
                                                        onClick={() => openTransferModal(bill)} 
                                                        className="text-orange-600 hover:text-orange-700 text-xs px-2 py-1 flex-1 sm:flex-none min-w-0"
                                                        title={t('transfer')}
                                                      >
                                                        <ArrowLeft className="h-3 w-3 sm:mr-1" />
                                                        <span className="hidden sm:inline">Transfer</span>
                                                      </Button>
                                                    </>
                                                  )}
                                                  <Button size="sm" variant="outline" onClick={() => { setCurrentBill(bill); setSelectedRecipients([]); setShowEmailDialog(true); }} className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                                    <Send className="h-3 w-3 sm:mr-1" />
                                                    <span className="hidden sm:inline">Email</span>
                                                  </Button>
                                                  <Button size="sm" variant="outline" onClick={() => deleteLaborBill(bill.id)} className="text-red-600 hover:text-red-700 text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                                    <Trash2 className="h-3 w-3 sm:mr-1" />
                                                    <span className="hidden sm:inline">Del</span>
                                                  </Button>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ))}
                                    {old.length === 0 && <p className="text-center text-slate-500 py-8">Nenhum labor bill anterior</p>}
                                  </div>
                                ) : (
                                  // PM: Layout original para usuários regulares
                                  <div className="space-y-3">
                                    {sortDrafts(old).map((bill) => {
                                      const total = (typeof bill?.grand_total === 'number' && !Number.isNaN(bill.grand_total))
                                        ? bill.grand_total
                                        : ((bill?.items || []).reduce((s, it) => s + (it.qty || 0) * (it.unit_price || 0), 0) + (bill?.extra_items || []).reduce((s, ex) => s + (ex.qty || 0) * (ex.unit_price || 0), 0));
                                      
                                      const isPaid = bill.paid === true;
                                      
                                      return (
                                        <div key={bill.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border rounded-lg bg-white gap-3">
                                          <div className="cursor-pointer flex-1" onClick={() => viewLaborBillPDF(bill.id)}>
                                            <div className="flex items-center gap-2">
                                              <div className="font-medium text-sm sm:text-base">{bill.job_name || 'Sem nome'}</div>
                                              {isPaid && (
                                                <div className="bg-green-100 border border-green-300 rounded px-2 py-1 text-xs flex items-center gap-1">
                                                  <span className="text-green-800 font-medium">💰 Paid</span>
                                                </div>
                                              )}
                                            </div>
                                            <div className="text-gray-600 text-xs sm:text-sm">{bill.order_number} • {formatNyDate(bill.install_date)} • {formatMoney(total)}</div>
                                            <BillTeamBadges bill={bill} />
                                            <SentSupervisorBadge bill={bill} />
                                          </div>
                                          <div className="flex flex-wrap gap-1 sm:gap-2">
                                            <Button size="sm" variant="outline" onClick={() => loadLaborBill(bill.id)} className="text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                              <Edit className="h-3 w-3 sm:mr-1" />
                                              <span className="hidden sm:inline">Editar</span>
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => { setCompareBill(bill); setShowOldCompareModal(true); }} data-testid={`compare-bill-btn-${bill.id}`} className="text-purple-600 hover:text-purple-700 text-xs px-2 py-1 flex-1 sm:flex-none min-w-0" title="Comparar antigo x novo">
                                              <FileSearch className="h-3 w-3 sm:mr-1" />
                                              <span className="hidden sm:inline">Comparar</span>
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => { setCurrentBill(bill); setSelectedRecipients([]); setShowEmailDialog(true); }} className="text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                              <Send className="h-3 w-3 sm:mr-1" />
                                              <span className="hidden sm:inline">Email</span>
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => deleteLaborBill(bill.id)} className="text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                              <Trash2 className="h-3 w-3 sm:mr-1" />
                                              <span className="hidden sm:inline">Del</span>
                                            </Button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                    {old.length === 0 && <p className="text-center text-slate-500 py-8">Nenhum labor bill anterior</p>}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                    {laborBills.length === 0 && <p className="text-center text-slate-500 py-8">Nenhum rascunho</p>}
                  </div>
                ) : (
                  // PM users: Layout com seções por data (igual ao ADMIN mas simplificado)
                  <div className="space-y-4">
                    {(() => {
                      // Aplicar filtros para PM
                      const filteredBills = laborBills.filter((b) => {
                        const q = draftsSearch.trim().toLowerCase();
                        const matchesSearch = !q || ((b.job_name || '').toLowerCase().includes(q) || (b.order_number || '').toLowerCase().includes(q));
                        const matchesPaidFilter = !showOnlyPaid || (b.paid === true);
                        const matchesSentFilter = !showOnlySentToSupervisor || !!b.last_sent_supervisor;
                        return matchesSearch && matchesPaidFilter && matchesSentFilter;
                      });

                      // Separar bills por data usando período configurável
                      const { recent, old } = separateLaborBillsByDate(filteredBills, parseInt(laborBillPeriod));

                      return (
                        <>
                          {/* Filtro de Período - PM */}
                          <div className="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 mb-4">
                            <Label className="text-sm font-semibold text-blue-800">
                              📅 Período:
                            </Label>
                            <select
                              value={laborBillPeriod}
                              onChange={(e) => changeLaborBillPeriod(e.target.value)}
                              className="border border-blue-300 rounded px-3 py-2 text-sm bg-white focus:border-blue-500 focus:ring-blue-100"
                            >
                              <option value="7">🗓️ Última Semana</option>
                              <option value="15">📅 Últimos 15 Dias</option>
                              <option value="30">📆 Último Mês</option>
                              <option value="60">📊 Últimos 2 Meses</option>
                              <option value="90">📈 Últimos 3 Meses</option>
                              <option value="180">📉 Últimos 6 Meses</option>
                            </select>
                            <span className="text-xs text-blue-700">
                              ({recent.length} {language === 'pt-BR' ? 'recentes' : 'recent'})
                            </span>
                          </div>

                          {/* Seção com nome dinâmico para PM */}
                          <div className="border border-blue-200 rounded-lg overflow-hidden">
                            <div 
                              className="bg-blue-50 p-4 cursor-pointer hover:bg-blue-100 transition-colors"
                              onClick={() => setRecentBillsExpanded(!recentBillsExpanded)}
                            >
                              <div className="flex items-center justify-between">
                                <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                                  📝 {getPeriodName(laborBillPeriod, language)} ({recent.length})
                                </h3>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-blue-600">
                                    {recentBillsExpanded ? 
                                      (language === 'pt-BR' ? 'Clique para minimizar' : 'Click to minimize') : 
                                      (language === 'pt-BR' ? 'Clique para expandir' : 'Click to expand')
                                    }
                                  </span>
                                  <span className={`text-blue-600 transition-transform ${recentBillsExpanded ? 'rotate-180' : ''}`}>
                                    ▼
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {recentBillsExpanded && (
                              <div className="p-4 bg-white space-y-3">
                                {sortDrafts(recent).map((bill) => {
                                  const total = (typeof bill?.grand_total === 'number' && !Number.isNaN(bill.grand_total))
                                    ? bill.grand_total
                                    : ((bill?.items || []).reduce((s, it) => s + (it.qty || 0) * (it.unit_price || 0), 0) + (bill?.extra_items || []).reduce((s, ex) => s + (ex.qty || 0) * (ex.unit_price || 0), 0));
                                  
                                  const isPaid = bill.paid === true;
                                  
                                  return (
                                    <div key={bill.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border rounded-lg bg-white gap-3">
                                      <div className="cursor-pointer flex-1" onClick={() => viewLaborBillPDF(bill.id)}>
                                        <div className="flex items-center gap-2">
                                          <div className="font-medium text-sm sm:text-base">{bill.job_name || 'Sem nome'}</div>
                                          {isPaid && (
                                            <div className="bg-green-100 border border-green-300 rounded px-2 py-1 text-xs flex items-center gap-1">
                                              <span className="text-green-800 font-medium">💰 Paid</span>
                                            </div>
                                          )}
                                        </div>
                                        <div className="text-gray-600 text-xs sm:text-sm">{bill.order_number} • {formatNyDate(bill.install_date)} • {formatMoney(total)}</div>
                                            <BillTeamBadges bill={bill} />
                                            <SentSupervisorBadge bill={bill} />
                                      </div>
                                      <div className="flex flex-wrap gap-1 sm:gap-2">
                                        <Button size="sm" variant="outline" onClick={() => loadLaborBill(bill.id)} className="text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                          <Edit className="h-3 w-3 sm:mr-1" />
                                          <span className="hidden sm:inline">Editar</span>
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => { setCompareBill(bill); setShowOldCompareModal(true); }} data-testid={`compare-bill-btn-${bill.id}`} className="text-purple-600 hover:text-purple-700 text-xs px-2 py-1 flex-1 sm:flex-none min-w-0" title="Comparar antigo x novo">
                                          <FileSearch className="h-3 w-3 sm:mr-1" />
                                          <span className="hidden sm:inline">Comparar</span>
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => { setCurrentBill(bill); setSelectedRecipients([]); setShowEmailDialog(true); }} className="text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                          <Send className="h-3 w-3 sm:mr-1" />
                                          <span className="hidden sm:inline">Email</span>
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => deleteLaborBill(bill.id)} className="text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                          <Trash2 className="h-3 w-3 sm:mr-1" />
                                          <span className="hidden sm:inline">Del</span>
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })}
                                {recent.length === 0 && <p className="text-center text-slate-500 py-8">Nenhum labor bill dos últimos 2 meses</p>}
                              </div>
                            )}
                          </div>

                          {/* Seção Anteriores para PM */}
                          <div className="border border-amber-200 rounded-lg overflow-hidden">
                            <div 
                              className="bg-amber-50 p-4 cursor-pointer hover:bg-amber-100 transition-colors"
                              onClick={() => setOldBillsExpanded(!oldBillsExpanded)}
                            >
                              <div className="flex items-center justify-between">
                                <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                                  📅 {language === 'pt-BR' ? 'Anteriores' : 'Previous'} ({old.length})
                                </h3>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-amber-600">
                                    {oldBillsExpanded ? 
                                      (language === 'pt-BR' ? 'Clique para minimizar' : 'Click to minimize') : 
                                      (language === 'pt-BR' ? 'Clique para expandir' : 'Click to expand')
                                    }
                                  </span>
                                  <span className={`text-amber-600 transition-transform ${oldBillsExpanded ? 'rotate-180' : ''}`}>
                                    ▼
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {oldBillsExpanded && (
                              <div className="p-4 bg-white space-y-3">
                                {sortDrafts(old).map((bill) => {
                                  const total = (typeof bill?.grand_total === 'number' && !Number.isNaN(bill.grand_total))
                                    ? bill.grand_total
                                    : ((bill?.items || []).reduce((s, it) => s + (it.qty || 0) * (it.unit_price || 0), 0) + (bill?.extra_items || []).reduce((s, ex) => s + (ex.qty || 0) * (ex.unit_price || 0), 0));
                                  
                                  const isPaid = bill.paid === true;
                                  
                                  return (
                                    <div key={bill.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border rounded-lg bg-white gap-3">
                                      <div className="cursor-pointer flex-1" onClick={() => viewLaborBillPDF(bill.id)}>
                                        <div className="flex items-center gap-2">
                                          <div className="font-medium text-sm sm:text-base">{bill.job_name || 'Sem nome'}</div>
                                          {isPaid && (
                                            <div className="bg-green-100 border border-green-300 rounded px-2 py-1 text-xs flex items-center gap-1">
                                              <span className="text-green-800 font-medium">💰 Paid</span>
                                            </div>
                                          )}
                                        </div>
                                        <div className="text-gray-600 text-xs sm:text-sm">{bill.order_number} • {formatNyDate(bill.install_date)} • {formatMoney(total)}</div>
                                            <BillTeamBadges bill={bill} />
                                            <SentSupervisorBadge bill={bill} />
                                      </div>
                                      <div className="flex flex-wrap gap-1 sm:gap-2">
                                        <Button size="sm" variant="outline" onClick={() => loadLaborBill(bill.id)} className="text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                          <Edit className="h-3 w-3 sm:mr-1" />
                                          <span className="hidden sm:inline">Editar</span>
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => { setCompareBill(bill); setShowOldCompareModal(true); }} data-testid={`compare-bill-btn-${bill.id}`} className="text-purple-600 hover:text-purple-700 text-xs px-2 py-1 flex-1 sm:flex-none min-w-0" title="Comparar antigo x novo">
                                          <FileSearch className="h-3 w-3 sm:mr-1" />
                                          <span className="hidden sm:inline">Comparar</span>
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => { setCurrentBill(bill); setSelectedRecipients([]); setShowEmailDialog(true); }} className="text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                          <Send className="h-3 w-3 sm:mr-1" />
                                          <span className="hidden sm:inline">Email</span>
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => deleteLaborBill(bill.id)} className="text-xs px-2 py-1 flex-1 sm:flex-none min-w-0">
                                          <Trash2 className="h-3 w-3 sm:mr-1" />
                                          <span className="hidden sm:inline">Del</span>
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })}
                                {old.length === 0 && <p className="text-center text-slate-500 py-8">Nenhum labor bill anterior</p>}
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Weekly Report - Versão compacta e sutil */}
          <TabsContent value="reports" className="mt-4">
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    📊 {t('weeklyReport')} - {language === 'pt-BR' ? 'Equipe' : 'Team'} {user?.username || 'PM'}
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-xs"
                    onClick={loadPMWeeklyReport}
                    disabled={weeklyReportLoading}
                  >
                    {weeklyReportLoading ? `🔄 ${t('loading')}` : `🔄 ${t('update')}`}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {weeklyReportLoading ? (
                  <div className="text-center py-8">
                    <div className="text-2xl mb-2">🔄</div>
                    <div className="text-gray-500">Carregando dados da sua equipe...</div>
                  </div>
                ) : weeklyReportData?.weeklyData?.length > 0 ? (
                  weeklyReportData.weeklyData.map((week, weekIndex) => (
                    <div key={`week-${week.week_ending}-${weekIndex}`} className="border rounded-lg bg-white shadow-sm">
                      {/* Header da Semana */}
                      <div className="bg-gray-50 border-b rounded-t-lg p-3">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div>
                            <h3 className="font-medium text-sm">
                              📅 {language === 'pt-BR' ? 'Semana terminada em' : 'Week ended on'}: {formatDate(week.week_ending)}
                            </h3>
                            <div className="text-xs text-gray-600">
                              {language === 'pt-BR' ? 'Equipe' : 'Team'}: {user?.username} | {week.entries.length} {language === 'pt-BR' ? 'trabalhos' : 'jobs'}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="bg-green-100 border border-green-300 rounded px-2 py-1">
                              <span className="text-green-800 font-medium text-xs">
                                {language === 'pt-BR' ? 'concluída' : 'completed'}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadWeeklyReportPDF(week)}
                              className="bg-blue-50 hover:bg-blue-100 text-xs px-2 py-1"
                              title={`${language === 'pt-BR' ? 'Download relatório da semana' : 'Download weekly report'} ${formatDate(week.week_ending)}`}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              {language === 'pt-BR' ? 'Download Report' : 'Download Report'}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Cards de estatísticas por semana */}
                      <div className="p-3">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                            <div className="text-blue-700 text-xs mb-1">
                              {language === 'pt-BR' ? 'Total de trabalhos' : 'Total jobs'}
                            </div>
                            <div className="text-xl font-semibold text-blue-600">
                              {week.total_jobs}
                            </div>
                          </div>
                          <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
                            <div className="text-purple-700 text-xs mb-1">{t('totalFinal')}</div>
                            <div className="text-xl font-semibold text-purple-600">
                              ${(week.total_final || 0).toLocaleString('en-US')}
                            </div>
                          </div>
                          <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
                            <div className="text-orange-700 text-xs mb-1">{t('expenses')}</div>
                            <div className="text-xl font-semibold text-orange-600">
                              ${(week.total_expenses || 0).toLocaleString('en-US')}
                            </div>
                          </div>
                          {week.backcharges_total > 0 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                              <div className="text-yellow-700 text-xs mb-1">Backcharges</div>
                              <div className="text-xl font-semibold text-yellow-600">
                                -${(week.backcharges_total || 0).toLocaleString('en-US')}
                              </div>
                            </div>
                          )}
                          {week.adicionais_total > 0 && (
                            <div className="bg-green-50 border border-green-200 rounded-md p-3" data-testid="pm-week-adicionais">
                              <div className="text-green-700 text-xs mb-1">{language === 'pt-BR' ? 'Adicionais' : 'Additional'}</div>
                              <div className="text-xl font-semibold text-green-600">
                                +${(week.adicionais_total || 0).toLocaleString('en-US')}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Tabela por semana */}
                        <div className="bg-white border rounded-md overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="text-left p-2 font-medium text-gray-700 text-xs">{t('date')}</th>
                                  <th className="text-left p-2 font-medium text-gray-700 text-xs">Job #</th>
                                  <th className="text-left p-2 font-medium text-gray-700 text-xs">{t('customer')}</th>
                                  <th className="text-left p-2 font-medium text-gray-700 text-xs">{t('labor')}</th>
                                  <th className="text-left p-2 font-medium text-gray-700 text-xs">{t('expenses')}</th>
                                  <th className="text-left p-2 font-medium text-gray-700 text-xs">
                                    {language === 'pt-BR' ? 'Valor Final' : 'Final Value'}
                                  </th>
                                  <th className="text-left p-2 font-medium text-gray-700 text-xs">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {week.entries.map((entry, entryIndex) => (
                                  <tr key={`entry-${entry.job_number}-${entryIndex}`} className="border-t hover:bg-gray-50">
                                    <td className="p-2 text-xs">
                                      {formatDate(entry.date) || '-'}
                                    </td>
                                    <td className="p-2 text-xs font-medium">{entry.job_number || '-'}</td>
                                    <td className="p-2 text-xs">{entry.customer || '-'}</td>
                                    <td className="p-2 text-xs text-black font-medium">
                                      ${(entry.labor_total || 0).toFixed(2)}
                                    </td>
                                    <td className="p-2 text-xs text-red-600 font-medium">
                                      ${(entry.expenses_total || 0).toFixed(2)}
                                    </td>
                                    <td className="p-2 text-xs text-green-600 font-medium">
                                      ${(entry.final_value || entry.labor_total || 0).toFixed(2)}
                                    </td>
                                    <td className="p-2 text-xs text-green-600 font-medium">
                                      {entry.found_match ? '✅ Processado' : '⚪ Pendente'}
                                    </td>
                                  </tr>
                                ))}
                                {/* Backcharge rows */}
                                {week.backcharges && week.backcharges.map((bc, bcIdx) => (
                                  <tr key={`bc-${bcIdx}`} className="border-t bg-yellow-50">
                                    <td className="p-2 text-xs text-yellow-800 font-medium">⚠️</td>
                                    <td className="p-2 text-xs text-yellow-800 font-medium">BACKCHARGE</td>
                                    <td className="p-2 text-xs text-yellow-800">{bc.descricao}</td>
                                    <td className="p-2 text-xs"></td>
                                    <td className="p-2 text-xs"></td>
                                    <td className="p-2 text-xs font-bold text-red-600">-${(bc.valor || 0).toFixed(2)}</td>
                                    <td className="p-2 text-xs text-yellow-700">⚠️ Dedução</td>
                                  </tr>
                                ))}
                                {/* Adicional rows */}
                                {week.adicionais && week.adicionais.map((ad, adIdx) => (
                                  <tr key={`ad-${adIdx}`} className="border-t bg-green-50">
                                    <td className="p-2 text-xs text-green-800 font-medium">💰</td>
                                    <td className="p-2 text-xs text-green-800 font-medium">ADICIONAL</td>
                                    <td className="p-2 text-xs text-green-800">{ad.descricao}</td>
                                    <td className="p-2 text-xs"></td>
                                    <td className="p-2 text-xs"></td>
                                    <td className="p-2 text-xs font-bold text-green-700">+${(ad.valor || 0).toFixed(2)}</td>
                                    <td className="p-2 text-xs text-green-700">💰 Acréscimo</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-2">📊</div>
                    <div className="font-medium text-gray-700">
                      {language === 'pt-BR' ? 'Nenhum dado encontrado' : 'No data found'}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {language === 'pt-BR' ? 
                        `Não há relatórios processados para a equipe ${user?.username || 'PM'}.` :
                        `No reports processed for team ${user?.username || 'PM'}.`
                      }
                    </div>
                    <div className="text-xs text-gray-400 mt-2">
                      {language === 'pt-BR' ? 
                        'Processe um relatório de payroll primeiro na aba Admin.' :
                        'Process a payroll report first in the Admin tab.'
                      }
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Setup */}
          <TabsContent value="emails" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Destinatários de E-mail</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 items-end mb-4">
                  <div className="flex-1">
                    <Label>Nome</Label>
                    <Input value={newRecipient.name} onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })} placeholder="Nome" />
                  </div>
                  <div className="flex-1">
                    <Label>E-mail</Label>
                    <Input value={newRecipient.email} onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })} placeholder="email@exemplo.com" />
                  </div>
                  <Button onClick={addEmailRecipient}>Adicionar</Button>
                </div>
                <div className="space-y-2">
                  {emailRecipients.map((r) => (
                    <div key={r.id} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                      <div className="flex items-center gap-2">
                        {r.admin_locked && (
                          <span title="Somente o Admin pode apagar" className="text-slate-400">🔒</span>
                        )}
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {r.name}
                            {r.is_finance && (
                              <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">💵 Financeiro</span>
                            )}
                          </div>
                          <div className="text-slate-600 text-sm">{r.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className={r.is_finance ? 'border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100' : 'text-slate-600'}
                          onClick={() => toggleRecipientFinance(r.id, !r.is_finance)}
                          data-testid={`recipient-finance-toggle-${r.id}`}
                          title="Marcar/desmarcar como e-mail do Financeiro"
                        >
                          {r.is_finance ? '✓ Financeiro' : 'Marcar Financeiro'}
                        </Button>
                        <Button variant="outline" className="text-red-600 hover:text-red-700" disabled={!!r.admin_locked} title={r.admin_locked ? 'Somente o Admin pode apagar' : ''} onClick={() => !r.admin_locked && deleteEmailRecipient(r.id)}>Remover</Button>
                      </div>
                    </div>
                  ))}
                  {emailRecipients.length === 0 && <p className="text-center text-slate-500 py-6">Nenhum destinatário cadastrado</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Docs (Arbitration) - Com Dropdown Apenas */}
          {user?.role !== 'admin' && (
            <TabsContent value="arbitration" className="mt-6">
              {/* Arbitration Files Sub-tab */}
              {arbitrationSubTab === 'files' && (
                <Card>
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <CardTitle className="flex items-center gap-2">
                        ⚖️ {t('arbitrationFiles')}
                      </CardTitle>
                      <div className="flex gap-2">
                        {selectedArbitrationFiles.length > 0 && (
                          <Button 
                            onClick={() => setShowBulkEmailModal(true)}
                            className="bg-green-600 hover:bg-green-700"
                            size="sm"
                          >
                            <Send className="h-4 w-4 mr-2" />
                            {language === 'pt-BR' ? `Enviar ${selectedArbitrationFiles.length}` : `Send ${selectedArbitrationFiles.length}`}
                          </Button>
                        )}
                        <Button 
                          onClick={() => setShowArbitrationForm(true)} 
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Plus className="h-4 w-4 mr-2" /> 
                          {language === 'pt-BR' ? 'Novo Arquivo' : 'New File'}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Search and Filter Controls */}
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div>
                        <Input 
                          placeholder={language === 'pt-BR' ? 'Buscar por nome...' : 'Search by name...'}
                          value={arbitrationSearch}
                          onChange={(e) => setArbitrationSearch(e.target.value)}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <select 
                          className="w-full border rounded px-3 py-2 bg-white text-sm"
                          value={arbitrationFilterPM}
                          onChange={(e) => setArbitrationFilterPM(e.target.value)}
                        >
                          <option value="ALL">{language === 'pt-BR' ? 'Todas as equipes' : 'All teams'}</option>
                          {pmOptions.map((pm) => (
                            <option key={pm} value={pm}>{pm}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <select 
                          className="w-full border rounded px-3 py-2 bg-white text-sm"
                          value={arbitrationFilterStatus}
                          onChange={(e) => setArbitrationFilterStatus(e.target.value)}
                        >
                          <option value="ALL">{language === 'pt-BR' ? 'Todos os status' : 'All status'}</option>
                          <option value="SIGNED">{language === 'pt-BR' ? 'Assinados' : 'Signed'}</option>
                          <option value="UNSIGNED">{language === 'pt-BR' ? 'Não assinados' : 'Unsigned'}</option>
                        </select>
                      </div>
                      <div>
                        <select 
                          className="w-full border rounded px-3 py-2 bg-white text-sm"
                          value={arbitrationSort}
                          onChange={(e) => setArbitrationSort(e.target.value)}
                        >
                          <option value="date_desc">{language === 'pt-BR' ? 'Data (Recente)' : 'Date (Recent)'}</option>
                          <option value="date_asc">{language === 'pt-BR' ? 'Data (Antiga)' : 'Date (Old)'}</option>
                          <option value="name_asc">{language === 'pt-BR' ? 'Nome (A-Z)' : 'Name (A-Z)'}</option>
                          <option value="name_desc">{language === 'pt-BR' ? 'Nome (Z-A)' : 'Name (Z-A)'}</option>
                          <option value="pm_asc">PM (A-Z)</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Selection Controls */}
                    {arbitrationFiles.length > 0 && (
                      <div className="mt-3 flex items-center gap-4">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedArbitrationFiles.length === arbitrationFiles.length && arbitrationFiles.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedArbitrationFiles([...arbitrationFiles]);
                              } else {
                                setSelectedArbitrationFiles([]);
                              }
                            }}
                            className="text-blue-600"
                          />
                          <span className="text-blue-600 font-medium">
                            {language === 'pt-BR' ? 'Selecionar Todos' : 'Select All'}
                          </span>
                        </label>
                        {selectedArbitrationFiles.length > 0 && (
                          <Badge variant="secondary" className="text-sm">
                            {selectedArbitrationFiles.length} {language === 'pt-BR' ? 'selecionados' : 'selected'}
                          </Badge>
                        )}
                      </div>
                    )}
                  </CardHeader>
                  
                  {/* Arbitration Files Content - Same as before */}
                  <CardContent>
                    {arbitrationLoading ? (
                      <div className="text-center py-8">
                        <div className="text-2xl mb-2">🔄</div>
                        <div>{language === 'pt-BR' ? 'Carregando arquivos de arbitragem...' : 'Loading arbitration files...'}</div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Existing arbitration files code */}
                        {arbitrationFiles.length === 0 ? (
                          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                            <div className="text-gray-400 mb-4 text-4xl">⚖️</div>
                            <h3 className="font-medium text-gray-700 mb-2">
                              {language === 'pt-BR' ? 'Nenhum arquivo de arbitragem' : 'No arbitration files'}
                            </h3>
                            <p className="text-gray-500 text-sm">
                              {language === 'pt-BR' ? 'Crie formulários de arbitragem digitais para seus helpers' : 'Create digital arbitration forms for your helpers'}
                            </p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {(() => {
                              // Apply filters and sorting
                              let filteredFiles = arbitrationFiles.filter(file => {
                                const matchesSearch = !arbitrationSearch || 
                                  file.helper_name.toLowerCase().includes(arbitrationSearch.toLowerCase());
                                
                                const matchesPM = arbitrationFilterPM === 'ALL' || file.pm_user === arbitrationFilterPM;
                                
                                const matchesStatus = arbitrationFilterStatus === 'ALL' || 
                                  (arbitrationFilterStatus === 'SIGNED' && file.signature && file.signature.startsWith('data:image')) ||
                                  (arbitrationFilterStatus === 'UNSIGNED' && (!file.signature || !file.signature.startsWith('data:image')));
                                
                                return matchesSearch && matchesPM && matchesStatus;
                              });

                              // Sort files
                              filteredFiles.sort((a, b) => {
                                switch (arbitrationSort) {
                                  case 'date_desc':
                                    return new Date(b.date) - new Date(a.date);
                                  case 'date_asc':
                                    return new Date(a.date) - new Date(b.date);
                                  case 'name_asc':
                                    return a.helper_name.localeCompare(b.helper_name);
                                  case 'name_desc':
                                    return b.helper_name.localeCompare(a.helper_name);
                                  case 'pm_asc':
                                    return a.pm_user.localeCompare(b.pm_user);
                                  default:
                                    return 0;
                                }
                              });

                              return filteredFiles.length === 0 ? (
                                <div className="col-span-3 text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                                  <div className="text-gray-400 mb-4 text-4xl">⚖️</div>
                                  <h3 className="font-medium text-gray-700 mb-2">
                                    {language === 'pt-BR' ? 'Nenhum arquivo encontrado' : 'No files found'}
                                  </h3>
                                  <p className="text-gray-500 text-sm">
                                    {language === 'pt-BR' ? 'Ajuste os filtros ou crie novos arquivos' : 'Adjust filters or create new files'}
                                  </p>
                                </div>
                              ) : (
                                filteredFiles.map((file) => (
                                  <div key={file.id} className={`bg-white border rounded-lg p-4 hover:shadow-md transition-shadow ${
                                    selectedArbitrationFiles.some(f => f.id === file.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                                  }`}>
                                    <div className="flex items-start gap-3">
                                      {/* Checkbox for Selection */}
                                      <input
                                        type="checkbox"
                                        checked={selectedArbitrationFiles.some(f => f.id === file.id)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedArbitrationFiles(prev => [...prev, file]);
                                          } else {
                                            setSelectedArbitrationFiles(prev => prev.filter(f => f.id !== file.id));
                                          }
                                        }}
                                        className="mt-1 text-blue-600 flex-shrink-0"
                                      />
                                      
                                      <div className="flex-1 min-w-0">
                                        {/* Existing card content */}
                                        <div className="flex items-start justify-between mb-3">
                                          <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <div className="text-lg">⚖️</div>
                                            <div className="flex-1 min-w-0">
                                              <div className="font-semibold text-gray-800 truncate">{file.helper_name}</div>
                                              <div className="text-xs text-gray-500">
                                                {language === 'pt-BR' ? 'Criado por' : 'Created by'} {file.pm_user}
                                              </div>
                                            </div>
                                          </div>
                                          <div className="text-right">
                                            <div className="text-xs text-gray-500">
                                              {new Date(file.date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}
                                            </div>
                                            <div className="flex gap-1 mt-1">
                                              <Badge variant="secondary" className="text-xs">PDF</Badge>
                                              {file.signature && file.signature.startsWith('data:image') ? (
                                                <Badge className="bg-green-100 text-green-800 text-xs">
                                                  ✅ {language === 'pt-BR' ? 'Assinado' : 'Signed'}
                                                </Badge>
                                              ) : (
                                                <Badge className="bg-red-100 text-red-800 text-xs">
                                                  ❌ {language === 'pt-BR' ? 'Não Assinado' : 'Unsigned'}
                                                </Badge>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* Content Information */}
                                        <div className="space-y-2 text-sm mb-4">
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">{language === 'pt-BR' ? 'Data' : 'Date'}:</span> 
                                            <span className="font-medium">{new Date(file.date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">PM:</span> 
                                            <span className="font-medium">{file.pm_user}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">Status:</span> 
                                            <span className="font-medium text-green-600">
                                              ✅ {language === 'pt-BR' ? 'Pronto' : 'Ready'}
                                            </span>
                                          </div>
                                        </div>
                                        
                                        {/* Action Buttons */}
                                        <div className="grid grid-cols-2 gap-1">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => viewArbitrationFile(file.id, file.helper_name)}
                                            className="text-xs"
                                          >
                                            <Eye className="h-3 w-3 mr-1" />
                                            {t('view')}
                                          </Button>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => downloadArbitrationFile(file.id)}
                                            className="text-xs"
                                          >
                                            <Download className="h-3 w-3 mr-1" />
                                            {t('download')}
                                          </Button>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                              setSelectedArbitrationFileId(file.id);
                                              setShowArbitrationEmailForm(true);
                                            }}
                                            className="text-xs bg-blue-50 hover:bg-blue-100"
                                          >
                                            <Send className="h-3 w-3 mr-1" />
                                            {language === 'pt-BR' ? 'Email' : 'Email'}
                                          </Button>
                                          {(!file.signature || !file.signature.startsWith('data:image')) && (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => {
                                                setArbitrationFileToSign(file);
                                                setShowArbitrationSignModal(true);
                                              }}
                                              className="text-xs bg-orange-50 hover:bg-orange-100"
                                            >
                                              <Edit className="h-3 w-3 mr-1" />
                                              {language === 'pt-BR' ? 'Assinar' : 'Sign'}
                                            </Button>
                                          )}
                                          {user?.role === 'admin' && (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={async () => {
                                                if (window.confirm(language === 'pt-BR' ? 
                                                  `Deletar arquivo de arbitragem de ${file.helper_name}?` :
                                                  `Delete arbitration file for ${file.helper_name}?`
                                                )) {
                                                  console.log('🗑️ Calling deleteArbitrationFile for:', file.id);
                                                  deleteArbitrationFile(file.id, file.helper_name);
                                                }
                                              }}
                                              className="text-red-600 hover:text-red-700 text-xs col-span-2"
                                              title={language === 'pt-BR' ? 'Deletar arquivo de arbitragem' : 'Delete arbitration file'}
                                            >
                                              <Trash2 className="h-3 w-3 mr-1" />
                                              {language === 'pt-BR' ? 'Deletar Arbitration' : 'Delete Arbitration'}
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Independent Contractor Sub-tab - Duplicate of Arbitration */}
              {arbitrationSubTab === 'contractor' && (
                <Card>
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <CardTitle className="flex items-center gap-2">
                        📋 {language === 'pt-BR' ? 'Independent Contractor' : 'Independent Contractor'}
                      </CardTitle>
                      <div className="flex gap-2">
                        {selectedContractorFiles.length > 0 && (
                          <Button 
                            onClick={() => setShowBulkEmailModal(true)}
                            className="bg-green-600 hover:bg-green-700"
                            size="sm"
                          >
                            <Send className="h-4 w-4 mr-2" />
                            {language === 'pt-BR' ? `Enviar ${selectedContractorFiles.length}` : `Send ${selectedContractorFiles.length}`}
                          </Button>
                        )}
                        <Button 
                          onClick={() => setShowContractorForm(true)} 
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Plus className="h-4 w-4 mr-2" /> 
                          {language === 'pt-BR' ? 'Novo Contrato' : 'New Contract'}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Search and Filter Controls */}
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div>
                        <Input 
                          placeholder={language === 'pt-BR' ? 'Buscar por nome...' : 'Search by name...'}
                          value={contractorSearch}
                          onChange={(e) => setContractorSearch(e.target.value)}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <select 
                          className="w-full border rounded px-3 py-2 bg-white text-sm"
                          value={contractorFilterPM}
                          onChange={(e) => setContractorFilterPM(e.target.value)}
                        >
                          <option value="ALL">{language === 'pt-BR' ? 'Todas as equipes' : 'All teams'}</option>
                          {pmOptions.map((pm) => (
                            <option key={pm} value={pm}>{pm}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <select 
                          className="w-full border rounded px-3 py-2 bg-white text-sm"
                          value={contractorFilterStatus}
                          onChange={(e) => setContractorFilterStatus(e.target.value)}
                        >
                          <option value="ALL">{language === 'pt-BR' ? 'Todos os status' : 'All status'}</option>
                          <option value="SIGNED">{language === 'pt-BR' ? 'Assinados' : 'Signed'}</option>
                          <option value="UNSIGNED">{language === 'pt-BR' ? 'Não assinados' : 'Unsigned'}</option>
                        </select>
                      </div>
                      <div>
                        <select 
                          className="w-full border rounded px-3 py-2 bg-white text-sm"
                          value={contractorSort}
                          onChange={(e) => setContractorSort(e.target.value)}
                        >
                          <option value="date_desc">{language === 'pt-BR' ? 'Data (Recente)' : 'Date (Recent)'}</option>
                          <option value="date_asc">{language === 'pt-BR' ? 'Data (Antiga)' : 'Date (Old)'}</option>
                          <option value="name_asc">{language === 'pt-BR' ? 'Nome (A-Z)' : 'Name (A-Z)'}</option>
                          <option value="name_desc">{language === 'pt-BR' ? 'Nome (Z-A)' : 'Name (Z-A)'}</option>
                          <option value="pm_asc">PM (A-Z)</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Selection Controls */}
                    {contractorFiles.length > 0 && (
                      <div className="mt-3 flex items-center gap-4">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedContractorFiles.length === filteredContractorFiles.length && filteredContractorFiles.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedContractorFiles([...filteredContractorFiles]);
                              } else {
                                setSelectedContractorFiles([]);
                              }
                            }}
                            className="text-blue-600"
                          />
                          <span className="text-blue-600 font-medium">
                            {language === 'pt-BR' ? 'Selecionar Todos' : 'Select All'}
                          </span>
                        </label>
                        {selectedContractorFiles.length > 0 && (
                          <Badge variant="secondary" className="text-sm">
                            {selectedContractorFiles.length} {language === 'pt-BR' ? 'selecionados' : 'selected'}
                          </Badge>
                        )}
                      </div>
                    )}
                  </CardHeader>
                  
                  <CardContent>
                    {contractorLoading ? (
                      <div className="text-center py-8">
                        <div className="text-2xl mb-2">🔄</div>
                        <div>{language === 'pt-BR' ? 'Carregando contratos...' : 'Loading contracts...'}</div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {contractorFiles.length === 0 || filteredContractorFiles.length === 0 ? (
                          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                            <div className="text-gray-400 mb-4 text-4xl">📋</div>
                            <h3 className="font-medium text-gray-700 mb-2">
                              {language === 'pt-BR' ? 'Nenhum contrato encontrado' : 'No contracts found'}
                            </h3>
                            <p className="text-gray-500 text-sm mb-4">
                              {language === 'pt-BR' ? 
                                'Crie contratos digitais para contractors independentes' :
                                'Create digital contracts for independent contractors'
                              }
                            </p>
                            <Button 
                              onClick={() => setShowContractorForm(true)}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              {language === 'pt-BR' ? 'Criar Primeiro Contrato' : 'Create First Contract'}
                            </Button>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredContractorFiles.map((file) => (
                              <div key={file.id} className={`bg-white border rounded-lg p-4 hover:shadow-md transition-shadow ${
                                selectedContractorFiles.some(f => f.id === file.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                              }`}>
                                <div className="flex items-start gap-3">
                                  {/* Checkbox for Selection */}
                                  <input
                                    type="checkbox"
                                    checked={selectedContractorFiles.some(f => f.id === file.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedContractorFiles(prev => [...prev, file]);
                                      } else {
                                        setSelectedContractorFiles(prev => prev.filter(f => f.id !== file.id));
                                      }
                                    }}
                                    className="mt-1 text-blue-600 flex-shrink-0"
                                  />
                                  
                                  <div className="flex-1 min-w-0">
                                    {/* Header */}
                                    <div className="flex items-start justify-between mb-3">
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <div className="text-lg">📋</div>
                                        <div className="flex-1 min-w-0">
                                          <div className="font-semibold text-gray-800 truncate">{file.helper_name}</div>
                                          <div className="text-xs text-gray-500">
                                            {language === 'pt-BR' ? 'Criado por' : 'Created by'} {file.pm_user}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-xs text-gray-500">
                                          {new Date(file.date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}
                                        </div>
                                        <div className="flex gap-1 mt-1">
                                          <Badge variant="secondary" className="text-xs">PDF</Badge>
                                          <Badge className="bg-green-100 text-green-800 text-xs">
                                            ✅ {language === 'pt-BR' ? 'Pronto' : 'Ready'}
                                          </Badge>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Content Information */}
                                    <div className="space-y-2 text-sm mb-4">
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">{language === 'pt-BR' ? 'Data' : 'Date'}:</span> 
                                        <span className="font-medium">{new Date(file.date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">PM:</span> 
                                        <span className="font-medium">{file.pm_user}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">Status:</span> 
                                        <span className="font-medium text-green-600">
                                          ✅ {language === 'pt-BR' ? 'Pronto' : 'Ready'}
                                        </span>
                                      </div>
                                    </div>
                                    
                                    {/* Action Buttons */}
                                    <div className="grid grid-cols-2 gap-1">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => viewContractorFile(file.id, file.helper_name)}
                                        className="text-xs"
                                      >
                                        <Eye className="h-3 w-3 mr-1" />
                                        {t('view')}
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => downloadContractorFile(file.id)}
                                        className="text-xs"
                                      >
                                        <Download className="h-3 w-3 mr-1" />
                                        {t('download')}
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          setSelectedContractorFileId(file.id);
                                          setShowContractorEmailForm(true);
                                        }}
                                        className="text-xs bg-blue-50 hover:bg-blue-100"
                                      >
                                        <Send className="h-3 w-3 mr-1" />
                                        {language === 'pt-BR' ? 'Email' : 'Email'}
                                      </Button>
                                      {user?.role === 'admin' && (
                                        <>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                              setContractorFileToEdit({...file});
                                              setShowContractorEditModal(true);
                                            }}
                                            className="text-gray-600 hover:text-gray-700 text-xs"
                                            title={language === 'pt-BR' ? 'Editar nome' : 'Edit name'}
                                          >
                                            <Edit className="h-3 w-3 mr-1" />
                                            {language === 'pt-BR' ? 'Editar' : 'Edit'}
                                          </Button>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={async () => {
                                              if (window.confirm(language === 'pt-BR' ? 
                                                `Deletar contrato de ${file.helper_name}?` :
                                                `Delete contract for ${file.helper_name}?`
                                              )) {
                                                console.log('🗑️ Calling deleteContractorFile for:', file.id);
                                                deleteContractorFile(file.id, file.helper_name);
                                              }
                                            }}
                                            className="text-red-600 hover:text-red-700 text-xs col-span-2"
                                            title={language === 'pt-BR' ? 'Deletar contrato' : 'Delete contract'}
                                          >
                                            <Trash2 className="h-3 w-3 mr-1" />
                                            {language === 'pt-BR' ? 'Deletar Contrato' : 'Delete Contract'}
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Seguro (Helper Waiver) Sub-tab */}
              {arbitrationSubTab === 'seguro' && (
                <HelperWaiverPanel
                  user={user}
                  language={language}
                  onViewPdf={(url, title) => handlePDFAction(url, title, false)}
                  onDownloadPdf={(url, filename) => handlePDFAction(url, filename, true)}
                />
              )}

              {/* Installation Docs Sub-tab - PM Users (Read Only) */}
              {arbitrationSubTab === 'installation' && (
                <Card>
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <CardTitle className="flex items-center gap-2">
                        🔧 {language === 'pt-BR' ? 'Installation Docs' : 'Installation Docs'}
                      </CardTitle>
                    </div>
                    
                    {/* Debug Info */}
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                      <strong>Debug:</strong> User: {user?.username} | Role: {user?.role} | Folders: {installationFolders.length} | Files: {installationFiles.length} | Current Folder: {currentInstallationFolderId || 'Root'}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Breadcrumb Navigation */}
                      <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                        <button
                          onClick={() => {
                            console.log('🏠 Clicked root navigation');
                            setCurrentInstallationFolderId(null);
                            setInstallationFolderPath([]);
                          }}
                          className="hover:text-blue-600 font-medium"
                        >
                          🏠 {language === 'pt-BR' ? 'Raiz' : 'Root'}
                        </button>
                        {installationFolderPath.map((folder, index) => (
                          <React.Fragment key={folder.id}>
                            <span>→</span>
                            <button
                              onClick={() => {
                                console.log('📁 Clicked breadcrumb for:', folder.name);
                                setCurrentInstallationFolderId(folder.id);
                                setInstallationFolderPath(prev => prev.slice(0, index + 1));
                              }}
                              className="hover:text-blue-600 font-medium"
                            >
                              {folder.name}
                            </button>
                          </React.Fragment>
                        ))}
                        {currentInstallationFolderId && (
                          <>
                            <span>→</span>
                            <span className="font-medium text-blue-700">
                              {installationFolders.find(f => f.id === currentInstallationFolderId)?.name || 'Pasta'}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Folders Section - PM Read Only */}
                      {installationFolders.filter(f => 
                        currentInstallationFolderId === null ? 
                          (f.parent_folder_id === null || f.parent_folder_id === undefined) : 
                          f.parent_folder_id === currentInstallationFolderId
                      ).length > 0 && (
                        <div>
                          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                            📁 {language === 'pt-BR' ? 'Pastas' : 'Folders'}
                          </h3>
                          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
                            {installationFolders
                              .filter(f => 
                                currentInstallationFolderId === null ? 
                                  (f.parent_folder_id === null || f.parent_folder_id === undefined) : 
                                  f.parent_folder_id === currentInstallationFolderId
                              )
                              .map((folder) => (
                              <div 
                                key={folder.id} 
                                className="bg-blue-50 border border-blue-200 rounded-lg p-3 hover:bg-blue-100 cursor-pointer transition-colors"
                                onClick={() => {
                                  console.log('📁 Folder clicked:', folder.name);
                                  navigateToInstallationFolder(folder.id);
                                }}
                              >
                                <div className="text-center">
                                  <div className="text-2xl mb-2">📁</div>
                                  <div className="text-sm font-medium text-gray-800 truncate">
                                    {folder.name}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {getInstallationFileCount(folder.id)} {language === 'pt-BR' ? 'arquivos' : 'files'}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Files Section - PM Read Only */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                            📄 {language === 'pt-BR' ? 'Arquivos de Instalação' : 'Installation Files'}
                            {currentInstallationFolderId && (
                              <Button
                                onClick={() => {
                                  console.log('⬅️ Back button clicked');
                                  navigateBackInstallation();
                                }}
                                variant="outline"
                                size="sm"
                                className="text-xs"
                              >
                                ← {language === 'pt-BR' ? 'Voltar' : 'Back'}
                              </Button>
                            )}
                          </h3>
                          
                          {/* Selection Controls for Download */}
                          {installationFiles.filter(f => 
                            currentInstallationFolderId === null ? 
                              (f.folder_id === null || f.folder_id === undefined) : 
                              f.folder_id === currentInstallationFolderId
                          ).length > 0 && (
                            <div className="flex items-center gap-4">
                              <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedInstallationFiles.length === installationFiles.filter(f => 
                                    currentInstallationFolderId === null ? 
                                      (f.folder_id === null || f.folder_id === undefined) : 
                                      f.folder_id === currentInstallationFolderId
                                  ).length && installationFiles.filter(f => 
                                    currentInstallationFolderId === null ? 
                                      (f.folder_id === null || f.folder_id === undefined) : 
                                      f.folder_id === currentInstallationFolderId
                                  ).length > 0}
                                  onChange={(e) => {
                                    const currentFolderFiles = installationFiles.filter(f => 
                                      currentInstallationFolderId === null ? 
                                        (f.folder_id === null || f.folder_id === undefined) : 
                                        f.folder_id === currentInstallationFolderId
                                    );
                                    if (e.target.checked) {
                                      setSelectedInstallationFiles([...currentFolderFiles]);
                                    } else {
                                      setSelectedInstallationFiles([]);
                                    }
                                  }}
                                  className="text-blue-600"
                                />
                                <span className="text-blue-600 font-medium">
                                  {language === 'pt-BR' ? 'Selecionar Todos' : 'Select All'}
                                </span>
                              </label>
                              
                              {/* Download Selected */}
                              {selectedInstallationFiles.length > 0 && (
                                <Button
                                  onClick={async () => {
                                    for (const file of selectedInstallationFiles) {
                                      const link = document.createElement('a');
                                      link.href = `${API}/installation-docs/${file.id}/download`;
                                      link.download = file.original_filename;
                                      link.click();
                                    }
                                    toast.success(language === 'pt-BR' ? 
                                      `Download de ${selectedInstallationFiles.length} arquivo(s) iniciado!` :
                                      `Download of ${selectedInstallationFiles.length} file(s) started!`
                                    );
                                    setSelectedInstallationFiles([]);
                                  }}
                                  variant="outline"
                                  size="sm"
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  {language === 'pt-BR' ? 'Baixar' : 'Download'} ({selectedInstallationFiles.length})
                                </Button>
                              )}
                            </div>
                          )}
                        </div>

                        {installationFiles.filter(f => 
                          currentInstallationFolderId === null ? 
                            (f.folder_id === null || f.folder_id === undefined) : 
                            f.folder_id === currentInstallationFolderId
                        ).length === 0 ? (
                          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                            <div className="text-gray-400 mb-4 text-4xl">🔧</div>
                            <h3 className="font-medium text-gray-700 mb-2">
                              {currentInstallationFolderId ? 
                                (language === 'pt-BR' ? 'Pasta vazia' : 'Empty folder') :
                                (language === 'pt-BR' ? 'Nenhum arquivo de instalação' : 'No installation files')
                              }
                            </h3>
                            <p className="text-gray-500 text-sm">
                              {language === 'pt-BR' ? 'O Admin ainda não adicionou materiais nesta pasta' : 'Admin has not added materials to this folder yet'}
                            </p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            {installationFiles
                              .filter(f => 
                                currentInstallationFolderId === null ? 
                                  (f.folder_id === null || f.folder_id === undefined) : 
                                  f.folder_id === currentInstallationFolderId
                              )
                              .map((file) => (
                              <div key={file.id} className={`bg-white border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer relative ${
                                selectedInstallationFiles.some(f => f.id === file.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                              }`}>
                                
                                {/* Checkbox */}
                                <div className="absolute top-2 left-2 z-10">
                                  <input
                                    type="checkbox"
                                    checked={selectedInstallationFiles.some(f => f.id === file.id)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      if (e.target.checked) {
                                        setSelectedInstallationFiles(prev => [...prev, file]);
                                      } else {
                                        setSelectedInstallationFiles(prev => prev.filter(f => f.id !== file.id));
                                      }
                                    }}
                                    className="text-blue-600 bg-white rounded border-2 shadow-sm"
                                  />
                                </div>

                                {/* Thumbnail */}
                                <div 
                                  className="h-32 bg-gray-100 flex items-center justify-center relative cursor-pointer hover:bg-gray-200 transition-colors"
                                  onClick={async () => {
                                    // Para PDFs, abrir em nova aba com autenticação
                                    if (file.file_type === 'pdf') {
                                      try {
                                        setInstallationPdfLoading(file.id); // Set loading for this specific file
                                        
                                        const token = sessionStorage.getItem('access_token');
                                        if (!token) {
                                          toast.error(language === 'pt-BR' ? 'Token não encontrado' : 'Token not found');
                                          setInstallationPdfLoading(null);
                                          return;
                                        }
                                        
                                        // Fetch o PDF com autenticação e criar blob URL
                                        const response = await fetch(`${API}/installation-docs/${file.id}/download`, {
                                          headers: {
                                            'Authorization': `Bearer ${token}`
                                          }
                                        });
                                        
                                        if (!response.ok) {
                                          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                                        }
                                        
                                        const blob = await response.blob();
                                        const pdfUrl = URL.createObjectURL(blob);
                                        
                                        // Abrir em modal
                                        handlePDFAction(pdfUrl, file.file_name || file.original_filename || 'document.pdf', false);
                                        toast.success(language === 'pt-BR' ? 'PDF aberto em modal' : 'PDF opened in modal');
                                        
                                      } catch (e) {
                                        console.error('Failed to open PDF:', e);
                                        toast.error(language === 'pt-BR' ? 'Erro ao abrir PDF' : 'Error opening PDF');
                                      } finally {
                                        setInstallationPdfLoading(null); // Clear loading state
                                      }
                                    } else {
                                      // Para outros tipos (vídeo, imagem), usar o modal
                                      setCurrentInstallationFile(file);
                                      setShowInstallationViewer(true);
                                    }
                                  }}
                                >
                                  {file.file_type === 'video' ? (
                                    <div className="text-center">
                                      <div className="text-3xl text-blue-500 mb-2">🎥</div>
                                      <div className="text-xs text-gray-600">VIDEO</div>
                                    </div>
                                  ) : file.file_type === 'image' ? (
                                    <img
                                      src={`${API}/installation-docs/${file.id}/thumbnail`}
                                      alt={file.original_filename}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                  ) : file.file_type === 'pdf' ? (
                                    <div className="text-center relative">
                                      {installationPdfLoading === file.id ? (
                                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
                                          <div className="text-white text-center">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                                            <div className="text-sm">Carregando...</div>
                                          </div>
                                        </div>
                                      ) : null}
                                      <div className="text-3xl text-red-500 mb-2">📄</div>
                                      <div className="text-xs text-gray-600 mb-2">PDF</div>
                                      <button className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded transition-colors">
                                        {language === 'pt-BR' ? 'Abrir PDF' : 'Open PDF'}
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="text-center">
                                      <div className="text-3xl text-gray-500 mb-2">📄</div>
                                      <div className="text-xs text-gray-600">{file.file_type?.toUpperCase()}</div>
                                    </div>
                                  )}
                                  
                                  {/* Fallback for image errors */}
                                  <div className="hidden w-full h-full bg-gray-200 items-center justify-center">
                                    <div className="text-center">
                                      <div className="text-2xl text-gray-400 mb-1">📄</div>
                                      <div className="text-xs text-gray-500">{file.file_type?.toUpperCase()}</div>
                                    </div>
                                  </div>
                                </div>

                                {/* File Info */}
                                <div className="p-3">
                                  <div className="font-medium text-sm text-gray-800 truncate mb-1">
                                    {file.original_filename}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {(file.file_size / 1024).toFixed(1)} KB
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {new Date(file.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}
                                  </div>
                                  {file.uploaded_by && (
                                    <div className="text-xs text-blue-600">
                                      {language === 'pt-BR' ? 'Por' : 'By'} {file.uploaded_by}
                                    </div>
                                  )}
                                </div>

                                {/* Quick Actions - Download Only for PM */}
                                <div className="px-3 pb-3">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const link = document.createElement('a');
                                      link.href = `${API}/installation-docs/${file.id}/download`;
                                      link.download = file.original_filename;
                                      link.click();
                                      toast.success(language === 'pt-BR' ? 'Download iniciado' : 'Download started');
                                    }}
                                    className="w-full text-xs"
                                    title={language === 'pt-BR' ? 'Download' : 'Download'}
                                  >
                                    <Download className="h-3 w-3 mr-1" />
                                    {language === 'pt-BR' ? 'Baixar' : 'Download'}
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Helpers Sub-tab - PM Manage Team Workers */}
              {arbitrationSubTab === 'helpers' && (
                <Card>
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <CardTitle className="flex items-center gap-2">
                        👷 {language === 'pt-BR' ? 'Gerenciar Funcionários' : 'Manage Helpers'}
                      </CardTitle>
                      <Button 
                        onClick={() => setShowHelperForm(true)} 
                        className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                        size="sm"
                      >
                        <Plus className="h-4 w-4 mr-2" /> 
                        {language === 'pt-BR' ? 'Adicionar Helper' : 'Add Helper'}
                      </Button>
                    </div>
                    
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
                      <p className="text-sm text-green-800">
                        <strong>👷 {language === 'pt-BR' ? 'Gerencie sua equipe' : 'Manage your team'}</strong>
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        {language === 'pt-BR' 
                          ? 'Adicione, edite ou remova funcionários da sua equipe. Estes dados são compartilhados com o administrador.' 
                          : 'Add, edit or remove workers from your team. This data is shared with the administrator.'}
                      </p>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    {/* Formulário Add/Edit Helper */}
                    {showHelperForm && (
                      <div className="bg-white border border-green-300 rounded-lg p-4 mb-4">
                        <h5 className="font-medium mb-3 text-sm">
                          {editingHelper ? '✏️ Editar Funcionário' : '➕ Novo Funcionário'}
                        </h5>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="sm:col-span-2">
                            <Label className="text-sm">Nome Completo *</Label>
                            <Input
                              value={helperFormData.full_name}
                              onChange={(e) => setHelperFormData({...helperFormData, full_name: e.target.value})}
                              placeholder="Nome completo do funcionário"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-sm">Telefone</Label>
                            <Input
                              value={helperFormData.phone}
                              onChange={(e) => setHelperFormData({...helperFormData, phone: e.target.value})}
                              onBlur={(e) => {
                                const formatted = formatPhoneNumber(e.target.value);
                                setHelperFormData({...helperFormData, phone: formatted});
                              }}
                              placeholder="+1(555)123-4567"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-sm">Endereço</Label>
                            <Input
                              value={helperFormData.address}
                              onChange={(e) => setHelperFormData({...helperFormData, address: e.target.value})}
                              placeholder="Endereço"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-sm">Data de Contratação</Label>
                            <Input
                              type="date"
                              value={helperFormData.hire_date}
                              onChange={(e) => setHelperFormData({...helperFormData, hire_date: e.target.value})}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-sm">SSN / Tax ID</Label>
                            <Input
                              value={helperFormData.ssn_tax_id}
                              onChange={(e) => setHelperFormData({...helperFormData, ssn_tax_id: e.target.value})}
                              placeholder="XXX-XX-XXXX"
                              className="text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="pm-helper-speaks-english"
                              checked={helperFormData.speaks_english}
                              onChange={(e) => {
                                const speaksEnglish = e.target.checked;
                                setHelperFormData({
                                  ...helperFormData, 
                                  speaks_english: speaksEnglish,
                                  is_team_translator: speaksEnglish ? helperFormData.is_team_translator : false
                                });
                              }}
                              className="rounded"
                            />
                            <Label htmlFor="pm-helper-speaks-english" className="text-sm cursor-pointer">
                              ☑️ Fala Inglês?
                            </Label>
                          </div>
                          {helperFormData.speaks_english && (
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="pm-helper-team-translator"
                                checked={helperFormData.is_team_translator}
                                onChange={(e) => setHelperFormData({...helperFormData, is_team_translator: e.target.checked})}
                                className="rounded"
                              />
                              <Label htmlFor="pm-helper-team-translator" className="text-sm cursor-pointer">
                                🗣️ Tradutor da equipe?
                              </Label>
                            </div>
                          )}
                        </div>
                        <div className="flex justify-end gap-2 mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={cancelHelperForm}
                            disabled={helpersLoading}
                          >
                            Cancelar
                          </Button>
                          <Button
                            size="sm"
                            onClick={saveMyHelper}
                            disabled={helpersLoading}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {helpersLoading ? '⏳ Salvando...' : (editingHelper ? '💾 Atualizar' : '➕ Adicionar')}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Lista de Helpers */}
                    {helpersLoading ? (
                      <div className="text-center py-8">
                        <div className="text-2xl mb-2">🔄</div>
                        <div>Carregando funcionários...</div>
                      </div>
                    ) : helpers.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
                        <div className="text-4xl mb-3">👷</div>
                        <p className="text-gray-600">Nenhum funcionário cadastrado</p>
                        <p className="text-sm text-gray-500 mt-1">Clique em "Adicionar Helper" para começar</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {helpers.map((helper) => (
                          <div key={helper.id} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="font-medium text-base mb-1">{helper.full_name}</div>
                                {helper.phone && <div className="text-sm text-gray-600">📞 {helper.phone}</div>}
                                {helper.address && <div className="text-sm text-gray-600">📍 {helper.address}</div>}
                                {helper.hire_date && <div className="text-sm text-gray-600">📅 Contratado: {helper.hire_date}</div>}
                                {helper.ssn_tax_id && <div className="text-sm text-gray-600">🆔 SSN: {helper.ssn_tax_id}</div>}
                                <div className="flex gap-2 mt-2">
                                  {helper.speaks_english && (
                                    <Badge variant="secondary" className="text-xs">🇺🇸 Fala Inglês</Badge>
                                  )}
                                  {helper.is_team_translator && (
                                    <Badge variant="default" className="text-xs">🗣️ Tradutor</Badge>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col gap-2 ml-3">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => startEditHelper(helper)}
                                  disabled={helpersLoading}
                                  className="h-8 px-3"
                                >
                                  <Edit className="h-3 w-3 mr-1" />
                                  Editar
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => deleteMyHelper(helper.id, helper.full_name)}
                                  disabled={helpersLoading}
                                  className="h-8 px-3 text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Remover
                                </Button>
                              </div>
                            </div>

                            {/* Documentos do Helper - Arbitration e Contractor */}
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <div className="text-xs font-semibold text-gray-700 mb-2">📄 Documentos:</div>
                              <div className="grid grid-cols-2 gap-2">
                                {/* Arbitration Agreement */}
                                <div>
                                  {helper.arbitration_agreement_id ? (
                                    <div className="border-2 border-orange-300 rounded bg-orange-50">
                                      <div 
                                        className="p-2 cursor-pointer hover:bg-orange-100 transition"
                                        onClick={async () => {
                                          // Ver Arbitration PDF vinculado
                                          try {
                                            const response = await axios.get(
                                              `${API}/arbitration-files/${helper.arbitration_agreement_id}/download`,
                                              { responseType: 'blob' }
                                            );
                                            
                                            const blobUrl = window.URL.createObjectURL(response.data);
                                            openPdfWithLoading({
                                              url: blobUrl,
                                              title: `Arbitration - ${helper.full_name}`
                                            });
                                            setShowPDFModal(true);
                                          } catch (e) {
                                            console.error('Erro ao carregar Arbitration:', e);
                                            toast.error('Erro ao carregar PDF');
                                          }
                                        }}
                                      >
                                        <div className="text-center">
                                          <div className="text-orange-600 text-lg">⚖️</div>
                                          <div className="text-xs text-orange-700 font-medium">Arbitration</div>
                                          <div className="text-xs text-orange-600 mt-1">✓ Vinculado</div>
                                        </div>
                                      </div>
                                      <button
                                        className="w-full text-xs text-red-600 hover:bg-red-50 py-1 border-t border-orange-200"
                                        onClick={async () => {
                                          if (window.confirm('Desvincular Arbitration deste helper?')) {
                                            try {
                                              await axios.put(
                                                `${API}/my-helpers/${helper.id}`,
                                                { arbitration_agreement_id: null }
                                              );
                                              toast.success('Arbitration desvinculado!');
                                              await loadMyHelpers();
                                            } catch (e) {
                                              toast.error('Erro ao desvincular');
                                            }
                                          }
                                        }}
                                      >
                                        ✕ Remover
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex gap-1">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1 h-auto py-2 border-dashed border-orange-300 hover:bg-orange-50"
                                        onClick={() => {
                                          // Criar novo Arbitration para este helper
                                          setHelperCreatingDocFor({
                                            helper_id: helper.id,
                                            helper_name: helper.full_name,
                                            doc_type: 'arbitration'
                                          });
                                          setArbitrationFormData({
                                            helper_name: helper.full_name,
                                            signature: '',
                                            date: new Date().toISOString().split('T')[0]
                                          });
                                          setHasSignature(false);
                                          setShowArbitrationForm(true);
                                          setActiveTab('arbitration');
                                          setArbitrationSubTab('files');
                                        }}
                                      >
                                        <div className="text-center">
                                          <div className="text-orange-400 text-lg">⚖️</div>
                                          <div className="text-xs">New Arbitration</div>
                                        </div>
                                      </Button>
                                      
                                      {/* Ícone de Lupa - Buscar Arbitration Existente */}
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="px-2 border-orange-300 hover:bg-orange-50"
                                        onClick={() => openLinkArbitrationModal(helper)}
                                        title="Buscar Arbitration existente"
                                      >
                                        <Search className="w-3 h-3 text-orange-600" />
                                      </Button>
                                    </div>
                                  )}
                                </div>

                                {/* Contractor Agreement */}
                                <div>
                                  {helper.contractor_agreement_id ? (
                                    <div className="border-2 border-teal-300 rounded bg-teal-50">
                                      <div 
                                        className="p-2 cursor-pointer hover:bg-teal-100 transition"
                                        onClick={async () => {
                                          // Ver Contractor PDF vinculado
                                          try {
                                            const response = await axios.get(
                                              `${API}/contractor-files/${helper.contractor_agreement_id}/download`,
                                              { responseType: 'blob' }
                                            );
                                            
                                            const blobUrl = window.URL.createObjectURL(response.data);
                                            openPdfWithLoading({
                                              url: blobUrl,
                                              title: `Contractor - ${helper.full_name}`
                                            });
                                            setShowPDFModal(true);
                                          } catch (e) {
                                            console.error('Erro ao carregar Contractor:', e);
                                            toast.error('Erro ao carregar PDF');
                                          }
                                        }}
                                      >
                                        <div className="text-center">
                                          <div className="text-teal-600 text-lg">📝</div>
                                          <div className="text-xs text-teal-700 font-medium">Contractor</div>
                                          <div className="text-xs text-teal-600 mt-1">✓ Vinculado</div>
                                        </div>
                                      </div>
                                      <button
                                        className="w-full text-xs text-red-600 hover:bg-red-50 py-1 border-t border-teal-200"
                                        onClick={async () => {
                                          if (window.confirm('Desvincular Contractor deste helper?')) {
                                            try {
                                              await axios.put(
                                                `${API}/my-helpers/${helper.id}`,
                                                { contractor_agreement_id: null }
                                              );
                                              toast.success('Contractor desvinculado!');
                                              await loadMyHelpers();
                                            } catch (e) {
                                              toast.error('Erro ao desvincular');
                                            }
                                          }
                                        }}
                                      >
                                        ✕ Remover
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex gap-1">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1 h-auto py-2 border-dashed border-teal-300 hover:bg-teal-50"
                                        onClick={() => {
                                          // Criar novo Contractor para este helper
                                          setHelperCreatingDocFor({
                                            helper_id: helper.id,
                                            helper_name: helper.full_name,
                                            doc_type: 'contractor'
                                          });
                                          setContractorFormData({
                                            helper_name: helper.full_name,
                                            title: 'Helper',
                                            signature: '',
                                            date: new Date().toISOString().split('T')[0]
                                          });
                                          setHasSignature(false);
                                          setShowContractorForm(true);
                                          setActiveTab('arbitration');
                                          setArbitrationSubTab('contractor');
                                        }}
                                      >
                                        <div className="text-center">
                                          <div className="text-teal-400 text-lg">📝</div>
                                          <div className="text-xs">New Contractor</div>
                                        </div>
                                      </Button>
                                      
                                      {/* Ícone de Lupa - Buscar Contractor Existente */}
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="px-2 border-teal-300 hover:bg-teal-50"
                                        onClick={() => openLinkContractorModal(helper)}
                                        title="Buscar Contractor existente"
                                      >
                                        <Search className="w-3 h-3 text-teal-600" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* LSWP Formstack Sub-tab - PM and ADMIN */}
              {arbitrationSubTab === 'lswp' && (
                <Card>
                  <CardHeader>
                    <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4">
                      <CardTitle className="text-xl font-bold text-blue-800 flex items-center gap-2">
                        📋 LSWP Formstack Integration
                      </CardTitle>
                      <p className="text-sm text-gray-600 mt-2">
                        Preencha os dados do trabalho e envie para o formulário LSWP do Formstack
                      </p>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-6">
                    {/* Formulário LSWP */}
                    <div className="bg-white border rounded-lg p-6">
                      <h3 className="font-semibold text-lg mb-4 text-blue-800">📝 Informações do Trabalho</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Customer Name */}
                        <div>
                          <Label className="text-sm font-medium">Nome do Cliente *</Label>
                          <Input
                            placeholder="Primeiro Nome"
                            value={lswpFormData.customer_first_name}
                            onChange={(e) => setLswpFormData({...lswpFormData, customer_first_name: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Sobrenome do Cliente *</Label>
                          <Input
                            placeholder="Sobrenome"
                            value={lswpFormData.customer_last_name}
                            onChange={(e) => setLswpFormData({...lswpFormData, customer_last_name: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        
                        {/* Customer Email */}
                        <div className="md:col-span-2">
                          <Label className="text-sm font-medium">Email do Cliente *</Label>
                          <Input
                            type="email"
                            placeholder="cliente@example.com"
                            value={lswpFormData.customer_email}
                            onChange={(e) => setLswpFormData({...lswpFormData, customer_email: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        
                        {/* Job Address */}
                        <div className="md:col-span-2">
                          <Label className="text-sm font-medium">Endereço do Trabalho *</Label>
                          <Input
                            placeholder="Rua, número, complemento"
                            value={lswpFormData.job_address_line1}
                            onChange={(e) => setLswpFormData({...lswpFormData, job_address_line1: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium">Cidade *</Label>
                          <Input
                            placeholder="Cidade"
                            value={lswpFormData.job_city}
                            onChange={(e) => setLswpFormData({...lswpFormData, job_city: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium">Estado *</Label>
                          <Input
                            placeholder="GA"
                            value={lswpFormData.job_state}
                            onChange={(e) => setLswpFormData({...lswpFormData, job_state: e.target.value})}
                            className="mt-1"
                            maxLength={2}
                          />
                        </div>
                        
                        <div className="md:col-span-2">
                          <Label className="text-sm font-medium">CEP/Zip Code *</Label>
                          <Input
                            placeholder="30308"
                            value={lswpFormData.job_zip}
                            onChange={(e) => setLswpFormData({...lswpFormData, job_zip: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        
                        {/* Quantities */}
                        <div>
                          <Label className="text-sm font-medium">Quantidade de Janelas</Label>
                          <Input
                            type="number"
                            min="0"
                            value={lswpFormData.num_windows}
                            onChange={(e) => setLswpFormData({...lswpFormData, num_windows: parseInt(e.target.value) || 0})}
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium">Quantidade de Portas</Label>
                          <Input
                            type="number"
                            min="0"
                            value={lswpFormData.num_doors}
                            onChange={(e) => setLswpFormData({...lswpFormData, num_doors: parseInt(e.target.value) || 0})}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      
                      {/* Cálculos Automáticos */}
                      {(lswpFormData.num_windows > 0 || lswpFormData.num_doors > 0) && (
                        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-800 mb-2">🧮 Valores Calculados Automaticamente:</h4>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-gray-600">Total Wet Cloths:</span>
                              <span className="font-bold ml-2">{(lswpFormData.num_windows + lswpFormData.num_doors) * 2}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Total Dry Cloths:</span>
                              <span className="font-bold ml-2">{(lswpFormData.num_windows + lswpFormData.num_doors) * 2}</span>
                            </div>
                            <div className="col-span-2">
                              <span className="text-gray-600">Tipo de Trabalho:</span>
                              <span className="font-bold ml-2">
                                {lswpFormData.num_windows > 0 && lswpFormData.num_doors > 0 ? 'Window (ambos marcados = só Window)' :
                                 lswpFormData.num_windows > 0 ? 'Window' :
                                 lswpFormData.num_doors > 0 ? 'Door' : 'Nenhum'}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Assinaturas */}
                      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Renovator Signature */}
                        <div>
                          <Label className="text-sm font-medium mb-2 block">Assinatura do Renovator *</Label>
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-2">
                            <SignatureCanvas
                              ref={renovatorSigPadRef}
                              canvasProps={{
                                className: 'w-full h-32 bg-white rounded',
                                style: { touchAction: 'none' }
                              }}
                              onEnd={() => setHasRenovatorSig(true)}
                            />
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              renovatorSigPadRef.current?.clear();
                              setHasRenovatorSig(false);
                            }}
                            className="mt-2 w-full"
                          >
                            🗑️ Limpar Assinatura
                          </Button>
                        </div>
                        
                        {/* Customer Signature */}
                        <div>
                          <Label className="text-sm font-medium mb-2 block">Assinatura do Cliente *</Label>
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-2">
                            <SignatureCanvas
                              ref={customerSigPadRef}
                              canvasProps={{
                                className: 'w-full h-32 bg-white rounded',
                                style: { touchAction: 'none' }
                              }}
                              onEnd={() => setHasCustomerSig(true)}
                            />
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              customerSigPadRef.current?.clear();
                              setHasCustomerSig(false);
                            }}
                            className="mt-2 w-full"
                          >
                            🗑️ Limpar Assinatura
                          </Button>
                        </div>
                      </div>
                      
                      {/* Botões de Ação */}
                      <div className="mt-6 flex gap-3">
                        <Button
                          onClick={() => setShowLSWPPreview(true)}
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                          disabled={
                            !lswpFormData.customer_first_name.trim() ||
                            !lswpFormData.customer_email.trim() ||
                            !lswpFormData.job_address_line1.trim() ||
                            !hasRenovatorSig ||
                            !hasCustomerSig
                          }
                        >
                          👁️ Preview e Enviar
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            if (window.confirm('Limpar todos os dados do formulário?')) {
                              setLswpFormData({
                                customer_first_name: '',
                                customer_last_name: '',
                                customer_email: '',
                                job_address_line1: '',
                                job_city: '',
                                job_state: '',
                                job_zip: '',
                                num_windows: 0,
                                num_doors: 0,
                                signature_renovator: '',
                                signature_customer: ''
                              });
                              renovatorSigPadRef.current?.clear();
                              customerSigPadRef.current?.clear();
                              setHasRenovatorSig(false);
                              setHasCustomerSig(false);
                            }
                          }}
                        >
                          🗑️ Limpar
                        </Button>
                      </div>
                    </div>
                    
                    {/* Instruções */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h4 className="font-semibold text-yellow-800 mb-2">ℹ️ Campos Preenchidos Automaticamente:</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-yellow-700">
                        <li>Data atual</li>
                        <li>Checkboxes de Window/Door (baseado nas quantidades)</li>
                        <li>Post Warnings Signs, Maintenance Containment, Post-Renovation Cleaning</li>
                        <li>Todos os campos "Applicable to All/Interior/Exterior Jobs"</li>
                        <li>Visual Inspection PASSED</li>
                        <li>Wet Cloth Tests PASSED</li>
                        <li>Total de wet/dry cloths (calculado automaticamente)</li>
                        <li>Nome do Renovator e Firma (baseado no PM logado: {user?.username})</li>
                        <li>Email do Renovator: pmhomeremodeling@gmail.com</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Manual de Instruções Sub-tab - Para PMs */}
              {arbitrationSubTab === 'manual' && (
                <InstructionManual 
                  userRole={user?.role}
                  language={language}
                  onExportPDF={exportManualToPDF}
                />
              )}


            </TabsContent>
          )}

          {/* Docs Tab - Admin Only */}
          {user?.role === 'admin' && (
            <TabsContent value="arbitration" className="mt-6">
              {/* Arbitration Files Sub-tab - Admin */}
              {arbitrationSubTab === 'files' && (
                <Card>
                  <CardHeader>
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-3 sm:p-4">
                      {/* Header - Responsivo para iPhone */}
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-base sm:text-lg text-purple-800">
                            ⚖️ {t('arbitrationFiles')} - {language === 'pt-BR' ? 'Visão Geral' : 'Overview'}
                          </h3>
                          <p className="text-purple-700 text-xs sm:text-sm mt-1">
                            {language === 'pt-BR' ? 
                              'Todos os arquivos de arbitragem criados pelos Team Leaders para seus Helpers.' :
                              'All arbitration files created by Team Leaders for their Helpers.'
                            }
                          </p>
                        </div>
                        
                        {/* Botões de Ação - Layout móvel otimizado */}
                        <div className="flex flex-wrap gap-2">
                          {selectedArbitrationFiles.length > 0 && (
                            <Button 
                              onClick={() => setShowBulkEmailModal(true)}
                              className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none"
                              size="sm"
                            >
                              <Send className="h-4 w-4 mr-1 sm:mr-2" />
                              <span className="text-xs sm:text-sm">
                                {language === 'pt-BR' ? `Enviar ${selectedArbitrationFiles.length}` : `Send ${selectedArbitrationFiles.length}`}
                              </span>
                            </Button>
                          )}
                          <Button 
                            onClick={() => setShowArbitrationForm(true)} 
                            className="bg-blue-600 hover:bg-blue-700 flex-1 sm:flex-none"
                            size="sm"
                          >
                            <Plus className="h-4 w-4 mr-1 sm:mr-2" /> 
                            <span className="text-xs sm:text-sm">
                              {language === 'pt-BR' ? 'Novo' : 'New'}
                            </span>
                          </Button>
                          <Button
                            onClick={() => setShowArbitrationImportModal(true)}
                            className="bg-purple-600 hover:bg-purple-700 flex-1 sm:flex-none"
                            size="sm"
                          >
                            <Upload className="h-4 w-4 mr-1 sm:mr-2" />
                            <span className="text-xs sm:text-sm">
                              {language === 'pt-BR' ? 'Importar' : 'Import'}
                            </span>
                          </Button>
                          <Button
                            onClick={() => loadArbitrationFiles()}
                            variant="outline"
                            size="sm"
                            disabled={arbitrationLoading}
                            className="bg-white"
                          >
                            <span className="text-xs sm:text-sm">
                              {arbitrationLoading ? `🔄 ${t('loading')}` : `🔄 ${t('update')}`}
                            </span>
                          </Button>
                        </div>
                      </div>

                      {/* Admin Search and Filter Controls - Mobile Optimized */}
                      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 mt-3 sm:mt-4">
                        <h4 className="font-semibold text-gray-800 mb-3 text-sm sm:text-base">
                          🔍 {language === 'pt-BR' ? 'Buscar e Filtrar' : 'Search & Filter'}
                        </h4>
                        
                        {/* Grid Responsivo - 1 coluna no iPhone */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
                          <Input 
                            placeholder={language === 'pt-BR' ? 'Buscar por nome...' : 'Search by name...'}
                            value={arbitrationSearch}
                            onChange={(e) => setArbitrationSearch(e.target.value)}
                            className="text-sm h-10 sm:h-9"
                          />
                          <select 
                            className="w-full border rounded px-3 py-2 bg-white text-sm h-10 sm:h-9"
                            value={arbitrationFilterPM}
                            onChange={(e) => setArbitrationFilterPM(e.target.value)}
                          >
                            <option value="ALL">{language === 'pt-BR' ? 'Todas as equipes' : 'All teams'}</option>
                            {pmOptions.map((pm) => (
                              <option key={pm} value={pm}>{pm}</option>
                            ))}
                          </select>
                          <select 
                            className="w-full border rounded px-3 py-2 bg-white text-sm h-10 sm:h-9"
                            value={arbitrationFilterStatus}
                            onChange={(e) => setArbitrationFilterStatus(e.target.value)}
                          >
                            <option value="ALL">{language === 'pt-BR' ? 'Todos os status' : 'All status'}</option>
                            <option value="SIGNED">{language === 'pt-BR' ? 'Assinados' : 'Signed'}</option>
                            <option value="UNSIGNED">{language === 'pt-BR' ? 'Não assinados' : 'Unsigned'}</option>
                          </select>
                          <select 
                            className="w-full border rounded px-3 py-2 bg-white text-sm h-10 sm:h-9"
                            value={arbitrationSort}
                            onChange={(e) => setArbitrationSort(e.target.value)}
                          >
                            <option value="date_desc">{language === 'pt-BR' ? 'Data (Recente)' : 'Date (Recent)'}</option>
                            <option value="date_asc">{language === 'pt-BR' ? 'Data (Antiga)' : 'Date (Old)'}</option>
                            <option value="name_asc">{language === 'pt-BR' ? 'Nome (A-Z)' : 'Name (A-Z)'}</option>
                            <option value="name_desc">{language === 'pt-BR' ? 'Nome (Z-A)' : 'Name (Z-A)'}</option>
                            <option value="pm_asc">PM (A-Z)</option>
                          </select>
                        </div>
                        
                        {/* Selection Controls - Admin */}
                        {arbitrationFiles.length > 0 && (
                          <div className="mt-3 flex items-center gap-4">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedArbitrationFiles.length === arbitrationFiles.length && arbitrationFiles.length > 0}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedArbitrationFiles([...arbitrationFiles]);
                                  } else {
                                    setSelectedArbitrationFiles([]);
                                  }
                                }}
                                className="text-blue-600"
                              />
                              <span className="text-blue-600 font-medium">
                                {language === 'pt-BR' ? 'Selecionar Todos' : 'Select All'}
                              </span>
                            </label>
                            {selectedArbitrationFiles.length > 0 && (
                              <Badge variant="secondary" className="text-sm">
                                {selectedArbitrationFiles.length} {language === 'pt-BR' ? 'selecionados' : 'selected'}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Admin Arbitration Files Grid - Same structure as PM version */}
                    <div className="p-4">
                      {arbitrationLoading ? (
                        <div className="text-center py-8">
                          <div className="text-2xl mb-2">🔄</div>
                          <div>{language === 'pt-BR' ? 'Carregando arquivos...' : 'Loading files...'}</div>
                        </div>
                      ) : (
                        arbitrationFiles.length === 0 ? (
                          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                            <div className="text-gray-400 mb-4 text-4xl">⚖️</div>
                            <h3 className="font-medium text-gray-700 mb-2">
                              {language === 'pt-BR' ? 'Nenhum arquivo encontrado' : 'No files found'}
                            </h3>
                            <p className="text-gray-500 text-sm">
                              {language === 'pt-BR' ? 'Os arquivos criados pelos Team Leaders aparecerão aqui' : 'Files created by Team Leaders will appear here'}
                            </p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {arbitrationFiles.map((file) => (
                              <div key={file.id} className={`bg-white border rounded-lg p-3 hover:shadow-md transition-shadow ${
                                selectedArbitrationFiles.some(f => f.id === file.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                              }`}>
                                <div className="flex items-start gap-3">
                                  {/* Checkbox for Selection */}
                                  <input
                                    type="checkbox"
                                    checked={selectedArbitrationFiles.some(f => f.id === file.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedArbitrationFiles(prev => [...prev, file]);
                                      } else {
                                        setSelectedArbitrationFiles(prev => prev.filter(f => f.id !== file.id));
                                      }
                                    }}
                                    className="mt-1 text-blue-600 flex-shrink-0"
                                  />
                                  
                                  <div className="flex-1 min-w-0">
                                    {/* Header */}
                                    <div className="flex items-start justify-between mb-3">
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <div className="text-lg">⚖️</div>
                                        <div className="flex-1 min-w-0">
                                          <div className="font-semibold text-gray-800 truncate">{file.helper_name}</div>
                                          <div className="text-xs text-gray-500">
                                            {language === 'pt-BR' ? 'Criado por' : 'Created by'} {file.pm_user}
                                          </div>
                                        </div>
                                        {/* Edit Name Button - CONTRACTOR FIXED */}
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            setArbitrationFileToEdit({...file});
                                            setShowArbitrationEditModal(true);
                                          }}
                                          className="text-gray-600 hover:text-gray-700 text-xs px-1 py-0.5 h-6"
                                          title={language === 'pt-BR' ? 'Editar nome' : 'Edit name'}
                                        >
                                          <Edit className="h-3 w-3" />
                                        </Button>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-xs text-gray-500">
                                          {new Date(file.date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}
                                        </div>
                                        <div className="flex gap-1 mt-1">
                                          <Badge variant="secondary" className="text-xs">PDF</Badge>
                                          {file.signature && file.signature.startsWith('data:image') ? (
                                            <Badge className="bg-green-100 text-green-800 text-xs">
                                              ✅ {language === 'pt-BR' ? 'Assinado' : 'Signed'}
                                            </Badge>
                                          ) : (
                                            <Badge className="bg-red-100 text-red-800 text-xs">
                                              ❌ {language === 'pt-BR' ? 'Não Assinado' : 'Unsigned'}
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Content Information */}
                                    <div className="space-y-2 text-sm mb-3">
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">{language === 'pt-BR' ? 'Data' : 'Date'}:</span> 
                                        <span className="font-medium">{new Date(file.date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">PM:</span> 
                                        <span className="font-medium">{file.pm_user}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">Status:</span> 
                                        <span className="font-medium text-green-600">
                                          ✅ {language === 'pt-BR' ? 'Pronto' : 'Ready'}
                                        </span>
                                      </div>
                                    </div>
                                    
                                    {/* Action Buttons */}
                                    <div className="grid grid-cols-2 gap-1">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => viewArbitrationFile(file.id, file.helper_name)}
                                        className="text-xs"
                                      >
                                        <Eye className="h-3 w-3 mr-1" />
                                        {t('view')}
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => downloadArbitrationFile(file.id)}
                                        className="text-xs"
                                      >
                                        <Download className="h-3 w-3 mr-1" />
                                        {t('download')}
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          setSelectedArbitrationFileId(file.id);
                                          setShowArbitrationEmailForm(true);
                                        }}
                                        className="text-xs bg-blue-50 hover:bg-blue-100"
                                      >
                                        <Send className="h-3 w-3 mr-1" />
                                        {language === 'pt-BR' ? 'Email' : 'Email'}
                                      </Button>
                                      {(!file.signature || !file.signature.startsWith('data:image')) && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            setArbitrationFileToSign(file);
                                            setShowArbitrationSignModal(true);
                                          }}
                                          className="text-xs bg-orange-50 hover:bg-orange-100"
                                        >
                                          <Edit className="h-3 w-3 mr-1" />
                                          {language === 'pt-BR' ? 'Assinar' : 'Sign'}
                                        </Button>
                                      )}
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={async () => {
                                          if (window.confirm(language === 'pt-BR' ? 
                                            `Deletar arquivo de arbitragem de ${file.helper_name}?` :
                                            `Delete arbitration file for ${file.helper_name}?`
                                          )) {
                                            console.log('🗑️ Admin Arbitration: Calling deleteArbitrationFile for:', file.id);
                                            deleteArbitrationFile(file.id, file.helper_name);
                                          }
                                        }}
                                        className="text-red-600 hover:text-red-700 text-xs col-span-2"
                                        title={language === 'pt-BR' ? 'Deletar arquivo de arbitragem' : 'Delete arbitration file'}
                                      >
                                        <Trash2 className="h-3 w-3 mr-1" />
                                        {language === 'pt-BR' ? 'Deletar Arbitration' : 'Delete Arbitration'}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )
                      )}
                    </div>
                  </CardHeader>
                </Card>
              )}

              {/* Independent Contractor Sub-tab - Admin Version with All Features */}
              {arbitrationSubTab === 'contractor' && (
                <Card>
                  <CardHeader>
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-3 sm:p-4">
                      {/* Header - Responsivo para iPhone */}
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-base sm:text-lg text-green-800">
                            📋 {language === 'pt-BR' ? 'Independent Contractor - Visão Geral' : 'Independent Contractor - Overview'}
                          </h3>
                          <p className="text-green-700 text-xs sm:text-sm mt-1">
                            {language === 'pt-BR' ? 
                              'Todos os contratos independentes criados pelos Team Leaders.' :
                              'All independent contracts created by Team Leaders.'
                            }
                          </p>
                        </div>
                        
                        {/* Botões de Ação - Layout móvel otimizado */}
                        <div className="flex flex-wrap gap-2">
                          {selectedContractorFiles.length > 0 && (
                            <Button 
                              onClick={() => setShowBulkEmailModal(true)}
                              className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none"
                              size="sm"
                            >
                              <Send className="h-4 w-4 mr-1 sm:mr-2" />
                              <span className="text-xs sm:text-sm">
                                {language === 'pt-BR' ? `Enviar ${selectedContractorFiles.length}` : `Send ${selectedContractorFiles.length}`}
                              </span>
                            </Button>
                          )}
                          <Button 
                            onClick={() => setShowContractorForm(true)} 
                            className="bg-blue-600 hover:bg-blue-700 flex-1 sm:flex-none"
                            size="sm"
                          >
                            <Plus className="h-4 w-4 mr-1 sm:mr-2" /> 
                            <span className="text-xs sm:text-sm">
                              {language === 'pt-BR' ? 'Novo' : 'New'}
                            </span>
                          </Button>
                          <Button
                            onClick={() => setShowContractorImportModal(true)}
                            className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none"
                            size="sm"
                          >
                            <Upload className="h-4 w-4 mr-1 sm:mr-2" />
                            <span className="text-xs sm:text-sm">
                              {language === 'pt-BR' ? 'Importar' : 'Import'}
                            </span>
                          </Button>
                          <Button
                            onClick={() => loadContractorFiles()}
                            variant="outline"
                            size="sm"
                            disabled={contractorLoading}
                            className="bg-white"
                          >
                            <span className="text-xs sm:text-sm">
                              {contractorLoading ? `🔄 ${t('loading')}` : `🔄 ${t('update')}`}
                            </span>
                          </Button>
                        </div>
                      </div>

                      {/* Admin Search and Filter Controls - Mobile Optimized */}
                      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 mt-3 sm:mt-4">
                        <h4 className="font-semibold text-gray-800 mb-3 text-sm sm:text-base">
                          🔍 {language === 'pt-BR' ? 'Buscar e Filtrar' : 'Search & Filter'}
                        </h4>
                        
                        {/* Grid Responsivo - 1 coluna no iPhone */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
                          <Input 
                            placeholder={language === 'pt-BR' ? 'Buscar por nome...' : 'Search by name...'}
                            value={contractorSearch}
                            onChange={(e) => setContractorSearch(e.target.value)}
                            className="text-sm h-10 sm:h-9"
                          />
                          <select 
                            className="w-full border rounded px-3 py-2 bg-white text-sm h-10 sm:h-9"
                            value={contractorFilterPM}
                            onChange={(e) => setContractorFilterPM(e.target.value)}
                          >
                            <option value="ALL">{language === 'pt-BR' ? 'Todas as equipes' : 'All teams'}</option>
                            {pmOptions.map((pm) => (
                              <option key={pm} value={pm}>{pm}</option>
                            ))}
                          </select>
                          <select 
                            className="w-full border rounded px-3 py-2 bg-white text-sm h-10 sm:h-9"
                            value={contractorFilterStatus}
                            onChange={(e) => setContractorFilterStatus(e.target.value)}
                          >
                            <option value="ALL">{language === 'pt-BR' ? 'Todos os status' : 'All status'}</option>
                            <option value="SIGNED">{language === 'pt-BR' ? 'Assinados' : 'Signed'}</option>
                            <option value="UNSIGNED">{language === 'pt-BR' ? 'Não assinados' : 'Unsigned'}</option>
                          </select>
                          <select 
                            className="w-full border rounded px-3 py-2 bg-white text-sm h-10 sm:h-9"
                            value={contractorSort}
                            onChange={(e) => setContractorSort(e.target.value)}
                          >
                            <option value="date_desc">{language === 'pt-BR' ? 'Data (Recente)' : 'Date (Recent)'}</option>
                            <option value="date_asc">{language === 'pt-BR' ? 'Data (Antiga)' : 'Date (Old)'}</option>
                            <option value="name_asc">{language === 'pt-BR' ? 'Nome (A-Z)' : 'Name (A-Z)'}</option>
                            <option value="name_desc">{language === 'pt-BR' ? 'Nome (Z-A)' : 'Name (Z-A)'}</option>
                            <option value="pm_asc">PM (A-Z)</option>
                          </select>
                        </div>
                        
                        {/* Selection Controls - Admin Contractor */}
                        {contractorFiles.length > 0 && (
                          <div className="mt-3 flex items-center gap-4">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedContractorFiles.length === filteredContractorFiles.length && filteredContractorFiles.length > 0}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedContractorFiles([...filteredContractorFiles]); // Use contractorFiles
                                  } else {
                                    setSelectedContractorFiles([]);
                                  }
                                }}
                                className="text-blue-600"
                              />
                              <span className="text-blue-600 font-medium">
                                {language === 'pt-BR' ? 'Selecionar Todos' : 'Select All'}
                              </span>
                            </label>
                            {selectedContractorFiles.length > 0 && (
                              <Badge variant="secondary" className="text-sm">
                                {selectedContractorFiles.length} {language === 'pt-BR' ? 'selecionados' : 'selected'}
                              </Badge>
                            )}
                          </div>
                        )}
                        
                        {/* Results Count */}
                        <div className="mt-3 text-xs text-gray-600">
                          {filteredContractorFiles.length} {language === 'pt-BR' ? 'contratos encontrados' : 'contracts found'}
                        </div>
                      </div>
                    </div>

                    {/* Admin Contractor Files Grid */}
                    <div className="p-4">
                      {contractorLoading ? (
                        <div className="text-center py-8">
                          <div className="text-2xl mb-2">🔄</div>
                          <div>{language === 'pt-BR' ? 'Carregando contratos...' : 'Loading contracts...'}</div>
                        </div>
                      ) : (
                        contractorFiles.length === 0 || filteredContractorFiles.length === 0 ? (
                          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                            <div className="text-gray-400 mb-4 text-4xl">📋</div>
                            <h3 className="font-medium text-gray-700 mb-2">
                              {language === 'pt-BR' ? 'Nenhum contrato encontrado' : 'No contracts found'}
                            </h3>
                            <p className="text-gray-500 text-sm">
                              {language === 'pt-BR' ? 'Os contratos criados pelos Team Leaders aparecerão aqui' : 'Contracts created by Team Leaders will appear here'}
                            </p>
                            <Button 
                              onClick={() => loadContractorFiles()}
                              variant="outline"
                              className="mt-3"
                            >
                              🔄 {language === 'pt-BR' ? 'Recarregar' : 'Reload'}
                            </Button>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredContractorFiles.map((file) => (
                              <div key={file.id} className={`bg-white border rounded-lg p-3 hover:shadow-md transition-shadow ${
                                selectedContractorFiles.some(f => f.id === file.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                              }`}>
                                <div className="flex items-start gap-3">
                                  {/* Checkbox for Selection */}
                                  <input
                                    type="checkbox"
                                    checked={selectedContractorFiles.some(f => f.id === file.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedContractorFiles(prev => [...prev, file]);
                                      } else {
                                        setSelectedContractorFiles(prev => prev.filter(f => f.id !== file.id));
                                      }
                                    }}
                                    className="mt-1 text-blue-600 flex-shrink-0"
                                  />
                                  
                                  <div className="flex-1 min-w-0">
                                    {/* Header */}
                                    <div className="flex items-start justify-between mb-3">
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <div className="text-lg">📋</div>
                                        <div className="flex-1 min-w-0">
                                          <div className="font-semibold text-gray-800 truncate">{file.helper_name}</div>
                                          <div className="text-xs text-gray-500">
                                            {language === 'pt-BR' ? 'Criado por' : 'Created by'} {file.pm_user}
                                          </div>
                                        </div>
                                        {/* Edit Name Button - Admin Only */}
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            setContractorFileToEdit({...file});
                                            setShowContractorEditModal(true);
                                          }}
                                          className="text-gray-600 hover:text-gray-700 text-xs px-1 py-0.5 h-6"
                                          title={language === 'pt-BR' ? 'Editar nome' : 'Edit name'}
                                        >
                                          <Edit className="h-3 w-3" />
                                        </Button>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-xs text-gray-500">
                                          {new Date(file.date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}
                                        </div>
                                        <div className="flex gap-1 mt-1">
                                          <Badge variant="secondary" className="text-xs">PDF</Badge>
                                          {file.signature && file.signature.startsWith('data:image') ? (
                                            <Badge className="bg-green-100 text-green-800 text-xs">
                                              ✅ {language === 'pt-BR' ? 'Assinado' : 'Signed'}
                                            </Badge>
                                          ) : (
                                            <Badge className="bg-red-100 text-red-800 text-xs">
                                              ❌ {language === 'pt-BR' ? 'Não Assinado' : 'Unsigned'}
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Content Information */}
                                    <div className="space-y-2 text-sm mb-3">
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">{language === 'pt-BR' ? 'Data' : 'Date'}:</span> 
                                        <span className="font-medium">{new Date(file.date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">PM:</span> 
                                        <span className="font-medium">{file.pm_user}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">Status:</span> 
                                        <span className="font-medium text-green-600">
                                          ✅ {language === 'pt-BR' ? 'Pronto' : 'Ready'}
                                        </span>
                                      </div>
                                    </div>
                                    
                                    {/* Action Buttons */}
                                    <div className="grid grid-cols-2 gap-1">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => viewContractorFile(file.id, file.helper_name)}
                                        className="text-xs"
                                      >
                                        <Eye className="h-3 w-3 mr-1" />
                                        {t('view')}
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => downloadContractorFile(file.id)}
                                        className="text-xs"
                                      >
                                        <Download className="h-3 w-3 mr-1" />
                                        {t('download')}
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          setSelectedContractorFileId(file.id);
                                          setShowContractorEmailForm(true);
                                        }}
                                        className="text-xs bg-blue-50 hover:bg-blue-100"
                                      >
                                        <Send className="h-3 w-3 mr-1" />
                                        {language === 'pt-BR' ? 'Email' : 'Email'}
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={async () => {
                                          if (window.confirm(language === 'pt-BR' ? 
                                            `Deletar contrato de ${file.helper_name}?` :
                                            `Delete contract for ${file.helper_name}?`
                                          )) {
                                            try {
                                              await axios.delete(`${API}/admin/contractor-files/${file.id}`);
                                              toast.success(language === 'pt-BR' ? 'Contrato deletado com sucesso' : 'Contract deleted successfully');
                                              await loadContractorFiles();
                                            } catch (e) {
                                              toast.error(getErrMsg(e, language === 'pt-BR' ? 'Erro ao deletar contrato' : 'Error deleting contract'));
                                            }
                                          }
                                        }}
                                        className="text-red-600 hover:text-red-700 text-xs col-span-2"
                                      >
                                        <Trash2 className="h-3 w-3 mr-1" />
                                        {language === 'pt-BR' ? 'Deletar' : 'Delete'}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )
                      )}
                    </div>
                  </CardHeader>
                </Card>
              )}

              {/* Seguro (Helper Waiver) Sub-tab - Admin */}
              {arbitrationSubTab === 'seguro' && (
                <HelperWaiverPanel
                  user={user}
                  language={language}
                  onViewPdf={(url, title) => handlePDFAction(url, title, false)}
                  onDownloadPdf={(url, filename) => handlePDFAction(url, filename, true)}
                />
              )}

              {/* Installation Docs Sub-tab - Admin Users (Full Access) */}
              {arbitrationSubTab === 'installation' && (
                <Card>
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <CardTitle className="flex items-center gap-2">
                        🔧 {language === 'pt-BR' ? 'Installation Docs' : 'Installation Docs'}
                      </CardTitle>
                      
                      {/* Admin Action Buttons */}
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => {
                            console.log('📤 Upload button clicked (Installation)');
                            setShowInstallationUploadModal(true);
                          }} 
                          className="bg-blue-600 hover:bg-blue-700"
                          size="sm"
                        >
                          <Plus className="h-4 w-4 mr-2" /> 
                          {language === 'pt-BR' ? 'Upload Arquivo' : 'Upload File'}
                        </Button>
                        <Button 
                          onClick={() => {
                            console.log('📁 New folder button clicked (Installation)');
                            setShowNewInstallationFolderModal(true);
                          }} 
                          variant="outline"
                          size="sm"
                        >
                          <Plus className="h-4 w-4 mr-2" /> 
                          {language === 'pt-BR' ? 'Nova Pasta' : 'New Folder'}
                        </Button>
                        <Button
                          onClick={() => {
                            console.log('🔄 Reload installation data');
                            loadInstallationFolders();
                            loadInstallationFiles();
                          }}
                          variant="outline"
                          size="sm"
                          disabled={installationLoading}
                        >
                          {installationLoading ? `🔄 ${t('loading')}` : `🔄 ${t('update')}`}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Debug Info */}
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                      <strong>Debug:</strong> User: {user?.username} | Role: {user?.role} | Folders: {installationFolders.length} | Files: {installationFiles.length} | Current Folder: {currentInstallationFolderId || 'Root'}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Breadcrumb Navigation */}
                      <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                        <button
                          onClick={() => {
                            console.log('🏠 Clicked root navigation');
                            setCurrentInstallationFolderId(null);
                            setInstallationFolderPath([]);
                          }}
                          className="hover:text-blue-600 font-medium"
                        >
                          🏠 {language === 'pt-BR' ? 'Raiz' : 'Root'}
                        </button>
                        {installationFolderPath.map((folder, index) => (
                          <React.Fragment key={folder.id}>
                            <span>→</span>
                            <button
                              onClick={() => {
                                console.log('📁 Clicked breadcrumb for:', folder.name);
                                setCurrentInstallationFolderId(folder.id);
                                setInstallationFolderPath(prev => prev.slice(0, index + 1));
                              }}
                              className="hover:text-blue-600 font-medium"
                            >
                              {folder.name}
                            </button>
                          </React.Fragment>
                        ))}
                        {currentInstallationFolderId && (
                          <>
                            <span>→</span>
                            <span className="font-medium text-blue-700">
                              {installationFolders.find(f => f.id === currentInstallationFolderId)?.name || 'Pasta'}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Folders Section */}
                      {installationFolders.filter(f => 
                        currentInstallationFolderId === null ? 
                          (f.parent_folder_id === null || f.parent_folder_id === undefined) : 
                          f.parent_folder_id === currentInstallationFolderId
                      ).length > 0 && (
                        <div>
                          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                            📁 {language === 'pt-BR' ? 'Pastas' : 'Folders'}
                            {/* Create subfolder button */}
                            {currentInstallationFolderId && (
                              <Button
                                onClick={() => setShowNewInstallationFolderModal(true)}
                                size="sm"
                                variant="outline"
                                className="text-xs"
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                {language === 'pt-BR' ? 'Subpasta' : 'Subfolder'}
                              </Button>
                            )}
                          </h3>
                          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
                            {installationFolders
                              .filter(f => 
                                currentInstallationFolderId === null ? 
                                  (f.parent_folder_id === null || f.parent_folder_id === undefined) : 
                                  f.parent_folder_id === currentInstallationFolderId
                              )
                              .map((folder) => (
                              <div 
                                key={folder.id} 
                                className="bg-blue-50 border border-blue-200 rounded-lg p-3 hover:bg-blue-100 cursor-pointer transition-colors group relative"
                                onClick={() => {
                                  console.log('📁 Folder clicked:', folder.name);
                                  navigateToInstallationFolder(folder.id);
                                }}
                              >
                                <div className="text-center">
                                  <div className="text-2xl mb-2">📁</div>
                                  <div className="text-sm font-medium text-gray-800 truncate">
                                    {folder.name}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {getInstallationFileCount(folder.id)} {language === 'pt-BR' ? 'arquivos' : 'files'}
                                  </div>
                                </div>
                                
                                {/* Admin Actions */}
                                <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      console.log('✏️ Rename button clicked for folder:', folder.name);
                                      e.stopPropagation();
                                      renameInstallationFolder(folder.id, folder.name);
                                    }}
                                    className="h-5 w-5 p-0 text-blue-600 hover:text-blue-700 mr-1"
                                    title={language === 'pt-BR' ? 'Renomear' : 'Rename'}
                                  >
                                    <Edit className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      console.log('🗑️ Delete button clicked for folder:', folder.name);
                                      e.stopPropagation();
                                      if (window.confirm(language === 'pt-BR' ? 
                                        `Deletar pasta "${folder.name}"?` : 
                                        `Delete folder "${folder.name}"?`
                                      )) {
                                        deleteInstallationFolder(folder.id, folder.name);
                                      }
                                    }}
                                    className="h-5 w-5 p-0 text-red-600 hover:text-red-700"
                                    title={language === 'pt-BR' ? 'Deletar' : 'Delete'}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                            
                            {/* Quick Create Folder */}
                            <div 
                              className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-3 hover:bg-gray-100 cursor-pointer transition-colors flex items-center justify-center"
                              onClick={() => setShowNewInstallationFolderModal(true)}
                            >
                              <div className="text-center">
                                <div className="text-2xl mb-2 text-gray-400">➕</div>
                                <div className="text-xs font-medium text-gray-600">
                                  {language === 'pt-BR' ? 'Nova Pasta' : 'New Folder'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Files Section */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                            📄 {language === 'pt-BR' ? 'Arquivos de Instalação' : 'Installation Files'}
                            {currentInstallationFolderId && (
                              <Button
                                onClick={() => {
                                  console.log('⬅️ Back button clicked');
                                  navigateBackInstallation();
                                }}
                                variant="outline"
                                size="sm"
                                className="text-xs"
                              >
                                ← {language === 'pt-BR' ? 'Voltar' : 'Back'}
                              </Button>
                            )}
                          </h3>
                          
                          {/* Selection Controls */}
                          {installationFiles.filter(f => 
                            currentInstallationFolderId === null ? 
                              (f.folder_id === null || f.folder_id === undefined) : 
                              f.folder_id === currentInstallationFolderId
                          ).length > 0 && (
                            <div className="flex items-center gap-4">
                              <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedInstallationFiles.length === installationFiles.filter(f => 
                                    currentInstallationFolderId === null ? 
                                      (f.folder_id === null || f.folder_id === undefined) : 
                                      f.folder_id === currentInstallationFolderId
                                  ).length && installationFiles.filter(f => 
                                    currentInstallationFolderId === null ? 
                                      (f.folder_id === null || f.folder_id === undefined) : 
                                      f.folder_id === currentInstallationFolderId
                                  ).length > 0}
                                  onChange={(e) => {
                                    const currentFolderFiles = installationFiles.filter(f => 
                                      currentInstallationFolderId === null ? 
                                        (f.folder_id === null || f.folder_id === undefined) : 
                                        f.folder_id === currentInstallationFolderId
                                    );
                                    if (e.target.checked) {
                                      setSelectedInstallationFiles([...currentFolderFiles]);
                                    } else {
                                      setSelectedInstallationFiles([]);
                                    }
                                  }}
                                  className="text-blue-600"
                                />
                                <span className="text-blue-600 font-medium">
                                  {language === 'pt-BR' ? 'Selecionar Todos' : 'Select All'}
                                </span>
                              </label>
                              
                              {/* Download Selected */}
                              {selectedInstallationFiles.length > 0 && (
                                <Button
                                  onClick={async () => {
                                    for (const file of selectedInstallationFiles) {
                                      const link = document.createElement('a');
                                      link.href = `${API}/installation-docs/${file.id}/download`;
                                      link.download = file.original_filename;
                                      link.click();
                                    }
                                    toast.success(language === 'pt-BR' ? 
                                      `Download de ${selectedInstallationFiles.length} arquivo(s) iniciado!` :
                                      `Download of ${selectedInstallationFiles.length} file(s) started!`
                                    );
                                    setSelectedInstallationFiles([]);
                                  }}
                                  variant="outline"
                                  size="sm"
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  {language === 'pt-BR' ? 'Baixar' : 'Download'} ({selectedInstallationFiles.length})
                                </Button>
                              )}
                            </div>
                          )}
                        </div>

                        {installationFiles.filter(f => 
                          currentInstallationFolderId === null ? 
                            (f.folder_id === null || f.folder_id === undefined) : 
                            f.folder_id === currentInstallationFolderId
                        ).length === 0 ? (
                          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                            <div className="text-gray-400 mb-4 text-4xl">🔧</div>
                            <h3 className="font-medium text-gray-700 mb-2">
                              {currentInstallationFolderId ? 
                                (language === 'pt-BR' ? 'Pasta vazia' : 'Empty folder') :
                                (language === 'pt-BR' ? 'Nenhum arquivo de instalação' : 'No installation files')
                              }
                            </h3>
                            <p className="text-gray-500 text-sm mb-4">
                              {language === 'pt-BR' ? 'Faça upload de vídeos e documentos para ajudar nas instalações' : 'Upload videos and documents to help with installations'}
                            </p>
                            <Button
                              onClick={() => setShowInstallationUploadModal(true)}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              {language === 'pt-BR' ? 'Upload Arquivo' : 'Upload File'}
                            </Button>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            {installationFiles
                              .filter(f => 
                                currentInstallationFolderId === null ? 
                                  (f.folder_id === null || f.folder_id === undefined) : 
                                  f.folder_id === currentInstallationFolderId
                              )
                              .map((file) => (
                              <div key={file.id} className={`bg-white border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer relative ${
                                selectedInstallationFiles.some(f => f.id === file.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                              }`}>
                                
                                {/* Checkbox */}
                                <div className="absolute top-2 left-2 z-10">
                                  <input
                                    type="checkbox"
                                    checked={selectedInstallationFiles.some(f => f.id === file.id)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      if (e.target.checked) {
                                        setSelectedInstallationFiles(prev => [...prev, file]);
                                      } else {
                                        setSelectedInstallationFiles(prev => prev.filter(f => f.id !== file.id));
                                      }
                                    }}
                                    className="text-blue-600 bg-white rounded border-2 shadow-sm"
                                  />
                                </div>

                                {/* Thumbnail */}
                                <div 
                                  className="h-32 bg-gray-100 flex items-center justify-center relative cursor-pointer hover:bg-gray-200 transition-colors"
                                  onClick={async () => {
                                    // Para PDFs, abrir em nova aba com autenticação
                                    if (file.file_type === 'pdf') {
                                      try {
                                        setInstallationPdfLoading(file.id); // Set loading for this specific file
                                        
                                        const token = sessionStorage.getItem('access_token');
                                        if (!token) {
                                          toast.error(language === 'pt-BR' ? 'Token não encontrado' : 'Token not found');
                                          setInstallationPdfLoading(null);
                                          return;
                                        }
                                        
                                        // Fetch o PDF com autenticação e criar blob URL
                                        const response = await fetch(`${API}/installation-docs/${file.id}/download`, {
                                          headers: {
                                            'Authorization': `Bearer ${token}`
                                          }
                                        });
                                        
                                        if (!response.ok) {
                                          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                                        }
                                        
                                        const blob = await response.blob();
                                        const pdfUrl = URL.createObjectURL(blob);
                                        
                                        // Abrir em modal
                                        handlePDFAction(pdfUrl, file.file_name || file.original_filename || 'document.pdf', false);
                                        toast.success(language === 'pt-BR' ? 'PDF aberto em modal' : 'PDF opened in modal');
                                        
                                      } catch (e) {
                                        console.error('Failed to open PDF:', e);
                                        toast.error(language === 'pt-BR' ? 'Erro ao abrir PDF' : 'Error opening PDF');
                                      } finally {
                                        setInstallationPdfLoading(null); // Clear loading state
                                      }
                                    } else {
                                      // Para outros tipos (vídeo, imagem), usar o modal
                                      setCurrentInstallationFile(file);
                                      setShowInstallationViewer(true);
                                    }
                                  }}
                                >
                                  {file.file_type === 'video' ? (
                                    <div className="text-center">
                                      <div className="text-3xl text-blue-500 mb-2">🎥</div>
                                      <div className="text-xs text-gray-600">VIDEO</div>
                                    </div>
                                  ) : file.file_type === 'image' ? (
                                    <img
                                      src={`${API}/installation-docs/${file.id}/thumbnail`}
                                      alt={file.original_filename}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                  ) : file.file_type === 'pdf' ? (
                                    <div className="text-center relative">
                                      {installationPdfLoading === file.id ? (
                                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
                                          <div className="text-white text-center">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                                            <div className="text-sm">Carregando...</div>
                                          </div>
                                        </div>
                                      ) : null}
                                      <div className="text-3xl text-red-500 mb-2">📄</div>
                                      <div className="text-xs text-gray-600 mb-2">PDF</div>
                                      <button className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded transition-colors">
                                        {language === 'pt-BR' ? 'Abrir PDF' : 'Open PDF'}
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="text-center">
                                      <div className="text-3xl text-gray-500 mb-2">📄</div>
                                      <div className="text-xs text-gray-600">{file.file_type?.toUpperCase()}</div>
                                    </div>
                                  )}
                                  
                                  {/* Fallback for image errors */}
                                  <div className="hidden w-full h-full bg-gray-200 items-center justify-center">
                                    <div className="text-center">
                                      <div className="text-2xl text-gray-400 mb-1">📄</div>
                                      <div className="text-xs text-gray-500">{file.file_type?.toUpperCase()}</div>
                                    </div>
                                  </div>
                                </div>

                                {/* File Info */}
                                <div className="p-3">
                                  <div className="font-medium text-sm text-gray-800 truncate mb-1">
                                    {file.original_filename}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {(file.file_size / 1024).toFixed(1)} KB
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {new Date(file.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}
                                  </div>
                                  {file.uploaded_by && (
                                    <div className="text-xs text-blue-600">
                                      {language === 'pt-BR' ? 'Por' : 'By'} {file.uploaded_by}
                                    </div>
                                  )}
                                </div>

                                {/* Quick Actions - Admin Full Access */}
                                <div className="px-3 pb-3">
                                  <div className="flex gap-1">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const link = document.createElement('a');
                                        link.href = `${API}/installation-docs/${file.id}/download`;
                                        link.download = file.original_filename;
                                        link.click();
                                        toast.success(language === 'pt-BR' ? 'Download iniciado' : 'Download started');
                                      }}
                                      className="flex-1 text-xs"
                                      title={language === 'pt-BR' ? 'Download' : 'Download'}
                                    >
                                      <Download className="h-3 w-3" />
                                    </Button>
                                    
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={async (e) => {
                                        console.log('🗑️ Delete file button clicked:', file.original_filename);
                                        e.stopPropagation();
                                        if (window.confirm(language === 'pt-BR' ? 
                                          `Deletar ${file.original_filename}?` : 
                                          `Delete ${file.original_filename}?`
                                        )) {
                                          deleteInstallationFile(file.id, file.original_filename);
                                        }
                                      }}
                                      className="flex-1 text-xs text-red-600 hover:text-red-700"
                                      title={language === 'pt-BR' ? 'Deletar' : 'Delete'}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Helpers Sub-tab - ADMIN View All Helpers */}
              {arbitrationSubTab === 'helpers' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      👷 {language === 'pt-BR' ? 'Todos os Funcionários' : 'All Helpers'}
                    </CardTitle>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                      <p className="text-sm text-blue-800">
                        <strong>👷 {language === 'pt-BR' ? 'Visão geral de todos os funcionários' : 'Overview of all helpers'}</strong>
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        {language === 'pt-BR' 
                          ? 'Visualize, edite ou remova funcionários de todas as equipes.' 
                          : 'View, edit or remove helpers from all teams.'}
                      </p>
                    </div>

                    {/* Filtro por PM */}
                    <div className="mt-3">
                      <Label className="text-sm">Filtrar por Equipe:</Label>
                      <select 
                        className="w-full sm:w-48 border rounded px-3 py-2 bg-white text-sm mt-1"
                        value={arbitrationFilterPM}
                        onChange={(e) => setArbitrationFilterPM(e.target.value)}
                      >
                        <option value="ALL">{language === 'pt-BR' ? 'Todas as Equipes' : 'All Teams'}</option>
                        {pmOptions.map((pm) => (
                          <option key={pm} value={pm}>{pm}</option>
                        ))}
                      </select>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    {/* Formulário de Edição - ADMIN */}
                    {showHelperForm && editingHelper && (
                      <div className="bg-white border border-blue-300 rounded-lg p-4 mb-4">
                        <h5 className="font-medium mb-3 text-sm flex items-center gap-2">
                          ✏️ Editar Funcionário
                          <Badge variant="outline" className="text-xs">{editingHelper.pm_user}</Badge>
                        </h5>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="sm:col-span-2">
                            <Label className="text-sm">Nome Completo *</Label>
                            <Input
                              value={helperFormData.full_name}
                              onChange={(e) => setHelperFormData({...helperFormData, full_name: e.target.value})}
                              placeholder="Nome completo"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-sm">Telefone</Label>
                            <Input
                              value={helperFormData.phone}
                              onChange={(e) => setHelperFormData({...helperFormData, phone: e.target.value})}
                              onBlur={(e) => {
                                const formatted = formatPhoneNumber(e.target.value);
                                setHelperFormData({...helperFormData, phone: formatted});
                              }}
                              placeholder="+1(555)123-4567"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-sm">Endereço</Label>
                            <Input
                              value={helperFormData.address}
                              onChange={(e) => setHelperFormData({...helperFormData, address: e.target.value})}
                              placeholder="Endereço"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-sm">Data de Contratação</Label>
                            <Input
                              type="date"
                              value={helperFormData.hire_date}
                              onChange={(e) => setHelperFormData({...helperFormData, hire_date: e.target.value})}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-sm">SSN / Tax ID</Label>
                            <Input
                              value={helperFormData.ssn_tax_id}
                              onChange={(e) => setHelperFormData({...helperFormData, ssn_tax_id: e.target.value})}
                              placeholder="XXX-XX-XXXX"
                              className="text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="admin-helper-english"
                              checked={helperFormData.speaks_english}
                              onChange={(e) => {
                                const speaksEnglish = e.target.checked;
                                setHelperFormData({
                                  ...helperFormData, 
                                  speaks_english: speaksEnglish,
                                  is_team_translator: speaksEnglish ? helperFormData.is_team_translator : false
                                });
                              }}
                              className="rounded"
                            />
                            <Label htmlFor="admin-helper-english" className="text-sm cursor-pointer">
                              ☑️ Fala Inglês?
                            </Label>
                          </div>
                          {helperFormData.speaks_english && (
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="admin-helper-translator"
                                checked={helperFormData.is_team_translator}
                                onChange={(e) => setHelperFormData({...helperFormData, is_team_translator: e.target.checked})}
                                className="rounded"
                              />
                              <Label htmlFor="admin-helper-translator" className="text-sm cursor-pointer">
                                🗣️ Tradutor da equipe?
                              </Label>
                            </div>
                          )}
                        </div>
                        <div className="flex justify-end gap-2 mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={cancelHelperForm}
                            disabled={helpersLoading}
                          >
                            Cancelar
                          </Button>
                          <Button
                            size="sm"
                            onClick={saveHelperAsAdmin}
                            disabled={helpersLoading}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            {helpersLoading ? '⏳ Salvando...' : '💾 Atualizar'}
                          </Button>
                        </div>
                      </div>
                    )}

                    {helpersLoading ? (
                      <div className="text-center py-8">
                        <div className="text-2xl mb-2">🔄</div>
                        <div>Carregando funcionários...</div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {helpers
                          .filter(h => arbitrationFilterPM === 'ALL' || h.pm_user === arbitrationFilterPM)
                          .map((helper) => (
                          <div key={helper.id} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <div className="font-medium text-base">{helper.full_name}</div>
                                  <Badge variant="outline" className="text-xs">{helper.pm_user}</Badge>
                                </div>
                                {helper.phone && <div className="text-sm text-gray-600">📞 {helper.phone}</div>}
                                {helper.address && <div className="text-sm text-gray-600">📍 {helper.address}</div>}
                                {helper.hire_date && <div className="text-sm text-gray-600">📅 {helper.hire_date}</div>}
                                {helper.ssn_tax_id && <div className="text-sm text-gray-600">🆔 {helper.ssn_tax_id}</div>}
                                <div className="flex gap-2 mt-2">
                                  {helper.speaks_english && (
                                    <Badge variant="secondary" className="text-xs">🇺🇸 Fala Inglês</Badge>
                                  )}
                                  {helper.is_team_translator && (
                                    <Badge variant="default" className="text-xs">🗣️ Tradutor</Badge>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Documentos vinculados */}
                            <div className="mt-3 pt-3 border-t">
                              <div className="text-xs font-semibold text-gray-700 mb-2">📄 Docs:</div>
                              <div className="grid grid-cols-2 gap-2">
                                {helper.arbitration_agreement_id ? (
                                  <div className="border border-orange-300 rounded bg-orange-50">
                                    <div 
                                      className="p-1 text-center cursor-pointer hover:bg-orange-100"
                                      onClick={async () => {
                                        try {
                                          const response = await axios.get(
                                            `${API}/arbitration-files/${helper.arbitration_agreement_id}/download`,
                                            { responseType: 'blob' }
                                          );
                                          const blobUrl = window.URL.createObjectURL(response.data);
                                          openPdfWithLoading({ url: blobUrl, title: `Arbitration - ${helper.full_name}` });
                                          setShowPDFModal(true);
                                        } catch (e) {
                                          toast.error('Erro ao carregar PDF');
                                        }
                                      }}
                                    >
                                      <div className="text-orange-600 text-sm">⚖️</div>
                                      <div className="text-xs text-orange-700">Arb</div>
                                    </div>
                                    <button
                                      className="w-full text-xs text-red-600 hover:bg-red-50 py-1 border-t border-orange-200"
                                      onClick={async () => {
                                        if (window.confirm('Desvincular Arbitration?')) {
                                          try {
                                            await axios.put(
                                              `${API}/admin/team-leaders/${helper.pm_user}/helpers/${helper.id}`,
                                              { arbitration_agreement_id: null }
                                            );
                                            toast.success('Arbitration desvinculado!');
                                            const { data } = await axios.get(`${API}/admin/all-helpers`);
                                            setHelpers(data);
                                          } catch (e) {
                                            toast.error('Erro ao desvincular');
                                          }
                                        }
                                      }}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <div className="border border-dashed border-gray-300 rounded p-1 bg-gray-50 text-center">
                                    <div className="text-gray-400 text-sm">⚖️</div>
                                    <div className="text-xs text-gray-400">—</div>
                                  </div>
                                )}

                                {helper.contractor_agreement_id ? (
                                  <div className="border border-teal-300 rounded bg-teal-50">
                                    <div 
                                      className="p-1 text-center cursor-pointer hover:bg-teal-100"
                                      onClick={async () => {
                                        try {
                                          const response = await axios.get(
                                            `${API}/contractor-files/${helper.contractor_agreement_id}/download`,
                                            { responseType: 'blob' }
                                          );
                                          const blobUrl = window.URL.createObjectURL(response.data);
                                          openPdfWithLoading({ url: blobUrl, title: `Contractor - ${helper.full_name}` });
                                          setShowPDFModal(true);
                                        } catch (e) {
                                          toast.error('Erro ao carregar PDF');
                                        }
                                      }}
                                    >
                                      <div className="text-teal-600 text-sm">📝</div>
                                      <div className="text-xs text-teal-700">Cont</div>
                                    </div>
                                    <button
                                      className="w-full text-xs text-red-600 hover:bg-red-50 py-1 border-t border-teal-200"
                                      onClick={async () => {
                                        if (window.confirm('Desvincular Contractor?')) {
                                          try {
                                            await axios.put(
                                              `${API}/admin/team-leaders/${helper.pm_user}/helpers/${helper.id}`,
                                              { contractor_agreement_id: null }
                                            );
                                            toast.success('Contractor desvinculado!');
                                            const { data } = await axios.get(`${API}/admin/all-helpers`);
                                            setHelpers(data);
                                          } catch (e) {
                                            toast.error('Erro ao desvincular');
                                          }
                                        }
                                      }}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <div className="border border-dashed border-gray-300 rounded p-1 bg-gray-50 text-center">
                                    <div className="text-gray-400 text-sm">📝</div>
                                    <div className="text-xs text-gray-400">—</div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Botões de ação */}
                            <div className="flex gap-2 mt-3">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  // Editar helper como ADMIN
                                  setEditingHelper(helper);
                                  setHelperFormData({
                                    full_name: helper.full_name || '',
                                    address: helper.address || '',
                                    phone: helper.phone || '',
                                    hire_date: helper.hire_date || '',
                                    ssn_tax_id: helper.ssn_tax_id || '',
                                    speaks_english: helper.speaks_english || false,
                                    is_team_translator: helper.is_team_translator || false
                                  });
                                  setShowHelperForm(true);
                                }}
                                className="flex-1"
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Editar
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  if (window.confirm(`Remover ${helper.full_name} da equipe ${helper.pm_user}?`)) {
                                    try {
                                      await axios.delete(`${API}/admin/team-leaders/${helper.pm_user}/helpers/${helper.id}`);
                                      toast.success('Helper removido!');
                                      // Recarregar todos os helpers
                                      const { data } = await axios.get(`${API}/admin/all-helpers`);
                                      setHelpers(data);
                                    } catch (e) {
                                      toast.error('Erro ao remover helper');
                                    }
                                  }
                                }}
                                className="flex-1 text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-3 w-3 mr-1" />
                                Remover
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {helpers.filter(h => arbitrationFilterPM === 'ALL' || h.pm_user === arbitrationFilterPM).length === 0 && !helpersLoading && (
                      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
                        <div className="text-4xl mb-3">👷</div>
                        <p className="text-gray-600">Nenhum funcionário encontrado</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}


              {/* Manual de Instruções Sub-tab */}
              {arbitrationSubTab === 'manual' && (
                <div className="space-y-6">
                  <InstructionManual 
                    userRole={user?.role}
                    language={language}
                    onExportPDF={exportManualToPDF}
                  />
                </div>
              )}

              {/* Installation Docs Sub-tab removed for PM users */}

            </TabsContent>
          )}
          {user?.role !== 'admin' && (
            <TabsContent value="support" className="mt-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{language === 'pt-BR' ? 'Suporte Técnico' : 'Technical Support'}</CardTitle>
                    <Button onClick={() => setSupportNewOpen(true)} className="bg-green-600 hover:bg-green-700">
                      <Plus className="h-4 w-4 mr-2" /> 
                      {language === 'pt-BR' ? 'Novo Ticket' : 'New Ticket'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {!supportView ? (
                    // Lista de tickets
                    <div className="space-y-3">
                      <div className="mb-4">
                        <Button onClick={loadMyTickets} variant="outline" size="sm">
                          🔄 {language === 'pt-BR' ? 'Atualizar Tickets' : 'Update Tickets'}
                        </Button>
                      </div>
                      
                      {supportTickets.length > 0 ? (
                        supportTickets.map((ticket) => (
                          <div key={ticket.id} className="border rounded-lg p-4 hover:bg-gray-50">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold">{ticket.title}</h4>
                                {ticket.has_unread_admin_reply && (
                                  <span className="text-orange-500" title="Nova resposta do admin disponível">
                                    🔔
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant={
                                  ticket.status === 'open' ? 'default' :
                                  ticket.status === 'pending' ? 'secondary' :
                                  ticket.status === 'resolved' ? 'outline' :
                                  'secondary'
                                }>
                                  {ticket.status === 'open' ? '🔴 Aberto' :
                                   ticket.status === 'pending' ? '🟡 Pendente' :
                                   ticket.status === 'resolved' ? `✅ ${language === 'pt-BR' ? 'Resolvido' : 'Resolved'}` :
                                   ticket.status}
                                </Badge>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="text-red-600 hover:text-red-700"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (window.confirm(language === 'pt-BR' ? 
                                      `Tem certeza que deseja excluir o ticket "${ticket.title}"?` : 
                                      `Are you sure you want to delete "${ticket.title}"?`
                                    )) {
                                      try {
                                        await axios.delete(`${API}/tickets/${ticket.id}`);
                                        toast.success(language === 'pt-BR' ? 'Ticket excluído!' : 'Ticket deleted!');
                                        await loadMyTickets();
                                      } catch (error) {
                                        console.error('Error:', error);
                                        toast.error(language === 'pt-BR' ? 'Erro ao excluir' : 'Error deleting');
                                      }
                                    }
                                  }}
                                  title={language === 'pt-BR' ? 'Excluir' : 'Delete'}
                                >
                                  🗑️
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => openMyTicket(ticket)}>
                                  Ver Ticket
                                </Button>
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 mb-2">
                              {language === 'pt-BR' ? 'Criado em' : 'Created on'}: {formatNyDateTime(ticket.created_at)}
                            </div>
                            <div className="text-sm text-gray-700">
                              {ticket.messages?.[0]?.text?.substring(0, 100)}...
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <div className="text-gray-500 mb-4">
                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.959 8.959 0 01-4.906-1.437L3 21l2.437-5.094A8.959 8.959 0 013 12c0-4.418 3.582-8 8-8s8 3.582 8 8z" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum ticket de suporte</h3>
                          <p className="text-gray-500 mb-4">
                            Quando precisar de ajuda, crie um novo ticket de suporte.
                          </p>
                          <Button onClick={() => setSupportNewOpen(true)} className="bg-green-600 hover:bg-green-700">
                            <Plus className="h-4 w-4 mr-2" /> Criar Primeiro Ticket
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Visualização de ticket específico
                    <div className="space-y-4">
                      <div className="flex items-center justify-between border-b pb-4">
                        <div>
                          <h3 className="text-lg font-semibold">{supportView.title}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant={
                              supportView.status === 'open' ? 'default' :
                              supportView.status === 'pending' ? 'secondary' :
                              supportView.status === 'resolved' ? 'outline' :
                              'secondary'
                            }>
                              {supportView.status === 'open' ? '🔴 Aberto' :
                               supportView.status === 'pending' ? '🟡 Pendente' :
                               supportView.status === 'resolved' ? '✅ Resolvido' :
                               supportView.status}
                            </Badge>
                            <span className="text-sm text-gray-500">
                              Criado em {formatNyDateTime(supportView.created_at)}
                            </span>
                          </div>
                        </div>
                        <Button variant="outline" onClick={() => setSupportView(null)}>
                          ← Voltar para Lista
                        </Button>
                      </div>

                      {/* Messages */}
                      <div className="space-y-4 max-h-96 overflow-y-auto">
                        {supportView.messages?.map((msg, index) => (
                          <div key={index} className={`p-4 rounded-lg ${
                            msg.role === 'admin' ? 'bg-blue-50 border-l-4 border-blue-500' : 'bg-gray-50 border-l-4 border-gray-300'
                          }`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="font-medium flex items-center gap-2">
                                {msg.role === 'admin' ? '👨‍💼 Admin' : `👤 ${msg.author}`}
                              </div>
                              <span className="text-sm text-gray-500">
                                {formatNyDateTime(msg.timestamp)}
                              </span>
                            </div>
                            <div className="text-gray-700 whitespace-pre-wrap">{msg.text}</div>
                            {msg.attachments?.length > 0 && (
                              <div className="mt-2">
                                <div className="text-sm font-medium text-gray-600 mb-1">Anexos:</div>
                                {msg.attachments.map((att, i) => (
                                  <div key={i} className="flex items-center gap-2 text-sm">
                                    <button
                                      onClick={async () => {
                                        try {
                                          const token = sessionStorage.getItem('access_token');
                                          const response = await fetch(`${API}/tickets/attachments/${att.id}`, {
                                            headers: {
                                              'Authorization': `Bearer ${token}`
                                            }
                                          });
                                          
                                          if (!response.ok) {
                                            throw new Error(`HTTP ${response.status}`);
                                          }
                                          
                                          const blob = await response.blob();
                                          const url = URL.createObjectURL(blob);
                                          
                                          // Verificar tipo de arquivo e abrir apropriadamente
                                          if (att.filename.toLowerCase().endsWith('.pdf')) {
                                            // Abrir PDF em modal
                                            openPdfWithLoading({ url: url, title: att.filename });
                                            setShowPDFModal(true);
                                          } else if (/\.(jpg|jpeg|png|heic|heif)$/i.test(att.filename)) {
                                            // Abrir imagem em modal
                                            setImageModalData({ url: url, title: att.filename });
                                            setShowImageModal(true);
                                          } else {
                                            // Outros arquivos - abrir em modal PDF como fallback
                                            openPdfWithLoading({ url: url, title: att.filename });
                                            setShowPDFModal(true);
                                          }
                                          
                                        } catch (error) {
                                          console.error('Error opening attachment:', error);
                                          toast.error(language === 'pt-BR' ? 'Erro ao abrir anexo' : 'Error opening attachment');
                                        }
                                      }}
                                      className="text-blue-600 hover:text-blue-700 underline cursor-pointer"
                                    >
                                      📎 {att.filename}
                                    </button>
                                    <Button
                                      onClick={async () => {
                                        try {
                                          const token = sessionStorage.getItem('access_token');
                                          const response = await fetch(`${API}/tickets/attachments/${att.id}`, {
                                            headers: {
                                              'Authorization': `Bearer ${token}`
                                            }
                                          });
                                          
                                          if (!response.ok) {
                                            throw new Error(`HTTP ${response.status}`);
                                          }
                                          
                                          const blob = await response.blob();
                                          const url = URL.createObjectURL(blob);
                                          const a = document.createElement('a');
                                          a.href = url;
                                          a.download = att.filename;
                                          document.body.appendChild(a);
                                          a.click();
                                          document.body.removeChild(a);
                                          URL.revokeObjectURL(url);
                                          
                                        } catch (error) {
                                          console.error('Error downloading attachment:', error);
                                          toast.error(language === 'pt-BR' ? 'Erro ao baixar anexo' : 'Error downloading attachment');
                                        }
                                      }}
                                      variant="outline"
                                      size="sm"
                                      className="text-green-600 hover:text-green-700 h-6 px-2 text-xs"
                                    >
                                      💾 {language === 'pt-BR' ? 'Baixar' : 'Download'}
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Reply Form */}
                      {supportView.status !== 'resolved' && (
                        <div className="border-t pt-4">
                          <h4 className="font-medium mb-3">Adicionar Resposta</h4>
                          <div className="space-y-3">
                            <Textarea
                              placeholder="Digite sua mensagem..."
                              value={supportReply}
                              onChange={(e) => setSupportReply(e.target.value)}
                              rows={4}
                            />
                            <div>
                              <input
                                type="file"
                                multiple
                                onChange={(e) => setSupportFiles(Array.from(e.target.files || []))}
                                className="mb-2"
                              />
                              {supportFiles.length > 0 && (
                                <div className="text-sm text-gray-600">
                                  {supportFiles.length} arquivo(s) selecionado(s)
                                </div>
                              )}
                            </div>
                            <Button onClick={replyMyTicket} disabled={!supportReply.trim()}>
                              📤 Enviar Resposta
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* History */}
          <TabsContent value="history" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Histórico de Envios</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {history.map((h) => (
                    <div key={h.id} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                      <div>
                        <div className="font-medium">{h.job_name}</div>
                        <div className="text-slate-600 text-sm">{language === 'pt-BR' ? 'Enviado em:' : 'Sent on:'} {formatNyDateTime(h.sent_at) || h.sent_at_str || '-'}</div>
                        <div className="text-slate-600 text-xs">{language === 'pt-BR' ? 'Para:' : 'To:'} {(h.last_sent_to_names && h.last_sent_to_names.length > 0) ? h.last_sent_to_names.join(', ') : (h.last_sent_to || []).join(', ')}</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => downloadPDF(h.id)}>
                        <Download className="h-4 w-4 mr-2" /> PDF
                      </Button>
                    </div>
                  ))}
                  {history.length === 0 && <p className="text-center text-slate-500 py-8">Sem histórico</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trash */}
          <TabsContent value="trash" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Lixeira (Soft Delete)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-3 mb-4">
                  <Input placeholder="Buscar por Job ou Ordem" value={trashSearch} onChange={(e) => setTrashSearch(e.target.value)} className="md:flex-1" />
                  <select value={trashSort} onChange={(e) => setTrashSort(e.target.value)} className="border rounded px-2 py-2">
                    <option value="deleted_desc">Excluído: mais recente</option>
                    <option value="deleted_asc">Excluído: mais antigo</option>
                    <option value="job_asc">Job: A-Z</option>
                    <option value="job_desc">Job: Z-A</option>
                  </select>
                </div>
                <div className="space-y-3">
                  {filteredTrash.map((bill) => (
                    <div key={bill.id} className="flex items-center justify-between p-4 bg-white rounded-lg border">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-800">{bill.job_name || "Sem nome"}</h3>
                        <p className="text-sm text-slate-600">Ordem: {bill.order_number || "-"} | Excluído em: {bill.deleted_at ? formatNyDateTime(bill.deleted_at) : ""}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => restoreBill(bill.id)}>Restaurar</Button>
                        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => openConfirmDelete(bill.id)}>Apagar Definitivamente</Button>
                      </div>
                    </div>
                  ))}
                  {filteredTrash.length === 0 && <p className="text-center text-slate-500 py-8">Lixeira vazia</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Admin */}
          {user?.role === 'admin' && (
            <TabsContent value="admin" className="mt-6">
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {adminSubTab !== 'hub' && (
                        <>
                          <Button
                            data-testid="admin-hub-back-btn"
                            variant="outline"
                            size="sm"
                            className="text-xs px-2.5 py-1"
                            onClick={() => setAdminSubTab('hub')}
                          >
                            ← {language === 'pt-BR' ? 'Painel Admin' : 'Admin Panel'}
                          </Button>
                          <span className="font-semibold text-slate-800 text-sm" data-testid="admin-hub-section-title">
                            {(NAV_SUBTAB_LABELS.admin && NAV_SUBTAB_LABELS.admin[adminSubTab]) || adminSubTab}
                          </span>
                        </>
                      )}
                    </div>
                    {adminSubTab === 'usuarios' && (
                      <Button onClick={() => setAdminCreateOpen(true)} size="sm" className="text-xs w-full sm:w-auto">
                        + Criar Usuário
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {adminSubTab === 'hub' && (
                    <AdminHub
                      language={language}
                      onNavigate={(k) => setAdminSubTab(k)}
                      onNavigateTab={(t) => safeSetActiveTab(t)}
                    />
                  )}

                  {adminSubTab === 'usuarios' && (
                    <>
                      {/* Versão mínima do app nativo */}
                      <div className="flex flex-wrap items-center gap-2 mb-3 p-2.5 bg-slate-50 border rounded-lg">
                        <span className="text-xs font-medium text-slate-600">📱 Versão mínima do app nativo:</span>
                        <Input
                          value={minNativeVersionInput}
                          onChange={(e) => setMinNativeVersionInput(e.target.value)}
                          placeholder="ex: 2.9.0"
                          className="h-8 w-28 text-xs"
                          data-testid="min-native-version-input"
                        />
                        <Button size="sm" onClick={saveMinNativeVersion} className="h-8 text-xs" data-testid="min-native-version-save">Salvar</Button>
                        <span className="text-[11px] text-slate-500">
                          {minNativeVersion ? `Nativos abaixo de v${minNativeVersion} ganham selo vermelho` : 'Vazio = alerta desativado'}
                        </span>
                      </div>
                      {/* Lista de Usuários */}
                      <div className="space-y-2">
                        {adminUsers.map((u) => (
                          <div key={u.username} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 border rounded-lg bg-white gap-3">
                            <div className="flex-1">
                              <div className="font-medium text-sm sm:text-base">{u.username} <span className="text-xs text-slate-500">({u.role})</span></div>
                              <div className="text-slate-600 text-xs sm:text-sm">{u.full_name || ''} {u.is_active ? '• ativo' : '• inativo'}</div>
                              {u.app_info ? (
                                <div className="text-xs text-slate-500 mt-0.5" data-testid={`user-app-version-${u.username}`}>
                                  {u.app_info.platform === 'android' ? '🤖 Android' : u.app_info.platform === 'ios' ? '🍎 iOS' : '🌐 Web'}
                                  {u.app_info.native_version ? ` v${u.app_info.native_version}${u.app_info.native_build ? ` (${u.app_info.native_build})` : ''}` : (u.app_info.web_version ? ` v.${u.app_info.web_version}` : '')}
                                  {u.app_info.updated_at && ` • visto ${u.app_info.updated_at.slice(0, 16).replace('T', ' ')}`}
                                  {minNativeVersion && (u.app_info.platform === 'android' || u.app_info.platform === 'ios') && u.app_info.native_version && compareVersions(u.app_info.native_version, minNativeVersion) < 0 && (
                                    <span data-testid={`user-outdated-badge-${u.username}`} className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-300 font-semibold text-[10px]">
                                      ⚠️ Desatualizado
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <div className="text-xs text-slate-400 italic mt-0.5" data-testid={`user-app-version-${u.username}`}>
                                  🕓 {language === 'pt-BR' ? 'versão será registrada no próximo acesso' : 'version will be recorded on next access'}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1 sm:gap-2">
                              <Button size="sm" variant="outline" onClick={() => openEditAdminUser(u)} className="text-xs px-2 py-1 flex-1 sm:flex-none">Editar</Button>
                              <Button size="sm" variant="outline" onClick={() => resetAdminPassword(u.username)} className="text-xs px-2 py-1 flex-1 sm:flex-none">Reset Senha</Button>
                              <Button size="sm" variant="outline" onClick={() => toggleAdminActive(u)} className="text-xs px-2 py-1 flex-1 sm:flex-none">{u.is_active ? 'Desativar' : 'Ativar'}</Button>
                              <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700 text-xs px-2 py-1 flex-1 sm:flex-none" onClick={() => deleteAdminUser(u)}>Apagar</Button>
                            </div>
                          </div>
                        ))}
                        {adminUsers.length === 0 && <p className="text-center text-slate-500 py-6">Nenhum usuário</p>}
                      </div>
                    </>
                  )}

                  {adminSubTab === 'savannah' && (
                    <SavannahExpensesPanel language={language} />
                  )}

                  {adminSubTab === 'vendas' && (
                    <SalesUsersPanel language={language} />
                  )}

                  {adminSubTab === 'comparar' && (
                    <CompareTeamsPanel
                      language={language}
                      onOpenMap={(teams, range) => { setCompareMapTeams(teams); setCompareMapRange(range || null); setShowScheduleMapModal(true); }}
                    />
                  )}

                  {adminSubTab === 'dados' && (
                    <>
                      <div className="flex flex-col md:flex-row gap-3 mb-4">
                        <select className="border rounded px-2 py-2" value={selectedPM} onChange={(e) => setSelectedPM(e.target.value)}>
                          {pmOptions.map((pm) => (
                            <option key={pm} value={pm}>{pm}</option>
                          ))}
                        </select>
                        <Input placeholder="Buscar por Job ou Ordem" value={pmSearch} onChange={(e) => setPmSearch(e.target.value)} className="md:flex-1" />
                        <Button variant="outline" onClick={() => loadAdminPMData(selectedPM)} disabled={adminPMLoading}>Recarregar</Button>
                        {selectedPM === 'ADMIN' && (
                          <>
                            <Button 
                              variant="outline" 
                              onClick={cleanupOrphanedLaborBills} 
                              disabled={loading}
                              className="bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-300"
                              title="Mover Labor Bills sem equipe para lixeira do ADMIN"
                            >
                              🧹 Limpar Órfãos
                            </Button>
                            <Button 
                              variant="outline" 
                              onClick={standardizeAllLaborBillDates} 
                              disabled={loading}
                              className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-300"
                              title="Padronizar datas dos Labor Bills neste ambiente"
                            >
                              📅 Alterar Datas
                            </Button>
                            <div className="text-xs text-gray-600 mt-2 p-2 bg-blue-50 rounded">
                              ℹ️ <strong>Para alterar no Deployed:</strong> Use este botão diretamente no ambiente Deployed
                            </div>
                            <Button 
                              variant="outline" 
                              onClick={fixEmailHistorySnapshots} 
                              disabled={loading}
                              className="bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border-yellow-300"
                              title="Corrigir histórico de emails antigos reconstruindo snapshots"
                            >
                              📧 Corrigir Emails
                            </Button>
                            <Button 
                              variant="outline" 
                              onClick={debugEmailHistory} 
                              disabled={loading}
                              className="bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-300"
                              title="Debug: Analisar estrutura dos emails antigos"
                            >
                              🔍 Debug Emails
                            </Button>
                          </>
                        )}
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* Drafts */}
                        <div className="bg-white border rounded-lg p-3">
                          <div className="font-semibold mb-2">Drafts — {selectedPM}</div>
                          <div className="space-y-2 max-h-80 overflow-auto">
                            {adminPMDrafts.filter((b) => (b.job_name||'').toLowerCase().includes(pmSearch.toLowerCase()) || (b.order_number||'').toLowerCase().includes(pmSearch.toLowerCase())).map((b) => (
                              <div key={b.id} className="flex items-center justify-between p-2 border rounded">
                                <div>
                                  <div className="font-medium">{b.job_name || 'Sem nome'}</div>
                                  <div className="text-xs text-slate-600">Ordem: {b.order_number || '-'} | Data: {formatNyDate(b.install_date)}</div>
                                </div>
                                <Button size="sm" variant="outline" onClick={() => adminDownloadPMpdf(selectedPM, b.id)}>
                                  <Download className="h-4 w-4 mr-1" /> PDF
                                </Button>
                              </div>
                            ))}
                            {adminPMDrafts.length === 0 && <p className="text-center text-slate-500 py-4">Sem rascunhos</p>}
                          </div>
                        </div>
                        {/* History */}
                        <div className="bg-white border rounded-lg p-3">
                          <div className="font-semibold mb-2">History — {selectedPM}</div>
                          <div className="space-y-2 max-h-80 overflow-auto">
                            {adminPMHistory.filter((b) => (b.job_name||'').toLowerCase().includes(pmSearch.toLowerCase()) || (b.order_number||'').toLowerCase().includes(pmSearch.toLowerCase())).map((b) => (
                              <div key={b.id} className="flex items-center justify-between p-2 border rounded">
                                <div>

              {/* Confirm dialog - User Trash */}
              <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Confirmar exclusão permanente</DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-slate-600">Esta ação não pode ser desfeita. Digite DELETE para confirmar.</p>
                  <div className="mt-3">
                    <Input placeholder="Digite DELETE" value={confirmDeleteText} onChange={(e) => setConfirmDeleteText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && confirmDeleteText.trim().toUpperCase() === 'DELETE') { confirmHardDelete(); } }} />
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={() => setConfirmDeleteOpen(false)} disabled={confirmDeleting}>Cancelar</Button>
                    <Button className="bg-red-600 hover:bg-red-700" disabled={confirmDeleteText !== 'DELETE' || confirmDeleting} onClick={confirmHardDelete}>{confirmDeleting ? 'Apagando...' : 'Apagar'}</Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Confirm dialog - Admin PM Trash */}
              <Dialog open={adminConfirmDeleteOpen} onOpenChange={setAdminConfirmDeleteOpen}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Confirmar exclusão permanente</DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-slate-600">Esta ação não pode ser desfeita. Digite DELETE para confirmar.</p>
                  <div className="mt-3">
                    <Input placeholder="Digite DELETE" value={adminConfirmDeleteText} onChange={(e) => setAdminConfirmDeleteText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && adminConfirmDeleteText.trim().toUpperCase() === 'DELETE') { confirmAdminHardDelete(); } }} />
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={() => setAdminConfirmDeleteOpen(false)}>Cancelar</Button>
                    <Button className="bg-red-600 hover:bg-red-700" disabled={adminConfirmDeleteText !== 'DELETE'} onClick={confirmAdminHardDelete}>Apagar</Button>
                  </div>
                </DialogContent>
              </Dialog>

                                  <div className="font-medium">{b.job_name}</div>
                                  <div className="text-slate-500 text-xs">Instalação: {formatNyDate(b.install_date)} • Ordem: {b.order_number}</div>
                                  <div className="text-slate-500 text-xs">{language === 'pt-BR' ? 'Enviado em:' : 'Sent on:'} {formatNyDateTime(b.sent_at) || b.sent_at_str || '-'}</div>
                                  <div className="text-slate-400 text-[11px]">{language === 'pt-BR' ? 'Para:' : 'To:'} {(b.last_sent_to_names && b.last_sent_to_names.length > 0) ? b.last_sent_to_names.join(', ') : (b.last_sent_to || []).join(', ')}</div>
                                </div>
                                <Button size="sm" variant="outline" onClick={() => adminDownloadPMpdf(selectedPM, b.id)}>
                                  <Download className="h-4 w-4 mr-1" /> PDF
                                </Button>
                              </div>
                            ))}
                            {adminPMHistory.length === 0 && <p className="text-center text-slate-500 py-4">Sem histórico</p>}
                          </div>
                        </div>
                        {/* Email Logs */}
                        <div className="bg-white border rounded-lg p-3">
                          <div className="font-semibold mb-2">Logs de E-mail — {selectedPM}</div>
                          <div className="space-y-2 max-h-72 overflow-auto">
                            {adminPMEmailLogs.map((lg) => (
                              <div key={lg.id} className="p-2 border rounded text-sm">
                                <div className="flex justify-between items-center">
                                  <div className="font-medium">{lg.subject}</div>
                                  <div className="text-xs text-slate-500">{formatNyDateTime(lg.sent_at) || lg.sent_at_str}</div>
                                </div>
                                <div className="text-xs text-slate-600">{language === 'pt-BR' ? 'Para:' : 'To:'} {(lg.recipients || []).map(r => r.name || r.email).join(', ')}</div>
                                <div className="text-xs text-slate-500">Bill: {lg.bill_id} • Por: {lg.by_user}</div>
                                <div className="text-xs text-slate-500">Job: {lg.snapshot?.job_name} • Order#: {lg.snapshot?.order_number} • Total: ${'{'}(lg.snapshot?.total||0).toLocaleString(){'}'}</div>
                              </div>
                            ))}
                            {adminPMEmailLogs.length === 0 && <p className="text-center text-slate-500 py-4">Sem logs</p>}
                          </div>
                        </div>

                        {/* Trash */}
                        <div className="bg-white border rounded-lg p-3">
                          <div className="flex justify-between items-center mb-2">
                            <div className="font-semibold">Trash — {selectedPM}</div>
                            <div className="flex gap-2">
                              <span className="text-xs text-gray-500">
                                ({adminPMTrash.length} itens)
                              </span>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                                onClick={() => {
                                  console.log(`🗑️ Esvaziar lixeira clicked for ${selectedPM}`);
                                  console.log(`🗑️ adminPMTrash.length: ${adminPMTrash.length}`);
                                  console.log(`🗑️ Setting emptyTrashOpen to true`);
                                  setEmptyTrashOpen(true);
                                }}
                                disabled={adminPMTrash.length === 0}
                              >
                                🗑️ Esvaziar Lixeira
                              </Button>
                            </div>
                          </div>
                          <div className="space-y-2 max-h-80 overflow-auto">
                            {adminPMTrash.filter((b) => (b.job_name||'').toLowerCase().includes(pmSearch.toLowerCase()) || (b.order_number||'').toLowerCase().includes(pmSearch.toLowerCase())).map((b) => (
                              <div key={b.id} className="flex items-center justify-between p-2 border rounded">
                                <div>
                                  <div className="font-medium">{b.job_name || 'Sem nome'}</div>
                                  <div className="text-xs text-slate-600">Ordem: {b.order_number || '-'} | Excluído em: {b.deleted_at ? formatNyDateTime(b.deleted_at) : ''}</div>
                                </div>
                                <div className="flex gap-2">
                                  <Button size="sm" variant="outline" onClick={() => adminRestorePMTrash(selectedPM, b.id)}>Restaurar</Button>
                                  <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => openAdminConfirmDelete(selectedPM, b.id)}>Apagar Definitivamente</Button>
                                </div>
                              </div>
                            ))}
                            {adminPMTrash.length === 0 && <p className="text-center text-slate-500 py-4">Lixeira vazia</p>}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {adminSubTab === 'laborbill' && (
                    <div className="space-y-6" data-testid="admin-laborbill-tab">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800">Labor Bill — Configurações</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Layout <b>"Renewal by Andersen Atlanta Install Labor Bill"</b>. O PDF gerado é <b>preenchível</b> — o supervisor (FOM) edita direto no PDF (dropdowns, checkboxes, assinatura). Preços abaixo (Global / Por PM) alimentam os totais do Labor Bill.
                        </p>
                      </div>

                      {/* Preview do molde (layout enviado ao supervisor) */}
                      <div className="bg-white border rounded-lg p-4" data-testid="laborbill-template-card">
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                          <div className="font-semibold text-slate-800">Layout do Labor Bill (molde enviado ao supervisor)</div>
                          <span className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-3 py-1">🎨 Editor visual — em breve (Fase 2)</span>
                        </div>
                        <img
                          src="/labor_bill_template_preview.png"
                          alt="Molde do Labor Bill"
                          className="w-full max-w-3xl mx-auto border rounded shadow-sm"
                          data-testid="laborbill-template-preview"
                        />
                      </div>

                      {/* Ligação IA → campo do Labor Bill (manual) */}
                      <AiFieldMappingCard onSaved={(m) => setAiFieldMap(m)} />

                      {/* Auto-preenchimento do Crew (FOM) */}
                      <div className="bg-white border rounded-lg p-4" data-testid="laborbill-crew-map-card">
                        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                          <div className="font-semibold text-slate-800">Auto-preenchimento do "Crew Name" (FOM)</div>
                          <Button variant="outline" onClick={saveCrewMapping} disabled={crewLoading} data-testid="laborbill-save-crewmap-btn">
                            Salvar Mapeamento
                          </Button>
                        </div>
                        <p className="text-xs text-gray-600 mb-3">
                          Associe cada PM à sua empresa/crew. O "Crew Name" do PDF será preenchido automaticamente ao gerar o Labor Bill — o FOM só revisa.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[40vh] overflow-auto">
                          {crewPms.map((pm) => (
                            <div key={pm.username} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50">
                              <div className="w-24 shrink-0">
                                <div className="text-sm font-medium">{pm.username}</div>
                                <div className="text-[11px] text-gray-500 truncate">{pm.full_name}</div>
                              </div>
                              <select
                                className="border rounded px-2 py-1.5 text-sm flex-1"
                                value={crewMap[pm.username] || ''}
                                onChange={(e) => setCrewMap((prev) => ({ ...prev, [pm.username]: e.target.value }))}
                                data-testid={`crew-select-${pm.username}`}
                              >
                                <option value="">— nenhum —</option>
                                {crewOptions.map((c) => (
                                  <option key={c} value={c}>{c}</option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Auto-preenchimento do FOM (Assigned + Email) */}
                      <div className="bg-white border rounded-lg p-4" data-testid="laborbill-fom-map-card">
                        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                          <div className="font-semibold text-slate-800">Auto-preenchimento do "FOM Assigned" + "FOM Email"</div>
                          <Button variant="outline" onClick={saveFomMapping} disabled={fomLoading} data-testid="laborbill-save-fommap-btn">
                            Salvar Mapeamento
                          </Button>
                        </div>
                        <p className="text-xs text-gray-600 mb-3">
                          Associe cada <b>supervisor</b> (puxado da Logística) ao FOM Assigned. O <b>FOM Email</b> é puxado automaticamente dos <b>Destinatários</b> (sub-aba Emails) pelo nome — deixe em branco para automático, ou escolha/di­gite para sobrescrever.
                        </p>
                        <datalist id="fom-email-recipients-list">
                          {emailRecipients.map((r) => (
                            <option key={r.id} value={r.email}>{r.name}</option>
                          ))}
                        </datalist>
                        {fomSupervisors.length === 0 ? (
                          <div className="text-sm text-gray-500 py-2">Nenhum supervisor encontrado na Logística ainda.</div>
                        ) : (
                          <div className="space-y-2 max-h-[45vh] overflow-auto">
                            {fomSupervisors.map((sup) => (
                              <div key={sup} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center p-2 rounded hover:bg-slate-50">
                                <div className="sm:col-span-3 text-sm font-medium truncate" title={sup}>{sup}</div>
                                <div className="sm:col-span-4">
                                  <select
                                    className="border rounded px-2 py-1.5 text-sm w-full"
                                    value={fomMap[sup]?.fom_assigned || ''}
                                    onChange={(e) => updateFomEntry(sup, 'fom_assigned', e.target.value)}
                                    data-testid={`fom-assigned-select-${sup}`}
                                  >
                                    <option value="">— FOM Assigned —</option>
                                    {fomOptions.map((f) => (
                                      <option key={f} value={f}>{f}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="sm:col-span-5">
                                  <Input
                                    type="email"
                                    list="fom-email-recipients-list"
                                    placeholder="Auto (dos Destinatários) — ou digite"
                                    value={fomMap[sup]?.fom_email || ''}
                                    onChange={(e) => updateFomEntry(sup, 'fom_email', e.target.value)}
                                    className="h-8 text-sm"
                                    data-testid={`fom-email-input-${sup}`}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Novos FOM personalizados: Nome = FOM Assigned + E-mail */}
                        <div className="mt-4 pt-4 border-t" data-testid="laborbill-custom-foms">
                          <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                            <div className="font-semibold text-slate-800">Novos FOM (Nome = FOM Assigned)</div>
                            <div className="flex items-center gap-2">
                              <Button variant="outline" size="sm" onClick={addCustomFom} data-testid="add-custom-fom-btn">
                                <Plus className="w-4 h-4 mr-1" /> Adicionar FOM
                              </Button>
                              <Button variant="outline" size="sm" onClick={saveCustomFoms} disabled={customFomsLoading} data-testid="save-custom-foms-btn">
                                Salvar Novos FOM
                              </Button>
                            </div>
                          </div>
                          <p className="text-xs text-gray-600 mb-3">
                            Cadastre supervisores que <b>não</b> estão na lista fixa. O <b>Nome</b> vira o <b>FOM Assigned</b> (aparece no PDF) e o <b>E-mail</b> vira o <b>FOM Email</b>. Ao salvar um Labor Bill, eles aparecem para seleção e preenchem os dois campos automaticamente.
                          </p>
                          {customFoms.length === 0 ? (
                            <div className="text-sm text-gray-500 py-2">Nenhum FOM personalizado. Clique em "Adicionar FOM".</div>
                          ) : (
                            <div className="space-y-2 max-h-[45vh] overflow-auto">
                              {customFoms.map((f, idx) => (
                                <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center p-2 rounded hover:bg-slate-50">
                                  <div className="sm:col-span-5">
                                    <Input
                                      placeholder="Nome do FOM (ex.: Smith, John)"
                                      value={f.name || ''}
                                      onChange={(e) => updateCustomFom(idx, 'name', e.target.value)}
                                      className="h-8 text-sm"
                                      data-testid={`custom-fom-name-${idx}`}
                                    />
                                  </div>
                                  <div className="sm:col-span-6">
                                    <Input
                                      type="email"
                                      placeholder="E-mail do FOM"
                                      value={f.email || ''}
                                      onChange={(e) => updateCustomFom(idx, 'email', e.target.value)}
                                      className="h-8 text-sm"
                                      data-testid={`custom-fom-email-${idx}`}
                                    />
                                  </div>
                                  <div className="sm:col-span-1 flex justify-end">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => removeCustomFom(idx)}
                                      data-testid={`remove-custom-fom-${idx}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Tabelas de Preços (movidas de ⚙️ Sistema) */}
                      <div data-testid="laborbill-pricing">
                        <div className="flex items-center gap-3 mb-3">
                          <Button onClick={() => setShowLaborDesigner(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="open-labor-designer-btn">
                            <Settings className="w-4 h-4 mr-1" /> Abrir Designer
                          </Button>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">
                          Gerencie itens, seções e <b>preços</b> do Labor Bill pelo <b>Designer</b>: renomear, reordenar (arraste), mudar preços e criar/remover itens e seções.
                        </p>
                        <div className="bg-white border rounded">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                            <div className="p-3 border-r">
                              <div className="font-semibold mb-2">Tabela de Preços (Esquerda)</div>
                              <div className="space-y-2 max-h-[60vh] overflow-auto">
                                {leftColumnSubgroups.filter(sg => sg.title !== 'Extra Labor').map((sg, i) => (
                                  <div key={i} className="mb-3">
                                    <div className="text-slate-700 font-medium mb-1">{sg.title}</div>
                                    {sg.items.map((it, j) => {
                                      const price = pricingMap[it.description] ?? it.unit_price;
                                      return (
                                        <div key={j} className="grid grid-cols-12 gap-2 items-center p-2 rounded hover:bg-slate-50">
                                          <div className="col-span-7 text-sm">{it.description}</div>
                                          <div className="col-span-2 text-xs text-slate-500">${it.unit_price}</div>
                                          <div className="col-span-3">
                                            <Input type="number" step="0.01" value={price} onChange={(e) => setPricingMap(prev => ({ ...prev, [it.description]: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm" />
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="p-3">
                              <div className="font-semibold mb-2">Tabela de Preços (Direita)</div>
                              <div className="space-y-2 max-h-[60vh] overflow-auto">
                                {rightColumnSubgroups.map((sg, i) => (
                                  <div key={i} className="mb-3">
                                    <div className="text-slate-700 font-medium mb-1">{sg.title}</div>
                                    {sg.items.map((it, j) => (
                                      <div key={j} className="grid grid-cols-12 gap-2 items-center p-2 rounded hover:bg-slate-50">
                                        <div className="col-span-7 text-sm">{it.description}</div>
                                        <div className="col-span-2 text-xs text-slate-500">${it.unit_price}</div>
                                        <div className="col-span-3">
                                          <Input type="number" step="0.01" value={pricingMap[it.description] ?? it.unit_price} onChange={(e) => setPricingMap(prev => ({ ...prev, [it.description]: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm" />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <LaborBillDesigner
                        open={showLaborDesigner}
                        onClose={() => setShowLaborDesigner(false)}
                        apiBase={API}
                        initialTemplate={{ left: leftColumnSubgroups, right: rightColumnSubgroups }}
                        onSaved={(t) => { setLeftColumnSubgroups(t.left); setRightColumnSubgroups(t.right); }}
                      />
                    </div>
                  )}


                  {adminSubTab === 'sistema' && (
                    <>
                      {/* 🛡️ Limpar Duplicatas de Labor Bills */}
                      <DuplicateLaborBillsCleanup />

                      {/* 🔔 Monitoramento automático diário de duplicatas */}
                      <DupCheckSettingsCard />

                      {/* 🤖 Conta demo para revisão do Google Play */}
                      <DemoUserPanel />

                      {/* 🗑️ Reset de fábrica de equipe */}
                      <TeamResetPanel />

                      {/* 🔌 Status das Integrações e Custos */}
                      <IntegrationsStatusCard API={API} />

                      {/* 🌎 Migração de Fuso Horário (New York) — uso único */}
                      <div className="bg-blue-50 border-2 border-blue-400 rounded-lg p-4 mb-4" data-testid="tz-migration-card">
                        <h4 className="font-bold text-blue-900 mb-1 flex items-center gap-2">🌎 Padronizar Fuso Horário (New York)</h4>
                        <p className="text-sm text-gray-700 mb-3">
                          Converte <b>todos os registros antigos</b> do sistema para o fuso de <b>New York (America/New_York)</b> e o formato <b>mm/dd/yyyy</b>.
                          É <b>seguro rodar mais de uma vez</b> (idempotente) e <b>não altera datas "só data"</b>. Recomendo clicar em <b>Prévia</b> antes de aplicar.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            data-testid="tz-migration-preview-btn"
                            variant="outline"
                            disabled={tzMigLoading}
                            className="border-blue-400 text-blue-800 hover:bg-blue-100"
                            onClick={async () => {
                              try {
                                setTzMigLoading(true); setTzMigResult(null);
                                const { data } = await axios.post(`${API}/admin/migrate-timezone-ny?dry_run=true`);
                                setTzMigResult(data);
                                toast.success(`Prévia: ${data.total_docs_affected} registro(s) seriam ajustados`);
                              } catch (e) {
                                toast.error('Erro na prévia: ' + (e.response?.data?.detail || 'Falha'));
                              } finally { setTzMigLoading(false); }
                            }}
                          >
                            {tzMigLoading ? '⏳ Processando...' : '🔍 Prévia (não altera nada)'}
                          </Button>
                          <Button
                            data-testid="tz-migration-apply-btn"
                            disabled={tzMigLoading}
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={async () => {
                              if (!window.confirm('Aplicar a migração de fuso horário em TODOS os registros? É seguro e pode ser repetido, mas altera dados. Recomendo ter feito a Prévia antes.')) return;
                              try {
                                setTzMigLoading(true);
                                const { data } = await axios.post(`${API}/admin/migrate-timezone-ny?dry_run=false`);
                                setTzMigResult(data);
                                toast.success(`✅ Migração aplicada: ${data.total_docs_affected} registro(s) ajustados`);
                              } catch (e) {
                                toast.error('Erro na migração: ' + (e.response?.data?.detail || 'Falha'));
                              } finally { setTzMigLoading(false); }
                            }}
                          >
                            {tzMigLoading ? '⏳ Aplicando...' : '⚡ Aplicar Migração (uso único)'}
                          </Button>
                        </div>
                        {tzMigResult && (
                          <div className="mt-3 bg-white border rounded p-3 text-sm" data-testid="tz-migration-result">
                            <div className="font-semibold mb-1">
                              {tzMigResult.dry_run ? '🔍 Prévia' : '✅ Aplicado'} · {tzMigResult.collections_scanned} coleções verificadas
                            </div>
                            <div className="text-gray-700">
                              <b>{tzMigResult.total_docs_affected}</b> registro(s) · <b>{tzMigResult.total_fields_affected}</b> campo(s) {tzMigResult.dry_run ? 'seriam ajustados' : 'ajustados'}.
                            </div>
                            {(tzMigResult.by_collection || []).length > 0 && (
                              <div className="mt-2 max-h-40 overflow-y-auto text-xs text-gray-600">
                                {tzMigResult.by_collection.map((c, i) => (
                                  <div key={i}>• {c.collection}: {c.docs_changed ?? '—'} reg / {c.fields_changed ?? '—'} campos {c.error ? `(erro: ${c.error})` : ''}</div>
                                ))}
                              </div>
                            )}
                            {tzMigResult.total_docs_affected === 0 && !tzMigResult.dry_run && (
                              <div className="text-green-700 mt-1">Tudo já estava no fuso de New York. ✅</div>
                            )}
                          </div>
                        )}
                      </div>


                      {/* Botão temporário de migração */}
                      <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-4">
                        <h4 className="font-semibold mb-2">Migração de Dados</h4>
                        <p className="text-sm text-gray-600 mb-3">Corrige full_name dos usuários (usando first_name + last_name) e atualiza nomes em todas as notificações e logs antigos</p>
                        <Button
                          onClick={() => setMigratingNames(true)}
                          className="bg-yellow-600 hover:bg-yellow-700"
                          data-testid="migrate-names-btn"
                        >
                          Atualizar Nomes nos Logs
                        </Button>
                      </div>

                      {/* Botão temporário - Corrigir Order Numbers */}
                      <div className="bg-orange-50 border border-orange-300 rounded-lg p-4 mb-4">
                        <h4 className="font-semibold mb-2">Corrigir Order Numbers</h4>
                        <p className="text-sm text-gray-600 mb-3">Remove zeros à esquerda de todos os Order Numbers (ex: #0467865 → #467865)</p>
                        <Button
                          onClick={async () => {
                            try {
                              const { data } = await axios.post(`${API}/admin/fix-order-numbers`);
                              toast.success(`${data.message}`);
                            } catch (e) {
                              toast.error('Erro: ' + (e.response?.data?.detail || 'Falha'));
                            }
                          }}
                          className="bg-orange-600 hover:bg-orange-700"
                        >
                          Corrigir Order Numbers
                        </Button>
                      </div>

                      {/* Modal de migração */}
                      <Dialog open={migratingNames} onOpenChange={() => {}}>
                        <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
                          <DialogHeader>
                            <DialogTitle>Atualizando Nomes</DialogTitle>
                          </DialogHeader>
                          {migrationResult ? (
                            <div className="space-y-3">
                              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                                <p className="font-semibold text-green-800">Migração concluída!</p>
                                <p className="text-sm">Usuários corrigidos: <b>{migrationResult.users_fixed}</b></p>
                                <p className="text-sm">Notificações atualizadas: <b>{migrationResult.notifications_updated}</b></p>
                                <p className="text-sm">Logs atualizados: <b>{migrationResult.activity_logs_updated}</b></p>
                              </div>
                              <Button className="w-full" onClick={() => { setMigratingNames(false); setMigrationResult(null); }}>
                                Fechar
                              </Button>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center py-8 gap-4">
                              <Loader2 className="h-10 w-10 animate-spin text-yellow-600" />
                              <p className="text-sm text-gray-600">{migrationStep}</p>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-sm text-blue-800">
                        As <b>Tabelas de Preços</b> do Labor Bill foram movidas para a aba <b>📄 Labor Bill</b>.
                      </div>

                      {/* Dropbox Automatic Backup Section */}
                      <div className="mt-6 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="font-semibold text-lg text-green-800 flex items-center gap-2">
                              📦 Backup Automático Dropbox
                            </h3>
                            <p className="text-sm text-green-700 mt-1">
                              Backup completo diário às 12:00 AM com rotação de 3 anos | 2TB disponível
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              onClick={async () => {
                                try {
                                  const { data } = await axios.get(`${API}/admin/backup/dropbox-status`);
                                  alert(JSON.stringify(data, null, 2));
                                } catch (e) {
                                  toast.error('Erro ao verificar status');
                                }
                              }}
                              variant="outline"
                              size="sm"
                            >
                              📊 Ver Status Dropbox
                            </Button>
                            <Button 
                              onClick={async () => {
                                try {
                                  const { data } = await axios.get(`${API}/admin/backup/scheduler-status`);
                                  
                                  const message = `
🕐 STATUS DO SCHEDULER DE BACKUP AUTOMÁTICO

${data.enabled ? '✅ SCHEDULER ATIVO' : '❌ SCHEDULER INATIVO'}

${data.enabled ? `
📅 Próxima Execução: ${data.next_run_time || 'N/A'}
🔧 Trigger: ${data.trigger || 'N/A'}
📋 Job: ${data.job_name || 'N/A'}

Configurações do MongoDB:
- Horário: ${data.settings_from_mongodb?.backup_hour || '?'}:${String(data.settings_from_mongodb?.backup_minute || '0').padStart(2, '0')}
- Frequência: ${data.settings_from_mongodb?.backup_frequency || '?'}
- Retenção: ${data.settings_from_mongodb?.retention_days || '?'} dias
- Timezone: ${data.settings_from_mongodb?.backup_timezone || '?'}
` : `
Mensagem: ${data.message}

O scheduler pode estar desativado porque:
- Não está em ambiente PRODUCTION
- AUTO_BACKUP_ENABLED não está configurado como true
- Backend não foi reiniciado após configuração
`}
                                  `.trim();
                                  
                                  alert(message);
                                  console.log('📊 Scheduler Status:', data);
                                  
                                } catch (e) {
                                  toast.error('Erro ao verificar scheduler: ' + (e.response?.data?.detail || e.message));
                                  console.error('Erro:', e);
                                }
                              }}
                              variant="outline"
                              size="sm"
                              className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-300"
                            >
                              🕐 Ver Status Scheduler
                            </Button>
                            <Button 
                              onClick={() => setShowDropboxTokenModal(true)}
                              variant="outline"
                              size="sm"
                              className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                            >
                              🔑 Atualizar Token
                            </Button>
                            <Button 
                              onClick={() => setShowDropboxOAuthModal(true)}
                              variant="outline"
                              size="sm"
                              className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                            >
                              🔐 Configurar OAuth (Sem Expiração)
                            </Button>
                          </div>
                        </div>
                        
                        <div className="bg-white rounded-lg p-4 mb-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-800">📋 Configurações</h4>
                            {!isEditingBackupSettings ? (
                              <Button 
                                onClick={() => setIsEditingBackupSettings(true)}
                                variant="outline"
                                size="sm"
                              >
                                ✏️ Editar
                              </Button>
                            ) : (
                              <div className="flex gap-2">
                                <Button 
                                  onClick={() => {
                                    setIsEditingBackupSettings(false);
                                    loadBackupSettings(); // Recarregar valores originais
                                  }}
                                  variant="outline"
                                  size="sm"
                                  disabled={isSavingBackupSettings}
                                >
                                  Cancelar
                                </Button>
                                <Button 
                                  onClick={saveBackupSettings}
                                  size="sm"
                                  disabled={isSavingBackupSettings}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  {isSavingBackupSettings ? '⏳ Salvando...' : '✅ Salvar'}
                                </Button>
                              </div>
                            )}
                          </div>
                          
                          <div className="space-y-3 text-sm">
                            {/* Agendamento */}
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Agendamento:</span>
                              {!isEditingBackupSettings ? (
                                <span className="font-medium">
                                  {backupSettings.backup_frequency === 'daily' ? 'Diário' : 
                                   backupSettings.backup_frequency === 'weekly' ? 'Semanal' : 'Mensal'} às {String(backupSettings.backup_hour).padStart(2, '0')}:{String(backupSettings.backup_minute).padStart(2, '0')} {backupSettings.backup_period}
                                </span>
                              ) : (
                                <div className="flex gap-2 items-center flex-wrap">
                                  <select
                                    value={backupSettings.backup_frequency}
                                    onChange={(e) => setBackupSettings({...backupSettings, backup_frequency: e.target.value})}
                                    className="border rounded px-2 py-1 text-sm"
                                  >
                                    <option value="daily">Diário</option>
                                    <option value="weekly">Semanal</option>
                                    <option value="monthly">Mensal</option>
                                  </select>
                                  <span className="text-xs">às</span>
                                  <Input
                                    type="number"
                                    min="1"
                                    max="12"
                                    value={backupSettings.backup_hour}
                                    onChange={(e) => setBackupSettings({...backupSettings, backup_hour: e.target.value})}
                                    className="w-14 h-8 text-center"
                                    placeholder="HH"
                                  />
                                  <span>:</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    max="59"
                                    value={backupSettings.backup_minute}
                                    onChange={(e) => setBackupSettings({...backupSettings, backup_minute: e.target.value})}
                                    className="w-14 h-8 text-center"
                                    placeholder="MM"
                                  />
                                  <select
                                    value={backupSettings.backup_period}
                                    onChange={(e) => setBackupSettings({...backupSettings, backup_period: e.target.value})}
                                    className="border rounded px-2 py-1 text-sm"
                                  >
                                    <option value="AM">AM</option>
                                    <option value="PM">PM</option>
                                  </select>
                                  <span className="text-xs text-gray-500">NY</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Fuso Horário */}
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Fuso Horário:</span>
                              <span className="font-medium text-sm">🗽 Nova York (EST/EDT)</span>
                            </div>
                            
                            {/* Conteúdo (não editável) */}
                            <div className="flex justify-between">
                              <span className="text-gray-600">Conteúdo:</span>
                              <span className="font-medium">MongoDB + GridFS + Código</span>
                            </div>
                            
                            {/* Rotação */}
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Rotação:</span>
                              {!isEditingBackupSettings ? (
                                <span className="font-medium">
                                  {Math.floor(parseInt(backupSettings.retention_days) / 365)} anos ({backupSettings.retention_days} dias)
                                </span>
                              ) : (
                                <div className="flex gap-2 items-center">
                                  <Input
                                    type="number"
                                    min="1"
                                    max="3650"
                                    value={backupSettings.retention_days}
                                    onChange={(e) => setBackupSettings({...backupSettings, retention_days: e.target.value})}
                                    className="w-24 h-8 text-center"
                                  />
                                  <span className="text-xs">dias</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Organização */}
                            <div className="flex justify-between">
                              <span className="text-gray-600">Organização:</span>
                              <span className="font-medium">Pasta por dia (YYYY-MM-DD)</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                          <h4 className="font-medium mb-2 text-yellow-800">✅ Dropbox Configurado!</h4>
                          <p className="text-sm text-yellow-700 mb-3">
                            Sistema pronto para fazer backups automáticos no Dropbox (2TB disponível)
                          </p>
                          <div className="bg-white rounded p-3 text-xs space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="text-green-600 font-bold">✓</span>
                              <span>Access Token configurado</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-green-600 font-bold">✓</span>
                              <span>Pasta: /PM_Backups (criada automaticamente)</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-green-600 font-bold">✓</span>
                              <span>Espaço: 2TB (muito espaço!)</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Histórico de Backups */}
                        <div className="bg-white border rounded-lg p-4 mb-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-800">📊 Histórico de Backups</h4>
                            <Button
                              onClick={loadBackupHistory}
                              variant="outline"
                              size="sm"
                              disabled={loadingBackupHistory}
                            >
                              {loadingBackupHistory ? '⏳ Carregando...' : '🔄 Atualizar'}
                            </Button>
                          </div>
                          
                          {loadingBackupHistory ? (
                            <div className="text-center py-4 text-gray-500">
                              Carregando histórico...
                            </div>
                          ) : backupHistory.length === 0 ? (
                            <div className="text-center py-4 text-gray-500">
                              Nenhum backup realizado ainda
                            </div>
                          ) : (
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {backupHistory.slice(0, 10).map((backup, idx) => (
                                <div 
                                  key={idx}
                                  className="bg-gray-50 rounded p-3 text-sm border border-gray-200 hover:bg-gray-100 transition"
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2">
                                      <span className={`font-bold ${backup.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                        {backup.status === 'success' ? '✅' : '❌'}
                                      </span>
                                      <span className="font-medium">
                                        {new Date(backup.date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })} - {new Date(backup.date).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true })}
                                      </span>
                                    </div>
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                      backup.environment === 'Production' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                                    }`}>
                                      {backup.environment}
                                    </span>
                                  </div>
                                  
                                  <div className="grid grid-cols-3 gap-2 text-xs text-gray-600">
                                    <div>
                                      <span className="font-medium">Tamanho:</span> {(backup.zip_size / 1024 / 1024).toFixed(2)} MB
                                    </div>
                                    <div>
                                      <span className="font-medium">Duração:</span> {backup.duration || 'N/A'}
                                    </div>
                                    <div>
                                      <span className="font-medium">GridFS:</span> {backup.gridfs_synced || 0} arquivos
                                    </div>
                                  </div>
                                  
                                  {backup.error && (
                                    <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                                      ❌ {backup.error}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          <Button 
                            onClick={startBackupWithProgress}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            ▶️ Executar Backup Agora
                          </Button>
                          
                          <Button 
                            onClick={async () => {
                              console.log('🔌 Testando conexão Dropbox...');
                              console.log('⏰ Horário:', formatNyDateTime());
                              try {
                                const { data } = await axios.post(`${API}/admin/backup/test-dropbox`);
                                console.log('✅ CONEXÃO DROPBOX SUCESSO!');
                                console.log('📊 Resposta:', data);
                                console.log('📦 Backups encontrados:', data.backups_found);
                                if (data.backups && data.backups.length > 0) {
                                  console.log('📋 Primeiros backups:', data.backups);
                                }
                                toast.success(`✅ Conexão OK! ${data.backups_found} backups encontrados`);
                              } catch (e) {
                                console.error('❌ ERRO NA CONEXÃO DROPBOX:', e);
                                console.error('📋 Detalhes do erro:', e.response?.data || e.message);
                                toast.error(getErrMsg(e, 'Falha na conexão Dropbox'));
                              }
                            }}
                            variant="outline"
                          >
                            🔌 Testar Conexão Dropbox
                          </Button>
                          
                          <Button 
                            onClick={() => {
                              setShowRestoreModal(true);
                              loadAvailableBackups();
                            }}
                            variant="outline"
                            className="bg-blue-50 hover:bg-blue-100"
                          >
                            ⬇️ Restaurar Backup
                          </Button>
                        </div>
                        
                        <div className="mt-4 text-xs text-gray-600 bg-white rounded p-3">
                          <p className="font-semibold mb-1">ℹ️ Como funciona:</p>
                          <ul className="list-disc list-inside space-y-1">
                            <li>Backup completo criado todo dia às 12:00 AM automaticamente</li>
                            <li>Estrutura: PM_Backups/2025-11-08/backup_2025-11-08_000000.zip</li>
                            <li>Inclui: Todas collections MongoDB + Todos arquivos GridFS + Código fonte</li>
                            <li>Após 3 anos, pastas antigas são deletadas automaticamente (rotação)</li>
                            <li>Use "Executar Backup Agora" para testar antes de ativar automático</li>
                          </ul>
                        </div>
                      </div>
                      
                      {/* Modal: Restaurar Backup */}
                      <Dialog open={showRestoreModal} onOpenChange={setShowRestoreModal}>
                        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>⬇️ Restaurar Backup do Dropbox</DialogTitle>
                          </DialogHeader>
                          
                          <div className="space-y-4">
                            {/* Seleção de Backup */}
                            <div>
                              <Label className="font-semibold mb-2 block">📅 Selecione o Backup</Label>
                              {availableBackups.length === 0 ? (
                                <p className="text-sm text-gray-600">Carregando backups disponíveis...</p>
                              ) : (
                                <select
                                  value={selectedBackupDate}
                                  onChange={(e) => {
                                    setSelectedBackupDate(e.target.value);
                                    const backup = availableBackups.find(b => b.date === e.target.value);
                                    if (backup && backup.files.length > 0) {
                                      setSelectedBackupFile(backup.files[0].name);
                                    }
                                  }}
                                  className="w-full border rounded px-3 py-2"
                                >
                                  <option value="">-- Selecione uma data --</option>
                                  {availableBackups.map(backup => (
                                    <option key={backup.date} value={backup.date}>
                                      📦 {backup.date} ({backup.total_files} arquivo(s))
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                            
                            {/* Mostrar arquivos do backup selecionado */}
                            {selectedBackupDate && (
                              <div className="bg-gray-50 rounded p-3">
                                <p className="text-sm font-medium mb-2">Arquivo(s) neste backup:</p>
                                {availableBackups.find(b => b.date === selectedBackupDate)?.files.map(file => (
                                  <label key={file.name} className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded cursor-pointer">
                                    <input
                                      type="radio"
                                      name="backup_file"
                                      checked={selectedBackupFile === file.name}
                                      onChange={() => setSelectedBackupFile(file.name)}
                                    />
                                    <div className="flex-1">
                                      <div className="text-sm font-medium">{file.name}</div>
                                      <div className="text-xs text-gray-600">
                                        {(file.size / (1024*1024)).toFixed(2)} MB • {file.modified ? formatNyDateTime(file.modified) : ''}
                                      </div>
                                    </div>
                                  </label>
                                ))}
                              </div>
                            )}
                            
                            {/* Opções de Restauração */}
                            {selectedBackupFile && (
                              <div className="border-t pt-4">
                                <Label className="font-semibold mb-3 block">👥 O que Restaurar?</Label>
                                
                                <div className="space-y-3">
                                  <label className="flex items-center gap-2 p-3 border rounded hover:bg-gray-50 cursor-pointer">
                                    <input
                                      type="radio"
                                      checked={restoreAllUsers}
                                      onChange={() => setRestoreAllUsers(true)}
                                    />
                                    <div>
                                      <div className="font-medium">🌐 Restaurar TODOS os Usuários</div>
                                      <div className="text-xs text-gray-600">Restaura todos os dados do backup (recomendado)</div>
                                    </div>
                                  </label>
                                  
                                  <label className="flex items-center gap-2 p-3 border rounded hover:bg-gray-50 cursor-pointer">
                                    <input
                                      type="radio"
                                      checked={!restoreAllUsers}
                                      onChange={() => setRestoreAllUsers(false)}
                                    />
                                    <div>
                                      <div className="font-medium">👤 Restaurar Usuários Específicos</div>
                                      <div className="text-xs text-gray-600">Escolha quais PMs restaurar</div>
                                    </div>
                                  </label>
                                </div>
                                
                                {/* Seleção de Usuários */}
                                {!restoreAllUsers && (
                                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                                    <Label className="text-sm mb-2 block">Selecione os PMs:</Label>
                                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                      {pmOptions.map(pm => (
                                        <label key={pm} className="flex items-center gap-2 text-sm">
                                          <input
                                            type="checkbox"
                                            checked={selectedUsersForRestore.includes(pm)}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setSelectedUsersForRestore([...selectedUsersForRestore, pm]);
                                              } else {
                                                setSelectedUsersForRestore(selectedUsersForRestore.filter(u => u !== pm));
                                              }
                                            }}
                                          />
                                          {pm}
                                        </label>
                                      ))}
                                    </div>
                                    <p className="text-xs text-gray-600 mt-2">
                                      {selectedUsersForRestore.length} usuário(s) selecionado(s)
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Botões */}
                            <div className="flex gap-2 pt-4 border-t">
                              <Button 
                                onClick={restoreBackup}
                                disabled={!selectedBackupFile || (!restoreAllUsers && selectedUsersForRestore.length === 0)}
                                className="flex-1 bg-blue-600 hover:bg-blue-700"
                              >
                                ⬇️ Restaurar Agora
                              </Button>
                              <Button onClick={() => setShowRestoreModal(false)} variant="outline">
                                Cancelar
                              </Button>
                            </div>
                            
                            <div className="bg-red-50 border border-red-200 rounded p-3">
                              <p className="text-xs text-red-700">
                                ⚠️ <strong>ATENÇÃO:</strong> A restauração vai SUBSTITUIR os dados atuais pelos do backup.
                                Certifique-se de escolher o backup correto antes de prosseguir.
                              </p>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                      
                      {/* Modal: Atualizar Token Dropbox */}
                      <Dialog open={showDropboxTokenModal} onOpenChange={setShowDropboxTokenModal}>
                        <DialogContent className="max-w-xl">
                          <DialogHeader>
                            <DialogTitle>🔑 Atualizar Token do Dropbox</DialogTitle>
                          </DialogHeader>
                          
                          <div className="space-y-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <h4 className="font-medium text-blue-900 mb-2">📋 Como Gerar um Novo Token:</h4>
                              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                                <li>Acesse: <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noopener noreferrer" className="underline">https://www.dropbox.com/developers/apps</a></li>
                                <li>Selecione seu app existente ou crie um novo</li>
                                <li>Vá na aba "Settings"</li>
                                <li>Role até "OAuth 2" e clique em "Generate" no campo "Generated access token"</li>
                                <li>Copie o token gerado (começa com <code className="bg-blue-100 px-1 rounded">sl.</code>)</li>
                              </ol>
                            </div>
                            
                            <div>
                              <Label className="font-semibold mb-2 block">🔐 Novo Token do Dropbox:</Label>
                              <Textarea
                                value={newDropboxToken}
                                onChange={(e) => setNewDropboxToken(e.target.value)}
                                placeholder="sl.xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx..."
                                className="font-mono text-xs min-h-[120px]"
                              />
                              <p className="text-xs text-gray-500 mt-2">
                                ⚠️ Cole o token completo gerado no Dropbox App Console
                              </p>
                            </div>
                            
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                              <p className="text-sm text-yellow-800">
                                <strong>⚠️ Importante:</strong> Após atualizar o token, o sistema irá reiniciar o backend automaticamente para aplicar a mudança.
                              </p>
                            </div>
                          </div>
                          
                          <DialogFooter>
                            <Button 
                              variant="outline" 
                              onClick={() => {
                                setShowDropboxTokenModal(false);
                                setNewDropboxToken('');
                              }}
                              disabled={isUpdatingToken}
                            >
                              Cancelar
                            </Button>
                            <Button 
                              onClick={updateDropboxToken}
                              disabled={isUpdatingToken || !newDropboxToken.trim()}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              {isUpdatingToken ? '⏳ Atualizando...' : '✅ Atualizar Token'}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>

                      
                      {/* Modal: Configurar Dropbox OAuth (Refresh Token) */}
                      <Dialog open={showDropboxOAuthModal} onOpenChange={setShowDropboxOAuthModal}>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>🔐 Configurar Dropbox OAuth (Sem Expiração)</DialogTitle>
                          </DialogHeader>
                          
                          <div className="space-y-4">
                            {/* Step 1: Credenciais */}
                            {oauthStep === 1 && (
                              <>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                  <h4 className="font-medium text-green-900 mb-2">✅ Passo 1: App Key e App Secret</h4>
                                  <p className="text-sm text-green-800 mb-2">
                                    Você forneceu essas credenciais! Vamos salvá-las no sistema.
                                  </p>
                                </div>
                                
                                <div>
                                  <Label className="font-semibold mb-2 block">🔑 App Key:</Label>
                                  <Input
                                    value={dropboxAppKey}
                                    onChange={(e) => setDropboxAppKey(e.target.value)}
                                    placeholder="jaydqg6um12gyzm"
                                    className="font-mono"
                                  />
                                </div>
                                
                                <div>
                                  <Label className="font-semibold mb-2 block">🔐 App Secret:</Label>
                                  <Input
                                    type="password"
                                    value={dropboxAppSecret}
                                    onChange={(e) => setDropboxAppSecret(e.target.value)}
                                    placeholder="oq297pk1ttd100d"
                                    className="font-mono"
                                  />
                                </div>
                                
                                <Button 
                                  onClick={saveDropboxOAuthCredentials}
                                  disabled={oauthLoading || !dropboxAppKey.trim() || !dropboxAppSecret.trim()}
                                  className="w-full"
                                >
                                  {oauthLoading ? '⏳ Salvando...' : '✅ Salvar Credenciais'}
                                </Button>
                              </>
                            )}
                            
                            {/* Step 2: Gerar Authorization URL */}
                            {oauthStep === 2 && (
                              <>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                  <h4 className="font-medium text-blue-900 mb-2">✅ Passo 2: Gerar Link de Autorização</h4>
                                  <p className="text-sm text-blue-800">
                                    Clique no botão abaixo para gerar o link de autorização do Dropbox.
                                  </p>
                                </div>
                                
                                <Button 
                                  onClick={generateDropboxAuthUrl}
                                  disabled={oauthLoading}
                                  className="w-full"
                                >
                                  {oauthLoading ? '⏳ Gerando...' : '🔗 Gerar Link de Autorização'}
                                </Button>
                              </>
                            )}
                            
                            {/* Step 3: Autorizar e Trocar Código */}
                            {oauthStep === 3 && (
                              <>
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                  <h4 className="font-medium text-yellow-900 mb-2">✅ Passo 3: Autorizar o Aplicativo</h4>
                                  <ol className="text-sm text-yellow-800 space-y-2 list-decimal list-inside">
                                    <li>Clique no link abaixo para abrir o Dropbox</li>
                                    <li>Faça login e autorize o aplicativo</li>
                                    <li>Copie o código de autorização exibido</li>
                                    <li>Cole o código aqui e finalize</li>
                                  </ol>
                                </div>
                                
                                <div className="bg-white border rounded-lg p-3">
                                  <Label className="font-semibold mb-2 block">🔗 Link de Autorização:</Label>
                                  <div className="flex gap-2">
                                    <Input
                                      value={dropboxAuthUrl}
                                      readOnly
                                      className="font-mono text-xs"
                                    />
                                    <Button 
                                      onClick={() => {
                                        window.open(dropboxAuthUrl, '_blank');
                                        toast.success('Link aberto em nova aba');
                                      }}
                                      size="sm"
                                    >
                                      🔗 Abrir
                                    </Button>
                                  </div>
                                </div>
                                
                                <div>
                                  <Label className="font-semibold mb-2 block">📋 Código de Autorização:</Label>
                                  <Textarea
                                    value={dropboxAuthCode}
                                    onChange={(e) => setDropboxAuthCode(e.target.value)}
                                    placeholder="Cole aqui o código que o Dropbox exibiu..."
                                    className="font-mono min-h-[100px]"
                                  />
                                </div>
                                
                                <Button 
                                  onClick={exchangeDropboxCode}
                                  disabled={oauthLoading || !dropboxAuthCode.trim()}
                                  className="w-full bg-green-600 hover:bg-green-700"
                                >
                                  {oauthLoading ? '⏳ Configurando...' : '🎉 Finalizar Configuração'}
                                </Button>
                              </>
                            )}
                            
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                              <p className="text-sm text-green-800">
                                <strong>✅ Vantagem:</strong> Com OAuth configurado, o token nunca mais expirará e os backups funcionarão automaticamente sem intervenção manual!
                              </p>
                            </div>
                          </div>
                          
                          <DialogFooter>
                            <Button 
                              variant="outline" 
                              onClick={() => {
                                setShowDropboxOAuthModal(false);
                                setOauthStep(1);
                                setDropboxAppKey('');
                                setDropboxAppSecret('');
                                setDropboxAuthUrl('');
                                setDropboxAuthCode('');
                              }}
                              disabled={oauthLoading}
                            >
                              Cancelar
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>

                      
                      {/* Modal: Preview de E-mail */}
                      <Dialog open={showEmailPreviewModal} onOpenChange={setShowEmailPreviewModal}>
                        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>👁️ Preview do E-mail</DialogTitle>
                          </DialogHeader>
                          
                          <div className="space-y-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <p className="text-sm text-blue-800">
                                📧 <strong>Assunto:</strong> {emailDefaults.subject
                                  .replace(/{JOB_NAME}/g, 'Casa do João Silva')
                                  .replace(/{ORDER_NUMBER}/g, '04406982')
                                  .replace(/{INSTALL_DATE}/g, '11/11/2025')
                                  .replace(/{PM_CODE}/g, 'PM1')}
                              </p>
                            </div>
                            
                            <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
                              <div className="bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700">
                                Como o cliente verá no email:
                              </div>
                              <div 
                                className="p-6 bg-white"
                                style={{ minHeight: '400px' }}
                                dangerouslySetInnerHTML={{
                                  __html: emailDefaults.body_html
                                    .replace(/{JOB_NAME}/g, 'Casa do João Silva')
                                    .replace(/{ORDER_NUMBER}/g, '04406982')
                                    .replace(/{INSTALL_DATE}/g, '11/11/2025')
                                    .replace(/{PM_CODE}/g, 'PM1')
                                    .replace(/{TOTAL_WINDOWS}/g, '5')
                                    .replace(/{TOTAL_DOORS}/g, '2')
                                    .replace(/{TOTAL_AMOUNT}/g, '$1,250.00')
                                }}
                              />
                            </div>
                            
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                              ℹ️ Os valores mostrados são exemplos. No email real, serão substituídos pelos dados do Labor Bill.
                            </div>
                          </div>
                          
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setShowEmailPreviewModal(false)}>
                              Fechar
                            </Button>
                          </DialogFooter>

                      
                      {/* Modal: Upload de Imagem para Email */}
                      <Dialog open={showEmailImageUploadModal} onOpenChange={setShowEmailImageUploadModal}>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle>🖼️ Inserir Imagem no Email</DialogTitle>
                          </DialogHeader>
                          
                          <div className="space-y-4">
                            <div>
                              <Label>Escolha a imagem:</Label>
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => setEmailImageFile(e.target.files[0])}
                                className="mt-2"
                              />
                            </div>
                            
                            {emailImageFile && (
                              <div className="bg-gray-50 p-3 rounded border">
                                <p className="text-sm"><strong>Arquivo:</strong> {emailImageFile.name}</p>
                                <p className="text-xs text-gray-600">Tamanho: {(emailImageFile.size / 1024).toFixed(2)} KB</p>
                              </div>
                            )}
                            
                            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
                              💡 A imagem será salva no File Manager e inserida no email automaticamente.
                            </div>
                          </div>
                          
                          <DialogFooter>
                            <Button 
                              variant="outline" 
                              onClick={() => {
                                setShowEmailImageUploadModal(false);
                                setEmailImageFile(null);
                              }}
                              disabled={emailImageUploading}
                            >
                              Cancelar
                            </Button>
                            <Button 
                              onClick={uploadEmailImage}
                              disabled={emailImageUploading || !emailImageFile}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              {emailImageUploading ? '⏳ Enviando...' : '✅ Inserir Imagem'}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>

                        </DialogContent>
                      </Dialog>
                    </>
                  )}

                  {adminSubTab === 'videos' && (
                    <div className="space-y-6">
                      {/* Toggle Vídeo de Entrada */}
                      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4 sm:p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg mb-1">🎬 Vídeo de Entrada</h3>
                            <p className="text-sm text-gray-600">
                              {language === 'pt-BR' 
                                ? 'Exibir vídeo animado ao fazer login no sistema' 
                                : 'Show animated video when logging into the system'}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-700">
                              {splashScreenEnabled ? (language === 'pt-BR' ? 'Ativado' : 'Enabled') : (language === 'pt-BR' ? 'Desativado' : 'Disabled')}
                            </span>
                            <button
                              onClick={async () => {
                                const newValue = !splashScreenEnabled;
                                
                                // Atualizar estado do toggle (NÃO dispara vídeo)
                                setSplashScreenEnabled(newValue);
                                
                                // Salvar no localStorage
                                localStorage.setItem('splash_screen_enabled', newValue ? 'true' : 'false');
                                
                                // Salvar no backend
                                try {
                                  await axios.post(`${API}/admin/settings/splash-screen`, {
                                    enabled: newValue
                                  });
                                  toast.success(
                                    newValue 
                                      ? (language === 'pt-BR' ? '✅ Vídeo de entrada ativado para próximos logins' : '✅ Splash screen enabled for next logins')
                                      : (language === 'pt-BR' ? '❌ Vídeo de entrada desativado para próximos logins' : '❌ Splash screen disabled for next logins')
                                  );
                                } catch (error) {
                                  console.error('Erro ao salvar configuração:', error);
                                  toast.error(language === 'pt-BR' ? 'Erro ao salvar' : 'Error saving');
                                }
                              }}
                              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                                splashScreenEnabled ? 'bg-blue-600' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                                  splashScreenEnabled ? 'translate-x-7' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Adicionar Novo Vídeo - Mobile Optimized */}
                      <div className="bg-white border rounded-lg p-4 sm:p-6 mobile-card">
                        <h3 className="font-semibold text-lg mb-4 mobile-card-title">🎬 Gerenciar Vídeos de Entrada</h3>
                        
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4 mobile-spacing-md">
                          <h4 className="font-medium text-blue-800 mb-2 mobile-subtitle">📝 Como adicionar vídeo:</h4>
                          <ol className="text-sm text-blue-700 space-y-1 mobile-text">
                            <li>1. Selecione arquivo MP4 do seu computador</li>
                            <li>2. Dê um nome para o vídeo</li>
                            <li>3. Clique em "Enviar Vídeo"</li>
                            <li>4. O vídeo será selecionado aleatoriamente na entrada do site</li>
                          </ol>
                        </div>
                        
                        <div className="space-y-4 mobile-form-group">
                          <div>
                            <Label className="mobile-text">Nome do Vídeo</Label>
                            <Input 
                              value={newVideoForm.name}
                              onChange={(e) => setNewVideoForm(prev => ({...prev, name: e.target.value}))}
                              placeholder="Ex: Animação de Entrada v2"
                              className="mt-1 mobile-input"
                            />
                          </div>
                          
                          <div>
                            <Label className="mobile-text">Arquivo MP4</Label>
                            <Input 
                              type="file"
                              accept="video/mp4,video/*"
                              onChange={(e) => setNewVideoForm(prev => ({...prev, file: e.target.files[0]}))}
                              className="mt-1 mobile-input"
                            />
                            {newVideoForm.file && (
                              <div className="text-sm text-green-600 mt-1 mobile-text-sm">
                                ✅ {newVideoForm.file.name} ({(newVideoForm.file.size / (1024*1024)).toFixed(1)}MB)
                              </div>
                            )}
                          </div>
                          
                          <div className="flex justify-end pt-2">
                            <Button 
                              onClick={uploadNewVideo}
                              disabled={!newVideoForm.name || !newVideoForm.file || videoUploading}
                              className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto mobile-btn-compact mobile-touch-target"
                            >
                              {videoUploading ? '📤 Enviando...' : '🎬 Enviar Vídeo'}
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Lista de Vídeos - Mobile Optimized */}
                      <div className="bg-white border rounded-lg p-4 sm:p-6 mobile-card">
                        <h3 className="font-semibold text-lg mb-4 mobile-card-title">📹 Vídeos Cadastrados</h3>
                        
                        <div className="space-y-3">
                          <div className="text-sm text-gray-600 mb-4 mobile-text">
                            A cada carregamento da página, um vídeo é selecionado aleatoriamente.
                          </div>
                          
                          {/* Lista de vídeos com preview - Mobile Optimized */}
                          <div className="border rounded p-3 text-sm mobile-card">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                              <div className="flex items-center gap-3">
                                {/* Mini preview do vídeo padrão - Responsivo */}
                                <div className="w-20 h-15 sm:w-16 sm:h-12 bg-gray-100 rounded border overflow-hidden flex-shrink-0">
                                  <video 
                                    className="w-full h-full object-cover"
                                    muted
                                    preload="metadata"
                                  >
                                    <source src="https://customer-assets.emergentagent.com/job_893c8c98-bbbd-438c-beb8-ac02ccc9688c/artifacts/yhcwkawb_152b2cbf57b840fb8635aa29a476b1c4.MP4" type="video/mp4" />
                                  </video>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-blue-600 mobile-subtitle">Vídeo Padrão</div>
                                  <div className="text-gray-600 text-xs mobile-text-sm">Vídeo atual do sistema</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 sm:gap-3 mobile-btn-stack">
                                <Button
                                  onClick={() => setPreviewingVideo({
                                    name: "Vídeo Padrão",
                                    url: "https://customer-assets.emergentagent.com/job_893c8c98-bbbd-438c-beb8-ac02ccc9688c/artifacts/yhcwkawb_152b2cbf57b840fb8635aa29a476b1c4.MP4"
                                  })}
                                  variant="outline"
                                  size="sm"
                                  className="text-xs px-3 py-2 mobile-btn-compact mobile-touch-target"
                                >
                                  👁️ Preview
                                </Button>
                                <div className="text-xs text-green-600 mobile-text-sm">✅ Ativo</div>
                              </div>
                            </div>
                          </div>
                          
                          {videosList.map((video, index) => (
                            <div key={video.id} className="border rounded p-3 text-sm mobile-card">
                              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                                <div className="flex items-center gap-3">
                                  {/* Mini preview do vídeo - Responsivo */}
                                  <div className="w-20 h-15 sm:w-16 sm:h-12 bg-gray-100 rounded border overflow-hidden flex-shrink-0">
                                    <video 
                                      className="w-full h-full object-cover"
                                      muted
                                      preload="metadata"
                                    >
                                      <source src={`${API}/admin/splash-videos/${video.id}/file`} type="video/mp4" />
                                    </video>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-blue-600 mobile-subtitle truncate">{video.name}</div>
                                    <div className="text-gray-600 text-xs mobile-text-sm">
                                      {video.filename} • {(video.size / (1024*1024)).toFixed(1)}MB
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 mobile-btn-stack">
                                  <Button
                                    onClick={() => setPreviewingVideo({
                                      name: video.name,
                                      url: `${API}/admin/splash-videos/${video.id}/file`
                                    })}
                                    variant="outline"
                                    size="sm"
                                    className="text-xs px-3 py-2 mobile-btn-compact mobile-touch-target"
                                  >
                                    👁️ Preview
                                  </Button>
                                  <div className="text-xs text-green-600 mobile-text-sm">✅ Ativo</div>
                                  <Button
                                    onClick={async () => {
                                      try {
                                        await axios.delete(`${API}/admin/splash-videos/${video.id}`);
                                        toast.success('Vídeo removido');
                                        await loadVideosList();
                                      } catch (error) {
                                        toast.error('Erro ao remover vídeo');
                                      }
                                    }}
                                    variant="outline"
                                    size="sm"
                                    className="text-xs px-3 py-2 mobile-btn-compact mobile-touch-target"
                                  >
                                    🗑️
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          {videosList.length === 0 && (
                            <div className="text-center text-gray-500 py-6 text-sm mobile-spacing-lg">
                              Nenhum vídeo adicional. Faça upload acima.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Aba Push removida */}

                  {adminSubTab === 'suporte' && (
                    <div className="space-y-6">
                      <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-4">
                        <h3 className="font-semibold text-lg mb-2 text-orange-800">🎫 Gerenciamento de Tickets</h3>
                        <p className="text-orange-700">
                          Gerencie todos os tickets de suporte dos usuários
                        </p>
                      </div>

                      {/* Ticket Stats */}
                      {ticketStats && (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div className="bg-white rounded-lg p-4 border shadow-sm border-l-4 border-red-500">
                            <div className="text-red-700 text-sm">🔴 Abertos</div>
                            <div className="text-2xl font-bold text-red-600">
                              {ticketStats.status_stats?.find(s => s._id === 'open')?.count || 0}
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-4 border shadow-sm border-l-4 border-yellow-500">
                            <div className="text-yellow-700 text-sm">🟡 Pendentes</div>
                            <div className="text-2xl font-bold text-yellow-600">
                              {ticketStats.status_stats?.find(s => s._id === 'pending')?.count || 0}
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-4 border shadow-sm border-l-4 border-green-500">
                            <div className="text-green-700 text-sm">✅ {language === 'pt-BR' ? 'Resolvidos' : 'Resolved'}</div>
                            <div className="text-2xl font-bold text-green-600">
                              {ticketStats.status_stats?.find(s => s._id === 'resolved')?.count || 0}
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-4 border shadow-sm border-l-4 border-blue-500">
                            <div className="text-blue-700 text-sm">📊 Total</div>
                            <div className="text-2xl font-bold text-blue-600">
                              {ticketStats.total_tickets || 0}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Tickets List */}
                      <div className="bg-white rounded-lg border shadow-sm">
                        <div className="p-4 border-b">
                          <div className="flex justify-between items-center">
                            <h4 className="font-semibold">🎫 Lista de Tickets</h4>
                            <div className="flex gap-2">
                              <Button onClick={loadTicketStats} variant="outline" size="sm">
                                🔄 {language === 'pt-BR' ? 'Atualizar Stats' : 'Refresh Stats'}
                              </Button>
                              <Button onClick={loadAdminTickets} variant="outline" size="sm">
                                🔄 {language === 'pt-BR' ? 'Recarregar Tickets' : 'Reload Tickets'}
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Filters */}
                        <div className="p-4 border-b bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <Label className="text-sm">Usuário:</Label>
                              <Input
                                placeholder="Filtrar por usuário..."
                                value={adminTicketFilter.pm}
                                onChange={(e) => setAdminTicketFilter(prev => ({ ...prev, pm: e.target.value }))}
                              />
                            </div>
                            <div>
                              <Label className="text-sm">Status:</Label>
                              <Select
                                value={adminTicketFilter.status || "all"}
                                onValueChange={(value) => setAdminTicketFilter(prev => ({ ...prev, status: value === "all" ? "" : value }))}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Todos os status" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">Todos os status</SelectItem>
                                  <SelectItem value="open">🔴 Abertos</SelectItem>
                                  <SelectItem value="pending">🟡 Pendentes</SelectItem>
                                  <SelectItem value="resolved">✅ {language === 'pt-BR' ? 'Resolvidos' : 'Resolved'}</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex items-end">
                              <Button onClick={loadAdminTickets} className="w-full">
                                🔍 Filtrar
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Tickets Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="text-left p-3 font-medium">Título</th>
                                <th className="text-left p-3 font-medium">Usuário</th>
                                <th className="text-left p-3 font-medium">Status</th>
                                <th className="text-left p-3 font-medium">Criado</th>
                                <th className="text-left p-3 font-medium">Mensagens</th>
                                <th className="text-left p-3 font-medium">Ações</th>
                              </tr>
                            </thead>
                            <tbody>
                              {adminTickets.length > 0 ? (
                                adminTickets.map((ticket) => (
                                  <tr key={ticket.id} className="border-t hover:bg-gray-50">
                                    <td className="p-3">
                                      <div className="font-medium max-w-xs truncate">
                                        {ticket.title}
                                      </div>
                                    </td>
                                    <td className="p-3">
                                      <Badge variant="outline">
                                        {ticket.pm_user}
                                      </Badge>
                                    </td>
                                    <td className="p-3">
                                      <Badge variant={
                                        ticket.status === 'open' ? 'default' :
                                        ticket.status === 'pending' ? 'secondary' :
                                        ticket.status === 'resolved' ? 'outline' :
                                        'secondary'
                                      }>
                                        {ticket.status === 'open' ? '🔴 Aberto' :
                                         ticket.status === 'pending' ? '🟡 Pendente' :
                                         ticket.status === 'resolved' ? `✅ ${language === 'pt-BR' ? 'Resolvido' : 'Resolved'}` :
                                         ticket.status}
                                      </Badge>
                                    </td>
                                    <td className="p-3 text-sm text-gray-600">
                                      {formatNyDateTime(ticket.created_at)}
                                    </td>
                                    <td className="p-3 text-sm">
                                      {ticket.messages?.length || 0} msg(s)
                                    </td>
                                    <td className="p-3">
                                      <div className="flex gap-1">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => setSelectedTicket(ticket)}
                                        >
                                          👁️ Ver
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="text-red-600 hover:text-red-700"
                                          onClick={async () => {
                                            if (window.confirm(language === 'pt-BR' ? 
                                              `Tem certeza que deseja excluir o ticket "${ticket.title}"? Esta ação não pode ser desfeita.` : 
                                              `Are you sure you want to delete the ticket "${ticket.title}"? This action cannot be undone.`
                                            )) {
                                              try {
                                                await axios.delete(`${API}/admin/tickets/${ticket.id}`);
                                                toast.success(language === 'pt-BR' ? 'Ticket excluído com sucesso!' : 'Ticket deleted successfully!');
                                                // Usar função loadAdminTickets para atualizar lista
                                                await loadAdminTickets();
                                              } catch (error) {
                                                console.error('Error deleting ticket:', error);
                                                toast.error(language === 'pt-BR' ? 'Erro ao excluir ticket' : 'Error deleting ticket');
                                              }
                                            }
                                          }}
                                        >
                                          🗑️
                                        </Button>
                                        <Select
                                          value={ticket.status}
                                          onValueChange={(newStatus) => updateTicketStatus(ticket.id, newStatus)}
                                        >
                                          <SelectTrigger className="w-24 h-8">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="open">🔴 Aberto</SelectItem>
                                            <SelectItem value="pending">🟡 Pendente</SelectItem>
                                            <SelectItem value="resolved">✅ Resolvido</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="6" className="p-6 text-center text-gray-500">
                                    {adminTicketsLoading ? "Carregando tickets..." : "Nenhum ticket encontrado"}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {adminSubTab === 'emails' && (
                    <>
                      
                      {/* Email Defaults Section */}
                      <div className="mb-6 bg-white border rounded p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-lg">✉️ Padrões de E-mail</h3>
                          <div className="flex gap-2 flex-wrap">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                // Template original do usuário
                                const defaultTemplate = `<html> <body style="font-family: Arial, sans-serif; color: #0f172a; background:#ffffff;"> <br/><div style="max-width:640px;margin:0 auto;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;"> <div style="background:#1e293b;color:#ffffff;padding:12px 16px;font-size:16px;font-weight:700;"> Labor Bill Details </div> <div style="padding:16px;"> <table style="width:100%;border-collapse:collapse;"> <tr><td style="padding:8px;border-bottom:1px solid #e2e8f0;color:#334155;width:170px;">Job Name:</td><td style="padding:8px;border-bottom:1px solid #e2e8f0;">{JOB_NAME}</td></tr> <tr><td style="padding:8px;border-bottom:1px solid #e2e8f0;color:#334155;">Order#:</td><td style="padding:8px;border-bottom:1px solid #e2e8f0;">{ORDER_NUMBER}</td></tr> <tr><td style="padding:8px;border-bottom:1px solid #e2e8f0;color:#334155;">Install Date:</td><td style="padding:8px;border-bottom:1px solid #e2e8f0;">{INSTALL_DATE}</td></tr> <tr><td style="padding:8px;border-bottom:1px solid #e2e8f0;color:#334155;">PM equivalente:</td><td style="padding:8px;border-bottom:1px solid #e2e8f0;">{PM_CODE}</td></tr> <tr><td style="padding:8px;border-bottom:1px solid #e2e8f0;color:#334155;">Total Windows:</td><td style="padding:8px;border-bottom:1px solid #e2e8f0;">{TOTAL_WINDOWS}</td></tr> <tr><td style="padding:8px;border-bottom:1px solid #e2e8f0;color:#334155;">Total Doors:</td><td style="padding:8px;border-bottom:1px solid #e2e8f0;">{TOTAL_DOORS}</td></tr> </table> <div style="margin-top:16px;padding:12px;border:1px solid #cbd5e1;border-radius:6px;background:#f8fafc;"> <strong style="font-size:18px;">TOTAL AMOUNT: {TOTAL_AMOUNT}</strong> </div> <p style="margin-top:16px;color:#475569;">Please, find the detailed Labor Bill Attached as PDF.</p> </div> </div> </body> </html>`;
                                setEmailDefaults({ ...emailDefaults, body_html: defaultTemplate });
                                toast.success('✅ Template original carregado!');
                              }}
                            >
                              📄 Template Padrão
                            </Button>
                            <Button variant="outline" onClick={saveAdminEmailDefaults} disabled={settingsLoading}>
                              💾 Salvar E-mail
                            </Button>
                            <Button 
                              variant="outline"
                              className="bg-yellow-50 border-yellow-300 hover:bg-yellow-100"
                              onClick={async () => {
                                try {
                                  setSettingsLoading(true);
                                  const { data } = await axios.get(`${API}/admin/debug/email-template-comparison`);
                                  
                                  console.log('🔍 DIAGNÓSTICO DE EMAIL TEMPLATE:', data);
                                  
                                  const message = `
🔍 DIAGNÓSTICO DO TEMPLATE DE EMAIL

📊 Status:
- Fonte: ${data.template_source === 'saved_from_database' ? '✅ Template salvo no banco' : '⚠️ Template padrão (fallback)'}
- Template salvo existe: ${data.saved_template_exists ? 'SIM' : 'NÃO'}
- Tamanho do template salvo: ${data.saved_template_length} caracteres
- Tamanho do template em uso: ${data.template_in_use_length} caracteres

📝 Preview do template salvo (início):
${data.saved_template_preview || 'Nenhum template salvo'}

📝 Preview do template salvo (fim):
${data.saved_template_end || 'Nenhum template salvo'}

🔧 Placeholders encontrados:
${Object.entries(data.placeholders_found).map(([key, found]) => `${found ? '✅' : '❌'} {${key}}`).join('\n')}

💡 ${data.advice}

Verifique o console do navegador para mais detalhes completos.
                                  `.trim();
                                  
                                  alert(message);
                                  
                                  if (!data.saved_template_exists) {
                                    toast.error('⚠️ Nenhum template salvo! O email está usando o template padrão.');
                                  } else if (data.template_source === 'saved_from_database') {
                                    toast.success('✅ Template salvo encontrado no banco!');
                                  }
                                  
                                } catch (e) {
                                  console.error('Erro ao buscar diagnóstico:', e);
                                  toast.error('Erro ao buscar diagnóstico: ' + (e.response?.data?.detail || e.message));
                                } finally {
                                  setSettingsLoading(false);
                                }
                              }}
                            >
                              🔍 Diagnosticar Template
                            </Button>
                            <Button 
                              variant="outline"
                              className="bg-red-50 border-red-300 hover:bg-red-100 text-red-700"
                              onClick={async () => {
                                if (!window.confirm('🗑️ LIMPAR SUPPRESSION LIST?\n\nIsso vai remover TODOS os emails bloqueados da lista de supressão do Resend.\n\nApós limpar, você poderá enviar emails novamente para esses destinatários.\n\nContinuar?')) {
                                  return;
                                }
                                
                                try {
                                  setSettingsLoading(true);
                                  toast.info('🔄 Limpando suppression list...');
                                  
                                  const { data } = await axios.post(`${API}/admin/email/clear-suppressions`);
                                  
                                  console.log('✅ Suppression list limpa:', data);
                                  
                                  const message = `
✅ SUPPRESSION LIST LIMPA!

📊 Resultados:
- Total encontrado: ${data.total_found}
- Removidos com sucesso: ${data.removed}
${data.errors ? `\n⚠️ Erros: ${data.errors.length}` : ''}

Agora você pode enviar emails novamente para todos os destinatários!
                                  `.trim();
                                  
                                  alert(message);
                                  toast.success(`✅ ${data.removed} emails removidos da suppression list!`);
                                  
                                } catch (e) {
                                  console.error('Erro ao limpar suppression list:', e);
                                  toast.error('Erro ao limpar: ' + (e.response?.data?.detail || e.message));
                                } finally {
                                  setSettingsLoading(false);
                                }
                              }}
                            >
                              🗑️ Limpar Suppression List
                            </Button>
                          </div>
                        </div>
                        
                        <div className="space-y-3 mb-4">
                          <div>
                            <Label>From (Email do Remetente)</Label>
                            <Input 
                              type="email"
                              placeholder="Ex.: laborbill@send.pm-home-remodeling.com" 
                              value={emailConfig.sender_email} 
                              onChange={(e) => setEmailConfig({ ...emailConfig, sender_email: e.target.value })} 
                            />
                            <div className="text-xs text-slate-500 mt-1">
                              ⚙️ Este email será usado como remetente em todos os emails do sistema
                            </div>
                          </div>
                          
                          <div>
                            <Label>BCC (Cópia Oculta)</Label>
                            <Input 
                              type="email"
                              placeholder="Ex.: laborbill@send.pm-home-remodeling.com" 
                              value={emailConfig.bcc_email} 
                              onChange={(e) => setEmailConfig({ ...emailConfig, bcc_email: e.target.value })} 
                            />
                            <div className="text-xs text-slate-500 mt-1">
                              📧 Email que receberá cópia oculta de todos os emails enviados
                            </div>
                          </div>
                          
                          <div className="flex gap-2">
                            <Button 
                              variant="default" 
                              onClick={saveEmailConfig} 
                              disabled={settingsLoading}
                              className="flex-1"
                            >
                              💾 Salvar Configurações de Email
                            </Button>
                          </div>
                          
                          <div className="text-xs bg-blue-50 border border-blue-200 p-3 rounded">
                            <strong className="text-blue-800">ℹ️ Importante:</strong>
                            <ul className="mt-2 ml-4 list-disc space-y-1 text-slate-600">
                              <li>Estas configurações são salvas no banco de dados</li>
                              <li>Ao fazer Deploy, as configurações do Deploy serão usadas</li>
                              <li>Preview e Deploy têm configurações separadas</li>
                            </ul>
                          </div>
                          
                          <div className="border-t pt-3 mt-3">
                            <h4 className="font-semibold mb-2">📧 Configuração do Template de Email</h4>
                          </div>
                          
                          <div>
                            <Label>Assunto (suporta placeholders)</Label>
                            <Input 
                              placeholder="Ex.: Labor Bill - {INSTALL_DATE} ({JOB_NAME} - {PM_CODE})" 
                              value={emailDefaults.subject} 
                              onChange={(e) => setEmailDefaults({ ...emailDefaults, subject: e.target.value })} 
                            />
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <input 
                              type="checkbox" 
                              checked={emailDefaults.bcc_sender} 
                              onChange={(e) => setEmailDefaults({ ...emailDefaults, bcc_sender: e.target.checked })} 
                            />
                            <Label className="mb-0">BCC para remetente</Label>
                          </div>
                        </div>
                        
                        <div className="space-y-4">
                          {/* Editor TinyMCE Profissional */}
                          <div className="space-y-2">
                            <Label className="text-base font-semibold">🎨 Editor Visual Profissional (TinyMCE)</Label>
                            
                            <Editor
                              apiKey="b1npdf71ye3jf87m58sw5zwmiw0ilgsynjr8itugrchwh3y9"
                              value={emailDefaults.body_html}
                              onEditorChange={(content) => {
                                setEmailDefaults({ ...emailDefaults, body_html: content });
                              }}
                              init={{
                                height: 800,  // Altura maior
                                menubar: true,
                                resize: true,  // Permite redimensionar arrastando
                                min_height: 600,
                                max_height: 1200,
                                plugins: [
                                  'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                                  'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                  'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount',
                                  'emoticons', 'codesample'
                                ],
                                toolbar: 'undo redo | blocks fontsize | ' +
                                  'bold italic underline strikethrough | ' +
                                  'forecolor backcolor | ' +
                                  'alignleft aligncenter alignright alignjustify | ' +
                                  'bullist numlist | outdent indent | ' +
                                  'link image media table | ' +
                                  'removeformat code fullscreen preview',
                                content_style: 'body { font-family:Arial,sans-serif; font-size:14px; padding: 10px; }',
                                branding: false,
                                promotion: false,
                                font_size_formats: '8pt 10pt 11pt 12pt 14pt 16pt 18pt 20pt 24pt 28pt 32pt 36pt',
                                automatic_uploads: true,
                                images_upload_handler: async (blobInfo) => {
                                  const formData = new FormData();
                                  formData.append('file', blobInfo.blob(), blobInfo.filename());
                                  formData.append('path', '/Email_Images');
                                  
                                  try {
                                    const { data } = await axios.post(`${API}/admin/files/upload`, formData);
                                    return `${API}/admin/files/${data.id}/preview`;
                                  } catch (e) {
                                    throw new Error('Upload failed: ' + e.message);
                                  }
                                }
                              }}
                            />
                            
                            {/* Placeholders Info */}
                            <div className="text-xs text-slate-500 bg-blue-50 p-3 rounded mb-3">
                              <strong>💡 Placeholders disponíveis:</strong>
                              <div className="grid grid-cols-7 gap-2 mt-2 font-mono text-xs">
                                <div>• {'{JOB_NAME}'}</div>
                                <div>• {'{ORDER_NUMBER}'}</div>
                                <div>• {'{INSTALL_DATE}'}</div>
                                <div>• {'{PM_CODE}'}</div>
                                <div>• {'{TOTAL_WINDOWS}'}</div>
                                <div>• {'{TOTAL_DOORS}'}</div>
                                <div>• {'{TOTAL_AMOUNT}'}</div>
                              </div>
                              <div className="mt-2">
                                🎨 Editor profissional com TODAS as ferramentas: cores, fontes, imagens, tabelas, links e muito mais!
                              </div>
                            </div>
                            
                            {/* Image Upload Instructions */}
                            <div className="text-xs bg-yellow-50 border border-yellow-200 p-4 rounded">
                              <strong className="text-yellow-800 text-sm">📸 Como Adicionar Imagens que Apareçam nos Emails:</strong>
                              
                              <div className="mt-3 space-y-3">
                                <div className="bg-white p-3 rounded border border-yellow-100">
                                  <strong className="text-yellow-700">✅ Método Recomendado: URL Pública do Dropbox</strong>
                                  <ol className="mt-2 ml-4 list-decimal space-y-1 text-slate-600">
                                    <li>Faça upload da imagem no Dropbox (https://dropbox.com)</li>
                                    <li>Clique nos 3 pontinhos (⋯) → "Share" → "Copy link"</li>
                                    <li><strong>Converter o link:</strong>
                                      <div className="bg-slate-100 p-2 rounded mt-1 font-mono text-xs">
                                        <div className="text-red-600">❌ Link original:</div>
                                        <div className="break-all">https://www.dropbox.com/s/abc123/imagem.png?dl=0</div>
                                        <div className="text-green-600 mt-1">✅ Link direto (usar este):</div>
                                        <div className="break-all">https://dl.dropboxusercontent.com/s/abc123/imagem.png</div>
                                      </div>
                                    </li>
                                    <li>No editor, clique no ícone de imagem → cole o <strong>link direto</strong> → Save</li>
                                  </ol>
                                </div>
                                
                                <div className="bg-white p-3 rounded border border-yellow-100">
                                  <strong className="text-yellow-700">💡 Outras Opções:</strong>
                                  <ul className="mt-2 ml-4 list-disc space-y-1 text-slate-600">
                                    <li><strong>Imgur:</strong> https://imgur.com (faça upload e copie o "Direct Link")</li>
                                    <li><strong>Google Drive:</strong> Compartilhe como "Qualquer pessoa com o link" e converta para link direto</li>
                                    <li><strong>Base64:</strong> Converta em https://www.base64-image.de/ e cole o código completo</li>
                                  </ul>
                                </div>
                                
                                <div className="bg-red-50 p-2 rounded border border-red-200">
                                  <strong className="text-red-700">⚠️ NÃO use:</strong>
                                  <ul className="mt-1 ml-4 list-disc text-xs text-red-600">
                                    <li>Upload direto do TinyMCE (gera URLs internas que não funcionam em emails)</li>
                                    <li>Imagens do seu computador sem hospedar online primeiro</li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col md:flex-row gap-3 mb-4">
                        <select className="border rounded px-2 py-2" value={selectedPM} onChange={(e) => setSelectedPM(e.target.value)} data-testid="admin-email-scope-select">
                          {emailScopeOptions.map((pm) => (
                            <option key={pm} value={pm}>{scopeLabel(pm)}</option>
                          ))}
                        </select>
                        <Button variant="outline" onClick={() => loadAdminPMRecipients(selectedPM)} disabled={adminPMLoading}>Recarregar</Button>
                      </div>

                      {/* Current Recipients for Selected PM */}
                      <div className="bg-white border rounded-lg p-4 mb-6">
                        <h3 className="font-semibold text-lg mb-4">Destinatários do {scopeLabel(selectedPM)}</h3>
                        <div className="space-y-2">
                          {adminPMRecipients.map((r) => (
                            <div key={r.id} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                              {editingRecipient?.id === r.id ? (
                                <>
                                  <div className="flex flex-col sm:flex-row gap-2 flex-1 mr-2">
                                    <Input
                                      value={editingRecipient.name}
                                      onChange={(e) => setEditingRecipient({ ...editingRecipient, name: e.target.value })}
                                      placeholder="Nome"
                                      className="flex-1"
                                      data-testid={`admin-recipient-edit-name-${r.id}`}
                                    />
                                    <Input
                                      value={editingRecipient.email}
                                      onChange={(e) => setEditingRecipient({ ...editingRecipient, email: e.target.value })}
                                      placeholder="email@exemplo.com"
                                      className="flex-1"
                                      data-testid={`admin-recipient-edit-email-${r.id}`}
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button size="sm" onClick={adminSaveRecipientEdit} data-testid={`admin-recipient-save-${r.id}`}>Salvar</Button>
                                    <Button size="sm" variant="outline" onClick={() => setEditingRecipient(null)} data-testid={`admin-recipient-cancel-${r.id}`}>Cancelar</Button>
                                  </div>
                                </>
                              ) : (
                              <>
                              <div className="flex items-center gap-2">
                                {r.admin_locked && (
                                  <span title="Adicionado pelo Admin" className="text-slate-400">🔒</span>
                                )}
                                <div>
                                  <div className="font-medium flex items-center gap-2">
                                    {r.name}
                                    {r.is_finance && (
                                      <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">💵 Financeiro</span>
                                    )}
                                  </div>
                                  <div className="text-slate-600 text-sm">{r.email}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className={r.is_finance ? 'border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100' : 'text-slate-600'}
                                  onClick={() => toggleRecipientFinance(r.id, !r.is_finance, () => loadAdminPMRecipients(selectedPM))}
                                  data-testid={`admin-recipient-finance-toggle-${r.id}`}
                                  title="Marcar/desmarcar como e-mail do Financeiro"
                                >
                                  {r.is_finance ? '✓ Financeiro' : 'Marcar Financeiro'}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setEditingRecipient({ id: r.id, name: r.name || '', email: r.email || '' })}
                                  data-testid={`admin-recipient-edit-${r.id}`}
                                >
                                  Editar
                                </Button>
                                <Button 
                                  variant="outline" 
                                  className="text-red-600 hover:text-red-700" 
                                  onClick={() => adminDeleteRecipientFromPM(selectedPM, r.id)}
                                  data-testid={`admin-recipient-remove-${r.id}`}
                                >
                                  Remover
                                </Button>
                              </div>
                              </>
                              )}
                            </div>
                          ))}
                          {adminPMRecipients.length === 0 && <p className="text-center text-slate-500 py-6">Nenhum destinatário cadastrado para {scopeLabel(selectedPM)}</p>}
                        </div>
                      </div>

                      {/* Add Recipient to Specific PM */}
                      <div className="bg-white border rounded-lg p-4 mb-6">
                        <h3 className="font-semibold text-lg mb-4">Adicionar Destinatário ao {scopeLabel(selectedPM)}</h3>
                        <div className="flex gap-3 items-end">
                          <div className="flex-1">
                            <Label>Nome</Label>
                            <Input 
                              value={adminNewRecipient.name} 
                              onChange={(e) => setAdminNewRecipient({ ...adminNewRecipient, name: e.target.value })} 
                              placeholder="Nome do destinatário" 
                            />
                          </div>
                          <div className="flex-1">
                            <Label>E-mail</Label>
                            <Input 
                              value={adminNewRecipient.email} 
                              onChange={(e) => setAdminNewRecipient({ ...adminNewRecipient, email: e.target.value })} 
                              placeholder="email@exemplo.com" 
                            />
                          </div>
                          <Button onClick={() => adminAddRecipientToPM(selectedPM)}>Adicionar ao {scopeLabel(selectedPM)}</Button>
                        </div>
                      </div>

                      {/* Add Recipient Globally */}
                      <div className="bg-white border rounded-lg p-4">
                        <h3 className="font-semibold text-lg mb-4">Adicionar Destinatário Globalmente</h3>
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <Label>Nome</Label>
                              <Input 
                                value={adminBulkRecipient.name} 
                                onChange={(e) => setAdminBulkRecipient({ ...adminBulkRecipient, name: e.target.value })} 
                                placeholder="Nome do destinatário" 
                              />
                            </div>
                            <div>
                              <Label>E-mail</Label>
                              <Input 
                                value={adminBulkRecipient.email} 
                                onChange={(e) => setAdminBulkRecipient({ ...adminBulkRecipient, email: e.target.value })} 
                                placeholder="email@exemplo.com" 
                              />
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <Label>Selecionar destinos</Label>
                              <Button variant="outline" size="sm" onClick={() => {
                                const all = pmOptions;
                                const selected = adminBulkRecipient?.pms || [];
                                const allSelected = all.every(p => selected.includes(p));
                                setAdminBulkRecipient({ ...(adminBulkRecipient||{}), pms: allSelected ? [] : all });
                              }}>Selecionar todos os PMs</Button>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
                              {emailScopeOptions.map((pm) => (
                                <label key={pm} className={`flex items-center space-x-2 ${pm === 'FOM' ? 'col-span-2 md:col-span-5 border-b pb-2 mb-1' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={(adminBulkRecipient.pms || []).includes(pm)}
                                    onChange={(e) => {
                                      const pms = adminBulkRecipient.pms || [];
                                      if (e.target.checked) {
                                        setAdminBulkRecipient({ ...adminBulkRecipient, pms: [...pms, pm] });
                                      } else {
                                        setAdminBulkRecipient({ ...adminBulkRecipient, pms: pms.filter(p => p !== pm) });
                                      }
                                    }}
                                    className="rounded"
                                    data-testid={`admin-bulk-scope-${pm}`}
                                  />
                                  <span className={`text-sm ${pm === 'FOM' ? 'font-semibold text-emerald-700' : ''}`}>{scopeLabel(pm)}{pm === 'FOM' ? ' — só o supervisor vê (ex.: Financeiro)' : ''}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Button onClick={adminAddRecipientsBulk}>Adicionar Globalmente</Button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {adminSubTab === 'mensagens' && (
                    <div className="space-y-4 sm:space-y-6">
                      {/* Enviar Notificação (melhorado para mobile) */}
                      <div className="bg-white border rounded-lg p-4 sm:p-6 mobile-card">
                        <h3 className="font-semibold text-lg mb-4 mobile-card-title">📨 Enviar Notificação</h3>
                        
                        <div className="space-y-4 mobile-form-group">
                          <div>
                            <Label className="mobile-text">Título</Label>
                            <Input 
                              value={adminNotificationForm.title}
                              onChange={(e) => setAdminNotificationForm(prev => ({...prev, title: e.target.value}))}
                              placeholder="Título da notificação..."
                              className="mt-1 mobile-input"
                            />
                          </div>
                          
                          <div>
                            <Label className="mobile-text">Mensagem</Label>
                            <Textarea 
                              value={adminNotificationForm.message}
                              onChange={(e) => setAdminNotificationForm(prev => ({...prev, message: e.target.value}))}
                              placeholder="Digite a mensagem que aparecerá no modal..."
                              className="mt-1 h-20 mobile-textarea"
                            />
                          </div>
                          
                          <div>
                            <Label className="mobile-text">Destinatário</Label>
                            <select 
                              className="border rounded px-3 py-2 w-full mt-1 mobile-select"
                              value={adminNotificationForm.targetUser}
                              onChange={(e) => setAdminNotificationForm(prev => ({...prev, targetUser: e.target.value}))}
                            >
                              <option value="ALL">Todos os usuários</option>
                              {allUsers.map(user => (
                                <option key={user.username} value={user.username}>
                                  {user.username}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="flex justify-end pt-2">
                            <Button onClick={createNotification} className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto mobile-btn-compact mobile-touch-target">
                              📨 Enviar Notificação
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Histórico Completo de Atividades do Sistema */}
                      <div className="bg-white border rounded-lg p-4 sm:p-6 mobile-card">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-lg mobile-card-title">Histórico de Atividades do Sistema</h3>
                          <div className="flex gap-2 flex-wrap justify-end">
                            <Button
                              data-testid="filter-labor-bills-btn"
                              onClick={toggleLaborBillsOnly}
                              size="sm"
                              variant={laborBillsOnly ? 'default' : 'outline'}
                              className={laborBillsOnly ? 'text-xs bg-blue-600 hover:bg-blue-700 text-white' : 'text-xs border-blue-400 text-blue-700 hover:bg-blue-50'}
                            >
                              💼 Só Labor Bills
                            </Button>
                            <Button
                              data-testid="filter-supervisor-btn"
                              onClick={toggleSupervisorOnly}
                              size="sm"
                              variant={supervisorOnly ? 'default' : 'outline'}
                              className={supervisorOnly ? 'text-xs bg-amber-600 hover:bg-amber-700 text-white' : 'text-xs border-amber-400 text-amber-700 hover:bg-amber-50'}
                            >
                              👷 Só Supervisor
                            </Button>
                            <Button
                              onClick={() => exportActivities('csv')}
                              size="sm"
                              variant="outline"
                              className="text-xs"
                            >
                              CSV
                            </Button>
                            <Button
                              onClick={() => exportActivities('pdf')}
                              size="sm"
                              variant="outline"
                              className="text-xs"
                            >
                              PDF
                            </Button>
                            <NotificationsPurgeDialog
                              filters={{ ...notificationHistoryFilter, tipo: effectiveHistoryTipo() }}
                              onDone={() => { loadAdminNotifications(1, { ...notificationHistoryFilter, tipo: effectiveHistoryTipo() }); loadAnnouncements?.(); }}
                            />
                          </div>
                        </div>

                        {/* Abas de Labor Bills (Criados / Enviados) */}
                        {laborBillsOnly && (
                          <div className="flex gap-2 mb-4" data-testid="labor-bill-tabs">
                            <Button
                              data-testid="labor-bill-tab-created"
                              onClick={() => selectLaborBillTab('created')}
                              size="sm"
                              variant={laborBillTab === 'created' ? 'default' : 'outline'}
                              className={laborBillTab === 'created' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-blue-300 text-blue-700'}
                            >
                              📋 Criados
                              <span className="ml-2 rounded-full bg-slate-200 text-slate-800 px-2 py-0.5 text-xs font-semibold" data-testid="labor-bill-created-count">{laborBillCounts.created}</span>
                            </Button>
                            <Button
                              data-testid="labor-bill-tab-sent"
                              onClick={() => selectLaborBillTab('sent')}
                              size="sm"
                              variant={laborBillTab === 'sent' ? 'default' : 'outline'}
                              className={laborBillTab === 'sent' ? 'bg-green-600 hover:bg-green-700 text-white' : 'border-green-300 text-green-700'}
                            >
                              📧 Enviados
                              <span className="ml-2 rounded-full bg-slate-200 text-slate-800 px-2 py-0.5 text-xs font-semibold" data-testid="labor-bill-sent-count">{laborBillCounts.sent}</span>
                            </Button>
                          </div>
                        )}

                        {/* Abas do Supervisor (Todos / Alterados / Sem alterações) */}
                        {supervisorOnly && (
                          <div className="flex gap-2 mb-4 flex-wrap" data-testid="supervisor-tabs">
                            <Button
                              data-testid="supervisor-tab-all"
                              onClick={() => selectSupervisorTab('all')}
                              size="sm"
                              variant={supervisorTab === 'all' ? 'default' : 'outline'}
                              className={supervisorTab === 'all' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'border-amber-300 text-amber-700'}
                            >
                              👷 Todos
                              <span className="ml-2 rounded-full bg-slate-200 text-slate-800 px-2 py-0.5 text-xs font-semibold" data-testid="supervisor-all-count">{supervisorCounts.total}</span>
                            </Button>
                            <Button
                              data-testid="supervisor-tab-changed"
                              onClick={() => selectSupervisorTab('changed')}
                              size="sm"
                              variant={supervisorTab === 'changed' ? 'default' : 'outline'}
                              className={supervisorTab === 'changed' ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'border-orange-300 text-orange-700'}
                            >
                              ✏️ Alterados
                              <span className="ml-2 rounded-full bg-slate-200 text-slate-800 px-2 py-0.5 text-xs font-semibold" data-testid="supervisor-changed-count">{supervisorCounts.changed}</span>
                            </Button>
                            <Button
                              data-testid="supervisor-tab-unchanged"
                              onClick={() => selectSupervisorTab('unchanged')}
                              size="sm"
                              variant={supervisorTab === 'unchanged' ? 'default' : 'outline'}
                              className={supervisorTab === 'unchanged' ? 'bg-green-600 hover:bg-green-700 text-white' : 'border-green-300 text-green-700'}
                            >
                              ✅ Sem alterações
                              <span className="ml-2 rounded-full bg-slate-200 text-slate-800 px-2 py-0.5 text-xs font-semibold" data-testid="supervisor-unchanged-count">{supervisorCounts.unchanged}</span>
                            </Button>
                          </div>
                        )}

                        {/* Filtros */}
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                          <h4 className="font-medium mb-3">Filtros</h4>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-1">Usuário (PM)</label>
                              <select
                                value={notificationHistoryFilter.pm}
                                onChange={(e) => {
                                  const newFilter = { ...notificationHistoryFilter, pm: e.target.value };
                                  setNotificationHistoryFilter(newFilter);
                                  loadAdminNotifications(1, newFilter);
                                }}
                                className="w-full px-3 py-2 border rounded-md text-sm"
                                data-testid="activity-user-filter"
                              >
                                <option value="">Todos</option>
                                {adminUsers.map(u => (
                                  <option key={u.username} value={u.username}>
                                    {u.full_name ? `${u.full_name} (${u.username})` : u.username}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Tipo</label>
                              <select
                                value={notificationHistoryFilter.tipo || ''}
                                onChange={(e) => {
                                  const newFilter = { ...notificationHistoryFilter, tipo: e.target.value };
                                  setNotificationHistoryFilter(newFilter);
                                  loadAdminNotifications(1, newFilter);
                                }}
                                className="w-full px-3 py-2 border rounded-md text-sm"
                              >
                                <option value="">Todos</option>
                                <option value="labor_bill_created">Novo Labor Bill</option>
                                <option value="labor_bill_edited">Labor Bill Editado</option>
                                <option value="labor_bill_sent">Labor Bill Enviado</option>
                                <option value="ticket_created">Novo Ticket</option>
                                <option value="rental_request">Solicitação de Aluguel</option>
                                <option value="rental_approved">Aluguel Aprovado</option>
                                <option value="rental_rejected">Aluguel Rejeitado</option>
                                <option value="rental_completed">Aluguel Finalizado</option>
                                <option value="truck_rental_requested">Aluguel Truck</option>
                                <option value="truck_rental_approved">Truck Aprovado</option>
                                <option value="truck_rental_completed">Truck Finalizado</option>
                                <option value="truck_problem">Problema no Truck</option>
                                <option value="maintenance_registered">Manutenção Registrada</option>
                                <option value="maintenance_completed">Manutenção Concluída</option>
                                <option value="checkout_requested">Checkout Solicitado</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Data Início</label>
                              <input
                                type="date"
                                value={notificationHistoryFilter.dateFrom}
                                onChange={(e) => setNotificationHistoryFilter({
                                  ...notificationHistoryFilter,
                                  dateFrom: e.target.value
                                })}
                                className="w-full px-3 py-2 border rounded-md text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Data Fim</label>
                              <input
                                type="date"
                                value={notificationHistoryFilter.dateTo}
                                onChange={(e) => setNotificationHistoryFilter({
                                  ...notificationHistoryFilter,
                                  dateTo: e.target.value
                                })}
                                className="w-full px-3 py-2 border rounded-md text-sm"
                              />
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-4">
                            <Button 
                              onClick={() => loadAdminNotifications(1, notificationHistoryFilter)} 
                              size="sm" 
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              Filtrar
                            </Button>
                            <Button 
                              onClick={() => {
                                const empty = { pm: '', status: '', tipo: '', dateFrom: '', dateTo: '' };
                                setNotificationHistoryFilter(empty);
                                loadAdminNotifications(1, empty);
                              }} 
                              variant="outline" 
                              size="sm"
                            >
                              Limpar Filtros
                            </Button>
                          </div>
                        </div>
                          
                        {/* Lista de Notificações/Atividades */}
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-500">
                            Página {notifPage} de {notifTotalPages} ({notificationHistoryTotal} registros)
                          </span>
                          <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">Por página:</label>
                            <select
                              value={notifPageSize}
                              onChange={(e) => {
                                const size = parseInt(e.target.value);
                                setNotifPageSize(size);
                                loadAdminNotifications(1, notificationHistoryFilter, size);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }}
                              className="px-2 py-1 border rounded text-sm"
                            >
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                              <option value={200}>200</option>
                            </select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {adminNotifications.map((activity, index) => (
                            <ActivityCard 
                              key={activity.id || index}
                              activity={activity}
                              language={language}
                              onOpenChanges={(ex) => { setFomReview(ex); setShowFomReviewModal(true); }}
                            />
                          ))}
                          
                          {adminNotifications.length === 0 && (
                            <div className="text-center text-gray-500 py-12">
                              <div className="text-lg font-medium mb-2">Nenhuma notificação encontrada</div>
                              <div className="text-sm">Ajuste os filtros ou crie uma nova notificação</div>
                            </div>
                          )}
                        </div>
                        
                        {/* Paginação */}
                        {notifTotalPages > 1 && (
                          <div className="flex justify-between items-center mt-4 pt-4 border-t">
                            <span className="text-sm text-gray-500">
                              Página {notifPage} de {notifTotalPages}
                            </span>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const p = Math.max(1, notifPage - 1);
                                  setNotifPage(p);
                                  loadAdminNotifications(p, { ...notificationHistoryFilter, tipo: effectiveHistoryTipo() });
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                disabled={notifPage <= 1}
                              >
                                Anterior
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const p = notifPage + 1;
                                  setNotifPage(p);
                                  loadAdminNotifications(p, { ...notificationHistoryFilter, tipo: effectiveHistoryTipo() });
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                disabled={notifPage >= notifTotalPages}
                              >
                                Próxima
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Aba Push removida */}

                  {adminSubTab === 'importar' && (
                    <>
                      <div className="space-y-6">
                        {/* File Selection and PM Selection */}
                        <div className="bg-white border rounded-lg p-6">
                          <h3 className="font-semibold text-lg mb-4">Importar Labor Bill de PDF</h3>
                          
                          {/* Single or Multiple Toggle */}
                          <div className="flex gap-4 mb-4">
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name="pdfImportMode"
                                value="single"
                                checked={pdfImportMode === 'single'}
                                onChange={() => {
                                  setPdfImportMode('single');
                                  setImportFiles([]);
                                  setImportFile(null);
                                  setImportPreviewData(null);
                                  setImportEditData(null);
                                  setImportMultiplePreviewData(null);
                                  setImportMultipleEditData([]);
                                  setImportAutoCommitResults(null);
                                  setEditingMultiplePdfIndex(null);
                                  setShowMultiplePdfEditModal(false);
                                }}
                                className="mr-2"
                              />
                              Importar PDF único
                            </label>
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name="pdfImportMode"
                                value="multiple"
                                checked={pdfImportMode === 'multiple'}
                                onChange={() => {
                                  setPdfImportMode('multiple');
                                  setImportFile(null);
                                  setImportPreviewData(null);
                                  setImportEditData(null);
                                  setImportAutoCommitResults(null);
                                }}
                                className="mr-2"
                              />
                              Importar múltiplos PDFs
                            </label>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <Label htmlFor={pdfImportMode === 'single' ? "import-file-input" : "import-multiple-input"}>
                                {pdfImportMode === 'multiple' ? "Selecionar Múltiplos PDFs" : "Selecionar Arquivo PDF"}
                              </Label>
                              {pdfImportMode === 'single' ? (
                                <Input
                                  id="import-file-input"
                                  type="file"
                                  accept=".pdf"
                                  onChange={handleImportFileSelect}
                                  className="mt-1"
                                />
                              ) : (
                                <Input
                                  id="import-multiple-input"
                                  type="file"
                                  accept=".pdf"
                                  multiple
                                  onChange={handleImportMultipleFiles}
                                  className="mt-1"
                                />
                              )}
                              {importFile && pdfImportMode === 'single' && (
                                <div className="text-sm text-green-600 mt-1">
                                  ✓ {importFile.name} selecionado
                                </div>
                              )}
                              {importFiles.length > 0 && pdfImportMode === 'multiple' && (
                                <div className="text-sm text-green-600 mt-1">
                                  ✓ {importFiles.length} arquivo(s) selecionado(s)
                                  <div className="text-xs text-gray-600 mt-1">
                                    {importFiles.map(f => f.name).join(", ")}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div>
                              <Label>Atribuir ao PM</Label>
                              <select 
                                className="border rounded px-3 py-2 w-full mt-1" 
                                value={importPM} 
                                onChange={(e) => setImportPM(e.target.value)}
                              >
                                {pmOptions.map((pm) => (
                                  <option key={pm} value={pm}>{pm}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          
                          <div className="flex gap-3">
                            {pdfImportMode === 'single' ? (
                              <Button 
                                onClick={previewImportPDF} 
                                disabled={!importFile || importLoading}
                              >
                                {importLoading ? "Analisando..." : "Analisar PDF"}
                              </Button>
                            ) : (
                              <Button 
                                onClick={previewMultiplePDFs} 
                                disabled={importFiles.length === 0 || importLoading}
                              >
                                {importLoading ? "Analisando..." : `Analisar ${importFiles.length || 0} PDFs`}
                              </Button>
                            )}
                            
                            {(importPreviewData || importMultiplePreviewData) && (
                              <Button 
                                variant="outline"
                                onClick={() => {
                                  setImportFile(null);
                                  setImportFiles([]);
                                  setImportPreviewData(null);
                                  setImportEditData(null);
                                  setImportMultiplePreviewData(null);
                                  setImportMultipleEditData([]);
                                  setImportAutoCommitResults(null);
                                  setEditingMultiplePdfIndex(null);
                                  setShowMultiplePdfEditModal(false);
                                  const fileInput = document.getElementById('import-file-input');
                                  const multipleInput = document.getElementById('import-multiple-input');
                                  if (fileInput) fileInput.value = '';
                                  if (multipleInput) multipleInput.value = '';
                                }}
                              >
                                Limpar
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Auto-commit Results */}
                        {importAutoCommitResults && (
                          <div className="bg-white border rounded-lg p-6">
                            <h3 className="font-semibold text-lg mb-4">Resultados da Importação</h3>
                            
                            {importAutoCommitResults.single && (
                              <div className="space-y-4">
                                <div className={`p-4 rounded-lg border ${importAutoCommitResults.single.success ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                                  <div className="font-medium">
                                    {importAutoCommitResults.single.success ? '✅ Sucesso' : '⚠️ Revisão Necessária'}
                                  </div>
                                  <div className="text-sm mt-1">{importAutoCommitResults.single.message}</div>
                                  
                                  {!importAutoCommitResults.single.success && (
                                    <div className="mt-3 space-y-2">
                                      <div className="text-sm">
                                        <strong>PDF Total:</strong> ${importAutoCommitResults.single.pdf_total?.toFixed(2)}
                                      </div>
                                      <div className="text-sm">
                                        <strong>Calculado:</strong> ${importAutoCommitResults.single.calculated_total?.toFixed(2)}
                                      </div>
                                      <div className="text-sm">
                                        <strong>Diferença:</strong> ${importAutoCommitResults.single.difference?.toFixed(2)}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {importAutoCommitResults.results && (
                              <div className="space-y-4">
                                {importAutoCommitResults.results.map((result, index) => (
                                  <div key={index} className={`p-4 rounded-lg border ${result.result.success ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                                    <div className="font-medium">
                                      PDF {index + 1}: {result.result.success ? '✅ Sucesso' : '⚠️ Revisão Necessária'}
                                    </div>
                                    <div className="text-sm mt-1">{result.result.message}</div>
                                    
                                    {!result.result.success && result.result.pdf_total && (
                                      <div className="mt-2 text-xs space-y-1">
                                        <div>PDF: ${result.result.pdf_total?.toFixed(2)} | Calculado: ${result.result.calculated_total?.toFixed(2)}</div>
                                        <div>Diferença: ${result.result.difference?.toFixed(2)}</div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Preview and Edit Data */}
                        {importPreviewData && importEditData && (
                          <div className="bg-white border rounded-lg p-6">
                            <h3 className="font-semibold text-lg mb-4">Dados Extraídos - Revisar e Editar</h3>
                            
                            {/* Basic Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                              <div>
                                <Label>Install Date</Label>
                                <Input
                                  value={importEditData.install_date}
                                  onChange={(e) => updateImportEditData('install_date', e.target.value)}
                                  placeholder="MM/DD/YY ou YYYY-MM-DD"
                                />
                              </div>
                              <div>
                                <Label>Job Name (Nome do Cliente)</Label>
                                <Input
                                  value={importEditData.job_name}
                                  onChange={(e) => updateImportEditData('job_name', e.target.value)}
                                  placeholder="Nome do cliente"
                                />
                              </div>
                              <div>
                                <Label>Order # (Número do Pedido)</Label>
                                <Input
                                  value={importEditData.order_number}
                                  onChange={(e) => updateImportEditData('order_number', e.target.value)}
                                  placeholder="Número do pedido"
                                />
                              </div>
                              <div>
                                <Label>Código PM</Label>
                                <Input
                                  value={importPM}
                                  readOnly
                                  className="bg-gray-100"
                                />
                              </div>
                              <div>
                                <Label>Total de Janelas</Label>
                                <Input
                                  type="number"
                                  value={importEditData.total_windows}
                                  onChange={(e) => updateImportEditData('total_windows', parseInt(e.target.value) || 0)}
                                />
                              </div>
                              <div>
                                <Label>Total de Portas</Label>
                                <Input
                                  type="number"
                                  value={importEditData.total_doors}
                                  onChange={(e) => updateImportEditData('total_doors', parseInt(e.target.value) || 0)}
                                />
                              </div>
                            </div>

                            {/* Grand Total from PDF */}
                            {importPreviewData.grand_total && (
                              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                                <h4 className="font-semibold text-green-800 mb-2">💰 Total Final Extraído do PDF</h4>
                                <div className="text-2xl font-bold text-green-900">
                                  ${importPreviewData.grand_total.toFixed(2)}
                                </div>
                                <div className="text-sm text-green-700 mt-1">
                                  Este é o valor total encontrado no PDF original
                                </div>
                              </div>
                            )}

                            {/* Calculated Items Total with Comparison */}
                            {importEditData && (importEditData.items?.length > 0 || importEditData.extra_items?.length > 0) && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                <h4 className="font-semibold text-blue-800 mb-2">🧮 Total Calculado dos Itens</h4>
                                <div className="text-2xl font-bold text-blue-900">
                                  ${calculateImportItemsTotal().toFixed(2)}
                                </div>
                                <div className="text-sm text-blue-700 mt-1">
                                  Soma dos itens principais + extras
                                </div>
                                
                                {/* Comparison with PDF Total */}
                                {importPreviewData.grand_total && (
                                  <div className="mt-3 pt-3 border-t border-blue-200">
                                    <div className="flex justify-between text-sm">
                                      <span>PDF Total:</span>
                                      <span className="font-medium">${importPreviewData.grand_total.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span>Itens Total:</span>
                                      <span className="font-medium">${calculateImportItemsTotal().toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm font-bold border-t pt-1 mt-1">
                                      <span>Diferença:</span>
                                      <span className={`${Math.abs(importPreviewData.grand_total - calculateImportItemsTotal()) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                                        ${Math.abs(importPreviewData.grand_total - calculateImportItemsTotal()).toFixed(2)}
                                        {Math.abs(importPreviewData.grand_total - calculateImportItemsTotal()) < 0.01 && " ✅"}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Items Table */}
                            {importEditData && (
                              <div className="mb-6">
                                <div className="flex justify-between items-center mb-3">
                                  <h4 className="font-semibold text-md">Itens Principais</h4>
                                  <Button
                                    size="sm"
                                    onClick={() => openItemSelector('main')}
                                    className="bg-blue-600 hover:bg-blue-700"
                                  >
                                    + Adicionar Item
                                  </Button>
                                </div>
                                
                                {importEditData.items && importEditData.items.length > 0 ? (
                                  <div className="overflow-x-auto border rounded">
                                    <table className="w-full">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th className="text-left p-3 font-medium">Descrição</th>
                                          <th className="text-left p-3 font-medium">Qtd</th>
                                          <th className="text-left p-3 font-medium">Preço Unit.</th>
                                          <th className="text-left p-3 font-medium">Total</th>
                                          <th className="text-left p-3 font-medium">Status</th>
                                          <th className="text-left p-3 font-medium">Ações</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                      {importEditData.items.map((item, index) => (
                                        <tr key={index} className="border-t">
                                          <td className="p-3">
                                            <Input
                                              value={item.description}
                                              onChange={(e) => updateImportItem(index, 'description', e.target.value)}
                                              className="text-sm"
                                            />
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              value={item.qty}
                                              onChange={(e) => {
                                                const qty = parseInt(e.target.value) || 0;
                                                updateImportItem(index, 'qty', qty);
                                                updateImportItem(index, 'total', qty * item.unit_price);
                                              }}
                                              className="text-sm w-20"
                                            />
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              step="0.01"
                                              value={item.unit_price}
                                              onChange={(e) => {
                                                const price = parseFloat(e.target.value) || 0;
                                                updateImportItem(index, 'unit_price', price);
                                                updateImportItem(index, 'total', item.qty * price);
                                              }}
                                              className="text-sm w-24"
                                            />
                                          </td>
                                          <td className="p-3 font-medium">
                                            ${item.total.toFixed(2)}
                                          </td>
                                          <td className="p-3">
                                            <Badge variant={item.matched ? "default" : "secondary"}>
                                              {item.matched ? "Reconhecido" : "Novo"}
                                            </Badge>
                                          </td>
                                          <td className="p-3">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => removeImportItem(index)}
                                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                ) : (
                                  <div className="text-center py-8 text-gray-500 border rounded-lg border-dashed">
                                    <div className="text-4xl mb-2">📋</div>
                                    <div>Nenhum item principal encontrado</div>
                                    <div className="text-sm">Use o botão acima para adicionar itens</div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Extra Items Table */}
                            {importEditData && (
                              <div className="mb-6">
                                <div className="flex justify-between items-center mb-3">
                                  <h4 className="font-semibold text-md">Itens Extras</h4>
                                  <Button
                                    size="sm"
                                    onClick={() => openItemSelector('extra')}
                                    className="bg-orange-600 hover:bg-orange-700"
                                  >
                                    + Adicionar Item Extra
                                  </Button>
                                </div>
                                
                                {importEditData.extra_items && importEditData.extra_items.length > 0 ? (
                                  <div className="overflow-x-auto border rounded">
                                    <table className="w-full">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th className="text-left p-3 font-medium">Descrição</th>
                                          <th className="text-left p-3 font-medium">Qtd</th>
                                          <th className="text-left p-3 font-medium">Preço Unit.</th>
                                          <th className="text-left p-3 font-medium">Total</th>
                                          <th className="text-left p-3 font-medium">Ações</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {importEditData.extra_items.map((item, index) => (
                                          <tr key={index} className="border-t">
                                            <td className="p-3">
                                              <Input
                                                value={item.description}
                                                onChange={(e) => updateImportExtraItem(index, 'description', e.target.value)}
                                                className="text-sm"
                                              />
                                            </td>
                                            <td className="p-3">
                                              <Input
                                                type="number"
                                                value={item.qty}
                                                onChange={(e) => {
                                                  const qty = parseInt(e.target.value) || 0;
                                                  updateImportExtraItem(index, 'qty', qty);
                                                  updateImportExtraItem(index, 'total', qty * item.unit_price);
                                                }}
                                                className="text-sm w-20"
                                              />
                                            </td>
                                            <td className="p-3">
                                              <Input
                                                type="number"
                                                step="0.01"
                                                value={item.unit_price}
                                                onChange={(e) => {
                                                  const price = parseFloat(e.target.value) || 0;
                                                  updateImportExtraItem(index, 'unit_price', price);
                                                  updateImportExtraItem(index, 'total', item.qty * price);
                                                }}
                                                className="text-sm w-24"
                                              />
                                            </td>
                                            <td className="p-3 font-medium">
                                              ${item.total.toFixed(2)}
                                            </td>
                                            <td className="p-3">
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => removeImportExtraItem(index)}
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                              >
                                                <Trash2 className="h-4 w-4" />
                                              </Button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <div className="text-center py-8 text-gray-500 border rounded-lg border-dashed">
                                    <div className="text-4xl mb-2">⭐</div>
                                    <div>Nenhum item extra encontrado</div>
                                    <div className="text-sm">Use o botão acima para adicionar itens extras</div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Notes */}
                            <div className="mb-6">
                              <Label>Observações</Label>
                              <Textarea
                                value={importEditData.notes || ''}
                                onChange={(e) => updateImportEditData('notes', e.target.value)}
                                placeholder="Observações adicionais..."
                                rows={3}
                              />
                            </div>

                            {/* Raw Data Preview (collapsible) */}
                            {importPreviewData.raw_text && (
                              <details className="mb-6">
                                <summary className="cursor-pointer font-medium text-gray-600 hover:text-gray-800">
                                  Ver Texto Bruto Extraído do PDF
                                </summary>
                                <div className="mt-2 p-3 bg-gray-50 border rounded text-xs font-mono whitespace-pre-wrap max-h-40 overflow-y-auto">
                                  {importPreviewData.raw_text}
                                </div>
                              </details>
                            )}

                            {/* Action Buttons */}
                            <div className="flex justify-end gap-3">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setImportFile(null);
                                  setImportFiles([]);
                                  setImportPreviewData(null);
                                  setImportEditData(null);
                                  setImportMultiplePreviewData(null);
                                  setImportMultipleEditData([]);
                                  setImportAutoCommitResults(null);
                                  setEditingMultiplePdfIndex(null);
                                  setShowMultiplePdfEditModal(false);
                                  const fileInput = document.getElementById('import-file-input');
                                  const multipleInput = document.getElementById('import-multiple-input');
                                  if (fileInput) fileInput.value = '';
                                  if (multipleInput) multipleInput.value = '';
                                }}
                              >
                                Cancelar
                              </Button>
                              <Button
                                onClick={commitImportPDF}
                                disabled={importCommitLoading}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                {importCommitLoading ? "Importando..." : `📝 Importar para ${importEditData?.pm_code || importPM}`}
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Multiple PDFs Preview and Edit Data */}
                        {importMultiplePreviewData && importMultipleEditData && (
                          <div className="bg-white border rounded-lg p-6">
                            <h3 className="font-semibold text-lg mb-4">Dados de Múltiplos PDFs - Revisar</h3>
                            
                            <div className="space-y-6">
                              {importMultipleEditData.map((fileData, index) => (
                                <div key={index} className={`border rounded-lg p-4 ${fileData.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                                  <h4 className="font-semibold mb-2">
                                    📄 {fileData.filename} {fileData.success ? '✅' : '❌'}
                                  </h4>
                                  
                                  {!fileData.success ? (
                                    <div className="text-red-600 text-sm">{fileData.error}</div>
                                  ) : (
                                    <div className="space-y-4">
                                      {/* Basic Info for this PDF */}
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                        <div>
                                          <span className="font-medium">Install Date:</span> {formatDate(fileData.data.install_date)}
                                        </div>
                                        <div>
                                          <span className="font-medium">Job:</span> {fileData.data.job_name}
                                        </div>
                                        <div>
                                          <span className="font-medium">Order:</span> {fileData.data.order_number}
                                        </div>
                                        <div>
                                          <span className="font-medium">Total:</span> ${fileData.data.grand_total?.toFixed(2)}
                                        </div>
                                      </div>
                                      
                                      {/* Items Summary */}
                                      <div className="text-sm">
                                        <span className="font-medium">Itens:</span> {fileData.data.items?.length || 0} principais, {fileData.data.extra_items?.length || 0} extras
                                      </div>
                                      
                                      {/* Edit Button */}
                                      <div className="flex justify-end mt-3">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => openMultiplePdfEditor(index)}
                                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                        >
                                          ✏️ Editar PDF
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {/* Multiple PDFs Action Buttons */}
                            <div className="flex justify-end gap-3 mt-6">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setImportFiles([]);
                                  setImportMultiplePreviewData(null);
                                  setImportMultipleEditData([]);
                                  setImportAutoCommitResults(null);
                                  setEditingMultiplePdfIndex(null);
                                  setShowMultiplePdfEditModal(false);
                                  const multipleInput = document.getElementById('import-multiple-input');
                                  if (multipleInput) multipleInput.value = '';
                                }}
                              >
                                Cancelar
                              </Button>
                              <Button
                                onClick={commitMultiplePDFs}
                                disabled={importCommitLoading || importMultipleEditData.filter(f => f.success).length === 0}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                {importCommitLoading ? "Importando..." : `📝 Importar ${importMultipleEditData.filter(f => f.success).length} PDFs Restantes`}
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Instructions */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-900 mb-2">ℹ️ Como Funciona</h4>
                          <ul className="text-sm text-blue-800 space-y-1">
                            <li>• <strong>1. Análise Automática:</strong> Ao clicar "Analisar", o sistema extrai dados e compara totais automaticamente</li>
                            <li>• <strong>2. Importação Automática:</strong> Se totais do PDF = totais calculados → importa automaticamente para o PM do PDF</li>
                            <li>• <strong>3. Revisão Manual:</strong> Se totais diferem → mostra diferenças e permite adicionar/editar itens</li>
                            <li>• <strong>4. Múltiplos PDFs:</strong> Processa todos os arquivos, importa automaticamente os que conferem</li>
                            <li>• <strong>Botão "Importar":</strong> Sempre disponível para importação manual após revisão</li>
                            <li>• Todas as datas originais do PDF são preservadas</li>
                            <li>• Labor Bills são criados como rascunho para o PM correspondente</li>
                          </ul>
                        </div>
                      </div>
                    </>
                  )}

                  {adminSubTab === 'leaders' && (
                    <div className="space-y-6">
                      <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-lg mb-2 text-green-800">👥 Gestão de Team Leaders</h3>
                            <p className="text-green-700 text-sm">
                              Visualize e gerencie informações pessoais e documentos de todos os líderes de equipe.
                            </p>
                          </div>
                          <Button
                            onClick={async () => {
                              const settings = await loadWaiverSettings();
                              setShowWaiverSettingsModal(true);
                            }}
                            variant="outline"
                            size="sm"
                            className="flex-shrink-0"
                          >
                            ⚙️ Config. Waiver
                          </Button>
                        </div>
                      </div>

                      {teamLeaderLoading ? (
                        <div className="text-center py-8">
                          <div className="text-2xl mb-2">🔄</div>
                          <div>Carregando informações dos Team Leaders...</div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {/* PM Users + Daily Leaders */}
                          {[
                            ...Array.from({ length: 10 }, (_, i) => ({ username: `PM${i + 1}`, type: 'pm' })),
                            ...(helperTeams || []).filter(t => t.leader_username).map(t => ({ username: t.leader_username, type: 'daily_leader', equipe: t.equipe, teamName: t.lider_nome }))
                          ].map(({ username: pmUsername, type: userType, equipe, teamName }) => {
                            const leader = teamLeaders.find(l => l.username === pmUsername);
                            
                            return (
                              <div key={pmUsername} className={`bg-white border rounded-lg p-4 hover:shadow-md transition-shadow ${userType === 'daily_leader' ? 'border-orange-200' : 'border-gray-200'}`}>
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <div className="text-lg">{userType === 'daily_leader' ? '👷' : '👤'}</div>
                                    <div>
                                      <div className="font-semibold text-blue-800">{pmUsername}</div>
                                      <div className="text-sm text-gray-600">
                                        {leader?.full_name || teamName || 'Não configurado'}
                                      </div>
                                      {userType === 'daily_leader' && (
                                        <div className="text-xs text-orange-600">Daily Leader • {equipe}</div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className={`px-2 py-1 rounded text-xs ${leader ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                      {leader ? '✅ Configurado' : '⏳ Pendente'}
                                    </div>
                                  </div>
                                </div>

                                {/* Info Summary */}
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Email:</span>
                                    <span className="font-medium">{leader?.email || 'N/A'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Telefone:</span>
                                    <span className="font-medium">{leader?.phone || 'N/A'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Posição:</span>
                                    <span className="font-medium">{leader?.position || 'N/A'}</span>
                                  </div>
                                </div>

                                {/* Documents Status */}
                                <div className="mt-3 pt-3 border-t border-gray-100">
                                  <h6 className="text-xs font-semibold text-gray-700 mb-2">📁 Documentos:</h6>
                                  <div className="grid grid-cols-4 gap-1 text-xs">
                                    <div className={`text-center py-1 rounded ${leader?.driver_license_file ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                      🆔<br/>{leader?.driver_license_file ? '✅' : '❌'}
                                    </div>
                                    <div className={`text-center py-1 rounded ${leader?.lead_certificate_file ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                      🏆<br/>{leader?.lead_certificate_file ? '✅' : '❌'}
                                    </div>
                                    <div className={`text-center py-1 rounded ${leader?.contractor_file ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                      📝<br/>{leader?.contractor_file ? '✅' : '❌'}
                                    </div>
                                    <div className="relative">
                                      <button
                                        onClick={() => handleViewWaiver(pmUsername)}
                                        className={`w-full text-center py-1 rounded cursor-pointer hover:opacity-80 ${
                                          leader?.waiver_status === 'signed' ? 'bg-green-100 text-green-700' :
                                          leader?.waiver_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                          'bg-gray-100 text-gray-500'
                                        }`}
                                        title={
                                          leader?.waiver_status === 'signed' ? 'Waiver Assinado' :
                                          leader?.waiver_status === 'pending' ? 'Aguardando Assinatura' :
                                          'Criar Waiver'
                                        }
                                      >
                                        📄<br/>
                                        {leader?.waiver_status === 'signed' ? '✅' :
                                         leader?.waiver_status === 'pending' ? '❓' :
                                         '➕'}
                                      </button>
                                      {leader?.waiver_file && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteWaiver(pmUsername);
                                          }}
                                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center hover:bg-red-600 text-[10px]"
                                          title="Deletar Waiver"
                                        >
                                          ×
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Botões de Waiver PT/EN - Aparecem quando assinado */}
                                {leader?.waiver_status === 'signed' && (
                                  <div className="mt-2 flex justify-center gap-2">
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        const pdfUrl = `${API}/waiver-files/pm/${pmUsername}/combined-pdf?lang=pt`;
                                        const response = await axios.get(pdfUrl, {
                                          responseType: 'blob',
                                          headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
                                        });
                                        
                                        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
                                        setPdfModalData({
                                          url: blobUrl,
                                          title: `${pmUsername} - Waivers (PT)`
                                        });
                                        setShowPDFModal(true);
                                      }}
                                      className="px-3 py-1 rounded border border-green-500 hover:bg-green-50 transition-all flex items-center gap-1 text-sm"
                                      title="Ver ambos contratos em Português"
                                    >
                                      🇧🇷 <span className="text-xs">PT</span>
                                    </button>
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        const pdfUrl = `${API}/waiver-files/pm/${pmUsername}/combined-pdf?lang=en`;
                                        const response = await axios.get(pdfUrl, {
                                          responseType: 'blob',
                                          headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
                                        });
                                        
                                        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
                                        setPdfModalData({
                                          url: blobUrl,
                                          title: `${pmUsername} - Waivers (EN)`
                                        });
                                        setShowPDFModal(true);
                                      }}
                                      className="px-3 py-1 rounded border border-blue-500 hover:bg-blue-50 transition-all flex items-center gap-1 text-sm"
                                      title="View both contracts in English"
                                    >
                                      🇺🇸 <span className="text-xs">EN</span>
                                    </button>
                                  </div>
                                )}

                                {/* Action Buttons */}
                                <div className="grid grid-cols-3 gap-2 mt-3">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={async () => {
                                      // BOTÃO VER - Modal somente visualização (read-only)
                                      try {
                                        setTeamLeaderLoading(true);
                                        
                                        // Carregar dados do Team Leader
                                        const { data } = await axios.get(`${API}/team-leader/${pmUsername}`);
                                        
                                        // SEMPRE puxar dados do Users para garantir sincronização
                                        console.log(`📥 Carregando dados do Users para ${pmUsername}...`);
                                        let finalData = { ...data };
                                        
                                        try {
                                          const userResponse = await axios.get(`${API}/admin/users`);
                                          const userData = userResponse.data.find(u => u.username === pmUsername);
                                          
                                          if (userData) {
                                            const fullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
                                            
                                            // Usar dados do Users como base, Team Leaders como override
                                            finalData = {
                                              username: pmUsername,
                                              full_name: data.full_name || fullName || '',
                                              email: data.email || '',
                                              phone: data.phone || userData.phone || '',
                                              address: data.address || userData.address || '',
                                              date_of_birth: data.date_of_birth || '',
                                              hire_date: data.hire_date || '',
                                              position: data.position || '',
                                              driver_license_number: data.driver_license_number || '',
                                              ssn_tax_id: data.ssn_tax_id || '',
                                              driver_license_file: data.driver_license_file,
                                              lead_certificate_file: data.lead_certificate_file,
                                              lead_firm_certificate_file: data.lead_firm_certificate_file,
                                              arbitration_file: data.arbitration_file,
                                              contractor_file: data.contractor_file
                                            };
                                            console.log(`✅ Dados mesclados Users + Team Leaders:`, finalData);
                                          }
                                        } catch (userError) {
                                          console.error('Erro ao carregar dados do Users:', userError);
                                        }
                                        
                                        setCurrentTeamLeader(finalData);
                                        
                                        // Populate form data for viewing
                                        setTeamLeaderFormData({
                                          full_name: finalData.full_name || '',
                                          email: finalData.email || '',
                                          phone: finalData.phone || '',
                                          address: finalData.address || '',
                                          date_of_birth: finalData.date_of_birth || '',
                                          hire_date: finalData.hire_date || '',
                                          position: finalData.position || '',
                                          driver_license_number: finalData.driver_license_number || '',
                                          ssn_tax_id: finalData.ssn_tax_id || ''
                                        });
                                        
                                        setTeamLeaderFiles({
                                          driver_license_file: finalData.driver_license_file,
                                          lead_certificate_file: finalData.lead_certificate_file,
                                          lead_firm_certificate_file: finalData.lead_firm_certificate_file,
                                          arbitration_file: finalData.arbitration_file,
                                          contractor_file: finalData.contractor_file
                                        });
                                        
                                        // Load helpers for viewing
                                        await loadTeamHelpers(pmUsername);
                                        
                                        setShowTeamLeaderViewModal(true);
                                      } catch (e) {
                                        console.error(`Error loading data for ${pmUsername}:`, e);
                                        toast.error(`Erro ao carregar dados de ${pmUsername}`);
                                      } finally {
                                        setTeamLeaderLoading(false);
                                      }
                                    }}
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    Ver
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={async () => {
                                        // BOTÃO EDITAR - Modal com edição + helpers
                                        try {
                                          setTeamLeaderLoading(true);
                                          
                                          // Carregar dados do Team Leader
                                          const { data } = await axios.get(`${API}/team-leader/${pmUsername}`);
                                          
                                          // SEMPRE puxar dados do Users para garantir sincronização
                                          console.log(`📥 Carregando dados do Users para ${pmUsername}...`);
                                          let finalData = { ...data };
                                          
                                          try {
                                            const userResponse = await axios.get(`${API}/admin/users`);
                                            const userData = userResponse.data.find(u => u.username === pmUsername);
                                            
                                            if (userData) {
                                              const fullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
                                              
                                              // Usar dados do Users como base, Team Leaders como override
                                              finalData = {
                                                username: pmUsername,
                                                full_name: data.full_name || fullName || '',
                                                email: data.email || '',
                                                phone: data.phone || userData.phone || '',
                                                address: data.address || userData.address || '',
                                                date_of_birth: data.date_of_birth || '',
                                                hire_date: data.hire_date || '',
                                                position: data.position || '',
                                                driver_license_number: data.driver_license_number || '',
                                                ssn_tax_id: data.ssn_tax_id || '',
                                                driver_license_file: data.driver_license_file,
                                                lead_certificate_file: data.lead_certificate_file,
                                                lead_firm_certificate_file: data.lead_firm_certificate_file,
                                                arbitration_file: data.arbitration_file,
                                                contractor_file: data.contractor_file
                                              };
                                              console.log(`✅ Dados mesclados Users + Team Leaders:`, finalData);
                                            }
                                          } catch (userError) {
                                            console.error('Erro ao carregar dados do Users:', userError);
                                          }
                                          
                                          setCurrentTeamLeader(finalData);
                                          
                                          // Populate form data for editing
                                          setTeamLeaderFormData({
                                            full_name: finalData.full_name || '',
                                            email: finalData.email || '',
                                            phone: finalData.phone || '',
                                            address: finalData.address || '',
                                            date_of_birth: finalData.date_of_birth || '',
                                            hire_date: finalData.hire_date || '',
                                            position: finalData.position || '',
                                            driver_license_number: finalData.driver_license_number || '',
                                            ssn_tax_id: finalData.ssn_tax_id || ''
                                          });
                                          
                                          setTeamLeaderFiles({
                                            driver_license_file: finalData.driver_license_file,
                                            lead_certificate_file: finalData.lead_certificate_file,
                                            lead_firm_certificate_file: data.lead_firm_certificate_file,
                                            arbitration_file: data.arbitration_file,
                                            contractor_file: data.contractor_file
                                          });
                                          
                                          // Load helpers for editing
                                          await loadTeamHelpers(pmUsername);
                                          
                                          setShowTeamLeaderEditModal(true);
                                        } catch (e) {
                                          console.error(`Error loading data for ${pmUsername}:`, e);
                                          toast.error(`Erro ao carregar dados de ${pmUsername}`);
                                        } finally {
                                          setTeamLeaderLoading(false);
                                        }
                                      }}
                                    >
                                      <Edit className="h-3 w-3 mr-1" />
                                      Editar
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="bg-blue-50 hover:bg-blue-100"
                                      onClick={async () => {
                                        // BOTÃO ENVIAR EMAIL
                                        try {
                                          setTeamLeaderLoading(true);
                                          
                                          // Carregar dados completos do PM
                                          const { data } = await axios.get(`${API}/team-leader/${pmUsername}`);
                                          
                                          // Mesclar com dados do Users
                                          const userResponse = await axios.get(`${API}/admin/users`);
                                          const userData = userResponse.data.find(u => u.username === pmUsername);
                                          const fullName = userData ? `${userData.first_name || ''} ${userData.last_name || ''}`.trim() : '';
                                          
                                          const finalData = {
                                            username: pmUsername,
                                            full_name: data.full_name || fullName || '',
                                            email: data.email || '',
                                            phone: data.phone || userData?.phone || '',
                                            address: data.address || userData?.address || '',
                                            ...data
                                          };
                                          
                                          setCurrentTeamLeader(finalData);
                                          setTeamLeaderFiles({
                                            driver_license_file: data.driver_license_file,
                                            lead_certificate_file: data.lead_certificate_file,
                                            lead_firm_certificate_file: data.lead_firm_certificate_file,
                                            arbitration_file: data.arbitration_file,
                                            contractor_file: data.contractor_file
                                          });
                                          
                                          // Carregar helpers
                                          await loadTeamHelpers(pmUsername);
                                          
                                          // Carregar email recipients do PM
                                          try {
                                            const { data: recipientsData } = await axios.get(`${API}/email-recipients`);
                                            // Filtrar apenas recipients deste PM
                                            const pmRecipients = recipientsData.filter(r => r.pm_user === pmUsername);
                                            setEmailRecipients(pmRecipients);
                                            console.log(`✅ Carregados ${pmRecipients.length} recipients de ${pmUsername}`);
                                          } catch (recipientError) {
                                            console.error('Erro ao carregar recipients:', recipientError);
                                          }
                                          
                                          // Carregar documentos disponíveis (Arbitration e Contractor)
                                          await loadAvailableDocuments(pmUsername);
                                          
                                          // Resetar seleções
                                          setTeamLeaderEmailData({
                                            recipient_email: '',
                                            recipient_name: '',
                                            selected_documents: [],
                                            selected_helpers: [],
                                            helper_documents: {},
                                            message: ''
                                          });
                                          
                                          setShowTeamLeaderEmailModal(true);
                                        } catch (e) {
                                          console.error('Erro ao preparar email:', e);
                                          toast.error('Erro ao carregar dados');
                                        } finally {
                                          setTeamLeaderLoading(false);
                                        }
                                      }}
                                    >
                                      <Mail className="h-3 w-3 mr-1" />
                                      Email
                                    </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Summary Stats */}
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold mb-3">📊 Resumo</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                          <div>
                            <div className="text-2xl font-bold text-blue-600">
                              {teamLeaders.filter(l => l.full_name).length}
                            </div>
                            <div className="text-sm text-gray-600">Perfis Configurados</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-green-600">
                              {teamLeaders.filter(l => l.driver_license_file).length}
                            </div>
                            <div className="text-sm text-gray-600">Driver Licenses</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-purple-600">
                              {teamLeaders.filter(l => l.lead_certificate_file).length}
                            </div>
                            <div className="text-sm text-gray-600">Certificados</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-teal-600">
                              {teamLeaders.filter(l => l.contractor_file).length}
                            </div>
                            <div className="text-sm text-gray-600">Contractors</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {adminSubTab === 'migracao' && (
                    <div className="space-y-6">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="font-semibold text-lg mb-4 text-blue-800">📦 Migração de Dados</h3>
                        <p className="text-blue-700 mb-4">
                          Download dos arquivos de backup para transferir todos os Labor Bills para o sistema deployado.
                        </p>
                        
                        <div className="bg-white border rounded-lg p-4 mb-4">
                          <h4 className="font-semibold mb-2">📊 Dados Disponíveis para Download:</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>• <strong>121 Labor Bills</strong> distribuídos entre todos os usuários</li>
                            <li>• <strong>110 Recipientes de Email</strong> configurados</li>
                            <li>• <strong>11 Usuários</strong> (ADMIN + PM1 até PM10)</li>
                            <li>• Configurações e dados completos de cada usuário</li>
                          </ul>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                          {[
                            'backup_admin_20250905_164929.json',
                            'backup_pm1_20250905_164929.json',
                            'backup_pm2_20250905_164929.json',
                            'backup_pm3_20250905_164930.json',
                            'backup_pm4_20250905_164930.json',
                            'backup_pm5_20250905_164930.json',
                            'backup_pm6_20250905_164930.json',
                            'backup_pm7_20250905_164931.json',
                            'backup_pm8_20250905_164931.json',
                            'backup_pm9_20250905_164931.json',
                            'backup_pm10_20250905_164931.json'
                          ].map((filename) => (
                            <Button
                              key={filename}
                              variant="outline"
                              size="sm"
                              onClick={() => downloadBackupFile(filename)}
                              className="text-left justify-start"
                            >
                              <Download className="h-4 w-4 mr-2" />
                              {filename.replace('backup_', '').replace('_20250905_164929.json', '').replace('_20250905_164930.json', '').replace('_20250905_164931.json', '').toUpperCase()}
                            </Button>
                          ))}
                        </div>

                        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h4 className="font-semibold text-yellow-800 mb-2">📋 Instruções para Importação:</h4>
                          <ol className="text-sm text-yellow-700 space-y-2">
                            <li><strong>1.</strong> Baixe todos os 11 arquivos JSON acima</li>
                            <li><strong>2.</strong> Acesse seu sistema deployado</li>
                            <li><strong>3.</strong> Login como cada usuário (ADMIN, PM1, PM2, etc.)</li>
                            <li><strong>4.</strong> Vá em "Configurações" → "Backup/Restore"</li>
                            <li><strong>5.</strong> Faça upload do arquivo JSON correspondente ao usuário</li>
                            <li><strong>6.</strong> Selecione modo "Merge" (preserva dados existentes)</li>
                            <li><strong>7.</strong> Repita para todos os usuários que possuem dados</li>
                          </ol>
                        </div>

                        <div className="mt-4 flex flex-col gap-3">
                          {/* Botão Preview (ambiente atual) */}
                          <Button
                            onClick={async () => {
                              try {
                                setBackupLoading(true);
                                toast.info('📦 Gerando arquivo ZIP com todos os backups do PREVIEW...');
                                
                                const token = sessionStorage.getItem('access_token');
                                if (!token) {
                                  toast.error('Sessão expirada. Faça login novamente.');
                                  return;
                                }
                                
                                console.log('🔄 Solicitando download do ZIP (PREVIEW)...');
                                
                                // Download ZIP file
                                const response = await fetch(`${API}/admin/backup/download-all-zip`, {
                                  method: 'GET',
                                  headers: {
                                    'Authorization': `Bearer ${token}`
                                  }
                                });
                                
                                if (!response.ok) {
                                  throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                                }
                                
                                console.log('✅ ZIP recebido, iniciando download...');
                                
                                // Get filename from Content-Disposition header
                                const contentDisposition = response.headers.get('Content-Disposition');
                                let filename = 'all_backups_preview.zip';
                                if (contentDisposition) {
                                  const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
                                  if (matches != null && matches[1]) {
                                    filename = matches[1].replace(/['"]/g, '');
                                  }
                                }
                                
                                // Download the blob usando função universal
                                const blob = await response.blob();
                                await downloadFile(blob, filename, 'application/zip');
                                
                                console.log(`✅ Download completo: ${filename}`);
                                
                              } catch (error) {
                                console.error('❌ Erro ao baixar ZIP:', error);
                                toast.error(`Erro ao baixar backups: ${error.message}`);
                              } finally {
                                setBackupLoading(false);
                              }
                            }}
                            className="bg-blue-600 hover:bg-blue-700"
                            disabled={backupLoading}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            {backupLoading ? '📦 Gerando ZIP...' : '📦 Baixar Todos (ZIP) - PREVIEW'}
                          </Button>

                          {/* ⚠️ BOTÃO TEMPORÁRIO - Backup do DEPLOYED (Produção) */}
                          <Button
                            onClick={async () => {
                              try {
                                setBackupLoading(true);
                                
                                // Pedir senha do ADMIN do Deployed
                                const deployedPassword = window.prompt(
                                  '🔐 Digite a senha do ADMIN no DEPLOYED (Produção):\n\n' +
                                  'URL: https://laborbill-dashboard.emergent.host',
                                  ''
                                );
                                
                                if (!deployedPassword) {
                                  toast.info('Backup cancelado');
                                  setBackupLoading(false);
                                  return;
                                }
                                
                                toast.info('🟢 Conectando ao DEPLOYED (PRODUÇÃO)...');
                                
                                const DEPLOYED_URL = 'https://laborbill-dashboard.emergent.host';
                                
                                // Passo 1: Login no Deployed
                                console.log('🔐 Fazendo login no Deployed...');
                                toast.info('🔐 Autenticando no DEPLOYED...');
                                
                                const loginResponse = await fetch(`${DEPLOYED_URL}/api/login`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ username: 'ADMIN', password: deployedPassword })
                                });
                                
                                if (!loginResponse.ok) {
                                  throw new Error('❌ Senha incorreta ou falha no login do DEPLOYED.');
                                }
                                
                                const loginData = await loginResponse.json();
                                const deployedToken = loginData.access_token;
                                console.log('✅ Login no Deployed OK');
                                
                                // Passo 2: Buscar lista de usuários
                                toast.info('📋 Buscando lista de usuários...');
                                const usersResponse = await fetch(`${DEPLOYED_URL}/api/admin/users`, {
                                  headers: { 'Authorization': `Bearer ${deployedToken}` }
                                });
                                
                                if (!usersResponse.ok) {
                                  throw new Error('Falha ao buscar usuários');
                                }
                                
                                const users = await usersResponse.json();
                                console.log(`📋 Encontrados ${users.length} usuários`);
                                toast.info(`📦 Coletando backups de ${users.length} usuários...`);
                                
                                // Passo 3: Coletar backup de cada usuário
                                const backups = [];
                                for (const user of users) {
                                  const username = user.username;
                                  console.log(`  🔄 Coletando backup de ${username}...`);
                                  
                                  try {
                                    const backupResponse = await fetch(`${DEPLOYED_URL}/api/admin/pm/${username}/backup/export`, {
                                      headers: { 'Authorization': `Bearer ${deployedToken}` }
                                    });
                                    
                                    if (backupResponse.ok) {
                                      const backupData = await backupResponse.json();
                                      backups.push({
                                        username: username,
                                        data: backupData
                                      });
                                      console.log(`    ✅ ${username} coletado`);
                                    } else {
                                      console.log(`    ⚠️ ${username} falhou`);
                                    }
                                  } catch (err) {
                                    console.log(`    ⚠️ ${username} erro: ${err.message}`);
                                  }
                                }
                                
                                if (backups.length === 0) {
                                  throw new Error('Nenhum backup foi coletado');
                                }
                                
                                console.log(`✅ ${backups.length} backups coletados`);
                                toast.info('📦 Criando arquivo ZIP...');
                                
                                // Passo 4: Criar ZIP no lado do cliente
                                const JSZip = (await import('jszip')).default;
                                const zip = new JSZip();
                                
                                const timestamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0].replace('T', '_');
                                
                                for (const backup of backups) {
                                  const filename = `backup_${backup.username.toLowerCase()}_${timestamp}.json`;
                                  const jsonStr = JSON.stringify(backup.data, null, 2);
                                  zip.file(filename, jsonStr);
                                }
                                
                                // Gerar ZIP
                                const zipBlob = await zip.generateAsync({ type: 'blob' });
                                
                                // Download usando função universal (timestamp já declarado acima)
                                await downloadFile(zipBlob, `DEPLOYED_all_backups_${timestamp}.zip`, 'application/zip');
                                
                                console.log(`✅ Download do DEPLOYED completo: ${backups.length} usuários`);
                                
                              } catch (error) {
                                console.error('❌ Erro ao baixar backups do DEPLOYED:', error);
                                toast.error(`Erro: ${error.message}`);
                              } finally {
                                setBackupLoading(false);
                              }
                            }}
                            className="bg-green-600 hover:bg-green-700 border-2 border-green-800"
                            disabled={backupLoading}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            {backupLoading ? '🟢 Conectando DEPLOYED...' : '🟢 BACKUP DEPLOYED (PRODUÇÃO)'}
                          </Button>
                          
                          <div className="text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded p-2">
                            ⚠️ <strong>BOTÃO TEMPORÁRIO:</strong> O botão verde acima conecta diretamente ao ambiente de PRODUÇÃO (https://laborbill-dashboard.emergent.host) e baixa os dados reais. Use antes de fazer deploy!
                          </div>
                        </div>
                      </div>
                    </div>
                  )}


                  {/* SUB-ABA: GERENCIADOR DE ARQUIVOS */}
                  {adminSubTab === 'arquivos' && (
                    <div className="space-y-4" style={{ overflow: 'visible' }}>
                      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4" style={{ overflow: 'visible' }}>
                        <h3 className="font-semibold text-lg mb-4 text-blue-800">📁 Gerenciador de Arquivos</h3>
                        
                        {/* Barra de Menu Superior */}
                        <div className="bg-white border border-gray-300 rounded-lg mb-4 relative" style={{ overflow: 'visible' }}>
                          <div className="flex items-center divide-x divide-gray-300 text-sm" style={{ overflow: 'visible' }}>
                            {/* Menu Arquivo */}
                            <div className="relative">
                              <button 
                                onClick={() => setOpenTopMenu(openTopMenu === 'arquivo' ? null : 'arquivo')}
                                className="px-4 py-2 hover:bg-gray-100 font-medium transition"
                              >
                                Arquivo
                              </button>
                              {openTopMenu === 'arquivo' && (
                                <div className="absolute left-0 top-full mt-0 bg-white border border-gray-300 rounded shadow-lg min-w-[180px]" style={{ zIndex: 9999 }}>
                                  <button onClick={() => { setShowCreateFolderModal(true); setOpenTopMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2">
                                    📁 Nova Pasta
                                  </button>
                                  <button onClick={() => { setShowFileUploadModal(true); setOpenTopMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2">
                                    📤 Upload
                                  </button>
                                  <div className="border-t border-gray-200 my-1"></div>
                                  <button onClick={() => {
                                    if (fileManagerSelectedItems.length > 0) {
                                      const item = fileManagerItems.find(i => i.id === fileManagerSelectedItems[0]);
                                      if (item) {
                                        setSelectedItemForProperties(item);
                                        setShowPropertiesModal(true);
                                      }
                                    }
                                    setOpenTopMenu(null);
                                  }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50" disabled={fileManagerSelectedItems.length === 0}>
                                    📋 Propriedades
                                  </button>
                                </div>
                              )}
                            </div>
                            
                            {/* Menu Editar */}
                            <div className="relative">
                              <button 
                                onClick={() => setOpenTopMenu(openTopMenu === 'editar' ? null : 'editar')}
                                className="px-4 py-2 hover:bg-gray-100 font-medium transition"
                              >
                                Editar
                              </button>
                              {openTopMenu === 'editar' && (
                                <div className="absolute left-0 top-full mt-0 bg-white border border-gray-300 rounded shadow-lg min-w-[180px]" style={{ zIndex: 9999 }}>
                                  <button onClick={() => { copyToClipboard(); setOpenTopMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50" disabled={fileManagerSelectedItems.length === 0}>
                                    📋 Copiar
                                  </button>
                                  <button onClick={() => { cutToClipboard(); setOpenTopMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50" disabled={fileManagerSelectedItems.length === 0}>
                                    ✂️ Cortar
                                  </button>
                                  <button onClick={() => { pasteFromClipboard(); setOpenTopMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50" disabled={fileManagerClipboard.items.length === 0}>
                                    📌 Colar
                                  </button>
                                  <div className="border-t border-gray-200 my-1"></div>
                                  <button onClick={() => { deleteSelectedItems(); setOpenTopMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-red-600 disabled:opacity-50" disabled={fileManagerSelectedItems.length === 0}>
                                    🗑️ Deletar
                                  </button>
                                </div>
                              )}
                            </div>
                            
                            {/* Menu Visualizar */}
                            <div className="relative">
                              <button 
                                onClick={() => setOpenTopMenu(openTopMenu === 'visualizar' ? null : 'visualizar')}
                                className="px-4 py-2 hover:bg-gray-100 font-medium transition"
                              >
                                Visualizar
                              </button>
                              {openTopMenu === 'visualizar' && (
                                <div className="absolute left-0 top-full mt-0 bg-white border border-gray-300 rounded shadow-lg min-w-[180px]" style={{ zIndex: 9999 }}>
                                  <button onClick={() => { setFileManagerViewMode('list'); setOpenTopMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2">
                                    📋 Lista {fileManagerViewMode === 'list' && '✓'}
                                  </button>
                                  <button onClick={() => { setFileManagerViewMode('grid'); setOpenTopMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2">
                                    🔲 Grade {fileManagerViewMode === 'grid' && '✓'}
                                  </button>
                                </div>
                              )}
                            </div>
                            
                            {/* Menu Ferramentas */}
                            <div className="relative">
                              <button 
                                onClick={() => setOpenTopMenu(openTopMenu === 'ferramentas' ? null : 'ferramentas')}
                                className="px-4 py-2 hover:bg-gray-100 font-medium transition"
                              >
                                Ferramentas
                              </button>
                              {openTopMenu === 'ferramentas' && (
                                <div className="absolute left-0 top-full mt-0 bg-white border border-gray-300 rounded shadow-lg min-w-[180px]" style={{ zIndex: 9999 }}>
                                  <button onClick={() => {
                                    if (fileManagerSelectedItems.length > 0) {
                                      const item = fileManagerItems.find(i => i.id === fileManagerSelectedItems[0]);
                                      if (item && item.type === 'file') {
                                        generateShareLink(item);
                                      }
                                    }
                                    setOpenTopMenu(null);
                                  }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50" disabled={fileManagerSelectedItems.length === 0}>
                                    🔗 Gerar Link
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Breadcrumb - com scroll horizontal no mobile */}
                        <div className="flex items-center gap-2 text-sm mb-4 overflow-x-auto pb-2">
                          <span className="font-semibold flex-shrink-0">📍</span>
                          <button onClick={() => navigateToFolder('/')} className="px-2 py-1 hover:bg-blue-100 rounded flex-shrink-0 text-xs sm:text-sm">
                            🏠 Raiz
                          </button>
                          {fileManagerCurrentPath !== '/' && fileManagerCurrentPath.split('/').filter(p => p).map((part, idx, arr) => {
                            const path = '/' + arr.slice(0, idx + 1).join('/');
                            return (
                              <React.Fragment key={path}>
                                <span className="flex-shrink-0 text-gray-400">›</span>
                                <button onClick={() => navigateToFolder(path)} className="px-2 py-1 hover:bg-blue-100 rounded flex-shrink-0 text-xs sm:text-sm font-medium">
                                  {part}
                                </button>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Busca */}
                      <Input
                        placeholder="🔍 Buscar arquivos..."
                        value={fileManagerSearchQuery}
                        onChange={(e) => setFileManagerSearchQuery(e.target.value)}
                        className="max-w-md"
                      />
                      
                      {/* Conteúdo */}
                      {fileManagerLoading ? (
                        <div className="bg-white border rounded-lg p-12 text-center">
                          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                          <p className="mt-4">Carregando...</p>
                        </div>
                      ) : (
                        <>
                          {/* VIEW: LISTA - Desktop */}
                          {fileManagerViewMode === 'list' && (
                            <>
                              {/* Tabela Desktop */}
                              <div className="bg-white border rounded-lg hidden md:block" style={{ overflow: 'visible' }}>
                                <table className="w-full">
                                <thead className="bg-gray-100 border-b">
                                  <tr>
                                    <th className="px-4 py-3 text-left w-10">
                                      <input type="checkbox" className="rounded" />
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold">Nome</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold">Tipo</th>
                                    <th className="px-4 py-3 text-right text-sm font-semibold">Tamanho</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold">Data</th>
                                    <th className="px-4 py-3 text-center text-sm font-semibold w-16">[...]</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y">
                                  {fileManagerItems
                                    .filter(item => !fileManagerSearchQuery || item.name.toLowerCase().includes(fileManagerSearchQuery.toLowerCase()))
                                    .map(item => (
                                      <tr key={item.id} className="hover:bg-gray-50 transition">
                                        <td className="px-4 py-3">
                                          <input
                                            type="checkbox"
                                            checked={fileManagerSelectedItems.includes(item.id)}
                                            onChange={() => toggleFileSelection(item.id)}
                                            className="rounded"
                                          />
                                        </td>
                                        <td
                                          className="px-4 py-3 cursor-pointer hover:bg-blue-50 transition"
                                          onClick={() => {
                                            if (item.type === 'folder') {
                                              navigateToFolder(item.full_path);
                                            } else {
                                              // Arquivos: abrir preview
                                              previewFileManagerFile(item);
                                            }
                                          }}
                                        >
                                          <div className="flex items-center gap-2">
                                            <span className="text-xl">
                                              {item.custom_icon ? (
                                                <span dangerouslySetInnerHTML={{ __html: item.custom_icon.includes('data:') ? `<img src="${item.custom_icon}" class="w-6 h-6 inline-block" />` : item.custom_icon }} />
                                              ) : (
                                                item.type === 'folder' ? '📁' : 
                                                item.file_type?.includes('pdf') ? '📄' :
                                                item.file_type?.includes('image') ? '🖼️' :
                                                item.file_type?.includes('video') ? '🎬' :
                                                item.file_type?.includes('word') ? '📝' :
                                                item.file_type?.includes('excel') ? '📊' :
                                                '📎'
                                              )}
                                            </span>
                                            <span className="font-medium">{item.name}</span>
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                          {item.type === 'folder' ? 'Pasta' : 'Arquivo'}
                                        </td>
                                        <td className="px-4 py-3 text-right text-sm text-gray-600">
                                          {item.type === 'folder' ? '--' : 
                                           item.size < 1024 ? `${item.size} B` :
                                           item.size < 1024 * 1024 ? `${(item.size / 1024).toFixed(1)} KB` :
                                           `${(item.size / (1024 * 1024)).toFixed(1)} MB`}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                          {item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' }) : '--'}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                          <div className="relative inline-block">
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setShowActionMenu(showActionMenu === item.id ? null : item.id);
                                              }}
                                              className="px-2 py-1 hover:bg-gray-200 rounded text-gray-700 font-bold"
                                            >
                                              ⋮
                                            </button>
                                            
                                            {/* Dropdown menu */}
                                            {showActionMenu === item.id && (
                                              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-300 rounded shadow-lg min-w-[200px]" style={{ zIndex: 9999 }}>
                                                {item.type === 'file' && (
                                                  <>
                                                    <button onClick={(e) => { e.stopPropagation(); downloadFileManagerFile(item.id, item.name); setShowActionMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm">
                                                      📥 Download
                                                    </button>
                                                    <button onClick={(e) => { e.stopPropagation(); generateShareLink(item); setShowActionMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm">
                                                      🔗 Compartilhar
                                                    </button>
                                                  </>
                                                )}
                                                <button onClick={(e) => { e.stopPropagation(); setFileManagerSelectedItems([item.id]); copyToClipboard(); setShowActionMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm">
                                                  📋 Copiar
                                                </button>
                                                <button onClick={(e) => { e.stopPropagation(); setFileManagerSelectedItems([item.id]); cutToClipboard(); setShowActionMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm">
                                                  ✂️ Recortar
                                                </button>
                                                <button onClick={(e) => { e.stopPropagation(); setFileManagerRenameItemId(item.id); setFileManagerRenameNewName(item.name); setShowRenameModal(true); setShowActionMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm">
                                                  ✏️ Renomear
                                                </button>
                                                <div className="border-t border-gray-200 my-1"></div>
                                                {item.type === 'folder' && (
                                                  <button onClick={(e) => { e.stopPropagation(); setSelectedFolderForIcon(item); setShowFolderIconModal(true); setShowActionMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm">
                                                    🎨 Alterar Ícone
                                                  </button>
                                                )}
                                                <button onClick={(e) => { e.stopPropagation(); setSelectedItemForProperties(item); setShowPropertiesModal(true); setShowActionMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm">
                                                  📋 Propriedades
                                                </button>
                                                <div className="border-t border-gray-200 my-1"></div>
                                                <button onClick={(e) => { e.stopPropagation(); deleteFileManagerItem(item.id, item.name); setShowActionMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm text-red-600">
                                                  🗑️ Deletar
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                              
                              {fileManagerItems.filter(item => !fileManagerSearchQuery || item.name.toLowerCase().includes(fileManagerSearchQuery.toLowerCase())).length === 0 && (
                                <div className="text-center py-12 text-gray-500">
                                  <p className="text-lg mb-2">📂 Pasta vazia</p>
                                  <p className="text-sm">Crie pastas ou faça upload de arquivos</p>
                                </div>
                              )}
                            </div>
                            
                            {/* Lista Mobile Compacta */}
                            <div className="bg-white border rounded-lg overflow-hidden md:hidden">
                              <div className="divide-y divide-gray-200">
                                {fileManagerItems
                                  .filter(item => !fileManagerSearchQuery || item.name.toLowerCase().includes(fileManagerSearchQuery.toLowerCase()))
                                  .map(item => (
                                    <div
                                      key={item.id}
                                      className="p-3 hover:bg-gray-50 transition cursor-pointer"
                                      onClick={() => {
                                        if (item.type === 'folder') {
                                          navigateToFolder(item.full_path);
                                        } else {
                                          previewFileManagerFile(item);
                                        }
                                      }}
                                    >
                                      {/* Linha 1: Checkbox + Ícone + Nome + Ação */}
                                      <div className="flex items-center gap-2 mb-1">
                                        <input
                                          type="checkbox"
                                          checked={fileManagerSelectedItems.includes(item.id)}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            toggleFileSelection(item.id);
                                          }}
                                          className="rounded flex-shrink-0"
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                        <span className="text-xl flex-shrink-0">
                                          {item.type === 'folder' ? '📁' : 
                                           item.file_type?.includes('pdf') ? '📄' :
                                           item.file_type?.includes('image') ? '🖼️' :
                                           item.file_type?.includes('video') ? '🎬' :
                                           item.file_type?.includes('word') ? '📝' :
                                           item.file_type?.includes('excel') ? '📊' :
                                           '📎'}
                                        </span>
                                        <span className="font-medium text-gray-800 flex-1 truncate">
                                          {item.name}
                                        </span>
                                        {item.type === 'folder' ? (
                                          <span className="text-gray-400 flex-shrink-0">→</span>
                                        ) : (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              // Abrir menu de ações
                                              const actions = [
                                                { label: '📥 Download', action: () => downloadFileManagerFile(item.id, item.name) },
                                                { label: '🔗 Compartilhar', action: () => generateShareLink(item) },
                                                { label: '✏️ Renomear', action: () => {
                                                  setFileManagerRenameItemId(item.id);
                                                  setFileManagerRenameNewName(item.name);
                                                  setShowRenameModal(true);
                                                }},
                                                { label: '🗑️ Deletar', action: () => deleteFileManagerItem(item.id, item.name) }
                                              ];
                                              
                                              const choice = window.prompt(
                                                `Ações para "${item.name}":\n\n` +
                                                '1. Download\n2. Compartilhar\n3. Renomear\n4. Deletar\n\n' +
                                                'Digite o número (1-4):'
                                              );
                                              
                                              if (choice && actions[parseInt(choice) - 1]) {
                                                actions[parseInt(choice) - 1].action();
                                              }
                                            }}
                                            className="text-gray-500 hover:text-gray-700 flex-shrink-0 p-1"
                                          >
                                            ⋮
                                          </button>
                                        )}
                                      </div>
                                      
                                      {/* Linha 2: Informações secundárias */}
                                      <div className="text-xs text-gray-500 ml-9">
                                        {item.type === 'folder' ? (
                                          <span>Pasta • {item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' }) : '--'}</span>
                                        ) : (
                                          <span>
                                            {item.file_type?.includes('pdf') ? 'PDF' :
                                             item.file_type?.includes('image') ? 'Imagem' :
                                             item.file_type?.includes('video') ? 'Vídeo' :
                                             item.file_type?.includes('word') ? 'Word' :
                                             item.file_type?.includes('excel') ? 'Excel' :
                                             'Arquivo'}
                                            {' • '}
                                            {item.size < 1024 ? `${item.size} B` :
                                             item.size < 1024 * 1024 ? `${(item.size / 1024).toFixed(1)} KB` :
                                             `${(item.size / (1024 * 1024)).toFixed(1)} MB`}
                                            {' • '}
                                            {item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' }) : '--'}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                              
                              {fileManagerItems.filter(item => !fileManagerSearchQuery || item.name.toLowerCase().includes(fileManagerSearchQuery.toLowerCase())).length === 0 && (
                                <div className="text-center py-12 text-gray-500">
                                  <p className="text-lg mb-2">📂 Pasta vazia</p>
                                  <p className="text-sm">Crie pastas ou faça upload de arquivos</p>
                                </div>
                              )}
                            </div>
                          </>
                          )}
                          
                          {/* VIEW: GRID */}
                          {fileManagerViewMode === 'grid' && (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                              {fileManagerItems
                                .filter(item => !fileManagerSearchQuery || item.name.toLowerCase().includes(fileManagerSearchQuery.toLowerCase()))
                                .map(item => (
                                  <div
                                    key={item.id}
                                    className={`bg-white border-2 rounded-lg p-4 cursor-pointer transition hover:shadow-md ${
                                      fileManagerSelectedItems.includes(item.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                                    }`}
                                    onClick={(e) => {
                                      // Ignorar cliques em checkbox e botões
                                      if (e.target.tagName === 'INPUT' || 
                                          e.target.tagName === 'BUTTON' || 
                                          e.target.closest('button') ||
                                          e.target.closest('input')) {
                                        return;
                                      }
                                      
                                      // Clique no card: abrir pasta ou preview arquivo
                                      if (item.type === 'folder') {
                                        navigateToFolder(item.full_path);
                                      } else {
                                        // Arquivos: abrir preview
                                        previewFileManagerFile(item);
                                      }
                                    }}
                                  >
                                    <div className="card-content">
                                      <div className="flex justify-end mb-2">
                                        <input
                                          type="checkbox"
                                          checked={fileManagerSelectedItems.includes(item.id)}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            toggleFileSelection(item.id);
                                          }}
                                          className="rounded"
                                        />
                                      </div>
                                    
                                    <div className="text-center mb-3">
                                      <span className="text-6xl">
                                        {item.type === 'folder' ? '📁' : 
                                         item.file_type?.includes('pdf') ? '📄' :
                                         item.file_type?.includes('image') ? '🖼️' :
                                         item.file_type?.includes('video') ? '🎬' :
                                         item.file_type?.includes('word') ? '📝' :
                                         item.file_type?.includes('excel') ? '📊' :
                                         '📎'}
                                      </span>
                                    </div>
                                    
                                    <div className="text-sm font-medium text-gray-800 text-center mb-2 truncate" title={item.name}>
                                      {item.name}
                                    </div>
                                    
                                    <div className="text-xs text-gray-500 text-center">
                                      {item.type === 'folder' ? 'Pasta' : 
                                       item.size < 1024 * 1024 ? `${(item.size / 1024).toFixed(1)} KB` :
                                       `${(item.size / (1024 * 1024)).toFixed(1)} MB`}
                                    </div>
                                    
                                    <div className="flex justify-center gap-1 mt-3">
                                      {item.type === 'file' && (
                                        <>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              downloadFileManagerFile(item.id, item.name);
                                            }}
                                            className="p-1 hover:bg-gray-100 rounded"
                                            title="Download"
                                          >
                                            <Download className="h-3 w-3" />
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              generateShareLink(item);
                                            }}
                                            className="p-1 hover:bg-gray-100 rounded"
                                            title="Compartilhar"
                                          >
                                            🔗
                                          </button>
                                        </>
                                      )}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setFileManagerRenameItemId(item.id);
                                          setFileManagerRenameNewName(item.name);
                                          setShowRenameModal(true);
                                        }}
                                        className="p-1 hover:bg-gray-100 rounded"
                                        title="Renomear"
                                      >
                                        <Edit className="h-3 w-3" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          deleteFileManagerItem(item.id, item.name);
                                        }}
                                        className="p-1 hover:bg-red-100 rounded text-red-600"
                                        title="Deletar"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </button>
                                    </div>
                                    </div>
                                  </div>
                                ))}
                              
                              {fileManagerItems.filter(item => !fileManagerSearchQuery || item.name.toLowerCase().includes(fileManagerSearchQuery.toLowerCase())).length === 0 && (
                                <div className="col-span-full text-center py-12 text-gray-500">
                                  <p className="text-lg mb-2">📂 Pasta vazia</p>
                                  <p className="text-sm">Crie pastas ou faça upload de arquivos</p>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Info Footer */}
                          <div className="bg-gray-50 border rounded-lg p-3 text-sm text-gray-600">
                            📊 Total: {fileManagerItems.length} item(s) 
                            {fileManagerSelectedItems.length > 0 && ` | ${fileManagerSelectedItems.length} selecionado(s)`}
                            {fileManagerClipboard.items.length > 0 && ` | 📋 ${fileManagerClipboard.items.length} na área de transferência`}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  
                  {/* MODAIS DO FILE MANAGER */}
                  
                  {/* Modal: Criar Pasta */}
                  <Dialog open={showCreateFolderModal} onOpenChange={setShowCreateFolderModal}>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>📁 Nova Pasta</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>Nome da Pasta</Label>
                          <Input
                            value={fileManagerNewFolderName}
                            onChange={(e) => setFileManagerNewFolderName(e.target.value)}
                            placeholder="Ex: Contratos 2025"
                            onKeyDown={(e) => e.key === 'Enter' && createFolder()}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={createFolder} className="flex-1">Criar</Button>
                          <Button onClick={() => setShowCreateFolderModal(false)} variant="outline">Cancelar</Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  {/* Modal: Upload */}
                  <Dialog open={showFileUploadModal} onOpenChange={setShowFileUploadModal}>
                    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>📤 Upload de Arquivos</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div 
                          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition"
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                              uploadFileManagerFiles(e.dataTransfer.files);
                            }
                          }}
                        >
                          <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                          <p className="text-lg font-medium mb-2">Arraste arquivos aqui ou clique para selecionar</p>
                          <Input
                            type="file"
                            multiple
                            onChange={(e) => {
                              if (e.target.files && e.target.files.length > 0) {
                                uploadFileManagerFiles(e.target.files);
                              }
                            }}
                            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.dng,.mp4,.mov,.avi,.doc,.docx,.xls,.xlsx,.txt,.csv"
                            className="mt-4"
                          />
                          <p className="text-sm text-gray-500 mt-2">
                            Tipos: PDF, Imagens (JPG, PNG, GIF, WebP, DNG), Vídeos, Word, Excel, TXT
                          </p>
                          <p className="text-xs text-gray-400 mt-1">Sem limite de tamanho</p>
                        </div>
                        
                        {fileManagerUploadingFiles.length > 0 && (
                          <div className="space-y-2">
                            <h4 className="font-semibold text-sm">Enviando:</h4>
                            {fileManagerUploadingFiles.map(file => (
                              <div key={file.name} className="bg-gray-50 rounded p-2">
                                <div className="flex justify-between text-xs mb-1">
                                  <span>{file.name}</span>
                                  <span>{fileManagerUploadProgress[file.name] || 0}%</span>
                                </div>
                                <div className="bg-gray-200 rounded-full h-2">
                                  <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${fileManagerUploadProgress[file.name] || 0}%` }} />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  {/* Modal: Renomear */}
                  <Dialog open={showRenameModal} onOpenChange={setShowRenameModal}>
                    <DialogContent className="max-h-[85vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>✏️ Renomear Item</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>Novo Nome</Label>
                          <Input
                            value={fileManagerRenameNewName}
                            onChange={(e) => setFileManagerRenameNewName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && renameFileManagerItem()}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={renameFileManagerItem} className="flex-1">Renomear</Button>
                          <Button onClick={() => setShowRenameModal(false)} variant="outline">Cancelar</Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  {/* Modal: Link de Compartilhamento */}
                  <Dialog open={showShareLinkModal} onOpenChange={setShowShareLinkModal}>
                    <DialogContent className="max-h-[85vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>🔗 Link de Compartilhamento</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>Arquivo: {shareLinkItem?.name}</Label>
                        </div>
                        <div>
                          <Label>Link Gerado (válido por 7 dias)</Label>
                          <div className="flex gap-2">
                            <Input value={generatedShareLink} readOnly className="flex-1" />
                            <Button
                              onClick={() => {
                                navigator.clipboard.writeText(generatedShareLink);
                                toast.success('Link copiado!');
                              }}
                              variant="outline"
                            >
                              📋 Copiar
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          💡 Qualquer pessoa com este link pode baixar o arquivo nos próximos 7 dias.
                        </p>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Modal: Propriedades do Item */}
                  <Dialog open={showPropertiesModal} onOpenChange={setShowPropertiesModal}>
                    <DialogContent className="max-h-[85vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>📋 Propriedades</DialogTitle>
                      </DialogHeader>
                      {selectedItemForProperties && (
                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between border-b pb-2">
                            <span className="font-semibold">Nome:</span>
                            <span className="text-gray-700">{selectedItemForProperties.name}</span>
                          </div>
                          <div className="flex justify-between border-b pb-2">
                            <span className="font-semibold">Tipo:</span>
                            <span className="text-gray-700">{selectedItemForProperties.type === 'folder' ? 'Pasta' : 'Arquivo'}</span>
                          </div>
                          {selectedItemForProperties.type === 'file' && (
                            <div className="flex justify-between border-b pb-2">
                              <span className="font-semibold">Tamanho:</span>
                              <span className="text-gray-700">
                                {selectedItemForProperties.size < 1024 ? `${selectedItemForProperties.size} B` :
                                 selectedItemForProperties.size < 1024 * 1024 ? `${(selectedItemForProperties.size / 1024).toFixed(2)} KB` :
                                 `${(selectedItemForProperties.size / (1024 * 1024)).toFixed(2)} MB`}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between border-b pb-2">
                            <span className="font-semibold">Caminho:</span>
                            <span className="text-gray-700 truncate max-w-xs">{selectedItemForProperties.full_path}</span>
                          </div>
                          <div className="flex justify-between border-b pb-2">
                            <span className="font-semibold">Criado em:</span>
                            <span className="text-gray-700">
                              {selectedItemForProperties.created_at ? formatNyDateTime(selectedItemForProperties.created_at) : '--'}
                            </span>
                          </div>
                          {selectedItemForProperties.created_by && (
                            <div className="flex justify-between border-b pb-2">
                              <span className="font-semibold">Criado por:</span>
                              <span className="text-gray-700">{selectedItemForProperties.created_by}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>

                  {/* Modal: Alterar Ícone da Pasta */}
                  <Dialog open={showFolderIconModal} onOpenChange={setShowFolderIconModal}>
                    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>🎨 Alterar Ícone da Pasta</DialogTitle>
                      </DialogHeader>
                      {selectedFolderForIcon && (
                        <div className="space-y-4">
                          <div>
                            <Label>Pasta: {selectedFolderForIcon.name}</Label>
                          </div>
                          
                          <div>
                            <Label className="mb-2 block">Galeria de Ícones</Label>
                            <div className="grid grid-cols-10 gap-2 p-4 bg-gray-50 rounded-lg max-h-60 overflow-y-auto">
                              {iconGallery.map((emoji, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => {
                                    setEmojiIconForFolder(selectedFolderForIcon.id, emoji);
                                  }}
                                  className="text-3xl p-2 hover:bg-gray-200 rounded transition"
                                  title={`Usar ${emoji}`}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          <div className="border-t pt-4">
                            <Label className="mb-2 block">Ou faça upload de um ícone personalizado (PNG/SVG, máx 1MB)</Label>
                            <Input
                              type="file"
                              accept=".png,.svg,image/png,image/svg+xml"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  if (file.size > 1024 * 1024) {
                                    toast.error('Arquivo muito grande! Máximo: 1MB');
                                    return;
                                  }
                                  uploadCustomFolderIcon(selectedFolderForIcon.id, file);
                                }
                              }}
                            />
                            <p className="text-xs text-gray-500 mt-2">
                              💡 Formatos aceitos: PNG, SVG | Tamanho máximo: 1MB
                            </p>
                          </div>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>



                  {adminSubTab === 'relatorios' && (
                    <div className="space-y-6">
                      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
                        <h3 className="font-semibold text-lg mb-4 text-purple-800">📊 Relatórios de Atividade</h3>
                        <p className="text-purple-700 mb-4">
                          Monitore todas as atividades dos usuários no sistema em tempo real.
                        </p>
                        
                        {/* Stats Cards */}
                        {activityStats && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div className="bg-white rounded-lg p-4 border">
                              <h4 className="font-semibold text-gray-700 mb-2">📈 Total de Logs</h4>
                              <div className="text-2xl font-bold text-blue-600">
                                {activityStats.total_logs?.toLocaleString() || 0}
                              </div>
                            </div>
                            <div className="bg-white rounded-lg p-4 border">
                              <h4 className="font-semibold text-gray-700 mb-2">👤 Usuários Ativos</h4>
                              <div className="text-2xl font-bold text-green-600">
                                {activityStats.user_stats?.length || 0}
                              </div>
                            </div>
                            <div className="bg-white rounded-lg p-4 border">
                              <h4 className="font-semibold text-gray-700 mb-2">🔥 Ações (30 dias)</h4>
                              <div className="text-2xl font-bold text-purple-600">
                                {activityStats.action_stats?.reduce((sum, stat) => sum + stat.count, 0) || 0}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Filters */}
                        <div className="bg-white rounded-lg p-4 border mb-6">
                          <h4 className="font-semibold mb-3">🔍 Filtros</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <Label className="text-sm">Usuário:</Label>
                              <Input
                                placeholder="Filtrar por usuário..."
                                value={activityFilters.user}
                                onChange={(e) => setActivityFilters(prev => ({ ...prev, user: e.target.value, page: 1 }))}
                              />
                            </div>
                            <div>
                              <Label className="text-sm">Ação:</Label>
                              <Select
                                value={activityFilters.action || "all"}
                                onValueChange={(value) => setActivityFilters(prev => ({ ...prev, action: value === "all" ? "" : value, page: 1 }))}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Todas as ações" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">Todas as ações</SelectItem>
                                  <SelectItem value="login">🔐 Login</SelectItem>
                                  <SelectItem value="create_bill">📝 Criar Labor Bill</SelectItem>
                                  <SelectItem value="edit_bill">✏️ Editar Labor Bill</SelectItem>
                                  <SelectItem value="send_email">📧 Enviar Email</SelectItem>
                                  <SelectItem value="download_pdf">📄 Download PDF</SelectItem>
                                  <SelectItem value="import_pdf">📥 Importar PDF</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex items-end">
                              <Button onClick={loadActivityLogs} disabled={activityLoading} className="w-full">
                                {activityLoading ? "Carregando..." : "🔍 Filtrar"}
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Activity Logs Table */}
                        <div className="bg-white rounded-lg border">
                          <div className="p-4 border-b">
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                              <h4 className="font-semibold">📋 Logs de Atividade</h4>
                              <div className="flex gap-2">
                                <Button onClick={loadActivityStats} variant="outline" size="sm" className="text-xs">
                                  🔄 {language === 'pt-BR' ? 'Atualizar Stats' : 'Refresh Stats'}
                                </Button>
                                <Button 
                                  onClick={async () => {
                                    try {
                                      setManualMatchLoading(true);
                                      
                                      // Solução temporária: mostrar alerta manual
                                      toast.success(
                                        'Para corrigir matches órfãos:\n\n' +
                                        '1. Identifique clientes com erro "Labor Bill não encontrado"\n' +
                                        '2. Processe um novo arquivo Excel na aba Payroll\n' +
                                        '3. Faça manual match para os clientes problemáticos\n\n' +
                                        'Isso irá sobrescrever os matches órfãos.',
                                        { duration: 8000 }
                                      );
                                      
                                    } catch (error) {
                                      console.error('❌ Error:', error);
                                      toast.error('Erro inesperado');
                                    } finally {
                                      setManualMatchLoading(false);
                                    }
                                  }} 
                                  variant="outline" 
                                  size="sm" 
                                  className="text-xs bg-blue-50 hover:bg-blue-100 border-blue-200"
                                  disabled={manualMatchLoading}
                                >
                                  {manualMatchLoading ? '💡 Lendo...' : '💡 Como Corrigir Órfãos'}
                                </Button>
                              </div>
                            </div>
                          </div>
                          
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="text-left p-3 font-medium">Data/Hora</th>
                                  <th className="text-left p-3 font-medium">Usuário</th>
                                  <th className="text-left p-3 font-medium">Ação</th>
                                  <th className="text-left p-3 font-medium">Descrição</th>
                                  <th className="text-left p-3 font-medium">IP</th>
                                </tr>
                              </thead>
                              <tbody>
                                {activityLogs.length > 0 ? (
                                  activityLogs.map((log, index) => {
                                    const isFomReview = log.action === 'labor_bill_fom_reviewed' || (log.details && log.details.type === 'labor_bill_fom_review');
                                    return (
                                    <tr
                                      key={index}
                                      className={`border-t hover:bg-gray-50 ${isFomReview ? 'cursor-pointer' : ''}`}
                                      onClick={isFomReview ? () => { setFomReview(log.details); setShowFomReviewModal(true); } : undefined}
                                      data-testid={isFomReview ? 'activity-fom-review-row' : undefined}
                                    >
                                      <td className="p-3 text-sm">
                                        {formatDateTime(log.timestamp)}
                                      </td>
                                      <td className="p-3">
                                        <Badge variant="outline">
                                          {log.full_name ? `${log.full_name} (${log.user})` : log.user}
                                        </Badge>
                                      </td>
                                      <td className="p-3">
                                        <Badge variant={
                                          log.action === 'login' ? 'default' :
                                          log.action === 'create_bill' ? 'secondary' :
                                          log.action === 'edit_bill' ? 'outline' :
                                          log.action === 'send_email' ? 'default' :
                                          'secondary'
                                        }>
                                          {log.action === 'login' ? '🔐 Login' :
                                           log.action === 'create_bill' ? '📝 Criar' :
                                           log.action === 'edit_bill' ? '✏️ Editar' :
                                           log.action === 'send_email' ? '📧 Email' :
                                           log.action === 'download_pdf' ? '📄 PDF' :
                                           log.action === 'import_pdf' ? '📥 Import' :
                                           log.action === 'labor_bill_fom_reviewed' ? '👷 Revisão FOM' :
                                           log.action}
                                        </Badge>
                                      </td>
                                      <td className="p-3 text-sm max-w-xs truncate">
                                        {log.description}
                                        {isFomReview ? <span className="ml-2 text-emerald-600 underline text-xs">ver detalhes</span> : null}
                                      </td>
                                      <td className="p-3 text-sm text-gray-500">
                                        {log.ip_address || '-'}
                                      </td>
                                    </tr>
                                    );
                                  })
                                ) : (
                                  <tr>
                                    <td colSpan="5" className="p-6 text-center text-gray-500">
                                      {activityLoading ? "Carregando logs..." : "Nenhum log encontrado"}
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          
                          {/* Pagination */}
                          <div className="p-4 border-t flex justify-between items-center">
                            <span className="text-sm text-gray-500">
                              Página {activityFilters.page} • {activityFilters.limit} por página
                            </span>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setActivityFilters(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                                disabled={activityFilters.page <= 1 || activityLoading}
                              >
                                ← Anterior
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setActivityFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                                disabled={activityLogs.length < activityFilters.limit || activityLoading}
                              >
                                Próxima →
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}



                
                {/* Empty Trash Confirmation Dialog - DENTRO da aba Admin */}
                <Dialog open={emptyTrashOpen} onOpenChange={setEmptyTrashOpen}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Esvaziar Lixeira de {selectedPM}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <p className="text-sm text-slate-600">
                        Esta ação irá apagar permanentemente <strong>TODOS</strong> os itens da lixeira de <strong>{selectedPM}</strong>. 
                        Esta ação não pode ser desfeita.
                      </p>
                      <div className="bg-red-50 border border-red-200 rounded p-3">
                        <div className="text-red-800 text-sm font-medium">⚠️ Atenção!</div>
                        <div className="text-red-700 text-sm mt-1">
                          Todos os rascunhos e históricos excluídos de <strong>{selectedPM}</strong> serão removidos permanentemente do sistema.
                        </div>
                      </div>
                      <div>
                        <Label>Digite "DELETE" para confirmar:</Label>
                        <Input 
                          placeholder="Digite DELETE" 
                          value={emptyTrashConfirmText} 
                          onChange={(e) => setEmptyTrashConfirmText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && emptyTrashConfirmText.trim().toUpperCase() === 'DELETE') {
                              emptyTrash();
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setEmptyTrashOpen(false);
                          setEmptyTrashConfirmText('');
                        }} 
                        disabled={emptyTrashLoading}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        className="bg-red-600 hover:bg-red-700" 
                        disabled={emptyTrashConfirmText.toUpperCase() !== 'DELETE' || emptyTrashLoading} 
                        onClick={() => {
                          console.log('🗑️ Modal emptyTrash button clicked');
                          emptyTrash();
                        }}
                      >
                        {emptyTrashLoading ? 'Esvaziando...' : `Esvaziar Lixeira de ${selectedPM}`}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                  {/* HELPER CONTROL SUBTAB */}
                  {adminSubTab === 'helper' && (
                    <div className="space-y-6">
                      {/* Cabeçalho com botões de ação */}
                      <div className="flex flex-wrap justify-between items-center border-b pb-4 gap-2">
                        <h3 className="text-lg font-semibold">📋 Relatórios Semanais</h3>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              loadHelperTeams();
                              setShowTeamsConfigModal(true);
                            }}
                          >
                            ⚙️ Equipes
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setShowMonthlyViewModal(true)}
                          >
                            📊 Relatório
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => setShowHelperControlModal(true)}
                          >
                            + Novo Relatório
                          </Button>
                        </div>
                      </div>

                      {/* CONTEÚDO: RELATÓRIOS (sempre visível) */}
                      <div>
                        {helperLoading ? (
                            <div className="text-center py-12">Carregando...</div>
                          ) : helperControls.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                              <p>Nenhum relatório criado</p>
                            </div>
                          ) : (
                            <div className="grid gap-4">
                              {helperControls.map(control => (
                                <Card key={control.id} className="cursor-pointer hover:shadow-md transition" onClick={() => loadHelperControlDetails(control.id)}>
                                  <CardContent className="p-4">
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <h4 className="font-semibold">
                                          {control.equipe} - Semana {control.semana}/{control.ano}
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                          {control.data_inicio} até {control.data_fim}
                                        </p>
                                        <p className="text-sm mt-2">
                                          📅 <strong>{control.dias_trabalhados}</strong> dias trabalhados
                                        </p>
                                      </div>
                                      <div className="text-right">
                                        <p className="text-sm text-gray-600">Valor Final:</p>
                                        <p className="text-2xl font-bold text-green-600">
                                          R${control.valor_final.toFixed(2)}
                                        </p>
                                      </div>
                                    </div>
                                    
                                    <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                                      <div className="bg-blue-50 p-2 rounded">
                                        <p className="text-gray-600">Trabalho</p>
                                        <p className="font-semibold">R${control.valor_total_trabalho.toFixed(2)}</p>
                                      </div>
                                      <div className="bg-orange-50 p-2 rounded">
                                        <p className="text-gray-600">Despesas</p>
                                        <p className="font-semibold">R${control.valor_despesas.toFixed(2)}</p>
                                      </div>
                                      <div className="bg-green-50 p-2 rounded">
                                        <p className="text-gray-600">Total</p>
                                        <p className="font-semibold">R${control.valor_final.toFixed(2)}</p>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          )}
                        </div>


                      {/* MODAIS */}

                      {/* Modal: Criar/Editar Equipe */}
                      <Dialog open={showHelperTeamModal} onOpenChange={setShowHelperTeamModal}>
                        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>
                              {editingHelperTeam ? '✏️ Editar Equipe' : '➕ Nova Equipe Helper'}
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label>Associar com PM (Schedule)</Label>
                              <p className="text-xs text-gray-500 mb-1">O calendário e jobs deste PM serão espelhados para o daily_leader</p>
                              <Select
                                value={helperTeamForm.equipe}
                                onValueChange={(value) => setHelperTeamForm(prev => ({ ...prev, equipe: value }))}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {pmOptions.filter(pm => pm !== 'ADMIN' && pm !== 'Johnny').map(pm => (
                                    <SelectItem key={pm} value={pm}>{pm}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label>Nome do Líder</Label>
                                <Input
                                  value={helperTeamForm.lider_nome}
                                  onChange={(e) => setHelperTeamForm(prev => ({ ...prev, lider_nome: e.target.value }))}
                                  placeholder="Ex: João Silva"
                                />
                              </div>
                              <div>
                                <Label>Diária do Líder (R$)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={helperTeamForm.lider_diaria}
                                  onChange={(e) => setHelperTeamForm(prev => ({ ...prev, lider_diaria: parseFloat(e.target.value) || 0 }))}
                                  placeholder="Ex: 150.00"
                                />
                              </div>
                            </div>

                            {/* Login do Líder (Daily Leader) */}
                            <div className="border-t pt-4">
                              <h4 className="font-semibold text-sm mb-2">Login do Líder (PMd)</h4>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label>Username</Label>
                                  <Input
                                    value={helperTeamForm.leader_username || ''}
                                    onChange={(e) => setHelperTeamForm(prev => ({ ...prev, leader_username: e.target.value }))}
                                    placeholder="Ex: PMd1"
                                  />
                                </div>
                                <div>
                                  <Label>Senha {editingHelperTeam?.leader_username ? '(deixe vazio para manter)' : ''}</Label>
                                  <Input
                                    type="password"
                                    value={helperTeamForm.leader_password || ''}
                                    onChange={(e) => setHelperTeamForm(prev => ({ ...prev, leader_password: e.target.value }))}
                                    placeholder={editingHelperTeam?.leader_username ? '••••••••' : 'Senha'}
                                  />
                                </div>
                              </div>
                            </div>

                            <div>
                              <div className="flex justify-between items-center mb-2">
                                <Label>Helpers</Label>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setHelperTeamForm(prev => ({
                                      ...prev,
                                      helpers: [...prev.helpers, { id: Date.now().toString(), nome: '', diaria: 0 }]
                                    }));
                                  }}
                                >
                                  + Adicionar Helper
                                </Button>
                              </div>

                              {helperTeamForm.helpers.length === 0 ? (
                                <p className="text-sm text-gray-500 py-4 text-center border-2 border-dashed rounded">
                                  Nenhum helper adicionado
                                </p>
                              ) : (
                                <div className="space-y-2">
                                  {helperTeamForm.helpers.map((helper, index) => (
                                    <div key={helper.id} className="flex gap-2">
                                      <Input
                                        placeholder="Nome"
                                        value={helper.nome}
                                        onChange={(e) => {
                                          const newHelpers = [...helperTeamForm.helpers];
                                          newHelpers[index].nome = e.target.value;
                                          setHelperTeamForm(prev => ({ ...prev, helpers: newHelpers }));
                                        }}
                                      />
                                      <Input
                                        type="number"
                                        step="0.01"
                                        placeholder="Diária"
                                        value={helper.diaria}
                                        onChange={(e) => {
                                          const newHelpers = [...helperTeamForm.helpers];
                                          newHelpers[index].diaria = parseFloat(e.target.value) || 0;
                                          setHelperTeamForm(prev => ({ ...prev, helpers: newHelpers }));
                                        }}
                                        className="w-32"
                                      />
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-red-600"
                                        onClick={() => {
                                          setHelperTeamForm(prev => ({
                                            ...prev,
                                            helpers: prev.helpers.filter((_, i) => i !== index)
                                          }));
                                        }}
                                      >
                                        🗑️
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div className="flex gap-2 pt-4">
                              <Button onClick={saveHelperTeam} className="flex-1" disabled={helperLoading}>
                                {helperLoading ? 'Salvando...' : 'Salvar Equipe'}
                              </Button>
                              <Button variant="outline" onClick={() => setShowHelperTeamModal(false)}>
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>

                      {/* Modal: Criar Controle */}
                      <Dialog open={showHelperControlModal} onOpenChange={setShowHelperControlModal}>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>📋 Novo Controle Semanal</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label>Equipe</Label>
                              <Select
                                value={helperControlForm.team_id}
                                onValueChange={(value) => setHelperControlForm(prev => ({ ...prev, team_id: value }))}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione a equipe" />
                                </SelectTrigger>
                                <SelectContent>
                                  {helperTeams.map(team => (
                                    <SelectItem key={team.id} value={team.id}>
                                      {team.equipe} - {team.lider_nome}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label>Ano</Label>
                                <Input
                                  type="number"
                                  value={helperControlForm.ano}
                                  onChange={(e) => setHelperControlForm(prev => ({ ...prev, ano: parseInt(e.target.value) }))}
                                />
                              </div>
                              <div>
                                <Label>Semana do Ano (1-52)</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  max="52"
                                  value={helperControlForm.semana}
                                  onChange={(e) => {
                                    const semana = parseInt(e.target.value);
                                    const dates = getWeekDates(helperControlForm.ano, semana);
                                    setHelperControlForm(prev => ({
                                      ...prev,
                                      semana,
                                      data_inicio: dates.start,
                                      data_fim: dates.end
                                    }));
                                  }}
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label>Data Início</Label>
                                <Input
                                  type="date"
                                  value={helperControlForm.data_inicio}
                                  onChange={(e) => setHelperControlForm(prev => ({ ...prev, data_inicio: e.target.value }))}
                                />
                              </div>
                              <div>
                                <Label>Data Fim</Label>
                                <Input
                                  type="date"
                                  value={helperControlForm.data_fim}
                                  onChange={(e) => setHelperControlForm(prev => ({ ...prev, data_fim: e.target.value }))}
                                />
                              </div>
                            </div>

                            <div className="bg-blue-50 p-3 rounded text-sm">
                              <p className="font-medium mb-1">ℹ️ Como funciona:</p>
                              <ul className="list-disc list-inside space-y-1 text-gray-700">
                                <li>O sistema buscará automaticamente os trabalhos no calendário</li>
                                <li>Contará os dias únicos que a equipe trabalhou</li>
                                <li>Calculará o valor (dias × diárias)</li>
                              </ul>
                            </div>

                            <div className="flex gap-2">
                              <Button onClick={createHelperControl} className="flex-1" disabled={helperLoading}>
                                {helperLoading ? 'Criando...' : 'Criar Controle'}
                              </Button>
                              <Button variant="outline" onClick={() => setShowHelperControlModal(false)}>
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>

                      {/* Modal: Detalhes do Controle + Despesas */}
                      <Dialog open={!!selectedHelperControl} onOpenChange={(open) => !open && setSelectedHelperControl(null)}>
                        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto mx-1 sm:mx-auto p-3 sm:p-6">
                          <DialogHeader>
                            <DialogTitle>
                              📋 Controle - {selectedHelperControl?.equipe} (Semana {selectedHelperControl?.semana}/{selectedHelperControl?.ano})
                            </DialogTitle>
                          </DialogHeader>

                          {selectedHelperControl && (
                            <div className="space-y-4">
                              {/* Resumo */}
                              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                                <div className="bg-gray-50 p-2 sm:p-3 rounded">
                                  <p className="text-xs sm:text-sm text-gray-600">Período</p>
                                  <p className="font-medium text-xs sm:text-base">{selectedHelperControl.data_inicio} - {selectedHelperControl.data_fim}</p>
                                </div>
                                <div className="bg-gray-50 p-2 sm:p-3 rounded">
                                  <p className="text-xs sm:text-sm text-gray-600">Dias Trabalhados</p>
                                  <p className="font-medium text-xs sm:text-base">{selectedHelperControl.dias_trabalhados} dias</p>
                                </div>
                              </div>

                              {/* Valores */}
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                                <div className="bg-blue-50 p-2 sm:p-3 rounded text-center">
                                  <p className="text-xs sm:text-sm text-gray-600">Trabalho</p>
                                  <p className="text-base sm:text-xl font-bold text-blue-600">R${selectedHelperControl.valor_total_trabalho.toFixed(2)}</p>
                                </div>
                                <div className="bg-orange-50 p-2 sm:p-3 rounded text-center">
                                  <p className="text-xs sm:text-sm text-gray-600">Despesas</p>
                                  <p className="text-base sm:text-xl font-bold text-orange-600">R${selectedHelperControl.valor_despesas.toFixed(2)}</p>
                                </div>
                                <div className="bg-green-50 p-2 sm:p-3 rounded text-center">
                                  <p className="text-xs sm:text-sm text-gray-600">Total Final</p>
                                  <p className="text-base sm:text-xl font-bold text-green-600">R${selectedHelperControl.valor_final.toFixed(2)}</p>
                                </div>
                              </div>

                              {/* Relatório de Dias Trabalhados */}
                              <DaysReport 
                                control={selectedHelperControl} 
                                team={helperTeams.find(t => t.id === selectedHelperControl.team_id)}
                                onUpdate={() => loadHelperControlDetails(selectedHelperControl.id)}
                              />

                              {/* Despesas */}
                              <div>
                                <div className="flex flex-wrap justify-between items-center mb-3 gap-2">
                                  <h4 className="font-semibold text-sm sm:text-base">Despesas</h4>
                                  <Button size="sm" className="text-xs" onClick={() => setShowHelperExpenseModal(true)}>
                                    + Adicionar
                                  </Button>
                                </div>

                                {!selectedHelperControl.expenses || selectedHelperControl.expenses.length === 0 ? (
                                  <p className="text-center text-gray-500 py-6 border-2 border-dashed rounded">
                                    Nenhuma despesa adicionada
                                  </p>
                                ) : (
                                  <div className="space-y-2">
                                    {selectedHelperControl.expenses.map(expense => (
                                      <div key={expense.id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-2 sm:p-3 bg-gray-50 rounded border">
                                        <div className="min-w-0">
                                          <p className="font-medium text-sm truncate">{expense.descricao}</p>
                                          <p className="text-xs text-gray-600">
                                            {expense.tipo === 'ai' ? '🤖 Extraído por IA' : '✍️ Manual'}
                                            {expense.arquivo_nome && ` • ${expense.arquivo_nome}`}
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-2 justify-end flex-shrink-0">
                                          <span className="font-bold text-orange-600 text-sm whitespace-nowrap">R${expense.valor.toFixed(2)}</span>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-red-600 text-xs px-2"
                                            onClick={() => deleteHelperExpense(expense.id)}
                                          >
                                            🗑️
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Botões de ação - Mais destaque */}
                              <div className="border-t pt-4 mt-4">
                                <div className="flex flex-col sm:flex-row gap-2">
                                  <Button 
                                    variant="default" 
                                    onClick={async () => {
                                      try {
                                        console.log('👁️ Visualizando PDF semanal...');
                                        
                                        const response = await axios.get(
                                          `${API}/helper-controls/${selectedHelperControl.id}/export-pdf`,
                                          { responseType: 'blob' }
                                        );
                                        
                                        const blob = new Blob([response.data], { type: 'application/pdf' });
                                        const url = window.URL.createObjectURL(blob);
                                        
                                        // Abrir no modal de PDF do sistema
                                        setPdfModalData({
                                          url: url,
                                          title: `Controle Semanal - ${selectedHelperControl.equipe} - Semana ${selectedHelperControl.semana}/${selectedHelperControl.ano}`
                                        });
                                        setShowPDFModal(true);
                                        
                                        toast.success('PDF carregado!');
                                      } catch (error) {
                                        console.error('Erro ao visualizar PDF:', error);
                                        toast.error('Erro ao visualizar PDF');
                                      }
                                    }}
                                    className="bg-purple-600 hover:bg-purple-700 flex-1"
                                  >
                                    👁️ Visualizar PDF
                                  </Button>
                                  <Button 
                                    variant="default" 
                                    onClick={async () => {
                                      try {
                                        console.log('📤 Compartilhando PDF semanal...');
                                        
                                        const response = await axios.get(
                                          `${API}/helper-controls/${selectedHelperControl.id}/export-pdf`,
                                          { responseType: 'blob' }
                                        );
                                        
                                        // Formatar datas (YYYY-MM-DD → MM-DD)
                                        const dataInicio = selectedHelperControl.data_inicio.split('-').slice(1).join('-'); // MM-DD
                                        const dataFim = selectedHelperControl.data_fim.split('-').slice(1).join('-'); // MM-DD
                                        const filename = `Controle_Semanal_${selectedHelperControl.equipe}_${dataInicio}_ao_${dataFim}.pdf`;
                                        const blob = new Blob([response.data], { type: 'application/pdf' });
                                        
                                        // Usar função de compartilhamento
                                        await downloadFile(blob, filename, 'application/pdf');
                                        
                                        toast.success('PDF compartilhado!');
                                      } catch (error) {
                                        console.error('Erro ao compartilhar PDF:', error);
                                        toast.error('Erro ao compartilhar PDF');
                                      }
                                    }}
                                    className="bg-blue-600 hover:bg-blue-700 flex-1"
                                  >
                                    📤 Compartilhar
                                  </Button>
                                </div>
                                
                                <div className="flex gap-2 mt-2">
                                  <Button variant="outline" onClick={() => deleteHelperControl(selectedHelperControl.id)} className="text-red-600 flex-1">
                                    🗑️ Deletar
                                  </Button>
                                  <Button variant="outline" onClick={() => setSelectedHelperControl(null)} className="flex-1">
                                    Fechar
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>

                      {/* Modal: Adicionar Despesa */}
                      <Dialog open={showHelperExpenseModal} onOpenChange={setShowHelperExpenseModal}>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>💰 Adicionar Despesa</DialogTitle>
                          </DialogHeader>

                          <div className="space-y-4">
                            {/* Tipo de despesa */}
                            <div className="flex gap-2">
                              <Button
                                variant={helperExpenseType === 'manual' ? 'default' : 'outline'}
                                onClick={() => setHelperExpenseType('manual')}
                                className="flex-1"
                              >
                                ✍️ Manual
                              </Button>
                              <Button
                                variant={helperExpenseType === 'ai' ? 'default' : 'outline'}
                                onClick={() => setHelperExpenseType('ai')}
                                className="flex-1"
                              >
                                🤖 Upload + IA
                              </Button>
                            </div>

                            {helperExpenseType === 'manual' ? (
                              <>
                                <div>
                                  <Label>Descrição</Label>
                                  <Input
                                    value={helperExpenseForm.descricao}
                                    onChange={(e) => setHelperExpenseForm(prev => ({ ...prev, descricao: e.target.value }))}
                                    placeholder="Ex: Gasolina"
                                  />
                                </div>
                                <div>
                                  <Label>Valor (R$)</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={helperExpenseForm.valor}
                                    onChange={(e) => setHelperExpenseForm(prev => ({ ...prev, valor: parseFloat(e.target.value) || 0 }))}
                                    placeholder="Ex: 150.00"
                                  />
                                </div>
                              </>
                            ) : (
                              <>
                                <div>
                                  <Label>Upload do Arquivo (PDF ou Imagem)</Label>
                                  <Input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => setHelperExpenseFile(e.target.files?.[0] || null)}
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    A IA vai extrair automaticamente o valor da nota/recibo
                                  </p>
                                </div>
                              </>
                            )}

                            <div className="flex gap-2">
                              <Button
                                onClick={helperExpenseType === 'manual' ? addManualExpense : addAIExpense}
                                className="flex-1"
                                disabled={helperLoading}
                              >
                                {helperLoading ? 'Adicionando...' : 'Adicionar'}
                              </Button>
                              <Button variant="outline" onClick={() => {
                                setShowHelperExpenseModal(false);
                                setHelperExpenseForm({ descricao: '', valor: 0 });
                                setHelperExpenseFile(null);
                              }}>
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>

                      {/* MODAL: CONFIGURAÇÃO DE EQUIPES */}
                      <Dialog open={showTeamsConfigModal} onOpenChange={setShowTeamsConfigModal}>
                        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>⚙️ Configurar Equipes Helper</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div className="flex justify-end mb-4">
                              <Button onClick={() => {
                                setEditingHelperTeam(null);
                                setHelperTeamForm({
                                  lider_nome: '',
                                  lider_diaria: 0,
                                  helpers: [],
                                  equipe: 'PM1',
                                  leader_username: '',
                                  leader_password: ''
                                });
                                setShowHelperTeamModal(true);
                              }}>
                                + Nova Equipe
                              </Button>
                            </div>

                            {helperLoading ? (
                              <div className="text-center py-12">Carregando...</div>
                            ) : helperTeams.length === 0 ? (
                              <div className="text-center py-12 text-gray-500">
                                <p>Nenhuma equipe cadastrada</p>
                              </div>
                            ) : (
                              <div className="grid gap-4">
                                {helperTeams.map(team => (
                                  <Card key={team.id}>
                                    <CardContent className="p-4">
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <h4 className="font-semibold text-lg">{team.equipe}</h4>
                                          <p className="text-sm text-gray-500">
                                            Líder: {team.lider_nome} (R${team.lider_diaria}/dia)
                                          </p>
                                          {team.leader_username ? (
                                            <p className="text-xs text-blue-600">Login: {team.leader_username}</p>
                                          ) : (
                                            <p className="text-xs text-orange-500">Sem login configurado</p>
                                          )}
                                        </div>
                                        <div className="flex gap-2">
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                              setEditingHelperTeam(team);
                                              setHelperTeamForm({
                                                lider_nome: team.lider_nome,
                                                lider_diaria: team.lider_diaria,
                                                helpers: team.helpers || [],
                                                equipe: team.equipe,
                                                leader_username: team.leader_username || '',
                                                leader_password: ''
                                              });
                                              setShowHelperTeamModal(true);
                                            }}
                                          >
                                            ✏️ Editar
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-red-600"
                                            onClick={() => deleteHelperTeam(team.id)}
                                          >
                                            🗑️ Deletar
                                          </Button>
                                        </div>
                                      </div>

                                      {team.helpers && team.helpers.length > 0 && (
                                        <div className="mt-3 pt-3 border-t">
                                          <p className="text-sm font-medium mb-2">Helpers:</p>
                                          <div className="grid gap-2">
                                            {team.helpers.map(helper => (
                                              <div key={helper.id} className="flex justify-between text-sm bg-gray-50 p-2 rounded">
                                                <span>{helper.nome}</span>
                                                <span className="text-gray-600">R${helper.diaria}/dia</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </CardContent>
                                  </Card>
                                ))}
                              </div>
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>

                      {/* MODAL: RELATÓRIO FINANCEIRO - Seleção de Equipe */}
                      <Dialog open={showMonthlyViewModal} onOpenChange={setShowMonthlyViewModal}>
                        <DialogContent className="max-w-sm">
                          <DialogHeader>
                            <DialogTitle className="text-lg font-bold">Relatório Financeiro</DialogTitle>
                          </DialogHeader>
                          
                          <div className="space-y-4">
                            <div>
                              <Label className="text-sm font-medium mb-2 block">Equipe</Label>
                              <select
                                value={monthlyViewTeamFilter}
                                onChange={(e) => setMonthlyViewTeamFilter(e.target.value)}
                                className="w-full border rounded px-3 py-2 text-sm"
                                data-testid="team-select-report"
                              >
                                <option value="all">Selecione uma equipe</option>
                                {Array.from(new Set(helperTeams.map(t => t.equipe))).map(eq => (
                                  <option key={eq} value={eq}>{eq}</option>
                                ))}
                              </select>
                            </div>

                            <Button 
                              onClick={() => {
                                if (monthlyViewTeamFilter === 'all') {
                                  toast.error('Selecione uma equipe.');
                                  return;
                                }
                                const equipeSelecionada = helperTeams.find(t => t.equipe === monthlyViewTeamFilter);
                                if (equipeSelecionada) {
                                  setSelectedHelperTeamForReport(equipeSelecionada);
                                  setShowHelperFinancialReport(true);
                                } else {
                                  toast.error('Equipe não encontrada.');
                                }
                              }}
                              className="w-full bg-blue-600 hover:bg-blue-700"
                              data-testid="generate-report-btn"
                            >
                              <Search className="w-4 h-4 mr-2" />
                              Gerar Relatório
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>

                    </div>
                  )}

                  {adminSubTab === 'contas' && (
                    <CompanyBillsTab onUpdate={loadAnnouncements} onOpenFile={(url, title) => openPdfWithLoading({ url, title })} />
                  )}

                  {adminSubTab === 'cofre' && (
                    <PasswordVaultTab />
                  )}

                  {adminSubTab === 'permissoes' && (
                    <PermissionsTab />
                  )}

                  {adminSubTab === 'acessos' && (
                    <AccessMonitor API={API} />
                  )}

                  {adminSubTab === 'alertas' && (
                    <AlertsManager API={API} />
                  )}
                  {adminSubTab === 'seguros' && (
                    <InsuranceManager API={API} />
                  )}

                </CardContent>
              </Card>
            </TabsContent>
          )}
          
          {/* Crew's Report Tab - Available for ADMIN and PM */}
          <TabsContent value="crews" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-blue-800 flex items-center gap-2">
                    👥 Crew's Report - Análise de Equipes
                  </CardTitle>
                  <p className="text-gray-600 text-sm mt-2">
                    Compare performance das equipes, veja rankings e analise resultados
                  </p>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  {/* Filtros e Controles */}
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-800 mb-4">🔍 Filtros e Visualização</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Período */}
                      <div>
                        <Label className="text-sm font-medium mb-2 block">📅 Período</Label>
                        <select
                          value={crewsReportPeriodType}
                          onChange={(e) => setCrewsReportPeriodType(e.target.value)}
                          className="w-full border rounded px-3 py-2 text-sm"
                        >
                          <option value="week">Semana Específica</option>
                          <option value="month">Mês</option>
                          <option value="quarter">Trimestre</option>
                          <option value="year">Ano</option>
                          <option value="custom">Período Customizado</option>
                        </select>
                      </div>
                      
                      {/* Semana Específica - Condicional */}
                      {crewsReportPeriodType === 'week' && (
                        <div>
                          <Label className="text-sm font-medium mb-2 block">📅 Selecionar Semana</Label>
                          <select
                            value={crewsReportSelectedWeek}
                            onChange={(e) => setCrewsReportSelectedWeek(e.target.value)}
                            className="w-full border rounded px-3 py-2 text-sm"
                          >
                            <option value="">Selecione uma semana...</option>
                            {crewsAvailableWeeks.map(week => {
                              // week_ending = data de PAGAMENTO (sexta). A semana REAL de
                              // trabalho vem dos dados (crewsWeekMeta), pois algumas equipes
                              // trabalham até sábado.
                              const meta = crewsWeekMeta[week];
                              const short = (iso) => {
                                if (!iso) return '';
                                const [, m, d] = iso.split('-');
                                return `${m}/${d}`;
                              };
                              const full = (iso) => {
                                if (!iso) return '';
                                const [y, m, d] = iso.split('-');
                                return `${m}/${d}/${y}`;
                              };
                              const label = meta
                                ? `${short(meta.work_start)} – ${short(meta.work_end)} · Pgto: ${full(week)}`
                                : `Pgto: ${full(week)}`;
                              return (
                                <option key={week} value={week}>
                                  {label}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      )}

                      
                      {/* Somar por (Trabalho x Pagamento) — vale p/ Mês, Trimestre e Ano */}
                      {['month', 'quarter', 'year'].includes(crewsReportPeriodType) && (
                        <div>
                          <Label className="text-sm font-medium mb-2 block">💵 Somar por</Label>
                          <select
                            data-testid="crews-sum-basis"
                            value={crewsSumBasis}
                            onChange={(e) => setCrewsSumBasis(e.target.value)}
                            disabled={crewsGroupBy === 'week'}
                            className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                          >
                            {crewsGroupBy !== 'week' && <option value="work">🗓️ Data do Trabalho</option>}
                            <option value="payment">💰 Data de Pagamento</option>
                          </select>
                          <p className="text-[11px] text-gray-500 mt-1">
                            {crewsGroupBy === 'week'
                              ? 'No modo "Por Semana" o sistema usa sempre a Data de Pagamento.'
                              : (crewsSumBasis === 'work'
                                ? 'Soma pelos trabalhos feitos no período escolhido.'
                                : 'Soma pelo que foi pago no período (ex.: pgto de 03/jun = trabalho de 25–30/mai).')}
                          </p>
                        </div>
                      )}

                      {/* Mensal */}
                      {crewsReportPeriodType === 'month' && (
                        <div>
                          <Label className="text-sm font-medium mb-2 block">📊 Selecionar Mês</Label>
                          <select
                            data-testid="crews-month-select"
                            value={crewsMonthlyMode === 'chooseMonth' && crewsSelectedMonthOption ? `month_${crewsSelectedMonthOption}` : 'last4weeks'}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value.startsWith('month_')) {
                                setCrewsMonthlyMode('chooseMonth');
                                setCrewsSelectedMonthOption(value.replace('month_', ''));
                              } else {
                                setCrewsMonthlyMode('last4weeks');
                                setCrewsSelectedMonthOption('');
                              }
                            }}
                            className="w-full border rounded px-3 py-2 text-sm"
                          >
                            <option value="last4weeks">🔵 Últimas 4 Semanas</option>
                            <option disabled>──────────</option>
                            {(crewsSumBasis === 'payment' ? crewsPayMonths : crewsWorkMonths).map(month => {
                              const [y, m] = month.split('-');
                              const nm = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'][parseInt(m)];
                              return (
                                <option key={month} value={`month_${month}`}>
                                  🔵 {nm} de {y}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      )}
                      
                      {/* Trimestral - 3 Sub-Opções */}
                      {crewsReportPeriodType === 'quarter' && (
                        <div className="space-y-3">
                          <Label className="text-sm font-medium mb-2 block">📊 Modo Trimestral</Label>
                          <div className="space-y-2">
                            <Button
                              onClick={() => {
                                setCrewsQuarterlyMode('last12weeks');
                              }}
                              variant={crewsQuarterlyMode === 'last12weeks' ? 'default' : 'outline'}
                              className="w-full justify-start text-sm"
                              size="sm"
                            >
                              🟢 Últimas 12 Semanas
                            </Button>
                            <Button
                              onClick={() => setCrewsQuarterlyMode('lastCompleteQuarter')}
                              variant={crewsQuarterlyMode === 'lastCompleteQuarter' ? 'default' : 'outline'}
                              className="w-full justify-start text-sm"
                              size="sm"
                            >
                              🟢 Último Trimestre Completo
                            </Button>
                            <div className="space-y-1">
                              <Button
                                onClick={() => setCrewsQuarterlyMode('chooseQuarter')}
                                variant={crewsQuarterlyMode === 'chooseQuarter' ? 'default' : 'outline'}
                                className="w-full justify-start text-sm"
                                size="sm"
                              >
                                🟢 Escolher Trimestre
                              </Button>
                              {crewsQuarterlyMode === 'chooseQuarter' && (
                                <select
                                  value={crewsSelectedQuarterOption}
                                  onChange={(e) => {
                                    setCrewsSelectedQuarterOption(e.target.value);
                                  }}
                                  className="w-full border rounded px-3 py-2 text-sm ml-4"
                                >
                                  <option value="">Selecione um trimestre...</option>
                                  {crewsAvailableQuarters.map(quarter => (
                                    <option key={quarter} value={quarter}>
                                      {quarter}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Anual - 3 Sub-Opções */}
                      {crewsReportPeriodType === 'year' && (
                        <div className="space-y-3">
                          <Label className="text-sm font-medium mb-2 block">📊 Modo Anual</Label>
                          <div className="space-y-2">
                            <Button
                              onClick={() => {
                                setCrewsAnnualMode('last52weeks');
                              }}
                              variant={crewsAnnualMode === 'last52weeks' ? 'default' : 'outline'}
                              className="w-full justify-start text-sm"
                              size="sm"
                            >
                              🟣 Últimas 52 Semanas
                            </Button>
                            <Button
                              onClick={() => {
                                setCrewsAnnualMode('lastCompleteYear');
                              }}
                              variant={crewsAnnualMode === 'lastCompleteYear' ? 'default' : 'outline'}
                              className="w-full justify-start text-sm"
                              size="sm"
                            >
                              🟣 Ano Completo Anterior
                            </Button>
                            <div className="space-y-1">
                              <Button
                                onClick={() => setCrewsAnnualMode('chooseYear')}
                                variant={crewsAnnualMode === 'chooseYear' ? 'default' : 'outline'}
                                className="w-full justify-start text-sm"
                                size="sm"
                              >
                                🟣 Escolher Ano
                              </Button>
                              {crewsAnnualMode === 'chooseYear' && (
                                <select
                                  value={crewsSelectedYearOption}
                                  onChange={(e) => {
                                    setCrewsSelectedYearOption(e.target.value);
                                  }}
                                  className="w-full border rounded px-3 py-2 text-sm ml-4"
                                >
                                  <option value="">Selecione um ano...</option>
                                  {crewsAvailableYears.map(year => (
                                    <option key={year} value={year}>
                                      {year}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      
                      {/* Período Customizado - Condicional */}
                      {crewsReportPeriodType === 'custom' && (
                        <>
                          <div>
                            <Label className="text-sm font-medium mb-2 block">📅 Data Inicial</Label>
                            <Input
                              type="date"
                              value={crewsReportStartDate}
                              onChange={(e) => setCrewsReportStartDate(e.target.value)}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-sm font-medium mb-2 block">📅 Data Final</Label>
                            <Input
                              type="date"
                              value={crewsReportEndDate}
                              onChange={(e) => setCrewsReportEndDate(e.target.value)}
                              className="text-sm"
                            />
                          </div>
                        </>
                      )}
                      
                      {/* Visualização */}
                      <div>
                        <Label className="text-sm font-medium mb-2 block">👁️ Visualização</Label>
                        <select
                          value={crewsReportViewMode}
                          onChange={(e) => setCrewsReportViewMode(e.target.value)}
                          className="w-full border rounded px-3 py-2 text-sm"
                        >
                          <option value="cards">📊 Cards</option>
                          <option value="table">📋 Tabela</option>
                          <option value="ranking">🏆 Rankings</option>
                          <option value="charts">📈 Gráficos</option>
                        </select>
                      </div>
                      
                      {/* Ordenar Por */}
                      <div>
                        <Label className="text-sm font-medium mb-2 block">↕️ Ordenar Por</Label>
                        <select
                          value={crewsReportSortBy}
                          onChange={(e) => setCrewsReportSortBy(e.target.value)}
                          className="w-full border rounded px-3 py-2 text-sm"
                        >
                          {user?.role === 'admin' && <option value="lucro_empresa">💼 Lucro (Empresa)</option>}
                          <option value="total_final">💰 Total Final (PMs)</option>
                          <option value="total_jobs">📊 Trabalhos</option>
                          {user?.role === 'admin' && <option value="total_labor">💵 Total Labor</option>}
                          {user?.role === 'admin' && <option value="total_expenses">💸 Despesas</option>}
                        </select>
                      </div>
                      
                      {/* Ordem */}
                      <div>
                        <Label className="text-sm font-medium mb-2 block">🔄 Ordem</Label>
                        <select
                          value={crewsReportSortOrder}
                          onChange={(e) => setCrewsReportSortOrder(e.target.value)}
                          className="w-full border rounded px-3 py-2 text-sm"
                        >
                          <option value="desc">⬇️ Maior para Menor</option>
                          <option value="asc">⬆️ Menor para Maior</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Botões de Ação */}
                    <div className="flex flex-wrap gap-2 mt-4">
                      <Button
                        onClick={loadCrewsReport}
                        disabled={crewsReportLoading}
                        className="bg-blue-600 hover:bg-blue-700"
                        size="sm"
                      >
                        {crewsReportLoading ? '🔄 Carregando...' : '🔄 Atualizar'}
                      </Button>
                      
                      <Button
                        onClick={() => {
                          if (crewsReportData?.teams) {
                            setCrewsReportSelectedTeams(crewsReportData.teams.map(t => t.team_name));
                          }
                        }}
                        variant="outline"
                        size="sm"
                      >
                        ✅ Selecionar Todas
                      </Button>
                      
                      <Button
                        onClick={() => setCrewsReportSelectedTeams([])}
                        variant="outline"
                        size="sm"
                      >
                        ❌ Limpar Seleção
                      </Button>
                      
                      <Button
                        onClick={() => setCrewsShowDetailedBills(!crewsShowDetailedBills)}
                        variant={crewsShowDetailedBills ? "default" : "outline"}
                        size="sm"
                      >
                        {crewsShowDetailedBills ? '📋 Esconder Bills' : '📋 Mostrar Bills'}
                      </Button>

                      <Button
                        data-testid="crews-groupby-toggle"
                        onClick={() => {
                          const next = crewsGroupBy === 'week' ? 'job' : 'week';
                          setCrewsGroupBy(next);
                          if (next === 'week') setCrewsSumBasis('payment');
                        }}
                        variant={crewsGroupBy === 'week' ? 'default' : 'outline'}
                        size="sm"
                        className={crewsGroupBy === 'week' ? 'bg-indigo-600 hover:bg-indigo-700' : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50'}
                      >
                        {crewsGroupBy === 'week' ? '📅 Por Semana' : '🧱 Por Trabalho'}
                      </Button>

                      {user?.role === 'admin' && (
                        <Button
                          data-testid="crews-compare-teams-btn"
                          onClick={() => setShowCompareTeamsModal(true)}
                          variant="outline"
                          size="sm"
                          className="bg-teal-50 border-teal-300 hover:bg-teal-100 text-teal-800"
                        >
                          🆚 Comparar Equipes
                        </Button>
                      )}
                      
                      {user?.role === 'admin' && (
                        <Button
                          onClick={() => setShowCustomizeModal(true)}
                          variant="outline"
                          size="sm"
                          className="bg-purple-50 border-purple-300 hover:bg-purple-100"
                        >
                          ⚙️ Customizar Campos
                        </Button>
                      )}

                      {user?.role === 'admin' && (
                        <Button
                          data-testid="crews-export-excel"
                          onClick={exportCrewsExcel}
                          variant="outline"
                          size="sm"
                          className="bg-green-50 border-green-300 hover:bg-green-100 text-green-800"
                        >
                          📊 Exportar Excel
                        </Button>
                      )}

                      {user?.role === 'admin' && (
                        <Button
                          data-testid="crews-export-pdf"
                          onClick={exportCrewsPDF}
                          variant="outline"
                          size="sm"
                          className="bg-red-50 border-red-300 hover:bg-red-100 text-red-800"
                        >
                          📄 Exportar PDF
                        </Button>
                      )}

                      {user?.role === 'admin' && (
                        <Button
                          data-testid="crews-compare-months-btn"
                          onClick={() => setShowCompareModal(true)}
                          variant="outline"
                          size="sm"
                          className="bg-indigo-50 border-indigo-300 hover:bg-indigo-100 text-indigo-800"
                        >
                          🆚 Comparar Meses
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Loading State */}
                  {crewsReportLoading && (
                    <div className="text-center py-12">
                      <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                      <p className="text-gray-600">Carregando relatório das equipes...</p>
                    </div>
                  )}
                  
                  {/* Resumo Geral - Expansível - APENAS ADMIN */}
                  {!crewsReportLoading && crewsReportData && user?.role === 'admin' && (
                    <>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg">
                        {/* Header Clicável */}
                        <div 
                          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => setCrewsSummaryExpanded(!crewsSummaryExpanded)}
                        >
                          <h3 className="font-semibold text-lg text-gray-800 flex items-center gap-2">
                            📊 Resumo Geral
                          </h3>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">
                              {crewsSummaryExpanded ? 'Clique para minimizar' : 'Clique para expandir'}
                            </span>
                            <span className={`text-gray-600 transition-transform ${crewsSummaryExpanded ? 'rotate-180' : ''}`}>
                              ▼
                            </span>
                          </div>
                        </div>
                        
                        {/* Conteúdo - Só aparece quando expandido */}
                        {crewsSummaryExpanded && (
                          <div className="p-4 pt-0">
                            {(() => {
                              // Contar quantos cards estão visíveis
                              const visibleCount = Object.values(crewsSummaryFields).filter(v => v).length;
                              
                              // Determinar classes baseado na quantidade
                              let gridCols = 'grid-cols-2 md:grid-cols-3';
                              let fontSize = 'text-3xl';
                              let padding = 'p-4';
                              
                              if (visibleCount <= 2) {
                                gridCols = 'grid-cols-1 md:grid-cols-2';
                                fontSize = 'text-4xl';
                                padding = 'p-6';
                              } else if (visibleCount <= 3) {
                                gridCols = 'grid-cols-2 md:grid-cols-3';
                                fontSize = 'text-3xl';
                                padding = 'p-5';
                              } else if (visibleCount <= 4) {
                                gridCols = 'grid-cols-2 md:grid-cols-4';
                                fontSize = 'text-2xl';
                                padding = 'p-4';
                              } else if (visibleCount <= 5) {
                                gridCols = 'grid-cols-2 md:grid-cols-5';
                                fontSize = 'text-xl';
                                padding = 'p-3';
                              } else {
                                gridCols = 'grid-cols-3 md:grid-cols-6';
                                fontSize = 'text-lg';
                                padding = 'p-3';
                              }
                              
                              return (
                                <div className={`grid ${gridCols} gap-3`}>
                                  {crewsSummaryFields.total_equipes && (
                                    <div className={`bg-blue-50 rounded-lg ${padding} border-l-4 border-blue-500`}>
                                      <div className="text-xs text-gray-600 mb-1">Total Equipes</div>
                                      <div className={`${fontSize} font-bold text-blue-700`}>{crewsReportData.total_teams}</div>
                                    </div>
                                  )}
                                  {crewsSummaryFields.total_trabalhos && (
                                    <div className={`bg-green-50 rounded-lg ${padding} border-l-4 border-green-500`}>
                                      <div className="text-xs text-gray-600 mb-1">Total Trabalhos</div>
                                      <div className={`${fontSize} font-bold text-green-700`}>{crewsReportData.total_jobs}</div>
                                    </div>
                                  )}
                                  {crewsSummaryFields.total_labor && (
                                    <div className={`bg-indigo-50 rounded-lg ${padding} border-l-4 border-indigo-500`}>
                                      <div className="text-xs text-gray-600 mb-1">Total Labor</div>
                                      <div className={`${visibleCount > 4 ? 'text-lg' : 'text-2xl'} font-bold text-indigo-700`}>{formatMoney(crewsReportData.total_labor)}</div>
                                    </div>
                                  )}
                                  {crewsSummaryFields.total_final && (
                                    <div className={`bg-teal-50 rounded-lg ${padding} border-l-4 border-teal-500`}>
                                      <div className="text-xs text-gray-600 mb-1">Total Final</div>
                                      <div className={`${visibleCount > 4 ? 'text-lg' : 'text-2xl'} font-bold text-teal-700`}>{formatMoney(crewsReportData.total_final)}</div>
                                    </div>
                                  )}
                                  {crewsSummaryFields.total_lucro && (
                                    <div className={`bg-purple-50 rounded-lg ${padding} border-l-4 border-purple-500`}>
                                      <div className="text-xs text-gray-600 mb-1">Total Lucro</div>
                                      <div className={`${visibleCount > 4 ? 'text-lg' : 'text-2xl'} font-bold text-purple-700`}>{formatMoney(crewsReportData.lucro_empresa || 0)}</div>
                                    </div>
                                  )}
                                  {crewsSummaryFields.total_despesas && (
                                    <div className={`bg-orange-50 rounded-lg ${padding} border-l-4 border-orange-500`}>
                                      <div className="text-xs text-gray-600 mb-1">Total Despesas</div>
                                      <div className={`${visibleCount > 4 ? 'text-lg' : 'text-2xl'} font-bold text-orange-700`}>{formatMoney(crewsReportData.total_expenses)}</div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  
                  {/* Seleção de Equipes e Visualizações - Para todos os usuários */}
                  {!crewsReportLoading && crewsReportData && (
                    <>
                      {/* Seleção de Equipes */}
                      <div className="bg-white border rounded-lg p-4">
                        <h4 className="font-semibold text-gray-800 mb-3">👥 Selecionar Equipes para Comparar</h4>
                        <div className="flex flex-wrap gap-2">
                          {crewsReportData.teams
                            .filter(team => {
                              // Filtrar PM1 para usuários não-admin
                              if (user?.role !== 'admin' && team.team_name === 'PM1') {
                                return false;
                              }
                              return true;
                            })
                            .sort((a, b) => {
                              // Ordenar PM1, PM2, ..., PM10 numericamente
                              const regex = /^PM(\d+)$/;
                              const matchA = a.team_name.match(regex);
                              const matchB = b.team_name.match(regex);
                              
                              if (matchA && matchB) {
                                return parseInt(matchA[1]) - parseInt(matchB[1]);
                              } else if (matchA && !matchB) {
                                return -1; // PM vem antes
                              } else if (!matchA && matchB) {
                                return 1;
                              } else {
                                return a.team_name.localeCompare(b.team_name);
                              }
                            })
                            .map(team => (
                            <label
                              key={team.team_name}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-pointer transition ${
                                crewsReportSelectedTeams.includes(team.team_name)
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={crewsReportSelectedTeams.includes(team.team_name)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setCrewsReportSelectedTeams([...crewsReportSelectedTeams, team.team_name]);
                                  } else {
                                    setCrewsReportSelectedTeams(crewsReportSelectedTeams.filter(t => t !== team.team_name));
                                  }
                                }}
                                className="w-4 h-4"
                              />
                              <span className="font-medium">{team.team_name}</span>
                              <Badge variant="secondary" className="text-xs">
                                {team.total_jobs} jobs
                              </Badge>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      {/* VISUALIZAÇÃO POR SEMANA (Cards / Tabela / Ranking) */}
                      {crewsGroupBy === 'week' && crewsReportViewMode !== 'charts' && (
                        <CrewsWeeklyView
                          teams={crewsReportData.teams}
                          selectedTeams={crewsReportSelectedTeams}
                          weeksBreakdown={crewsReportData.weeks_breakdown}
                          viewMode={crewsReportViewMode}
                          cardsFields={crewsCardsFields}
                          tableFields={crewsTableFields}
                          rankingFields={crewsRankingFields}
                          sortBy={crewsReportSortBy}
                          sortOrder={crewsReportSortOrder}
                          isAdmin={user?.role === 'admin'}
                          formatMoney={formatMoney}
                        />
                      )}

                      {/* VISUALIZAÇÃO: CARDS */}
                      {crewsReportViewMode === 'cards' && crewsGroupBy === 'job' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {crewsReportData.teams
                            .filter(team => {
                              // Filtrar equipes selecionadas
                              if (!crewsReportSelectedTeams.includes(team.team_name)) return false;
                              // Filtrar PM1 para usuários não-admin
                              if (user?.role !== 'admin' && team.team_name === 'PM1') return false;
                              return true;
                            })
                            .sort((a, b) => {
                              const aVal = a[crewsReportSortBy] || 0;
                              const bVal = b[crewsReportSortBy] || 0;
                              return crewsReportSortOrder === 'desc' ? bVal - aVal : aVal - bVal;
                            })
                            .map((team, index) => (
                              <div key={team.team_name} className="bg-white border rounded-lg shadow-sm hover:shadow-md transition">
                                <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-3 rounded-t-lg">
                                  <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-bold">{team.team_name}</h3>
                                    {index < 3 && crewsReportSortOrder === 'desc' && (
                                      <span className="text-2xl">
                                        {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="p-4 space-y-3">
                                  <div className="grid grid-cols-2 gap-3 text-sm">
                                    {crewsCardsFields.trabalhos && (
                                      <div>
                                        <div className="text-gray-500 text-xs">Trabalhos</div>
                                        <div className="text-xl font-bold text-blue-600">{team.total_jobs}</div>
                                      </div>
                                    )}
                                    {crewsCardsFields.total_labor && user?.role === 'admin' && (
                                      <div>
                                        <div className="text-gray-500 text-xs">Total Labor</div>
                                        <div className="text-sm font-medium">{formatMoney(team.total_labor)}</div>
                                      </div>
                                    )}
                                    {crewsCardsFields.total_final && user?.role === 'admin' && (
                                      <div>
                                        <div className="text-gray-500 text-xs">Total Final (PM)</div>
                                        <div className="text-lg font-bold text-teal-600">{formatMoney(team.total_final)}</div>
                                      </div>
                                    )}
                                    {crewsCardsFields.lucro && user?.role === 'admin' && (
                                      <div>
                                        <div className="text-gray-500 text-xs">Lucro (Empresa)</div>
                                        <div className="text-lg font-bold text-purple-600">{formatMoney(team.lucro_empresa || 0)}</div>
                                      </div>
                                    )}
                                    {crewsCardsFields.porcentagem && user?.role === 'admin' && (
                                      <div>
                                        <div className="text-gray-500 text-xs">Porcentagem</div>
                                        <div className="text-sm font-medium">{team.percentage}%</div>
                                      </div>
                                    )}
                                    {crewsCardsFields.despesas && user?.role === 'admin' && (
                                      <div>
                                        <div className="text-gray-500 text-xs">Despesas</div>
                                        <div className="text-sm font-medium text-orange-600">{formatMoney(team.total_expenses)}</div>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Labor Bills Detalhados */}
                                  {crewsShowDetailedBills && team.labor_bills && team.labor_bills.length > 0 && (
                                    <div className="mt-4 pt-4 border-t border-gray-200">
                                      <div className="text-xs font-semibold text-gray-700 mb-2">
                                        📋 Labor Bills ({team.labor_bills.length})
                                      </div>
                                      <div className="space-y-2 max-h-60 overflow-y-auto">
                                        {team.labor_bills.map((bill, idx) => (
                                          bill.type === 'backcharge' ? (
                                            <div key={idx} className="bg-yellow-50 rounded p-2 text-xs border border-yellow-200">
                                              <div className="font-semibold text-yellow-800">⚠️ BACKCHARGE {bill.date ? `· ${bill.date}` : ''}</div>
                                              <div className="text-yellow-700 mt-1">
                                                {bill.descricao || bill.customer}
                                                {user?.role === 'admin' && <> · <span className="font-bold text-red-600">{formatMoney(bill.final_value)}</span></>}
                                              </div>
                                            </div>
                                          ) : bill.type === 'adicional' ? (
                                            <div key={idx} className="bg-green-50 rounded p-2 text-xs border border-green-200">
                                              <div className="font-semibold text-green-800">💰 ADICIONAL {bill.date ? `· ${bill.date}` : ''}</div>
                                              <div className="text-green-700 mt-1">
                                                {bill.descricao || bill.customer}
                                                {user?.role === 'admin' && <> · <span className="font-bold text-green-700">+{formatMoney(bill.valor)}</span></>}
                                              </div>
                                            </div>
                                          ) : (
                                            <div key={idx} className="bg-gray-50 rounded p-2 text-xs">
                                              <div className="font-medium text-gray-800">
                                                {bill.date ? <span className="text-gray-500 mr-2">{bill.date}</span> : null}{bill.customer}
                                              </div>
                                              <div className="text-gray-600 mt-1">
                                                {user?.role === 'admin' ? (
                                                  <>Job#: {bill.job_number} | Despesas: {formatMoney(bill.expenses)} | Final: <span className="font-bold text-green-600">{formatMoney(bill.final_value)}</span></>
                                                ) : (
                                                  <>Job#: {bill.job_number}</>
                                                )}
                                              </div>
                                            </div>
                                          )
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                      
                      {/* VISUALIZAÇÃO: TABELA */}
                      {crewsReportViewMode === 'table' && crewsGroupBy === 'job' && (
                        <div className="bg-white border rounded-lg overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-blue-600 text-white">
                                <tr>
                                  <th className="px-4 py-3 text-left text-sm font-semibold">Equipe</th>
                                  {crewsTableFields.trabalhos && <th className="px-4 py-3 text-center text-sm font-semibold">Trabalhos</th>}
                                  {crewsTableFields.total_labor && user?.role === 'admin' && <th className="px-4 py-3 text-right text-sm font-semibold">Total Labor</th>}
                                  {crewsTableFields.despesas && <th className="px-4 py-3 text-right text-sm font-semibold">Despesas</th>}
                                  {crewsTableFields.backcharge && user?.role === 'admin' && <th className="px-4 py-3 text-right text-sm font-semibold">Backcharge / Adicional</th>}
                                  {crewsTableFields.total_final && user?.role === 'admin' && <th className="px-4 py-3 text-right text-sm font-semibold">Total Final (PM)</th>}
                                  {crewsTableFields.lucro && user?.role === 'admin' && <th className="px-4 py-3 text-right text-sm font-semibold">Lucro (Empresa)</th>}
                                  {crewsTableFields.porcentagem && user?.role === 'admin' && <th className="px-4 py-3 text-center text-sm font-semibold">%</th>}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {crewsReportData.teams
                                  .filter(team => {
                                    // Filtrar equipes selecionadas
                                    if (!crewsReportSelectedTeams.includes(team.team_name)) return false;
                                    // Filtrar PM1 para usuários não-admin
                                    if (user?.role !== 'admin' && team.team_name === 'PM1') return false;
                                    return true;
                                  })
                                  .sort((a, b) => {
                                    const aVal = a[crewsReportSortBy] || 0;
                                    const bVal = b[crewsReportSortBy] || 0;
                                    return crewsReportSortOrder === 'desc' ? bVal - aVal : aVal - bVal;
                                  })
                                  .map((team, index) => (
                                    <React.Fragment key={team.team_name}>
                                      <tr className="hover:bg-gray-50 transition">
                                        <td className="px-4 py-3">
                                          <div className="flex items-center gap-2">
                                            {index < 3 && crewsReportSortOrder === 'desc' && (
                                              <span>{index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}</span>
                                            )}
                                            <span className="font-bold text-blue-700">{team.team_name}</span>
                                          </div>
                                        </td>
                                        {crewsTableFields.trabalhos && <td className="px-4 py-3 text-center font-medium">{team.total_jobs}</td>}
                                        {crewsTableFields.total_labor && user?.role === 'admin' && <td className="px-4 py-3 text-right">{formatMoney(team.total_labor)}</td>}
                                        {crewsTableFields.despesas && user?.role === 'admin' && <td className="px-4 py-3 text-right text-orange-600">{formatMoney(team.total_expenses)}</td>}
                                        {crewsTableFields.backcharge && user?.role === 'admin' && <td className="px-4 py-3 text-right text-yellow-700">{(team.backcharge_total || 0) > 0 ? `- ${formatMoney(team.backcharge_total)}` : ''}{(team.adicional_total || 0) > 0 ? <span className="text-green-700">{(team.backcharge_total || 0) > 0 ? ' / ' : ''}+ {formatMoney(team.adicional_total)}</span> : null}{!(team.backcharge_total || 0) && !(team.adicional_total || 0) ? '-' : ''}</td>}
                                        {crewsTableFields.total_final && user?.role === 'admin' && <td className="px-4 py-3 text-right font-bold text-teal-600">{formatMoney(team.total_final)}</td>}
                                        {crewsTableFields.lucro && user?.role === 'admin' && <td className="px-4 py-3 text-right font-bold text-purple-600">{formatMoney(team.lucro_empresa || 0)}</td>}
                                        {crewsTableFields.porcentagem && user?.role === 'admin' && <td className="px-4 py-3 text-center">{team.percentage}%</td>}
                                      </tr>
                                      
                                      {/* Labor Bills Detalhados */}
                                      {crewsShowDetailedBills && team.labor_bills && team.labor_bills.length > 0 && (
                                        <tr>
                                          <td colSpan={1 + Object.values(crewsTableFields).filter(v => v).length} className="px-4 py-3 bg-gray-50">
                                            <div className="space-y-1">
                                              <div className="text-xs font-semibold text-gray-700 mb-2">
                                                📋 Labor Bills Detalhados:
                                              </div>
                                              {team.labor_bills.map((bill, idx) => {
                                                if (bill.type === 'backcharge') {
                                                  // Linha de BACKCHARGE (reduz Total Final, aumenta Lucro)
                                                  return (
                                                    <div key={idx} className="text-xs grid grid-cols-[auto_1fr_1fr_1fr_1fr_1fr_1fr] gap-3 items-center py-1 pl-4 bg-yellow-50 rounded">
                                                      <span>⚠️</span>
                                                      <span>{bill.date || ''}</span>
                                                      <span className="font-semibold text-yellow-800">BACKCHARGE</span>
                                                      <span className="col-span-2 text-yellow-700 truncate">{bill.descricao || bill.customer}</span>
                                                      <span className="font-bold text-red-600">{formatMoney(bill.final_value)}</span>
                                                      {user?.role === 'admin' && <span className="font-bold text-purple-600">Lucro: +{formatMoney(bill.valor)}</span>}
                                                    </div>
                                                  );
                                                }
                                                if (bill.type === 'adicional') {
                                                  // Linha de ADICIONAL (aumenta Total Final, reduz Lucro)
                                                  return (
                                                    <div key={idx} className="text-xs grid grid-cols-[auto_1fr_1fr_1fr_1fr_1fr_1fr] gap-3 items-center py-1 pl-4 bg-green-50 rounded">
                                                      <span>💰</span>
                                                      <span>{bill.date || ''}</span>
                                                      <span className="font-semibold text-green-800">ADICIONAL</span>
                                                      <span className="col-span-2 text-green-700 truncate">{bill.descricao || bill.customer}</span>
                                                      <span className="font-bold text-green-700">+{formatMoney(bill.valor)}</span>
                                                      {user?.role === 'admin' && <span className="font-bold text-purple-600">Lucro: -{formatMoney(bill.valor)}</span>}
                                                    </div>
                                                  );
                                                }
                                                if (user?.role === 'admin') {
                                                  // ADMIN vê tudo
                                                  const lucro = (bill.labor_total || 0) - (bill.final_value || 0);
                                                  return (
                                                    <div key={idx} className="text-xs text-gray-600 grid grid-cols-[auto_1fr_1fr_1fr_1fr_1fr_1fr] gap-3 items-center py-1 pl-4">
                                                      <span>•</span>
                                                      <span>{bill.date || 'N/A'}</span>
                                                      <span>{bill.customer}</span>
                                                      <span>Labor: {formatMoney(bill.labor_total)}</span>
                                                      <span className="text-orange-600">Despesas: {formatMoney(bill.expenses)}</span>
                                                      <span className="font-bold text-green-600">Final: {formatMoney(bill.final_value)}</span>
                                                      <span className="font-bold text-purple-600">Lucro: {formatMoney(lucro)}</span>
                                                    </div>
                                                  );
                                                } else {
                                                  // PM vê apenas data, Job# e Customer
                                                  return (
                                                    <div key={idx} className="text-xs text-gray-600 py-1 pl-4">
                                                      <span>• </span>
                                                      <span className="text-gray-500">{bill.date || ''}</span>
                                                      <span className="font-medium ml-2">{bill.customer}</span>
                                                      <span className="text-gray-500 ml-2">Job#: {bill.job_number}</span>
                                                    </div>
                                                  );
                                                }
                                              })}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      
                      {/* SUBTOTAIS POR SEMANA (folha a folha) */}
                      {crewsGroupBy === 'job' && crewsReportData?.weeks_breakdown && crewsReportData.weeks_breakdown.length > 1 && crewsReportViewMode !== 'charts' && (
                        <div className="bg-white border border-indigo-200 rounded-lg mt-4" data-testid="crews-weeks-breakdown">
                          {/* Header Clicável */}
                          <div
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-indigo-50/60 transition-colors"
                            onClick={() => {
                              const next = !crewsWeeksExpanded;
                              setCrewsWeeksExpanded(next);
                              localStorage.setItem('crewsWeeksBreakdownExpanded', String(next));
                            }}
                            data-testid="crews-weeks-breakdown-toggle"
                          >
                            <h4 className="font-semibold text-indigo-800">🗓️ Subtotais por Semana (folha a folha)</h4>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-600 hidden sm:inline">
                                {crewsWeeksExpanded ? 'Clique para minimizar' : 'Clique para expandir'}
                              </span>
                              <span className={`text-indigo-600 transition-transform ${crewsWeeksExpanded ? 'rotate-180' : ''}`}>
                                ▼
                              </span>
                            </div>
                          </div>

                          {crewsWeeksExpanded && (
                          <div className="overflow-x-auto px-4 pb-4">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-indigo-50 text-indigo-900">
                                  <th className="px-3 py-2 text-left">Semana (trabalho)</th>
                                  <th className="px-3 py-2 text-center">Pgto</th>
                                  {crewsTableFields.trabalhos && <th className="px-3 py-2 text-center">Trabalhos</th>}
                                  {crewsTableFields.total_labor && user?.role === 'admin' && <th className="px-3 py-2 text-right">Labor</th>}
                                  {crewsTableFields.despesas && user?.role === 'admin' && <th className="px-3 py-2 text-right">Despesas</th>}
                                  {crewsTableFields.backcharge && user?.role === 'admin' && <th className="px-3 py-2 text-right">Backcharge / Adicional</th>}
                                  {crewsTableFields.total_final && user?.role === 'admin' && <th className="px-3 py-2 text-right">Total Final</th>}
                                  {crewsTableFields.lucro && user?.role === 'admin' && <th className="px-3 py-2 text-right">Lucro</th>}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {crewsReportData.weeks_breakdown.map((w, i) => {
                                  const fmtShort = (iso) => { if (!iso) return '-'; const [, m, d] = iso.split('-'); return `${m}/${d}`; };
                                  const fmtFull = (iso) => { if (!iso) return '-'; const [y, m, d] = iso.split('-'); return `${m}/${d}/${y}`; };
                                  return (
                                    <tr key={i} className="hover:bg-gray-50">
                                      <td className="px-3 py-2 font-medium">{w.work_start ? `${fmtShort(w.work_start)} – ${fmtShort(w.work_end)}` : '-'}</td>
                                      <td className="px-3 py-2 text-center text-gray-600">{fmtFull(w.week_ending)}</td>
                                      {crewsTableFields.trabalhos && <td className="px-3 py-2 text-center font-medium">{w.jobs}</td>}
                                      {crewsTableFields.total_labor && user?.role === 'admin' && <td className="px-3 py-2 text-right">{formatMoney(w.total_labor)}</td>}
                                      {crewsTableFields.despesas && user?.role === 'admin' && <td className="px-3 py-2 text-right text-orange-600">{formatMoney(w.total_expenses)}</td>}
                                      {crewsTableFields.backcharge && user?.role === 'admin' && <td className="px-3 py-2 text-right text-yellow-700">{(w.backcharge_total || 0) > 0 ? `- ${formatMoney(w.backcharge_total)}` : ''}{(w.adicional_total || 0) > 0 ? <span className="text-green-700">{(w.backcharge_total || 0) > 0 ? ' / ' : ''}+ {formatMoney(w.adicional_total)}</span> : null}{!(w.backcharge_total || 0) && !(w.adicional_total || 0) ? '-' : ''}</td>}
                                      {crewsTableFields.total_final && user?.role === 'admin' && <td className="px-3 py-2 text-right font-bold text-teal-600">{formatMoney(w.total_final)}</td>}
                                      {crewsTableFields.lucro && user?.role === 'admin' && <td className="px-3 py-2 text-right font-bold text-purple-600">{formatMoney(w.lucro)}</td>}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          )}
                        </div>
                      )}

                      {/* VISUALIZAÇÃO: GRÁFICOS */}
                      {crewsReportViewMode === 'charts' && (
                        <div className="space-y-6">
                          {/* Seletor de Semanas */}
                          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-purple-800">📅 Selecionar Semanas para Comparação</h4>
                              {crewsAvailableWeeks.length > 0 && (
                                <span className="text-xs text-purple-700 bg-white px-3 py-1 rounded-full border border-purple-300">
                                  📊 {crewsAvailableWeeks.length} semanas disponíveis 
                                  {crewsAvailableWeeks.length > 0 && (
                                    <span className="ml-2 font-medium">
                                      (desde {crewsAvailableWeeks[crewsAvailableWeeks.length - 1]})
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                            
                            {/* Botões de Atalho */}
                            <div className="flex flex-wrap gap-2 mb-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => selectLastNWeeks(4)}
                                className="text-xs"
                              >
                                Últimas 4 Semanas
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => selectLastNWeeks(8)}
                                className="text-xs"
                              >
                                Últimas 8 Semanas
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => selectLastNWeeks(12)}
                                className="text-xs"
                              >
                                Últimas 12 Semanas
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => selectLastNWeeks(13)}
                                className="text-xs bg-yellow-50 hover:bg-yellow-100"
                              >
                                📅 Último Trimestre (13 semanas)
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => selectLastNWeeks(26)}
                                className="text-xs bg-orange-50 hover:bg-orange-100"
                              >
                                📅 Últimos 6 Meses (26 semanas)
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => selectLastNWeeks(52)}
                                className="text-xs bg-purple-50 hover:bg-purple-100"
                              >
                                📅 Último Ano (52 semanas)
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  // Selecionar desde a primeira semana até a última
                                  setSelectedWeeksForCharts(crewsAvailableWeeks);
                                }}
                                className="text-xs bg-blue-50 hover:bg-blue-100"
                              >
                                📊 Desde o Início ({crewsAvailableWeeks.length} semanas)
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setSelectedWeeksForCharts([])}
                                className="text-xs text-red-600 hover:text-red-700"
                              >
                                🗑️ Limpar
                              </Button>
                            </div>
                            
                            {/* Checkboxes de Semanas */}
                            {crewsAvailableWeeks.length > 0 ? (
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-48 overflow-y-auto p-2 bg-white rounded border">
                                {crewsAvailableWeeks.map(week => (
                                  <label key={week} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={selectedWeeksForCharts.includes(week)}
                                      onChange={() => toggleWeekSelection(week)}
                                      className="rounded"
                                    />
                                    <span className="text-xs font-medium">{week}</span>
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-600">Carregando semanas disponíveis...</p>
                            )}
                            
                            <p className="text-xs text-gray-600 mt-2">
                              {selectedWeeksForCharts.length} semana(s) selecionada(s)
                            </p>
                          </div>
                          
                          {/* Controles de Gráfico */}
                          {selectedWeeksForCharts.length > 0 && (
                            <>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Tipo de Gráfico */}
                                <div>
                                  <Label className="text-sm font-medium mb-2 block">📊 Tipo de Gráfico</Label>
                                  <select
                                    value={crewsChartType}
                                    onChange={(e) => setCrewsChartType(e.target.value)}
                                    className="w-full border rounded px-3 py-2 text-sm"
                                  >
                                    <option value="timeline">📈 Evolução Temporal (Linhas)</option>
                                    <option value="comparison">📊 Comparação por Semana (Barras)</option>
                                    <option value="pie">🥧 Distribuição Total (Pizza)</option>
                                    <option value="teamComparison">👥 Comparação por Equipe (Barras)</option>
                                  </select>
                                </div>
                                
                                {/* Métrica */}
                                <div>
                                  <Label className="text-sm font-medium mb-2 block">📈 Métrica</Label>
                                  <select
                                    value={crewsChartMetric}
                                    onChange={(e) => setCrewsChartMetric(e.target.value)}
                                    className="w-full border rounded px-3 py-2 text-sm"
                                  >
                                    <option value="total_jobs">Total de Trabalhos</option>
                                    {user?.role === 'admin' && <option value="total_labor">Total Labor</option>}
                                    {user?.role === 'admin' && <option value="total_final">Total Final (PM)</option>}
                                    {user?.role === 'admin' && <option value="lucro_empresa">Lucro (Empresa)</option>}
                                    {user?.role === 'admin' && <option value="total_expenses">Despesas</option>}
                                  </select>
                                </div>
                              </div>
                              
                              {/* Área do Gráfico */}
                              {loadingChartsData ? (
                                <div className="bg-white border rounded-lg p-12 text-center">
                                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                                  <p className="mt-4 text-gray-600">Carregando dados...</p>
                                </div>
                              ) : (
                                <div className="bg-white border rounded-lg p-6">
                                  <h4 className="font-semibold text-gray-800 mb-4">
                                    {crewsChartType === 'timeline' && '📈 Evolução Temporal'}
                                    {crewsChartType === 'comparison' && '📊 Comparação por Semana'}
                                    {crewsChartType === 'pie' && '🥧 Distribuição Total'}
                                    {crewsChartType === 'teamComparison' && '👥 Comparação por Equipe'}
                                  </h4>
                                  
                                  <div className="h-96">
                                    {crewsChartType === 'timeline' && (
                                      <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={(() => {
                                          // Preparar dados para gráfico de linhas
                                          const chartData = [];
                                          selectedWeeksForCharts.forEach(week => {
                                            const weekData = weeksDataForCharts[week];
                                            if (weekData && weekData.teams) {
                                              const dataPoint = { week };
                                              weekData.teams
                                                .filter(t => {
                                                  // Filtrar equipes selecionadas
                                                  if (!crewsReportSelectedTeams.includes(t.team_name)) return false;
                                                  // Filtrar PM1 para não-admin
                                                  if (user?.role !== 'admin' && t.team_name === 'PM1') return false;
                                                  return true;
                                                })
                                                .forEach(team => {
                                                  dataPoint[team.team_name] = team[crewsChartMetric] || 0;
                                                });
                                              chartData.push(dataPoint);
                                            }
                                          });
                                          return chartData;
                                        })()}>
                                          <CartesianGrid strokeDasharray="3 3" />
                                          <XAxis dataKey="week" angle={-45} textAnchor="end" height={80} style={{ fontSize: 12 }} />
                                          <YAxis style={{ fontSize: 12 }} />
                                          <Tooltip formatter={(value) => crewsChartMetric.includes('total') || crewsChartMetric.includes('lucro') ? formatMoney(value) : value} />
                                          <Legend />
                                          {crewsReportSelectedTeams.map((team, idx) => {
                                            const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
                                            return <Line key={team} type="monotone" dataKey={team} stroke={colors[idx % colors.length]} strokeWidth={2} />;
                                          })}
                                        </LineChart>
                                      </ResponsiveContainer>
                                    )}
                                    
                                    {crewsChartType === 'comparison' && (
                                      <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={(() => {
                                          // Preparar dados para barras agrupadas por semana
                                          const chartData = [];
                                          selectedWeeksForCharts.forEach(week => {
                                            const weekData = weeksDataForCharts[week];
                                            if (weekData && weekData.teams) {
                                              const dataPoint = { week };
                                              weekData.teams
                                                .filter(t => {
                                                  // Filtrar equipes selecionadas
                                                  if (!crewsReportSelectedTeams.includes(t.team_name)) return false;
                                                  // Filtrar PM1 para não-admin
                                                  if (user?.role !== 'admin' && t.team_name === 'PM1') return false;
                                                  return true;
                                                })
                                                .forEach(team => {
                                                  dataPoint[team.team_name] = team[crewsChartMetric] || 0;
                                                });
                                              chartData.push(dataPoint);
                                            }
                                          });
                                          return chartData;
                                        })()}>
                                          <CartesianGrid strokeDasharray="3 3" />
                                          <XAxis dataKey="week" angle={-45} textAnchor="end" height={80} style={{ fontSize: 12 }} />
                                          <YAxis style={{ fontSize: 12 }} />
                                          <Tooltip formatter={(value) => crewsChartMetric.includes('total') || crewsChartMetric.includes('lucro') ? formatMoney(value) : value} />
                                          <Legend />
                                          {crewsReportSelectedTeams.map((team, idx) => {
                                            const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
                                            return <Bar key={team} dataKey={team} fill={colors[idx % colors.length]} />;
                                          })}
                                        </BarChart>
                                      </ResponsiveContainer>
                                    )}
                                    
                                    {crewsChartType === 'pie' && (
                                      <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                          <Pie
                                            data={(() => {
                                              // Calcular total por equipe em todas as semanas selecionadas
                                              const teamTotals = {};
                                              selectedWeeksForCharts.forEach(week => {
                                                const weekData = weeksDataForCharts[week];
                                                if (weekData && weekData.teams) {
                                                  weekData.teams
                                                    .filter(t => {
                                                      // Filtrar equipes selecionadas
                                                      if (!crewsReportSelectedTeams.includes(t.team_name)) return false;
                                                      // Filtrar PM1 para não-admin
                                                      if (user?.role !== 'admin' && t.team_name === 'PM1') return false;
                                                      return true;
                                                    })
                                                    .forEach(team => {
                                                      if (!teamTotals[team.team_name]) {
                                                        teamTotals[team.team_name] = 0;
                                                      }
                                                      teamTotals[team.team_name] += team[crewsChartMetric] || 0;
                                                    });
                                                }
                                              });
                                              return Object.entries(teamTotals).map(([name, value]) => ({ name, value }));
                                            })()}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                            outerRadius={120}
                                            fill="#8884d8"
                                            dataKey="value"
                                          >
                                            {(() => {
                                              const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
                                              return crewsReportSelectedTeams.map((team, idx) => (
                                                <Cell key={`cell-${idx}`} fill={colors[idx % colors.length]} />
                                              ));
                                            })()}
                                          </Pie>
                                          <Tooltip formatter={(value) => crewsChartMetric.includes('total') || crewsChartMetric.includes('lucro') ? formatMoney(value) : value} />
                                          <Legend />
                                        </PieChart>
                                      </ResponsiveContainer>
                                    )}
                                    
                                    {crewsChartType === 'teamComparison' && (
                                      <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={(() => {
                                          // Preparar dados para barras agrupadas por equipe
                                          const chartData = [];
                                          crewsReportSelectedTeams
                                            .filter(teamName => {
                                              // Filtrar PM1 para não-admin
                                              if (user?.role !== 'admin' && teamName === 'PM1') return false;
                                              return true;
                                            })
                                            .forEach(teamName => {
                                              const dataPoint = { team: teamName };
                                              selectedWeeksForCharts.forEach(week => {
                                                const weekData = weeksDataForCharts[week];
                                                if (weekData && weekData.teams) {
                                                  const team = weekData.teams.find(t => t.team_name === teamName);
                                                  if (team) {
                                                    dataPoint[week] = team[crewsChartMetric] || 0;
                                                  }
                                                }
                                              });
                                              chartData.push(dataPoint);
                                            });
                                          return chartData;
                                        })()}>
                                          <CartesianGrid strokeDasharray="3 3" />
                                          <XAxis dataKey="team" style={{ fontSize: 12 }} />
                                          <YAxis style={{ fontSize: 12 }} />
                                          <Tooltip formatter={(value) => crewsChartMetric.includes('total') || crewsChartMetric.includes('lucro') ? formatMoney(value) : value} />
                                          <Legend />
                                          {selectedWeeksForCharts.map((week, idx) => {
                                            const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
                                            return <Bar key={week} dataKey={week} fill={colors[idx % colors.length]} />;
                                          })}
                                        </BarChart>
                                      </ResponsiveContainer>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {/* Insights Automáticos */}
                              {!loadingChartsData && Object.keys(weeksDataForCharts).length > 0 && (
                                <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
                                  <h4 className="font-semibold text-green-800 mb-3">📊 Insights Rápidos</h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                    {(() => {
                                      // Calcular insights
                                      let bestWeek = null;
                                      let bestWeekValue = 0;
                                      let worstWeek = null;
                                      let worstWeekValue = Infinity;
                                      let bestTeam = null;
                                      let bestTeamAvg = 0;
                                      const teamAverages = {};
                                      
                                      // Análise por semana
                                      selectedWeeksForCharts.forEach(week => {
                                        const weekData = weeksDataForCharts[week];
                                        if (weekData && weekData.teams) {
                                          const weekTotal = weekData.teams
                                            .filter(t => {
                                              // Filtrar equipes selecionadas
                                              if (!crewsReportSelectedTeams.includes(t.team_name)) return false;
                                              // Filtrar PM1 para não-admin
                                              if (user?.role !== 'admin' && t.team_name === 'PM1') return false;
                                              return true;
                                            })
                                            .reduce((sum, t) => sum + (t[crewsChartMetric] || 0), 0);
                                          
                                          if (weekTotal > bestWeekValue) {
                                            bestWeekValue = weekTotal;
                                            bestWeek = week;
                                          }
                                          if (weekTotal < worstWeekValue) {
                                            worstWeekValue = weekTotal;
                                            worstWeek = week;
                                          }
                                          
                                          // Calcular médias por equipe
                                          weekData.teams
                                            .filter(t => {
                                              // Filtrar equipes selecionadas
                                              if (!crewsReportSelectedTeams.includes(t.team_name)) return false;
                                              // Filtrar PM1 para não-admin
                                              if (user?.role !== 'admin' && t.team_name === 'PM1') return false;
                                              return true;
                                            })
                                            .forEach(team => {
                                              if (!teamAverages[team.team_name]) {
                                                teamAverages[team.team_name] = { total: 0, count: 0 };
                                              }
                                              teamAverages[team.team_name].total += team[crewsChartMetric] || 0;
                                              teamAverages[team.team_name].count += 1;
                                            });
                                        }
                                      });
                                      
                                      // Melhor equipe (maior média)
                                      Object.entries(teamAverages).forEach(([name, data]) => {
                                        const avg = data.total / data.count;
                                        if (avg > bestTeamAvg) {
                                          bestTeamAvg = avg;
                                          bestTeam = name;
                                        }
                                      });
                                      
                                      const metricLabel = {
                                        total_jobs: 'trabalhos',
                                        total_labor: '',
                                        total_final: '',
                                        lucro_empresa: '',
                                        total_expenses: ''
                                      }[crewsChartMetric] || '';
                                      
                                      const formatValue = (val) => {
                                        if (crewsChartMetric === 'total_jobs') return val.toFixed(0);
                                        return formatMoney(val);
                                      };
                                      
                                      return (
                                        <>
                                          {bestWeek && (
                                            <div className="bg-white p-3 rounded border border-green-300">
                                              <span className="text-green-700 font-semibold">🌟 Melhor Semana:</span>
                                              <p className="text-gray-800">{bestWeek} - {formatValue(bestWeekValue)} {metricLabel}</p>
                                            </div>
                                          )}
                                          {worstWeek && worstWeekValue !== Infinity && (
                                            <div className="bg-white p-3 rounded border border-orange-300">
                                              <span className="text-orange-700 font-semibold">📉 Semana Mais Fraca:</span>
                                              <p className="text-gray-800">{worstWeek} - {formatValue(worstWeekValue)} {metricLabel}</p>
                                            </div>
                                          )}
                                          {bestTeam && (
                                            <div className="bg-white p-3 rounded border border-blue-300">
                                              <span className="text-blue-700 font-semibold">🏆 Melhor Equipe (média):</span>
                                              <p className="text-gray-800">{bestTeam} - {formatValue(bestTeamAvg)} {metricLabel}</p>
                                            </div>
                                          )}
                                          <div className="bg-white p-3 rounded border border-purple-300">
                                            <span className="text-purple-700 font-semibold">📈 Período Analisado:</span>
                                            <p className="text-gray-800">{selectedWeeksForCharts.length} semana(s)</p>
                                          </div>
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                          
                          {/* Empty State - Nenhuma semana selecionada */}
                          {selectedWeeksForCharts.length === 0 && (
                            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                              <p className="text-gray-600 mb-2 text-lg">📅 Selecione pelo menos uma semana</p>
                              <p className="text-sm text-gray-500">Use os checkboxes acima ou os botões de atalho</p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* VISUALIZAÇÃO: RANKING */}
                      {crewsReportViewMode === 'ranking' && crewsGroupBy === 'job' && (
                        <div className="space-y-4">
                          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-300 rounded-lg p-6">
                            <h3 className="text-xl font-bold text-yellow-800 mb-6 flex items-center gap-2">
                              🏆 Top Performers - {crewsReportSortBy === 'total_final' ? 'Lucro' : crewsReportSortBy === 'total_jobs' ? 'Trabalhos' : crewsReportSortBy === 'total_labor' ? 'Total Labor' : 'Despesas'}
                            </h3>
                            
                            <div className="space-y-3">
                              {crewsReportData.teams
                                .filter(team => {
                                  // Filtrar equipes selecionadas
                                  if (!crewsReportSelectedTeams.includes(team.team_name)) return false;
                                  // Filtrar PM1 para usuários não-admin
                                  if (user?.role !== 'admin' && team.team_name === 'PM1') return false;
                                  return true;
                                })
                                .sort((a, b) => {
                                  const aVal = a[crewsReportSortBy] || 0;
                                  const bVal = b[crewsReportSortBy] || 0;
                                  return crewsReportSortOrder === 'desc' ? bVal - aVal : aVal - bVal;
                                })
                                .map((team, index) => (
                                  <div
                                    key={team.team_name}
                                    className={`flex items-center gap-4 p-4 rounded-lg border-2 transition ${
                                      index === 0 ? 'border-yellow-400 bg-yellow-50' :
                                      index === 1 ? 'border-gray-400 bg-gray-50' :
                                      index === 2 ? 'border-orange-400 bg-orange-50' :
                                      'border-gray-200 bg-white'
                                    }`}
                                  >
                                    <div className="text-3xl font-bold text-gray-400 w-12 text-center">
                                      {index < 3 ? (index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉') : `#${index + 1}`}
                                    </div>
                                    <div className="flex-1">
                                      <div className="font-bold text-lg text-gray-800">{team.team_name}</div>
                                      <div className="text-sm text-gray-600">
                                        {[
                                          crewsRankingFields.trabalhos && `${team.total_jobs} trabalhos`,
                                          crewsRankingFields.total_labor && user?.role === 'admin' && `Labor: ${formatMoney(team.total_labor)}`,
                                          crewsRankingFields.total_final && user?.role === 'admin' && `Final: ${formatMoney(team.total_final)}`,
                                          crewsRankingFields.lucro && user?.role === 'admin' && `Lucro: ${formatMoney(team.lucro_empresa || 0)}`,
                                          crewsRankingFields.porcentagem && user?.role === 'admin' && `${team.percentage}%`,
                                          crewsRankingFields.despesas && user?.role === 'admin' && `Desp: ${formatMoney(team.total_expenses)}`
                                        ].filter(Boolean).join(' | ')}
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-2xl font-bold text-green-600">
                                        {user?.role === 'admin' ? (
                                          crewsReportSortBy === 'lucro_empresa' ? formatMoney(team.lucro_empresa || 0) :
                                          crewsReportSortBy === 'total_final' ? formatMoney(team.total_final) :
                                          crewsReportSortBy === 'total_jobs' ? team.total_jobs :
                                          crewsReportSortBy === 'total_labor' ? formatMoney(team.total_labor) :
                                          formatMoney(team.total_expenses)
                                        ) : (
                                          crewsReportSortBy === 'total_jobs' ? team.total_jobs : team.total_jobs
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {user?.role === 'admin' ? (
                                          crewsReportSortBy === 'lucro_empresa' ? 'Lucro (Empresa)' :
                                          crewsReportSortBy === 'total_final' ? 'Total Final' :
                                          crewsReportSortBy === 'total_jobs' ? 'Trabalhos' :
                                          crewsReportSortBy === 'total_labor' ? 'Total Labor' : 'Despesas'
                                        ) : (
                                          'Trabalhos'
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Mensagem se nenhuma equipe selecionada */}
                      {crewsReportSelectedTeams.length === 0 && (
                        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="text-gray-600">
                            Selecione pelo menos uma equipe para visualizar o relatório
                          </p>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Empty State */}
                  {!crewsReportLoading && !crewsReportData && (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-gray-600 mb-4">
                        Clique em "Atualizar" para carregar o relatório das equipes
                      </p>
                      <Button onClick={loadCrewsReport} className="bg-blue-600 hover:bg-blue-700">
                        🔄 Carregar Relatório
                      </Button>
                    </div>
                  )}

                  
                  {/* Modal: Customizar Campos Visíveis */}
                  <Dialog open={showCompareModal} onOpenChange={setShowCompareModal}>
                    <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="crews-compare-modal">
                      <DialogHeader className="flex-shrink-0">
                        <DialogTitle className="text-xl font-bold text-indigo-800 flex items-center gap-2">
                          🆚 Comparar Meses
                        </DialogTitle>
                        <p className="text-sm text-gray-600 mt-1">
                          Cada mês é mostrado por <b>Data do Trabalho</b> e por <b>Data de Pagamento</b>.
                        </p>
                      </DialogHeader>

                      <div className="overflow-y-auto flex-1 pr-2 space-y-4" style={{ WebkitOverflowScrolling: 'touch' }}>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm font-medium mb-1 block">Mês A</Label>
                            <select
                              data-testid="compare-month-a"
                              value={crewsCompareMonthA}
                              onChange={(e) => setCrewsCompareMonthA(e.target.value)}
                              className="w-full border rounded px-3 py-2 text-sm"
                            >
                              <option value="">Selecione...</option>
                              {Array.from(new Set([...crewsWorkMonths, ...crewsPayMonths])).sort((a, b) => b.localeCompare(a)).map(m => (
                                <option key={m} value={m}>{fmtMonthYm(m)}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <Label className="text-sm font-medium mb-1 block">Mês B</Label>
                            <select
                              data-testid="compare-month-b"
                              value={crewsCompareMonthB}
                              onChange={(e) => setCrewsCompareMonthB(e.target.value)}
                              className="w-full border rounded px-3 py-2 text-sm"
                            >
                              <option value="">Selecione...</option>
                              {Array.from(new Set([...crewsWorkMonths, ...crewsPayMonths])).sort((a, b) => b.localeCompare(a)).map(m => (
                                <option key={m} value={m}>{fmtMonthYm(m)}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {crewsCompareLoading && (
                          <div className="text-center text-gray-500 py-8" data-testid="compare-loading">⏳ Carregando comparação...</div>
                        )}

                        {(!crewsCompareMonthA || !crewsCompareMonthB) && !crewsCompareLoading && (
                          <div className="text-center text-gray-400 py-8">Escolha os dois meses para comparar.</div>
                        )}

                        {!crewsCompareLoading && crewsCompareData && crewsCompareMonthA && crewsCompareMonthB && (
                          <div className="overflow-x-auto" data-testid="compare-table">
                            <table className="w-full text-sm border rounded">
                              <thead>
                                <tr className="bg-indigo-50 text-indigo-900">
                                  <th className="px-3 py-2 text-left">Métrica</th>
                                  <th className="px-3 py-2 text-right border-l">{fmtMonthYm(crewsCompareMonthA)}<div className="text-[11px] font-normal text-emerald-700">🗓️ Trabalho</div></th>
                                  <th className="px-3 py-2 text-right">{fmtMonthYm(crewsCompareMonthA)}<div className="text-[11px] font-normal text-blue-700">💰 Pagamento</div></th>
                                  <th className="px-3 py-2 text-right border-l">{fmtMonthYm(crewsCompareMonthB)}<div className="text-[11px] font-normal text-emerald-700">🗓️ Trabalho</div></th>
                                  <th className="px-3 py-2 text-right">{fmtMonthYm(crewsCompareMonthB)}<div className="text-[11px] font-normal text-blue-700">💰 Pagamento</div></th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {[
                                  { k: 'Trabalhos', money: false, get: (d) => (d && d.total_jobs) || 0 },
                                  { k: 'Total Labor', money: true, get: (d) => (d && d.total_labor) || 0 },
                                  { k: 'Despesas', money: true, get: (d) => (d && d.total_expenses) || 0 },
                                  { k: 'Backcharge', money: true, get: (d) => (d && d.total_backcharges) || 0 },
                                  { k: 'Total Final (PM)', money: true, bold: true, get: (d) => (d && d.total_final) || 0 },
                                  { k: 'Lucro (Empresa)', money: true, bold: true, get: (d) => (d && d.lucro_empresa) || 0 },
                                ].map((row, i) => {
                                  const cell = (d) => row.money ? formatMoney(row.get(d)) : row.get(d);
                                  return (
                                    <tr key={i} className="hover:bg-gray-50">
                                      <td className="px-3 py-2 font-medium">{row.k}</td>
                                      <td className={`px-3 py-2 text-right border-l ${row.bold ? 'font-bold text-teal-700' : ''}`}>{cell(crewsCompareData.aWork)}</td>
                                      <td className={`px-3 py-2 text-right ${row.bold ? 'font-bold text-teal-700' : ''}`}>{cell(crewsCompareData.aPay)}</td>
                                      <td className={`px-3 py-2 text-right border-l ${row.bold ? 'font-bold text-teal-700' : ''}`}>{cell(crewsCompareData.bWork)}</td>
                                      <td className={`px-3 py-2 text-right ${row.bold ? 'font-bold text-teal-700' : ''}`}>{cell(crewsCompareData.bPay)}</td>
                                    </tr>
                                  );
                                })}
                                <tr className="bg-yellow-50">
                                  <td className="px-3 py-2 font-semibold text-yellow-800">Δ Pgto − Trab (Total Final)</td>
                                  <td className="px-3 py-2 text-right font-bold text-yellow-800 border-l" colSpan={2}>
                                    {formatMoney(((crewsCompareData.aPay && crewsCompareData.aPay.total_final) || 0) - ((crewsCompareData.aWork && crewsCompareData.aWork.total_final) || 0))}
                                  </td>
                                  <td className="px-3 py-2 text-right font-bold text-yellow-800 border-l" colSpan={2}>
                                    {formatMoney(((crewsCompareData.bPay && crewsCompareData.bPay.total_final) || 0) - ((crewsCompareData.bWork && crewsCompareData.bWork.total_final) || 0))}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                            <p className="text-[11px] text-gray-500 mt-2">
                              Δ = quanto o total muda quando você olha pela data de pagamento em vez da data do trabalho.
                            </p>
                          </div>
                        )}
                      </div>

                      <DialogFooter className="flex-shrink-0">
                        <Button variant="outline" onClick={() => setShowCompareModal(false)}>Fechar</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  <CompareTeamsModal
                    open={showCompareTeamsModal}
                    onClose={() => setShowCompareTeamsModal(false)}
                    teams={crewsReportData?.teams || []}
                    selectedTeams={crewsReportSelectedTeams}
                    weeksBreakdown={crewsReportData?.weeks_breakdown || []}
                    tableFields={crewsTableFields}
                    isAdmin={user?.role === 'admin'}
                    formatMoney={formatMoney}
                  />

                  <Dialog open={showCustomizeModal} onOpenChange={setShowCustomizeModal}>
                    <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
                      <DialogHeader className="flex-shrink-0">
                        <DialogTitle className="text-xl font-bold text-purple-800 flex items-center gap-2">
                          ⚙️ Customizar Campos Visíveis
                        </DialogTitle>
                        <p className="text-sm text-gray-600 mt-2">
                          Escolha quais campos mostrar em cada visualização
                        </p>
                      </DialogHeader>
                      
                      {/* Conteúdo com Scroll */}
                      <div className="space-y-6 overflow-y-auto flex-1 pr-2" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {/* Cards */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-800 mb-3">📊 Visualização: Cards</h4>
                          <div className="grid grid-cols-2 gap-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsCardsFields.trabalhos}
                                onChange={(e) => setCrewsCardsFields({...crewsCardsFields, trabalhos: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Trabalhos</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsCardsFields.total_final}
                                onChange={(e) => setCrewsCardsFields({...crewsCardsFields, total_final: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Total Final</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsCardsFields.lucro}
                                onChange={(e) => setCrewsCardsFields({...crewsCardsFields, lucro: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Lucro</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsCardsFields.total_labor}
                                onChange={(e) => setCrewsCardsFields({...crewsCardsFields, total_labor: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Total Labor</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsCardsFields.porcentagem}
                                onChange={(e) => setCrewsCardsFields({...crewsCardsFields, porcentagem: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Porcentagem</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsCardsFields.despesas}
                                onChange={(e) => setCrewsCardsFields({...crewsCardsFields, despesas: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Despesas</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsCardsFields.backcharge}
                                onChange={(e) => setCrewsCardsFields({...crewsCardsFields, backcharge: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Backcharge</span>
                            </label>
                          </div>
                        </div>
                        
                        {/* Tabela */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h4 className="font-semibold text-green-800 mb-3">📋 Visualização: Tabela</h4>
                          <div className="grid grid-cols-2 gap-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsTableFields.trabalhos}
                                onChange={(e) => setCrewsTableFields({...crewsTableFields, trabalhos: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Trabalhos</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsTableFields.total_labor}
                                onChange={(e) => setCrewsTableFields({...crewsTableFields, total_labor: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Total Labor</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsTableFields.total_final}
                                onChange={(e) => setCrewsTableFields({...crewsTableFields, total_final: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Total Final</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsTableFields.lucro}
                                onChange={(e) => setCrewsTableFields({...crewsTableFields, lucro: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Lucro</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsTableFields.porcentagem}
                                onChange={(e) => setCrewsTableFields({...crewsTableFields, porcentagem: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Porcentagem</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsTableFields.despesas}
                                onChange={(e) => setCrewsTableFields({...crewsTableFields, despesas: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Despesas</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsTableFields.backcharge}
                                onChange={(e) => setCrewsTableFields({...crewsTableFields, backcharge: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Backcharge</span>
                            </label>
                          </div>
                        </div>
                        
                        {/* Rankings */}
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h4 className="font-semibold text-yellow-800 mb-3">🏆 Visualização: Rankings</h4>
                          <div className="grid grid-cols-2 gap-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsRankingFields.trabalhos}
                                onChange={(e) => setCrewsRankingFields({...crewsRankingFields, trabalhos: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Trabalhos</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsRankingFields.total_labor}
                                onChange={(e) => setCrewsRankingFields({...crewsRankingFields, total_labor: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Total Labor</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsRankingFields.total_final}
                                onChange={(e) => setCrewsRankingFields({...crewsRankingFields, total_final: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Total Final</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsRankingFields.lucro}
                                onChange={(e) => setCrewsRankingFields({...crewsRankingFields, lucro: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Lucro</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsRankingFields.porcentagem}
                                onChange={(e) => setCrewsRankingFields({...crewsRankingFields, porcentagem: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Porcentagem</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsRankingFields.despesas}
                                onChange={(e) => setCrewsRankingFields({...crewsRankingFields, despesas: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Despesas</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsRankingFields.backcharge}
                                onChange={(e) => setCrewsRankingFields({...crewsRankingFields, backcharge: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Backcharge</span>
                            </label>
                          </div>
                        </div>
                        
                        {/* Resumo Geral */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-800 mb-3">📊 Resumo Geral</h4>
                          <div className="grid grid-cols-2 gap-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsSummaryFields.total_equipes}
                                onChange={(e) => setCrewsSummaryFields({...crewsSummaryFields, total_equipes: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Total Equipes</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsSummaryFields.total_trabalhos}
                                onChange={(e) => setCrewsSummaryFields({...crewsSummaryFields, total_trabalhos: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Total Trabalhos</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsSummaryFields.total_labor}
                                onChange={(e) => setCrewsSummaryFields({...crewsSummaryFields, total_labor: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Total Labor</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsSummaryFields.total_final}
                                onChange={(e) => setCrewsSummaryFields({...crewsSummaryFields, total_final: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Total Final</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsSummaryFields.total_lucro}
                                onChange={(e) => setCrewsSummaryFields({...crewsSummaryFields, total_lucro: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Total Lucro</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={crewsSummaryFields.total_despesas}
                                onChange={(e) => setCrewsSummaryFields({...crewsSummaryFields, total_despesas: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Total Despesas</span>
                            </label>
                          </div>
                        </div>
                      </div>
                      
                      {/* Botões - Fixos no fundo */}
                      <div className="flex justify-end gap-3 pt-4 border-t flex-shrink-0">
                        <Button
                          variant="outline"
                          onClick={() => setShowCustomizeModal(false)}
                        >
                          Cancelar
                        </Button>
                        <Button
                          onClick={() => {
                            setShowCustomizeModal(false);
                            toast.success('Customização aplicada!');
                          }}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          ✓ Aplicar
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                </CardContent>
              </Card>
            </TabsContent>

            {/* Payroll Tab - ADMIN Only */}
            {user?.role === 'admin' && (


            <TabsContent value="payroll" className="mt-6">
              <Card>
                <CardContent>
                  <div className="space-y-6">
                    {/* Header responsivo */}
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-3 sm:p-4">
                      <h3 className="font-semibold text-lg mb-2 text-green-800">💰 Weekly Payroll Calculator</h3>
                      <p className="text-green-700 text-sm">
                        Automatize o cálculo semanal de pagamentos das equipes baseado nos Labor Bills.
                      </p>
                    </div>

                    {/* Sub-tabs responsivos */}
                    <div className="flex flex-col sm:flex-row gap-2 border-b">
                      <Button
                        variant={payrollSubTab === 'upload' ? 'default' : 'ghost'}
                        onClick={() => setPayrollSubTab('upload')}
                        className="justify-start text-sm"
                        size="sm"
                      >
                        📊 Upload Excel
                      </Button>
                      <Button
                        variant={payrollSubTab === 'teams' ? 'default' : 'ghost'}
                        onClick={() => setPayrollSubTab('teams')}
                        className="justify-start text-sm"
                        size="sm"
                      >
                        👥 Configurar Equipes
                      </Button>
                      <Button
                        variant={payrollSubTab === 'reports' ? 'default' : 'ghost'}
                        onClick={() => setPayrollSubTab('reports')}
                        className="justify-start text-sm"
                        size="sm"
                      >
                        📋 Relatórios
                      </Button>
                    </div>

                    {/* Upload Excel Tab */}
                    {payrollSubTab === 'upload' && (
                      <div className="space-y-4">
                        <div className="border rounded-lg p-3 sm:p-6">
                          <h4 className="font-semibold mb-4 text-sm sm:text-base">📤 Upload Weekly Excel File</h4>
                          
                          <div className="grid grid-cols-1 gap-4 mb-4">
                            <div>
                              <Label className="text-sm font-semibold">Week Ending Date *</Label>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                <Input
                                  type="date"
                                  data-testid="payroll-week-input"
                                  value={payrollWeek}
                                  onChange={(e) => {
                                    console.log('📅 Week ending date changed:', e.target.value);
                                    setPayrollWeek(e.target.value);
                                  }}
                                  className="text-sm border-2 border-blue-300 focus:border-blue-500"
                                  required
                                />
                                {payrollWeek && <span className="text-green-600 text-xs sm:text-sm font-medium">✅ {new Date(payrollWeek).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}</span>}
                              </div>
                              <div className="text-xs text-blue-600 mt-1">
                                💡 Data da sexta-feira da semana do payroll
                              </div>
                            </div>
                            <div>
                              <Label className="text-sm font-semibold">Excel File *</Label>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                <Input
                                  type="file"
                                  data-testid="payroll-file-input"
                                  accept=".xlsx,.xls,.xlsm"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      console.log('📊 Excel file selected:', file.name);
                                      setPayrollFile(file);
                                      setPayrollData(null);
                                      setPayrollReport(null);
                                    }
                                  }}
                                  className="text-sm border-2 border-green-300 focus:border-green-500"
                                  required
                                />
                                {payrollFile && <span className="text-green-600 text-xs sm:text-sm font-medium">✅ {payrollFile.name}</span>}
                              </div>
                              <div className="text-xs text-green-600 mt-1">
                                💡 Arquivo Excel com dados do payroll semanal
                              </div>
                            </div>
                          </div>

                          <Button
                            data-testid="payroll-process-btn"
                            onClick={() => {
                              console.log('🔄 Process button clicked');
                              console.log('📊 payrollFile:', payrollFile ? payrollFile.name : 'null');
                              console.log('📅 payrollWeek:', payrollWeek || 'empty');
                              console.log('⏳ payrollLoading:', payrollLoading);
                              processPayrollExcel();
                            }}
                            disabled={!payrollFile || !payrollWeek || payrollLoading}
                            className={`w-full text-sm sm:text-base ${
                              !payrollFile || !payrollWeek 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                            size="sm"
                          >
                            {payrollLoading ? (
                              <>⏳ Processando...</>
                            ) : (
                              !payrollFile || !payrollWeek ? (
                                <>❌ Preencha os campos acima</>
                              ) : (
                                <>🔄 Processar Excel</>
                              )
                            )}
                          </Button>
                        </div>

                        {/* DEPOIS que processou o Excel */}
                        {payrollReport && (
                          <>
                            {/* Seção Dados Processados por Equipe */}
                            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                              <h3 className="font-semibold text-base sm:text-lg text-blue-800">🎯 Dados Processados por Equipe</h3>
                            </div>

                            {/* 5 Cards de Estatísticas - Calculados dinamicamente baseado nas tabelas */}
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                              <div className="bg-white rounded-lg p-4 border-l-4 border-blue-500 shadow-sm">
                                <div className="flex items-center gap-2">
                                  <div className="text-blue-500 text-lg">👷</div>
                                  <div>
                                    <div className="text-blue-700 text-sm">Total trabalhos</div>
                                    <div className="text-2xl font-bold text-blue-600">
                                      {payrollData?.length || 0}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500 shadow-sm">
                                <div className="flex items-center gap-2">
                                  <div className="text-green-500 text-lg">💰</div>
                                  <div>
                                    <div className="text-green-700 text-sm">Total Labor</div>
                                    <div className="text-2xl font-bold text-green-600">
                                      {formatMoney(payrollData?.reduce((sum, entry) => sum + (entry.labor_total || 0), 0) || 0)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="bg-white rounded-lg p-4 border-l-4 border-purple-500 shadow-sm">
                                <div className="flex items-center gap-2">
                                  <div className="text-purple-500 text-lg">💜</div>
                                  <div>
                                    <div className="text-purple-700 text-sm">Total Final</div>
                                    <div className="text-2xl font-bold text-purple-600">
                                      ${payrollData?.reduce((sum, entry) => sum + (entry.calculated_value || 0), 0)?.toFixed(2) || '0.00'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="bg-white rounded-lg p-4 border-l-4 border-orange-500 shadow-sm">
                                <div className="flex items-center gap-2">
                                  <div className="text-orange-500 text-lg">📊</div>
                                  <div>
                                    <div className="text-orange-700 text-sm">Lucro</div>
                                    <div className="text-2xl font-bold text-orange-600">
                                      ${(() => {
                                        if (!payrollData || !teamPercentages) return '0.00';
                                        
                                        let totalProfit = 0;
                                        
                                        // Agrupar por equipe e calcular lucro considerando 0%
                                        const teamGroups = {};
                                        payrollData.forEach(entry => {
                                          const teamName = entry.team_name || 'Sem Equipe';
                                          if (!teamGroups[teamName]) {
                                            teamGroups[teamName] = { labor: 0, final: 0 };
                                          }
                                          teamGroups[teamName].labor += (entry.labor_total || 0);
                                          teamGroups[teamName].final += (entry.calculated_value || 0);
                                        });
                                        
                                        // Calcular lucro por equipe considerando 0%
                                        Object.keys(teamGroups).forEach(teamName => {
                                          const configuredTeam = teamPercentages.find(tp => tp.team_name === teamName);
                                          const teamPercentage = configuredTeam ? configuredTeam.percentage : 20;
                                          
                                          if (teamPercentage === 0) {
                                            // Equipe 0% = todo o labor vira lucro
                                            totalProfit += teamGroups[teamName].labor;
                                          } else {
                                            // Equipe com % = labor - valor final
                                            totalProfit += teamGroups[teamName].labor - teamGroups[teamName].final;
                                          }
                                        });
                                        
                                        return totalProfit.toFixed(2);
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="bg-white rounded-lg p-4 border-l-4 border-red-500 shadow-sm">
                                <div className="flex items-center gap-2">
                                  <div className="text-red-500 text-lg">💸</div>
                                  <div>
                                    <div className="text-red-700 text-sm">Total Despesas</div>
                                    <div className="text-2xl font-bold text-red-600">
                                      ${payrollData?.reduce((sum, entry) => {
                                        const entryExpenses = entry.expenses ? Object.values(entry.expenses).reduce((expSum, val) => expSum + (val || 0), 0) : 0;
                                        return sum + entryExpenses;
                                      }, 0)?.toFixed(2) || '0.00'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Cards de Equipes */}
                            {payrollReport.team_summary && (
                              <div>
                                <h3 className="font-semibold text-lg mb-4">💰 Resumo Financeiro por Equipe:</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                  {payrollReport.team_summary.sort((a, b) => {
                                    const getPMNumber = (teamName) => {
                                      const match = teamName.match(/PM(\d+)/);
                                      return match ? parseInt(match[1]) : 999;
                                    };
                                    return getPMNumber(a.team_name) - getPMNumber(b.team_name);
                                  }).map((team) => {
                                    // Buscar porcentagem configurada apenas para EXIBIR
                                    const configuredTeam = teamPercentages.find(tp => tp.team_name === team.team_name);
                                    const percentage = configuredTeam ? configuredTeam.percentage : 20;
                                    
                                    return (
                                      <div key={team.team_name} className="bg-white border rounded-lg p-4 relative">
                                        <div className="absolute top-3 right-3">
                                          {payrollReport.id ? (
                                            <Button 
                                              size="sm" 
                                              variant="outline"
                                              onClick={() => downloadPayrollPDF(payrollReport.id, team.team_name)}
                                              className="p-1"
                                              title="Baixar PDF desta equipe"
                                            >
                                              <Download className="h-4 w-4" />
                                            </Button>
                                          ) : (
                                            <Button 
                                              size="sm" 
                                              variant="outline"
                                              onClick={() => toast.info('Salve o relatório primeiro para baixar PDFs individuais por equipe')}
                                              className="p-1 opacity-50"
                                              title="Salve o relatório primeiro"
                                            >
                                              <Download className="h-4 w-4" />
                                            </Button>
                                          )}
                                        </div>
                                        <h4 className="font-bold text-lg mb-3">{team.team_name}</h4>
                                        <div className="space-y-2 text-sm">
                                          <div className="flex justify-between">
                                            <span>Trabalhos:</span>
                                            <span className="font-medium">{team.total_jobs || team.total_entries}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span>Labor Total:</span>
                                            <span className="font-medium">{formatMoney(team.total_labor || 0)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span>Despesas:</span>
                                            <span className="font-medium text-red-600">${(team.total_expenses || 0).toFixed(2)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span>Porcentagem:</span>
                                            <span className="font-medium">{percentage}%</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span>Lucro:</span>
                                            <span className="font-medium text-blue-600">${((team.lucro_empresa || 0) + (backcharges[team.team_name] || []).reduce((s, b) => s + b.valor, 0) - (adicionais[team.team_name] || []).reduce((s, a) => s + a.valor, 0)).toFixed(2)}</span>
                                          </div>
                                          {(backcharges[team.team_name] || []).length > 0 && (
                                            <div className="flex justify-between text-yellow-700 bg-yellow-50 rounded px-1">
                                              <span>Backcharge:</span>
                                              <span className="font-medium">-${(backcharges[team.team_name] || []).reduce((s, b) => s + b.valor, 0).toFixed(2)}</span>
                                            </div>
                                          )}
                                          {(adicionais[team.team_name] || []).length > 0 && (
                                            <div className="flex justify-between text-green-700 bg-green-50 rounded px-1">
                                              <span>Adicional:</span>
                                              <span className="font-medium">+${(adicionais[team.team_name] || []).reduce((s, a) => s + a.valor, 0).toFixed(2)}</span>
                                            </div>
                                          )}
                                          <div className="flex justify-between border-t pt-2">
                                            <span>Valor Final:</span>
                                            <span className="font-bold text-green-600">${((team.total_final_value || team.total_final || 0) - (backcharges[team.team_name] || []).reduce((s, b) => s + b.valor, 0) + (adicionais[team.team_name] || []).reduce((s, a) => s + a.valor, 0)).toFixed(2)}</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Lista de clientes por equipe */}
                            {payrollData && payrollData.length > 0 && (
                              <div className="space-y-6">
                                <div className="flex items-center justify-between mb-4">
                                  <h3 className="font-semibold text-lg">📋 Detalhes por Equipe:</h3>
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-xs">
                                    <div className="flex items-center gap-3 text-blue-800">
                                      <span>✅ Match encontrado</span>
                                      <span>⚠️ Match manual</span>
                                      <span>🚨 Alerta de valor</span>
                                    </div>
                                  </div>
                                </div>
                                {(() => {
                                  // Agrupar payrollData por team_name
                                  const teamGroups = {};
                                  payrollData.forEach((entry) => {
                                    const teamName = entry.team_name || 'Sem Equipe';
                                    if (!teamGroups[teamName]) {
                                      teamGroups[teamName] = [];
                                    }
                                    teamGroups[teamName].push(entry);
                                  });

                                  // Ordenar por PM (PM1, PM2, PM3, etc.)
                                  const sortedTeamNames = Object.keys(teamGroups).sort((a, b) => {
                                    const getPMNumber = (teamName) => {
                                      const match = teamName.match(/PM(\d+)/);
                                      return match ? parseInt(match[1]) : 999;
                                    };
                                    return getPMNumber(a) - getPMNumber(b);
                                  });

                                  return sortedTeamNames.map((teamName) => {
                                    const entries = teamGroups[teamName];
                                    
                                    // Calcular totais para esta equipe
                                    const teamTotals = entries.reduce((totals, entry) => {
                                      const totalExpenses = entry.expenses ? Object.values(entry.expenses).reduce((sum, val) => sum + (val || 0), 0) : 0;
                                      return {
                                        totalExpenses: totals.totalExpenses + totalExpenses,
                                        totalLabor: totals.totalLabor + (entry.labor_total || 0),
                                        totalFinal: totals.totalFinal + (entry.calculated_value || 0)
                                      };
                                    }, { totalExpenses: 0, totalLabor: 0, totalFinal: 0 });
                                    
                                    // Buscar porcentagem configurada para calcular lucro correto
                                    const configuredTeam = teamPercentages.find(tp => tp.team_name === teamName);
                                    const teamPercentage = configuredTeam ? configuredTeam.percentage : 20;
                                    
                                    // Se equipe tem 0%, o Valor Final também entra no lucro
                                    const teamProfit = teamPercentage === 0 
                                      ? teamTotals.totalLabor  // Equipe 0% = todo o labor vira lucro
                                      : teamTotals.totalLabor - teamTotals.totalFinal;  // Equipe com % = labor - valor final
                                    
                                    return (
                                      <div key={teamName} className="border rounded-lg">
                                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-b p-3 sm:p-4">
                                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                            <h4 className="font-semibold text-lg">👥 {teamName}</h4>
                                            <div className="grid grid-cols-2 sm:flex sm:gap-4 text-xs">
                                              <div className="text-center">
                                                <div className="text-gray-600">Despesas</div>
                                                <div className="font-medium text-red-600">${teamTotals.totalExpenses.toFixed(2)}</div>
                                              </div>
                                              <div className="text-center">
                                                <div className="text-gray-600">Labor</div>
                                                <div className="font-medium text-blue-600">{formatMoney(teamTotals.totalLabor)}</div>
                                              </div>
                                              <div className="text-center">
                                                <div className="text-gray-600">Valor Final</div>
                                                <div className="font-medium text-green-600">${(teamTotals.totalFinal - (backcharges[teamName] || []).reduce((s, b) => s + b.valor, 0) + (adicionais[teamName] || []).reduce((s, a) => s + a.valor, 0)).toFixed(2)}</div>
                                              </div>
                                              <div className="text-center">
                                                <div className="text-gray-600">Lucro</div>
                                                <div className="font-medium text-purple-600">${(teamProfit + (backcharges[teamName] || []).reduce((s, b) => s + b.valor, 0) - (adicionais[teamName] || []).reduce((s, a) => s + a.valor, 0)).toFixed(2)}</div>
                                              </div>
                                              {(backcharges[teamName] || []).length > 0 && (
                                                <div className="text-center">
                                                  <div className="text-gray-600">Backcharge</div>
                                                  <div className="font-medium text-yellow-600">-${(backcharges[teamName] || []).reduce((s, b) => s + b.valor, 0).toFixed(2)}</div>
                                                </div>
                                              )}
                                              {(adicionais[teamName] || []).length > 0 && (
                                                <div className="text-center">
                                                  <div className="text-gray-600">Adicional</div>
                                                  <div className="font-medium text-green-600">+${(adicionais[teamName] || []).reduce((s, a) => s + a.valor, 0).toFixed(2)}</div>
                                                </div>
                                              )}
                                              <div className="flex items-end gap-1">
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  className="text-xs h-6 px-2 border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setBackchargeKind('backcharge');
                                                    setBackchargeTeam(teamName);
                                                    setBackchargeForm({ valor: '', descricao: '' });
                                                    setShowBackchargeModal(true);
                                                  }}
                                                >
                                                  ⚠️ Backcharge
                                                </Button>
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  className="text-xs h-6 px-2 border-green-400 text-green-700 hover:bg-green-50"
                                                  data-testid={`preview-adicional-btn-${teamName}`}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setBackchargeKind('adicional');
                                                    setBackchargeTeam(teamName);
                                                    setBackchargeForm({ valor: '', descricao: '' });
                                                    setShowBackchargeModal(true);
                                                  }}
                                                >
                                                  💰 Adicional
                                                </Button>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="p-2 sm:p-4">
                                          <div className="overflow-x-auto">
                                            <table className="w-full text-xs sm:text-sm min-w-[600px]">
                                              <thead className="bg-gray-50">
                                                <tr>
                                                  <th className="text-left p-2 sm:p-3 font-medium">Data</th>
                                                  <th className="text-left p-2 sm:p-3 font-medium">Job #</th>
                                                  <th className="text-left p-2 sm:p-3 font-medium">Customer</th>
                                                  <th className="text-left p-2 sm:p-3 font-medium">Labor</th>
                                                  <th className="text-left p-2 sm:p-3 font-medium">Despesas</th>
                                                  <th className="text-left p-2 sm:p-3 font-medium">Valor Final</th>
                                                  <th className="text-left p-2 sm:p-3 font-medium">Match/Alert</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {entries.map((entry, index) => (
                                                  <tr 
                                                    key={index} 
                                                    className={`border-t hover:bg-gray-50 cursor-pointer ${
                                                      entry.found_match 
                                                        ? 'bg-green-50 hover:bg-green-100' 
                                                        : 'bg-red-50 hover:bg-red-100'
                                                    }`}
                                                    onClick={() => handleWorkEntryClick(entry)}
                                                  >
                                                    <td className="p-2 sm:p-3">{formatDate(entry.date)}</td>
                                                    <td className="p-2 sm:p-3">{entry.job_number}</td>
                                                    <td className="p-2 sm:p-3 truncate max-w-[120px]">{entry.customer}</td>
                                                    <td className="p-2 sm:p-3 font-medium">{formatMoney(entry.labor_total || entry.labor || 0)}</td>
                                                    <td className="p-2 sm:p-3 font-medium text-red-600">
                                                      ${entry.expenses ? Object.values(entry.expenses).reduce((sum, val) => sum + (val || 0), 0).toFixed(2) : '0.00'}
                                                    </td>
                                                    <td className="p-2 sm:p-3 font-medium text-green-600">${(entry.calculated_value || 0).toFixed(2)}</td>
                                                    <td className="p-2 sm:p-3">
                                                      <div className="flex items-center gap-1">
                                                        {entry.found_match ? (
                                                          <Badge className="bg-green-100 text-green-800 text-xs">✅</Badge>
                                                        ) : (
                                                          <Badge className="bg-orange-100 text-orange-800 text-xs">⚠️</Badge>
                                                        )}
                                                        {entry.price_discrepancy && (
                                                          <div 
                                                            className="text-red-500 cursor-help" 
                                                            title={`Alerta: Labor do Excel ($${(entry.labor_total || 0).toFixed(2)}) diferente do Labor Bill ($${(entry.labor_bill_total || 0).toFixed(2)})`}
                                                          >
                                                            🚨
                                                          </div>
                                                        )}
                                                        {entry.suggested_teams && entry.suggested_teams.length > 1 && !entry.is_shared_work && (
                                                          <button
                                                            data-testid={`suggest-divide-${entry.id}`}
                                                            onClick={(e) => { e.stopPropagation(); openTeamDivisionModal(entry); }}
                                                            className="bg-amber-100 border border-amber-300 text-amber-800 rounded px-1.5 py-0.5 text-[10px] font-semibold hover:bg-amber-200 whitespace-nowrap"
                                                            title={`Trabalho multi-equipe (${entry.suggested_teams.join(', ')}). Clique para dividir igualmente entre as equipes.`}
                                                          >
                                                            👥 Dividir: {entry.suggested_teams.join('+')}
                                                          </button>
                                                        )}
                                                      </div>
                                                    </td>
                                                  </tr>
                                                ))}
                                                {/* Backcharge rows - yellow background */}
                                                {(backcharges[teamName] || []).map((bc, bcIdx) => (
                                                  <tr key={`bc-${bcIdx}`} className="border-t bg-yellow-50 hover:bg-yellow-100">
                                                    <td className="p-2 sm:p-3 text-yellow-800 font-medium">⚠️</td>
                                                    <td className="p-2 sm:p-3 text-yellow-800 font-medium">BACKCHARGE</td>
                                                    <td className="p-2 sm:p-3 text-yellow-800">{bc.descricao}</td>
                                                    <td className="p-2 sm:p-3"></td>
                                                    <td className="p-2 sm:p-3"></td>
                                                    <td className="p-2 sm:p-3 font-bold text-red-600">-${bc.valor.toFixed(2)}</td>
                                                    <td className="p-2 sm:p-3">
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setBackcharges(prev => ({
                                                            ...prev,
                                                            [teamName]: (prev[teamName] || []).filter((_, i) => i !== bcIdx)
                                                          }));
                                                        }}
                                                        className="text-red-500 hover:text-red-700 text-xs"
                                                        title="Remover backcharge"
                                                      >
                                                        🗑️
                                                      </button>
                                                    </td>
                                                  </tr>
                                                ))}
                                                {/* Adicional rows - green background */}
                                                {(adicionais[teamName] || []).map((ad, adIdx) => (
                                                  <tr key={`ad-${adIdx}`} className="border-t bg-green-50 hover:bg-green-100" data-testid={`preview-adicional-row-${teamName}-${adIdx}`}>
                                                    <td className="p-2 sm:p-3 text-green-800 font-medium">💰</td>
                                                    <td className="p-2 sm:p-3 text-green-800 font-medium">ADICIONAL</td>
                                                    <td className="p-2 sm:p-3 text-green-800">{ad.descricao}</td>
                                                    <td className="p-2 sm:p-3"></td>
                                                    <td className="p-2 sm:p-3"></td>
                                                    <td className="p-2 sm:p-3 font-bold text-green-700">+${ad.valor.toFixed(2)}</td>
                                                    <td className="p-2 sm:p-3">
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setAdicionais(prev => ({
                                                            ...prev,
                                                            [teamName]: (prev[teamName] || []).filter((_, i) => i !== adIdx)
                                                          }));
                                                        }}
                                                        className="text-red-500 hover:text-red-700 text-xs"
                                                        title="Remover adicional"
                                                      >
                                                        🗑️
                                                      </button>
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            )}

                            {/* Botões de Salvamento */}

                            {/* Modal Backcharge / Adicional */}
                            <Dialog open={showBackchargeModal} onOpenChange={setShowBackchargeModal}>
                              <DialogContent className="max-w-md">
                                <DialogHeader>
                                  <DialogTitle>{backchargeKind === 'adicional' ? '💰 Adicional' : '⚠️ Backcharge'} - {backchargeTeam}</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div>
                                    <Label>{backchargeKind === 'adicional' ? 'Valor a acrescentar ($)' : 'Valor a deduzir ($)'}</Label>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder="Ex: 150.00"
                                      value={backchargeForm.valor}
                                      onChange={(e) => setBackchargeForm(prev => ({ ...prev, valor: e.target.value }))}
                                      data-testid="preview-adjust-valor-input"
                                    />
                                  </div>
                                  <div>
                                    <Label>Descrição</Label>
                                    <Input
                                      placeholder={backchargeKind === 'adicional' ? 'Ex: Bônus por desempenho, serviço extra...' : 'Ex: Dano no material, retrabalho janela #3...'}
                                      value={backchargeForm.descricao}
                                      onChange={(e) => setBackchargeForm(prev => ({ ...prev, descricao: e.target.value }))}
                                      data-testid="preview-adjust-descricao-input"
                                    />
                                  </div>
                                  <div className={`border rounded p-3 text-sm ${backchargeKind === 'adicional' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-yellow-50 border-yellow-200 text-yellow-800'}`}>
                                    {backchargeKind === 'adicional' ? `Este valor será somado ao Valor Final da equipe ${backchargeTeam}.` : `Este valor será subtraído do Valor Final da equipe ${backchargeTeam}.`}
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      className={`flex-1 ${backchargeKind === 'adicional' ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700'}`}
                                      data-testid="preview-adjust-apply-btn"
                                      onClick={() => {
                                        const valor = parseFloat(backchargeForm.valor);
                                        if (!valor || valor <= 0) { toast.error('Informe um valor válido'); return; }
                                        if (!backchargeForm.descricao.trim()) { toast.error('Informe uma descrição'); return; }
                                        const item = { id: Date.now().toString(), valor, descricao: backchargeForm.descricao.trim() };
                                        const setter = backchargeKind === 'adicional' ? setAdicionais : setBackcharges;
                                        setter(prev => ({
                                          ...prev,
                                          [backchargeTeam]: [...(prev[backchargeTeam] || []), item]
                                        }));
                                        toast.success(`${backchargeKind === 'adicional' ? 'Adicional' : 'Backcharge'} de $${valor.toFixed(2)} aplicado em ${backchargeTeam}`);
                                        setShowBackchargeModal(false);
                                      }}
                                    >
                                      {backchargeKind === 'adicional' ? 'Aplicar Adicional' : 'Aplicar Backcharge'}
                                    </Button>
                                    <Button variant="outline" onClick={() => setShowBackchargeModal(false)}>
                                      Cancelar
                                    </Button>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                            {payrollReport?.is_preview && (
                              <div className="mt-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h3 className="font-semibold text-lg text-green-800 mb-2">💾 Salvar Relatório</h3>
                                    <p className="text-green-700 text-sm">
                                      Salvar dados processados no banco de dados
                                    </p>
                                  </div>
                                  <div className="flex gap-3">
                                    <Button 
                                      onClick={() => saveReportOnly()}
                                      disabled={saveReportLoading}
                                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-3"
                                      size="lg"
                                    >
                                      {saveReportLoading ? (
                                        <>
                                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                          Salvando...
                                        </>
                                      ) : (
                                        <>
                                          💾 Salvar Relatório
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                </div>
                                <div className="mt-4 text-sm text-green-600">
                                  <div>
                                    <strong>📊 Salvar Relatório:</strong> Salva clientes encontrados para esta data
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Teams Configuration Tab */}
                    {payrollSubTab === 'teams' && (
                      <div className="space-y-4">
                        <div className="border rounded-lg p-6">
                          <h4 className="font-semibold mb-4">👥 Configurar Porcentagens das Equipes</h4>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                              <Label>Nome da Equipe</Label>
                              <Input
                                placeholder="Ex: PM1, PM2, etc."
                                value={newTeamName}
                                onChange={(e) => setNewTeamName(e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Porcentagem (%)</Label>
                              <Input
                                type="number"
                                placeholder="Ex: 75"
                                value={newTeamPercentage}
                                onChange={(e) => setNewTeamPercentage(e.target.value)}
                                min="0"
                                max="100"
                              />
                            </div>
                            <div className="flex items-end">
                              <Button onClick={addTeamPercentage} className="w-full">
                                Adicionar Equipe
                              </Button>
                            </div>
                          </div>

                          <div className="space-y-2">
                            {teamPercentages.map((team) => (
                              <div key={team.id} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                                <div className="flex items-center gap-3">
                                  <span className="font-medium">{team.team_name}</span>
                                  {editingTeam === team.team_name ? (
                                    <div className="flex items-center gap-2">
                                      <Input
                                        type="number"
                                        value={tempPercentage}
                                        onChange={(e) => setTempPercentage(e.target.value)}
                                        className="w-20"
                                        min="0"
                                        max="100"
                                      />
                                      <span>%</span>
                                    </div>
                                  ) : (
                                    <Badge variant="outline">{team.percentage?.toFixed(0)}%</Badge>
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  {editingTeam === team.team_name ? (
                                    <>
                                      <Button size="sm" onClick={() => updateTeamPercentage(team.team_name, tempPercentage)}>
                                        <Save className="h-4 w-4" />
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => {
                                        setEditingTeam(null);
                                        setTempPercentage('');
                                      }}>
                                        ✗
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Button size="sm" variant="outline" onClick={() => {
                                        setEditingTeam(team.team_name);
                                        setTempPercentage(team.percentage?.toString() || '0');
                                      }}>
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => deleteTeamPercentage(team.team_name)}>
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                            {teamPercentages.length === 0 && (
                              <p className="text-center text-slate-500 py-6">
                                Nenhuma equipe configurada. Adicione equipes acima.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Reports Tab */}
                    {payrollSubTab === 'reports' && (
                      <div className="space-y-4">
                        <div className="border rounded-lg p-3 sm:p-6">
                          <PayrollReportsList
                            reports={payrollReports}
                            language={language}
                            onView={(report) => setSelectedReport(report)}
                            onGeneratePDFs={generateAllPayrollPDFs}
                            onAddRemoveClients={() => {
                              setShowManualClientModal(true);
                              setManualClientAction('add');
                              loadAvailableLaborBills();
                            }}
                            onEditReports={() => {
                              setShowEditReportModal(true);
                              loadPayrollReports();
                            }}
                            onBackcharges={() => setShowReportBackchargeModal(true)}
                            onClearThrottle={() => {
                              window.__last_gen_all_pdfs__ = 0;
                              toast.success(language === 'pt-BR' ? 'Throttling de PDF limpo!' : 'PDF throttling cleared!');
                            }}
                            onExportCSV={exportPayrollReportsCSV}
                          />
                          <ReportBackchargeModal
                            open={showReportBackchargeModal}
                            onOpenChange={setShowReportBackchargeModal}
                            reports={payrollReports}
                            language={language}
                            onChanged={(reportId, adj) => {
                              setPayrollReports((prev) => prev.map((r) => (r.id === reportId ? { ...r, ...adj } : r)));
                              setSelectedReport((prev) => (prev && prev.id === reportId ? { ...prev, ...adj } : prev));
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* Report Tab - Daily Leader */}
          {user?.role === 'daily_leader' && (
            <TabsContent value="report" className="mt-6">
              <DailyLeaderReport />
            </TabsContent>
          )}


          {/* TRUCKS TAB - Sistema de Aluguel de Caminhões */}

          {/* ABA: Push Notifications */}
          <TabsContent value="push" className="mt-6">
            <PushNotificationsPanel language={language} />
          </TabsContent>

          <TabsContent value="trucks" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  🚛 {language === 'pt-BR' ? 'Sistema de Caminhões' : 'Truck System'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {user?.role === 'admin' ? (
                  /* ADMIN VIEW */
                  <div className="space-y-4">
                    {/* Sub-tabs Admin */}
                    <div className="flex gap-2 border-b pb-2 overflow-x-auto">
                      <Button size="sm" variant={trucksSubTab === 'dashboard' ? 'default' : 'outline'} onClick={() => setTrucksSubTab('dashboard')}>
                        📊 Dashboard
                      </Button>
                      <Button size="sm" variant={trucksSubTab === 'fleet' ? 'default' : 'outline'} onClick={() => setTrucksSubTab('fleet')}>
                        🚛 {language === 'pt-BR' ? 'Frota' : 'Fleet'}
                      </Button>
                      <Button size="sm" variant={trucksSubTab === 'requests' ? 'default' : 'outline'} onClick={() => setTrucksSubTab('requests')}>
                        📋 {language === 'pt-BR' ? 'Solicitações' : 'Requests'}
                      </Button>
                    </div>

                    {/* Dashboard */}
                    {trucksSubTab === 'dashboard' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <Card className="bg-green-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-3xl font-bold text-green-600">{trucks.filter(t => t.status === 'disponivel').length}</p>
                              <p className="text-sm text-gray-600">{language === 'pt-BR' ? 'Disponíveis' : 'Available'}</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-yellow-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-3xl font-bold text-yellow-600">{trucks.filter(t => t.status === 'reservado').length}</p>
                              <p className="text-sm text-gray-600">{language === 'pt-BR' ? 'Reservados' : 'Reserved'}</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-blue-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-3xl font-bold text-blue-600">{trucks.filter(t => t.status === 'alugado').length}</p>
                              <p className="text-sm text-gray-600">{language === 'pt-BR' ? 'Em Uso' : 'In Use'}</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-orange-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-3xl font-bold text-orange-600">{trucks.filter(t => t.status === 'manutencao').length}</p>
                              <p className="text-sm text-gray-600">{language === 'pt-BR' ? 'Manutenção' : 'Maintenance'}</p>
                            </CardContent>
                          </Card>
                        </div>

                        {/* Aluguéis Ativos - Admin View */}
                        <div>
                          <h3 className="font-semibold mb-3 flex items-center gap-2">
                            🔄 {language === 'pt-BR' ? 'Aluguéis Ativos' : 'Active Rentals'} ({myRentals.filter(r => r.status === 'ativo').length})
                          </h3>
                          {myRentals.filter(r => r.status === 'ativo').map(rental => {
                            const rentalTruck = trucks.find(t => t.id === rental.truck_id);
                            if (!rentalTruck) return null;
                            return (
                              <Card key={rental.id} className="mb-3">
                                <CardContent className="p-4">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-semibold">{rentalTruck.placa} • {rental.pm_name}</p>
                                      <p className="text-sm text-gray-600">
                                        {new Date(rental.check_in.data).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })} - {language === 'pt-BR' ? 'Ativo' : 'Active'}
                                      </p>
                                      <p className="text-sm text-gray-600">
                                        🗒️ {rental.total_atualizacoes || 0} {language === 'pt-BR' ? 'atualizações' : 'updates'}
                                      </p>
                                    </div>
                                    <div className="flex gap-2">
                                      <Button size="sm" variant="outline" onClick={() => {
                                        setSelectedRental(rental);
                                        setSelectedTruck(rentalTruck);
                                        setShowRentalHistoryModal(true);
                                      }}>
                                        👁️ {language === 'pt-BR' ? 'Ver' : 'View'}
                                      </Button>
                                      <Button size="sm" onClick={() => {
                                        setSelectedRental(rental);
                                        setSelectedTruck(rentalTruck);
                                        setShowCheckOutModal(true);
                                      }} className="bg-green-600 hover:bg-green-700">
                                        🏁 Check-out
                                      </Button>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })}
                          {myRentals.filter(r => r.status === 'ativo').length === 0 && (
                            <p className="text-center text-gray-500 py-8">{language === 'pt-BR' ? 'Nenhum aluguel ativo no momento' : 'No active rentals at the moment'}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Fleet Management */}
                    {trucksSubTab === 'fleet' && (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h3 className="font-semibold">{language === 'pt-BR' ? 'Gerenciar Frota' : 'Manage Fleet'}</h3>
                          <Button onClick={() => {
                            setSelectedTruck(null);
                            setShowTruckFormModal(true);
                          }} className="bg-blue-600 hover:bg-blue-700">
                            ➕ {language === 'pt-BR' ? 'Novo Caminhão' : 'New Truck'}
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {trucks.map(truck => (
                            <TruckCard 
                              key={truck.id}
                              truck={truck}
                              isAdmin={true}
                              onEdit={(t) => {
                                setSelectedTruck(t);
                                setShowTruckFormModal(true);
                              }}
                              onMaintenance={(t) => {
                                setSelectedTruck(t);
                                setShowMaintenanceHistoryModal(true);
                              }}
                              onViewDocument={handleViewTruckDocument}
                              onVisibilityChange={() => {
                                loadTrucks();
                              }}
                              language={language}
                            />
                          ))}
                        </div>
                        {trucks.length === 0 && (
                          <div className="text-center py-12">
                            <p className="text-gray-500">{language === 'pt-BR' ? 'Nenhum caminhão cadastrado' : 'No trucks registered'}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Requests */}
                    {trucksSubTab === 'requests' && (
                      <div className="space-y-4">
                        <h3 className="font-semibold">{language === 'pt-BR' ? 'Solicitações Pendentes' : 'Pending Requests'}</h3>
                        <div className="space-y-3">
                          {myRentals.filter(r => r.status === 'pendente').map(rental => {
                            const rentalTruck = trucks.find(t => t.id === rental.truck_id);
                            return (
                              <RentalRequestCard 
                                key={rental.id}
                                rental={rental}
                                truck={rentalTruck}
                                onApprove={(r) => {
                                  setSelectedRental(r);
                                  setSelectedTruck(rentalTruck);
                                  setApproveRejectAction('approve');
                                  setShowApproveRejectModal(true);
                                }}
                                onReject={(r) => {
                                  setSelectedRental(r);
                                  setSelectedTruck(rentalTruck);
                                  setApproveRejectAction('reject');
                                  setShowApproveRejectModal(true);
                                }}
                                language={language}
                              />
                            );
                          })}
                          {myRentals.filter(r => r.status === 'pendente').length === 0 && (
                            <p className="text-center text-gray-500 py-8">{language === 'pt-BR' ? 'Nenhuma solicitação pendente' : 'No pending requests'}</p>
                          )}
                        </div>
                      </div>
                    )}

                  </div>
                ) : (
                  /* PM VIEW */
                  <div className="space-y-4">
                    {/* Sub-tabs PM */}
                    <div className="flex gap-2 border-b pb-2 overflow-x-auto">
                      <Button size="sm" variant={trucksSubTab === 'my-rentals' ? 'default' : 'outline'} onClick={() => setTrucksSubTab('my-rentals')}>
                        📋 {language === 'pt-BR' ? 'Meus Aluguéis' : 'My Rentals'}
                      </Button>
                      <Button size="sm" variant={trucksSubTab === 'available' ? 'default' : 'outline'} onClick={() => setTrucksSubTab('available')}>
                        🚛 {language === 'pt-BR' ? 'Disponíveis' : 'Available'}
                      </Button>
                      <Button size="sm" variant={trucksSubTab === 'history' ? 'default' : 'outline'} onClick={() => setTrucksSubTab('history')}>
                        📜 {language === 'pt-BR' ? 'Histórico' : 'History'}
                      </Button>
                    </div>

                    {/* My Rentals */}
                    {trucksSubTab === 'my-rentals' && (
                      <div className="space-y-3">
                        <h3 className="font-semibold">{language === 'pt-BR' ? 'Meus Aluguéis' : 'My Rentals'}</h3>
                        
                        {/* Aluguel Ativo */}
                        {activeRental && (() => {
                          const rentalTruck = trucks.find(t => t.id === activeRental.truck_id);
                          return rentalTruck ? (
                            <div>
                              <p className="text-sm text-blue-600 font-semibold mb-2">🔵 {language === 'pt-BR' ? 'Aluguel Ativo' : 'Active Rental'}</p>
                              <ActiveRentalCard 
                                rental={activeRental}
                                truck={rentalTruck}
                                language={language}
                                onRequestCheckout={() => handleRequestCheckout(activeRental)}
                                onAddUpdate={() => {
                                  setSelectedRental(activeRental);
                                  setShowRentalUpdateModal(true);
                                }}
                                onViewHistory={() => {
                                  setSelectedRental(activeRental);
                                  setSelectedTruck(rentalTruck);
                                  setShowRentalHistoryModal(true);
                                }}
                              />
                            </div>
                          ) : null;
                        })()}

                        {/* Aprovados - Aguardando Check-in */}
                        {myRentals.filter(r => r.status === 'aprovado').length > 0 && (
                          <div>
                            <p className="text-sm text-green-600 font-semibold mb-2 mt-4">✅ {language === 'pt-BR' ? 'Aprovados - Fazer Check-in' : 'Approved - Do Check-in'}</p>
                            {myRentals.filter(r => r.status === 'aprovado').map(rental => {
                              const rentalTruck = trucks.find(t => t.id === rental.truck_id);
                              return (
                                <Card key={rental.id} className="mb-2">
                                  <CardContent className="p-3">
                                    <p className="font-semibold">{rentalTruck?.modelo} ({rentalTruck?.placa})</p>
                                    <p className="text-sm text-gray-600">{language === 'pt-BR' ? 'Início:' : 'Start:'} {new Date(rental.data_inicio_solicitada).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}</p>
                                    <Button size="sm" onClick={() => {
                                      setSelectedRental(rental);
                                      setSelectedTruck(rentalTruck);
                                      setShowCheckInModal(true);
                                    }} className="mt-2 bg-green-600 hover:bg-green-700">
                                      ✅ {language === 'pt-BR' ? 'Fazer Check-in' : 'Do Check-in'}
                                    </Button>
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        )}

                        {/* Pendentes */}
                        {myRentals.filter(r => r.status === 'pendente').length > 0 && (
                          <div>
                            <p className="text-sm text-yellow-600 font-semibold mb-2 mt-4">🟡 {language === 'pt-BR' ? 'Aguardando Aprovação' : 'Awaiting Approval'}</p>
                            {myRentals.filter(r => r.status === 'pendente').map(rental => {
                              const rentalTruck = trucks.find(t => t.id === rental.truck_id);
                              return (
                                <RentalRequestCard 
                                  key={rental.id}
                                  rental={rental}
                                  truck={rentalTruck}
                                  language={language}
                                />
                              );
                            })}
                          </div>
                        )}

                        {myRentals.length === 0 && (
                          <div className="text-center py-12">
                            <p className="text-gray-500">{language === 'pt-BR' ? 'Você ainda não tem aluguéis' : 'You have no rentals yet'}</p>
                            <Button onClick={() => setTrucksSubTab('available')} className="mt-4">
                              {language === 'pt-BR' ? 'Ver Caminhões Disponíveis' : 'View Available Trucks'}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Available Trucks */}
                    {trucksSubTab === 'available' && (
                      <div className="space-y-4">
                        <h3 className="font-semibold">{language === 'pt-BR' ? 'Caminhões Disponíveis' : 'Available Trucks'}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {trucks.filter(t => t.status === 'disponivel').map(truck => (
                            <TruckCard 
                              key={truck.id}
                              truck={truck}
                              isAdmin={false}
                              onSelect={handleRequestRental}
                              onViewDocument={handleViewTruckDocument}
                              language={language}
                            />
                          ))}
                        </div>
                        {trucks.filter(t => t.status === 'disponivel').length === 0 && (
                          <p className="text-center text-gray-500 py-12">{language === 'pt-BR' ? 'Nenhum caminhão disponível no momento' : 'No trucks available at the moment'}</p>
                        )}
                      </div>
                    )}

                    {/* History */}
                    {trucksSubTab === 'history' && (
                      <div className="space-y-4">
                        <h3 className="font-semibold">{language === 'pt-BR' ? 'Histórico de Aluguéis' : 'Rental History'}</h3>
                        {myRentals.filter(r => ['finalizado', 'rejeitado', 'cancelado'].includes(r.status)).map(rental => {
                          const rentalTruck = trucks.find(t => t.id === rental.truck_id);
                          
                          const getStatusBadge = (status) => {
                            switch(status) {
                              case 'finalizado':
                                return <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">✅ {language === 'pt-BR' ? 'Completo' : 'Complete'}</span>;
                              case 'rejeitado':
                                return <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">❌ {language === 'pt-BR' ? 'Rejeitado' : 'Rejected'}</span>;
                              case 'cancelado':
                                return <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">🚫 {language === 'pt-BR' ? 'Cancelado' : 'Cancelled'}</span>;
                              default:
                                return <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">⏳ {status}</span>;
                            }
                          };
                          
                          return (
                            <Card key={rental.id} className="mb-2">
                              <CardContent className="p-3">
                                <div className="flex items-start justify-between mb-2">
                                  <p className="font-semibold">{rentalTruck?.modelo} ({rentalTruck?.placa})</p>
                                  {getStatusBadge(rental.status)}
                                </div>
                                {rental.status === 'finalizado' && (
                                  <>
                                    <p className="text-sm text-gray-600">
                                      {rental.check_in?.data && new Date(rental.check_in.data).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })} - {rental.check_out?.data && new Date(rental.check_out.data).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}
                                    </p>
                                    <p className="text-sm font-semibold text-green-600">
                                      ${rental.financeiro?.valor_final?.toFixed(2)} ({rental.financeiro?.dias_alugados} {language === 'pt-BR' ? 'dias' : 'days'})
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {rental.financeiro?.status_pagamento === 'pago' ? '✅ Pago' : '⏳ Pendente Pagamento'}
                                    </p>
                                  </>
                                )}
                                {rental.status === 'rejeitado' && (
                                  <p className="text-sm text-red-600 mt-1">
                                    {language === 'pt-BR' ? 'Motivo:' : 'Reason:'} {rental.motivo_rejeicao}
                                  </p>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                        {myRentals.filter(r => ['finalizado', 'rejeitado', 'cancelado'].includes(r.status)).length === 0 && (
                          <p className="text-center text-gray-500 py-8">{language === 'pt-BR' ? 'Nenhum histórico ainda' : 'No history yet'}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>


      {/* Truck Rental Modals */}
      <RentalRequestModal isOpen={showRentalRequestModal} onClose={() => setShowRentalRequestModal(false)} truck={selectedTruck} onSuccess={handleRentalSuccess} language={language} />
      <RentalUpdateModal isOpen={showRentalUpdateModal} onClose={() => setShowRentalUpdateModal(false)} rental={selectedRental} onSuccess={handleRentalSuccess} language={language} />
      <RentalHistoryModal isOpen={showRentalHistoryModal} onClose={() => setShowRentalHistoryModal(false)} rental={selectedRental} truck={selectedTruck} language={language} />
      <CheckInModal isOpen={showCheckInModal} onClose={() => setShowCheckInModal(false)} rental={selectedRental} truck={selectedTruck} onSuccess={handleRentalSuccess} language={language} />
      <CheckOutModal isOpen={showCheckOutModal} onClose={() => setShowCheckOutModal(false)} rental={selectedRental} truck={selectedTruck} onSuccess={handleRentalSuccess} language={language} />
      <ApproveRejectModal isOpen={showApproveRejectModal} onClose={() => setShowApproveRejectModal(false)} rental={selectedRental} truck={selectedTruck} action={approveRejectAction} onSuccess={handleRentalSuccess} language={language} />
      <TruckFormModal isOpen={showTruckFormModal} onClose={() => setShowTruckFormModal(false)} truck={selectedTruck} onSuccess={handleRentalSuccess} language={language} />
      

      {/* Maintenance Modals */}
      <MaintenanceModal 
        isOpen={showMaintenanceModal} 
        onClose={() => setShowMaintenanceModal(false)} 
        truck={selectedTruck} 
        onSuccess={handleRentalSuccess} 
        language={language} 
      />
      <MaintenanceHistoryModal 
        isOpen={showMaintenanceHistoryModal} 
        onClose={() => setShowMaintenanceHistoryModal(false)} 
        truck={selectedTruck}
        onNewMaintenance={(truck) => {
          setSelectedTruck(truck);
          setShowMaintenanceModal(true);
        }}
        onCompleteMaintenance={(maint, truck) => {
          setSelectedMaintenance(maint);
          setSelectedTruck(truck);
          setShowCompleteMaintenanceModal(true);
        }}
        language={language} 
      />
      <CompleteMaintenanceModal 
        isOpen={showCompleteMaintenanceModal} 
        onClose={() => setShowCompleteMaintenanceModal(false)} 
        maintenance={selectedMaintenance}
        truck={selectedTruck}
        onSuccess={handleRentalSuccess} 
        language={language} 
      />
      
      {/* Schedule Work Modal */}
      <AddEditWorkModal
        isOpen={showAddWorkModal}
        onClose={() => {
          setShowAddWorkModal(false);
          setSelectedWork(null);
          setMeasureSource(null);
          setProjectSource(null);
        }}
        work={selectedWork}
        date={selectedDate}
        onSuccess={() => loadScheduleJobs(selectedDate.getMonth(), selectedDate.getFullYear())}
        onAIProcess={startLaborBillAI}
        language={language}
        userRole={user?.role}
        fromMeasure={measureSource}
        fromProject={projectSource}
      />

      {/* Undo Merge Banner (Admin/GM) - dentro da árvore React, clique confiável */}
      {undoMerge && (
        <div
          data-testid="undo-merge-banner"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-3 bg-zinc-900 text-white px-4 py-3 rounded-full shadow-2xl border border-zinc-700"
        >
          <span className="text-sm font-medium">🔀 {language === 'pt-BR' ? 'Trabalhos mesclados' : 'Jobs merged'}</span>
          <button
            data-testid="undo-merge-btn"
            onClick={() => handleUndoMerge(undoMerge.undoId)}
            className="text-sm font-bold text-indigo-300 hover:text-indigo-200 underline underline-offset-2"
          >
            ↩️ {language === 'pt-BR' ? 'Desfazer' : 'Undo'}
          </button>
          <button
            onClick={() => { if (undoTimerRef.current) { clearTimeout(undoTimerRef.current); undoTimerRef.current = null; } setUndoMerge(null); }}
            className="text-zinc-400 hover:text-white text-lg leading-none ml-1"
            aria-label="fechar"
          >
            ×
          </button>
        </div>
      )}


      {/* Merge Jobs Confirm Modal (Admin/GM) */}
      <Dialog open={!!mergeConfirm} onOpenChange={(o) => { if (!o) setMergeConfirm(null); }}>
        <DialogContent className="max-w-md" data-testid="merge-jobs-dialog">
          <DialogHeader>
            <DialogTitle>🔀 {language === 'pt-BR' ? 'Mesclar trabalhos?' : 'Merge jobs?'}</DialogTitle>
          </DialogHeader>
          {mergeConfirm && (
            <div className="space-y-3 text-sm">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5">
                <div className="text-[11px] font-semibold text-emerald-700 mb-0.5">
                  ✅ {language === 'pt-BR' ? 'Manter (recebe os dados)' : 'Keep (receives data)'}
                </div>
                <div className="font-medium text-zinc-900">{mergeConfirm.target.customer_name}</div>
                <div className="text-xs text-zinc-500">#{mergeConfirm.target.order_number || '—'} • {mergeConfirm.target.team}</div>
              </div>
              <div className="text-center text-zinc-400 text-lg leading-none">↑</div>
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-2.5">
                <div className="text-[11px] font-semibold text-rose-700 mb-0.5">
                  🗑️ {language === 'pt-BR' ? 'Será APAGADO (dados mesclados acima)' : 'Will be DELETED (data merged above)'}
                </div>
                <div className="font-medium text-zinc-900">{mergeConfirm.source.customer_name}</div>
                <div className="text-xs text-zinc-500">#{mergeConfirm.source.order_number || '—'} • {mergeConfirm.source.team}</div>
              </div>
              <p className="text-xs text-zinc-500">
                {language === 'pt-BR'
                  ? 'O trabalho de cima mantém o que já tem preenchido e recebe do outro tudo que estiver vazio (logística, etc.). Esta ação não pode ser desfeita.'
                  : 'The top job keeps its filled fields and receives the empty ones from the other (logistics, etc.). This cannot be undone.'}
              </p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setMergeConfirm(null)} data-testid="merge-cancel-btn">
              {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={() => handleMergeJobs(mergeConfirm.source, mergeConfirm.target)}
              data-testid="merge-confirm-btn"
            >
              🔀 {language === 'pt-BR' ? 'Mesclar' : 'Merge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Block Day Modal */}
      <BlockDayModal
        isOpen={showBlockDayModal}
        onClose={() => setShowBlockDayModal(false)}
        date={selectedDate}
        scheduleJobs={scheduleJobs}
        onBlock={handleBlockDay}
        language={language}
      />
      

      
      {/* Notification Modal */}
      <RentalNotificationModal
        notification={currentNotification}
        isOpen={showNotificationModal}
        onClose={() => setShowNotificationModal(false)}
        onApprove={handleApproveFromModal}
        onReject={handleRejectFromModal}
        onViewDetails={handleViewDetailsFromModal}
        onMarkAsRead={handleMarkNotificationAsRead}
        language={language}
      />

      {/* FOM Review Changes Modal (notificação + histórico) */}
      <FomChangesModal
        open={showFomReviewModal}
        onClose={() => setShowFomReviewModal(false)}
        data={fomReview}
      />

        

        {/* Modal Visualização Conta/Alerta (Home) */}
        <Dialog open={!!homeBillViewItem} onOpenChange={() => setHomeBillViewItem(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>
                {BILL_CATEGORY_ICONS[homeBillViewItem?.category] || '📌'} {homeBillViewItem?.name}
              </DialogTitle>
            </DialogHeader>
            {homeBillViewItem && (() => {
              const unit = companyUnits.find(u => u.id === homeBillViewItem.unit_id);
              const dateField = homeBillViewItem.expiry_date || homeBillViewItem.next_due_date;
              const daysLeft = (() => { if (!dateField) return 999; const t = new Date(); t.setHours(0,0,0,0); return Math.ceil((new Date(dateField+'T12:00:00') - t) / 86400000); })();
              return (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-500">Categoria:</span><p className="font-semibold">{homeBillViewItem.category}</p></div>
                    <div><span className="text-gray-500">Vencimento:</span><p className="font-semibold">{dateField ? new Date(dateField+'T12:00:00').toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' }) : '-'}</p></div>
                    {homeBillViewItem.value > 0 && <div><span className="text-gray-500">Valor:</span><p className="font-semibold text-orange-600">${homeBillViewItem.value?.toLocaleString('en-US', {minimumFractionDigits: 2})}</p></div>}
                    {homeBillViewItem.frequency && <div><span className="text-gray-500">Frequência:</span><p className="font-semibold">{homeBillViewItem.frequency === 'daily' ? 'Diária' : homeBillViewItem.frequency === 'monthly' ? 'Mensal' : homeBillViewItem.frequency === 'annual' ? 'Anual' : homeBillViewItem.frequency === 'quarterly' ? 'Trimestral' : homeBillViewItem.frequency === 'weekly' ? 'Semanal' : homeBillViewItem.frequency}</p></div>}
                    {unit && <div><span className="text-gray-500">Unidade:</span><p className="font-semibold">{unit.name}</p></div>}
                    <div><span className="text-gray-500">Status:</span><p className={`font-semibold ${daysLeft < 0 ? 'text-red-600' : daysLeft <= 7 ? 'text-yellow-600' : 'text-green-600'}`}>{daysLeft < 0 ? `Vencido há ${Math.abs(daysLeft)}d` : `${daysLeft} dias restantes`}</p></div>
                  </div>
                  {homeBillViewItem.notes && <div className="text-sm"><span className="text-gray-500">Obs:</span> {homeBillViewItem.notes}</div>}
                  {homeBillViewItem.file_name && homeBillViewItem.file_id && (
                    <div className="text-sm">
                      <span className="text-gray-500">Documento:</span>
                      <span 
                        onClick={() => { setHomeBillViewItem(null); openPdfWithLoading({ url: `${API}/uploaded-files/${homeBillViewItem.file_id}`, title: homeBillViewItem.file_name }); }}
                        className="ml-1 text-blue-600 hover:underline cursor-pointer font-medium"
                      >
                        📎 {homeBillViewItem.file_name}
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="mt-4 flex flex-col gap-2">
              {homeBillViewItem && !homeBillViewItem.item_type && (
                <Button data-testid="home-bill-mark-paid-btn" onClick={() => markHomeBillPaid(homeBillViewItem)} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                  <CheckCircle className="h-4 w-4 mr-1.5" /> Marcar como Pago
                </Button>
              )}
              <Button onClick={() => { setHomeBillViewItem(null); setActiveTab('admin'); setAdminSubTab('contas'); }} className="w-full bg-blue-600 hover:bg-blue-700">
                Ver em Contas a Pagar
              </Button>
              <Button variant="outline" onClick={() => setHomeBillViewItem(null)} className="w-full">Fechar</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Share Extension Modal */}
        <ShareReceiverModal
          isOpen={shareModalOpen}
          onClose={() => { setShareModalOpen(false); setSharedFile(null); setSharedFileType(''); }}
          sharedFile={sharedFile}
          sharedFileType={sharedFileType}
        />

        {/* Flyer de notificação push com imagem */}
        <NotificationFlyer />

        {/* Footer - Copyright e Versão */}
        <footer className="mt-8 py-4 border-t border-gray-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-2">


      {/* Truck Document Viewer Modal */}
      {showTruckDocModal && truckDocToView && (
        <Dialog open={showTruckDocModal} onOpenChange={setShowTruckDocModal}>
          <DialogContent className="max-w-4xl h-[80vh]">
            <DialogHeader>
              <DialogTitle>
                {truckDocToView.type === 'registration' ? '📄 Registration' : '🛡️ Seguro'} - {truckDocToView.truck?.placa}
              </DialogTitle>
            </DialogHeader>
            <div className="h-full overflow-auto bg-gray-50 p-4">
              {truckDocToView.url.endsWith('.pdf') || truckDocToView.url.includes('.pdf') ? (
                <iframe src={truckDocToView.url} className="w-full h-full border-0 rounded" title="Document" />
              ) : (
                <img src={truckDocToView.url} alt="Document" className="w-full h-auto rounded shadow" />
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

            <div className="text-sm text-gray-600 text-center sm:text-left">
              Copyright © 2025 PM Home Remodeling LLC. All rights reserved.
            </div>
            <div className="text-sm text-gray-500 font-mono">
              v7.8
            </div>
          </div>
        </footer>
      </div>

      {/* Selected Report Details Modal */}
      {selectedReport && (
        <Dialog open={!!selectedReport} onOpenChange={handleCloseReportModal}>
          <DialogContent className="sm:max-w-6xl">
            <DialogHeader>
              <DialogTitle>📊 Detalhes do Relatório - {formatDate(selectedReport.week_ending)}</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 max-h-[70vh] overflow-auto pr-1">
              {/* Resumo Financeiro - 5 Cards iguais aos da parte principal */}
              <div>
                <h4 className="font-semibold text-blue-800 mb-3">🎯 Resumo Financeiro</h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-white rounded-lg p-4 border-l-4 border-blue-500 shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="text-blue-500 text-lg">👷</div>
                      <div>
                        <div className="text-blue-700 text-sm">Total trabalhos</div>
                        <div className="text-2xl font-bold text-blue-600">
                          {selectedReport.entries?.length || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border-l-4 border-green-500 shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="text-green-500 text-lg">💰</div>
                      <div>
                        <div className="text-green-700 text-sm">Total Labor</div>
                        <div className="text-2xl font-bold text-green-600">
                          {formatMoney(selectedReport.entries?.reduce((sum, entry) => sum + (entry.labor_total || 0), 0) || 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border-l-4 border-purple-500 shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="text-purple-500 text-lg">💜</div>
                      <div>
                        <div className="text-purple-700 text-sm">Total Final</div>
                        <div className="text-2xl font-bold text-purple-600">
                          ${(() => {
                            const entriesTotal = selectedReport.entries?.reduce((sum, entry) => sum + (entry.calculated_value || 0), 0) || 0;
                            const bcTotal = selectedReport.backcharges ? Object.values(selectedReport.backcharges).reduce((sum, bcs) => sum + (Array.isArray(bcs) ? bcs.reduce((s, b) => s + (b.valor || 0), 0) : 0), 0) : 0;
                            const adTotal = selectedReport.adicionais ? Object.values(selectedReport.adicionais).reduce((sum, ads) => sum + (Array.isArray(ads) ? ads.reduce((s, a) => s + (a.valor || 0), 0) : 0), 0) : 0;
                            return (entriesTotal - bcTotal + adTotal).toFixed(2);
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border-l-4 border-orange-500 shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="text-orange-500 text-lg">📊</div>
                      <div>
                        <div className="text-orange-700 text-sm">Lucro</div>
                        <div className="text-2xl font-bold text-orange-600">
                          ${(() => {
                            if (!selectedReport.entries || !teamPercentages) return '0.00';
                            
                            let totalProfit = 0;
                            
                            // Agrupar por equipe e calcular lucro considerando 0%
                            const teamGroups = {};
                            selectedReport.entries.forEach(entry => {
                              const teamName = entry.team_name || 'Sem Equipe';
                              if (!teamGroups[teamName]) {
                                teamGroups[teamName] = { labor: 0, final: 0 };
                              }
                              teamGroups[teamName].labor += (entry.labor_total || 0);
                              teamGroups[teamName].final += (entry.calculated_value || 0);
                            });
                            
                            // Calcular lucro por equipe considerando 0%
                            Object.keys(teamGroups).forEach(teamName => {
                              const configuredTeam = teamPercentages.find(tp => tp.team_name === teamName);
                              const teamPercentage = configuredTeam ? configuredTeam.percentage : 20;
                              
                              if (teamPercentage === 0) {
                                // Equipe 0% = todo o labor vira lucro
                                totalProfit += teamGroups[teamName].labor;
                              } else {
                                // Equipe com % = labor - valor final
                                totalProfit += teamGroups[teamName].labor - teamGroups[teamName].final;
                              }
                            });
                            
                            return totalProfit.toFixed(2);
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border-l-4 border-red-500 shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="text-red-500 text-lg">💸</div>
                      <div>
                        <div className="text-red-700 text-sm">Total Despesas</div>
                        <div className="text-2xl font-bold text-red-600">
                          ${selectedReport.entries?.reduce((sum, entry) => {
                            const entryExpenses = entry.expenses ? Object.values(entry.expenses).reduce((expSum, val) => expSum + (val || 0), 0) : 0;
                            return sum + entryExpenses;
                          }, 0)?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Backcharges total */}
                  {selectedReport.backcharges && Object.values(selectedReport.backcharges).some(bcs => bcs && bcs.length > 0) && (
                    <div className="bg-white rounded-lg p-4 border-l-4 border-yellow-500 shadow-sm">
                      <div className="flex items-center gap-2">
                        <div className="text-yellow-500 text-lg">⚠️</div>
                        <div>
                          <div className="text-yellow-700 text-sm">Backcharges</div>
                          <div className="text-2xl font-bold text-yellow-600">
                            -${Object.values(selectedReport.backcharges).reduce((sum, bcs) => 
                              sum + (Array.isArray(bcs) ? bcs.reduce((s, b) => s + (b.valor || 0), 0) : 0), 0).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Adicionais total */}
                  {selectedReport.adicionais && Object.values(selectedReport.adicionais).some(ads => ads && ads.length > 0) && (
                    <div className="bg-white rounded-lg p-4 border-l-4 border-green-500 shadow-sm" data-testid="report-view-adicionais-total">
                      <div className="flex items-center gap-2">
                        <div className="text-green-500 text-lg">💰</div>
                        <div>
                          <div className="text-green-700 text-sm">Adicionais</div>
                          <div className="text-2xl font-bold text-green-600">
                            +${Object.values(selectedReport.adicionais).reduce((sum, ads) => 
                              sum + (Array.isArray(ads) ? ads.reduce((s, a) => s + (a.valor || 0), 0) : 0), 0).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Teams Summary - Calculado dinamicamente dos entries para garantir consistência */}
              {selectedReport.entries && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold">💰 Resumo por Equipe</h4>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={recalculateTeamSummary}
                      className="flex items-center gap-1"
                    >
                      <RefreshCw className="h-4 w-4" /> Atualizar
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {selectedReport.team_summary?.sort((a, b) => {
                      // Ordenar por número do PM (PM1, PM2, PM3...)
                      const getPMNumber = (teamName) => {
                        const match = teamName.match(/PM(\d+)/);
                        return match ? parseInt(match[1]) : 999;
                      };
                      return getPMNumber(a.team_name) - getPMNumber(b.team_name);
                    }).map((team, index) => {
                      // Buscar porcentagem configurada apenas para EXIBIR
                      const configuredTeam = teamPercentages.find(tp => tp.team_name === team.team_name);
                      const teamPercentage = configuredTeam ? configuredTeam.percentage : 20;
                      
                      // Backcharges e adicionais da equipe
                      const teamBcs = selectedReport.backcharges && selectedReport.backcharges[team.team_name];
                      const teamBcTotal = Array.isArray(teamBcs) ? teamBcs.reduce((s, b) => s + (b.valor || 0), 0) : 0;
                      const teamAds = selectedReport.adicionais && selectedReport.adicionais[team.team_name];
                      const teamAdTotal = Array.isArray(teamAds) ? teamAds.reduce((s, a) => s + (a.valor || 0), 0) : 0;
                      
                      return (
                        <div key={index} className="bg-white border rounded-lg p-4">
                          <h5 className="font-semibold mb-2">{team.team_name}</h5>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span>Trabalhos:</span>
                              <span className="font-medium">{team.total_jobs || team.total_entries}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Labor Total:</span>
                              <span className="font-medium">${(team.total_labor || 0).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Despesas:</span>
                              <span className="font-medium text-red-600">${(team.total_expenses || 0).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Porcentagem:</span>
                              <span className="font-medium">{teamPercentage}%</span>
                            </div>
                            {teamBcTotal > 0 && (
                              <div className="flex justify-between bg-yellow-50 rounded px-1">
                                <span className="text-yellow-700">Backcharge:</span>
                                <span className="font-medium text-yellow-700">-${teamBcTotal.toFixed(2)}</span>
                              </div>
                            )}
                            {teamAdTotal > 0 && (
                              <div className="flex justify-between bg-green-50 rounded px-1">
                                <span className="text-green-700">Adicional:</span>
                                <span className="font-medium text-green-700">+${teamAdTotal.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span>Lucro:</span>
                              <span className="font-medium text-blue-600">${((team.lucro_empresa || 0) + teamBcTotal - teamAdTotal).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between border-t pt-2">
                              <span>Valor Final:</span>
                              <span className="font-bold text-green-600">${((team.total_final_value || team.total_final || 0) - teamBcTotal + teamAdTotal).toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Work Entries - clientes encontrados agrupados por equipe */}
              {selectedReport.entries && (
                <div>
                  <h4 className="font-semibold mb-3">📋 Clientes Encontrados por Equipe</h4>
                  
                  {(() => {
                    // Group entries by team
                    const foundEntries = selectedReport.entries.filter(entry => entry.found_match);
                    const entriesByTeam = foundEntries.reduce((groups, entry) => {
                      const team = entry.team_name || 'Sem Equipe';
                      if (!groups[team]) {
                        groups[team] = [];
                      }
                      groups[team].push(entry);
                      return groups;
                    }, {});
                    
                    const teamNames = Object.keys(entriesByTeam).sort();
                    
                    return (
                      <div className="space-y-4">
                        {teamNames.map((teamName) => (
                          <div key={teamName} className="border border-gray-200 rounded-lg overflow-hidden">
                            {/* Team Header */}
                            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200 px-4 py-3">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <h5 className="font-semibold text-blue-800 flex items-center gap-2">
                                  👥 {teamName}
                                </h5>
                                <div className="grid grid-cols-2 sm:flex sm:gap-4 text-xs">
                                  {(() => {
                                    // Calcular totais desta equipe
                                    const teamEntries = entriesByTeam[teamName];
                                    const teamTotals = teamEntries.reduce((totals, entry) => {
                                      const totalExpenses = entry.expenses ? Object.values(entry.expenses).reduce((sum, val) => sum + (val || 0), 0) : 0;
                                      return {
                                        totalExpenses: totals.totalExpenses + totalExpenses,
                                        totalLabor: totals.totalLabor + (entry.labor_total || 0),
                                        totalFinal: totals.totalFinal + (entry.calculated_value || 0)
                                      };
                                    }, { totalExpenses: 0, totalLabor: 0, totalFinal: 0 });
                                    
                                    // Buscar porcentagem e calcular lucro com lógica 0%
                                    const configuredTeam = teamPercentages.find(tp => tp.team_name === teamName);
                                    const teamPercentage = configuredTeam ? configuredTeam.percentage : 20;
                                    const teamProfit = teamPercentage === 0 
                                      ? teamTotals.totalLabor  // Equipe 0% = todo o labor vira lucro
                                      : teamTotals.totalLabor - teamTotals.totalFinal;  // Equipe com % = labor - valor final
                                    
                                    // Backcharges e adicionais
                                    const teamBcs = selectedReport.backcharges && selectedReport.backcharges[teamName];
                                    const bcTotal = Array.isArray(teamBcs) ? teamBcs.reduce((s, b) => s + (b.valor || 0), 0) : 0;
                                    const teamAds = selectedReport.adicionais && selectedReport.adicionais[teamName];
                                    const adTotal = Array.isArray(teamAds) ? teamAds.reduce((s, a) => s + (a.valor || 0), 0) : 0;
                                    
                                    return (
                                      <>
                                        <div className="text-center">
                                          <div className="text-gray-600">Despesas</div>
                                          <div className="font-medium text-red-600">${teamTotals.totalExpenses.toFixed(2)}</div>
                                        </div>
                                        <div className="text-center">
                                          <div className="text-gray-600">Labor</div>
                                          <div className="font-medium text-blue-600">${teamTotals.totalLabor.toFixed(2)}</div>
                                        </div>
                                        <div className="text-center">
                                          <div className="text-gray-600">Valor Final</div>
                                          <div className="font-medium text-green-600">${(teamTotals.totalFinal - bcTotal + adTotal).toFixed(2)}</div>
                                        </div>
                                        <div className="text-center">
                                          <div className="text-gray-600">Lucro</div>
                                          <div className="font-medium text-purple-600">${(teamProfit + bcTotal - adTotal).toFixed(2)}</div>
                                        </div>
                                        {bcTotal > 0 && (
                                          <div className="text-center">
                                            <div className="text-gray-600">Backcharge</div>
                                            <div className="font-medium text-yellow-600">-${bcTotal.toFixed(2)}</div>
                                          </div>
                                        )}
                                        {adTotal > 0 && (
                                          <div className="text-center">
                                            <div className="text-gray-600">Adicional</div>
                                            <div className="font-medium text-green-600">+${adTotal.toFixed(2)}</div>
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                            
                            {/* Team Entries */}
                            <div className="space-y-1 max-h-64 overflow-auto">
                              {entriesByTeam[teamName].map((entry, index) => (
                                <div 
                                  key={entry.id || `${teamName}-${index}`}
                                  className="flex items-center justify-between p-3 border-b last:border-b-0 bg-white hover:bg-gray-50"
                                >
                                  <div className="flex-1">
                                    <div className="font-medium text-sm">{entry.customer}</div>
                                    <div className="text-xs text-gray-600">
                                      Job: {entry.job_number} | {language === 'pt-BR' ? 'Data' : 'Date'}: {formatDate(entry.date)} | Labor: ${(entry.labor_total || entry.labor || 0).toFixed(2)}
                                      {(() => {
                                        const expenses = entry.expenses || {};
                                        const totalExpenses = Object.values(expenses).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
                                        if (totalExpenses > 0) {
                                          return (
                                            <span className="text-red-600 font-medium">
                                              {' | '}{language === 'pt-BR' ? 'Despesas' : 'Expenses'}: ${totalExpenses.toFixed(2)}
                                            </span>
                                          );
                                        }
                                        return null;
                                      })()}
                                    </div>
                                    {entry.calculated_value && (
                                      <div className="text-xs text-purple-600">
                                        Valor Final: ${entry.calculated_value.toFixed(2)}
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className="flex gap-1 ml-3">
                                    <Badge className="bg-green-100 text-green-800 text-xs">✅</Badge>
                                    {entry.match_type && (
                                      <Badge className="bg-blue-100 text-blue-800 text-xs">
                                        {entry.match_type === 'manual' ? '🔧' : '🤖'}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                              {/* Backcharge rows */}
                              {selectedReport.backcharges && selectedReport.backcharges[teamName] && selectedReport.backcharges[teamName].map((bc, bcIdx) => (
                                <div 
                                  key={`bc-${bcIdx}`}
                                  className="flex items-center justify-between p-3 border-b last:border-b-0 bg-yellow-50"
                                >
                                  <div className="flex-1">
                                    <div className="font-medium text-sm text-yellow-800">⚠️ BACKCHARGE</div>
                                    <div className="text-xs text-yellow-700">{bc.descricao}</div>
                                  </div>
                                  <div className="font-bold text-red-600 text-sm">-${(bc.valor || 0).toFixed(2)}</div>
                                </div>
                              ))}
                              {/* Adicional rows */}
                              {selectedReport.adicionais && selectedReport.adicionais[teamName] && selectedReport.adicionais[teamName].map((ad, adIdx) => (
                                <div 
                                  key={`ad-${adIdx}`}
                                  className="flex items-center justify-between p-3 border-b last:border-b-0 bg-green-50"
                                  data-testid={`report-view-adicional-${teamName}-${adIdx}`}
                                >
                                  <div className="flex-1">
                                    <div className="font-medium text-sm text-green-800">💰 ADICIONAL</div>
                                    <div className="text-xs text-green-700">{ad.descricao}</div>
                                  </div>
                                  <div className="font-bold text-green-700 text-sm">+${(ad.valor || 0).toFixed(2)}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                        
                        {foundEntries.length === 0 && (
                          <div className="text-center py-6 bg-gray-50 rounded-lg">
                            <p className="text-slate-500">Nenhum cliente encontrado neste relatório</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <div className="text-sm text-blue-800">
                      <strong>📊 Resumo Geral:</strong> {selectedReport.entries.filter(entry => entry.found_match).length} de {selectedReport.entries.length} clientes encontrados
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button onClick={handleCloseReportModal}>Fechar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* User Settings Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('userSettings')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 max-h-[70vh] overflow-auto pr-1">
            {/* Redefinir Senha */}
            <div>
              <h4 className="font-semibold mb-3">{t('changePassword')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>{language === 'pt-BR' ? 'Senha Atual' : 'Current Password'}</Label>
                  <Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
                </div>
                <div>
                  <Label>{language === 'pt-BR' ? 'Nova Senha' : 'New Password'}</Label>
                  <Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
                </div>
              </div>
              <div className="flex justify-end mt-3">
                <Button variant="outline" onClick={async () => {
                  if (!pw.current || !pw.next) return toast.error(language === 'pt-BR' ? 'Preencha as senhas' : 'Fill in passwords');
                  try {
                    setProfileLoading(true);
                    await axios.post(`${API}/change-password`, { old_password: pw.current, new_password: pw.next });
                    toast.success(language === 'pt-BR' ? 'Senha alterada' : 'Password changed');
                    setPw({ current: "", next: "" });
                  } catch (e) {
                    toast.error(e.response?.data?.detail || (language === 'pt-BR' ? 'Falha ao alterar a senha' : 'Failed to change password'));
                  } finally {
                    setProfileLoading(false);
                  }
                }} disabled={profileLoading}>{t('changePassword')}</Button>
              </div>
            </div>

            {/* Backup */}
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Backup</h4>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <Button onClick={exportBackup} disabled={backupLoading} className="bg-slate-700 hover:bg-slate-800">Baixar Backup (JSON)</Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                  <div className="md:col-span-2">
                    <Input type="file" accept="application/json" onChange={onBackupFileChange} />
                  </div>
                  <div>
                    <select value={importMode} onChange={(e) => setImportMode(e.target.value)} className="border rounded px-2 py-2 w-full">
                      <option value="merge">Merge (mesclar)</option>
                      <option value="replace">Replace (substituir)</option>
                    </select>
                  </div>
                  {importMode === 'replace' && (
                    <div className="md:col-span-3 text-sm text-red-700">
                      <label>
                        <input type="checkbox" checked={confirmReplace} onChange={(e) => setConfirmReplace(e.target.checked)} className="mr-2 align-middle" />
                        Eu entendo que o modo Replace irá substituir meus dados atuais.
                      </label>
                    </div>
                  )}
                  <div className="md:col-span-3 flex justify-end">
                    <Button variant="outline" onClick={importBackup} disabled={backupLoading || !backupFile}>Carregar Backup</Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Destinatários de E-mail */}
            {user?.role !== 'admin' && (
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">
                  📧 {language === 'pt-BR' ? 'Destinatários de E-mail' : 'Email Recipients'}
                </h4>
                
                {/* Formulário para adicionar novo destinatário */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h5 className="font-medium text-blue-800 mb-3">
                    {language === 'pt-BR' ? 'Adicionar Novo Destinatário' : 'Add New Recipient'}
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label>{t('fullName')}</Label>
                      <Input 
                        value={newRecipient.name} 
                        onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })} 
                        placeholder={language === 'pt-BR' ? 'Ex: João Silva' : 'Ex: John Smith'}
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <Label>{t('email')}</Label>
                      <Input 
                        type="email"
                        value={newRecipient.email} 
                        onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })} 
                        placeholder={language === 'pt-BR' ? 'joao.silva@exemplo.com' : 'john.smith@example.com'}
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <Label>{language === 'pt-BR' ? 'Telefone (SMS)' : 'Phone (SMS)'}</Label>
                      <Input 
                        type="tel"
                        value={newRecipient.phone || ''} 
                        onChange={(e) => setNewRecipient({ ...newRecipient, phone: e.target.value })} 
                        placeholder="+1 (555) 123-4567"
                        className="bg-white"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end mt-3">
                    <Button 
                      onClick={addEmailRecipient}
                      disabled={!newRecipient.name || !newRecipient.email}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {language === 'pt-BR' ? 'Adicionar Destinatário' : 'Add Recipient'}
                    </Button>
                  </div>
                </div>

                {/* Lista de destinatários existentes */}
                <div className="space-y-3">
                  <h5 className="font-medium text-gray-700">
                    {language === 'pt-BR' ? 'Destinatários Cadastrados' : 'Registered Recipients'} ({emailRecipients.length})
                  </h5>
                  
                  {emailRecipients.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                      <div className="text-gray-400 mb-2">📧</div>
                      <p className="text-gray-500 text-sm">Nenhum destinatário cadastrado</p>
                      <p className="text-gray-400 text-xs mt-1">Adicione destinatários para enviar Labor Bills por email</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {emailRecipients.map((recipient) => (
                        <div 
                          key={recipient.id} 
                          className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                {recipient.admin_locked && (
                                  <span title="Adicionado pelo Admin - Não pode ser removido" className="text-yellow-500">
                                    🔒
                                  </span>
                                )}
                                <div className="font-medium text-gray-900 truncate">
                                  {recipient.name}
                                </div>
                              </div>
                              <div className="text-sm text-gray-600 truncate">
                                {recipient.email}
                              </div>
                              {recipient.admin_locked && (
                                <div className="text-xs text-yellow-600 mt-1">
                                  Gerenciado pelo Admin
                                </div>
                              )}
                            </div>
                            <div className="ml-3">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                disabled={recipient.admin_locked}
                                title={recipient.admin_locked ? 'Apenas o Admin pode remover este destinatário' : 'Remover destinatário'}
                                onClick={() => !recipient.admin_locked && deleteEmailRecipient(recipient.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Informações adicionais */}
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>💡 {language === 'pt-BR' ? 'Dica' : 'Tip'}:</strong> {language === 'pt-BR' ? 
                      'Os destinatários cadastrados aqui estarão disponíveis ao enviar Labor Bills por email. Se você adicionar um telefone, será enviado um SMS avisando sobre o email (incluindo para verificar spam/junk).' :
                      'Recipients registered here will be available when sending Labor Bills by email. If you add a phone number, an SMS will be sent notifying about the email (including to check spam/junk).'
                    }
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    {language === 'pt-BR' ? 
                      'Destinatários com ícone 🔒 foram adicionados pelo Admin e não podem ser removidos.' :
                      'Recipients with 🔒 icon were added by Admin and cannot be removed.'
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Informações Pessoais (Team Leader Profile) - Apenas PMs */}
            {user?.role !== 'admin' && (
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold">{t('personalInfo')}</h4>
                  <Button 
                    onClick={async () => {
                      try {
                        setTeamLeaderLoading(true);
                        
                        // Carregar dados do próprio PM
                        await loadMyTeamLeaderInfo();
                        
                        // Carregar helpers do PM (próprios)
                        await loadMyHelpers();
                        
                        // Abrir modal VER (com helpers editáveis para PM)
                        setShowTeamLeaderViewModal(true);
                      } catch (e) {
                        console.error('Erro ao carregar perfil:', e);
                        toast.error('Erro ao carregar perfil');
                      } finally {
                        setTeamLeaderLoading(false);
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <User className="h-4 w-4 mr-2" />
                    {t('manageProfile')}
                  </Button>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800 mb-2">
                    <strong>📋 {language === 'pt-BR' ? 'Gerencie suas informações pessoais e documentos' : 'Manage your personal information and documents'}</strong>
                  </p>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• {language === 'pt-BR' ? 'Dados pessoais e de contato' : 'Personal and contact data'}</li>
                    <li>• Upload de Driver License</li>
                    <li>• {language === 'pt-BR' ? 'Certificados profissionais' : 'Professional certificates'}</li>
                    <li>• {language === 'pt-BR' ? 'Documentos de background' : 'Background documents'}</li>
                    <li>• Arbitration files</li>
                  </ul>
                </div>
              </div>
            )}
            
            {/* Meus Documentos - Apenas PMs */}
            {user?.role !== 'admin' && (
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">
                  📄 {language === 'pt-BR' ? 'Meus Documentos' : 'My Documents'}
                </h4>
                
                {/* Waivers Assinados - Mostrar se tem pelo menos 1 assinado */}
                {pendingWaivers.filter(w => w.status === 'signed').length > 0 ? (
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">📄</span>
                          <div>
                            <p className="font-medium text-green-800">
                              Termos de Responsabilidade Assinados
                            </p>
                            <p className="text-sm text-gray-600">
                              PM Home Remodeling & Bimcle Construction
                            </p>
                            <p className="text-xs text-green-600 mt-1">
                              ✅ Assinado em {pendingWaivers.find(w => w.status === 'signed')?.subcontratado_data}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={async () => {
                              const pdfUrl = `${API}/waiver-files/pm/${user.username}/combined-pdf?lang=pt`;
                              const response = await axios.get(pdfUrl, {
                                responseType: 'blob',
                                headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
                              });
                              
                              const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
                              setPdfModalData({
                                url: blobUrl,
                                title: 'Waivers Combinados (Português)'
                              });
                              setShowPDFModal(true);
                            }}
                            className="w-12 h-12 rounded border-2 border-green-500 hover:border-green-600 hover:shadow-lg transition-all flex items-center justify-center text-2xl"
                            title="Ver em Português (PM Home + Bimcle)"
                          >
                            🇧🇷
                          </button>
                          <button
                            onClick={async () => {
                              const pdfUrl = `${API}/waiver-files/pm/${user.username}/combined-pdf?lang=en`;
                              const response = await axios.get(pdfUrl, {
                                responseType: 'blob',
                                headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
                              });
                              
                              const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
                              setPdfModalData({
                                url: blobUrl,
                                title: 'Combined Waivers (English)'
                              });
                              setShowPDFModal(true);
                            }}
                            className="w-12 h-12 rounded border-2 border-blue-500 hover:border-blue-600 hover:shadow-lg transition-all flex items-center justify-center text-2xl"
                            title="View in English (PM Home + Bimcle)"
                          >
                            🇺🇸
                          </button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <p className="text-sm">Nenhum documento assinado ainda.</p>
                  </div>
                )}
              </div>
            )}

            {/* Admin removido do modal de Configurações */}
            {false && user?.role === 'admin' && (
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Admin — Usuários</h4>

                {/* Criar */}
                <div className="bg-slate-50 p-3 rounded-lg mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label>Username</Label>
                    <Input value={adminCreate.username} onChange={(e) => setAdminCreate({ ...adminCreate, username: e.target.value })} />
                  </div>

                  <div>
                    <Label>Senha</Label>
                    <Input type="password" value={adminCreate.password} onChange={(e) => setAdminCreate({ ...adminCreate, password: e.target.value })} />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <select className="border rounded px-2 py-2 w-full" value={adminCreate.role} onChange={(e) => setAdminCreate({ ...adminCreate, role: e.target.value })}>
                      <option value="user">PM (User)</option>
                      <option value="admin">Admin</option>
                      <option value="general_manager">General Manager</option>
                      <option value="daily_leader">Daily Leader</option>
                    </select>
                  </div>
                  <div>
                    <Label>Primeiro Nome</Label>
                    <Input value={adminCreate.first_name} onChange={(e) => setAdminCreate({ ...adminCreate, first_name: e.target.value })} />
                  </div>
                  <div>
                    <Label>Sobrenome</Label>
                    <Input value={adminCreate.last_name} onChange={(e) => setAdminCreate({ ...adminCreate, last_name: e.target.value })} />
                  </div>
                  <div>
                    <Label>Telefone</Label>
                    <Input value={adminCreate.phone} onChange={(e) => setAdminCreate({ ...adminCreate, phone: e.target.value })} />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Endereço</Label>
                    <Input value={adminCreate.address} onChange={(e) => setAdminCreate({ ...adminCreate, address: e.target.value })} />
                  </div>
                  <div className="md:col-span-3 flex justify-end">
                    <Button onClick={createAdminUser} disabled={adminLoading}>Criar Usuário</Button>
                  </div>
                </div>

                {/* Lista */}
                <div className="space-y-2">
                  {adminUsers.map((u) => (
                    <div key={u.username} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                      <div>
                        <div className="font-medium">{u.username} <span className="text-xs text-slate-500">({u.role})</span></div>
                        <div className="text-slate-600 text-sm">{u.full_name || ''} {u.is_active ? '• ativo' : '• inativo'}</div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEditAdminUser(u)}>Editar</Button>
                        <Button size="sm" variant="outline" onClick={() => resetAdminPassword(u.username)}>Reset Senha</Button>
                        <Button size="sm" variant="outline" onClick={() => toggleAdminActive(u)}>{u.is_active ? 'Desativar' : 'Ativar'}</Button>
                      </div>
                    </div>
                  ))}
                  {adminUsers.length === 0 && <p className="text-center text-slate-500 py-6">Nenhum usuário</p>}
                </div>

                {/* Editar (modal) */}
                {/* O editor inline foi removido para evitar conflitos e usamos modal abaixo */}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Create User Dialog */}
      <Dialog open={adminCreateOpen} onOpenChange={setAdminCreateOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Criar Novo Usuário</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Username</Label>
                <Input value={adminCreate.username} onChange={(e) => setAdminCreate({ ...adminCreate, username: e.target.value })} />
              </div>
              <div>
                <Label>Senha</Label>
                <Input type="password" value={adminCreate.password} onChange={(e) => setAdminCreate({ ...adminCreate, password: e.target.value })} />
              </div>
              <div>
                <Label>Role</Label>
                <select className="border rounded px-2 py-2 w-full" value={adminCreate.role} onChange={(e) => setAdminCreate({ ...adminCreate, role: e.target.value })}>
                  <option value="user">PM (User)</option>
                  <option value="admin">Admin</option>
                  <option value="general_manager">General Manager</option>
                  <option value="daily_leader">Daily Leader</option>
                </select>
              </div>
              <div>
                <Label>Primeiro Nome</Label>
                <Input value={adminCreate.first_name} onChange={(e) => setAdminCreate({ ...adminCreate, first_name: e.target.value })} />
              </div>
              <div>
                <Label>Sobrenome</Label>
                <Input value={adminCreate.last_name} onChange={(e) => setAdminCreate({ ...adminCreate, last_name: e.target.value })} />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input value={adminCreate.phone} onChange={(e) => setAdminCreate({ ...adminCreate, phone: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Endereço</Label>
                <Input value={adminCreate.address} onChange={(e) => setAdminCreate({ ...adminCreate, address: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAdminCreateOpen(false)}>Cancelar</Button>
              <Button onClick={createAdminUser} disabled={adminLoading}>Criar Usuário</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    {/* Admin Edit User Dialog */}
      <Dialog open={adminEditOpen} onOpenChange={setAdminEditOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Usuário {adminEdit?.username || ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Primeiro Nome</Label>
                <Input value={adminEditData.first_name} onChange={(e) => setAdminEditData({ ...adminEditData, first_name: e.target.value })} />
              </div>
              <div>
                <Label>Sobrenome</Label>
                <Input value={adminEditData.last_name} onChange={(e) => setAdminEditData({ ...adminEditData, last_name: e.target.value })} />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input value={adminEditData.phone} onChange={(e) => setAdminEditData({ ...adminEditData, phone: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Endereço</Label>
                <Input value={adminEditData.address} onChange={(e) => setAdminEditData({ ...adminEditData, address: e.target.value })} />
              </div>
              <div>
                <Label>Role</Label>
                <select className="border rounded px-2 py-2 w-full" value={adminEditData.role} onChange={(e) => setAdminEditData({ ...adminEditData, role: e.target.value })}>
                  <option value="user">PM (User)</option>
                  <option value="admin">Admin</option>
                  <option value="general_manager">General Manager</option>
                  <option value="daily_leader">Daily Leader</option>
                </select>
              </div>
              <div>
                <Label>Status</Label>
                <select className="border rounded px-2 py-2 w-full" value={adminEditData.is_active ? 'ativo' : 'inativo'} onChange={(e) => setAdminEditData({ ...adminEditData, is_active: e.target.value === 'ativo' })}>
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAdminEditOpen(false)}>Cancelar</Button>
              <Button onClick={async () => { await saveEditAdminUser(); setAdminEditOpen(false); }} disabled={adminLoading}>Salvar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Enviar Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-lg labor-bill-email-modal">
          <DialogHeader className="relative">
            <DialogTitle>Enviar e-mail</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Selecione um ou mais destinatários:</p>
            <div className="max-h-64 overflow-auto border rounded">
              {emailRecipients.length === 0 && (
                <div className="p-4 text-center text-slate-500">Nenhum destinatário cadastrado</div>
              )}
              {emailRecipients.map((r) => (
                <label key={r.id} className="flex items-start gap-2 px-3 py-2 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedRecipients.includes(r.id)}
                    onChange={() => toggleRecipientSelection(r.id)}
                    className="rounded flex-shrink-0 mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm leading-tight">{r.name}</div>
                    <div className="text-slate-600 text-xs leading-tight mt-0.5">{r.email}</div>
                  </div>
                  {r.admin_locked && <span className="text-slate-400 flex-shrink-0 text-xs mt-1" title="Criado pelo Admin">🔒</span>}
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancelar</Button>
              <Button
                onClick={async () => {
                  if (!currentBill?.id) return toast.error('Salve o rascunho primeiro');
                  if (selectedRecipients.length === 0) return toast.error('Selecione ao menos um destinatário');
                  try {
                    setLoading(true);
                    await axios.post(`${API}/send-email`, {
                      labor_bill_id: currentBill.id,
                      recipient_ids: selectedRecipients,
                      app_origin: publicOrigin(),
                    });

      {/* User Trash Confirm Dialog (rendered for non-admin users globally) */}
      {user?.role !== 'admin' && (
        <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirmar exclusão permanente</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">Esta ação não pode ser desfeita. Digite DELETE para confirmar.</p>
            <div className="mt-3">
              <Input
                placeholder="Digite DELETE"
                value={confirmDeleteText}
                onChange={(e) => setConfirmDeleteText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && confirmDeleteText.trim().toUpperCase() === 'DELETE') {
                    confirmHardDelete();
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setConfirmDeleteOpen(false)} disabled={confirmDeleting}>Cancelar</Button>
              <Button className="bg-red-600 hover:bg-red-700" disabled={confirmDeleteText.trim().toUpperCase() !== 'DELETE' || confirmDeleting} onClick={confirmHardDelete}>
                {confirmDeleting ? 'Apagando...' : 'Apagar'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

                    toast.success('E-mail enviado');
                    setShowEmailDialog(false);
                    await loadLaborBills();
                    await loadHistory();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Falha ao enviar e-mail');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Enviar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: confirmar supervisor ao salvar Labor Bill */}
      <Dialog open={showSaveSupervisorModal} onOpenChange={setShowSaveSupervisorModal}>
        <DialogContent className="sm:max-w-md save-supervisor-modal" data-testid="save-supervisor-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-amber-500">⚠️</span>
              {saveSupervisorMode === 'detected' && supervisorCtx?.detected?.supervisor
                ? 'Supervisor detectado'
                : 'Nenhum supervisor detectado'}
            </DialogTitle>
          </DialogHeader>

          {saveSupervisorMode === 'detected' && supervisorCtx?.detected?.supervisor ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div className="text-sm text-slate-600">Supervisor detectado na Logística:</div>
                <div className="font-semibold text-slate-900 text-base mt-0.5" data-testid="detected-supervisor-name">
                  👤 {supervisorCtx.detected.supervisor}
                </div>
                {supervisorCtx.detected.fom_assigned && (
                  <div className="text-xs text-slate-600 mt-1">FOM: {supervisorCtx.detected.fom_assigned}</div>
                )}
                {supervisorCtx.detected.fom_email ? (
                  <div className="text-xs text-slate-600">✉️ {supervisorCtx.detected.fom_email}</div>
                ) : (
                  <div className="text-xs text-red-500 mt-1">⚠️ Sem e-mail configurado para este supervisor</div>
                )}
              </div>
              <p className="text-sm text-slate-700">Deseja salvar com este supervisor?</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  className="bg-green-600 hover:bg-green-700 flex-1 whitespace-normal h-auto py-2 min-w-0 text-sm leading-tight text-center"
                  data-testid="save-supervisor-confirm-detected-btn"
                  disabled={loading}
                  onClick={() => confirmSaveWithSupervisor(supervisorCtx.detected.supervisor)}
                >
                  Sim, salvar com {supervisorCtx.detected.supervisor}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 whitespace-normal h-auto py-2 min-w-0 text-sm leading-tight text-center"
                  data-testid="save-supervisor-choose-other-btn"
                  disabled={loading}
                  onClick={() => { setSaveSupervisorMode('choose'); setSaveSupervisorChoice(''); }}
                >
                  Não, escolher outro
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {saveSupervisorMode !== 'detected' && !supervisorCtx?.detected?.supervisor && (
                <p className="text-sm text-red-600 font-medium">
                  Nenhum supervisor detectado no trabalho. Escolha um supervisor antes de salvar.
                </p>
              )}
              <div>
                <Label className="text-sm">Escolha o supervisor que deseja salvar</Label>
                <select
                  className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                  data-testid="save-supervisor-select"
                  value={saveSupervisorChoice}
                  onChange={(e) => setSaveSupervisorChoice(e.target.value)}
                >
                  <option value="">— Selecione —</option>
                  {(supervisorCtx.supervisors || []).map((s) => (
                    <option key={s.supervisor} value={s.supervisor}>
                      {s.supervisor}{s.fom_email ? ` (${s.fom_email})` : ' (sem e-mail)'}
                    </option>
                  ))}
                </select>
                {(supervisorCtx.supervisors || []).length === 0 && (
                  <div className="text-xs text-amber-600 mt-1">
                    Nenhum supervisor mapeado. Configure o mapeamento FOM na sub-aba "📄 Labor Bill".
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  className="bg-green-600 hover:bg-green-700 flex-1 whitespace-normal h-auto py-2 min-w-0 text-sm leading-tight text-center"
                  data-testid="save-supervisor-confirm-choice-btn"
                  disabled={loading || !saveSupervisorChoice}
                  onClick={() => confirmSaveWithSupervisor(saveSupervisorChoice)}
                >
                  Salvar com este supervisor
                </Button>
                <Button variant="outline" onClick={() => setShowSaveSupervisorModal(false)} disabled={loading}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: enviar e-mail para o supervisor salvo? */}
      <Dialog open={showEmailSupervisorModal} onOpenChange={setShowEmailSupervisorModal}>
        <DialogContent className="sm:max-w-md email-supervisor-modal" data-testid="email-supervisor-modal">
          <DialogHeader>
            <DialogTitle>Enviar Labor Bill por e-mail</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <div className="text-sm text-slate-600">Supervisor salvo:</div>
              <div className="font-semibold text-slate-900 mt-0.5" data-testid="email-supervisor-name">
                👤 {emailSupervisor.name || '—'}
              </div>
              <div className="text-sm text-slate-700 mt-0.5" data-testid="email-supervisor-email">✉️ {emailSupervisor.email}</div>
            </div>
            <p className="text-sm text-slate-700">Deseja enviar para o supervisor salvo?</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                className="bg-green-600 hover:bg-green-700 flex-1 whitespace-normal h-auto py-2 min-w-0 text-sm leading-tight text-center"
                data-testid="email-supervisor-send-btn"
                disabled={loading}
                onClick={sendToSavedSupervisor}
              >
                Sim, enviar para o supervisor
              </Button>
              <Button
                variant="outline"
                className="flex-1 whitespace-normal h-auto py-2 min-w-0 text-sm leading-tight text-center"
                data-testid="email-supervisor-choose-btn"
                disabled={loading}
                onClick={() => {
                  setShowEmailSupervisorModal(false);
                  if (emailRecipients.length === 0) { toast.error('Cadastre destinatários primeiro'); return; }
                  setSelectedRecipients([]);
                  setShowEmailDialog(true);
                }}
              >
                Não, escolher destinatários
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* View Labor Bill Modal */}
      <Dialog open={viewBillModalOpen} onOpenChange={setViewBillModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" aria-describedby="ticket-modal-description">
          <DialogHeader>
            <DialogTitle>Visualizar Labor Bill</DialogTitle>
          </DialogHeader>
          {viewBillData && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-600">Job Name</Label>
                  <div className="text-sm">{viewBillData.job_name || '-'}</div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Order #</Label>
                  <div className="text-sm">{viewBillData.order_number || '-'}</div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Install Date</Label>
                  <div className="text-sm">{formatDate(viewBillData.install_date) || '-'}</div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">PM</Label>
                  <div className="text-sm">{viewBillData.pm_user || '-'}</div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Total Windows</Label>
                  <div className="text-sm">{viewBillData.total_windows || 0}</div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Total Doors</Label>
                  <div className="text-sm">{viewBillData.total_doors || 0}</div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Status</Label>
                  <div className="text-sm">
                    <Badge variant={viewBillData.status === 'sent' ? 'default' : 'secondary'}>
                      {viewBillData.status === 'sent' ? 'Enviado' : 'Rascunho'}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Grand Total</Label>
                  <div className="text-sm font-semibold">
                    ${(viewBillData.grand_total || 0).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Items Table */}
              {viewBillData.items && viewBillData.items.length > 0 && (
                <div>
                  <h4 className="font-semibold text-md mb-3">Itens Principais</h4>
                  <div className="overflow-x-auto border rounded">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-3 font-medium">Descrição</th>
                          <th className="text-left p-3 font-medium">Qtd</th>
                          <th className="text-left p-3 font-medium">Preço Unit.</th>
                          <th className="text-left p-3 font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewBillData.items.map((item, index) => (
                          <tr key={index} className="border-t">
                            <td className="p-3">{item.description}</td>
                            <td className="p-3">{item.qty}</td>
                            <td className="p-3">${item.unit_price?.toFixed(2) || '0.00'}</td>
                            <td className="p-3 font-medium">${item.total?.toFixed(2) || '0.00'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Extra Items Table */}
              {viewBillData.extra_items && viewBillData.extra_items.length > 0 && (
                <div>
                  <h4 className="font-semibold text-md mb-3">Itens Extras</h4>
                  <div className="overflow-x-auto border rounded">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-3 font-medium">Descrição</th>
                          <th className="text-left p-3 font-medium">Qtd</th>
                          <th className="text-left p-3 font-medium">Preço Unit.</th>
                          <th className="text-left p-3 font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewBillData.extra_items.map((item, index) => (
                          <tr key={index} className="border-t">
                            <td className="p-3">{item.description}</td>
                            <td className="p-3">{item.qty}</td>
                            <td className="p-3">${item.unit_price?.toFixed(2) || '0.00'}</td>
                            <td className="p-3 font-medium">${item.total?.toFixed(2) || '0.00'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Notes */}
              {viewBillData.notes && (
                <div>
                  <h4 className="font-semibold text-md mb-3">Observações</h4>
                  <div className="bg-gray-50 border rounded p-3">
                    <p className="text-sm whitespace-pre-wrap">{viewBillData.notes}</p>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setViewBillModalOpen(false)}>
              Fechar
            </Button>
            {viewBillData && (
              <>
                <Button variant="outline" onClick={() => downloadPDF(viewBillData.id)}>
                  <Download className="h-4 w-4 mr-1" /> Download PDF
                </Button>
                <Button onClick={() => {
                  setViewBillModalOpen(false);
                  loadLaborBill(viewBillData.id);
                }}>
                  Editar
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Item Modal */}
      <Dialog open={addItemModalOpen} onOpenChange={setAddItemModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {addItemType === 'regular' ? 'Adicionar Item Principal' : 'Adicionar Item Extra'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {addItemType === 'regular' ? (
              <div>
                <h4 className="font-medium mb-3">Selecione um item predefinido:</h4>
                <div className="max-h-96 overflow-y-auto border rounded">
                  {leftColumnSubgroups.concat(rightColumnSubgroups).map((subgroup, groupIndex) => (
                    <div key={groupIndex} className="p-3 border-b">
                      <h5 className="font-medium text-sm text-gray-600 mb-2">{subgroup.title}</h5>
                      <div className="space-y-1">
                        {subgroup.items.map((item, itemIndex) => (
                          <button
                            key={itemIndex}
                            onClick={() => {
                              addNewImportItem(item);
                              setAddItemModalOpen(false);
                            }}
                            className="w-full text-left p-2 rounded hover:bg-gray-50 flex justify-between items-center"
                          >
                            <span className="text-sm">{item.description}</span>
                            <span className="text-sm text-gray-500">${item.unit_price}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  
                  {/* Outro option */}
                  <div className="p-3">
                    <button
                      onClick={() => {
                        addNewImportExtraItem();
                        setAddItemModalOpen(false);
                      }}
                      className="w-full text-left p-2 rounded hover:bg-gray-50 text-green-600 font-medium"
                    >
                      + Outro (Extra Labor)
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <h4 className="font-medium mb-3">Adicionar Item Extra:</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Item extra será adicionado à seção "Extra Labor" com valores personalizáveis.
                </p>
                <Button
                  onClick={() => {
                    addNewImportExtraItem();
                    setAddItemModalOpen(false);
                  }}
                  className="w-full"
                >
                  Adicionar Item Extra
                </Button>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setAddItemModalOpen(false)}>
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Ticket View Modal */}
      <Dialog open={!!selectedTicket} onOpenChange={(open) => !open && setSelectedTicket(null)}>
        <DialogContent 
          className="max-w-4xl admin-ticket-modal" 
          style={{ 
            position: 'fixed',
            top: '45%',
            transform: 'translateX(-50%) translateY(-50%)',
            maxHeight: '85vh',
            overflowY: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
          aria-describedby="ticket-modal-description"
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>🎫 Visualizar Ticket</DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4 flex-1 overflow-y-auto" style={{ 
              WebkitOverflowScrolling: 'touch',
              overflowY: 'auto'
            }}>
              {/* Ticket Header */}
              <div className="border-b pb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-semibold">{selectedTicket.title}</h3>
                  <div className="flex items-center gap-2">
                    <Badge variant={
                      selectedTicket.status === 'open' ? 'default' :
                      selectedTicket.status === 'pending' ? 'secondary' :
                      selectedTicket.status === 'resolved' ? 'outline' :
                      'secondary'
                    }>
                      {selectedTicket.status === 'open' ? '🔴 Aberto' :
                       selectedTicket.status === 'pending' ? '🟡 Pendente' :
                       selectedTicket.status === 'resolved' ? '✅ Resolvido' :
                       selectedTicket.status}
                    </Badge>
                    <Select
                      value={selectedTicket.status}
                      onValueChange={(newStatus) => updateTicketStatus(selectedTicket.id, newStatus)}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">🔴 Aberto</SelectItem>
                        <SelectItem value="pending">🟡 Pendente</SelectItem>
                        <SelectItem value="resolved">✅ Resolvido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  <strong>Usuário:</strong> {selectedTicket.pm_user} | 
                  <strong> Criado:</strong> {formatNyDateTime(selectedTicket.created_at)} | 
                  <strong> ID:</strong> {selectedTicket.id}
                </div>
              </div>

              {/* Messages */}
              <div className="space-y-4 max-h-96 overflow-y-auto">
                <h4 className="font-semibold">💬 Histórico de Mensagens</h4>
                {selectedTicket.messages?.map((msg, index) => (
                  <div key={index} className={`p-4 rounded-lg border ${
                    msg.role === 'admin' ? 'bg-blue-50 border-blue-200 border-l-4 border-l-blue-500' : 'bg-gray-50 border-gray-200 border-l-4 border-l-gray-400'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium flex items-center gap-2">
                        {msg.role === 'admin' ? (
                          <span className="text-blue-700">👨‍💼 Admin ({msg.author})</span>
                        ) : (
                          <span className="text-gray-700">👤 {msg.author}</span>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">
                        {formatNyDateTime(msg.timestamp)}
                      </span>
                    </div>
                    <div className="text-gray-700 whitespace-pre-wrap mb-2">{msg.text}</div>
                    {msg.attachments?.length > 0 && (
                      <div className="mt-2">
                        <div className="text-sm font-medium text-gray-600 mb-1">📎 Anexos:</div>
                        <div className="space-y-1">
                          {msg.attachments.map((att, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <button
                                onClick={async () => {
                                  try {
                                    const token = sessionStorage.getItem('access_token');
                                    const response = await fetch(`${API}/tickets/attachments/${att.id}`, {
                                      headers: {
                                        'Authorization': `Bearer ${token}`
                                      }
                                    });
                                    
                                    if (!response.ok) {
                                      throw new Error(`HTTP ${response.status}`);
                                    }
                                    
                                    const blob = await response.blob();
                                    const url = URL.createObjectURL(blob);
                                    
                                    // Verificar tipo de arquivo e abrir apropriadamente
                                    if (att.filename.toLowerCase().endsWith('.pdf')) {
                                      // Abrir PDF em modal
                                      setPdfModalData({ url: url, title: att.filename });
                                      setShowPDFModal(true);
                                    } else if (/\.(jpg|jpeg|png|heic|heif)$/i.test(att.filename)) {
                                      // Abrir imagem em modal
                                      setImageModalData({ url: url, title: att.filename });
                                      setShowImageModal(true);
                                    } else {
                                      // Outros arquivos - abrir em modal PDF como fallback
                                      setPdfModalData({ url: url, title: att.filename });
                                      setShowPDFModal(true);
                                    }
                                    
                                  } catch (error) {
                                    console.error('Error opening attachment:', error);
                                    toast.error(language === 'pt-BR' ? 'Erro ao abrir anexo' : 'Error opening attachment');
                                  }
                                }}
                                className="text-blue-600 hover:text-blue-700 underline cursor-pointer"
                              >
                                📄 {att.filename}
                              </button>
                              <Button
                                onClick={async () => {
                                  try {
                                    const token = sessionStorage.getItem('access_token');
                                    const response = await fetch(`${API}/tickets/attachments/${att.id}`, {
                                      headers: {
                                        'Authorization': `Bearer ${token}`
                                      }
                                    });
                                    
                                    if (!response.ok) {
                                      throw new Error(`HTTP ${response.status}`);
                                    }
                                    
                                    const blob = await response.blob();
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = att.filename;
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                    URL.revokeObjectURL(url);
                                    
                                  } catch (error) {
                                    console.error('Error downloading attachment:', error);
                                    toast.error(language === 'pt-BR' ? 'Erro ao baixar anexo' : 'Error downloading attachment');
                                  }
                                }}
                                variant="outline"
                                size="sm"
                                className="text-green-600 hover:text-green-700 h-6 px-2 text-xs"
                              >
                                💾 {language === 'pt-BR' ? 'Baixar' : 'Download'}
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Admin Reply Form */}
              {selectedTicket.status !== 'resolved' && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">💬 Responder como Admin</h4>
                  <div className="space-y-3">
                    <Textarea
                      placeholder="Digite sua resposta..."
                      value={ticketReply}
                      onChange={(e) => setTicketReply(e.target.value)}
                      rows={4}
                      className="mobile-textarea input-focus-scroll"
                      style={{ fontSize: '16px' }}
                    />
                    <div>
                      <input
                        type="file"
                        multiple
                        onChange={(e) => setTicketFiles(Array.from(e.target.files || []))}
                        className="mb-2"
                      />
                      {ticketFiles.length > 0 && (
                        <div className="text-sm text-gray-600">
                          {ticketFiles.length} arquivo(s) selecionado(s)
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={async () => {
                          if (!ticketReply.trim()) {
                            toast.error('Mensagem é obrigatória');
                            return;
                          }
                          
                          try {
                            const formData = new FormData();
                            formData.append('text', ticketReply);
                            ticketFiles.forEach(file => formData.append('files', file));
                            
                            await axios.post(`${API}/admin/tickets/${selectedTicket.id}/messages`, formData, {
                              headers: {
                                'Authorization': axios.defaults.headers.common['Authorization'],
                                'Content-Type': 'multipart/form-data'
                              }
                            });
                            
                            toast.success('Resposta enviada com sucesso!');
                            setTicketReply('');
                            setTicketFiles([]);
                            
                            // Reload tickets and update selected ticket
                            await loadAdminTickets();
                            const updatedTicket = adminTickets.find(t => t.id === selectedTicket.id);
                            if (updatedTicket) setSelectedTicket(updatedTicket);
                          } catch (e) {
                            toast.error('Erro ao enviar resposta: ' + (e.response?.data?.detail || e.message));
                          }
                        }}
                        disabled={!ticketReply.trim()}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        📤 Enviar Resposta
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setSelectedTicket(null)}
                      >
                        ✖️ Fechar
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Support Ticket Modal */}
      <Dialog open={supportNewOpen} onOpenChange={(open) => {
        setSupportNewOpen(open);
        // Reset campos quando modal fechar
        if (!open) {
          setSupportNewTitle('');
          setSupportNewText('');
          setSupportNewFiles([]);
        }
      }}>
        <DialogContent className="max-w-2xl keyboard-aware-scroll">
          <DialogHeader>
            <DialogTitle>
              🎫 {language === 'pt-BR' ? 'Novo Ticket de Suporte' : 'New Support Ticket'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mobile-modal-content">
            <div>
              <Label>{language === 'pt-BR' ? 'Título do Ticket' : 'Ticket Title'}</Label>
              <Input
                placeholder={language === 'pt-BR' ? 'Descreva brevemente o problema...' : 'Briefly describe the issue...'}
                value={supportNewTitle}
                onChange={(e) => setSupportNewTitle(e.target.value)}
                className="mobile-input"
              />
            </div>
            <div>
              <Label>{language === 'pt-BR' ? 'Descrição Detalhada' : 'Detailed Description'}</Label>
              <Textarea
                placeholder={language === 'pt-BR' ? 'Descreva seu problema ou dúvida em detalhes...' : 'Describe your problem or question in detail...'}
                value={supportNewText}
                onChange={(e) => setSupportNewText(e.target.value)}
                rows={6}
                className="mobile-textarea"
              />
            </div>
            <div>
              <Label>{language === 'pt-BR' ? 'Prioridade' : 'Priority'}</Label>
              <select
                value={supportNewPriority}
                onChange={(e) => setSupportNewPriority(e.target.value)}
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">🟢 {language === 'pt-BR' ? 'Baixa' : 'Low'} - {language === 'pt-BR' ? 'Dúvidas gerais, sugestões' : 'General questions, suggestions'}</option>
                <option value="medium">🟡 {language === 'pt-BR' ? 'Média' : 'Medium'} - {language === 'pt-BR' ? 'Problemas que não impedem o trabalho' : 'Issues that do not block work'}</option>
                <option value="high">🟠 {language === 'pt-BR' ? 'Alta' : 'High'} - {language === 'pt-BR' ? 'Problemas que atrapalham o trabalho' : 'Issues that hinder work'}</option>
                <option value="urgent">🔴 {language === 'pt-BR' ? 'Urgente' : 'Urgent'} - {language === 'pt-BR' ? 'Sistema não funciona, bloqueio total' : 'System not working, total blockage'}</option>
              </select>
            </div>
            <div>
              <Label>{language === 'pt-BR' ? 'Anexos (opcional)' : 'Attachments (optional)'}</Label>
              <input
                type="file"
                multiple
                onChange={(e) => setSupportNewFiles(Array.from(e.target.files || []))}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 mobile-input"
              />
              {supportNewFiles.length > 0 && (
                <div className="mt-2 text-sm text-gray-600">
                  {supportNewFiles.length} {language === 'pt-BR' ? 'arquivo(s) selecionado(s)' : 'file(s) selected'}:
                  <ul className="list-disc list-inside mt-1">
                    {supportNewFiles.map((file, index) => (
                      <li key={index}>{file.name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6 mobile-buttons">
            <Button variant="outline" onClick={() => {
              setSupportNewOpen(false);
              setSupportNewTitle('');
              setSupportNewText('');
              setSupportNewPriority('medium');
              setSupportNewFiles([]);
            }} className="mobile-btn">
              {t('cancel')}
            </Button>
            <Button 
              onClick={async () => {
                if (!supportNewTitle.trim()) {
                  toast.error(language === 'pt-BR' ? 'Título é obrigatório' : 'Title is required');
                  return;
                }
                
                try {
                  const formData = new FormData();
                  formData.append('title', supportNewTitle);
                  formData.append('text', supportNewText || '');
                  formData.append('priority', supportNewPriority);
                  supportNewFiles.forEach(file => formData.append('files', file));
                  
                  await axios.post(`${API}/tickets`, formData, {
                    headers: {
                      'Authorization': axios.defaults.headers.common['Authorization'],
                      'Content-Type': 'multipart/form-data'
                    }
                  });
                  
                  toast.success(language === 'pt-BR' ? 'Ticket criado com sucesso!' : 'Ticket created successfully!');
                  setSupportNewOpen(false);
                  setSupportNewTitle('');
                  setSupportNewText('');
                  setSupportNewPriority('medium');
                  setSupportNewFiles([]);
                  loadMyTickets();
                } catch (e) {
                  toast.error(language === 'pt-BR' ? 'Erro ao criar ticket' : 'Error creating ticket' + ': ' + (e.response?.data?.detail || e.message));
                }
              }}
              disabled={!supportNewTitle.trim()}
              className="bg-green-600 hover:bg-green-700 mobile-btn"
            >
              🎫 {language === 'pt-BR' ? 'Criar Ticket' : 'Create Ticket'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save Conflict Modal */}
      <Dialog open={conflictModalOpen} onOpenChange={setConflictModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>⚠️ Relatório Duplicado Encontrado</DialogTitle>
          </DialogHeader>
          
          {conflictData && (
            <div className="space-y-4">
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-semibold mb-3 text-yellow-800">Já existe um relatório para esta data!</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <strong>Data da Semana:</strong> {formatDate(conflictData.existing_report.week_ending) || 'Data não disponível'}
                  </div>
                  <div>
                    <strong>Relatório Existente:</strong> {conflictData.existing_report.entries_count} entradas
                  </div>
                  <div>
                    <strong>Novo Relatório:</strong> {conflictData.new_entries_count} entradas
                  </div>
                  <div>
                    <strong>Criado em:</strong> {formatDate(conflictData.existing_report.created_at) || 'Data não disponível'}
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold">Como deseja proceder?</h4>
                
                <div className="space-y-2">
                  <Button 
                    onClick={() => {
                      setConflictModalOpen(false);
                      saveReportOnly('overwrite');
                    }}
                    className="w-full justify-start bg-red-600 hover:bg-red-700 text-white"
                    size="lg"
                  >
                    🔄 Sobrescrever Relatório Antigo
                    <span className="text-red-200 text-sm ml-2">
                      (Remove o relatório existente e salva o novo)
                    </span>
                  </Button>
                  
                  <Button 
                    onClick={() => {
                      setConflictModalOpen(false);
                      saveReportOnly('merge');
                    }}
                    className="w-full justify-start bg-blue-600 hover:bg-blue-700 text-white"
                    size="lg"
                  >
                    ➕ Adicionar Mudanças
                    <span className="text-blue-200 text-sm ml-2">
                      (Combina com o relatório existente, evitando duplicatas)
                    </span>
                  </Button>
                  
                  <Button 
                    onClick={() => setConflictModalOpen(false)}
                    variant="outline"
                    className="w-full"
                    size="lg"
                  >
                    ❌ Cancelar
                  </Button>
                </div>
              </div>
              
              <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
                <strong>💡 Dica:</strong> A regra do sistema garante que nunca haverá dois clientes com o mesmo nome, mesma data e mesma equipe (PM).
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Manual Match Modal - Nova implementação simples */}
      <Dialog open={showManualMatchModal} onOpenChange={setShowManualMatchModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>🔧 Selecionar Labor Bill para Match Manual</DialogTitle>
          </DialogHeader>
          
          {currentUnmatchedEntry && (
            <div className="space-y-4">
              {/* Work Entry Info */}
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-semibold mb-3 text-red-800">❌ Cliente não encontrado automaticamente:</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div><strong>Job #:</strong> {currentUnmatchedEntry.job_number}</div>
                  <div><strong>{language === 'pt-BR' ? 'Data:' : 'Date:'}</strong> {currentUnmatchedEntry.date}</div>
                  <div><strong>Customer:</strong> {currentUnmatchedEntry.customer}</div>
                  <div><strong>Labor:</strong> ${(currentUnmatchedEntry.labor_total || 0).toFixed(2)}</div>
                  <div><strong>Team:</strong> 
                    <span className="px-2 py-1 ml-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                      {currentUnmatchedEntry.team_name || 'UNKNOWN'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Team Labor Bills */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-green-800">
                    ✅ Labor Bills disponíveis da equipe {currentUnmatchedEntry.team_name}:
                  </h4>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={showAllLaborBillsInMatch}
                        onChange={(e) => setShowAllLaborBillsInMatch(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-gray-700">
                        📅 Incluir labor bills anteriores
                      </span>
                    </label>
                  </div>
                </div>
                
                {(() => {
                  // Aplicar filtro de data aos labor bills usando período configurável
                  const { recent, old } = separateLaborBillsByDate(teamLaborBills, parseInt(laborBillPeriod));
                  const billsToDisplay = showAllLaborBillsInMatch ? [...recent, ...old] : recent;
                  
                  return (
                    <div className="space-y-3">
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-800">
                          📊 Mostrando: <strong>{billsToDisplay.length}</strong> labor bills
                          {showAllLaborBillsInMatch ? 
                            ` (${recent.length} recentes + ${old.length} anteriores)` : 
                            ` (${getPeriodName(laborBillPeriod, language).toLowerCase()})`
                          }
                        </p>
                      </div>
                      
                      {billsToDisplay.length > 0 ? (
                        <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="text-left p-3 font-medium">Job Name</th>
                                <th className="text-left p-3 font-medium">Order #</th>
                                <th className="text-left p-3 font-medium">Install Date</th>
                                <th className="text-left p-3 font-medium">Total</th>
                                {showAllLaborBillsInMatch && <th className="text-left p-3 font-medium">Período</th>}
                                <th className="text-left p-3 font-medium">Ação</th>
                              </tr>
                            </thead>
                            <tbody>
                              {billsToDisplay.map((bill) => {
                                const isRecent = recent.some(r => r.id === bill.id);
                                
                                return (
                                  <tr key={bill.id} className={`hover:bg-gray-50 border-t ${!isRecent && showAllLaborBillsInMatch ? 'bg-amber-50' : ''}`}>
                                    <td className="p-3 max-w-[200px] truncate" title={bill.job_name}>
                                      {bill.job_name}
                                    </td>
                                    <td className="p-3 font-mono">{bill.order_number}</td>
                                    <td className="p-3">{formatDate(bill.install_date)}</td>
                                    <td className="p-3 font-semibold">${(bill.grand_total || 0).toFixed(2)}</td>
                                    {showAllLaborBillsInMatch && (
                                      <td className="p-3">
                                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                          isRecent ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'
                                        }`}>
                                          {isRecent ? '📝 Recente' : '📅 Anterior'}
                                        </span>
                                      </td>
                                    )}
                                    <td className="p-3">
                                      <Button
                                        size="sm"
                                        onClick={() => handleManualMatch(bill.id)}
                                        disabled={manualMatchLoading}
                                        className="bg-green-600 hover:bg-green-700 text-white"
                                      >
                                        {manualMatchLoading ? 'Processando...' : '✅ Selecionar'}
                                      </Button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <p>Nenhum labor bill {showAllLaborBillsInMatch ? 'encontrado' : 'dos últimos 2 meses encontrado'}</p>
                          {!showAllLaborBillsInMatch && old.length > 0 && (
                            <p className="text-sm mt-2">
                              💡 Existe(m) {old.length} labor bill(s) anterior(es). Marque "Incluir anteriores" para vê-los.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Labor Bill Details Modal */}
      <Dialog open={showLaborBillDetails} onOpenChange={setShowLaborBillDetails}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle>📋 Detalhes do Labor Bill Encontrado</DialogTitle>
              <Button 
                variant="outline" 
                size="sm"
                className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                onClick={async () => {
                  if (selectedLaborBillDetails?.workEntry?.team_name) {
                    await loadAvailableLaborBillsForChange(selectedLaborBillDetails.workEntry.team_name);
                    setShowChangeLaborBillModal(true);
                  }
                }}
                disabled={changeLaborBillLoading}
              >
                {changeLaborBillLoading ? '🔄 Carregando...' : '🔄 Trocar Labor Bill'}
              </Button>
            </div>
          </DialogHeader>
          
          {selectedLaborBillDetails && (
            <div className="space-y-6">
              {/* Work Entry Info */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Work Entry Associada:</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div><strong>Job #:</strong> {selectedLaborBillDetails.workEntry.job_number}</div>
                  <div><strong>{language === 'pt-BR' ? 'Data:' : 'Date:'}</strong> {selectedLaborBillDetails.workEntry.date}</div>
                  <div><strong>Customer:</strong> {selectedLaborBillDetails.workEntry.customer}</div>
                  <div><strong>Labor Total:</strong> ${selectedLaborBillDetails.workEntry.labor_total?.toFixed(2) || '0.00'}</div>
                  <div><strong>Team:</strong> {selectedLaborBillDetails.workEntry.team_name}</div>
                  <div><strong>Match Type:</strong> 
                    <span className={`px-2 py-1 rounded text-xs font-semibold ml-2 ${
                      selectedLaborBillDetails.workEntry.match_type === 'order_number' 
                        ? 'bg-blue-100 text-blue-700' 
                        : selectedLaborBillDetails.workEntry.match_type === 'manual'
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {selectedLaborBillDetails.workEntry.match_type === 'order_number' ? '🔢 Order #' : 
                       selectedLaborBillDetails.workEntry.match_type === 'manual' ? '🔧 Manual' : '📝 Nome+Data'}
                    </span>
                  </div>
                  <div><strong>Valor Final:</strong> 
                    <span className="text-green-700 font-bold">
                      ${selectedLaborBillDetails.workEntry.calculated_value?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Labor Bill Details */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Detalhes do Labor Bill:</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div><strong>Job Name:</strong> {selectedLaborBillDetails.laborBill.job_name}</div>
                  <div><strong>Order #:</strong> {selectedLaborBillDetails.laborBill.order_number}</div>
                  <div><strong>Install Date:</strong> {formatDate(selectedLaborBillDetails.laborBill.install_date)}</div>
                  <div><strong>PM:</strong> {selectedLaborBillDetails.laborBill.pm_user}</div>
                  <div><strong>Total Windows:</strong> {selectedLaborBillDetails.laborBill.total_windows}</div>
                  <div><strong>Total Doors:</strong> {selectedLaborBillDetails.laborBill.total_doors}</div>
                  <div><strong>Grand Total:</strong> 
                    <span className="font-bold text-green-600">
                      ${selectedLaborBillDetails.laborBill.grand_total?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div><strong>Status:</strong> 
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      selectedLaborBillDetails.laborBill.status === 'draft' 
                        ? 'bg-yellow-100 text-yellow-700'
                        : selectedLaborBillDetails.laborBill.status === 'sent'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {selectedLaborBillDetails.laborBill.status}
                    </span>
                  </div>
                </div>
                
                {selectedLaborBillDetails.laborBill.notes && (
                  <div className="mt-3">
                    <strong>Notes:</strong>
                    <div className="bg-white p-2 rounded border text-sm mt-1">
                      {selectedLaborBillDetails.laborBill.notes}
                    </div>
                  </div>
                )}
              </div>

              {/* Identified Expenses */}
              {selectedLaborBillDetails.workEntry.expenses && (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold">Despesas Identificadas:</h4>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingLaborBillExpenses(!editingLaborBillExpenses)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      {editingLaborBillExpenses ? 'Cancelar' : 'Editar Despesas'}
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(selectedLaborBillDetails.workEntry.expenses).map(([category, value]) => (
                      <div key={category} className="bg-white p-2 rounded border">
                        <div className="text-xs text-gray-600 mb-1">{category}</div>
                        {editingLaborBillExpenses ? (
                          <Input
                            type="number"
                            step="0.01"
                            defaultValue={value}
                            onChange={(e) => {
                              const newExpenses = {
                                ...selectedLaborBillDetails.workEntry.expenses,
                                [category]: parseFloat(e.target.value) || 0
                              };
                              setSelectedLaborBillDetails(prev => ({
                                ...prev,
                                workEntry: {
                                  ...prev.workEntry,
                                  expenses: newExpenses
                                }
                              }));
                            }}
                            className="h-8 text-sm"
                          />
                        ) : (
                          <div className="font-semibold">${value?.toFixed(2) || '0.00'}</div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {editingLaborBillExpenses && (
                    <div className="mt-4 flex gap-2">
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            await updateWorkEntryExpenses(
                              selectedLaborBillDetails.workEntry.id, 
                              selectedLaborBillDetails.workEntry.expenses
                            );
                            setEditingLaborBillExpenses(false);
                          } catch (e) {
                            console.error('Error updating expenses:', e);
                          }
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        💾 Salvar Despesas
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingLaborBillExpenses(false);
                          // Reset expenses to original values
                          if (selectedLaborBillDetails.workEntry.expenses) {
                            // Reload from current state
                            setSelectedLaborBillDetails(prev => ({...prev}));
                          }
                        }}
                      >
                        ❌ Cancelar
                      </Button>
                    </div>
                  )}
                  
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-sm">
                      <strong>Total Despesas:</strong> 
                      <span className="font-bold text-red-600 ml-2">
                        ${selectedLaborBillDetails.workEntry.expenses ? 
                          Object.values(selectedLaborBillDetails.workEntry.expenses).reduce((sum, val) => sum + val, 0).toFixed(2) : 
                          '0.00'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Labor Bill Items */}
              {selectedLaborBillDetails.laborBill.items && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-3">Itens do Labor Bill:</h4>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm border rounded">
                      <thead className="bg-white sticky top-0">
                        <tr>
                          <th className="border p-2 text-left">Descrição</th>
                          <th className="border p-2 text-left">Qty</th>
                          <th className="border p-2 text-left">Unit Price</th>
                          <th className="border p-2 text-left">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedLaborBillDetails.laborBill.items.map((item, idx) => (
                          <tr key={idx} className="hover:bg-white">
                            <td className="border p-2">{item.description}</td>
                            <td className="border p-2 text-center">{item.quantity}</td>
                            <td className="border p-2">${item.unit_price?.toFixed(2) || '0.00'}</td>
                            <td className="border p-2 font-semibold">${item.total?.toFixed(2) || '0.00'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setShowLaborBillDetails(false);
                    loadLaborBillFromPayroll(selectedLaborBillDetails.laborBill.id);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  ✏️ Editar Labor Bill
                </Button>
                
                {/* Team Division Button - only show for matched entries that aren't already shared */}
                {selectedLaborBillDetails.workEntry.found_match && !selectedLaborBillDetails.workEntry.is_shared_work && (
                  <Button
                    onClick={() => {
                      setShowLaborBillDetails(false);
                      openTeamDivisionModal(selectedLaborBillDetails.workEntry);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    👥 Dividir entre Equipes
                  </Button>
                )}
                
                <Button
                  variant="outline"
                  onClick={() => setShowLaborBillDetails(false)}
                >
                  ❌ Fechar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Team Division Modal */}
      <Dialog open={showTeamDivisionModal} onOpenChange={setShowTeamDivisionModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>👥 Dividir Trabalho entre Equipes</DialogTitle>
          </DialogHeader>
          
          {currentEntryForDivision && (
            <div className="space-y-6">
              {/* Work Entry Info */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Trabalho Selecionado:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><strong>Job #:</strong> {currentEntryForDivision.job_number}</div>
                  <div><strong>Customer:</strong> {currentEntryForDivision.customer}</div>
                  <div><strong>{language === 'pt-BR' ? 'Data:' : 'Date:'}</strong> {currentEntryForDivision.date}</div>
                  <div><strong>Valor Final:</strong> 
                    <span className="font-bold text-green-600 ml-2">
                      ${currentEntryForDivision.calculated_value?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Team Selection */}
              <div>
                <h4 className="font-semibold mb-3">Selecionar Equipes que Trabalharam Juntas:</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {availableTeams.map((teamName) => (
                    <div
                      key={teamName}
                      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        selectedTeamsForDivision.includes(teamName)
                          ? 'bg-green-100 border-green-500'
                          : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                      }`}
                      onClick={() => handleTeamSelectionForDivision(teamName)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{teamName}</span>
                        {selectedTeamsForDivision.includes(teamName) && (
                          <span className="text-green-600">✅</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-3 text-sm text-gray-600">
                  <strong>Equipes selecionadas:</strong> {selectedTeamsForDivision.length} de {availableTeams.length}
                </div>
              </div>

              {/* Division Preview */}
              {selectedTeamsForDivision.length >= 2 && (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">📊 Previsão da Divisão:</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <strong>Labor original (Excel):</strong> ${currentEntryForDivision.labor_total?.toFixed(2) || '0.00'}
                    </div>
                    <div>
                      <strong>Número de equipes:</strong> {selectedTeamsForDivision.length}
                    </div>
                    <div>
                      <strong>Labor por equipe:</strong> 
                      <span className="font-bold text-blue-600 ml-2">
                        ${((currentEntryForDivision.labor_total || 0) / selectedTeamsForDivision.length).toFixed(2)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 bg-white p-2 rounded mt-2">
                      <strong>Processo:</strong> Cada equipe receberá ${((currentEntryForDivision.labor_total || 0) / selectedTeamsForDivision.length).toFixed(2)} de Labor 
                      e despesas divididas proporcionalmente, depois será aplicado: 
                      <br/>(Labor dividido - Despesas divididas) × Percentagem da equipe + Despesas divididas = Valor final
                    </div>
                    <div className="mt-2">
                      <strong>Equipes:</strong> {selectedTeamsForDivision.join(', ')}
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={confirmTeamDivision}
                  disabled={selectedTeamsForDivision.length < 2 || teamDivisionLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {teamDivisionLoading ? 'Processando...' : '👥 Confirmar Divisão'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowTeamDivisionModal(false)}
                  disabled={teamDivisionLoading}
                >
                  ❌ Cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Labor Bill Modal */}
      <Dialog open={showChangeLaborBillModal} onOpenChange={setShowChangeLaborBillModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>🔄 Trocar Labor Bill</DialogTitle>
          </DialogHeader>
          
          {selectedLaborBillDetails && (
            <div className="space-y-6">
              {/* Current Match Info */}
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-semibold mb-3 text-red-800">Labor Bill Atual:</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div><strong>Cliente:</strong> {selectedLaborBillDetails.workEntry.customer}</div>
                  <div><strong>Job Name:</strong> {selectedLaborBillDetails.laborBill.job_name}</div>
                  <div><strong>Order #:</strong> {selectedLaborBillDetails.laborBill.order_number}</div>
                  <div><strong>Total:</strong> ${selectedLaborBillDetails.laborBill.grand_total?.toFixed(2) || '0.00'}</div>
                </div>
              </div>

              {/* Available Labor Bills */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-green-800">
                    🔍 Labor Bills Disponíveis para Team {selectedLaborBillDetails.workEntry.team_name}:
                  </h4>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={showAllLaborBillsInChange}
                        onChange={(e) => setShowAllLaborBillsInChange(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-gray-700">
                        📅 Incluir labor bills anteriores
                      </span>
                    </label>
                  </div>
                </div>
                
                {changeLaborBillLoading ? (
                  <div className="p-6 text-center text-gray-500">
                    <div className="text-2xl mb-2">🔄</div>
                    <div>Carregando Labor Bills...</div>
                  </div>
                ) : (
                  (() => {
                    // Aplicar filtro de data aos labor bills disponíveis para trocar usando período configurável
                    const { recent, old } = separateLaborBillsByDate(availableLaborBillsForChange, parseInt(laborBillPeriod));
                    const billsToDisplay = showAllLaborBillsInChange ? [...recent, ...old] : recent;
                    
                    return (
                      <div className="space-y-3">
                        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                          <p className="text-sm text-blue-800">
                            📊 Mostrando: <strong>{billsToDisplay.length}</strong> labor bills
                            {showAllLaborBillsInChange ? 
                              ` (${recent.length} recentes + ${old.length} anteriores)` : 
                              ` (${getPeriodName(laborBillPeriod, language).toLowerCase()})`
                            }
                          </p>
                        </div>
                        
                        {billsToDisplay.length > 0 ? (
                          <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="p-3 text-left border-b">Job Name</th>
                                  <th className="p-3 text-left border-b">Order #</th>
                                  <th className="p-3 text-left border-b">Install Date</th>
                                  <th className="p-3 text-left border-b">Total</th>
                                  {showAllLaborBillsInChange && <th className="p-3 text-left border-b">Período</th>}
                                  <th className="p-3 text-left border-b">Ação</th>
                                </tr>
                              </thead>
                              <tbody>
                                {billsToDisplay
                                  .filter(bill => bill.id !== selectedLaborBillDetails.workEntry.labor_bill_id) // Excluir o atual
                                  .map((bill) => {
                                  const isRecent = recent.some(r => r.id === bill.id);
                                  
                                  return (
                                    <tr key={bill.id} className={`hover:bg-gray-50 border-b ${!isRecent && showAllLaborBillsInChange ? 'bg-amber-50' : ''}`}>
                                      <td className="p-3 max-w-[200px] truncate" title={bill.job_name}>
                                        {bill.job_name || 'Sem nome'}
                                      </td>
                                      <td className="p-3">{bill.order_number || '-'}</td>
                                      <td className="p-3">{formatDate(bill.install_date)}</td>
                                      <td className="p-3 font-semibold">${(bill.grand_total || 0).toFixed(2)}</td>
                                      {showAllLaborBillsInChange && (
                                        <td className="p-3">
                                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                            isRecent ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'
                                          }`}>
                                            {isRecent ? '📝 Recente' : '📅 Anterior'}
                                          </span>
                                        </td>
                                      )}
                                      <td className="p-3">
                                        <Button
                                          size="sm"
                                          onClick={() => handleChangeLaborBill(bill.id)}
                                          disabled={changeLaborBillLoading}
                                          className="bg-green-600 hover:bg-green-700 text-white"
                                        >
                                          {changeLaborBillLoading ? 'Processando...' : '✅ Selecionar'}
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <p>Nenhum labor bill {showAllLaborBillsInChange ? 'disponível' : 'dos últimos 2 meses disponível'}</p>
                            {!showAllLaborBillsInChange && old.length > 0 && (
                              <p className="text-sm mt-2">
                                💡 Existe(m) {old.length} labor bill(s) anterior(es). Marque "Incluir anteriores" para vê-los.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}
              </div>
              
              {/* Footer */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowChangeLaborBillModal(false)}
                  disabled={changeLaborBillLoading}
                >
                  ❌ Cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Independent Contractor Form Modal */}
      <Dialog open={showContractorForm} onOpenChange={setShowContractorForm}>
        <DialogContent className="sm:max-w-2xl max-h-[95vh] overflow-y-auto mx-2 sm:mx-4">
          <DialogHeader className="sticky top-0 bg-white z-10 pb-3 sm:pb-4 border-b">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              📋 {language === 'pt-BR' ? 'Novo Independent Contractor' : 'New Independent Contractor'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 sm:space-y-6 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                <strong>📋 {language === 'pt-BR' ? 'Certificação de Contratante Independente' : 'Independent Contractor Certification'}</strong> - {language === 'pt-BR' ? 'Preencha os dados e assine digitalmente' : 'Fill in the data and sign digitally'}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-sm">{language === 'pt-BR' ? 'Nome Completo do Contractor' : 'Contractor Full Name'} *</Label>
                <Input 
                  value={contractorFormData.helper_name}
                  onChange={(e) => setContractorFormData({...contractorFormData, helper_name: e.target.value})}
                  placeholder={language === 'pt-BR' ? 'Ex: João Silva Santos' : 'Ex: John Smith'}
                  className="text-sm"
                />
              </div>
              <div>
                <Label className="text-sm">{language === 'pt-BR' ? 'Título/Posição' : 'Title/Position'} *</Label>
                <select 
                  className="w-full border rounded px-3 py-2 bg-white text-sm"
                  value={contractorFormData.title}
                  onChange={(e) => setContractorFormData({...contractorFormData, title: e.target.value})}
                >
                  <option value="Helper">Helper</option>
                  <option value="Líder">Líder</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-sm">{t('date')} *</Label>
                <Input 
                  type="date"
                  value={contractorFormData.date}
                  onChange={(e) => setContractorFormData({...contractorFormData, date: e.target.value})}
                  className="text-sm"
                />
              </div>
            </div>

            {/* Instruções */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs sm:text-sm text-yellow-800">
                <strong>📋 {language === 'pt-BR' ? 'Instruções' : 'Instructions'}:</strong>
              </p>
              <ul className="text-xs sm:text-sm text-yellow-700 mt-1 space-y-1">
                <li>• {language === 'pt-BR' ? 'Preencha o nome completo do contractor' : 'Fill in the contractor full name'}</li>
                <li>• {language === 'pt-BR' ? 'Selecione o título/posição (Líder ou Helper)' : 'Select title/position (Líder or Helper)'}</li>
                <li>• {language === 'pt-BR' ? 'Confirme a data do contrato' : 'Confirm the contract date'}</li>
                <li>• {language === 'pt-BR' ? 'O PDF será gerado automaticamente' : 'PDF will be generated automatically'}</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t bg-white sticky bottom-0">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowContractorForm(false);
                setContractorFormData({
                  helper_name: '',
                  title: 'Helper',
                  signature: '',
                  date: new Date().toISOString().split('T')[0]
                });
              }}
              className="w-full sm:w-auto text-sm"
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={createContractorFile}
              disabled={!contractorFormData.helper_name || !contractorFormData.date || contractorLoading}
              className="bg-green-600 hover:bg-green-700 w-full sm:w-auto text-sm"
            >
              {contractorLoading ? `⏳ ${t('saving')}` : `📋 ${language === 'pt-BR' ? 'Salvar Contrato' : 'Save Contract'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Arbitration File PDF Viewer Modal - Full Document */}
      <Dialog open={showArbitrationViewer} onOpenChange={closeArbitrationViewer}>
        <DialogContent className="sm:max-w-6xl max-h-[95vh] overflow-auto">
          <DialogHeader className="border-b pb-3">
            <DialogTitle className="flex items-center gap-2">
              📄 {language === 'pt-BR' ? 'Visualização' : 'Viewing'}: {arbitrationPdfTitle}
            </DialogTitle>
          </DialogHeader>
          
          <div className="h-[80vh] w-full bg-gray-50 rounded overflow-hidden pdf-modal-mobile-container">
            {arbitrationPdfUrl ? (
              // Detectar mobile/iPad e usar componente apropriado
              /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Safari/i.test(navigator.userAgent) || window.innerWidth <= 1024 ? (
                <MobilePDFViewer 
                  pdfUrl={arbitrationPdfUrl}
                  onError={(error) => {
                    console.error('Error in MobilePDFViewer:', error);
                    toast.error('Erro ao carregar PDF');
                  }}
                />
              ) : (
                <iframe
                  src={arbitrationPdfUrl}
                  className="w-full h-full border-0 rounded bg-white"
                  title={`PDF - ${arbitrationPdfTitle}`}
                  loading="eager"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  allow="fullscreen"
                />
              )
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-4xl mb-4">⏳</div>
                  <div className="text-gray-600 mb-2">{language === 'pt-BR' ? 'Carregando PDF...' : 'Loading PDF...'}</div>
                  <div className="text-xs text-gray-400">{language === 'pt-BR' ? 'Aguarde enquanto o arquivo é processado' : 'Please wait while the file is processed'}</div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center gap-3 mt-4 pt-3 border-t">
            <div className="text-xs text-gray-500">
              📄 PDF: {arbitrationPdfUrl ? (language === 'pt-BR' ? 'Carregado ✅' : 'Loaded ✅') : (language === 'pt-BR' ? 'Carregando...' : 'Loading...')}
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={closeArbitrationViewer}
              >
                {language === 'pt-BR' ? 'Fechar' : 'Close'}
              </Button>
              {arbitrationPdfUrl && (
                <Button 
                  onClick={() => {
                    // Download the same PDF being viewed
                    const link = document.createElement('a');
                    link.href = arbitrationPdfUrl;
                    link.download = `arbitration_${arbitrationPdfTitle.replace(/\s+/g, '_')}.pdf`;
                    link.click();
                    toast.success(language === 'pt-BR' ? 'Download iniciado' : 'Download started');
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('download')}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Report Modal */}
      <Dialog open={showEditReportModal} onOpenChange={setShowEditReportModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              ✏️ {language === 'pt-BR' ? 'Editar Relatórios de Payroll' : 'Edit Payroll Reports'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Report Selection */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-3">
                📊 {language === 'pt-BR' ? 'Selecionar Relatório' : 'Select Report'}
              </h4>
              <select 
                className="w-full border rounded px-3 py-2 bg-white text-sm"
                value={editReportData.selectedReport?.id || ''}
                onChange={(e) => {
                  const report = payrollReports.find(r => r.id === e.target.value);
                  setEditReportData(prev => ({...prev, selectedReport: report, selectedEntry: null}));
                }}
              >
                <option value="">
                  {language === 'pt-BR' ? 'Selecione um relatório...' : 'Select a report...'}
                </option>
                {payrollReports.map((report) => (
                  <option key={report.id} value={report.id}>
                    {language === 'pt-BR' ? 'Semana terminada em' : 'Week ended'}: {formatDate(report.week_ending)} - {report.total_entries} {language === 'pt-BR' ? 'entradas' : 'entries'}
                  </option>
                ))}
              </select>
            </div>

            {/* Entries List */}
            {editReportData.selectedReport && (
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="bg-gray-50 border-b p-3">
                  <h4 className="font-semibold text-gray-800">
                    📋 {language === 'pt-BR' ? 'Entradas do Relatório' : 'Report Entries'}
                  </h4>
                </div>
                <div className="max-h-64 overflow-auto">
                  {editReportData.selectedReport.entries?.filter(entry => entry.found_match).map((entry, index) => (
                    <div 
                      key={entry.id || index} 
                      className={`p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                        editReportData.selectedEntry?.id === entry.id ? 'bg-blue-50 border-blue-200' : ''
                      }`}
                      onClick={() => selectEntryForEdit(entry)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-sm">{entry.customer}</div>
                          <div className="text-xs text-gray-600">
                            Job: {entry.job_number} | {language === 'pt-BR' ? 'Equipe' : 'Team'}: {entry.team_name} | Labor: ${(entry.labor_total || 0).toFixed(2)}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          ${(entry.calculated_value || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Edit Form */}
            {editReportData.selectedEntry && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-3">
                  ✏️ {language === 'pt-BR' ? 'Editar Entrada' : 'Edit Entry'}
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-sm">{language === 'pt-BR' ? 'Nome do Cliente' : 'Customer Name'}</Label>
                    <Input 
                      value={editReportData.editedValues.customer}
                      onChange={(e) => setEditReportData(prev => ({
                        ...prev,
                        editedValues: {...prev.editedValues, customer: e.target.value}
                      }))}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Job Number</Label>
                    <Input 
                      value={editReportData.editedValues.job_number}
                      onChange={(e) => setEditReportData(prev => ({
                        ...prev,
                        editedValues: {...prev.editedValues, job_number: e.target.value}
                      }))}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Labor Total</Label>
                    <Input 
                      type="number"
                      step="0.01"
                      value={editReportData.editedValues.labor_total}
                      onChange={(e) => {
                        const newLaborTotal = parseFloat(e.target.value) || 0;
                        const newFinalValue = recalculateFinalValue(newLaborTotal, editReportData.editedValues.expenses);
                        setEditReportData(prev => ({
                          ...prev,
                          editedValues: {
                            ...prev.editedValues, 
                            labor_total: newLaborTotal,
                            calculated_value: newFinalValue
                          }
                        }));
                      }}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{language === 'pt-BR' ? 'Valor Final (Calculado)' : 'Final Value (Calculated)'}</Label>
                    <Input 
                      type="number"
                      step="0.01"
                      value={editReportData.editedValues.calculated_value}
                      className="text-sm bg-gray-100"
                      disabled
                      title={language === 'pt-BR' ? 'Calculado automaticamente baseado na fórmula' : 'Automatically calculated based on formula'}
                    />
                  </div>
                </div>

                {/* Expenses Section */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                  <h5 className="font-medium text-orange-800 mb-3">
                    💰 {language === 'pt-BR' ? 'Despesas do Trabalho' : 'Job Expenses'}
                  </h5>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(editReportData.editedValues.expenses || {}).map(([expenseType, amount]) => (
                      <div key={expenseType} className="relative">
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-xs">{expenseType}</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newExpenses = {...editReportData.editedValues.expenses};
                              delete newExpenses[expenseType];
                              setEditReportData(prev => ({
                                ...prev,
                                editedValues: {...prev.editedValues, expenses: newExpenses}
                              }));
                            }}
                            className="text-red-600 hover:text-red-700 h-4 w-4 p-0"
                            title={language === 'pt-BR' ? 'Remover despesa' : 'Remove expense'}
                          >
                            <Trash2 className="h-2 w-2" />
                          </Button>
                        </div>
                        <Input 
                          type="number"
                          step="0.01"
                          value={amount || 0}
                          onChange={(e) => {
                            const newExpenses = {...editReportData.editedValues.expenses};
                            newExpenses[expenseType] = parseFloat(e.target.value) || 0;
                            const newFinalValue = recalculateFinalValue(editReportData.editedValues.labor_total, newExpenses);
                            setEditReportData(prev => ({
                              ...prev,
                              editedValues: {
                                ...prev.editedValues, 
                                expenses: newExpenses,
                                calculated_value: newFinalValue
                              }
                            }));
                          }}
                          className="text-xs"
                        />
                      </div>
                    ))}
                    
                    {/* Add new expense type */}
                    <div className="sm:col-span-2 md:col-span-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const expenseName = prompt(
                            language === 'pt-BR' ? 'Nome da nova despesa:' : 'New expense name:'
                          );
                          if (expenseName && expenseName.trim()) {
                            const newExpenses = {...editReportData.editedValues.expenses};
                            newExpenses[expenseName.trim()] = 0;
                            setEditReportData(prev => ({
                              ...prev,
                              editedValues: {...prev.editedValues, expenses: newExpenses}
                            }));
                          }
                        }}
                        className="text-xs"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        {language === 'pt-BR' ? 'Adicionar Despesa' : 'Add Expense'}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="mt-3 p-3 bg-white rounded border text-xs">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <strong>{language === 'pt-BR' ? 'Total de Despesas' : 'Total Expenses'}:</strong> 
                        <span className="ml-2 font-bold text-orange-600">
                          ${Object.values(editReportData.editedValues.expenses || {}).reduce((sum, val) => sum + (parseFloat(val) || 0), 0).toFixed(2)}
                        </span>
                      </div>
                      <div>
                        <strong>{language === 'pt-BR' ? 'Valor Final Calculado' : 'Calculated Final Value'}:</strong>
                        <span className="ml-2 font-bold text-green-600">
                          ${editReportData.editedValues.calculated_value.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-600">
                      <strong>{language === 'pt-BR' ? 'Fórmula' : 'Formula'}:</strong> (Labor - {language === 'pt-BR' ? 'Despesas' : 'Expenses'} - 20%) + {language === 'pt-BR' ? 'Despesas' : 'Expenses'} = {language === 'pt-BR' ? 'Valor Final' : 'Final Value'}
                    </div>
                  </div>
                </div>

                <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-blue-700">
                  <strong>{language === 'pt-BR' ? 'Nota' : 'Note'}:</strong> {language === 'pt-BR' ? 'As mudanças serão refletidas nos Weekly Reports dos PMs automaticamente' : 'Changes will be reflected in PM Weekly Reports automatically'}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEditReportModal(false);
                setEditReportData({
                  selectedReport: null,
                  selectedEntry: null,
                  editedValues: {
                    customer: '',
                    job_number: '',
                    labor_total: 0,
                    expenses: {},
                    calculated_value: 0
                  }
                });
              }}
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={updateReportEntry}
              disabled={!editReportData.selectedEntry || editReportLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {editReportLoading ? `⏳ ${t('saving')}` : `✏️ ${language === 'pt-BR' ? 'Atualizar Entrada' : 'Update Entry'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Client Management Modal */}
      <Dialog open={showManualClientModal} onOpenChange={setShowManualClientModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {manualClientAction === 'add' ? '➕ Adicionar Cliente à Equipe' : '➖ Remover Cliente da Equipe'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Action Selection */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-3">🎯 Ação</h4>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="clientAction" 
                    checked={manualClientAction === 'add'}
                    onChange={() => setManualClientAction('add')}
                    className="text-blue-600"
                  />
                  <span className="text-sm">➕ Adicionar cliente à equipe</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="clientAction" 
                    checked={manualClientAction === 'remove'}
                    onChange={() => setManualClientAction('remove')}
                    className="text-red-600"
                  />
                  <span className="text-sm">➖ Remover cliente da equipe</span>
                </label>
              </div>
            </div>

            {manualClientAction === 'add' ? (
              <>
                {/* Add Client Section */}
                <div className="space-y-4">
                  {/* Search and Filter */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h5 className="font-medium text-gray-800 mb-3">🔍 Buscar Labor Bills</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm">Buscar por nome/job</Label>
                        <Input 
                          value={clientSearch}
                          onChange={(e) => setClientSearch(e.target.value)}
                          placeholder="Digite nome do job ou cliente..."
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Filtrar por equipe</Label>
                        <select 
                          className="w-full border rounded px-3 py-2 bg-white text-sm"
                          value={clientFilterTeam}
                          onChange={(e) => setClientFilterTeam(e.target.value)}
                        >
                          <option value="ALL">Todas as equipes</option>
                          {pmOptions.map((pm) => (
                            <option key={pm} value={pm}>{pm}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Selecionar Labor Bill</Label>
                      <div className="mt-2 max-h-48 overflow-auto border rounded-lg bg-white">
                        {(() => {
                          const filteredBills = availableLaborBills.filter(bill => {
                            const matchesSearch = !clientSearch || 
                              (bill.job_name || '').toLowerCase().includes(clientSearch.toLowerCase()) ||
                              (bill.order_number || '').toLowerCase().includes(clientSearch.toLowerCase());
                            
                            const matchesTeam = clientFilterTeam === 'ALL' || bill.pm_user === clientFilterTeam;
                            
                            return matchesSearch && matchesTeam;
                          });

                          return filteredBills.length === 0 ? (
                            <div className="p-4 text-center text-gray-500">
                              <div className="text-2xl mb-2">🔍</div>
                              <div className="text-sm">Nenhum Labor Bill encontrado</div>
                              <div className="text-xs mt-1">Ajuste os filtros de busca</div>
                            </div>
                          ) : (
                            <div className="divide-y">
                              {filteredBills.map((bill) => (
                                <label 
                                  key={bill.id} 
                                  className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
                                >
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="radio"
                                      name="laborBillSelection"
                                      value={bill.id}
                                      checked={manualClientData.selectedLaborBill?.id === bill.id}
                                      onChange={() => {
                                        setManualClientData(prev => ({...prev, selectedLaborBill: bill}));
                                        calculateClientValues(bill);
                                      }}
                                      className="text-blue-600"
                                    />
                                    <div>
                                      <div className="font-medium text-sm">{bill.job_name || 'Sem nome'}</div>
                                      <div className="text-xs text-gray-600">
                                        Ordem: {bill.order_number} | {bill.pm_user} | ${(bill.grand_total || 0).toFixed(2)}
                                      </div>
                                    </div>
                                  </div>
                                  <Badge variant="outline" className="text-xs">
                                    {bill.pm_user}
                                  </Badge>
                                </label>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <Label>Equipe de Destino</Label>
                        <select 
                          className="w-full border rounded px-3 py-2 mt-2 bg-white text-sm"
                          value={manualClientData.selectedTeam}
                          onChange={(e) => setManualClientData(prev => ({...prev, selectedTeam: e.target.value}))}
                        >
                          {pmOptions.map((pm) => (
                            <option key={pm} value={pm}>{pm}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <Label>Week Ending Date</Label>
                        <Input 
                          type="date"
                          value={manualClientData.weekEnding}
                          onChange={(e) => setManualClientData(prev => ({...prev, weekEnding: e.target.value}))}
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Calculated Values Display */}
                  {manualClientData.selectedLaborBill && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-medium text-green-800 mb-3">📊 Valores Calculados</h5>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <div><strong>Labor Bill:</strong> {manualClientData.selectedLaborBill.job_name}</div>
                          <div><strong>Ordem:</strong> {manualClientData.selectedLaborBill.order_number}</div>
                          <div><strong>PM Original:</strong> {manualClientData.selectedLaborBill.pm_user}</div>
                        </div>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Total Labor:</span>
                            <span className="font-medium">${manualClientData.laborTotal.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Despesas:</span>
                            <span className="font-medium">${manualClientData.expenses.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between border-t pt-2">
                            <span className="font-semibold">Valor Final:</span>
                            <span className="font-bold text-green-700">${manualClientData.finalValue.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-blue-700">
                        <strong>Fórmula aplicada:</strong> (Total Labor - Despesas - 20%) + Despesas = Valor Final
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Remove Client Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Selecionar Relatório</Label>
                    <select 
                      className="w-full border rounded px-3 py-2 mt-2 bg-white text-sm"
                      value={manualClientData.selectedReport?.id || ''}
                      onChange={(e) => {
                        const report = payrollReports.find(r => r.id === e.target.value);
                        setManualClientData(prev => ({...prev, selectedReport: report}));
                      }}
                    >
                      <option value="">Selecione um relatório...</option>
                      {payrollReports.map((report) => (
                        <option key={report.id} value={report.id}>
                          {formatDate(report.week_ending)} - {report.total_entries} clientes
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <Label>Cliente a Remover</Label>
                    <select 
                      className="w-full border rounded px-3 py-2 mt-2 bg-white text-sm"
                      value={manualClientData.selectedLaborBill?.labor_bill_id || ''}
                      onChange={(e) => {
                        const entry = manualClientData.selectedReport?.entries?.find(entry => entry.labor_bill_id === e.target.value);
                        console.log('🔍 Selected entry for removal:', entry);
                        setManualClientData(prev => ({...prev, selectedLaborBill: entry}));
                      }}
                      disabled={!manualClientData.selectedReport}
                    >
                      <option value="">Selecione um cliente...</option>
                      {manualClientData.selectedReport?.entries?.filter(entry => entry.found_match).map((entry) => (
                        <option key={entry.labor_bill_id || entry.id} value={entry.labor_bill_id}>
                          {entry.customer} - {entry.team_name} - ${(entry.labor_total || 0).toFixed(2)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {manualClientData.selectedLaborBill && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <h5 className="font-medium text-red-800 mb-2">📄 Cliente a ser Removido:</h5>
                    <div className="text-sm text-red-700">
                      <div><strong>Cliente:</strong> {manualClientData.selectedLaborBill.customer}</div>
                      <div><strong>Job:</strong> {manualClientData.selectedLaborBill.job_number}</div>
                      <div><strong>Equipe Atual:</strong> {manualClientData.selectedLaborBill.team_name}</div>
                      <div><strong>Valor:</strong> ${(manualClientData.selectedLaborBill.labor_total || 0).toFixed(2)}</div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Instructions */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>💡 Instruções:</strong>
              </p>
              <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                {manualClientAction === 'add' ? (
                  <>
                    <li>• Selecione um Labor Bill que não foi incluído no payroll</li>
                    <li>• Escolha a equipe que deve receber o pagamento</li>
                    <li>• Defina a data da semana e valor labor</li>
                    <li>• O cliente será adicionado ao Weekly Report da equipe</li>
                  </>
                ) : (
                  <>
                    <li>• Selecione um relatório de payroll existente</li>
                    <li>• Escolha o cliente que foi incluído incorretamente</li>
                    <li>• O cliente será removido do relatório e Weekly Report</li>
                    <li>• Os cálculos serão atualizados automaticamente</li>
                  </>
                )}
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowManualClientModal(false);
                setManualClientData({
                  selectedReport: null,
                  selectedTeam: 'PM1',
                  selectedLaborBill: null,
                  laborTotal: 0,
                  weekEnding: ''
                });
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={manualClientAction === 'add' ? addClientToPayrollTeam : removeClientFromPayrollTeam}
              disabled={
                manualClientAction === 'add' 
                  ? !manualClientData.selectedLaborBill || !manualClientData.weekEnding || !manualClientData.selectedTeam || manualClientLoading
                  : !manualClientData.selectedReport || !manualClientData.selectedLaborBill || manualClientLoading
              }
              className={manualClientAction === 'add' ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
            >
              {manualClientLoading ? '⏳ Processando...' : (
                manualClientAction === 'add' ? '➕ Adicionar Cliente' : '➖ Remover Cliente'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Arbitration File Name Modal */}
      <Dialog open={showArbitrationEditModal} onOpenChange={setShowArbitrationEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              ✏️ {language === 'pt-BR' ? 'Editar Nome do Arquivo' : 'Edit File Name'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {arbitrationFileToEdit && (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="font-medium text-blue-800 mb-2">
                    📄 {language === 'pt-BR' ? 'Arquivo Atual' : 'Current File'}:
                  </h4>
                  <div className="text-sm text-blue-700">
                    <div><strong>PM:</strong> {arbitrationFileToEdit.pm_user}</div>
                    <div><strong>{language === 'pt-BR' ? 'Data' : 'Date'}:</strong> {new Date(arbitrationFileToEdit.date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}</div>
                    <div><strong>Status:</strong> {arbitrationFileToEdit.signature && arbitrationFileToEdit.signature.startsWith('data:image') ? 
                      (language === 'pt-BR' ? '✅ Assinado' : '✅ Signed') : 
                      (language === 'pt-BR' ? '❌ Não Assinado' : '❌ Unsigned')
                    }</div>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold">
                    {language === 'pt-BR' ? 'Nome do Helper' : 'Helper Name'} *
                  </Label>
                  <Input 
                    value={arbitrationFileToEdit.helper_name || ''}
                    onChange={(e) => setArbitrationFileToEdit(prev => ({
                      ...prev,
                      helper_name: e.target.value
                    }))}
                    placeholder={language === 'pt-BR' ? 'Digite o novo nome...' : 'Enter new name...'}
                    className="text-sm mt-2"
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>⚠️ {language === 'pt-BR' ? 'Atenção' : 'Warning'}:</strong> {language === 'pt-BR' ? 
                      'Alterar o nome afetará como o arquivo aparece na lista para todos os usuários.' :
                      'Changing the name will affect how the file appears in the list for all users.'
                    }
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowArbitrationEditModal(false);
                setArbitrationFileToEdit(null);
              }}
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={editArbitrationFileName}
              disabled={!arbitrationFileToEdit?.helper_name?.trim() || arbitrationLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {arbitrationLoading ? `⏳ ${t('saving')}` : `✏️ ${language === 'pt-BR' ? 'Salvar Nome' : 'Save Name'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contractor Import Modal - SEPARATE from Arbitration */}
      <Dialog open={showContractorImportModal} onOpenChange={setShowContractorImportModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              📁 {language === 'pt-BR' ? 'Importar Contratos' : 'Import Contracts'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                <strong>📤 {language === 'pt-BR' ? 'Importação de Contratos' : 'Contract Import'}</strong> - {language === 'pt-BR' ? 'Selecione vários arquivos PDF de contratos para importar' : 'Select multiple contract PDF files to import'}
              </p>
            </div>

            <div>
              <Label>{language === 'pt-BR' ? 'Selecionar Arquivos PDF' : 'Select PDF Files'}</Label>
              <Input 
                type="file"
                accept="application/pdf"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setContractorImportFiles(files);
                  console.log(`📁 Selected ${files.length} contractor files for import`);
                }}
                className="mt-2"
              />
              {contractorImportFiles.length > 0 && (
                <div className="mt-2 text-xs text-gray-600">
                  {contractorImportFiles.length} {language === 'pt-BR' ? 'arquivo(s) selecionado(s)' : 'file(s) selected'}
                </div>
              )}
            </div>

            {/* Files Preview */}
            {contractorImportFiles.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <h5 className="font-medium text-gray-800 mb-2">📋 {language === 'pt-BR' ? 'Arquivos Selecionados' : 'Selected Files'}:</h5>
                <div className="space-y-1 max-h-32 overflow-auto">
                  {contractorImportFiles.map((file) => (
                    <div key={`${file.name}-${file.size}-${file.lastModified}`} className="flex items-center justify-between text-sm">
                      <span className="truncate">{file.name}</span>
                      <span className="text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Import Results */}
            {contractorImportResults.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h5 className="font-medium text-blue-800 mb-2">📊 {language === 'pt-BR' ? 'Resultados da Importação' : 'Import Results'}:</h5>
                <div className="space-y-1 max-h-32 overflow-auto">
                  {contractorImportResults.map((result) => (
                    <div key={`${result.filename}-${result.success}-${Math.random()}`} className="flex items-center justify-between text-sm">
                      <span className="truncate">{result.filename}</span>
                      <span className={result.success ? 'text-green-600' : 'text-red-600'}>
                        {result.success ? '✅ Sucesso' : `❌ ${result.error}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>💡 {language === 'pt-BR' ? 'Instruções' : 'Instructions'}:</strong>
              </p>
              <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                <li>• {language === 'pt-BR' ? 'Selecione um ou mais arquivos PDF de contratos' : 'Select one or more contract PDF files'}</li>
                <li>• {language === 'pt-BR' ? 'Contratos importados ficarão sempre "Prontos"' : 'Imported contracts will always be "Ready"'}</li>
                <li>• {language === 'pt-BR' ? 'Todos ficarão visíveis para todos os PMs' : 'All will be visible to all PMs'}</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowContractorImportModal(false);
                setContractorImportFiles([]);
                setContractorImportResults([]);
              }}
            >
              {language === 'pt-BR' ? 'Fechar' : 'Close'}
            </Button>
            <Button 
              onClick={importContractorFiles}
              disabled={contractorImportFiles.length === 0 || contractorLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {contractorLoading ? `⏳ ${language === 'pt-BR' ? 'Importando...' : 'Importing...'}` : `📁 ${language === 'pt-BR' ? 'Importar' : 'Import'} ${contractorImportFiles.length} ${language === 'pt-BR' ? 'Arquivo(s)' : 'File(s)'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Arbitration Import Modal */}
      <Dialog open={showArbitrationImportModal} onOpenChange={setShowArbitrationImportModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              📁 Importar Arbitration Files
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-800">
                <strong>📤 Importação Múltipla</strong> - Selecione vários arquivos PDF de arbitragem para importar
              </p>
            </div>

            <div>
              <Label>Selecionar Arquivos PDF</Label>
              <Input 
                type="file"
                accept="application/pdf"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setArbitrationImportFiles(files);
                  console.log(`📁 Selected ${files.length} files for import`);
                }}
                className="mt-2"
              />
              {arbitrationImportFiles.length > 0 && (
                <div className="mt-2 text-xs text-gray-600">
                  {arbitrationImportFiles.length} arquivo(s) selecionado(s)
                </div>
              )}
            </div>

            {/* Files Preview */}
            {arbitrationImportFiles.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <h5 className="font-medium text-gray-800 mb-2">📋 Arquivos Selecionados:</h5>
                <div className="space-y-1 max-h-32 overflow-auto">
                  {arbitrationImportFiles.map((file) => (
                    <div key={`${file.name}-${file.size}-${file.lastModified}`} className="flex items-center justify-between text-sm">
                      <span className="truncate">{file.name}</span>
                      <span className="text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Import Results */}
            {arbitrationImportResults.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h5 className="font-medium text-blue-800 mb-2">📊 Resultados da Importação:</h5>
                <div className="space-y-1 max-h-32 overflow-auto">
                  {arbitrationImportResults.map((result) => (
                    <div key={`${result.filename}-${result.success}-${Math.random()}`} className="flex items-center justify-between text-sm">
                      <span className="truncate">{result.filename}</span>
                      <span className={result.success ? 'text-green-600' : 'text-red-600'}>
                        {result.success ? '✅ Sucesso' : `❌ ${result.error}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>💡 Instruções:</strong>
              </p>
              <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                <li>• Selecione um ou mais arquivos PDF de arbitragem</li>
                <li>• O sistema verificará automaticamente se têm assinatura</li>
                <li>• Arquivos sem assinatura terão botão "Assinar" disponível</li>
                <li>• Todos ficarão visíveis para todos os PMs</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowArbitrationImportModal(false);
                setArbitrationImportFiles([]);
                setArbitrationImportResults([]);
              }}
            >
              Fechar
            </Button>
            <Button 
              onClick={importArbitrationFiles}
              disabled={arbitrationImportFiles.length === 0 || arbitrationLoading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {arbitrationLoading ? '⏳ Importando...' : `📁 Importar ${arbitrationImportFiles.length} Arquivo(s)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Arbitration Sign Modal */}
      <Dialog open={showArbitrationSignModal} onOpenChange={setShowArbitrationSignModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              ✍️ Assinar Arbitration File
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {arbitrationFileToSign && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h4 className="font-medium text-blue-800 mb-2">📄 Arquivo a ser Assinado:</h4>
                <div className="text-sm text-blue-700">
                  <div><strong>Helper:</strong> {arbitrationFileToSign.helper_name}</div>
                  <div><strong>{language === 'pt-BR' ? 'Data:' : 'Date:'}</strong> {new Date(arbitrationFileToSign.date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}</div>
                  <div><strong>Criado por:</strong> {arbitrationFileToSign.pm_user}</div>
                </div>
              </div>
            )}

            {/* Assinatura Digital */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <Label className="text-base font-semibold">Assinatura Digital *</Label>
              <p className="text-sm text-purple-600 mb-3">
                ✍️ Assine com o dedo (touch) ou mouse no campo abaixo
              </p>
              
              <div className="bg-white border-2 border-purple-300 rounded-lg overflow-hidden">
                <SignatureCanvas 
                  ref={signaturePadRef}
                  canvasProps={{
                    width: 400,
                    height: 150,
                    className: 'signature-canvas w-full h-auto'
                  }}
                  backgroundColor="white"
                  penColor="#2563eb"
                  dotSize={2}
                  onEnd={onSignatureEnd}
                />
              </div>
              
              <div className="flex justify-between items-center mt-3">
                <Button 
                  type="button"
                  variant="outline" 
                  size="sm"
                  onClick={clearSignature}
                  className="bg-red-50 hover:bg-red-100"
                >
                  🧹 Limpar
                </Button>
                <div className="text-xs flex items-center gap-2">
                  {hasSignature ? (
                    <span className="text-green-600 font-medium">✅ Assinatura OK</span>
                  ) : (
                    <span className="text-purple-600">💡 Assine aqui</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowArbitrationSignModal(false);
                setArbitrationFileToSign(null);
                setHasSignature(false);
                if (signaturePadRef.current) {
                  signaturePadRef.current.clear();
                }
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={signArbitrationFile}
              disabled={!hasSignature || arbitrationLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {arbitrationLoading ? '⏳ Salvando...' : '✍️ Assinar Arquivo'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Number Validation Modal */}
      <Dialog open={showOrderValidationModal} onOpenChange={setShowOrderValidationModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              ⚠️ Order # Duplicado Encontrado
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <h4 className="font-medium text-red-800 mb-2">Order # já existe:</h4>
              <div className="text-sm text-red-700">
                <div><strong>Order #:</strong> {orderValidationData.duplicateOrderNumber}</div>
                {orderValidationData.existingBill && (
                  <>
                    <div><strong>Job:</strong> {orderValidationData.existingBill.job_name}</div>
                    <div><strong>PM:</strong> {orderValidationData.existingBill.pm_user}</div>
                    <div><strong>{language === 'pt-BR' ? 'Data:' : 'Date:'}</strong> {formatDate(orderValidationData.existingBill.install_date)}</div>
                    <div><strong>Status:</strong> {orderValidationData.existingBill.status}</div>
                    <div><strong>Total:</strong> ${(orderValidationData.existingBill.grand_total || 0).toFixed(2)}</div>
                  </>
                )}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h4 className="font-medium text-blue-800 mb-2">Este Labor Bill é um Service?</h4>
              <p className="text-sm text-blue-700 mb-3">
                <strong>Service:</strong> Quando precisamos voltar na casa do cliente para arrumar alguma coisa 
                e fazemos um novo Labor Bill com o mesmo número.
              </p>
              
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="serviceOption" 
                    checked={orderValidationData.isService === true}
                    onChange={() => setOrderValidationData(prev => ({...prev, isService: true}))}
                    className="text-blue-600"
                  />
                  <span className="text-sm">
                    <strong>Sim, é um Service</strong> - Adicionar 'S' ao Order # ({orderValidationData.duplicateOrderNumber}S)
                  </span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="serviceOption" 
                    checked={orderValidationData.isService === false}
                    onChange={() => setOrderValidationData(prev => ({...prev, isService: false}))}
                    className="text-red-600"
                  />
                  <span className="text-sm">
                    <strong>Não, não é um Service</strong> - Alterar o Order # manualmente
                  </span>
                </label>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Importante:</strong> Não é permitido ter dois Labor Bills com o mesmo Order #. 
                Escolha uma das opções acima para continuar.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowOrderValidationModal(false);
                setOrderValidationData({
                  duplicateOrderNumber: '',
                  existingBill: null,
                  isService: false,
                  pendingSave: false
                });
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={() => handleOrderValidationResponse(orderValidationData.isService)}
              className={orderValidationData.isService ? "bg-blue-600 hover:bg-blue-700" : "bg-red-600 hover:bg-red-700"}
            >
              {orderValidationData.isService ? '✅ Adicionar S' : '❌ Alterar Order #'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Labor Bill Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🔄 Transferir Labor Bill
            </DialogTitle>
          </DialogHeader>
          
          {transferBillData && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h4 className="font-medium text-blue-800 mb-2">Labor Bill a ser transferido:</h4>
                <div className="text-sm">
                  <div><strong>Job:</strong> {transferBillData.job_name || 'Sem nome'}</div>
                  <div><strong>Ordem:</strong> {transferBillData.order_number || 'N/A'}</div>
                  <div><strong>{language === 'pt-BR' ? 'Data:' : 'Date:'}</strong> {formatDate(transferBillData.install_date)}</div>
                  <div><strong>PM Atual:</strong> <span className="text-blue-600 font-medium">{transferBillData.pm_user}</span></div>
                  <div><strong>Total:</strong> ${(transferBillData.grand_total || 0).toFixed(2)}</div>
                </div>
              </div>

              <div>
                <Label className="font-semibold">Transferir para qual PM?</Label>
                <select 
                  className="w-full border rounded px-3 py-2 mt-2 bg-white" 
                  value={transferTargetPM} 
                  onChange={(e) => setTransferTargetPM(e.target.value)}
                >
                  {pmOptions.filter(pm => pm !== transferBillData.pm_user).map((pm) => (
                    <option key={pm} value={pm}>{pm}</option>
                  ))}
                </select>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Atenção:</strong> Esta ação irá transferir o Labor Bill de 
                  <strong> {transferBillData.pm_user}</strong> para <strong>{transferTargetPM}</strong>.
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  O PM de destino terá acesso total ao Labor Bill transferido.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowTransferModal(false);
                setTransferBillId(null);
                setTransferBillData(null);
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={transferLaborBill}
              disabled={!transferTargetPM || transferLoading}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {transferLoading ? '⏳ Transferindo...' : '🔄 Confirmar Transferência'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Send Arbitration Files Modal */}
      <Dialog open={showBulkEmailModal} onOpenChange={setShowBulkEmailModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              📧 {language === 'pt-BR' ? 'Enviar Múltiplos Arquivos' : 'Send Multiple Files'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Selected Files Preview */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-3">
                📋 {language === 'pt-BR' ? 'Arquivos Selecionados' : 'Selected Files'} ({selectedContractorFiles.length})
              </h4>
              <div className="max-h-32 overflow-auto space-y-2">
                {selectedContractorFiles.map((file) => (
                  <div key={file.id} className="flex items-center justify-between text-sm bg-white rounded p-2">
                    <div>
                      <div className="font-medium">{file.helper_name}</div>
                      <div className="text-xs text-gray-600">{file.pm_user} • {new Date(file.date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}</div>
                    </div>
                    <Badge className={file.signature && file.signature.startsWith('data:image') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {file.signature && file.signature.startsWith('data:image') ? '✅' : '❌'}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>

            {/* Recipient Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold">
                  {language === 'pt-BR' ? 'Selecionar Destinatários' : 'Select Recipients'} *
                </Label>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bulkEmailData.selectAll}
                      onChange={(e) => {
                        const selectAll = e.target.checked;
                        setBulkEmailData(prev => ({
                          ...prev,
                          selectAll,
                          selectedRecipients: selectAll ? emailRecipients : []
                        }));
                      }}
                      className="text-blue-600"
                    />
                    <span className="text-blue-600 font-medium">
                      {language === 'pt-BR' ? 'Selecionar Todos' : 'Select All'}
                    </span>
                  </label>
                  <Badge variant="secondary" className="text-xs">
                    {bulkEmailData.selectedRecipients.length} {language === 'pt-BR' ? 'selecionados' : 'selected'}
                  </Badge>
                </div>
              </div>
              
              <div className="max-h-48 overflow-auto border rounded-lg bg-white">
                {emailRecipients.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <div className="text-2xl mb-2">📧</div>
                    <div className="text-sm">{language === 'pt-BR' ? 'Nenhum destinatário cadastrado' : 'No recipients registered'}</div>
                    <div className="text-xs mt-1">{language === 'pt-BR' ? 'Cadastre destinatários na aba Configurações' : 'Register recipients in Settings tab'}</div>
                  </div>
                ) : (
                  <div className="divide-y">
                    {emailRecipients.map((recipient) => (
                      <label 
                        key={recipient.id} 
                        className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={bulkEmailData.selectedRecipients.some(r => r.id === recipient.id)}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setBulkEmailData(prev => {
                                let newSelected;
                                if (isChecked) {
                                  newSelected = [...prev.selectedRecipients, recipient];
                                } else {
                                  newSelected = prev.selectedRecipients.filter(r => r.id !== recipient.id);
                                }
                                return {
                                  ...prev,
                                  selectedRecipients: newSelected,
                                  selectAll: newSelected.length === emailRecipients.length
                                };
                              });
                            }}
                            className="text-blue-600"
                          />
                          <div>
                            <div className="font-medium text-sm">{recipient.name}</div>
                            <div className="text-xs text-gray-600">{recipient.email}</div>
                          </div>
                        </div>
                        {recipient.admin_locked && (
                          <span className="text-yellow-500 text-xs" title="Adicionado pelo Admin">🔒</span>
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Email Preview */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>📋 {language === 'pt-BR' ? 'O que será enviado' : 'What will be sent'}:</strong>
              </p>
              <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                <li>• {selectedArbitrationFiles.length} {language === 'pt-BR' ? 'PDFs em anexo' : 'PDFs attached'}</li>
                <li>• {language === 'pt-BR' ? 'Cada PDF com seu template completo' : 'Each PDF with complete template'}</li>
                <li>• {language === 'pt-BR' ? 'Email profissional para cada destinatário' : 'Professional email to each recipient'}</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowBulkEmailModal(false);
                setBulkEmailData({
                  selectedRecipients: [],
                  selectAll: false
                });
              }}
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={arbitrationSubTab === 'contractor' ? sendMultipleContractorFiles : sendMultipleArbitrationFiles}
              disabled={
                (arbitrationSubTab === 'contractor' ? 
                  (selectedContractorFiles.length === 0 || bulkEmailData.selectedRecipients.length === 0 || contractorLoading) :
                  (selectedArbitrationFiles.length === 0 || bulkEmailData.selectedRecipients.length === 0 || arbitrationLoading)
                )
              }
              className="bg-green-600 hover:bg-green-700"
            >
              {(arbitrationSubTab === 'contractor' ? contractorLoading : arbitrationLoading) ? 
                `⏳ ${language === 'pt-BR' ? 'Enviando...' : 'Sending...'}` : 
                `📧 ${language === 'pt-BR' ? 'Enviar' : 'Send'} ${
                  arbitrationSubTab === 'contractor' ? 
                    selectedContractorFiles.length : 
                    selectedArbitrationFiles.length
                } ${
                  arbitrationSubTab === 'contractor' ? 
                    'contrato' + (selectedContractorFiles.length > 1 ? 's' : '') :
                    'PDF' + (selectedArbitrationFiles.length > 1 ? 's' : '')
                }`
              }
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Contractor File Name Modal */}
      <Dialog open={showContractorEditModal} onOpenChange={setShowContractorEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              ✏️ {language === 'pt-BR' ? 'Editar Nome do Contrato' : 'Edit Contract Name'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {contractorFileToEdit && (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="font-medium text-blue-800 mb-2">
                    📄 {language === 'pt-BR' ? 'Contrato Atual' : 'Current Contract'}:
                  </h4>
                  <div className="text-sm text-blue-700">
                    <div><strong>PM:</strong> {contractorFileToEdit.pm_user}</div>
                    <div><strong>{language === 'pt-BR' ? 'Data' : 'Date'}:</strong> {new Date(contractorFileToEdit.date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}</div>
                    <div><strong>Tipo:</strong> {contractorFileToEdit.title || 'Helper'}</div>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold">
                    {language === 'pt-BR' ? 'Nome do Contractor' : 'Contractor Name'} *
                  </Label>
                  <Input 
                    value={contractorFileToEdit.helper_name || ''}
                    onChange={(e) => setContractorFileToEdit(prev => ({
                      ...prev,
                      helper_name: e.target.value
                    }))}
                    placeholder={language === 'pt-BR' ? 'Digite o novo nome...' : 'Enter new name...'}
                    className="text-sm mt-2"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {language === 'pt-BR' ? 
                      'Este nome aparecerá no contrato e será usado para identificação' :
                      'This name will appear on the contract and be used for identification'
                    }
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowContractorEditModal(false);
                setContractorFileToEdit(null);
              }}
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={editContractorFileNameFixed}
              disabled={!contractorFileToEdit?.helper_name?.trim() || contractorLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {contractorLoading ? `⏳ ${t('saving')}` : `✏️ ${language === 'pt-BR' ? 'Salvar Nome' : 'Save Name'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Arbitration Email Modal - Seleção Múltipla */}
      <Dialog open={showArbitrationEmailForm} onOpenChange={setShowArbitrationEmailForm}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              📧 {language === 'pt-BR' ? 'Enviar para Supervisores' : 'Send to Supervisors'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>📤 {language === 'pt-BR' ? 'Envio de Arbitration File' : 'Arbitration File Sending'}</strong> - {language === 'pt-BR' ? 'Selecione um ou mais destinatários' : 'Select one or more recipients'}
              </p>
            </div>

            {/* Multiple Selection from Contacts */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold">
                  {language === 'pt-BR' ? 'Selecionar Destinatários' : 'Select Recipients'} *
                </Label>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={arbitrationEmailData.selectAll}
                      onChange={(e) => {
                        const selectAll = e.target.checked;
                        setArbitrationEmailData(prev => ({
                          ...prev,
                          selectAll,
                          selectedRecipients: selectAll ? emailRecipients : []
                        }));
                      }}
                      className="text-blue-600"
                    />
                    <span className="text-blue-600 font-medium">
                      {language === 'pt-BR' ? 'Selecionar Todos' : 'Select All'}
                    </span>
                  </label>
                  <Badge variant="secondary" className="text-xs">
                    {arbitrationEmailData.selectedRecipients.length} {language === 'pt-BR' ? 'selecionados' : 'selected'}
                  </Badge>
                </div>
              </div>
              
              <div className="max-h-48 overflow-auto border rounded-lg bg-white">
                {emailRecipients.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <div className="text-2xl mb-2">📧</div>
                    <div className="text-sm">{language === 'pt-BR' ? 'Nenhum destinatário cadastrado' : 'No recipients registered'}</div>
                    <div className="text-xs mt-1">{language === 'pt-BR' ? 'Cadastre destinatários na aba Configurações' : 'Register recipients in Settings tab'}</div>
                  </div>
                ) : (
                  <div className="divide-y">
                    {emailRecipients.map((recipient) => (
                      <label 
                        key={recipient.id} 
                        className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={arbitrationEmailData.selectedRecipients.some(r => r.id === recipient.id)}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setArbitrationEmailData(prev => {
                                let newSelected;
                                if (isChecked) {
                                  newSelected = [...prev.selectedRecipients, recipient];
                                } else {
                                  newSelected = prev.selectedRecipients.filter(r => r.id !== recipient.id);
                                }
                                return {
                                  ...prev,
                                  selectedRecipients: newSelected,
                                  selectAll: newSelected.length === emailRecipients.length
                                };
                              });
                            }}
                            className="text-blue-600"
                          />
                          <div>
                            <div className="font-medium text-sm">{recipient.name}</div>
                            <div className="text-xs text-gray-600">{recipient.email}</div>
                          </div>
                        </div>
                        {recipient.admin_locked && (
                          <span className="text-yellow-500 text-xs" title="Adicionado pelo Admin">🔒</span>
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>📋 {language === 'pt-BR' ? 'O que será enviado' : 'What will be sent'}:</strong>
              </p>
              <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                <li>• {language === 'pt-BR' ? 'PDF do Arbitration File com seu template' : 'Arbitration File PDF with your template'}</li>
                <li>• {language === 'pt-BR' ? 'Dados do helper e assinatura digital' : 'Helper data and digital signature'}</li>
                <li>• {language === 'pt-BR' ? 'Email profissional da empresa' : 'Professional company email'}</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowArbitrationEmailForm(false);
                setArbitrationEmailData({ 
                  supervisor_email: '', 
                  supervisor_name: '',
                  selectedRecipients: [],
                  selectAll: false
                });
              }}
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={sendArbitrationToSupervisor}
              disabled={arbitrationEmailData.selectedRecipients.length === 0 || arbitrationLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {arbitrationLoading ? `⏳ ${language === 'pt-BR' ? 'Enviando...' : 'Sending...'}` : 
                `📧 ${language === 'pt-BR' ? 'Enviar Email' : 'Send Email'} (${arbitrationEmailData.selectedRecipients.length})`
              }
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Contractor Email Modal */}
      <Dialog open={showContractorEmailForm} onOpenChange={setShowContractorEmailForm}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              📧 {language === 'pt-BR' ? 'Enviar Contrato' : 'Send Contract'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>📤 {language === 'pt-BR' ? 'Envio de Independent Contractor' : 'Independent Contractor Sending'}</strong> - {language === 'pt-BR' ? 'Selecione um ou mais destinatários' : 'Select one or more recipients'}
              </p>
            </div>

            {/* Multiple Selection from Contacts */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold">
                  {language === 'pt-BR' ? 'Selecionar Destinatários' : 'Select Recipients'} *
                </Label>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={contractorEmailData.selectAll}
                      onChange={(e) => {
                        const selectAll = e.target.checked;
                        setContractorEmailData(prev => ({
                          ...prev,
                          selectAll,
                          selectedRecipients: selectAll ? emailRecipients : []
                        }));
                      }}
                      className="text-blue-600"
                    />
                    <span className="text-blue-600 font-medium">
                      {language === 'pt-BR' ? 'Selecionar Todos' : 'Select All'}
                    </span>
                  </label>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
                {emailRecipients.map((recipient) => (
                  <label key={recipient.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={contractorEmailData.selectedRecipients.some(r => r.id === recipient.id)}
                      onChange={(e) => {
                        setContractorEmailData(prev => ({
                          ...prev,
                          selectedRecipients: e.target.checked 
                            ? [...prev.selectedRecipients, recipient]
                            : prev.selectedRecipients.filter(r => r.id !== recipient.id),
                          selectAll: false
                        }));
                      }}
                      className="text-blue-600"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{recipient.name}</div>
                      <div className="text-xs text-gray-500">{recipient.email}</div>
                    </div>
                  </label>
                ))}
                {emailRecipients.length === 0 && (
                  <p className="text-center text-gray-500 py-3 text-sm">
                    {language === 'pt-BR' ? 'Nenhum destinatário encontrado' : 'No recipients found'}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => {
              setShowContractorEmailForm(false);
              setContractorEmailData({ 
                supervisor_email: '', 
                supervisor_name: '',
                selectedRecipients: [],
                selectAll: false
              });
            }}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={sendContractorToSupervisor}
              disabled={contractorEmailData.selectedRecipients.length === 0 || contractorLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {contractorLoading ? `⏳ ${language === 'pt-BR' ? 'Enviando...' : 'Sending...'}` : 
                `📧 ${language === 'pt-BR' ? 'Enviar Email' : 'Send Email'} (${contractorEmailData.selectedRecipients.length})`
              }
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Arbitration File Form Modal - Otimizado Mobile/Tablet */}
      <Dialog open={showArbitrationForm} onOpenChange={setShowArbitrationForm}>
        <DialogContent className="sm:max-w-2xl max-h-[95vh] overflow-y-auto mx-2 sm:mx-4">
          <DialogHeader className="sticky top-0 bg-white z-10 pb-3 sm:pb-4 border-b">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              ⚖️ {language === 'pt-BR' ? 'Novo Arbitration File Helper' : 'New Arbitration File Helper'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 sm:space-y-6 py-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>📋 Formulário Digital de Arbitragem</strong> - Preencha os dados e assine digitalmente
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="sm:col-span-2 sm:col-span-1">
                <Label className="text-sm">{language === 'pt-BR' ? 'Nome Completo do Helper' : 'Helper Full Name'} *</Label>
                <Input 
                  value={arbitrationFormData.helper_name}
                  onChange={(e) => setArbitrationFormData({...arbitrationFormData, helper_name: e.target.value})}
                  placeholder={language === 'pt-BR' ? 'Ex: João Silva Santos' : 'Ex: John Smith'}
                  className="text-sm"
                />
              </div>
              <div>
                <Label className="text-sm">{t('date')} *</Label>
                <Input 
                  type="date"
                  value={arbitrationFormData.date}
                  onChange={(e) => setArbitrationFormData({...arbitrationFormData, date: e.target.value})}
                  className="text-sm"
                />
              </div>
            </div>

            {/* Assinatura Digital com Canvas - Otimizado */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4">
              <Label className="text-sm sm:text-base font-semibold">{t('digitalSignature')} *</Label>
              <p className="text-xs sm:text-sm text-purple-600 mb-3 sm:mb-4">
                ✍️ {language === 'pt-BR' ? 'Assine com o dedo (touch) ou mouse no campo abaixo' : 'Sign with finger (touch) or mouse in the field below'}
              </p>
              
              <div className="bg-white border-2 border-purple-300 rounded-lg overflow-hidden shadow-sm">
                <SignatureCanvas 
                  ref={signaturePadRef}
                  canvasProps={{
                    width: window.innerWidth < 640 ? Math.min(window.innerWidth - 100, 400) : 500,
                    height: window.innerWidth < 640 ? 150 : 200,
                    className: 'signature-canvas w-full h-auto'
                  }}
                  backgroundColor="white"
                  penColor="#2563eb"
                  dotSize={2}
                  onEnd={onSignatureEnd}
                />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-3 mt-3">
                <Button 
                  type="button"
                  variant="outline" 
                  size="sm"
                  onClick={clearSignature}
                  className="bg-red-50 hover:bg-red-100 w-full sm:w-auto text-xs"
                >
                  🧹 {t('clearSignature')}
                </Button>
                <div className="text-xs flex items-center justify-center sm:justify-end gap-2">
                  {hasSignature ? (
                    <span className="text-green-600 font-medium">
                      ✅ {language === 'pt-BR' ? 'Assinatura OK' : 'Signature OK'}
                    </span>
                  ) : (
                    <span className="text-purple-600">
                      💡 {language === 'pt-BR' ? 'Assine de forma clara' : 'Sign clearly'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Instruções simplificadas - Mobile */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs sm:text-sm text-blue-800">
                <strong>📋 Instruções:</strong>
              </p>
              <ul className="text-xs sm:text-sm text-blue-700 mt-1 space-y-1">
                <li>• Preencha o nome completo do helper</li>
                <li>• Confirme a data do trabalho</li>
                <li>• Assine no campo de assinatura</li>
                <li>• PDF será gerado automaticamente</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t bg-white sticky bottom-0">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowArbitrationForm(false);
                if (signaturePadRef.current) {
                  signaturePadRef.current.clear();
                }
                setHasSignature(false);
                setArbitrationFormData({
                  helper_name: '',
                  signature: '',
                  date: new Date().toISOString().split('T')[0]
                });
              }}
              className="w-full sm:w-auto text-sm"
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={createArbitrationFile}
              disabled={!arbitrationFormData.helper_name || !arbitrationFormData.date || !hasSignature || arbitrationLoading}
              className="bg-green-600 hover:bg-green-700 w-full sm:w-auto text-sm"
            >
              {arbitrationLoading ? `⏳ ${t('saving')}` : `✍️ ${t('signAndSave')}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Team Leader Profile Modal - Otimizado Mobile/Tablet */}
      
      {/* Modal VER (Read-Only) - Visualização apenas */}
      <Dialog open={showTeamLeaderViewModal} onOpenChange={setShowTeamLeaderViewModal}>
        <DialogContent className="sm:max-w-4xl max-h-[95vh] overflow-auto mx-2 sm:mx-4">
          <DialogHeader className="border-b pb-3 sm:pb-4">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              👁️ Visualizar - {currentTeamLeader?.username || ''}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 p-2 sm:p-0">
            {/* Dados do Líder - Read Only */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <h4 className="font-semibold text-blue-800 mb-3 text-sm sm:text-base">
                📝 {language === 'pt-BR' ? 'Dados do Líder' : 'Leader Data'}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-600">{t('fullName')}</Label>
                  <div className="text-sm font-medium">{teamLeaderFormData.full_name || 'N/A'}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">{t('email')}</Label>
                  <div className="text-sm font-medium">{teamLeaderFormData.email || 'N/A'}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">{t('phone')}</Label>
                  <div className="text-sm font-medium">{teamLeaderFormData.phone || 'N/A'}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">{t('position')}</Label>
                  <div className="text-sm font-medium">{teamLeaderFormData.position || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Funcionários (Helpers) - Editável para PM, Read-Only para Admin */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-green-800 text-sm sm:text-base">
                  👷 {language === 'pt-BR' ? 'Funcionários da Equipe' : 'Team Workers'}
                </h4>
                {/* PM pode adicionar seus próprios helpers */}
                {user?.role !== 'admin' && (
                  <Button
                    onClick={() => setShowHelperForm(!showHelperForm)}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    disabled={helpersLoading}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {showHelperForm ? 'Cancelar' : 'Add Helper'}
                  </Button>
                )}
              </div>

              {/* Formulário Add/Edit Helper - Para PMs */}
              {showHelperForm && user?.role !== 'admin' && (
                <div className="bg-white border border-green-300 rounded-lg p-4 mb-4">
                  <h5 className="font-medium mb-3 text-sm">
                    {editingHelper ? '✏️ Editar Funcionário' : '➕ Novo Funcionário'}
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <Label className="text-sm">Nome Completo *</Label>
                      <Input
                        value={helperFormData.full_name}
                        onChange={(e) => setHelperFormData({...helperFormData, full_name: e.target.value})}
                        placeholder="Nome completo do funcionário"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Telefone</Label>
                      <Input
                        value={helperFormData.phone}
                        onChange={(e) => setHelperFormData({...helperFormData, phone: e.target.value})}
                        onBlur={(e) => {
                          const formatted = formatPhoneNumber(e.target.value);
                          setHelperFormData({...helperFormData, phone: formatted});
                        }}
                        placeholder="+1(555)123-4567"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Endereço</Label>
                      <Input
                        value={helperFormData.address}
                        onChange={(e) => setHelperFormData({...helperFormData, address: e.target.value})}
                        placeholder="Endereço"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Data de Contratação</Label>
                      <Input
                        type="date"
                        value={helperFormData.hire_date}
                        onChange={(e) => setHelperFormData({...helperFormData, hire_date: e.target.value})}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">SSN / Tax ID</Label>
                      <Input
                        value={helperFormData.ssn_tax_id}
                        onChange={(e) => setHelperFormData({...helperFormData, ssn_tax_id: e.target.value})}
                        placeholder="XXX-XX-XXXX"
                        className="text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="pm-speaks-english"
                        checked={helperFormData.speaks_english}
                        onChange={(e) => {
                          const speaksEnglish = e.target.checked;
                          setHelperFormData({
                            ...helperFormData, 
                            speaks_english: speaksEnglish,
                            is_team_translator: speaksEnglish ? helperFormData.is_team_translator : false
                          });
                        }}
                        className="rounded"
                      />
                      <Label htmlFor="pm-speaks-english" className="text-sm cursor-pointer">
                        ☑️ Fala Inglês?
                      </Label>
                    </div>
                    {helperFormData.speaks_english && (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="pm-team-translator"
                          checked={helperFormData.is_team_translator}
                          onChange={(e) => setHelperFormData({...helperFormData, is_team_translator: e.target.checked})}
                          className="rounded"
                        />
                        <Label htmlFor="pm-team-translator" className="text-sm cursor-pointer">
                          🗣️ Tradutor da equipe?
                        </Label>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={cancelHelperForm}
                      disabled={helpersLoading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      onClick={user?.role === 'admin' ? saveHelper : saveMyHelper}
                      disabled={helpersLoading}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {helpersLoading ? '⏳ Salvando...' : (editingHelper ? '💾 Atualizar' : '➕ Adicionar')}
                    </Button>
                  </div>
                </div>
              )}
              
              {helpersLoading ? (
                <div className="text-center py-4">
                  <div className="text-xl">⏳</div>
                  <div className="text-sm text-gray-600">Carregando...</div>
                </div>
              ) : helpers.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-sm">
                  Nenhum funcionário cadastrado
                </div>
              ) : (
                <div className="space-y-2">
                  {helpers.map((helper) => (
                    <div key={helper.id} className="bg-white border rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-sm">{helper.full_name}</div>
                          {helper.phone && <div className="text-xs text-gray-600">📞 {helper.phone}</div>}
                          {helper.address && <div className="text-xs text-gray-600">📍 {helper.address}</div>}
                          {helper.hire_date && <div className="text-xs text-gray-600">📅 Contratado: {helper.hire_date}</div>}
                          {helper.ssn_tax_id && <div className="text-xs text-gray-600">🆔 SSN: {helper.ssn_tax_id}</div>}
                          <div className="flex gap-2 mt-1">
                            {helper.speaks_english && (
                              <Badge variant="secondary" className="text-xs">🇺🇸 Fala Inglês</Badge>
                            )}
                            {helper.is_team_translator && (
                              <Badge variant="default" className="text-xs">🗣️ Tradutor</Badge>
                            )}
                          </div>

                          {/* Documentos vinculados - Visualização */}
                          {(helper.arbitration_agreement_id || helper.contractor_agreement_id) && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              <div className="text-xs text-gray-500 mb-1">📄 Docs:</div>
                              <div className="flex gap-2">
                                {helper.arbitration_agreement_id && (
                                  <div 
                                    className="flex-1 border border-orange-300 rounded p-1 bg-orange-50 text-center cursor-pointer hover:shadow"
                                    onClick={async () => {
                                      try {
                                        const response = await axios.get(
                                          `${API}/arbitration-files/${helper.arbitration_agreement_id}/download`,
                                          { responseType: 'blob' }
                                        );
                                        const blobUrl = window.URL.createObjectURL(response.data);
                                        setPdfModalData({ url: blobUrl, title: `Arbitration - ${helper.full_name}` });
                                        setShowPDFModal(true);
                                      } catch (e) {
                                        toast.error('Erro ao carregar PDF');
                                      }
                                    }}
                                  >
                                    <div className="text-orange-600 text-sm">⚖️</div>
                                    <div className="text-xs text-orange-700">Arb</div>
                                  </div>
                                )}
                                {helper.contractor_agreement_id && (
                                  <div 
                                    className="flex-1 border border-teal-300 rounded p-1 bg-teal-50 text-center cursor-pointer hover:shadow"
                                    onClick={async () => {
                                      try {
                                        const response = await axios.get(
                                          `${API}/contractor-files/${helper.contractor_agreement_id}/download`,
                                          { responseType: 'blob' }
                                        );
                                        const blobUrl = window.URL.createObjectURL(response.data);
                                        setPdfModalData({ url: blobUrl, title: `Contractor - ${helper.full_name}` });
                                        setShowPDFModal(true);
                                      } catch (e) {
                                        toast.error('Erro ao carregar PDF');
                                      }
                                    }}
                                  >
                                    <div className="text-teal-600 text-sm">📝</div>
                                    <div className="text-xs text-teal-700">Cont</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        {/* Botões Edit/Delete apenas para PMs */}
                        {user?.role !== 'admin' && (
                          <div className="flex gap-1 ml-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                startEditHelper(helper);
                                setShowHelperForm(true);
                              }}
                              className="h-7 px-2"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteMyHelper(helper.id, helper.full_name)}
                              className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Documentos - Read Only com Miniaturas */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4">
              <h4 className="font-semibold text-purple-800 mb-3 text-sm sm:text-base">
                📁 {language === 'pt-BR' ? 'Documentos' : 'Documents'}
              </h4>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {['driver_license_file', 'lead_certificate_file', 'lead_firm_certificate_file', 'arbitration_file', 'contractor_file'].map((docType) => {
                  const doc = teamLeaderFiles[docType];
                  const labels = {
                    driver_license_file: '🆔 Driver License',
                    lead_certificate_file: '🏆 Lead Cert',
                    lead_firm_certificate_file: '🏛️ Firm Cert',
                    arbitration_file: '⚖️ Arbitration',
                    contractor_file: '📝 Contractor'
                  };
                  
                  return (
                    <div key={docType} className="space-y-1">
                      <div className="text-xs font-medium text-purple-700 text-center truncate">{labels[docType]}</div>
                      {doc ? (
                        <div 
                          className="relative w-full h-32 bg-gray-100 rounded border-2 border-purple-300 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow group"
                          onClick={async () => {
                            if (doc.file_type === 'pdf') {
                              // Open PDF in modal viewer
                              const blobUrl = await getDocumentBlobUrl(docType);
                              if (blobUrl) {
                                setPdfModalData({
                                  url: blobUrl,
                                  title: labels[docType]
                                });
                                setShowPDFModal(true);
                              }
                            } else {
                              // Open image in modal viewer
                              const blobUrl = await getDocumentBlobUrl(docType);
                              if (blobUrl) {
                                setImageModalData({
                                  url: blobUrl,
                                  title: labels[docType]
                                });
                                setShowImageModal(true);
                              }
                            }
                          }}
                        >
                          {doc.file_type === 'image' ? (
                            <>
                              <img 
                                src={imageUrls[docType] || ''}
                                alt={labels[docType]}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div className="hidden w-full h-full bg-purple-50 items-center justify-center">
                                <div className="text-center">
                                  <div className="text-purple-400 text-xl mb-1">🖼️</div>
                                  <div className="text-xs text-purple-600">Carregando...</div>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full bg-purple-50 flex items-center justify-center">
                              <div className="text-center">
                                <div className="text-purple-400 text-2xl mb-1">📄</div>
                                <div className="text-xs text-purple-600 font-medium">PDF</div>
                              </div>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                            <Eye className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      ) : (
                        <div className="w-full h-32 bg-gray-50 border-2 border-gray-200 border-dashed rounded flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-gray-300 text-xl mb-1">—</div>
                            <div className="text-gray-400 text-xs">Sem documento</div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            {/* Botão de Email para PMs */}
            {user?.role !== 'admin' && (
              <Button 
                onClick={async () => {
                  // Carregar email recipients do próprio PM
                  try {
                    const { data: recipientsData } = await axios.get(`${API}/email-recipients`);
                    setEmailRecipients(recipientsData);
                    console.log(`✅ Carregados ${recipientsData.length} recipients`);
                  } catch (recipientError) {
                    console.error('Erro ao carregar recipients:', recipientError);
                  }
                  
                  // Carregar documentos disponíveis (Arbitration e Contractor)
                  await loadAvailableDocuments(user.username);
                  
                  // Resetar seleções
                  setTeamLeaderEmailData({
                    recipient_email: '',
                    recipient_name: '',
                    selected_documents: [],
                    selected_helpers: [],
                    helper_documents: {},
                    message: ''
                  });
                  setShowTeamLeaderEmailModal(true);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Mail className="h-4 w-4 mr-2" />
                Enviar por Email
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowTeamLeaderViewModal(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal EDITAR (Com Helpers) - Edição completa */}
      <Dialog open={showTeamLeaderEditModal} onOpenChange={setShowTeamLeaderEditModal}>
        <DialogContent className="sm:max-w-5xl max-h-[95vh] overflow-auto mx-2 sm:mx-4">
          <DialogHeader className="border-b pb-3 sm:pb-4">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              ✏️ Editar - {currentTeamLeader?.username || ''}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 p-2 sm:p-0">
            {/* Dados do Líder - Editável */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <h4 className="font-semibold text-blue-800 mb-3 text-sm sm:text-base">
                📝 {language === 'pt-BR' ? 'Dados do Líder' : 'Leader Data'}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">{t('fullName')} *</Label>
                  <Input 
                    value={teamLeaderFormData.full_name}
                    onChange={(e) => setTeamLeaderFormData({...teamLeaderFormData, full_name: e.target.value})}
                    placeholder="Nome completo"
                    className="bg-white text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm">{t('email')}</Label>
                  <Input 
                    type="email"
                    value={teamLeaderFormData.email}
                    onChange={(e) => setTeamLeaderFormData({...teamLeaderFormData, email: e.target.value})}
                    placeholder="email@exemplo.com"
                    className="bg-white text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm">{t('phone')}</Label>
                  <Input 
                    value={teamLeaderFormData.phone}
                    onChange={(e) => setTeamLeaderFormData({...teamLeaderFormData, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    className="bg-white text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm">{t('position')}</Label>
                  <Input 
                    value={teamLeaderFormData.position}
                    onChange={(e) => setTeamLeaderFormData({...teamLeaderFormData, position: e.target.value})}
                    placeholder="Project Manager, Lead..."
                    className="bg-white text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm">{t('address')}</Label>
                  <Textarea 
                    value={teamLeaderFormData.address}
                    onChange={(e) => setTeamLeaderFormData({...teamLeaderFormData, address: e.target.value})}
                    placeholder="Endereço completo"
                    className="bg-white text-sm"
                    rows={2}
                  />
                </div>
              </div>
              
              <div className="flex justify-end mt-3">
                <Button 
                  onClick={updateMyTeamLeaderInfo}
                  disabled={teamLeaderLoading}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {teamLeaderLoading ? '⏳ Salvando...' : '💾 Salvar Dados do Líder'}
                </Button>
              </div>
            </div>

            {/* Funcionários (Helpers) - Editável com CRUD */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-green-800 text-sm sm:text-base">
                  👷 {language === 'pt-BR' ? 'Funcionários da Equipe' : 'Team Workers'}
                </h4>
                <Button
                  onClick={() => setShowHelperForm(!showHelperForm)}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  disabled={helpersLoading}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {showHelperForm ? 'Cancelar' : 'Add Helper'}
                </Button>
              </div>

              {/* Formulário Add/Edit Helper */}
              {showHelperForm && (
                <div className="bg-white border border-green-300 rounded-lg p-4 mb-4">
                  <h5 className="font-medium mb-3 text-sm">
                    {editingHelper ? '✏️ Editar Funcionário' : '➕ Novo Funcionário'}
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <Label className="text-sm">Nome Completo *</Label>
                      <Input
                        value={helperFormData.full_name}
                        onChange={(e) => setHelperFormData({...helperFormData, full_name: e.target.value})}
                        placeholder="Nome completo do funcionário"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Telefone</Label>
                      <Input
                        value={helperFormData.phone}
                        onChange={(e) => setHelperFormData({...helperFormData, phone: e.target.value})}
                        onBlur={(e) => {
                          const formatted = formatPhoneNumber(e.target.value);
                          setHelperFormData({...helperFormData, phone: formatted});
                        }}
                        placeholder="+1(555)123-4567"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Endereço</Label>
                      <Input
                        value={helperFormData.address}
                        onChange={(e) => setHelperFormData({...helperFormData, address: e.target.value})}
                        placeholder="Endereço"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Data de Contratação</Label>
                      <Input
                        type="date"
                        value={helperFormData.hire_date}
                        onChange={(e) => setHelperFormData({...helperFormData, hire_date: e.target.value})}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">SSN / Tax ID</Label>
                      <Input
                        value={helperFormData.ssn_tax_id}
                        onChange={(e) => setHelperFormData({...helperFormData, ssn_tax_id: e.target.value})}
                        placeholder="XXX-XX-XXXX"
                        className="text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="speaks-english"
                        checked={helperFormData.speaks_english}
                        onChange={(e) => {
                          const speaksEnglish = e.target.checked;
                          setHelperFormData({
                            ...helperFormData, 
                            speaks_english: speaksEnglish,
                            // Reset translator if no longer speaks English
                            is_team_translator: speaksEnglish ? helperFormData.is_team_translator : false
                          });
                        }}
                        className="rounded"
                      />
                      <Label htmlFor="speaks-english" className="text-sm cursor-pointer">
                        ☑️ Fala Inglês?
                      </Label>
                    </div>
                    {helperFormData.speaks_english && (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="team-translator"
                          checked={helperFormData.is_team_translator}
                          onChange={(e) => setHelperFormData({...helperFormData, is_team_translator: e.target.checked})}
                          className="rounded"
                        />
                        <Label htmlFor="team-translator" className="text-sm cursor-pointer">
                          🗣️ Tradutor da equipe?
                        </Label>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={cancelHelperForm}
                      disabled={helpersLoading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      onClick={saveHelper}
                      disabled={helpersLoading}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {helpersLoading ? '⏳ Salvando...' : (editingHelper ? '💾 Atualizar' : '➕ Adicionar')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Lista de Helpers */}
              {helpersLoading ? (
                <div className="text-center py-4">
                  <div className="text-xl">⏳</div>
                  <div className="text-sm text-gray-600">Carregando...</div>
                </div>
              ) : helpers.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-sm bg-white rounded border border-dashed">
                  Nenhum funcionário cadastrado. Clique em "Add Helper" para começar.
                </div>
              ) : (
                <div className="space-y-2">
                  {helpers.map((helper) => (
                    <div key={helper.id} className="bg-white border rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-sm">{helper.full_name}</div>
                          {helper.phone && <div className="text-xs text-gray-600">📞 {helper.phone}</div>}
                          {helper.address && <div className="text-xs text-gray-600">📍 {helper.address}</div>}
                          {helper.hire_date && <div className="text-xs text-gray-600">📅 Contratado: {helper.hire_date}</div>}
                          {helper.ssn_tax_id && <div className="text-xs text-gray-600">🆔 SSN: {helper.ssn_tax_id}</div>}
                          <div className="flex gap-2 mt-1">
                            {helper.speaks_english && (
                              <Badge variant="secondary" className="text-xs">🇺🇸 Fala Inglês</Badge>
                            )}
                            {helper.is_team_translator && (
                              <Badge variant="default" className="text-xs">🗣️ Tradutor</Badge>
                            )}
                          </div>

                          {/* Documentos vinculados - Visualização */}
                          {(helper.arbitration_agreement_id || helper.contractor_agreement_id) && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              <div className="text-xs text-gray-500 mb-1">📄 Docs:</div>
                              <div className="flex gap-2">
                                {helper.arbitration_agreement_id && (
                                  <div className="flex-1 border border-orange-300 rounded bg-orange-50">
                                    <div 
                                      className="p-1 text-center cursor-pointer hover:bg-orange-100"
                                      onClick={async () => {
                                        try {
                                          const response = await axios.get(
                                            `${API}/arbitration-files/${helper.arbitration_agreement_id}/download`,
                                            { responseType: 'blob' }
                                          );
                                          const blobUrl = window.URL.createObjectURL(response.data);
                                          openPdfWithLoading({ url: blobUrl, title: `Arbitration - ${helper.full_name}` });
                                          setShowPDFModal(true);
                                        } catch (e) {
                                          toast.error('Erro ao carregar PDF');
                                        }
                                      }}
                                    >
                                      <div className="text-orange-600 text-sm">⚖️</div>
                                      <div className="text-xs text-orange-700">Arb</div>
                                    </div>
                                    <button
                                      className="w-full text-xs text-red-600 hover:bg-red-50 py-1"
                                      onClick={async () => {
                                        if (window.confirm('Desvincular Arbitration deste helper?')) {
                                          try {
                                            await axios.put(
                                              `${API}/admin/team-leaders/${helper.pm_user}/helpers/${helper.id}`,
                                              { arbitration_agreement_id: null }
                                            );
                                            toast.success('Arbitration desvinculado!');
                                            await loadTeamHelpers(currentTeamLeader.username);
                                          } catch (e) {
                                            toast.error('Erro ao desvincular');
                                          }
                                        }
                                      }}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                )}
                                {helper.contractor_agreement_id && (
                                  <div className="flex-1 border border-teal-300 rounded bg-teal-50">
                                    <div 
                                      className="p-1 text-center cursor-pointer hover:bg-teal-100"
                                      onClick={async () => {
                                        try {
                                          const response = await axios.get(
                                            `${API}/contractor-files/${helper.contractor_agreement_id}/download`,
                                            { responseType: 'blob' }
                                          );
                                          const blobUrl = window.URL.createObjectURL(response.data);
                                          openPdfWithLoading({ url: blobUrl, title: `Contractor - ${helper.full_name}` });
                                          setShowPDFModal(true);
                                        } catch (e) {
                                          toast.error('Erro ao carregar PDF');
                                        }
                                      }}
                                    >
                                      <div className="text-teal-600 text-sm">📝</div>
                                      <div className="text-xs text-teal-700">Cont</div>
                                    </div>
                                    <button
                                      className="w-full text-xs text-red-600 hover:bg-red-50 py-1"
                                      onClick={async () => {
                                        if (window.confirm('Desvincular Contractor deste helper?')) {
                                          try {
                                            await axios.put(
                                              `${API}/admin/team-leaders/${helper.pm_user}/helpers/${helper.id}`,
                                              { contractor_agreement_id: null }
                                            );
                                            toast.success('Contractor desvinculado!');
                                            await loadTeamHelpers(currentTeamLeader.username);
                                          } catch (e) {
                                            toast.error('Erro ao desvincular');
                                          }
                                        }
                                      }}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 ml-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => startEditHelper(helper)}
                            disabled={helpersLoading}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteHelper(helper.id, helper.full_name)}
                            disabled={helpersLoading}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Documentos do Líder */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4">
              <h4 className="font-semibold text-purple-800 mb-3 text-sm sm:text-base">
                📁 {language === 'pt-BR' ? 'Documentos do Líder' : 'Leader Documents'}
              </h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {/* Driver License */}
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <h5 className="font-medium mb-2 text-sm flex items-center gap-2">
                    🆔 Driver License
                    {teamLeaderFiles.driver_license_file && (
                      <Badge variant="secondary" className="text-xs">✓</Badge>
                    )}
                  </h5>
                  
                  {teamLeaderFiles.driver_license_file ? (
                    <div className="space-y-2">
                      {/* Thumbnail */}
                      <div 
                        className="relative w-full h-32 bg-gray-100 rounded border-2 border-gray-300 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow group"
                        onClick={async () => {
                          if (teamLeaderFiles.driver_license_file.file_type === 'pdf') {
                            // Open PDF in modal
                            const blobUrl = await getDocumentBlobUrl('driver_license_file');
                            if (blobUrl) {
                              setPdfModalData({
                                url: blobUrl,
                                title: 'Driver License'
                              });
                              setShowPDFModal(true);
                            }
                          } else {
                            // Open image in ImageViewerModal - use cached blob URL
                            const blobUrl = imageUrls.driver_license_file || await getDocumentBlobUrl('driver_license_file');
                            if (blobUrl) {
                              setImageModalData({
                                url: blobUrl,
                                title: 'Driver License'
                              });
                              setShowImageModal(true);
                            }
                          }
                        }}
                      >
                        {teamLeaderFiles.driver_license_file.file_type === 'image' ? (
                          <img 
                            src={imageUrls.driver_license_file || `${API}/team-leader/${currentTeamLeader.username}/download/driver_license_file`}
                            alt="Driver License"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full bg-red-50 flex items-center justify-center">
                            <div className="text-center">
                              <div className="text-red-400 text-2xl mb-1">📄</div>
                              <div className="text-xs text-red-600 font-medium">PDF</div>
                            </div>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                          <Eye className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => downloadTeamLeaderDocument('driver_license_file')}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                        {user?.role === 'admin' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => deleteTeamLeaderDocument('driver_license_file')}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) uploadTeamLeaderDocument('driver_license_file', file);
                        }}
                        className="hidden"
                        id="edit-driver-license-upload"
                      />
                      <label htmlFor="edit-driver-license-upload" className="cursor-pointer">
                        <div className="text-gray-400 text-2xl mb-1">📄</div>
                        <div className="text-xs text-gray-600">Clique para upload</div>
                        <div className="text-xs text-gray-400">JPG, PNG, PDF, HEIC</div>
                      </label>
                    </div>
                  )}
                  
                  {uploadingFiles.driver_license_file && (
                    <div className="mt-2 text-center text-xs text-blue-600">⏳ Uploading...</div>
                  )}
                </div>

                {/* Lead Certificate */}
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <h5 className="font-medium mb-2 text-sm flex items-center gap-2">
                    🏆 Lead Certificate
                    {teamLeaderFiles.lead_certificate_file && (
                      <Badge variant="secondary" className="text-xs">✓</Badge>
                    )}
                  </h5>
                  
                  {teamLeaderFiles.lead_certificate_file ? (
                    <div className="space-y-2">
                      {/* Thumbnail */}
                      <div 
                        className="relative w-full h-32 bg-gray-100 rounded border-2 border-gray-300 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow group"
                        onClick={async () => {
                          if (teamLeaderFiles.lead_certificate_file.file_type === 'pdf') {
                            const blobUrl = await getDocumentBlobUrl('lead_certificate_file');
                            if (blobUrl) {
                              setPdfModalData({
                                url: blobUrl,
                                title: 'Lead Certificate'
                              });
                              setShowPDFModal(true);
                            }
                          } else {
                            const blobUrl = imageUrls.lead_certificate_file || await getDocumentBlobUrl('lead_certificate_file');
                            if (blobUrl) {
                              setImageModalData({
                                url: blobUrl,
                                title: 'Lead Certificate'
                              });
                              setShowImageModal(true);
                            }
                          }
                        }}
                      >
                        {teamLeaderFiles.lead_certificate_file.file_type === 'image' ? (
                          <img 
                            src={imageUrls.lead_certificate_file || `${API}/team-leader/${currentTeamLeader.username}/download/lead_certificate_file`}
                            alt="Lead Certificate"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-yellow-50 flex items-center justify-center">
                            <div className="text-center">
                              <div className="text-yellow-400 text-2xl mb-1">📄</div>
                              <div className="text-xs text-yellow-600 font-medium">PDF</div>
                            </div>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                          <Eye className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => downloadTeamLeaderDocument('lead_certificate_file')}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                        {user?.role === 'admin' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => deleteTeamLeaderDocument('lead_certificate_file')}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) uploadTeamLeaderDocument('lead_certificate_file', file);
                        }}
                        className="hidden"
                        id="edit-lead-certificate-upload"
                      />
                      <label htmlFor="edit-lead-certificate-upload" className="cursor-pointer">
                        <div className="text-gray-400 text-2xl mb-1">🏆</div>
                        <div className="text-xs text-gray-600">Clique para upload</div>
                        <div className="text-xs text-gray-400">JPG, PNG, PDF, HEIC</div>
                      </label>
                    </div>
                  )}
                  
                  {uploadingFiles.lead_certificate_file && (
                    <div className="mt-2 text-center text-xs text-blue-600">⏳ Uploading...</div>
                  )}
                </div>

                {/* Lead Firm Certificate */}
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <h5 className="font-medium mb-2 text-sm flex items-center gap-2">
                    🏛️ Lead Firm Certificate
                    {teamLeaderFiles.lead_firm_certificate_file && (
                      <Badge variant="secondary" className="text-xs">✓</Badge>
                    )}
                  </h5>
                  
                  {teamLeaderFiles.lead_firm_certificate_file ? (
                    <div className="space-y-2">
                      <div 
                        className="relative w-full h-32 bg-gray-100 rounded border-2 border-gray-300 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow group"
                        onClick={async () => {
                          if (teamLeaderFiles.lead_firm_certificate_file.file_type === 'pdf') {
                            const blobUrl = await getDocumentBlobUrl('lead_firm_certificate_file');
                            if (blobUrl) {
                              setPdfModalData({
                                url: blobUrl,
                                title: 'Lead Firm Certificate'
                              });
                              setShowPDFModal(true);
                            }
                          } else {
                            const blobUrl = imageUrls.lead_firm_certificate_file || await getDocumentBlobUrl('lead_firm_certificate_file');
                            if (blobUrl) {
                              setImageModalData({
                                url: blobUrl,
                                title: 'Lead Firm Certificate'
                              });
                              setShowImageModal(true);
                            }
                          }
                        }}
                      >
                        {teamLeaderFiles.lead_firm_certificate_file.file_type === 'image' ? (
                          <img 
                            src={imageUrls.lead_firm_certificate_file || `${API}/team-leader/${currentTeamLeader.username}/download/lead_firm_certificate_file`}
                            alt="Lead Firm Certificate"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-purple-50 flex items-center justify-center">
                            <div className="text-center">
                              <div className="text-purple-400 text-2xl mb-1">📄</div>
                              <div className="text-xs text-purple-600 font-medium">PDF</div>
                            </div>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                          <Eye className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => downloadTeamLeaderDocument('lead_firm_certificate_file')}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                        {user?.role === 'admin' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => deleteTeamLeaderDocument('lead_firm_certificate_file')}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) uploadTeamLeaderDocument('lead_firm_certificate_file', file);
                        }}
                        className="hidden"
                        id="edit-lead-firm-certificate-upload"
                      />
                      <label htmlFor="edit-lead-firm-certificate-upload" className="cursor-pointer">
                        <div className="text-gray-400 text-2xl mb-1">🏛️</div>
                        <div className="text-xs text-gray-600">Clique para upload</div>
                        <div className="text-xs text-gray-400">JPG, PNG, PDF, HEIC</div>
                      </label>
                    </div>
                  )}
                  
                  {uploadingFiles.lead_firm_certificate_file && (
                    <div className="mt-2 text-center text-xs text-blue-600">⏳ Uploading...</div>
                  )}
                </div>

                {/* Arbitration File */}
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <h5 className="font-medium mb-2 text-sm flex items-center gap-2">
                    ⚖️ Arbitration Agreement
                    {teamLeaderFiles.arbitration_file && (
                      <Badge variant="secondary" className="text-xs">✓</Badge>
                    )}
                  </h5>
                  
                  {teamLeaderFiles.arbitration_file ? (
                    <div className="space-y-2">
                      <div 
                        className="relative w-full h-32 bg-gray-100 rounded border-2 border-gray-300 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow group"
                        onClick={async () => {
                          const blobUrl = await getDocumentBlobUrl('arbitration_file');
                          if (blobUrl) {
                            setPdfModalData({
                              url: blobUrl,
                              title: 'Arbitration Agreement'
                            });
                            setShowPDFModal(true);
                          }
                        }}
                      >
                        <div className="w-full h-full bg-orange-50 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-orange-400 text-2xl mb-1">📄</div>
                            <div className="text-xs text-orange-600 font-medium">PDF</div>
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                          <Eye className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => downloadTeamLeaderDocument('arbitration_file')}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                        {user?.role === 'admin' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => deleteTeamLeaderDocument('arbitration_file')}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="border-2 border-dashed border-orange-300 rounded-lg p-4 text-center bg-orange-50">
                        <div className="text-orange-400 text-2xl mb-1">⚖️</div>
                        <div className="text-xs text-orange-700 font-medium mb-2">Selecionar da Lista</div>
                        <Button
                          size="sm"
                          onClick={() => setShowArbitrationSelector(true)}
                          className="bg-orange-600 hover:bg-orange-700 text-xs"
                        >
                          📋 Escolher Arbitration
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {uploadingFiles.arbitration_file && (
                    <div className="mt-2 text-center text-xs text-blue-600">⏳ Uploading...</div>
                  )}
                </div>

                {/* Independent Contractor */}
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <h5 className="font-medium mb-2 text-sm flex items-center gap-2">
                    📝 Independent Contractor
                    {teamLeaderFiles.contractor_file && (
                      <Badge variant="secondary" className="text-xs">✓</Badge>
                    )}
                  </h5>
                  
                  {teamLeaderFiles.contractor_file ? (
                    <div className="space-y-2">
                      <div 
                        className="relative w-full h-32 bg-gray-100 rounded border-2 border-gray-300 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow group"
                        onClick={async () => {
                          const blobUrl = await getDocumentBlobUrl('contractor_file');
                          if (blobUrl) {
                            setPdfModalData({
                              url: blobUrl,
                              title: 'Independent Contractor'
                            });
                            setShowPDFModal(true);
                          }
                        }}
                      >
                        <div className="w-full h-full bg-teal-50 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-teal-400 text-2xl mb-1">📄</div>
                            <div className="text-xs text-teal-600 font-medium">PDF</div>
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                          <Eye className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => downloadTeamLeaderDocument('contractor_file')}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                        {user?.role === 'admin' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => deleteTeamLeaderDocument('contractor_file')}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="border-2 border-dashed border-teal-300 rounded-lg p-4 text-center bg-teal-50">
                        <div className="text-teal-400 text-2xl mb-1">📝</div>
                        <div className="text-xs text-teal-700 font-medium mb-2">Selecionar da Lista</div>
                        <Button
                          size="sm"
                          onClick={() => setShowContractorSelector(true)}
                          className="bg-teal-600 hover:bg-teal-700 text-xs"
                        >
                          📋 Escolher Contractor
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {uploadingFiles.contractor_file && (
                    <div className="mt-2 text-center text-xs text-blue-600">⏳ Uploading...</div>
                  )}
                </div>
              </div>

              {/* Upload Guidelines */}
              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                <strong>📋 Info:</strong> Formatos aceitos: JPG, PNG, HEIC, PDF | Max: 10MB por arquivo
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={() => {
              setShowTeamLeaderEditModal(false);
              cancelHelperForm();
            }}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Seletor de Arbitration Agreement */}
      <Dialog open={showArbitrationSelector} onOpenChange={setShowArbitrationSelector}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader className="border-b pb-3">
            <DialogTitle className="flex items-center gap-2">
              ⚖️ Selecionar Arbitration Agreement - {currentTeamLeader?.username}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 p-2">
            {availableArbitrationDocs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">📋</div>
                <div className="text-sm">Nenhum Arbitration Agreement criado ainda.</div>
                <div className="text-xs mt-1">Crie um na aba "Arbitration" primeiro.</div>
              </div>
            ) : (
              <div className="space-y-2">
                {availableArbitrationDocs.map((doc) => (
                  <div 
                    key={doc.id}
                    className={`border rounded-lg p-3 cursor-pointer transition-all ${
                      selectedArbitrationDoc?.id === doc.id 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                    }`}
                    onClick={() => setSelectedArbitrationDoc(doc)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-sm">{doc.helper_name}</div>
                          <Badge variant="outline" className="text-xs">{doc.pm_user}</Badge>
                        </div>
                        <div className="text-xs text-gray-600">Data: {doc.date}</div>
                        {doc.signature && (
                          <Badge variant="secondary" className="text-xs mt-1">✍️ Assinado</Badge>
                        )}
                      </div>
                      {selectedArbitrationDoc?.id === doc.id && (
                        <div className="text-orange-600 text-xl">✓</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowArbitrationSelector(false);
                setSelectedArbitrationDoc(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (selectedArbitrationDoc) {
                  attachDocumentToLeader('arbitration_file', selectedArbitrationDoc.id, `Arbitration_${selectedArbitrationDoc.helper_name}`);
                }
              }}
              disabled={!selectedArbitrationDoc || uploadingFiles.arbitration_file}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {uploadingFiles.arbitration_file ? '⏳ Anexando...' : '📎 Anexar Selecionado'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Seletor de Independent Contractor */}
      <Dialog open={showContractorSelector} onOpenChange={setShowContractorSelector}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader className="border-b pb-3">
            <DialogTitle className="flex items-center gap-2">
              📝 Selecionar Independent Contractor - {currentTeamLeader?.username}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 p-2">
            {availableContractorDocs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">📝</div>
                <div className="text-sm">Nenhum Independent Contractor criado ainda.</div>
                <div className="text-xs mt-1">Crie um na aba "Contractor" primeiro.</div>
              </div>
            ) : (
              <div className="space-y-2">
                {availableContractorDocs.map((doc) => (
                  <div 
                    key={doc.id}
                    className={`border rounded-lg p-3 cursor-pointer transition-all ${
                      selectedContractorDoc?.id === doc.id 
                        ? 'border-teal-500 bg-teal-50' 
                        : 'border-gray-200 hover:border-teal-300 hover:bg-teal-50'
                    }`}
                    onClick={() => setSelectedContractorDoc(doc)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-sm">{doc.helper_name}</div>
                          <Badge variant="outline" className="text-xs">{doc.pm_user}</Badge>
                        </div>
                        <div className="text-xs text-gray-600">
                          {doc.title && <span className="mr-2">Título: {doc.title}</span>}
                          Data: {doc.date}
                        </div>
                        {doc.signature && (
                          <Badge variant="secondary" className="text-xs mt-1">✍️ Assinado</Badge>
                        )}
                      </div>
                      {selectedContractorDoc?.id === doc.id && (
                        <div className="text-teal-600 text-xl">✓</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowContractorSelector(false);
                setSelectedContractorDoc(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (selectedContractorDoc) {
                  attachDocumentToLeader('contractor_file', selectedContractorDoc.id, `Contractor_${selectedContractorDoc.helper_name}`);
                }
              }}
              disabled={!selectedContractorDoc || uploadingFiles.contractor_file}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {uploadingFiles.contractor_file ? '⏳ Anexando...' : '📎 Anexar Selecionado'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Envio de Email do Team Leader */}
      <Dialog open={showTeamLeaderEmailModal} onOpenChange={setShowTeamLeaderEmailModal}>
        <DialogContent className="sm:max-w-3xl max-w-[95vw] max-h-[85vh] flex flex-col p-0">
          <DialogHeader className="border-b pb-3 px-4 pt-4 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              📧 Enviar Informações da Equipe - {currentTeamLeader?.username}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 p-3 sm:p-4 overflow-y-auto flex-1">
            {/* Destinatário */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-blue-800 mb-3 text-xs sm:text-sm">📮 Destinatário</h5>
              
              {/* Lista de Recipients Cadastrados */}
              {emailRecipients && emailRecipients.length > 0 && (
                <div className="mb-4">
                  <Label className="text-xs sm:text-sm mb-2 block">Selecionar destinatário cadastrado:</Label>
                  <div className="space-y-1 max-h-32 overflow-y-auto bg-white rounded border p-2">
                    {emailRecipients.map((recipient) => (
                      <div 
                        key={recipient.id}
                        className="flex items-center gap-2 p-2 hover:bg-blue-50 rounded cursor-pointer text-xs sm:text-sm"
                        onClick={() => {
                          setTeamLeaderEmailData({
                            ...teamLeaderEmailData,
                            recipient_name: recipient.name,
                            recipient_email: recipient.email
                          });
                        }}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{recipient.name}</div>
                          <div className="text-xs text-gray-600 truncate">{recipient.email}</div>
                        </div>
                        {teamLeaderEmailData.recipient_email === recipient.email && (
                          <div className="text-blue-600 flex-shrink-0">✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-gray-600 mt-2">Ou preencha manualmente abaixo:</div>
                </div>
              )}
              
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label className="text-xs sm:text-sm">Nome *</Label>
                  <Input
                    value={teamLeaderEmailData.recipient_name}
                    onChange={(e) => setTeamLeaderEmailData({...teamLeaderEmailData, recipient_name: e.target.value})}
                    placeholder="Nome do destinatário"
                    className="text-xs sm:text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs sm:text-sm">Email *</Label>
                  <Input
                    type="email"
                    value={teamLeaderEmailData.recipient_email}
                    onChange={(e) => setTeamLeaderEmailData({...teamLeaderEmailData, recipient_email: e.target.value})}
                    placeholder="email@exemplo.com"
                    className="text-xs sm:text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Seleção de Documentos */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-purple-800 mb-3 text-xs sm:text-sm">📁 Documentos para Anexar</h5>
              <div className="space-y-2">
                {['driver_license_file', 'lead_certificate_file', 'lead_firm_certificate_file', 'arbitration_file', 'contractor_file'].map((docType) => {
                  const doc = teamLeaderFiles[docType];
                  const labels = {
                    driver_license_file: '🆔 Driver License',
                    lead_certificate_file: '🏆 Lead Certificate',
                    lead_firm_certificate_file: '🏛️ Lead Firm Certificate',
                    arbitration_file: '⚖️ Arbitration Agreement',
                    contractor_file: '📝 Independent Contractor'
                  };
                  
                  if (!doc) return null;
                  
                  return (
                    <div key={docType} className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        id={`email-doc-${docType}`}
                        checked={teamLeaderEmailData.selected_documents.includes(docType)}
                        onChange={(e) => {
                          const selected = teamLeaderEmailData.selected_documents;
                          if (e.target.checked) {
                            setTeamLeaderEmailData({
                              ...teamLeaderEmailData,
                              selected_documents: [...selected, docType]
                            });
                          } else {
                            setTeamLeaderEmailData({
                              ...teamLeaderEmailData,
                              selected_documents: selected.filter(d => d !== docType)
                            });
                          }
                        }}
                        className="rounded flex-shrink-0"
                      />
                      <Label htmlFor={`email-doc-${docType}`} className="text-xs sm:text-sm cursor-pointer">
                        {labels[docType]}
                      </Label>
                    </div>
                  );
                })}
                {Object.keys(teamLeaderFiles).every(k => !teamLeaderFiles[k]) && (
                  <div className="text-center py-3 text-gray-500 text-sm">
                    Nenhum documento disponível para anexar
                  </div>
                )}
              </div>
            </div>

            {/* Seleção de Helpers */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-green-800 mb-3 text-xs sm:text-sm">👷 Funcionários para Incluir</h5>
              {helpers.length === 0 ? (
                <div className="text-center py-3 text-gray-500 text-xs sm:text-sm">
                  Nenhum funcionário cadastrado
                </div>
              ) : (
                <div className="space-y-2">
                  {helpers.map((helper) => (
                    <div key={helper.id} className="border rounded-lg p-2 bg-white">
                      <div className="flex items-start gap-2 flex-wrap">
                        <input
                          type="checkbox"
                          id={`email-helper-${helper.id}`}
                          checked={teamLeaderEmailData.selected_helpers.includes(helper.id)}
                          onChange={(e) => {
                            const selected = teamLeaderEmailData.selected_helpers;
                            if (e.target.checked) {
                              setTeamLeaderEmailData({
                                ...teamLeaderEmailData,
                                selected_helpers: [...selected, helper.id]
                              });
                            } else {
                              // Remove helper e seus documentos
                              const newHelperDocs = {...teamLeaderEmailData.helper_documents};
                              delete newHelperDocs[helper.id];
                              setTeamLeaderEmailData({
                                ...teamLeaderEmailData,
                                selected_helpers: selected.filter(h => h !== helper.id),
                                helper_documents: newHelperDocs
                              });
                            }
                          }}
                          className="rounded mt-1 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <Label htmlFor={`email-helper-${helper.id}`} className="text-xs sm:text-sm cursor-pointer block">
                            {helper.full_name}
                            {helper.is_team_translator && <Badge variant="default" className="ml-2 text-xs">🗣️ Tradutor</Badge>}
                          </Label>
                          
                          {/* Botões para anexar documentos do helper */}
                          {teamLeaderEmailData.selected_helpers.includes(helper.id) && (
                            <div className="flex gap-1 mt-2 flex-wrap">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs px-2 py-1 h-7"
                                onClick={() => {
                                  setHelperDocSelectorData({
                                    helper_id: helper.id,
                                    helper_name: helper.full_name,
                                    doc_type: 'arbitration'
                                  });
                                  setShowHelperDocSelector(true);
                                }}
                              >
                                ⚖️ Arbitration
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs px-2 py-1 h-7"
                                onClick={() => {
                                  setHelperDocSelectorData({
                                    helper_id: helper.id,
                                    helper_name: helper.full_name,
                                    doc_type: 'contractor'
                                  });
                                  setShowHelperDocSelector(true);
                                }}
                              >
                                📝 Contractor
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Mostrar documentos anexados para este helper */}
                      {teamLeaderEmailData.helper_documents[helper.id] && (
                        <div className="mt-2 flex gap-2 text-xs flex-wrap">
                          {teamLeaderEmailData.helper_documents[helper.id].arbitration_id && (
                            <Badge variant="secondary" className="text-xs">⚖️ Arbitration anexado</Badge>
                          )}
                          {teamLeaderEmailData.helper_documents[helper.id].contractor_id && (
                            <Badge variant="secondary" className="text-xs">📝 Contractor anexado</Badge>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mensagem Opcional */}
            <div>
              <Label className="text-xs sm:text-sm">Mensagem Adicional (Opcional)</Label>
              <Textarea
                value={teamLeaderEmailData.message}
                onChange={(e) => setTeamLeaderEmailData({...teamLeaderEmailData, message: e.target.value})}
                placeholder="Adicione uma mensagem personalizada..."
                className="text-xs sm:text-sm"
                rows={3}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 p-3 sm:p-4 border-t bg-white flex-shrink-0">
            <Button 
              variant="outline" 
              onClick={() => setShowTeamLeaderEmailModal(false)}
              className="w-full sm:w-auto text-sm order-2 sm:order-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={sendTeamLeaderEmail}
              disabled={!teamLeaderEmailData.recipient_email || !teamLeaderEmailData.recipient_name || teamLeaderEmailLoading}
              className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto text-sm order-1 sm:order-2"
            >
              {teamLeaderEmailLoading ? '⏳ Enviando...' : '📧 Enviar Email'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Documentos por Helper */}
      <Dialog open={showHelperDocSelector} onOpenChange={setShowHelperDocSelector}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-auto">
          <DialogHeader className="border-b pb-3">
            <DialogTitle>
              {helperDocSelectorData.doc_type === 'arbitration' ? '⚖️ Arbitration Agreement' : '📝 Independent Contractor'} - {helperDocSelectorData.helper_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-2 p-2">
            {helperDocSelectorData.doc_type === 'arbitration' ? (
              availableArbitrationDocs.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-sm">
                  Nenhum Arbitration Agreement disponível
                </div>
              ) : (
                availableArbitrationDocs.map((doc) => (
                  <div
                    key={doc.id}
                    className="border rounded-lg p-3 cursor-pointer hover:bg-orange-50 transition-all"
                    onClick={() => {
                      // Anexar documento ao helper
                      const helperDocs = {...teamLeaderEmailData.helper_documents};
                      if (!helperDocs[helperDocSelectorData.helper_id]) {
                        helperDocs[helperDocSelectorData.helper_id] = {};
                      }
                      helperDocs[helperDocSelectorData.helper_id].arbitration_id = doc.id;
                      
                      setTeamLeaderEmailData({
                        ...teamLeaderEmailData,
                        helper_documents: helperDocs
                      });
                      
                      toast.success(`Arbitration anexado para ${helperDocSelectorData.helper_name}`);
                      setShowHelperDocSelector(false);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{doc.helper_name}</div>
                        <div className="text-xs text-gray-600">
                          <Badge variant="outline" className="text-xs mr-2">{doc.pm_user}</Badge>
                          Data: {doc.date}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )
            ) : (
              availableContractorDocs.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-sm">
                  Nenhum Independent Contractor disponível
                </div>
              ) : (
                availableContractorDocs.map((doc) => (
                  <div
                    key={doc.id}
                    className="border rounded-lg p-3 cursor-pointer hover:bg-teal-50 transition-all"
                    onClick={() => {
                      // Anexar documento ao helper
                      const helperDocs = {...teamLeaderEmailData.helper_documents};
                      if (!helperDocs[helperDocSelectorData.helper_id]) {
                        helperDocs[helperDocSelectorData.helper_id] = {};
                      }
                      helperDocs[helperDocSelectorData.helper_id].contractor_id = doc.id;
                      
                      setTeamLeaderEmailData({
                        ...teamLeaderEmailData,
                        helper_documents: helperDocs
                      });
                      
                      toast.success(`Contractor anexado para ${helperDocSelectorData.helper_name}`);
                      setShowHelperDocSelector(false);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{doc.helper_name}</div>
                        <div className="text-xs text-gray-600">
                          <Badge variant="outline" className="text-xs mr-2">{doc.pm_user}</Badge>
                          {doc.title && <span className="mr-2">Título: {doc.title}</span>}
                          Data: {doc.date}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )
            )}
          </div>

          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowHelperDocSelector(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Independent Contractor PDF Viewer Modal - Full Document */}
      <Dialog open={showContractorViewer} onOpenChange={closeContractorViewer}>
        <DialogContent className="sm:max-w-6xl max-h-[95vh] overflow-auto">
          <DialogHeader className="border-b pb-3">
            <DialogTitle className="flex items-center gap-2">
              📋 {language === 'pt-BR' ? 'Visualização Contrato' : 'Contract Viewing'}: {contractorPdfTitle}
            </DialogTitle>
          </DialogHeader>
          
          <div className="h-[80vh] w-full bg-gray-50 rounded overflow-hidden pdf-modal-mobile-container">
            {contractorPdfUrl ? (
              // Detectar mobile/iPad e usar componente apropriado
              /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Safari/i.test(navigator.userAgent) || window.innerWidth <= 1024 ? (
                <MobilePDFViewer 
                  pdfUrl={contractorPdfUrl}
                  onError={(error) => {
                    console.error('Error in MobilePDFViewer:', error);
                    toast.error('Erro ao carregar PDF');
                  }}
                />
              ) : (
                <iframe
                  src={contractorPdfUrl}
                  className="w-full h-full border-0 rounded bg-white"
                  title={`PDF - ${contractorPdfTitle}`}
                  loading="eager"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  allow="fullscreen"
                  sandbox="allow-same-origin allow-scripts allow-forms"
                />
              )
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-4xl mb-4">⏳</div>
                  <div className="text-gray-600 mb-2">{language === 'pt-BR' ? 'Carregando PDF...' : 'Loading PDF...'}</div>
                  <div className="text-xs text-gray-400">{language === 'pt-BR' ? 'Aguarde enquanto o arquivo é processado' : 'Please wait while the file is processed'}</div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center gap-3 mt-4 pt-3 border-t">
            <div className="text-xs text-gray-500">
              📋 PDF: {contractorPdfUrl ? (language === 'pt-BR' ? 'Carregado ✅' : 'Loaded ✅') : (language === 'pt-BR' ? 'Carregando...' : 'Loading...')}
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={closeContractorViewer}
              >
                {language === 'pt-BR' ? 'Fechar' : 'Close'}
              </Button>
              {contractorPdfUrl && (
                <Button 
                  onClick={() => {
                    // Download the same PDF being viewed
                    const link = document.createElement('a');
                    link.href = contractorPdfUrl;
                    link.download = `contractor_${contractorPdfTitle.replace(/\s+/g, '_')}.pdf`;
                    link.click();
                    toast.success(language === 'pt-BR' ? 'Download iniciado' : 'Download started');
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('download')}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Announcement Confirmation Modal - iPad Compatible */}
      {showDeleteConfirmModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: 9999 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeleteConfirmModal(false);
              setAnnouncementToDelete(null);
            }
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-4 text-red-600">
              🗑️ {language === 'pt-BR' ? 'Confirmar Exclusão' : 'Confirm Deletion'}
            </h3>
            
            {announcementToDelete && (
              <div className="space-y-3">
                <p className="text-gray-700">
                  {language === 'pt-BR' ? 'Tem certeza que deseja deletar este aviso?' : 'Are you sure you want to delete this announcement?'}
                </p>
                
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="font-medium text-sm">{announcementToDelete.title}</div>
                  <div className="text-xs text-gray-600 mt-1">{announcementToDelete.content}</div>
                </div>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-800 text-sm">
                    <strong>⚠️ {language === 'pt-BR' ? 'Atenção' : 'Warning'}:</strong> {language === 'pt-BR' ? 
                      'Esta ação não pode ser desfeita. O aviso será removido para todos os usuários.' :
                      'This action cannot be undone. The announcement will be removed for all users.'
                    }
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex gap-3 mt-6">
              <button
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setShowDeleteConfirmModal(false);
                  setAnnouncementToDelete(null);
                }}
                className="flex-1 bg-gray-500 text-white px-4 py-2 rounded"
              >
                {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                onMouseDown={(e) => {
                  e.stopPropagation();
                  confirmDeleteAnnouncement();
                }}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded"
              >
                {language === 'pt-BR' ? 'Deletar Aviso' : 'Delete Announcement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Company Announcements Modal */}
      <Dialog open={showAnnouncementModal} onOpenChange={setShowAnnouncementModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              📢 {editingAnnouncement ? 
                (language === 'pt-BR' ? 'Editar Aviso' : 'Edit Announcement') :
                (language === 'pt-BR' ? 'Novo Aviso da Empresa' : 'New Company Announcement')
              }
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>📢 {language === 'pt-BR' ? 'Aviso da Empresa' : 'Company Announcement'}</strong> - 
                {language === 'pt-BR' ? 'Visível para todos os PMs' : 'Visible to all PMs'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>{language === 'pt-BR' ? 'Título do Aviso' : 'Announcement Title'} *</Label>
                <Input 
                  value={announcementForm.title}
                  onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                  placeholder={language === 'pt-BR' ? 'Ex: Reunião de equipe amanhã às 14h' : 'Ex: Team meeting tomorrow at 2pm'}
                  className="text-base"
                />
              </div>
              
              <div>
                <Label>{language === 'pt-BR' ? 'Prioridade' : 'Priority'}</Label>
                <select 
                  className="w-full border rounded px-3 py-2 mt-2 bg-white"
                  value={announcementForm.priority}
                  onChange={(e) => setAnnouncementForm({...announcementForm, priority: e.target.value})}
                >
                  <option value="low">📝 {language === 'pt-BR' ? 'Baixa' : 'Low'}</option>
                  <option value="normal">ℹ️ {language === 'pt-BR' ? 'Normal' : 'Normal'}</option>
                  <option value="high">⚠️ {language === 'pt-BR' ? 'Alta' : 'High'}</option>
                  <option value="urgent">🚨 {language === 'pt-BR' ? 'Urgente' : 'Urgent'}</option>
                </select>
              </div>
              
              <div>
                <Label>{language === 'pt-BR' ? 'Expira em (opcional)' : 'Expires on (optional)'}</Label>
                <Input 
                  type="date"
                  value={announcementForm.expires_at}
                  onChange={(e) => setAnnouncementForm({...announcementForm, expires_at: e.target.value})}
                  className="mt-2"
                />
              </div>
              
              <div className="md:col-span-2">
                <Label>{language === 'pt-BR' ? 'Conteúdo do Aviso' : 'Announcement Content'} *</Label>
                <Textarea 
                  value={announcementForm.content}
                  onChange={(e) => setAnnouncementForm({...announcementForm, content: e.target.value})}
                  placeholder={language === 'pt-BR' ? 
                    'Digite o conteúdo completo do aviso aqui...' :
                    'Enter the full announcement content here...'
                  }
                  rows={4}
                  className="mt-2"
                />
              </div>
            </div>

            {/* Preview */}
            {announcementForm.title && announcementForm.content && (
              <div className="bg-slate-800 text-white rounded-lg p-4">
                <h5 className="font-medium text-gray-300 mb-2">
                  {language === 'pt-BR' ? 'Preview:' : 'Preview:'}
                </h5>
                <div className="bg-slate-700 rounded-lg p-3 border border-slate-600">
                  <div className="flex items-center gap-2 mb-1">
                    <h6 className="font-semibold text-white">{announcementForm.title}</h6>
                    <Badge className={`text-xs ${
                      announcementForm.priority === 'urgent' ? 'bg-red-500 text-white' :
                      announcementForm.priority === 'high' ? 'bg-orange-500 text-white' :
                      announcementForm.priority === 'normal' ? 'bg-blue-500 text-white' :
                      'bg-gray-500 text-white'
                    }`}>
                      {announcementForm.priority === 'urgent' ? '🚨' :
                       announcementForm.priority === 'high' ? '⚠️' :
                       announcementForm.priority === 'normal' ? 'ℹ️' : '📝'}
                    </Badge>
                  </div>
                  <p className="text-gray-300 text-sm">{announcementForm.content}</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowAnnouncementModal(false);
                setEditingAnnouncement(null);
                setAnnouncementForm({
                  title: '',
                  content: '',
                  priority: 'normal',
                  expires_at: ''
                });
              }}
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={createAnnouncement}
              disabled={!announcementForm.title.trim() || !announcementForm.content.trim() || announcementsLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {announcementsLoading ? 
                `⏳ ${language === 'pt-BR' ? 'Salvando...' : 'Saving...'}` : 
                (editingAnnouncement ? 
                  `✏️ ${language === 'pt-BR' ? 'Atualizar Aviso' : 'Update Announcement'}` :
                  `📢 ${language === 'pt-BR' ? 'Criar Aviso' : 'Create Announcement'}`
                )
              }
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Personal Documents Modals */}
      {user?.role === 'personal' && (
        <>
          {/* New Folder Modal */}
          <Dialog open={showNewFolderModal} onOpenChange={(open) => {
            console.log('📁 New Folder Modal onOpenChange:', open);
            setShowNewFolderModal(open);
          }}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  📁 {language === 'pt-BR' ? 'Nova Pasta' : 'New Folder'}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    <strong>📋 Debug:</strong> Modal está aberto - showNewFolderModal: {showNewFolderModal.toString()}
                  </p>
                </div>
                
                <div>
                  <Label>{language === 'pt-BR' ? 'Nome da Pasta' : 'Folder Name'} *</Label>
                  <Input 
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder={language === 'pt-BR' ? 'Digite o nome da pasta...' : 'Enter folder name...'}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newFolderName.trim()) {
                        createPersonalFolder();
                      }
                    }}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    console.log('📁 Cancel button clicked');
                    setShowNewFolderModal(false);
                    setNewFolderName('');
                  }}
                >
                  {t('cancel')}
                </Button>
                <Button 
                  onClick={() => {
                    console.log('📁 Create folder button clicked');
                    createPersonalFolder();
                  }}
                  disabled={!newFolderName.trim()}
                  className="bg-green-600 hover:bg-green-700"
                >
                  📁 {language === 'pt-BR' ? 'Criar Pasta' : 'Create Folder'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Document Upload Modal */}
          <Dialog open={showDocumentUpload} onOpenChange={(open) => {
            console.log('📤 Document Upload Modal onOpenChange:', open);
            setShowDocumentUpload(open);
          }}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  📤 {language === 'pt-BR' ? 'Adicionar Documentos' : 'Add Documents'}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>📋 Debug:</strong> Modal está aberto - showDocumentUpload: {showDocumentUpload.toString()}
                  </p>
                  <p className="text-sm text-blue-800 mt-1">
                    <strong>📋 {language === 'pt-BR' ? 'Upload de Documentos' : 'Document Upload'}</strong> - 
                    {language === 'pt-BR' ? 'Selecione um ou múltiplos arquivos' : 'Select one or multiple files'}
                  </p>
                </div>

                <div>
                  <Label>{language === 'pt-BR' ? 'Selecionar Arquivos' : 'Select Files'}</Label>
                  <Input 
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setUploadFiles(files);
                    }}
                    className="mt-2"
                  />
                  {uploadFiles.length > 0 && (
                    <div className="mt-2 text-xs text-gray-600">
                      {uploadFiles.length} {language === 'pt-BR' ? 'arquivo(s) selecionado(s)' : 'file(s) selected'}
                    </div>
                  )}
                </div>

                {/* Files Preview */}
                {uploadFiles.length > 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h5 className="font-medium text-gray-800 mb-2">
                      📋 {language === 'pt-BR' ? 'Arquivos Selecionados' : 'Selected Files'}:
                    </h5>
                    <div className="space-y-1 max-h-32 overflow-auto">
                      {uploadFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span className="truncate">{file.name}</span>
                          <span className="text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>💡 {language === 'pt-BR' ? 'Dica' : 'Tip'}:</strong>
                  </p>
                  <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                    <li>• {language === 'pt-BR' ? 'Tamanho máximo: 10MB por arquivo' : 'Maximum size: 10MB per file'}</li>
                    <li>• {language === 'pt-BR' ? 'Formatos: Imagens, PDFs, Documentos' : 'Formats: Images, PDFs, Documents'}</li>
                    <li>• {language === 'pt-BR' ? 'Miniaturas automáticas para visualização' : 'Automatic thumbnails for preview'}</li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowDocumentUpload(false);
                    setUploadFiles([]);
                  }}
                >
                  {t('cancel')}
                </Button>
                <Button 
                  onClick={uploadPersonalDocuments}
                  disabled={uploadFiles.length === 0 || documentsLoading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {documentsLoading ? 
                    `⏳ ${language === 'pt-BR' ? 'Enviando...' : 'Uploading...'}` : 
                    `📤 ${language === 'pt-BR' ? 'Enviar' : 'Upload'} ${uploadFiles.length > 0 ? `(${uploadFiles.length})` : ''}`
                  }
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Installation Docs Modals */}
      {showInstallationUploadModal && (
        <Dialog open={showInstallationUploadModal} onOpenChange={setShowInstallationUploadModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                📤 {language === 'pt-BR' ? 'Upload de Arquivos de Instalação' : 'Upload Installation Files'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="installation-files">
                  {language === 'pt-BR' ? 'Selecionar Arquivos' : 'Select Files'}
                </Label>
                <Input
                  id="installation-files"
                  type="file"
                  multiple
                  accept="image/*,video/*,.pdf,.doc,.docx"
                  onChange={(e) => setInstallationUploadFiles(Array.from(e.target.files))}
                  className="mt-1"
                />
              </div>
              
              {installationUploadFiles.length > 0 && (
                <div className="space-y-2">
                  <Label>{language === 'pt-BR' ? 'Arquivos Selecionados:' : 'Selected Files:'}</Label>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {installationUploadFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm truncate">{file.name}</span>
                        <span className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {
                  setShowInstallationUploadModal(false);
                  setInstallationUploadFiles([]);
                }}>
                  {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button 
                  onClick={async () => {
                    // Upload logic would go here
                    toast.success(language === 'pt-BR' ? 'Arquivos enviados!' : 'Files uploaded!');
                    setShowInstallationUploadModal(false);
                    setInstallationUploadFiles([]);
                  }}
                  disabled={installationUploadFiles.length === 0}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {language === 'pt-BR' ? 'Enviar' : 'Upload'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showNewInstallationFolderModal && (
        <Dialog open={showNewInstallationFolderModal} onOpenChange={setShowNewInstallationFolderModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                📁 {language === 'pt-BR' ? 'Nova Pasta de Instalação' : 'New Installation Folder'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="folder-name">
                  {language === 'pt-BR' ? 'Nome da Pasta' : 'Folder Name'}
                </Label>
                <Input
                  id="folder-name"
                  value={newInstallationFolderName}
                  onChange={(e) => setNewInstallationFolderName(e.target.value)}
                  placeholder={language === 'pt-BR' ? 'Digite o nome da pasta...' : 'Enter folder name...'}
                  className="mt-1"
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {
                  setShowNewInstallationFolderModal(false);
                  setNewInstallationFolderName('');
                }}>
                  {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button 
                  onClick={async () => {
                    if (newInstallationFolderName.trim()) {
                      // Create folder logic would go here
                      toast.success(language === 'pt-BR' ? 'Pasta criada!' : 'Folder created!');
                      setShowNewInstallationFolderModal(false);
                      setNewInstallationFolderName('');
                    }
                  }}
                  disabled={!newInstallationFolderName.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {language === 'pt-BR' ? 'Criar' : 'Create'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Installation Docs Modals */}
      {/* New Installation Folder Modal */}
      <Dialog open={showNewInstallationFolderModal} onOpenChange={(open) => {
        console.log('📁 Installation folder modal onOpenChange:', open);
        setShowNewInstallationFolderModal(open);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              📁 {language === 'pt-BR' ? 'Nova Pasta de Instalação' : 'New Installation Folder'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                <strong>📋 Debug:</strong> Modal está aberto - showNewInstallationFolderModal: {showNewInstallationFolderModal.toString()}
              </p>
              <p className="text-sm text-green-800 mt-1">
                <strong>📋 {language === 'pt-BR' ? 'Organização Hierárquica' : 'Hierarchical Organization'}</strong> - 
                {currentInstallationFolderId ? 
                  (language === 'pt-BR' ? `Criando pasta dentro de "${installationFolders.find(f => f.id === currentInstallationFolderId)?.name || 'Pasta'}"` : 
                   `Creating folder inside "${installationFolders.find(f => f.id === currentInstallationFolderId)?.name || 'Folder'}"`) :
                  (language === 'pt-BR' ? 'Criando pasta na raiz' : 'Creating folder at root level')
                }
              </p>
            </div>
            
            <div>
              <Label>{language === 'pt-BR' ? 'Nome da Pasta' : 'Folder Name'} *</Label>
              <Input 
                value={newInstallationFolderName}
                onChange={(e) => setNewInstallationFolderName(e.target.value)}
                placeholder={language === 'pt-BR' ? 'Ex: Instalação de Janelas' : 'Ex: Window Installation'}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newInstallationFolderName.trim()) {
                    createInstallationFolder();
                  }
                }}
              />
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>💡 {language === 'pt-BR' ? 'Sugestões de Pastas' : 'Folder Suggestions'}:</strong>
              </p>
              <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                <li>• {language === 'pt-BR' ? 'Instalação de Janelas' : 'Window Installation'}</li>
                <li>• {language === 'pt-BR' ? 'Instalação de Portas' : 'Door Installation'}</li>
                <li>• {language === 'pt-BR' ? 'Ferramentas e Equipamentos' : 'Tools and Equipment'}</li>
                <li>• {language === 'pt-BR' ? 'Segurança e Procedimentos' : 'Safety and Procedures'}</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                console.log('📁 Cancel installation folder modal');
                setShowNewInstallationFolderModal(false);
                setNewInstallationFolderName('');
              }}
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={() => {
                console.log('📁 Create installation folder clicked');
                createInstallationFolder();
              }}
              disabled={!newInstallationFolderName.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              📁 {language === 'pt-BR' ? 'Criar Pasta' : 'Create Folder'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Installation Upload Modal */}
      <Dialog open={showInstallationUploadModal} onOpenChange={setShowInstallationUploadModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              📤 {language === 'pt-BR' ? 'Upload Materiais de Instalação' : 'Upload Installation Materials'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>📤 {language === 'pt-BR' ? 'Upload Local' : 'Upload Location'}</strong> - 
                {currentInstallationFolderId ? 
                  (language === 'pt-BR' ? `Enviando para pasta: "${installationFolders.find(f => f.id === currentInstallationFolderId)?.name || 'Pasta'}"` : 
                   `Uploading to folder: "${installationFolders.find(f => f.id === currentInstallationFolderId)?.name || 'Folder'}"`) :
                  (language === 'pt-BR' ? 'Enviando para a raiz' : 'Uploading to root level')
                }
              </p>
              <p className="text-sm text-blue-700 mt-1">
                {language === 'pt-BR' ? 'Selecione vídeos, PDFs e imagens para treinamento' : 'Select videos, PDFs and images for training'}
              </p>
            </div>

            <div>
              <Label>{language === 'pt-BR' ? 'Selecionar Arquivos' : 'Select Files'}</Label>
              <Input 
                type="file"
                multiple
                accept="video/*,image/*,.pdf,.doc,.docx,.txt"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setInstallationUploadFiles(files);
                }}
                className="mt-2"
              />
              {installationUploadFiles.length > 0 && (
                <div className="mt-2 text-xs text-gray-600">
                  {installationUploadFiles.length} {language === 'pt-BR' ? 'arquivo(s) selecionado(s)' : 'file(s) selected'}
                </div>
              )}
            </div>

            {/* Files Preview */}
            {installationUploadFiles.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-32 overflow-auto">
                <h5 className="font-medium text-gray-800 mb-2">
                  📋 {language === 'pt-BR' ? 'Arquivos' : 'Files'}:
                </h5>
                <div className="space-y-1">
                  {installationUploadFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between text-xs">
                      <span className="truncate">{file.name}</span>
                      <span className="text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>💡 {language === 'pt-BR' ? 'Dica' : 'Tip'}:</strong>
              </p>
              <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                <li>• {language === 'pt-BR' ? 'Vídeos: MP4, AVI, MOV para demonstrações' : 'Videos: MP4, AVI, MOV for demonstrations'}</li>
                <li>• {language === 'pt-BR' ? 'PDFs: Manuais e especificações técnicas' : 'PDFs: Manuals and technical specifications'}</li>
                <li>• {language === 'pt-BR' ? 'Imagens: Fotos de instalações e diagramas' : 'Images: Installation photos and diagrams'}</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowInstallationUploadModal(false);
                setInstallationUploadFiles([]);
              }}
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={uploadInstallationFiles}
              disabled={installationUploadFiles.length === 0 || installationLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {installationLoading ? 
                `⏳ ${language === 'pt-BR' ? 'Enviando...' : 'Uploading...'}` : 
                `📤 ${language === 'pt-BR' ? 'Upload' : 'Upload'} ${installationUploadFiles.length > 0 ? `(${installationUploadFiles.length})` : ''}`
              }
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Daily Work URL Configuration Modal */}
      <Dialog open={showDailyWorkUpload} onOpenChange={setShowDailyWorkUpload}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🔗 {language === 'pt-BR' ? 'Configurar Link Excel - Trabalhos Diários' : 'Configure Excel Link - Daily Work'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="font-semibold text-blue-800 mb-2">
                🔗 {language === 'pt-BR' ? 'Como Obter Link Correto' : 'How to Get Correct Link'}
              </h5>
              <div className="text-sm text-blue-700 space-y-2">
                <div>
                  <strong>SharePoint (RBA Atlanta):</strong>
                  <ul className="text-xs ml-4 mt-1">
                    <li>• Copie o link de compartilhamento do arquivo</li>
                    <li>• O sistema converte automaticamente para download</li>
                    <li>• Formato: https://rbaatlanta-my.sharepoint.com/:x:/g/...</li>
                  </ul>
                </div>
                <div>
                  <strong>Google Sheets:</strong>
                  <ul className="text-xs ml-4 mt-1">
                    <li>• Arquivo → Download → Microsoft Excel (.xlsx)</li>
                    <li>• Ou use: Arquivo → Compartilhar → Publicar na web</li>
                  </ul>
                </div>
                <div>
                  <strong>OneDrive Personal:</strong>
                  <ul className="text-xs ml-4 mt-1">
                    <li>• Clique com botão direito → "Gerar link de download"</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <h5 className="font-semibold text-green-800 mb-1">
                📋 {language === 'pt-BR' ? 'Colunas Obrigatórias do Excel' : 'Required Excel Columns'}
              </h5>
              <div className="text-xs text-green-700 grid grid-cols-2 gap-1">
                <div>• Store Location</div>
                <div>• Account Name</div>
                <div>• Primary Resource Name</div>
                <div>• Contact: Full Name</div>
                <div>• Order Number</div>
                <div>• Order Owner</div>
                <div>• Payment Type</div>
                <div>• Amount Due</div>
                <div>• Installation Date</div>
                <div>• Load City</div>
                <div>• Door</div>
                <div>• Time</div>
              </div>
            </div>

            <div>
              <Label>{language === 'pt-BR' ? 'URL do Arquivo Excel' : 'Excel File URL'}</Label>
              {dailyWorkConfig?.excel_url && (
                <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                  <strong>🔗 URL Configurada:</strong>
                  <div className="text-xs text-gray-600 truncate">{dailyWorkConfig.excel_url}</div>
                  <div className="text-xs text-gray-500">
                    {language === 'pt-BR' ? 'Última atualização:' : 'Last updated:'} {
                      dailyWorkConfig.last_updated ? 
                        formatNyDateTime(dailyWorkConfig.last_updated) :
                        'N/A'
                    }
                  </div>
                </div>
              )}
              <Input 
                type="url"
                placeholder="https://docs.google.com/spreadsheets/d/.../export?format=xlsx"
                value={dailyWorkFile || ''}
                onChange={(e) => {
                  setDailyWorkFile(e.target.value);
                }}
                className="mt-2"
              />
              <div className="mt-2 text-xs text-gray-600">
                💡 {language === 'pt-BR' ? 
                  'Cole o link direto para download do Excel (Google Sheets, OneDrive, etc.)' :
                  'Paste the direct download link for Excel (Google Sheets, OneDrive, etc.)'
                }
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                <strong>🔄 {language === 'pt-BR' ? 'Sincronização Automática' : 'Automatic Sync'}:</strong> {language === 'pt-BR' ? 
                  'Configure uma vez e o sistema pode baixar automaticamente o arquivo atualizado diariamente.' :
                  'Configure once and the system can automatically download the updated file daily.'
                }
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ {language === 'pt-BR' ? 'Atenção' : 'Warning'}:</strong> {language === 'pt-BR' ? 
                  'Este processamento vai sobrescrever todos os trabalhos diários existentes.' :
                  'This processing will overwrite all existing daily work data.'
                }
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDailyWorkUpload(false);
                setDailyWorkFile(null);
              }}
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={uploadDailyWorkExcel}
              disabled={!dailyWorkFile || dailyWorkLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {dailyWorkLoading ? 
                `⏳ ${language === 'pt-BR' ? 'Baixando e Processando...' : 'Downloading and Processing...'}` : 
                `🔗 ${language === 'pt-BR' ? 'Processar Link' : 'Process Link'}`
              }
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Push Notification Modal removed - using internal notifications only */}

      {/* Modal de Preview de Vídeo - Mobile Optimized */}
      <Dialog open={!!previewingVideo} onOpenChange={() => setPreviewingVideo(null)}>
        <DialogContent className="max-w-sm sm:max-w-4xl max-h-[90vh] overflow-y-auto mobile-modal">
          <DialogHeader className="mobile-modal-header">
            <DialogTitle className="flex items-center gap-2 mobile-title text-center">
              🎬 {previewingVideo?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mobile-modal-content">
            <div className="bg-black rounded-lg overflow-hidden">
              <video 
                controls
                autoPlay
                muted
                playsInline
                className="w-full h-auto max-h-64 sm:max-h-96"
                style={{ 
                  filter: 'contrast(1.1) brightness(1.05) saturate(1.1)',
                  imageRendering: 'crisp-edges'
                }}
              >
                <source src={previewingVideo?.url} type="video/mp4" />
              </video>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mobile-spacing-md">
              <h4 className="font-medium text-blue-800 mb-2 mobile-subtitle">ℹ️ Informações:</h4>
              <div className="text-sm text-blue-700 space-y-1 mobile-text">
                <p><strong>Nome:</strong> {previewingVideo?.name}</p>
                <p><strong>Como aparece:</strong> Este vídeo pode ser selecionado aleatoriamente na entrada do site</p>
                <p><strong>Duração:</strong> Toca por completo, usuário pode pular com botão "⏭️ Pular"</p>
              </div>
            </div>
            
            <div className="flex justify-center mobile-modal-footer">
              <Button 
                onClick={() => setPreviewingVideo(null)} 
                variant="outline"
                className="w-full sm:w-auto mobile-btn-compact mobile-touch-target"
              >
                ✅ Fechar Preview
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Ticket Rápido (Home) */}
      <Dialog open={showQuickTicketModal} onOpenChange={(open) => {
        setShowQuickTicketModal(open);
        // Reset campos quando modal fechar
        if (!open) {
          setQuickTicketForm({ title: '', message: '' });
          setQuickTicketFiles([]);
        }
      }}>
        <DialogContent className="max-w-md mobile-modal keyboard-aware-scroll">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 mobile-title">
              🎫 {language === 'pt-BR' ? 'Novo Ticket de Suporte' : 'New Support Ticket'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mobile-modal-content">
            <div>
              <Label className="mobile-text">{language === 'pt-BR' ? 'Título *' : 'Title *'}</Label>
              <Input 
                value={quickTicketForm.title}
                onChange={(e) => setQuickTicketForm(prev => ({...prev, title: e.target.value}))}
                placeholder={language === 'pt-BR' ? 'Descreva o problema brevemente...' : 'Briefly describe the issue...'}
                className="mt-1 mobile-input"
              />
            </div>
            
            <div>
              <Label className="mobile-text">{language === 'pt-BR' ? 'Descrição (Opcional)' : 'Description (Optional)'}</Label>
              <Textarea 
                value={quickTicketForm.message}
                onChange={(e) => setQuickTicketForm(prev => ({...prev, message: e.target.value}))}
                placeholder={language === 'pt-BR' ? 'Detalhe o problema ou dúvida...' : 'Detail the problem or question...'}
                className="mt-1 h-20 mobile-textarea"
              />
            </div>
            
            <div>
              <Label className="mobile-text">{language === 'pt-BR' ? 'Anexos (Opcional)' : 'Attachments (Optional)'}</Label>
              <Input
                type="file"
                multiple
                accept="image/*,application/pdf,.txt,.heic,.heif"
                onChange={(e) => setQuickTicketFiles(Array.from(e.target.files || []))}
                className="mobile-input"
              />
              {quickTicketFiles.length > 0 && (
                <div className="text-xs text-gray-600 mt-1">
                  {language === 'pt-BR' ? `${quickTicketFiles.length} arquivo(s) selecionado(s)` : `${quickTicketFiles.length} file(s) selected`}
                </div>
              )}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 justify-end mobile-modal-footer">
              <Button 
                onClick={() => {
                  setShowQuickTicketModal(false);
                  setQuickTicketForm({ title: '', message: '' }); // Reset campos ao cancelar
                  setQuickTicketFiles([]); // Reset arquivos ao cancelar
                }}
                variant="outline"
                className="mobile-btn-compact mobile-touch-target"
              >
                ❌ {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button 
                onClick={createQuickTicket}
                disabled={!quickTicketForm.title.trim()}
                className="bg-blue-600 hover:bg-blue-700 mobile-btn-compact mobile-touch-target"
              >
                🎫 {language === 'pt-BR' ? 'Criar Ticket' : 'Create Ticket'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Internal Notification Modal removido */}

      {/* Modal de Notificação - Design delicado + Tickets (APENAS para tickets e notificações antigas) */}
      <Dialog open={showOldNotificationModal && notificationView} onOpenChange={() => setShowOldNotificationModal(false)}>
        <DialogContent className="max-w-sm sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-center text-lg font-medium text-gray-800">
              {notificationView?.type === 'ticket_alert' ? '🎫 Ticket em Aberto' : '💌 Mensagem'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 p-2">
            <div className="text-center space-y-3">
              <h3 className="text-base font-medium text-gray-700 leading-relaxed">
                {notificationView?.title || notificationView?.titulo}
              </h3>
              <div className={`border rounded-xl p-4 shadow-sm ${
                notificationView?.type === 'ticket_alert' 
                  ? 'bg-gradient-to-br from-red-50 to-rose-50 border-red-100' 
                  : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100'
              }`}>
                <p className={`text-sm font-medium leading-relaxed ${
                  notificationView?.type === 'ticket_alert' ? 'text-red-800' : 'text-blue-800'
                }`}>
                  {(() => {
                    const _msgs = notificationView?.messages;
                    const _lastThreadText = Array.isArray(_msgs) && _msgs.length > 0
                      ? (_msgs[_msgs.length - 1]?.text || _msgs[0]?.text)
                      : '';
                    const fullMessage = notificationView?.message || notificationView?.mensagem || _lastThreadText || '';
                    
                    // Para tickets, separar a descrição se estiver no formato esperado
                    if (fullMessage.includes('📝 Descrição:')) {
                      const parts = fullMessage.split('📝 Descrição:');
                      return (
                        <div className="space-y-3">
                          <div>{parts[0].trim()}</div>
                          <div className="border-t pt-3 mt-3">
                            <div className="font-semibold text-xs mb-1">📝 DESCRIÇÃO DO TICKET:</div>
                            <div className="bg-white bg-opacity-50 p-3 rounded border italic">
                              {parts[1].trim()}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // Para outras notificações, mostrar mensagem normal
                    return fullMessage;
                  })()}
                </p>
              </div>
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                <span>👤 {notificationView?.from_user || notificationView?.messages?.[0]?.author || notificationView?.pm_name}</span>
                <span>•</span>
                <span>{notificationView?.created_at && new Date(notificationView.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}</span>
              </div>
            </div>
            
            <div className="flex flex-col gap-2 pt-2">
              {notificationView?.type === 'ticket_alert' && (
                <Button 
                  onClick={() => {
                    // Ir para aba Suporte
                    setActiveTab('admin');
                    setAdminSubTab('suporte');
                    setShowOldNotificationModal(false);
                    showNextNotification();
                  }}
                  className="w-full bg-red-600 hover:bg-red-700 text-white rounded-lg py-3 font-medium shadow-sm"
                >
                  🎫 Ir para Suporte
                </Button>
              )}
              
              <Button 
                onClick={async () => {
                  if (notificationView && notificationView.type !== 'ticket_alert') {
                    await openMyNotification(notificationView);
                  }
                  showNextNotification();
                }}
                className={`w-full rounded-lg py-3 font-medium shadow-sm ${
                  notificationView?.type === 'ticket_alert' 
                    ? 'bg-gray-500 hover:bg-gray-600 text-white'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                }`}
              >
                {notificationView?.type === 'ticket_alert' ? '✨ OK' : '✨ OK'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Edição Individual de PDF Múltiplo */}
      <Dialog open={showMultiplePdfEditModal} onOpenChange={setShowMultiplePdfEditModal}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-hidden p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="text-xl font-bold">
              ✏️ Editar Labor Bill: {editingMultiplePdfIndex !== null && importMultipleEditData[editingMultiplePdfIndex]?.filename}
            </DialogTitle>
          </DialogHeader>
          
          {/* Header com Comparativo - FIXO NO TOPO */}
          {editingMultiplePdfIndex !== null && importMultipleEditData[editingMultiplePdfIndex] && (
            <div className="pdf-modal-sticky-header">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div className="bg-white p-3 rounded-lg border shadow-sm">
                  <div className="text-sm text-gray-600 font-medium">📄 Total do PDF</div>
                  <div className="text-2xl font-bold text-blue-600">
                    ${(importMultipleEditData[editingMultiplePdfIndex].data.grand_total || 0).toFixed(2)}
                  </div>
                </div>
                <div className="bg-white p-3 rounded-lg border shadow-sm">
                  <div className="text-sm text-gray-600 font-medium">🧮 Total Calculado</div>
                  <div className="text-2xl font-bold text-green-600">
                    ${calculateMultiplePdfTotal().toFixed(2)}
                  </div>
                </div>
                <div className="bg-white p-3 rounded-lg border shadow-sm">
                  <div className="text-sm text-gray-600 font-medium">⚖️ Diferença</div>
                  <div className={`text-2xl font-bold ${Math.abs((importMultipleEditData[editingMultiplePdfIndex].data.grand_total || 0) - calculateMultiplePdfTotal()) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                    ${Math.abs((importMultipleEditData[editingMultiplePdfIndex].data.grand_total || 0) - calculateMultiplePdfTotal()).toFixed(2)}
                    {Math.abs((importMultipleEditData[editingMultiplePdfIndex].data.grand_total || 0) - calculateMultiplePdfTotal()) < 0.01 ? ' ✅' : ' ⚠️'}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Conteúdo Scrollable */}
          {editingMultiplePdfIndex !== null && importMultipleEditData[editingMultiplePdfIndex] && (
            <div className="pdf-modal-scrollable space-y-6">
              {/* Dados Básicos */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">📋 Informações Básicas</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Data de Instalação</Label>
                    <Input
                      type="date"
                      value={importMultipleEditData[editingMultiplePdfIndex].data.install_date || ''}
                      onChange={(e) => {
                        const newData = [...importMultipleEditData];
                        newData[editingMultiplePdfIndex].data.install_date = e.target.value;
                        setImportMultipleEditData(newData);
                      }}
                    />
                  </div>
                  <div>
                    <Label>Nome do Job</Label>
                    <Input
                      value={importMultipleEditData[editingMultiplePdfIndex].data.job_name || ''}
                      onChange={(e) => {
                        const newData = [...importMultipleEditData];
                        newData[editingMultiplePdfIndex].data.job_name = e.target.value;
                        setImportMultipleEditData(newData);
                      }}
                    />
                  </div>
                  <div>
                    <Label>Número do Pedido</Label>
                    <Input
                      value={importMultipleEditData[editingMultiplePdfIndex].data.order_number || ''}
                      onChange={(e) => {
                        const newData = [...importMultipleEditData];
                        newData[editingMultiplePdfIndex].data.order_number = e.target.value;
                        setImportMultipleEditData(newData);
                      }}
                    />
                  </div>
                  <div>
                    <Label>Código PM</Label>
                    <select 
                      className="border rounded px-3 py-2 w-full" 
                      value={importMultipleEditData[editingMultiplePdfIndex].data.pm_code || ''}
                      onChange={(e) => {
                        const newData = [...importMultipleEditData];
                        newData[editingMultiplePdfIndex].data.pm_code = e.target.value;
                        setImportMultipleEditData(newData);
                      }}
                    >
                      {pmOptions.map((pm) => (
                        <option key={pm} value={pm}>{pm}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Total Windows</Label>
                    <Input
                      type="number"
                      value={importMultipleEditData[editingMultiplePdfIndex].data.total_windows || 0}
                      onChange={(e) => {
                        const newData = [...importMultipleEditData];
                        newData[editingMultiplePdfIndex].data.total_windows = parseInt(e.target.value) || 0;
                        setImportMultipleEditData(newData);
                      }}
                    />
                  </div>
                  <div>
                    <Label>Total Doors</Label>
                    <Input
                      type="number"
                      value={importMultipleEditData[editingMultiplePdfIndex].data.total_doors || 0}
                      onChange={(e) => {
                        const newData = [...importMultipleEditData];
                        newData[editingMultiplePdfIndex].data.total_doors = parseInt(e.target.value) || 0;
                        setImportMultipleEditData(newData);
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Itens Principais */}
              <div className="bg-white border rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold">🔧 Itens Principais</h4>
                  <Button
                    size="sm"
                    onClick={() => openItemSelector('main')}
                  >
                    + Adicionar Item
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {(importMultipleEditData[editingMultiplePdfIndex].data.items || []).map((item, itemIndex) => (
                    <div key={itemIndex} className="grid grid-cols-1 md:grid-cols-5 gap-3 p-3 border rounded">
                      <div className="md:col-span-2">
                        <Label>Descrição</Label>
                        <Input
                          value={item.description || ''}
                          onChange={(e) => updateMultiplePdfItem(itemIndex, 'description', e.target.value)}
                          placeholder="Ex: Unit up to 120 UI"
                        />
                      </div>
                      <div>
                        <Label>Qtd</Label>
                        <Input
                          type="number"
                          value={item.qty || 1}
                          onChange={(e) => updateMultiplePdfItem(itemIndex, 'qty', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Preço Unit.</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.unit_price || 0}
                          onChange={(e) => updateMultiplePdfItem(itemIndex, 'unit_price', e.target.value)}
                        />
                      </div>
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <Label>Total</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={item.total || 0}
                            onChange={(e) => updateMultiplePdfItem(itemIndex, 'total', e.target.value)}
                          />
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newData = [...importMultipleEditData];
                            newData[editingMultiplePdfIndex].data.items = newData[editingMultiplePdfIndex].data.items.filter((_, i) => i !== itemIndex);
                            setImportMultipleEditData(newData);
                          }}
                          className="text-red-600"
                        >
                          🗑️
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Itens Extras */}
              <div className="bg-white border rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold">⭐ Itens Extras</h4>
                  <Button
                    size="sm"
                    onClick={() => openItemSelector('extra')}
                  >
                    + Adicionar Item Extra
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {(importMultipleEditData[editingMultiplePdfIndex].data.extra_items || []).map((item, itemIndex) => (
                    <div key={itemIndex} className="grid grid-cols-1 md:grid-cols-5 gap-3 p-3 border rounded">
                      <div className="md:col-span-2">
                        <Label>Descrição</Label>
                        <Input
                          value={item.description || ''}
                          onChange={(e) => updateMultiplePdfExtraItem(itemIndex, 'description', e.target.value)}
                          placeholder="Ex: Trip Charge 50"
                        />
                      </div>
                      <div>
                        <Label>Qtd</Label>
                        <Input
                          type="number"
                          value={item.qty || 1}
                          onChange={(e) => updateMultiplePdfExtraItem(itemIndex, 'qty', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Preço Unit.</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.unit_price || 0}
                          onChange={(e) => updateMultiplePdfExtraItem(itemIndex, 'unit_price', e.target.value)}
                        />
                      </div>
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <Label>Total</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={item.total || 0}
                            onChange={(e) => updateMultiplePdfExtraItem(itemIndex, 'total', e.target.value)}
                          />
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newData = [...importMultipleEditData];
                            newData[editingMultiplePdfIndex].data.extra_items = newData[editingMultiplePdfIndex].data.extra_items.filter((_, i) => i !== itemIndex);
                            setImportMultipleEditData(newData);
                          }}
                          className="text-red-600"
                        >
                          🗑️
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notas */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <Label>📝 Notas</Label>
                <textarea
                  className="border rounded px-3 py-2 w-full h-20 mt-1"
                  value={importMultipleEditData[editingMultiplePdfIndex].data.notes || ''}
                  onChange={(e) => {
                    const newData = [...importMultipleEditData];
                    newData[editingMultiplePdfIndex].data.notes = e.target.value;
                    setImportMultipleEditData(newData);
                  }}
                  placeholder="Informações adicionais..."
                />
              </div>

              {/* Botões de Ação */}
              <div className="flex justify-between items-center pt-4 border-t">
                <div className="text-sm text-gray-600">
                  PDFs restantes para editar: {importMultipleEditData.filter(f => f.success).length - 1}
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={closeMultiplePdfEditor}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={saveAndImportMultiplePdf}
                    disabled={importCommitLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {importCommitLoading ? "Importando..." : "💾 Salvar e Importar"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Itens */}
      <Dialog open={showItemSelectionModal} onOpenChange={setShowItemSelectionModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              📋 Selecionar {itemSelectionMode === 'main' ? 'Item Principal' : 'Item Extra'} da Lista
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Busca Funcional */}
            <div>
              <Input
                placeholder="🔍 Buscar item por descrição..."
                value={itemSearchTerm}
                onChange={(e) => setItemSearchTerm(e.target.value)}
                className="w-full"
              />
              {itemSearchTerm && (
                <div className="text-sm text-gray-600 mt-1">
                  Buscando por: "{itemSearchTerm}"
                </div>
              )}
            </div>
            
            {/* Lista de Itens Agrupada */}
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {/* Itens Principais com Filtro de Busca */}
              {itemSelectionMode === 'main' && (
                <>
                  {leftColumnSubgroups
                    .map(subgroup => ({
                      ...subgroup,
                      items: filterItemsBySearch(subgroup.items.map(item => ({...item, subgroup: subgroup.title})), itemSearchTerm)
                    }))
                    .filter(subgroup => subgroup.items.length > 0) // Só mostrar grupos que têm itens após filtro
                    .map((subgroup, sgIndex) => (
                    <div key={`left-${sgIndex}`} className="border rounded-lg p-4">
                      <h4 className="font-semibold text-sm mb-3 text-blue-600">{subgroup.title}</h4>
                      <div className="grid gap-2">
                        {subgroup.items.map((item, itemIndex) => (
                          <div
                            key={`left-${sgIndex}-${itemIndex}`}
                            className="flex items-center justify-between p-3 border rounded hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => selectItemFromList(item)}
                          >
                            <div className="flex-1">
                              <div className="font-medium">{item.description}</div>
                              <div className="text-xs text-gray-600">{subgroup.title}</div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-green-600">${item.unit_price.toFixed(2)}</div>
                              <div className="text-xs text-gray-500">unidade</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  
                  {rightColumnSubgroups
                    .map(subgroup => ({
                      ...subgroup,
                      items: filterItemsBySearch(subgroup.items.map(item => ({...item, subgroup: subgroup.title})), itemSearchTerm)
                    }))
                    .filter(subgroup => subgroup.items.length > 0) // Só mostrar grupos que têm itens após filtro
                    .map((subgroup, sgIndex) => (
                    <div key={`right-${sgIndex}`} className="border rounded-lg p-4">
                      <h4 className="font-semibold text-sm mb-3 text-purple-600">{subgroup.title}</h4>
                      <div className="grid gap-2">
                        {subgroup.items.map((item, itemIndex) => (
                          <div
                            key={`right-${sgIndex}-${itemIndex}`}
                            className="flex items-center justify-between p-3 border rounded hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => selectItemFromList(item)}
                          >
                            <div className="flex-1">
                              <div className="font-medium">{item.description}</div>
                              <div className="text-xs text-gray-600">{subgroup.title}</div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-green-600">${item.unit_price.toFixed(2)}</div>
                              <div className="text-xs text-gray-500">unidade</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  
                  {/* Mensagem quando filtro não retorna resultados */}
                  {itemSearchTerm && 
                   leftColumnSubgroups.concat(rightColumnSubgroups)
                     .every(sg => filterItemsBySearch(sg.items.map(item => ({...item, subgroup: sg.title})), itemSearchTerm).length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      <div className="text-4xl mb-2">🔍</div>
                      <div>Nenhum item encontrado para "{itemSearchTerm}"</div>
                      <div className="text-sm">Tente outros termos de busca</div>
                    </div>
                  )}
                </>
              )}
              
              {/* Itens Extras com Filtro de Busca */}
              {itemSelectionMode === 'extra' && (
                <>
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold text-sm mb-3 text-orange-600">Itens Extras Comuns</h4>
                    <div className="grid gap-2">
                      {filterItemsBySearch(availableItems.filter(item => item.category === 'extra'), itemSearchTerm)
                        .map((item, itemIndex) => (
                        <div
                          key={`extra-${itemIndex}`}
                          className="flex items-center justify-between p-3 border rounded hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => selectItemFromList(item)}
                        >
                          <div className="flex-1">
                            <div className="font-medium">{item.description}</div>
                            <div className="text-xs text-gray-600">{item.subgroup}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-green-600">${item.unit_price.toFixed(2)}</div>
                            <div className="text-xs text-gray-500">{item.unit_price > 0 ? 'valor fixo' : 'valor variável'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Mensagem quando filtro de extras não retorna resultados */}
                    {itemSearchTerm && 
                     filterItemsBySearch(availableItems.filter(item => item.category === 'extra'), itemSearchTerm).length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <div className="text-4xl mb-2">🔍</div>
                        <div>Nenhum item extra encontrado para "{itemSearchTerm}"</div>
                        <div className="text-sm">Tente outros termos de busca</div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            
            {/* Rodapé */}
            <div className="flex justify-between items-center pt-4 border-t">
              <div className="text-sm text-gray-600">
                Clique em um item para adicionar ao Labor Bill
                {itemSearchTerm && (
                  <span className="ml-2 text-blue-600">
                    • Filtrado por "{itemSearchTerm}"
                  </span>
                )}
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  setShowItemSelectionModal(false);
                  setItemSearchTerm(''); // Limpar busca ao fechar
                }}
              >
                Fechar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Verificação de Duplicatas */}
      <Dialog open={showDuplicateModal} onOpenChange={setShowDuplicateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-lg font-bold">
              ⚠️ Trabalhos Duplicados Encontrados
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {duplicateInfo && (
              <div className="text-center space-y-4">
                <div className="text-4xl">🔄</div>
                <div>
                  <div className="font-medium mb-2">
                    {duplicateInfo.duplicate_count} trabalhos já existem para {duplicateInfo.work_date}
                  </div>
                  <div className="text-sm text-gray-600">
                    Como você deseja proceder?
                  </div>
                </div>
                
                {/* Lista dos duplicatas encontrados */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-h-40 overflow-y-auto">
                  <div className="font-semibold text-yellow-800 text-sm mb-2">📋 Trabalhos duplicados:</div>
                  <div className="space-y-1 text-xs text-yellow-700">
                    {duplicateInfo.duplicates?.map((dup, index) => (
                      <div key={index} className="flex justify-between">
                        <span>{dup.customer_name}</span>
                        <span>{dup.order_number}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Opções de ação */}
            <div className="space-y-3">
              <Button
                onClick={handleDuplicatesReplace}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                🔄 Substituir Existentes
                <div className="text-xs opacity-75 mt-1">Remove trabalhos antigos e adiciona novos</div>
              </Button>
              
              <Button
                onClick={handleDuplicatesContinue}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                ➕ Continuar e Manter Ambos
                <div className="text-xs opacity-75 mt-1">Mantém existentes e adiciona novos</div>
              </Button>
              
              <Button
                variant="outline"
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateInfo(null);
                  setPendingProcessData(null);
                  toast.info('Processamento cancelado');
                }}
                className="w-full"
              >
                Cancelar
              </Button>
            </div>
            
            {/* Info sobre replicação */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
              <div className="text-blue-800 text-sm">
                📡 <strong>{language === 'pt-BR' ? 'Importante:' : 'Important:'}</strong> Sua escolha afetará todos os usuários PM também
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Simples para Colar Dados do Excel - Layout melhorado */}
      <Dialog open={showExcelDataPasteModal} onOpenChange={setShowExcelDataPasteModal}>
        <DialogContent className="max-w-3xl excel-paste-modal" style={{ 
          position: 'fixed',
          top: '45%',
          transform: 'translateX(-50%) translateY(-50%)',
          maxHeight: '85vh',
          overflowY: 'hidden'
        }}>
          <DialogHeader className="border-b pb-4 mb-4 flex-shrink-0">
            <DialogTitle className="text-xl font-bold text-indigo-800 flex items-center gap-2">
              📋 Importar Logística do Excel
            </DialogTitle>
            <p className="text-sm text-gray-600 mt-2">
              Cole os dados do Excel para adicionar informações de logística aos trabalhos do calendário
            </p>
          </DialogHeader>
          
          {/* Conteúdo com scroll interno */}
          <div 
            className="space-y-4 px-1 overflow-y-auto flex-1" 
            style={{ 
              maxHeight: 'calc(90vh - 250px)',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {/* Seleção de Data - Layout melhorado */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
              <Label className="text-sm font-semibold text-blue-800 mb-2 block">
                📅 Data dos Trabalhos
              </Label>
              <Input
                type="date"
                value={excelWorkDate}
                onChange={(e) => setExcelWorkDate(e.target.value)}
                className="text-sm bg-white border-blue-200 focus:border-blue-400 focus:ring-blue-100"
              />
              <p className="text-xs text-blue-600 mt-2">
                ⚠️ Certifique-se de selecionar a data correta dos trabalhos
              </p>
            </div>
            
            {/* Instruções simples - Mantidas mas com melhor espaçamento */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="font-semibold text-green-800 mb-2">📋 Como colar:</div>
              <ol className="list-decimal list-inside space-y-1 text-green-700 text-sm">
                <li>Abra o Excel Online em outra aba</li>
                <li>Faça login: <strong>pmhomeremodeling@gmail.com</strong></li>
                <li>Selecione <strong>apenas os dados</strong> (sem cabeçalho)</li>
                <li>Copie com <strong>Ctrl+C</strong></li>
                <li>Cole no campo abaixo com <strong>Ctrl+V</strong></li>
                <li>✅ Sistema importa apenas equipes <strong>PM Home Remodeling</strong></li>
              </ol>
            </div>
            
            {/* Área de Texto - Layout melhorado */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                📊 Dados do Excel
                <span className="text-xs font-normal text-gray-500">
                  (Cole aqui os dados copiados)
                </span>
              </Label>
              <textarea
                placeholder="Cole aqui os dados copiados do Excel Online...

Exemplo:
Store Location  Primary Resource Name   Order Number    Installation Date       Load City       Door    Time
0930 - Atlanta  PM Home Remodeling 4    04406982        10/7/2025       Lawrenceville   4       6:30"
                value={excelPastedData}
                onChange={(e) => setExcelPastedData(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded font-mono text-xs bg-gray-50 focus:border-green-400 focus:ring-green-100 resize-none input-focus-scroll"
                style={{ 
                  lineHeight: '1.4',
                  height: '200px',
                  maxHeight: '200px',
                  fontSize: '16px'
                }}
              />
              {/* Contador de dados */}
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span>
                  📊 {excelPastedData ? `${excelPastedData.split('\n').filter(line => line.trim()).length} linhas` : 'Nenhum dado'}
                </span>
                <span>
                  📋 {excelPastedData ? `${excelPastedData.split('\n')[0]?.split('\t').length || 0} colunas` : ''}
                </span>
                {excelPastedData && (
                  <span className="text-green-600 font-medium">
                    ✅ Dados prontos para processar
                  </span>
                )}
              </div>
            </div>
          </div>
            
          {/* Botões - Layout melhorado e fixo no fundo */}
          <div className="flex gap-3 pt-6 border-t bg-gray-50 p-6 -mx-6 -mb-6 rounded-b-lg flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowExcelDataPasteModal(false);
                setExcelPastedData('');
                setExcelWorkDate('');
              }}
              className="flex-1 h-12 text-sm font-medium border-gray-300 hover:bg-gray-100"
            >
              ❌ Cancelar
            </Button>
            <Button
              onClick={() => processExcelPastedData()}
              disabled={!excelPastedData.trim() || !excelWorkDate}
              className="flex-1 h-12 text-sm font-semibold bg-green-600 hover:bg-green-700 disabled:bg-gray-300"
            >
              {!excelPastedData.trim() || !excelWorkDate ? (
                <>⏳ Aguardando dados...</>
              ) : (
                <>🚀 Processar Dados</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Conflito de Datas */}
      <Dialog open={showDateConflictModal} onOpenChange={setShowDateConflictModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-amber-800 flex items-center gap-2">
              ⚠️ Conflito de Datas Detectado
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              A data selecionada no modal é diferente da data que está no Excel. Qual data você deseja usar para estes trabalhos?
            </p>
            
            {dateConflictInfo && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between p-3 bg-white rounded border border-amber-300">
                  <div>
                    <div className="text-xs text-gray-500">Data Selecionada no Modal:</div>
                    <div className="text-lg font-bold text-indigo-700">
                      {new Date(dateConflictInfo.modal_date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{dateConflictInfo.modal_date}</div>
                  </div>
                  <Button
                    onClick={async () => {
                      toast.info('Usando data do modal...');
                      setShowDateConflictModal(false);
                      await processExcelPastedData(true, dateConflictInfo.modal_date);
                    }}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    ✅ Usar Esta
                  </Button>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-white rounded border border-amber-300">
                  <div>
                    <div className="text-xs text-gray-500">Data do Excel (Coluna 8):</div>
                    <div className="text-lg font-bold text-emerald-700">
                      {dateConflictInfo.excel_date_formatted}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{dateConflictInfo.excel_date}</div>
                  </div>
                  <Button
                    onClick={async () => {
                      toast.info('Usando data do Excel...');
                      setShowDateConflictModal(false);
                      await processExcelPastedData(true, dateConflictInfo.excel_date);
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    ✅ Usar Esta
                  </Button>
                </div>
              </div>
            )}
            
            <div className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded p-3">
              💡 <strong>Dica:</strong> Se você quer que todos os trabalhos sejam agendados para o mesmo dia (independente da data no Excel), use a data do modal.
            </div>
          </div>
          
          <div className="flex gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowDateConflictModal(false);
                setDateConflictInfo(null);
                setPendingExcelData(null);
              }}
              className="flex-1"
            >
              ❌ Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* PDF Loading Modal */}
      <Dialog open={pdfLoading} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm">
          <div className="flex flex-col items-center justify-center py-8">
            <RefreshCw className="h-12 w-12 text-indigo-600 animate-spin mb-4" />
            <p className="text-lg font-semibold text-gray-900">
              {language === 'pt-BR' ? 'Carregando PDF...' : 'Loading PDF...'}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              {language === 'pt-BR' ? 'Aguarde um momento' : 'Please wait'}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={showPDFModal}
        onClose={() => {
          setShowPDFModal(false);
          // Libera o blob da memória (evita acúmulo ao abrir vários PDFs)
          if (pdfModalData.url && pdfModalData.url.startsWith('blob:')) {
            setTimeout(() => window.URL.revokeObjectURL(pdfModalData.url), 1000);
          }
        }}
        pdfUrl={pdfModalData.url}
        title={pdfModalData.title}
        onDownload={() => handlePDFAction(pdfModalData.url, pdfModalData.title, true)}
      />

      {/* Comparar Labor Bill antigo x novo */}
      {compareBill && (
        <OldBillComparisonModal
          key={compareBill.id}
          open={showOldCompareModal}
          onClose={() => setShowOldCompareModal(false)}
          bill={compareBill}
          user={user}
        />
      )}

      {/* Aviso de app nativo desatualizado */}
      <Dialog open={!!updateNotice} onOpenChange={(o) => { if (!o) setUpdateNotice(null); }}>
        <DialogContent className="max-w-sm" data-testid="update-notice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">📲 Atualização disponível</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <p>
              Seu aplicativo está <span className="font-semibold text-red-600">desatualizado</span> (v{updateNotice?.current}).
              A versão mínima recomendada é <span className="font-semibold">v{updateNotice?.min}</span>.
            </p>
            <p>
              {updateNotice?.platform === 'android'
                ? 'Atualize agora pela Google Play para continuar recebendo todas as melhorias.'
                : 'Abra a App Store, busque por "PM Home Remodeling" e toque em Atualizar.'}
            </p>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setUpdateNotice(null)} data-testid="update-notice-later-btn">
              Depois
            </Button>
            {updateNotice?.platform === 'android' && (
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="update-notice-store-btn"
                onClick={() => {
                  window.open('https://play.google.com/store/apps/details?id=com.JohnnyGarbim.pmlaborbills', '_blank');
                  setUpdateNotice(null);
                }}
              >
                Atualizar agora
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Formulários embutidos (sheet minimizável): Completion Checklist / LEAD Form */}
      <EmbeddedFormSheet
        open={!!formSheet}
        title={formSheet?.title || ''}
        url={formSheet?.url || ''}
        onClose={() => setFormSheet(null)}
      />

      {/* Mapa de Schedule (Google Maps) */}
      <ScheduleMapModal
        isOpen={showScheduleMapModal}
        onClose={() => { setShowScheduleMapModal(false); setCompareMapTeams([]); setCompareMapRange(null); }}
        user={user}
        language={language}
        initialTeams={compareMapTeams}
        initialRange={compareMapRange}
      />

      {/* Image Viewer Modal */}


      {/* Service Notes Modal */}
      <ServiceNotesModal
        isOpen={showServiceNotesModal}
        onClose={() => setShowServiceNotesModal(false)}
        notes={serviceNotesData.notes}
        title={serviceNotesData.title}
        language={language}
      />

      <ImageViewerModal 
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        imageUrl={imageModalData.url}
        title={imageModalData.title}
        onDownload={() => {
          const a = document.createElement('a');
          a.href = imageModalData.url;
          a.download = imageModalData.title;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }}
      />

      {/* Office Viewer Modal */}
      <OfficeViewerModal 
        isOpen={showOfficeModal}
        onClose={() => setShowOfficeModal(false)}
        fileUrl={officeModalData.url}
        fileName={officeModalData.fileName}
        onDownload={() => {
          if (officeModalData.itemId) {
            downloadFileManagerFile(officeModalData.itemId, officeModalData.fileName);
          }
          setShowOfficeModal(false);
        }}
      />

      {/* Video Viewer Modal */}
      <VideoViewerModal 
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        videoUrl={videoModalData.url}
        videoTitle={videoModalData.title}
      />

      {/* File Preview Loading Overlay */}
      {filePreviewLoading && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center"
          style={{ zIndex: 99999 }}
        >
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center mb-4">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Carregando arquivo...
              </h3>
              <p className="text-sm text-gray-600 truncate">
                📄 {filePreviewName}
              </p>
            </div>
            
            {/* Barra de progresso */}
            <div className="mb-3">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Progresso</span>
                <span className="font-semibold">{filePreviewProgress}%</span>
              </div>
              <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${filePreviewProgress}%` }}
                ></div>
              </div>
            </div>
            
            <p className="text-xs text-gray-500 text-center">
              {filePreviewProgress < 100 ? '⏳ Aguarde, baixando arquivo...' : '✨ Processando...'}
            </p>
          </div>
        </div>
      )}

      {/* Backup Progress Floating Card */}
      {backupProgress && (backupProgress.is_running || backupProgress.progress > 0) && (
        <div 
          className="fixed bottom-4 right-4 bg-white rounded-lg shadow-2xl border-2 border-green-500 p-4 w-80 animate-slide-up"
          style={{ zIndex: 10000 }}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              {backupProgress.is_running && (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
              )}
              {!backupProgress.is_running && backupProgress.progress === 100 && (
                <span className="text-green-600 text-xl">✅</span>
              )}
              <h4 className="font-semibold text-gray-800">
                {backupProgress.is_running ? 'Backup em Andamento' : 'Backup Concluído'}
              </h4>
            </div>
            <button
              onClick={() => setBackupProgress(null)}
              className="text-gray-400 hover:text-gray-600 text-sm"
              title={backupProgress.is_running ? "Minimizar (backup continua)" : "Fechar"}
            >
              ✕
            </button>
          </div>
          
          <div className="mb-3">
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span className="text-sm">{backupProgress.current_step}</span>
              <span className="font-bold text-green-600">{backupProgress.progress}%</span>
            </div>
            <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${backupProgress.progress}%` }}
              ></div>
            </div>
          </div>
          
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>📦 Enviando para Dropbox (2TB)</span>
            <span className="font-mono">{backupProgress.progress}/100</span>
          </div>
          
          {backupProgress.error && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              ❌ Erro: {backupProgress.error}
            </div>
          )}
        </div>
      )}

      {/* Restore Progress Floating Card */}
      {restoreProgress && (restoreProgress.is_running || restoreProgress.progress > 0) && (
        <div 
          className="fixed bottom-4 right-4 bg-white rounded-lg shadow-2xl border-2 border-blue-500 p-4 w-80 animate-slide-up"
          style={{ zIndex: 10000 }}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              {restoreProgress.is_running && (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              )}
              {!restoreProgress.is_running && restoreProgress.progress === 100 && (
                <span className="text-blue-600 text-xl">✅</span>
              )}
              <h4 className="font-semibold text-gray-800">
                {restoreProgress.is_running ? 'Restauração em Andamento' : 'Restauração Concluída'}
              </h4>
            </div>
            <button
              onClick={() => setRestoreProgress(null)}
              className="text-gray-400 hover:text-gray-600 text-sm"
              title={restoreProgress.is_running ? "Minimizar (restauração continua)" : "Fechar"}
            >
              ✕
            </button>
          </div>
          
          <div className="mb-3">
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span className="text-sm">{restoreProgress.current_step}</span>
              <span className="font-bold text-blue-600">{restoreProgress.progress}%</span>
            </div>
            <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${restoreProgress.progress}%` }}
              ></div>
            </div>
          </div>
          
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>⬇️ Restaurando do Dropbox</span>
            <span className="font-mono">{restoreProgress.progress}/100</span>
          </div>
          
          {restoreProgress.error && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              ❌ Erro: {restoreProgress.error}
            </div>
          )}
        </div>
      )}


      {/* Modal de Seleção de Data do Daily Work */}
      <Dialog open={showDatePickerModal} onOpenChange={setShowDatePickerModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              📅 {language === 'pt-BR' ? 'Selecionar Data' : 'Select Date'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{language === 'pt-BR' ? 'Data dos Trabalhos' : 'Work Date'}</Label>
              <Input
                type="date"
                value={tempSelectedDate}
                onChange={(e) => setTempSelectedDate(e.target.value)}
                className="bg-white text-sm mt-2"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button 
              variant="outline" 
              onClick={() => setShowDatePickerModal(false)}
            >
              {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button 
              onClick={() => {
                setDailyWorkDateFilter(tempSelectedDate);
                setShowDatePickerModal(false);
                loadDailyWork(tempSelectedDate);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {language === 'pt-BR' ? 'Confirmar' : 'Confirm'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal: Vincular Arbitration Existente ao Helper */}
      <Dialog open={showLinkArbitrationModal} onOpenChange={setShowLinkArbitrationModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-lg font-bold text-orange-800 flex items-center gap-2">
              🔍 Buscar Arbitration Existente
            </DialogTitle>
            <p className="text-sm text-gray-600 mt-2">
              Selecione um Arbitration já criado para vincular a <strong>{helperToLinkDoc?.name}</strong>
            </p>
          </DialogHeader>
          
          {/* Busca */}
          <div className="flex-shrink-0 mb-4">
            <Input
              placeholder="Buscar por nome do helper..."
              value={linkDocSearch}
              onChange={(e) => setLinkDocSearch(e.target.value)}
              className="w-full"
            />
          </div>
          
          {/* Lista de Documentos com Scroll */}
          <div className="flex-1 overflow-y-auto space-y-2" style={{ WebkitOverflowScrolling: 'touch' }}>
            {availableArbitrationDocs
              .filter(doc => {
                if (!linkDocSearch.trim()) return true;
                return doc.helper_name?.toLowerCase().includes(linkDocSearch.toLowerCase());
              })
              .map(doc => (
                <div
                  key={doc.id}
                  className="border border-orange-200 rounded-lg p-3 hover:bg-orange-50 transition cursor-pointer"
                  onClick={() => linkArbitrationToHelper(doc.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-800">⚖️ {doc.helper_name}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {doc.pm_user} • {doc.created_at ? new Date(doc.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' }) : 'Sem data'}
                      </div>
                      <Badge className="mt-1 text-xs" variant={doc.status === 'signed' ? 'default' : 'secondary'}>
                        {doc.status === 'signed' ? '✅ Assinado' : '📝 Pendente'}
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      className="bg-orange-600 hover:bg-orange-700"
                      disabled={linkingDocument}
                    >
                      {linkingDocument ? '⏳' : '✓ Selecionar'}
                    </Button>
                  </div>
                </div>
              ))}
            
            {availableArbitrationDocs.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p>📋 Nenhum Arbitration encontrado</p>
                <p className="text-sm mt-2">Crie um novo Arbitration primeiro</p>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex justify-end gap-2 pt-4 border-t flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowLinkArbitrationModal(false);
                setHelperToLinkDoc(null);
                setLinkDocSearch('');
              }}
            >
              ✖️ Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal: Vincular Contractor Existente ao Helper */}
      <Dialog open={showLinkContractorModal} onOpenChange={setShowLinkContractorModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-lg font-bold text-teal-800 flex items-center gap-2">
              🔍 Buscar Contractor Existente
            </DialogTitle>
            <p className="text-sm text-gray-600 mt-2">
              Selecione um Contractor já criado para vincular a <strong>{helperToLinkDoc?.name}</strong>
            </p>
          </DialogHeader>
          
          {/* Busca */}
          <div className="flex-shrink-0 mb-4">
            <Input
              placeholder="Buscar por nome do helper..."
              value={linkDocSearch}
              onChange={(e) => setLinkDocSearch(e.target.value)}
              className="w-full"
            />
          </div>
          
          {/* Lista de Documentos com Scroll */}
          <div className="flex-1 overflow-y-auto space-y-2" style={{ WebkitOverflowScrolling: 'touch' }}>
            {availableContractorDocs
              .filter(doc => {
                if (!linkDocSearch.trim()) return true;
                return doc.helper_name?.toLowerCase().includes(linkDocSearch.toLowerCase());
              })
              .map(doc => (
                <div
                  key={doc.id}
                  className="border border-teal-200 rounded-lg p-3 hover:bg-teal-50 transition cursor-pointer"
                  onClick={() => linkContractorToHelper(doc.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-800">📝 {doc.helper_name}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {doc.pm_user} • {doc.created_at ? new Date(doc.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' }) : 'Sem data'}
                      </div>
                      <Badge className="mt-1 text-xs" variant={doc.status === 'signed' ? 'default' : 'secondary'}>
                        {doc.status === 'signed' ? '✅ Assinado' : '📝 Pendente'}
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      className="bg-teal-600 hover:bg-teal-700"
                      disabled={linkingDocument}
                    >
                      {linkingDocument ? '⏳' : '✓ Selecionar'}
                    </Button>
                  </div>
                </div>
              ))}
            
            {availableContractorDocs.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p>📋 Nenhum Contractor encontrado</p>
                <p className="text-sm mt-2">Crie um novo Contractor primeiro</p>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex justify-end gap-2 pt-4 border-t flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowLinkContractorModal(false);
                setHelperToLinkDoc(null);
                setLinkDocSearch('');
              }}
            >
              ✖️ Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal: Preview LSWP antes de enviar */}
      <Dialog open={showLSWPPreview} onOpenChange={setShowLSWPPreview}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-bold text-green-800 flex items-center gap-2">
              👁️ Preview - LSWP Formstack
            </DialogTitle>
            <p className="text-sm text-gray-600 mt-2">
              Revise todos os dados antes de enviar para o Formstack
            </p>
          </DialogHeader>
          
          {/* Preview Content com Scroll */}
          <div className="flex-1 overflow-y-auto space-y-4 pr-2" style={{ WebkitOverflowScrolling: 'touch' }}>
            {/* Customer Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-3">👤 Informações do Cliente</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-600">Nome:</span> <span className="font-medium">{lswpFormData.customer_first_name} {lswpFormData.customer_last_name}</span></div>
                <div><span className="text-gray-600">Email:</span> <span className="font-medium">{lswpFormData.customer_email}</span></div>
                <div className="col-span-2"><span className="text-gray-600">Endereço:</span> <span className="font-medium">{lswpFormData.job_address_line1}, {lswpFormData.job_city}, {lswpFormData.job_state} {lswpFormData.job_zip}</span></div>
              </div>
            </div>
            
            {/* Work Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-3">🏗️ Informações do Trabalho</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-600">Janelas:</span> <span className="font-medium">{lswpFormData.num_windows}</span></div>
                <div><span className="text-gray-600">Portas:</span> <span className="font-medium">{lswpFormData.num_doors}</span></div>
                <div><span className="text-gray-600">Wet Cloths:</span> <span className="font-medium">{(lswpFormData.num_windows + lswpFormData.num_doors) * 2}</span></div>
                <div><span className="text-gray-600">Dry Cloths:</span> <span className="font-medium">{(lswpFormData.num_windows + lswpFormData.num_doors) * 2}</span></div>
                <div className="col-span-2"><span className="text-gray-600">Data:</span> <span className="font-medium">{new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}</span></div>
              </div>
            </div>
            
            {/* Auto-filled Checkboxes */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-semibold text-purple-800 mb-3">✅ Checkboxes Marcados Automaticamente</h4>
              <div className="space-y-2 text-sm">
                <div className="font-medium text-purple-700">Tipo de Trabalho:</div>
                <div className="ml-4">
                  {lswpFormData.num_windows > 0 && lswpFormData.num_doors > 0 ? (
                    <Badge className="bg-blue-600">✓ Window (apenas Window quando ambos)</Badge>
                  ) : lswpFormData.num_windows > 0 ? (
                    <Badge className="bg-blue-600">✓ Window</Badge>
                  ) : lswpFormData.num_doors > 0 ? (
                    <Badge className="bg-green-600">✓ Door</Badge>
                  ) : (
                    <span className="text-gray-500">Nenhum</span>
                  )}
                </div>
                <div className="mt-3 space-y-1">
                  <div>✅ Post Warnings Signs</div>
                  <div>✅ Maintenance Containment</div>
                  <div>✅ Post-Renovation Cleaning</div>
                  <div>✅ Applicable to All Jobs (todos Yes)</div>
                  <div>✅ Applicable to Interior Jobs Only (todos Yes)</div>
                  <div>✅ Applicable to Exterior Jobs Only (todos Yes)</div>
                  <div>✅ Visual Inspection PASSED</div>
                  <div>✅ Wet Cloth Tests PASSED</div>
                </div>
              </div>
            </div>
            
            {/* Renovator Info */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h4 className="font-semibold text-orange-800 mb-3">👨‍💼 Informações do Renovator</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-600">Firma:</span> <span className="font-medium">
                  {user?.username === 'PM1' ? 'PM Home Remodeling 1' :
                   user?.username === 'PM2' ? 'PM Home Remodeling 2' :
                   user?.username === 'PM3' ? 'PM Home Remodeling 3' :
                   user?.username === 'PM4' ? 'PM Home Remodeling 4' :
                   user?.username === 'PM5' ? 'PM Home Remodeling 5' :
                   user?.username === 'PM7' ? 'PM Home Remodeling 7' :
                   user?.username === 'PM8' ? 'PM Home Remodeling 8' : 'PM Home Remodeling'}
                </span></div>
                <div><span className="text-gray-600">Nome:</span> <span className="font-medium">
                  {user?.username === 'PM1' ? 'Johnny Lucas Garbim' :
                   user?.username === 'PM2' ? 'Luan Gustavo Dos Santos' :
                   user?.username === 'PM3' ? 'Leonardo Lennen Casotti' :
                   user?.username === 'PM4' ? 'Ryt Charles De Almeida' :
                   user?.username === 'PM5' ? 'Sergio Luiz Correia' :
                   user?.username === 'PM7' ? 'Maderson Alves Santos' :
                   user?.username === 'PM8' ? 'Elionaldo Freitas dos Santos' : user?.username}
                </span></div>
                <div className="col-span-2"><span className="text-gray-600">Email:</span> <span className="font-medium">pmhomeremodeling@gmail.com</span></div>
              </div>
            </div>
            
            {/* Assinaturas Preview */}
            <div className="grid grid-cols-2 gap-4">
              <div className="border rounded-lg p-3">
                <div className="font-medium text-sm text-gray-700 mb-2">Assinatura Renovator:</div>
                {hasRenovatorSig ? (
                  <div className="bg-gray-50 border rounded p-2">
                    <img src={renovatorSigPadRef.current?.toDataURL()} alt="Renovator Signature" className="w-full h-20 object-contain" />
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">Nenhuma assinatura</div>
                )}
              </div>
              <div className="border rounded-lg p-3">
                <div className="font-medium text-sm text-gray-700 mb-2">Assinatura Cliente:</div>
                {hasCustomerSig ? (
                  <div className="bg-gray-50 border rounded p-2">
                    <img src={customerSigPadRef.current?.toDataURL()} alt="Customer Signature" className="w-full h-20 object-contain" />
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">Nenhuma assinatura</div>
                )}
              </div>
            </div>
          </div>
          
          {/* Footer com Botões */}
          <div className="flex justify-end gap-3 pt-4 border-t flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => setShowLSWPPreview(false)}
            >
              ← Voltar
            </Button>
            <Button
              onClick={submitLSWPToFormstack}
              disabled={lswpSubmitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {lswpSubmitting ? '⏳ Enviando...' : '🚀 Enviar para Formstack'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ========================================
          FLOATING HAMBURGER BUTTON (iOS/iPad ONLY)
          ======================================== */}
      {isIPhone && showFloatingButton && (
        <>
          {/* Botão Flutuante Arrastável */}
          <button
            onTouchStart={handleButtonTouchStart}
            onTouchMove={handleButtonTouchMove}
            onTouchEnd={handleButtonTouchEnd}
            onClick={handleButtonClick}
            className={`fixed z-[9999] bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full shadow-2xl transition-all duration-300 ${
              isDraggingButton ? 'scale-110' : 'scale-100 hover:scale-105'
            } ${showFloatingMenu ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            style={{
              left: `${floatingButtonPosition.x}px`,
              top: `${floatingButtonPosition.y}px`,
              width: '60px',
              height: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 20px rgba(59, 130, 246, 0.4)',
              touchAction: 'none'
            }}
            aria-label="Menu"
          >
            <svg
              className="w-7 h-7"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>

          {/* Overlay Escuro */}
          {showFloatingMenu && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-[99997] transition-opacity duration-300"
              onClick={closeFloatingMenu}
              style={{ animation: 'fadeIn 0.3s ease-out' }}
            />
          )}

          {/* Menu Lateral Deslizante - Design Moderno */}
          {showFloatingMenu && (
            <div
              className="fixed top-0 right-0 h-full w-64 shadow-2xl z-[99998] transform transition-all duration-300 ease-out backdrop-blur-md floating-menu"
              style={{
                animation: 'slideInRight 0.3s ease-out',
                background: 'rgba(255, 255, 255, 0.25)',
                borderLeft: '1px solid rgba(255, 255, 255, 0.3)'
              }}
            >
              {/* Close Button */}
              <div className="flex justify-end p-4">
                <button
                  onClick={closeFloatingMenu}
                  className="text-gray-700 hover:bg-white hover:bg-opacity-40 rounded-full p-2 transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Menu Items */}
              <div className="px-4 pb-4 space-y-1">
                {/* Home */}
                <button
                  onClick={() => {
                    safeSetActiveTab('home');
                    closeFloatingMenu();
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${
                    activeTab === 'home'
                      ? 'bg-white bg-opacity-80 text-blue-600 shadow-sm'
                      : 'text-gray-700 hover:bg-white hover:bg-opacity-60'
                  }`}
                >
                  <span className="text-xl">🏠</span>
                  <span>Home</span>
                </button>

                {/* Labor */}
                <button
                  onClick={() => {
                    safeSetActiveTab('drafts');
                    closeFloatingMenu();
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${
                    activeTab === 'drafts'
                      ? 'bg-white bg-opacity-80 text-blue-600 shadow-sm'
                      : 'text-gray-700 hover:bg-white hover:bg-opacity-60'
                  }`}
                >
                  <span className="text-xl">📋</span>
                  <span>Labor</span>
                </button>

                {/* Reports (PM) ou Crews + Payroll (ADMIN) */}
                {user?.role === 'admin' ? (
                  <>
                    {/* Crews Report */}
                    <button
                      onClick={() => {
                        safeSetActiveTab('crews');
                        closeFloatingMenu();
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${
                        activeTab === 'crews'
                          ? 'bg-white bg-opacity-80 text-blue-600 shadow-sm'
                          : 'text-gray-700 hover:bg-white hover:bg-opacity-60'
                      }`}
                    >
                      <span className="text-xl">👥</span>
                      <span>Crews</span>
                    </button>
                    
                    {/* Payroll */}
                    <button
                      onClick={() => {
                        safeSetActiveTab('payroll');
                        closeFloatingMenu();
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${
                        activeTab === 'payroll'
                          ? 'bg-white bg-opacity-80 text-blue-600 shadow-sm'
                          : 'text-gray-700 hover:bg-white hover:bg-opacity-60'
                      }`}
                    >
                      <span className="text-xl">💰</span>
                      <span>Payroll</span>
                    </button>
                  </>
                ) : (
                  <>
                    {/* Report for PMs */}
                    <button
                      onClick={() => {
                        safeSetActiveTab('reports');
                        closeFloatingMenu();
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${
                        activeTab === 'reports'
                          ? 'bg-white bg-opacity-80 text-blue-600 shadow-sm'
                          : 'text-gray-700 hover:bg-white hover:bg-opacity-60'
                      }`}
                    >
                      <span className="text-xl">📊</span>
                      <span>Reports</span>
                    </button>
                    
                    {/* Crews for PMs */}
                    <button
                      onClick={() => {
                        safeSetActiveTab('crews');
                        closeFloatingMenu();
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${
                        activeTab === 'crews'
                          ? 'bg-white bg-opacity-80 text-blue-600 shadow-sm'
                          : 'text-gray-700 hover:bg-white hover:bg-opacity-60'
                      }`}
                    >
                      <span className="text-xl">👥</span>
                      <span>Crews</span>
                    </button>
                  </>
                )}

                {/* Docs - Apenas Expansível (não é uma aba real) */}
                <div className="space-y-0.5">
                  <button
                    onClick={() => {
                      // Apenas alternar expansão, NÃO navegar
                      setFloatingDocsExpanded(!floatingDocsExpanded);
                    }}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-all font-medium ${
                      floatingDocsExpanded
                        ? 'bg-white bg-opacity-60 text-gray-700'
                        : 'text-gray-700 hover:bg-white hover:bg-opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">📄</span>
                      <span>Docs</span>
                    </div>
                    <span className={`text-xs transition-transform ${floatingDocsExpanded ? 'rotate-180' : ''}`}>▼</span>
                  </button>
                  
                  {/* Sub-abas de Docs (mostrar se expandido) */}
                  {floatingDocsExpanded && (
                    <div className="ml-6 space-y-0.5 mt-1">
                      <button
                        onClick={() => {
                          setActiveTab('arbitration');
                          setArbitrationSubTab('files');
                          closeFloatingMenu();
                          setFloatingDocsExpanded(false); // Fechar expansão ao navegar
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                          activeTab === 'arbitration' && arbitrationSubTab === 'files'
                            ? 'bg-white bg-opacity-70 text-blue-600'
                            : 'text-gray-600 hover:bg-white hover:bg-opacity-50'
                        }`}
                      >
                        <span>⚖️</span>
                        <span>Arbitration</span>
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('arbitration');
                          setArbitrationSubTab('contractor');
                          closeFloatingMenu();
                          setFloatingDocsExpanded(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                          activeTab === 'arbitration' && arbitrationSubTab === 'contractor'
                            ? 'bg-white bg-opacity-70 text-blue-600'
                            : 'text-gray-600 hover:bg-white hover:bg-opacity-50'
                        }`}
                      >
                        <span>📋</span>
                        <span>Contractor</span>
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('arbitration');
                          setArbitrationSubTab('installation');
                          closeFloatingMenu();
                          setFloatingDocsExpanded(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                          activeTab === 'arbitration' && arbitrationSubTab === 'installation'
                            ? 'bg-white bg-opacity-70 text-blue-600'
                            : 'text-gray-600 hover:bg-white hover:bg-opacity-50'
                        }`}
                      >
                        <span>🔧</span>
                        <span>Installation</span>
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('arbitration');
                          setArbitrationSubTab('helpers');
                          closeFloatingMenu();
                          setFloatingDocsExpanded(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                          activeTab === 'arbitration' && arbitrationSubTab === 'helpers'
                            ? 'bg-white bg-opacity-70 text-blue-600'
                            : 'text-gray-600 hover:bg-white hover:bg-opacity-50'
                        }`}
                      >
                        <span>👷</span>
                        <span>Helpers</span>
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('arbitration');
                          setArbitrationSubTab('lswp');
                          closeFloatingMenu();
                          setFloatingDocsExpanded(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                          activeTab === 'arbitration' && arbitrationSubTab === 'lswp'
                            ? 'bg-white bg-opacity-70 text-blue-600'
                            : 'text-gray-600 hover:bg-white hover:bg-opacity-50'
                        }`}
                      >
                        <span>📋</span>
                        <span>LSWP Formstack</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Suporte (PM) ou Admin (ADMIN) */}
                {user?.role === 'admin' ? (
                  <button
                    onClick={() => {
                      safeSetActiveTab('admin');
                      closeFloatingMenu();
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${
                      activeTab === 'admin'
                        ? 'bg-white bg-opacity-80 text-blue-600 shadow-sm'
                        : 'text-gray-700 hover:bg-white hover:bg-opacity-60'
                    }`}
                  >
                    <span className="text-xl">⚙️</span>
                    <span>Admin</span>
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      safeSetActiveTab('support');
                      closeFloatingMenu();
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${
                      activeTab === 'support'
                        ? 'bg-white bg-opacity-80 text-blue-600 shadow-sm'
                        : 'text-gray-700 hover:bg-white hover:bg-opacity-60'
                    }`}
                  >
                    <span className="text-xl">🎫</span>
                    <span>Suporte</span>
                  </button>
                )}

                {/* Divider */}
                <div className="pt-4 mt-4 border-t border-white border-opacity-30"></div>

                {/* Logout */}
                <button
                  onClick={() => {
                    closeFloatingMenu();
                    handleLogout();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-white hover:bg-opacity-60 transition-all font-medium"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Sair</span>
                </button>
              </div>
            </div>
          )}
        </>
      )}


      
      {/* Helper Financial Report Modal */}
      {selectedHelperTeamForReport && (
        <HelperFinancialReportModal
          isOpen={showHelperFinancialReport}
          onClose={() => {
            setShowHelperFinancialReport(false);
            setSelectedHelperTeamForReport(null);
          }}
          teamId={selectedHelperTeamForReport.id}
          teamName={selectedHelperTeamForReport.equipe}
          language={language}
        />
      )}

      {/* Modal de Loading para Download do Código */}
      <Dialog open={backupLoading} onOpenChange={() => {}}>
        <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-center">
              📦 {language === 'pt-BR' ? 'Preparando Download' : 'Preparing Download'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600"></div>
            </div>
            <p className="text-lg font-semibold text-gray-900 mb-2">
              {language === 'pt-BR' ? 'Gerando arquivo ZIP...' : 'Generating ZIP file...'}
            </p>
            <p className="text-sm text-gray-600">
              {language === 'pt-BR' 
                ? 'Por favor, aguarde. Isso pode levar alguns minutos.' 
                : 'Please wait. This may take a few minutes.'}
            </p>
            <div className="mt-6 space-y-2 text-xs text-left bg-gray-50 p-4 rounded-lg">
              <p className="flex items-center gap-2">
                <span className="animate-pulse">⏳</span>
                {language === 'pt-BR' ? 'Compactando código frontend...' : 'Compressing frontend code...'}
              </p>
              <p className="flex items-center gap-2">
                <span className="animate-pulse">⏳</span>
                {language === 'pt-BR' ? 'Compactando código backend...' : 'Compressing backend code...'}
              </p>
              <p className="flex items-center gap-2">
                <span className="animate-pulse">⏳</span>
                {language === 'pt-BR' ? 'Incluindo dependências...' : 'Including dependencies...'}
              </p>
              <p className="flex items-center gap-2">
                <span className="animate-pulse">⏳</span>
                {language === 'pt-BR' ? 'Preparando download...' : 'Preparing download...'}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Waiver Modal */}
      <WaiverModal
        isOpen={showWaiverModal}
        onClose={() => setShowWaiverModal(false)}
        onSubmit={handleSubmitWaiver}
        pmUser={currentWaiverPM}
        initialData={waiverSettings}
        mode={waiverMode}
        waiverData={waiverData || currentWaiverToSign}
        availablePMs={['PM1', 'PM2', 'PM3', 'PM4', 'PM5', 'PM6', 'PM7', 'PM8', 'PM9', 'PM10']}
      />
      
      {/* Waiver Settings Modal */}
      <WaiverSettingsModal
        isOpen={showWaiverSettingsModal}
        onClose={() => setShowWaiverSettingsModal(false)}
        onSave={handleSaveWaiverSettings}
        initialData={waiverSettings}
      />

      {/* AI Labor Bill Progress Toast */}
      <AIProgressToast
        isVisible={showAIToast}
        progress={aiToastProgress}
        currentStep={aiToastStep}
        isComplete={aiToastComplete}
        hasError={aiToastError}
        queueInfo={aiQueueInfo}
        onClose={() => setShowAIToast(false)}
      />
      
      {/* Job Hover Popovers - MÚLTIPLOS */}
      {hoveredJobs.length > 0 && hoveredJobsData.length > 0 && (
        <>
          {hoveredJobsData.map((jobData, index) => (
            <div
              key={hoveredJobs[index]}
              onMouseEnter={handlePopoverMouseEnter}
              onMouseLeave={handlePopoverMouseLeave}
            >
              <JobHoverPopover
                job={jobData}
                extractedData={jobData}
                position={popoverPositions[index] || { x: 0, y: 0 }}
                onClose={() => {
                  setHoveredJobs([]);
                  setHoveredJobsData([]);
                }}
              />
            </div>
          ))}
        </>
      )}


      {/* Floating Batch AI Progress Widget */}
      {batchAiProgress?.running && (
        <div className="fixed bottom-4 right-4 z-[9999] bg-white border-2 border-purple-300 rounded-xl shadow-2xl p-4 w-72" data-testid="batch-ai-widget">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
              <Loader2 className="h-3.5 w-3.5 text-purple-600 animate-spin" />
            </div>
            <span className="font-semibold text-sm text-purple-800">IA Processando</span>
          </div>
          <div className="text-xs text-gray-600 mb-2 truncate">
            {batchAiProgress.current_job || 'Preparando...'}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
            <div 
              className="bg-gradient-to-r from-violet-500 to-purple-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${batchAiProgress.total > 0 ? (batchAiProgress.processed / batchAiProgress.total * 100) : 0}%` }}
            />
          </div>
          <div className="text-xs text-gray-500 flex justify-between">
            <span>{batchAiProgress.processed || 0}/{batchAiProgress.total || 0} trabalhos</span>
            {batchAiProgress.errors > 0 && <span className="text-red-500">{batchAiProgress.errors} erros</span>}
          </div>
        </div>
      )}

      {/* Batch AI Complete Toast Widget */}
      {!batchAiProgress?.running && batchAiProgress?.message && (
        <div 
          className="fixed bottom-4 right-4 z-[9999] bg-white border-2 border-green-300 rounded-xl shadow-2xl p-4 w-72 cursor-pointer"
          onClick={() => setBatchAiProgress({ running: false })}
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">✅</span>
            <span className="font-semibold text-sm text-green-800">IA Concluída</span>
            <span className="text-xs text-gray-400 ml-auto">✕</span>
          </div>
          <div className="text-xs text-gray-600">{batchAiProgress.message}</div>
        </div>
      )}

        </div>
      )}
    </>

  );
}

export default App;